import { Layers, Box3, Vector3, Matrix4, DataTexture, RedFormat, RGBAFormat, LinearFilter, UnsignedByteType, FloatType, Texture, OrthographicCamera, WebGLRenderer, WebGLRenderTarget, Group, PerspectiveCamera, NoToneMapping, LinearEncoding, Vector4, Mesh, Object3D, ShaderMaterial, Raycaster, Vector2, MathUtils, Scene, ClampToEdgeWrapping, WebGL3DRenderTarget, ShortType, RedIntegerFormat, NearestFilter, Material, RawShaderMaterial, DoubleSide, GLSL3, PlaneGeometry, Matrix3, RepeatWrapping, Color, LineDashedMaterial, BufferAttribute, Line as Line$1, Quaternion, ShapeUtils, Float32BufferAttribute, CustomBlending, AddEquation, OneMinusDstAlphaFactor, OneFactor, DstAlphaFactor, Data3DTexture, MeshBasicMaterial, Line3, BufferGeometry, InterleavedBufferAttribute, SrcAlphaFactor, ZeroFactor, OneMinusSrcAlphaFactor, Clock } from 'three';
import dicomParser from 'dicom-parser';
import Tiff from 'tiff.js';
import jpeg from 'jpeg-js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { Text } from 'troika-three-text';
import init, { decompressChunkInto, DCCTConstants, parseDCCTFooter } from '@dcmctkit/wasm';
import { proto3, Timestamp, Empty, DoubleValue, BoolValue } from '@bufbuild/protobuf';
import { mergeWith, isArray, sortBy } from 'lodash';
import { jsx } from 'react/jsx-runtime';
import { useRef, useEffect } from 'react';

const prototype = Layers.prototype;
const toMask = (value) => typeof value === "bigint" ? value : BigInt(value ?? 0);
const getBit = (channel) => {
  if (!Number.isInteger(channel) || channel < 0) {
    throw new Error(`[Layers] channel must be a non-negative integer, received ${channel}`);
  }
  return 1n << BigInt(channel);
};
if (!prototype.__diagnocatBigIntLayersPatched__) {
  prototype.set = function(channel) {
    const layers = this;
    layers.mask = getBit(channel);
  };
  prototype.enable = function(channel) {
    const layers = this;
    layers.mask = toMask(layers.mask) | getBit(channel);
  };
  prototype.enableAll = function() {
    const layers = this;
    layers.mask = ~0n;
  };
  prototype.toggle = function(channel) {
    const layers = this;
    layers.mask = toMask(layers.mask) ^ getBit(channel);
  };
  prototype.disable = function(channel) {
    const layers = this;
    layers.mask = toMask(layers.mask) & ~getBit(channel);
  };
  prototype.disableAll = function() {
    const layers = this;
    layers.mask = 0n;
  };
  prototype.test = function(layers) {
    const currentLayers = this;
    return (toMask(currentLayers.mask) & toMask(layers.mask)) !== 0n;
  };
  prototype.isEnabled = function(channel) {
    const layers = this;
    return (toMask(layers.mask) & getBit(channel)) !== 0n;
  };
  Object.defineProperty(prototype, "__diagnocatBigIntLayersPatched__", {
    configurable: false,
    enumerable: false,
    value: true,
    writable: false
  });
}

class ListenerStorage {
  constructor() {
    this.listeners = [];
  }
  add(callback, priority = 0, force = false) {
    if (!this.has(callback)) {
      if (priority !== 0 && this.listeners.length > 0) {
        const index = this.findIndex(this.listeners, priority, 0, this.listeners.length - 1);
        this.listeners.splice(index, 0, {
          callback,
          enable: true,
          priority
        });
      } else {
        this.listeners.push({ callback, enable: true, priority });
      }
      if (force && this.listenersForFire && typeof this.firedListenerIndex !== "undefined") {
        if (priority !== 0 && this.listeners.length > 0) {
          const index = this.findIndex(this.listenersForFire, priority, 0, this.listeners.length - 1);
          if (index > this.firedListenerIndex) {
            this.listenersForFire.splice(index, 0, {
              callback,
              enable: true,
              priority
            });
          } else if (index <= this.firedListenerIndex) {
            if (this.listenersForFire[this.firedListenerIndex + 1].priority === priority) {
              this.listenersForFire.splice(this.firedListenerIndex + 1, 0, {
                callback,
                enable: true,
                priority
              });
            }
          }
        } else {
          this.listenersForFire.push({
            callback,
            enable: true,
            priority
          });
        }
      }
    }
  }
  remove(callback, force = false) {
    let index = this.listeners.findIndex((listener) => listener.callback === callback);
    if (index >= 0) {
      this.listeners.splice(index, 1);
    }
    if (force && this.listenersForFire && typeof this.firedListenerIndex !== "undefined") {
      index = this.listenersForFire.findIndex((listener) => listener.callback === callback);
      if (index > this.firedListenerIndex) {
        this.listenersForFire.splice(index, 1);
      } else {
        console.error("listener already executed");
      }
    }
  }
  findIndex(listeners, priority, start, end) {
    let middle = Math.floor((start + end) / 2);
    if (listeners[middle].priority === priority) {
      return middle;
    } else {
      if (priority < listeners[middle].priority) {
        if (middle < end) {
          return this.findIndex(listeners, priority, middle + 1, end);
        }
        return end + 1;
      } else {
        if (start < middle) {
          return this.findIndex(listeners, priority, start, middle - 1);
        }
        return start;
      }
    }
  }
  fire(arg) {
    if (typeof arg === "undefined" && typeof this.fireArgument !== "undefined") {
      arg = this.fireArgument;
    }
    this.listenersForFire = this.getListeners();
    this.listenersForFire.forEach((listener, index) => {
      this.firedListenerIndex = index;
      if (listener.enable) {
        listener.callback(arg);
      }
    });
    this.listenersForFire = void 0;
    this.firedListenerIndex = void 0;
  }
  getListeners() {
    return [...this.listeners];
  }
  clear() {
    this.listeners.splice(0);
  }
  isEmpty() {
    return this.listeners.length === 0;
  }
  has(callback) {
    return this.listeners.findIndex((listener) => listener.callback === callback) >= 0;
  }
  setEnabled(value, callback) {
    if (callback) {
      if (callback.flag === "me") {
        const listener = this.listeners.find((listener2) => listener2.callback === callback.callback);
        if (listener) {
          listener.enable = value;
        }
      } else {
        const listener = this.listeners.filter((listener2) => listener2.callback !== callback.callback);
        listener.forEach((listener2) => listener2.enable = value);
      }
    } else {
      this.listeners.forEach((listener) => listener.enable = value);
    }
  }
  enable(callback) {
    if (callback) {
      this.setEnabled(true, { callback, flag: "me" });
    } else {
      this.setEnabled(true);
    }
  }
  disable(callback) {
    if (callback) {
      this.setEnabled(false, { callback, flag: "me" });
    } else {
      this.setEnabled(false);
    }
  }
  enableExcept(callbacks) {
    if (Array.isArray(callbacks)) {
      this.disable();
      callbacks.forEach((callback) => this.setEnabled(false, { callback, flag: "me" }));
    } else {
      this.setEnabled(true, { callback: callbacks, flag: "exceptMe" });
    }
  }
  disableExcept(callbacks) {
    if (Array.isArray(callbacks)) {
      this.disable();
      callbacks.forEach((callback) => this.setEnabled(true, { callback, flag: "me" }));
    } else {
      this.setEnabled(false, { callback: callbacks, flag: "exceptMe" });
    }
  }
}

const mouseButtons = ["left", "middle", "right"];
const anyButtonEventTypesArr = ["pointerdown", "pointerup"];
const specialEventTypesArr = ["click", "dblclick", "contextmenu", "pointermove", "pointerenter", "pointerleave", "pointerout", "pointerover", "wheel"];
const isSpecialEventType = (type) => {
  return specialEventTypesArr.some((el) => el === type);
};
class PointerEventsStorage {
  constructor(element = document) {
    this.element = element;
    this.listeners = {};
    this.callbacks = {};
  }
  init() {
    anyButtonEventTypesArr.forEach((type) => {
      this.listeners[type] = {
        left: new ListenerStorage(),
        middle: new ListenerStorage(),
        right: new ListenerStorage()
      };
      this.callbacks[type] = {
        callback: (event) => this.listeners[type][mouseButtons[event.button]].fire(event),
        isListening: false
      };
    });
    specialEventTypesArr.forEach((type) => {
      this.listeners[type] = new ListenerStorage();
      this.callbacks[type] = {
        callback: (event) => this.listeners[type].fire(event),
        isListening: false
      };
    });
  }
  dispose() {
    if (this.listeners) {
      this.clear();
      Object.keys(this.listeners).forEach((key) => delete this.listeners[key]);
    }
  }
  changeDomElement(element) {
    const oldDomElement = this.element;
    this.element = element;
    anyButtonEventTypesArr.forEach((eventType) => {
      oldDomElement.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      this.callbacks[eventType].isListening = false;
      this.startListening(eventType);
    });
    specialEventTypesArr.forEach((eventType) => {
      oldDomElement.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      this.callbacks[eventType].isListening = false;
      this.startListening(eventType);
    });
  }
  addAnyButtonListener(eventType, button, listener, priority, force) {
    this.listeners[eventType][button].add(listener, priority, force);
    this.startListening(eventType);
  }
  addSpecialEventListeners(eventType, listener, priority, force) {
    this.listeners[eventType].add(listener, priority, force);
    this.startListening(eventType);
  }
  removeButtonListener(eventType, button, listener, force) {
    if (this.listeners) {
      this.listeners[eventType][button].remove(listener, force);
      this.stopListening(eventType);
    }
  }
  removeMoveAndWheelListener(eventType, listener, force) {
    if (this.listeners) {
      this.listeners[eventType].remove(listener, force);
      this.stopListening(eventType);
    }
  }
  startListening(eventType) {
    if (this.callbacks[eventType].callback && !this.callbacks[eventType].isListening) {
      if (!this.isEmpty(eventType)) {
        this.callbacks[eventType].isListening = true;
        this.element.addEventListener(eventType, this.callbacks[eventType].callback, { capture: false, passive: false });
      }
    }
  }
  stopListening(eventType) {
    if (this.callbacks[eventType].callback && this.callbacks[eventType].isListening) {
      if (this.isEmpty(eventType)) {
        this.callbacks[eventType].isListening = false;
        this.element.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      }
    }
  }
  clear() {
    anyButtonEventTypesArr.forEach((eventType) => {
      Object.values(this.listeners[eventType]).forEach((value) => value.clear());
      this.stopListening(eventType);
    });
    specialEventTypesArr.forEach((eventType) => {
      this.listeners[eventType].clear();
      this.stopListening(eventType);
    });
  }
  isEmpty(eventType) {
    if (isSpecialEventType(eventType)) {
      return this.listeners[eventType].isEmpty();
    }
    return Object.values(this.listeners[eventType]).every((value) => value.isEmpty());
  }
  has(eventType, listener, button) {
    if (this.listeners[eventType] instanceof ListenerStorage) {
      return this.listeners[eventType].has(listener);
    } else if (button) {
      return this.listeners[eventType][button].has(listener);
    }
    return false;
  }
  disableButton(eventType, button, listener) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].disable(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].disable(listener);
    }
  }
  enableButton(eventType, button, listener) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].enable(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].enable(listener);
    }
  }
  disableButtonExcept(eventType, listener, button) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].disableExcept(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].disableExcept(listener);
    }
  }
  enableButtonExcept(eventType, listener, button) {
    if (eventType === "click" || eventType === "dblclick" || eventType === "contextmenu") {
      this.listeners[eventType].enableExcept(listener);
    } else if (button && typeof button !== "function") {
      this.listeners[eventType][button].enableExcept(listener);
    }
  }
  disableWheel(listener) {
    this.listeners["wheel"].disable(listener);
  }
  enableWheel(listener) {
    this.listeners["wheel"].enable(listener);
  }
  disableMove(listener) {
    this.listeners["pointermove"].disable(listener);
  }
  enableMove(listener) {
    this.listeners["pointermove"].enable(listener);
  }
  disableMoveExcept(listeners) {
    this.listeners["pointermove"].disableExcept(listeners);
  }
  enableMoveExcept(listeners) {
    this.listeners["pointermove"].enableExcept(listeners);
  }
}

class PointerController {
  constructor() {
    this.listeners = (/* @__PURE__ */ new Map()).set(document, new PointerEventsStorage());
  }
  init() {
    this.listeners.forEach((val) => val.init());
  }
  dispose() {
    this.listeners.forEach((val) => val.dispose());
  }
  clear() {
    this.dispose();
    this.listeners.clear();
  }
  redefineDomElement(newElement, oldElement) {
    const oldElementListeners = this.listeners.get(oldElement);
    if (oldElementListeners) {
      oldElementListeners.changeDomElement(newElement);
      this.listeners.set(newElement, oldElementListeners);
      this.listeners.delete(oldElement);
    }
  }
  addListener(eventType, listener, element = document, button, priority, force) {
    let mouseEventListeners = this.listeners.get(element);
    if (!mouseEventListeners) {
      mouseEventListeners = new PointerEventsStorage(element);
      this.listeners.set(element, mouseEventListeners);
      mouseEventListeners.init();
    }
    if (isSpecialEventType(eventType)) {
      if ((!button || typeof button === "number") && typeof priority !== "number") {
        mouseEventListeners.addSpecialEventListeners(eventType, listener, button, priority);
        return () => this.removeListener(eventType, listener, element);
      } else {
        console.error("listener is not added. Event type: " + eventType);
      }
    } else if (button !== void 0) {
      if (typeof button !== "number" && typeof priority !== "boolean") {
        mouseEventListeners.addAnyButtonListener(eventType, button, listener, priority, force);
        return () => this.removeListener(eventType, listener, element, button);
      } else {
        console.error("listener is not added. Event type: " + eventType + ", button: " + button);
      }
    } else {
      console.error("listener is not added. Event type: " + eventType + ", button: " + button);
    }
  }
  removeListener(eventType, listener, element = document, button, force = false) {
    let mouseEventListeners;
    if (element) {
      mouseEventListeners = this.listeners.get(element);
    }
    if (mouseEventListeners) {
      if (isSpecialEventType(eventType)) {
        if (typeof button === "boolean" || typeof button === "undefined") {
          mouseEventListeners.removeMoveAndWheelListener(eventType, listener, button);
        }
      } else if (button !== void 0 && typeof button !== "boolean") {
        mouseEventListeners.removeButtonListener(eventType, button, listener, force);
      }
    } else {
      console.warn("listener is not found");
    }
  }
}

const keyBoardEventTypesArr = ["keydown", "keyup", "keypress"];
class KeyboardController {
  init() {
    this.listeners = {
      keydown: /* @__PURE__ */ new Map(),
      keyup: /* @__PURE__ */ new Map(),
      keypress: /* @__PURE__ */ new Map()
    };
    this.callbacks = {};
    keyBoardEventTypesArr.forEach((type) => {
      this.callbacks[type] = {
        callback: (event) => this.listeners[type].get(event.code)?.fire(event),
        isListening: true
      };
    });
  }
  dispose() {
    if (this.listeners) {
      keyBoardEventTypesArr.forEach((eventType) => {
        this.listeners[eventType].forEach(
          (ListenerStorage2) => ListenerStorage2.clear()
        );
        this.listeners[eventType].clear();
        this.stopListening(eventType);
      });
    }
  }
  addListener(eventType, listener, eventCode, priority) {
    const listenerStorage = this.listeners[eventType].get(eventCode);
    if (listenerStorage) {
      listenerStorage.add(listener, priority);
    } else {
      const storage = new ListenerStorage();
      storage.add(listener, priority);
      this.listeners[eventType].set(eventCode, storage);
    }
    this.startListening(eventType);
  }
  removeListener(eventType, listener, code) {
    const listenerStorage = this.listeners[eventType].get(code);
    if (listenerStorage) {
      listenerStorage.remove(listener);
      this.stopListening(eventType);
    }
  }
  disableKeys(keys) {
    if (keys) {
      Object.values(this.listeners).forEach((item) => keys.forEach((key) => item.get(key)?.disable()));
    } else {
      Object.values(this.listeners).forEach((item) => item.forEach((ListenerStorage2) => ListenerStorage2.disable()));
    }
  }
  enableKeys(keys) {
    if (keys) {
      Object.values(this.listeners).forEach((item) => keys.forEach((key) => item.get(key)?.enable()));
    } else {
      Object.values(this.listeners).forEach((item) => item.forEach((ListenerStorage2) => ListenerStorage2.enable()));
    }
  }
  startListening(eventType) {
    if (!this.isEmpty(eventType)) {
      document.addEventListener(eventType, this.callbacks[eventType].callback, false);
    }
  }
  stopListening(eventType) {
    if (this.callbacks[eventType].isListening) {
      if (this.isEmpty(eventType)) {
        this.callbacks[eventType].isListening = false;
        document.removeEventListener(eventType, this.callbacks[eventType].callback, false);
      }
    }
  }
  isEmpty(eventType) {
    return this.listeners[eventType].size === 0;
  }
}

class EventDispatcher {
  constructor(eventsList) {
    this.listeners = /* @__PURE__ */ new Map();
    this.map = /* @__PURE__ */ new Map();
    if (eventsList) {
      this.setListeners(eventsList);
    }
  }
  setListeners(eventsList) {
    !this.listeners.size && eventsList.forEach((event) => this.listeners.set(event, new ListenerStorage()));
  }
  deleteListeners() {
    this.clearListeners();
    this.listeners.clear();
  }
  addEventListener(type, callback, priority) {
    this.listeners.get(type).add(callback, priority);
    return () => this.removeEventListener(type, callback);
  }
  addEventListenerOnce(type, callback, priority) {
    let fnOnce = this.map.get(callback);
    if (!fnOnce) {
      fnOnce = (event) => {
        callback(event);
        this.removeEventListener(type, fnOnce);
        this.map.delete(callback);
      };
      this.map.set(callback, fnOnce);
    }
    return this.addEventListener(type, fnOnce, priority);
  }
  removeEventListener(type, callback, force) {
    this.listeners.get(type)?.remove(callback, force);
  }
  hasEventListener(type, callback) {
    return this.listeners.get(type).has(callback);
  }
  eventIsListened(eventName) {
    return this.listeners.has(eventName);
  }
  dispatchEvent(event) {
    this.listeners.get(event.type).fire(event);
  }
  clearListeners(type) {
    if (type) {
      this.listeners.get(type)?.clear();
    } else {
      this.listeners.forEach((listenerStorage) => listenerStorage.clear());
    }
  }
}

const appEventNames = [
  "WillReunload",
  "resize",
  "resourceAdded",
  "applicationProgress",
  "activeViewWillChange",
  "activeViewChanged",
  "viewAdded",
  "viewRemoved"
];
class App extends EventDispatcher {
  constructor() {
    super();
    this.inited = false;
    this.views = {};
    this.mouseController = new PointerController();
    this.keyboardController = new KeyboardController();
    this.handleResize = this.handleResize.bind(this);
    this.resources = /* @__PURE__ */ new Map();
    this.startupSystems = [];
  }
  static Instance() {
    return this.instance ?? (this.instance = new App());
  }
  get view() {
    if (!this.views.main) {
      console.error("main view is not defined");
    }
    return this.views.main;
  }
  status() {
    return this.inited ? "run" : "stop";
  }
  init() {
    this.mouseController.init();
    this.keyboardController.init();
    window.addEventListener("resize", this.handleResize, false);
    this.inited = true;
  }
  dispose() {
    if (!this.inited) return;
    for (const key in this.views) {
      this.views[key].dispose();
      delete this.views[key];
    }
    this.keyboardController.dispose();
    this.mouseController.dispose();
    this.clearListeners();
    window.removeEventListener("resize", this.handleResize, false);
    this.inited = false;
  }
  clear() {
    this.dispose();
    this.deleteListeners();
  }
  setView(name, view) {
    if (this.views[name]) {
      console.error(`view ${String(name)} already exists`);
      return;
    }
    view.name = name;
    this.views[name] = view;
    this.dispatchEvent({ type: "viewAdded", view });
  }
  getView(name) {
    return this.views[name];
  }
  removeView(name) {
    const view = this.views[name];
    if (!view) {
      console.error(`view ${String(name)} not found`);
      return;
    }
    view.dispose();
    delete this.views[name];
    this.dispatchEvent({ type: "viewRemoved", view });
  }
  async runStartups() {
    for (let i = 0, il = this.startupSystems.length; i < il; i++) {
      const system = this.startupSystems[i];
      if (system.prototype?.constructor) {
        await new system().start();
      } else {
        await system();
      }
      this.dispatchEvent({ type: "applicationProgress", progress: (i + 1) / il });
    }
  }
  getActiveView() {
    return this.activeView;
  }
  setActiveView(view) {
    if (this.activeView === view) {
      return;
    }
    this.dispatchEvent({ type: "activeViewWillChange", current: this.activeView, next: view });
    const previous = this.activeView;
    this.activeView = view;
    this.dispatchEvent({ type: "activeViewChanged", previous, current: this.activeView });
  }
  deleteView(viewName) {
    const view = this.views[viewName];
    if (!view) {
      console.error(`view ${view} is not defined`);
    }
    view.dispose();
    this.views[viewName] = void 0;
  }
  addResources(name, resource) {
    this.resources.set(name, resource);
    this.dispatchEvent({ type: "resourceAdded", name });
  }
  getResources(name) {
    return this.resources.get(name);
  }
  handleResize() {
    this.dispatchEvent({ type: "resize" });
  }
}

const MIRenderEventNames = [...appEventNames];
const MIRender = App.Instance();
const setupMIRender = () => {
  if (MIRender.status() === "stop") {
    MIRender.setListeners(MIRenderEventNames);
    MIRender.init();
  } else {
    console.error("MIRender already launched");
  }
};

const createElement = (name, ...children) => {
  const element = document.createElement(name);
  children.forEach((children2) => {
    element.appendChild(children2);
  });
  return element;
};
const setAttributes = (element, attribute) => {
  let key;
  for (key in attribute) {
    if (key === "style") {
      for (let styleName in attribute.style) {
        const value = attribute.style[styleName];
        if (value) {
          element.style[styleName] = value;
        }
      }
    } else {
      const value = attribute[key];
      if (typeof value !== "undefined") {
        element[key] = value;
      }
    }
  }
};
const construct = (tagName) => (style, ...children) => {
  let element;
  if (style instanceof HTMLElement) {
    element = createElement(tagName, style, ...children);
  } else {
    element = createElement(tagName, ...children);
    if (style) {
      setAttributes(element, style);
    }
  }
  return element;
};
const HTML = {
  div: construct("div"),
  img: construct("img"),
  canvas: construct("canvas")
};

class RowGroup {
  constructor(layout) {
    this.rows = [];
    this.layout = layout;
  }
  update() {
    let y = 0;
    const rowsNumber = this.rows.length;
    this.rows.forEach((row, index) => {
      row.y = y;
      row.update();
      y -= row.computedHeight + (index !== rowsNumber - 1 ? this.layout.gap ? this.layout.gap : 0 : 0);
    });
    this.computedHeight = -y;
  }
  getCSSStyle() {
    let gridTemplateRows = "";
    this.rows.forEach((row) => gridTemplateRows += row.computedHeight / this.computedHeight * 100 + "% ");
    return {
      gridTemplateRows
    };
  }
}

class Col {
  constructor(props = {}) {
    this.width = props.width;
    this.x = 0;
    this.cells = [];
  }
  update() {
    this.computedWidth = 0;
    this.cells.forEach((cell) => {
      this.computedWidth = Math.max(this.computedWidth, cell.selfWidth);
    });
  }
}

class ColGroup {
  constructor(layout) {
    this.cols = [];
    this.layout = layout;
  }
  addCol() {
    const col = new Col();
    this.cols.push(col);
    return col;
  }
  update() {
    let x = 0;
    const colsNumber = this.cols.length;
    this.cols.forEach((col, index) => {
      col.x = x;
      col.update();
      x += col.computedWidth + (index !== colsNumber - 1 ? this.layout.gap ? this.layout.gap : 0 : 0);
    });
    this.computedWidth = x;
  }
  getCSSStyle() {
    let gridTemplateColumns = "";
    this.cols.forEach((col) => gridTemplateColumns += col.computedWidth / this.computedWidth * 100 + "% ");
    return {
      gridTemplateColumns
    };
  }
}

class LayoutNode {
  traverse(cb) {
    cb(this);
    this.getNodes()?.forEach((node) => {
      cb(node);
      node.traverse(cb);
    });
  }
}

const row = (style, ...cells) => {
  const row2 = new Row();
  if (style instanceof Cell) {
    row2.addCell(style);
  } else {
    row2.setProps(style);
  }
  cells.forEach((cell) => row2.addCell(cell));
  return row2;
};
class Row extends LayoutNode {
  constructor() {
    super();
    this.cells = [];
    this.y = 0;
  }
  getNodes() {
    return this.cells;
  }
  setProps(props) {
    let key;
    for (key in props) {
      this[key] = props[key];
    }
  }
  addCell(props) {
    let cell;
    let col;
    if (this.layout) {
      col = this.layout.getCol(this.cells.length);
      if (!col) {
        col = this.layout.colGroup.addCol();
      }
    }
    if (props instanceof Cell) {
      cell = props;
    } else {
      cell = new Cell();
      if (props) {
        cell.setProps(props);
      }
    }
    cell.row = this;
    col && col.cells.push(cell);
    this.cells.push(cell);
  }
  update() {
    this.computedHeight = this.height || this.minHeight || 0;
    this.cells.forEach((cell) => {
      cell.update();
      this.computedHeight = Math.max(this.computedHeight, cell.selfHeight);
    });
  }
  getStretchPower() {
    return this.cells.filter((cell) => cell.stretch).map((cell) => cell.selfWidth / this.computedWidth).reduce((a, b) => a + b);
  }
  get computedWidth() {
    return this.layout?.colGroup.computedWidth || this.cells.map((cell) => cell.computedWidth).reduce((a, b) => a + b);
  }
  getCell(...indices) {
    const index = indices.shift();
    if (index) {
      return this.cells[index].getCell(...indices);
    }
  }
  getBlocks(blokType, blocks) {
    return this.cells.map((cell) => cell.getBlocks(blokType, blocks)).flat();
  }
  getHTMLElement() {
    return this.cells.map((cell) => cell.getHTMLElement());
  }
  updateRefs() {
    return this.cells.map((cell) => cell.updateRefs());
  }
  clearRefs() {
    return this.cells.map((cell) => cell.clearRefs());
  }
}

const div$1 = HTML.div;
const layout = (style, ...rows) => {
  const layout2 = new Layout();
  if (style instanceof Row) {
    layout2.addRow(style);
  } else {
    layout2.setProps(style);
  }
  rows.forEach((row) => layout2.addRow(row));
  layout2.update();
  return layout2;
};
class Layout extends LayoutNode {
  constructor() {
    super();
    this.rowGroup = new RowGroup(this);
    this.colGroup = new ColGroup(this);
  }
  get computedHeight() {
    return this.rowGroup.computedHeight;
  }
  get computedWidth() {
    return this.colGroup.computedWidth;
  }
  getNodes() {
    return this.rowGroup.rows;
  }
  setProps(props) {
    let key;
    for (key in props) {
      this[key] = props[key];
    }
  }
  addRow(row = new Row(), index) {
    row.layout = this;
    if (index) {
      this.rowGroup.rows.splice(index, 0, row);
    } else {
      this.rowGroup.rows.push(row);
    }
    row.cells.forEach((cell, index2) => {
      let col = this.getCol(index2);
      if (!col) {
        col = this.colGroup.addCol();
      }
      col.cells.push(cell);
    });
  }
  getCol(index) {
    return this.colGroup.cols[index];
  }
  update() {
    this.rowGroup.update();
    this.colGroup.update();
  }
  getCell(...indices) {
    const index = indices.shift();
    if (index) {
      return this.rowGroup.rows[index].getCell(...indices);
    }
  }
  updateBlocks(blokType, blocks) {
    const block = this.rowGroup.rows.map((row) => row.getBlocks(blokType, blocks)).flat();
    this.blocks = block;
    return block;
  }
  updateHTMLElement() {
    if (!this.sheet) {
      this.sheet = new CSSStyleSheet();
    }
    const aspectRatio = this.computedWidth / this.computedHeight;
    let style = {
      zIndex: this.zIndex?.toString(),
      backgroundColor: this.backgroundColor,
      alignItems: this.alignItems,
      justifyItems: this.justifyItems,
      gap: this.gap ? `${this.gap / this.computedHeight * 100}% ${this.gap / this.computedWidth * 100}%` : void 0,
      aspectRatio: aspectRatio.toString(),
      display: "grid",
      ...this.colGroup.getCSSStyle(),
      ...this.rowGroup.getCSSStyle()
    };
    if (this.id) {
      const contentQueries = `@container (max-aspect-ratio: ${aspectRatio}) {#${this.id}{width: 100%; height: auto;}} 
      @container (min-aspect-ratio: ${aspectRatio}) {#${this.id}{height: 100%; width: auto;}}`;
      const cssRule = this.cssRule ? contentQueries + this.cssRule : contentQueries;
      this.sheet.replaceSync(cssRule);
    } else {
      style.width = this.computedWidth > this.computedHeight ? "100%" : void 0;
      style.height = this.computedHeight > this.computedWidth ? "100%" : void 0;
      this.cssRule && this.sheet.replaceSync(this.cssRule);
    }
    const res = div$1(
      { style, id: this.id, className: this.className },
      ...this.rowGroup.rows.map((row) => row.getHTMLElement()).flat()
    );
    if (this.element) {
      const parent = this.element.parentElement;
      this.element.remove();
      parent?.appendChild(res);
    }
    this.element = res;
    return res;
  }
  updateRefs() {
    if (this.ref) {
      this.ref.current = this.element;
      this.ref.blocks = this.blocks;
      this.ref.layout = this;
    }
    this.rowGroup.rows.map((row) => row.updateRefs());
  }
  clearRefs() {
    if (this.ref) {
      this.ref.current = null;
      this.ref.blocks = void 0;
      this.ref.layout = void 0;
    }
    this.rowGroup.rows.map((row) => row.clearRefs());
  }
  insertStyleSheet() {
    this.traverse((node) => {
      if (node instanceof Layout) {
        document.adoptedStyleSheets.push(node.sheet);
      }
    });
  }
  removeStyleSheet() {
    this.traverse((node) => {
      if (node instanceof Layout) {
        const index = document.adoptedStyleSheets.indexOf(node.sheet);
        if (index >= 0) {
          document.adoptedStyleSheets.splice(index, 1);
        }
      }
    });
  }
  mountElement(parent) {
    if (!this.element) {
      this.updateHTMLElement();
    }
    this.updateRefs();
    parent.appendChild(this.element);
    this.insertStyleSheet();
  }
  unmountElement() {
    if (this.element) {
      this.element.remove();
      this.clearRefs();
      this.removeStyleSheet();
    }
  }
}

const div = HTML.div;
const isCellContent = (object) => {
  return object instanceof Layout || object.computedHeight && object.computedWidth;
};
const cell = (style, content) => {
  const cell2 = new Cell();
  if (style && isCellContent(style)) {
    cell2.content = style;
  } else {
    style && cell2.setProps(style);
    cell2.content = content;
  }
  return cell2;
};
class Cell extends LayoutNode {
  constructor() {
    super();
  }
  static sizeUtil(width, height, rotation) {
    const hw = (width || 0) / 2;
    const hh = (height || 0) / 2;
    const bBox = new Box3(new Vector3(-hw, -hh, 0), new Vector3(hw, hh, 0));
    const rotMatrix = new Matrix4().makeRotationZ(rotation);
    bBox.applyMatrix4(rotMatrix);
    const size = bBox.getSize(new Vector3());
    return { bBox, width: size.x, height: size.y };
  }
  getNodes() {
    return this.content ? [this.content] : void 0;
  }
  setProps(props) {
    let key;
    for (key in props) {
      this[key] = props[key];
    }
    if (this.rotation) {
      this.bBox = Cell.sizeUtil(this.width || 0, this.height || 0, this.rotation).bBox;
    } else {
      this.bBox = void 0;
    }
  }
  getCol() {
    if (!this.row) {
      return;
    }
    const index = this.row.cells.indexOf(this);
    return this.row.layout?.colGroup.cols[index];
  }
  get x() {
    let result = this.getCol()?.x || 0;
    if (!result && this.row) {
      const index = this.row.cells.indexOf(this);
      result = 0;
      for (let i = 0; i < index; i++) {
        result += this.row.cells[i].selfHeight;
      }
    }
    const justifySelf = this.justifySelf || this.row?.layout?.justifyItems;
    if (justifySelf === "end") {
      result += this.computedWidth - this.selfWidth;
    } else if (justifySelf === "center") {
      result += (this.computedWidth - this.selfWidth) / 2;
    }
    return result;
  }
  get y() {
    let result = this.row?.y || 0;
    const alignSelf = this.alignSelf || this.row?.layout?.alignItems;
    if (alignSelf === "end") {
      result -= this.computedHeight - this.selfHeight;
    } else if (alignSelf === "center") {
      result -= (this.computedHeight - this.selfHeight) / 2;
    }
    return result;
  }
  get computedHeight() {
    return this.row?.computedHeight || this.selfHeight;
  }
  get computedWidth() {
    return this.getCol()?.computedWidth || this.selfWidth;
  }
  get selfHeight() {
    const scale = this.scale || 1;
    let height = this.height || 0;
    if (this.bBox) {
      height = this.bBox.max.y - this.bBox.min.y;
    }
    return Math.max(this.content?.computedHeight || 0, height * scale);
  }
  get selfWidth() {
    const scale = this.scale || 1;
    let width = this.width || 0;
    if (this.bBox) {
      width = this.bBox.max.x - this.bBox.min.x;
    }
    return Math.max(this.content?.computedWidth || 0, width * scale);
  }
  update() {
    this.content instanceof Layout && this.content.update();
  }
  getCell(...indices) {
    indices.shift();
    if (this.content && this.content.getCell) {
      return this.content.getCell(...indices);
    }
    return this;
  }
  getBlocks(blokType, blocks) {
    if (this.content && this.content.updateBlocks) {
      const contentBlocks = this.content.updateBlocks(blokType, blocks);
      contentBlocks.forEach((block2) => block2.setLayoutTransform(block2.layoutX + this.x, block2.layoutY + this.y, block2.layoutRotation));
      return contentBlocks;
    }
    let block;
    block = this.id && blocks?.get(this.id) || this.block;
    if (block) {
      block.width = this.selfWidth;
      block.height = this.selfHeight;
    } else {
      block = new blokType(this.selfWidth, this.selfHeight, this.id || "");
      this.id && blocks && blocks.set(this.id, block);
    }
    block.setLayoutTransform(this.x, this.y, this.rotation || 0);
    this.block = block;
    return block;
  }
  getHTMLElement() {
    const style = {
      alignSelf: this.alignSelf,
      justifySelf: this.justifySelf,
      backgroundColor: this.backgroundColor || this.row?.backgroundColor,
      width: this.selfWidth / this.computedWidth * 100 + "%",
      height: this.selfHeight / this.computedHeight * 100 + "%"
    };
    const attributes = { id: this.id, style, className: this.className };
    const children = this.content?.updateHTMLElement?.();
    const element = children ? div(attributes, ...Array.isArray(children) ? children : [children]) : div(attributes);
    this.element = element;
    return element;
  }
  updateRefs() {
    if (this.ref) {
      this.ref.current = this.element;
      this.ref.block = this.block;
      this.ref.cell = this;
    }
    this.content?.updateRefs();
  }
  clearRefs() {
    if (this.ref) {
      this.ref.current = null;
      this.ref.block = void 0;
      this.ref.cell = void 0;
    }
    this.content?.clearRefs();
  }
}

const MIRenderMatrixPartitions = ["UpperLeft", "UpperMiddle", "UpperRight", "MiddleLeft", "MiddleMiddle", "MiddleRight", "LowerLeft", "LowerMiddle", "LowerRight"];
const getImageMatrix = (groupedImageProps) => {
  const images = [];
  for (let i = 0; i < 3; i++) {
    const row2 = [];
    images.push(row2);
    for (let j = 0; j < 3; j++) {
      const partitions = MIRenderMatrixPartitions[i * 3 + j];
      row2.push(groupedImageProps[partitions].map((item) => ({ ...item })));
    }
  }
  return images;
};
const css = `.viewport { border-radius: 8px } .viewport:hover { box-shadow: 0 0 0 2px var(--purpleflat) }`;
const getImageScale = (props) => {
  const pixelSpacing = props.pixelSpacing?.xMmPerPixel;
  if (typeof pixelSpacing === "number" && Number.isFinite(pixelSpacing) && pixelSpacing > 0) {
    return pixelSpacing;
  }
  const metaScale = props.meta?.scale;
  if (metaScale && typeof metaScale === "object" && !Array.isArray(metaScale)) {
    const x = metaScale.X;
    if (typeof x === "number" && Number.isFinite(x) && x > 0) {
      return x;
    }
  }
  return 1;
};
const getImageSize = (props, sizeMode) => {
  const scale = sizeMode === "millimeters" ? getImageScale(props) : 1;
  return {
    width: props.originalSize.width * scale,
    height: props.originalSize.height * scale
  };
};
const gapSize = (props, sizeMode) => {
  let min = Infinity;
  props.flat(2).forEach((item) => {
    const { width, height } = getImageSize(item, sizeMode);
    const minSize = Math.min(width, height);
    if (minSize < min) {
      min = minSize;
    }
  });
  return Math.min(60, min * 0.1);
};
const FMXMatrixLayout = (props, domRefs, specialImageProps, layoutOptions, cssRule = css, sizeMode = "pixels") => {
  const gap = gapSize(props, sizeMode);
  return layout(
    { gap: layoutOptions?.gap ?? gap, zIndex: 2, cssRule, id: "matrix" },
    ...props.map((rowData, rowIndex, rows) => row(
      ...rowData.map((imageBlock, imageBlockIndex, cells) => cell(
        {
          alignSelf: rowIndex === 0 ? "end" : rowIndex === rows.length - 1 ? "start" : "center",
          justifySelf: imageBlockIndex === 0 ? "end" : imageBlockIndex === cells.length - 1 ? "start" : "center"
        },
        layout(
          { alignItems: rowIndex === 0 ? "end" : rowIndex === rows.length - 1 ? "start" : "center", gap: layoutOptions?.contentGap },
          row(
            ...imageBlock.map((imageProps) => {
              const { width, height } = getImageSize(imageProps, sizeMode);
              return cell({
                id: imageProps.ImageID,
                className: "viewport",
                width,
                height,
                scale: specialImageProps?.[imageProps.ImageID]?.scale,
                rotation: imageProps.OrientationAngle * Math.PI / 180,
                ref: domRefs?.getViewportRef(imageProps.ImageID),
                stretch: imageProps.stretch
              });
            })
          )
        )
      ))
    ))
  );
};
const MIRenderCropLayout = (props, api) => {
  const minDim = Math.min(
    ...props.map((p) => Math.min((p.width || 0) * (p.scale ?? 1), (p.height || 0) * (p.scale ?? 1)))
  );
  const gap = Number.isFinite(minDim) && minDim > 0 ? Math.min(60, minDim * 0.1) : 30;
  return layout(
    { gap, zIndex: 2, id: "crop" },
    row(
      ...props.map((cellProp) => cell({ ...cellProp, ref: api.getViewportRef(cellProp.id) }))
    )
  );
};

const parseDicom = (byteArray) => {
  const dataSet = dicomParser.parseDicom(byteArray);
  const pixelData = dataSet.elements.x7fe00010;
  const width = dataSet.int16("x00280011");
  const height = dataSet.int16("x00280010");
  const wc = dataSet.floatString("x00281050");
  const ww = dataSet.floatString("x00281051");
  const dicomPixelSpacing = dataSet.string("x00280030")?.split("\\").map(Number);
  const pixelSpacing = dicomPixelSpacing?.length === 2 ? {
    // DICOM (0028,0030) stores row spacing before column spacing.
    xMmPerPixel: dicomPixelSpacing[1],
    yMmPerPixel: dicomPixelSpacing[0]
  } : void 0;
  const bitsAllocated = dataSet.uint16("x00280100");
  if (bitsAllocated === void 0) {
    throw new Error("Missing required DICOM attribute BitsAllocated (0028,0100)");
  }
  const pixelRepresentation = dataSet.uint16("x00280103");
  if (pixelRepresentation === void 0) {
    throw new Error("Missing required DICOM attribute PixelRepresentation (0028,0103)");
  }
  let data;
  const expectedLength = width * height * (bitsAllocated === 16 ? 2 : 1);
  if (pixelData.length !== expectedLength) {
    throw new Error(`Unexpected pixel data length: ${pixelData.length}, expected: ${expectedLength}`);
  }
  if (bitsAllocated === 8) {
    const pixels = pixelRepresentation === 0 ? new Uint8Array(dataSet.byteArray.buffer, pixelData.dataOffset, pixelData.length) : new Int8Array(dataSet.byteArray.buffer, pixelData.dataOffset, pixelData.length);
    data = pixelRepresentation === 0 ? new Uint16Array(pixels) : new Int16Array(pixels);
  } else if (bitsAllocated === 16) {
    const bytesPerPixel = 2;
    const pixelCount = pixelData.length / bytesPerPixel;
    data = pixelRepresentation === 0 ? new Uint16Array(dataSet.byteArray.buffer, pixelData.dataOffset, pixelCount) : new Int16Array(dataSet.byteArray.buffer, pixelData.dataOffset, pixelCount);
  } else {
    throw new Error(`Unsupported bits allocated: ${bitsAllocated}`);
  }
  return { width, height, ww, wc, pixelSpacing, data };
};

const SUPPORTS_FLOAT_TYPE = typeof document !== "undefined" ? !!document.createElement("canvas").getContext("webgl2")?.getExtension("OES_texture_float_linear") : true;
class DicomTexture extends DataTexture {
  constructor(data, width, height, window, pixelSpacing) {
    const format = data.length / (width * height) === 1 ? RedFormat : RGBAFormat;
    let max = data[0];
    let min = data[0];
    for (let i = 1; i < data.length; i++) {
      if (data[i] > max) max = data[i];
      if (data[i] < min) min = data[i];
    }
    if (!window) {
      window = {
        ww: max - min,
        wc: min + (max - min) / 2
      };
    }
    let textureData;
    let textureType;
    let adjustedWindow;
    if (!SUPPORTS_FLOAT_TYPE) {
      const uint8Data = new Uint8Array(data.length);
      const range = max - min;
      for (let i = 0; i < data.length; i++) {
        const normalized = range > 0 ? (data[i] - min) / range * 255 : 0;
        uint8Data[i] = Math.max(0, Math.min(255, Math.round(normalized)));
      }
      textureData = uint8Data;
      textureType = UnsignedByteType;
      adjustedWindow = {
        ww: window.ww / range * 255,
        wc: (window.wc - min) / range * 255
      };
    } else {
      textureData = data;
      textureType = FloatType;
      adjustedWindow = window;
    }
    super(textureData, width, height, format, textureType);
    this.isNormalized = !SUPPORTS_FLOAT_TYPE;
    if (!SUPPORTS_FLOAT_TYPE) {
      this.originalMin = min;
      this.originalMax = max;
    }
    this.ww = adjustedWindow.ww;
    this.wc = adjustedWindow.wc;
    this.pixelSpacing = pixelSpacing;
    this.magFilter = LinearFilter;
    this.minFilter = LinearFilter;
    this.flipY = true;
  }
  get width() {
    return this.image.width;
  }
  get height() {
    return this.image.height;
  }
}

const promiseWithResolvers = () => {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return {
    promise,
    resolve,
    reject
  };
};

async function sleep(milliseconds) {
  await new Promise((res) => setTimeout(res, milliseconds));
}

const STORAGE_RETRY_ATTEMPTS = 5;
const STORAGE_RETRY_BASE_DELAY_MS = 300;
const STORAGE_RETRY_MAX_DELAY_MS = 5e3;
const RETRYABLE_STORAGE_STATUSES = /* @__PURE__ */ new Set([408, 429, 500, 502, 503, 504]);
const isRetryableStorageStatus = (status) => RETRYABLE_STORAGE_STATUSES.has(status);
const parseRetryAfterMs = (retryAfter) => {
  const value = retryAfter?.trim();
  if (!value) {
    return void 0;
  }
  if (/^\d+$/.test(value)) {
    return Number(value) * 1e3;
  }
  const retryAt = Date.parse(value);
  return Number.isNaN(retryAt) ? void 0 : Math.max(retryAt - Date.now(), 0);
};
const getStorageRetryDelayMs = ({
  attempt,
  retryAfter
}) => {
  if (attempt >= STORAGE_RETRY_ATTEMPTS) {
    return null;
  }
  const backoffMs = Math.random() * Math.min(
    STORAGE_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1),
    STORAGE_RETRY_MAX_DELAY_MS
  );
  const retryAfterMs = parseRetryAfterMs(retryAfter);
  if (retryAfterMs === void 0) {
    return backoffMs;
  }
  return retryAfterMs > STORAGE_RETRY_MAX_DELAY_MS ? null : Math.max(backoffMs, retryAfterMs);
};
const waitForStorageRetry = (delayMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(signal.reason);
    return;
  }
  const handleAbort = () => {
    clearTimeout(timer);
    reject(signal?.reason);
  };
  const timer = setTimeout(() => {
    signal?.removeEventListener("abort", handleAbort);
    resolve();
  }, delayMs);
  signal?.addEventListener("abort", handleAbort, { once: true });
});
const fetchStorageAttempt = async ({
  input,
  init,
  attempt,
  onRetry
}) => {
  const { response, error } = await fetch(input, init).then(
    (fetchedResponse) => ({ response: fetchedResponse }),
    (fetchError) => ({ error: fetchError })
  );
  if (response && !isRetryableStorageStatus(response.status)) {
    return response;
  }
  const delayMs = init.signal?.aborted ? null : getStorageRetryDelayMs({
    attempt,
    retryAfter: response?.headers.get("Retry-After")
  });
  if (delayMs === null) {
    if (response) {
      return response;
    }
    throw error;
  }
  await response?.body?.cancel();
  onRetry?.({ attempt, delayMs, status: response?.status, error });
  await waitForStorageRetry(delayMs, init.signal);
  return fetchStorageAttempt({ input, init, attempt: attempt + 1, onRetry });
};
const fetchWithStorageRetry = (input, init = {}, { onRetry } = {}) => fetchStorageAttempt({ input, init, attempt: 1, onRetry });

const logStorageRetry = (source, url) => ({ attempt, delayMs, status, error }) => console.warn(
  `[${source}] storage request retry. attempt=${attempt} delay_ms=${Math.round(delayMs)} status=${status ?? "none"} error=${error instanceof Error ? error.message : "none"} url=${url.split("?")[0]}`
);

const isDICOM = (buffer) => buffer[128] === 68 && buffer[129] === 73 && buffer[130] === 67 && buffer[131] === 77;
const isTIFF = (buffer) => buffer[0] === 73 && buffer[1] === 73 && buffer[2] === 42 && buffer[3] === 0 || buffer[0] === 77 && buffer[1] === 77 && buffer[2] === 0 && buffer[3] === 42;
const isJPG = (buffer) => buffer[0] === 255 && buffer[1] === 216 && buffer[2] === 255;
class ImageLoader {
  constructor() {
    this.images = {};
    this.promises = /* @__PURE__ */ new Map();
    this.credentials = "same-origin";
  }
  async get(url, cb) {
    if (Array.isArray(url)) {
      return Promise.all(url.map((url2) => this.getOneImage(url2, cb)));
    } else {
      return this.getOneImage(url, cb);
    }
  }
  setWithCredentials(value) {
    this.credentials = value ? "include" : "same-origin";
  }
  async getOneImage(url, cb) {
    let image;
    let promise = this.promises.get(url);
    if (promise) {
      image = await promise;
    } else {
      image = this.images[url];
      if (!image) {
        promise = this.load(url);
        if (!promise) return;
        this.promises.set(url, promise);
        try {
          image = await promise;
          this.images[url] = image;
        } finally {
          this.promises.delete(url);
          !image && this.tryToResolveWaitForLoaded();
        }
      }
    }
    if (cb) {
      cb(url, image);
    }
    this.tryToResolveWaitForLoaded();
    return image;
  }
  tryToResolveWaitForLoaded() {
    sleep(0).then(() => {
      if (this.waitForLoaded && !this.promises.size) {
        this.waitForLoaded.resolve();
        this.waitForLoaded = void 0;
      }
    });
  }
  async load(url) {
    const response = await this.tryToFetch(url);
    const arrayBuffer = await response.arrayBuffer();
    const array = new Uint8Array(arrayBuffer);
    if (isDICOM(array)) {
      const { width, height, ww, wc, data, pixelSpacing } = parseDicom(array);
      const texture = new DicomTexture(new Float32Array(data), width, height, ww && wc ? { ww, wc } : void 0, pixelSpacing);
      texture.needsUpdate = true;
      return texture;
    } else if (isTIFF(array)) {
      const image = new Tiff({ buffer: arrayBuffer });
      const pixelsBuffer = image.readRGBAImage();
      const width = image.width();
      const height = image.height();
      const size = width * height * 4;
      image.close();
      if (pixelsBuffer.byteLength === size) {
        const data = new Uint8ClampedArray(pixelsBuffer);
        const texture = new Texture(new ImageData(data, width, height));
        texture.flipY = true;
        texture.needsUpdate = true;
        return texture;
      } else {
        return Promise.reject(`[ImageLoader] Failed to parse TIFF image - unexpected pixel buffer size. Expected ${size} bytes but got ${pixelsBuffer.byteLength} bytes. URL: ${url}`);
      }
    } else if (isJPG(array)) {
      if (typeof window === "undefined") {
        const { data, width, height } = jpeg.decode(array);
        return new DataTexture(data, width, height);
      } else {
        const blob = new Blob([array]);
        const url2 = URL.createObjectURL(blob);
        try {
          const image = new Image();
          image.src = url2;
          await image.decode();
          const texture = new Texture(image);
          texture.needsUpdate = true;
          return texture;
        } catch (e) {
          return Promise.reject(`[ImageLoader] Error creating image bitmap. URL ${url2}`);
        } finally {
          URL.revokeObjectURL(url2);
        }
      }
    } else {
      const blob = new Blob([new Uint8Array(arrayBuffer)]);
      try {
        const bitmap = await createImageBitmap(blob, { imageOrientation: "flipY" });
        const texture = new Texture(bitmap);
        texture.needsUpdate = true;
        return texture;
      } catch (e) {
        return Promise.reject(`[ImageLoader] Error creating image bitmap. URL ${url}`);
      }
    }
  }
  async tryToFetch(url) {
    const response = await fetchWithStorageRetry(
      url,
      { credentials: this.credentials },
      { onRetry: logStorageRetry("ImageLoader", url) }
    );
    if (!response.ok) {
      console.error(`[ImageLoader] fetching failed. Status ${response.status}. URL ${url}`);
      return Promise.reject(`[ImageLoader] All attempts to fetch image have failed. URL ${url}`);
    }
    return response;
  }
  waitForLoadingComplete() {
    if (!this.waitForLoaded && this.promises.size) {
      this.waitForLoaded = promiseWithResolvers();
      return this.waitForLoaded.promise;
    }
    return Promise.resolve();
  }
  clearCache() {
    Object.values(this.images).forEach((image) => image.dispose());
    this.images = {};
    this.promises.clear();
    this.waitForLoaded = void 0;
  }
}
var ImageLoader$1 = new ImageLoader();

new OrthographicCamera();
class Picture {
  constructor(container, imageElement) {
    this.container = new WeakRef(container);
    this.imageElement = new WeakRef(imageElement);
  }
  setImageContainer(container) {
    this.imageContainer = container;
  }
  dispose() {
    this.imageElement.deref()?.remove();
  }
  derefImage() {
    return this.imageElement.deref();
  }
  getHeight() {
    const heightMatch = this.imageElement.deref()?.style.height.match(/\d+/)?.[0];
    return heightMatch ? parseInt(heightMatch) : void 0;
  }
  getWidth() {
    const widthMatch = this.imageElement.deref()?.style.width.match(/\d+/)?.[0];
    return widthMatch ? parseInt(widthMatch) : void 0;
  }
}

class CroppedPicture extends Picture {
  constructor(box, container, style) {
    const canvas = HTML.canvas(style);
    super(container, canvas);
    container.appendChild(canvas);
    this.box = new Box3(new Vector3(box.X?.Min || 0, -(box.Y?.Max || 0), 0), new Vector3(box.X?.Max, -(box.Y?.Min || 0), 0));
  }
  derefImage() {
    return this.imageElement.deref();
  }
  update() {
    const canvas = this.imageElement.deref();
    if (!canvas) return;
    if (this.imageContainer.getImage().object3d.material.uniforms.dataTexture.value.image) {
      const context = canvas.getContext("2d");
      const imageData = toImageData(this.imageContainer, "main", { width: this.getWidth(), height: this.getHeight() }, this.box);
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      context.putImageData(imageData, 0, 0);
    }
  }
}
const camera = new OrthographicCamera();
let reserveRenderer;
const toImageData = (imageContainer, viewName, outputSize, box3) => {
  let renderer = MIRender.getView(viewName)?.getRenderer() || reserveRenderer;
  if (!renderer) {
    console.error(`[toImageData] renderer for ${viewName} is not defined`);
    reserveRenderer = renderer = new WebGLRenderer();
  }
  const image = imageContainer.getImage();
  let imageSize = fit(camera, image, box3, true);
  const outputWidth = Math.max(outputSize?.width || outputSize?.minWidth || NaN, outputSize?.minWidth || outputSize?.width || NaN);
  const outputHeight = Math.max(outputSize?.height || outputSize?.minHeight || NaN, outputSize?.minHeight || outputSize?.height || NaN);
  const scaleW = outputWidth / imageSize.x;
  const scaleH = outputHeight / imageSize.y;
  const scale = Math.min(scaleW, scaleH) || scaleW || scaleH || 1;
  const width = Math.round(imageSize.x * scale);
  const height = Math.round(imageSize.y * scale);
  const pixels = new Uint8ClampedArray(width * height * 4);
  const target = new WebGLRenderTarget(width, height);
  renderer.setViewport(0, 0, width, height);
  renderer.setClearColor(0, 0);
  renderer.setRenderTarget(target);
  renderer.render(imageContainer.object3d, camera);
  renderer.readRenderTargetPixels(target, 0, 0, width, height, pixels);
  renderer.setRenderTarget(null);
  return new ImageData(pixels, width, height);
};
const fit = (camera2, image, box3, flipY = true) => {
  const size = new Vector3();
  if (box3) {
    image.object3d.parent?.updateMatrixWorld();
    const box = box3.clone();
    box.applyMatrix4(image.object3d.parent.matrixWorld);
    box.getCenter(camera2.position);
    size.subVectors(box.max, box.min);
  } else {
    image.object3d.updateWorldMatrix(true, false);
    const x = new Vector3().setFromMatrixColumn(image.object3d.matrixWorld, 0);
    const y = new Vector3().setFromMatrixColumn(image.object3d.matrixWorld, 1);
    const diagonalA = x.clone().add(y);
    const diagonalB = x.clone().sub(y);
    size.set(Math.max(Math.abs(diagonalA.x), Math.abs(diagonalB.x)), Math.max(Math.abs(diagonalA.y), Math.abs(diagonalB.y)), 0);
    const box = image.object3d.geometry.boundingBox.clone();
    box.applyMatrix4(image.object3d.matrixWorld);
    box.getCenter(camera2.position);
  }
  const cameraHeight = 600;
  const cameraWidth = 600 * size.x / size.y;
  camera2.scale.y = flipY ? -1 : 1;
  camera2.layers.enableAll();
  camera2.near = -10;
  camera2.far = 10;
  camera2.left = -0.5 * cameraWidth;
  camera2.right = 0.5 * cameraWidth;
  camera2.top = 0.5 * cameraHeight;
  camera2.bottom = -0.5 * cameraHeight;
  camera2.zoom = Math.min(cameraHeight / size.y, cameraWidth / size.x);
  camera2.updateMatrix();
  camera2.updateProjectionMatrix();
  return size;
};

const coreEventNames = [
  "transformCameraStart",
  "transformCameraEnd",
  "transformCamera",
  "add",
  "delete",
  "annotationsUserDelete",
  "addLayer",
  "deleteLayer",
  "ActiveViewportWillChange",
  "ActiveViewportChanged",
  "hover",
  "unhover",
  "select",
  "deselect",
  "render",
  "transform",
  "transformStart",
  "transformEnd",
  "contextMenuOpen",
  "contextMenuClose",
  "modeChanged",
  "visibility",
  "viewportSchemeChanged",
  "geometryChanged",
  "materialChanged",
  "setRenderingStyle",
  "makeScreenshot",
  "viewportAdded",
  "viewportRemoved"
];

const FullHeadMPRUIEventNames = [
  "fullHeadMPRImageOptionsChanged",
  "fullHeadMPRAnnotationsChanged",
  "fullHeadMPRAnnotationsPreview",
  "fullHeadMPRReady",
  "fullHeadMPRPresentationReady",
  "fullHeadMPRActivePaneChanged",
  "fullHeadMPRPaneLayoutChanged",
  "fullHeadMPRViewUpdated",
  "fullHeadMPRBackendChanged",
  "fullHeadMPRBenchmarkUpdated",
  "fullHeadMPRFocusChanged",
  "fullHeadMPRToothModeChanged",
  "fullHeadMPRViewportRendered",
  "fullHeadMPRChunkLoadProgress",
  "fullHeadMPRMetricsUpdated",
  "fullHeadMPRError"
];
const FullHeadMPREventNames = [
  ...coreEventNames,
  ...FullHeadMPRUIEventNames
];

class ToolsController {
  constructor(hostObject) {
    this.hostObject = hostObject;
    this.tools = /* @__PURE__ */ new Map();
    this.enable = false;
  }
  init() {
    if (!this.enable) {
      this.enable = true;
      this.initServiceTools();
      this.activeMode?.init();
    }
  }
  dispose() {
    if (this.enable) {
      this.disposeServiceTools();
      this.tools?.forEach((tool) => tool.dispose());
      this.activeMode?.dispose();
      this.activeMode = void 0;
      this.enable = false;
    }
  }
  clear() {
    this.dispose();
    this.serviceTools = void 0;
    this.modes = void 0;
    this.tools.clear();
  }
  setServiceTools(toolTypes) {
    this.serviceTools = /* @__PURE__ */ new Set();
    toolTypes.forEach((toolType) => this.serviceTools.add(new toolType(this.hostObject)));
  }
  initServiceTools() {
    this.serviceTools?.forEach((tool) => tool.init());
  }
  disposeServiceTools() {
    this.serviceTools?.forEach((tool) => tool.dispose());
  }
  setTools(tools) {
    let toolInstance;
    tools.forEach((tool) => {
      toolInstance = new tool(this.hostObject);
      if (!this.tools.has(tool)) {
        this.tools.set(tool, toolInstance);
      }
    });
  }
  getTools() {
    return this.tools;
  }
  getTool(name) {
    return this.tools?.get(name);
  }
  getToolByName(name) {
    for (const [key, value] of this.tools) {
      if (key.name === name) return value;
    }
  }
  setModes(modes) {
    if (!this.modes) {
      this.modes = modes;
      Object.keys(modes).forEach(
        (modeName) => modes[modeName].setHostObject(this.hostObject, modeName)
      );
    }
  }
  activateMode(name) {
    if (this.modes && this.activeMode !== this.modes[name]) {
      this.enable && this.modes[name].init();
      this.activeMode = this.modes[name];
    }
  }
  getModeName() {
    return this.activeMode?.getName();
  }
  getModes() {
    return this.modes;
  }
  getMode() {
    return this.activeMode;
  }
}

class Container {
  constructor(hostObject, objects) {
    this.hostObject = hostObject;
    this.objects = objects;
  }
}

class LayerContainer extends Container {
  constructor(layer, objects) {
    super(layer, objects);
    this.objects = objects;
    this.components = /* @__PURE__ */ new Map();
    this.deleteOne = this.deleteOne.bind(this);
    this.addOne = this.addOne.bind(this);
  }
  size() {
    return this.objects.length;
  }
  add(entity) {
    if (Array.isArray(entity)) {
      const added = entity.filter(this.addOne);
      if (added.length) {
        this.hostObject.getView().dispatchEvent({ type: "add", entities: [...entity], container: this.hostObject });
      }
    } else {
      if (this.addOne(entity)) {
        this.hostObject.getView().dispatchEvent({ type: "add", entities: [entity], container: this.hostObject });
      }
    }
  }
  delete(entity) {
    if (Array.isArray(entity)) {
      const deleted = entity.filter(this.deleteOne);
      if (deleted.length) {
        this.hostObject.getGroup().remove(...entity.map((item) => item.object3d));
        this.hostObject.getView().dispatchEvent({ type: "delete", entities: deleted, container: this.hostObject });
      }
    } else {
      if (this.objects.includes(entity)) {
        if (this.deleteOne(entity)) {
          this.hostObject.getGroup().remove(entity.object3d);
          this.hostObject.getView().dispatchEvent({ type: "delete", entities: [entity], container: this.hostObject });
        }
      }
    }
  }
  addOne(entity) {
    if (entity.getLayer() !== this.hostObject) {
      this.objects.push(entity);
      entity.setLayer(this.hostObject);
      this.hostObject.getGroup().add(entity.object3d);
      entity.components.forEach((value, key) => {
        const set = this.components.get(key);
        if (set) {
          set.add(value);
        } else {
          this.components.set(key, /* @__PURE__ */ new Set([value]));
        }
      });
      return true;
    }
    return false;
  }
  deleteOne(entity) {
    const index = this.objects.indexOf(entity);
    if (index >= 0) {
      this.objects.splice(index, 1);
      entity.setLayer(void 0);
      entity.components.forEach((value, key) => {
        const set = this.components.get(key);
        if (set) {
          set.delete(value);
        }
      });
      return true;
    }
    return false;
  }
}

class Layer {
  constructor(view, name, objects = []) {
    this.getObjectByName = (name, force = false) => {
      if (!(force || this.on && !this.lock)) return;
      return this.container.objects.find((entity) => entity.object3d.name === name);
    };
    this.objects = objects;
    this.on = true;
    this.lock = false;
    this.view = view;
    this.name = name;
    this.group = new Group();
    this.group.name = name;
    this.container = new LayerContainer(this, objects);
  }
  getGroup() {
    return this.group;
  }
  getObjects(force = false) {
    if (force || this.on && !this.lock) {
      return this.objects;
    }
    return [];
  }
  getComponent(type) {
    return this.container.components.get(type);
  }
  has(obj) {
    return this.objects.includes(obj);
  }
  add(object) {
    this.container.add(object);
  }
  delete(object) {
    this.container.delete(object);
  }
  clear() {
    this.group.remove(...this.objects.map((item) => item.object3d));
    const tmpObjects = [...this.objects];
    this.objects.length = 0;
    this.container.components.clear();
    tmpObjects.forEach((obj) => {
      obj.setLayer(void 0);
    });
    this.view.dispatchEvent({ type: "delete", entities: tmpObjects, container: this });
  }
  hide() {
    this.group.visible = false;
  }
  show() {
    this.group.visible = true;
  }
  getName() {
    return this.name;
  }
  getView() {
    return this.view;
  }
}

class CameraHelper {
  constructor(cameraController) {
    this.cameraController = cameraController;
  }
  init() {
    this.handleResize();
    this.cameraController.viewport.addEventListener("resize", this.handleResize, 10);
  }
  dispose() {
    App.Instance().removeEventListener("resize", this.handleResize);
  }
  getDistanceFactor() {
    return this.distanceFactor;
  }
  get viewport() {
    return this.cameraController.viewport;
  }
}

class OrthographicCameraHelper extends CameraHelper {
  constructor(cameraController) {
    super(cameraController);
    this.handleResize = () => {
      const sizeRatio = OrthographicCameraHelper.setSize(this.camera, this.viewport.width, this.viewport.height, this.frustumSize);
      if (sizeRatio) {
        this.sizeRatio = sizeRatio;
      }
    };
    this.frustumSize = 600;
    this.sizeRatio = 1;
    this.updateDistanceFactor();
  }
  get camera() {
    return this.cameraController.getOrthographicCamera();
  }
  getSizeRatio() {
    return this.sizeRatio;
  }
  static setSize(camera, width, height, frustumSize) {
    if (width === 0 || height === 0) {
      return;
    }
    const sizeRatio = frustumSize / height;
    const aspect = width / height;
    camera.left = -0.5 * frustumSize * aspect;
    camera.right = 0.5 * frustumSize * aspect;
    camera.top = frustumSize / 2;
    camera.bottom = -frustumSize / 2;
    camera.updateProjectionMatrix();
    return sizeRatio;
  }
  updateDistanceFactor() {
    return this.distanceFactor = 1;
  }
}

class PerspectiveCameraHelper extends CameraHelper {
  constructor(cameraController) {
    super(cameraController);
    this.offset = new Vector3();
    this.handleResize = () => {
      this.camera.aspect = this.viewport.width / this.viewport.height;
      this.camera.updateProjectionMatrix();
      this.updateSubDistCoefficient();
      this.updateDistanceFactor();
    };
    this.updateDistanceFactor();
  }
  get camera() {
    return this.cameraController.getPerspectiveCamera();
  }
  updateSubDistCoefficient() {
    this.subCoefficient = 2 * Math.tan(this.camera.fov / 2 * Math.PI / 180) / this.viewport.height;
  }
  updateDistanceFactor() {
    return this.distanceFactor = this.offset.copy(this.cameraController.getPivotPoint()).sub(this.camera.position).length() * this.subCoefficient;
  }
}

const initialProps = {
  perspective: {},
  orthographic: { left: -1, right: 1, top: 1, bottom: -1, near: -1e3, far: 1e3 },
  zoom: 1,
  matrix: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
  type: "camera-controller",
  key: "",
  up: [0, 1, 0],
  pivotPoint: [0, 0, 0]
};
class CameraController {
  constructor(viewport, props = initialProps) {
    this.viewport = viewport;
    var { fov, aspect, near, far } = props.perspective || {};
    this.perspectiveCamera = new PerspectiveCamera(fov, aspect, near, far);
    var { left, right, top, bottom, near, far } = props.orthographic || initialProps.orthographic;
    this.orthographicCamera = new OrthographicCamera(left, right, top, bottom, near, far);
    const matrix = props.matrix ? props.matrix : initialProps.matrix;
    this.perspectiveCamera.matrix.set(...matrix);
    this.perspectiveCamera.matrixWorldNeedsUpdate = true;
    this.orthographicCamera.matrix.set(...matrix);
    this.orthographicCamera.matrixWorldNeedsUpdate = true;
    if (props.up) {
      this.perspectiveCamera.up.set(...props.up).normalize();
      this.orthographicCamera.up.set(...props.up).normalize();
    }
    this.pivotPoint = new Vector3();
    this.cameraX = new Vector3();
    this.cameraY = new Vector3();
    this.cameraZ = new Vector3();
    this.perspectiveCameraHelper = new PerspectiveCameraHelper(this);
    this.orthographicCameraHelper = new OrthographicCameraHelper(this);
    this.orthographic();
    this.onTransform = this.onTransform.bind(this);
  }
  init() {
    this.cameraHelper.init();
    this.viewport.view.addEventListener("transformCamera", this.onTransform, 10);
  }
  onTransform(event) {
    if (event.viewport === this.viewport) {
      this.camera.updateMatrixWorld();
    }
  }
  updatePivotPoint(point, fireEvent = true) {
    this.pivotPoint.copy(point);
    this.viewport.dispatchEvent({ type: "updatePivotPoint", point: this.pivotPoint, fireEvent });
  }
  getPivotPoint() {
    return this.pivotPoint;
  }
  dispose() {
    this.perspectiveCameraHelper.dispose();
    this.orthographicCameraHelper.dispose();
  }
  saveState() {
    this.savedState = this.toJson();
  }
  setPosition(v, y, z) {
    this.camera.matrix.setPosition(v, y, z);
  }
  updateMatrix(matrix) {
    this.camera.matrix.copy(matrix);
    this.camera.matrixWorldNeedsUpdate = true;
  }
  orthographic() {
    this.camera = this.orthographicCamera;
    this.cameraHelper = this.orthographicCameraHelper;
    this.perspectiveCameraHelper.dispose();
    this.orthographicCameraHelper.init();
    this.getDistanceFactor = () => this.orthographicCameraHelper.updateDistanceFactor();
    this.viewport.dispatchEvent({
      type: "cameraChanged",
      previous: this.perspectiveCamera,
      current: this.orthographicCamera
    });
  }
  perspective() {
    this.camera = this.perspectiveCamera;
    this.cameraHelper = this.perspectiveCameraHelper;
    this.orthographicCameraHelper.dispose();
    this.perspectiveCameraHelper.init();
    this.getDistanceFactor = () => this.perspectiveCameraHelper.updateDistanceFactor();
    this.viewport.dispatchEvent({
      type: "cameraChanged",
      previous: this.orthographicCamera,
      current: this.perspectiveCamera
    });
  }
  copyFromActive() {
    const camera = this.camera === this.orthographicCamera ? this.perspectiveCamera : this.orthographicCamera;
    camera.matrix.copy(this.camera.matrix);
    camera.matrix.decompose(camera.position, camera.quaternion, camera.scale);
    camera.layers = this.camera.layers;
  }
  getPerspectiveCamera() {
    return this.perspectiveCamera;
  }
  getOrthographicCamera() {
    return this.orthographicCamera;
  }
  get xDir() {
    return this.cameraX.setFromMatrixColumn(this.camera.matrix, 0);
  }
  get yDir() {
    return this.cameraY.setFromMatrixColumn(this.camera.matrix, 1);
  }
  get zDir() {
    return this.cameraZ.setFromMatrixColumn(this.camera.matrix, 2);
  }
  fromJson(props) {
    this.perspectiveCamera.matrix.fromArray(props.matrix);
    this.perspectiveCamera.matrix.decompose(this.perspectiveCamera.position, this.perspectiveCamera.quaternion, this.perspectiveCamera.scale);
    this.perspectiveCamera.matrixWorldNeedsUpdate = true;
    props.perspective.fov ? this.perspectiveCamera.fov = props.perspective.fov : void 0;
    props.perspective.aspect ? this.perspectiveCamera.aspect = props.perspective.aspect : void 0;
    props.perspective.near ? this.perspectiveCamera.near = props.perspective.near : void 0;
    props.perspective.far ? this.perspectiveCamera.far = props.perspective.far : void 0;
    this.perspectiveCamera.zoom = props.zoom;
    props.up ? this.perspectiveCamera.up.fromArray(props.up) : void 0;
    this.perspectiveCameraHelper.handleResize();
    this.orthographicCamera.matrix.fromArray(props.matrix);
    this.orthographicCamera.matrix.decompose(this.orthographicCamera.position, this.orthographicCamera.quaternion, this.orthographicCamera.scale);
    this.orthographicCamera.matrixWorldNeedsUpdate = true;
    this.orthographicCamera.left = props.orthographic.left;
    this.orthographicCamera.right = props.orthographic.right;
    this.orthographicCamera.top = props.orthographic.top;
    this.orthographicCamera.bottom = props.orthographic.bottom;
    props.orthographic.near ? this.orthographicCamera.near = props.orthographic.near : void 0;
    props.orthographic.far ? this.orthographicCamera.far = props.orthographic.far : void 0;
    this.orthographicCamera.zoom = props.zoom;
    props.up ? this.orthographicCamera.up.fromArray(props.up) : void 0;
    this.orthographicCameraHelper.handleResize();
    const pivotPoint = new Vector3().fromArray(props.pivotPoint);
    this.updatePivotPoint(pivotPoint);
    this.viewport.dispatchEvent({ type: "cameraChanged", previous: this.camera, current: this.camera });
  }
  toJson() {
    return {
      perspective: {
        fov: this.perspectiveCamera.fov,
        aspect: this.perspectiveCamera.aspect,
        near: this.perspectiveCamera.near,
        far: this.perspectiveCamera.far
      },
      orthographic: {
        left: this.orthographicCamera.left,
        right: this.orthographicCamera.right,
        top: this.orthographicCamera.top,
        bottom: this.orthographicCamera.bottom,
        near: this.orthographicCamera.near,
        far: this.orthographicCamera.far
      },
      zoom: this.camera.zoom,
      matrix: this.camera.matrix.toArray(),
      type: "camera-controller",
      key: this.camera.uuid ?? "",
      up: this.camera.up.toArray(),
      pivotPoint: this.getPivotPoint().toArray()
    };
  }
  fromSavedState() {
    if (this.savedState) {
      this.fromJson(this.savedState);
    } else {
      console.error("state is not saved");
    }
  }
}

class ViewportRenderer {
  constructor(viewport) {
    this.viewport = viewport;
  }
}
class StandardViewportRenderer extends ViewportRenderer {
  constructor(viewport, toneMapping = NoToneMapping, outputEncoding = LinearEncoding) {
    super(viewport);
    this.toneMapping = toneMapping;
    this.outputEncoding = outputEncoding;
  }
  render() {
    this.viewport.view.renderViewport(this.viewport, () => this.draw());
  }
  draw() {
    const r = this.viewport.view.getRenderer();
    r.setViewport(this.viewport.size);
    r.setScissor(this.viewport.size);
    r.setScissorTest(true);
    r.setClearColor(this.viewport.clearColor, this.viewport.alpha);
    const toneMappingOld = r.toneMapping;
    const outputEncodingOld = r.outputEncoding;
    r.toneMapping = this.toneMapping;
    r.outputEncoding = this.outputEncoding;
    this.gpuProbe?.begin();
    r.render(this.viewport.view.scene, this.viewport.camera);
    this.gpuProbe?.end();
    r.toneMapping = toneMappingOld;
    r.outputEncoding = outputEncodingOld;
  }
}

const viewportEventNames = ["cameraChanged", "zoomChanged", "updatePivotPoint", "resize", "reposition"];
class Viewport extends EventDispatcher {
  constructor(view, name, domElement, threeLayer = 0, {
    cameraControllerProps,
    eventNames,
    mutationObserverTarget
  } = {}) {
    super(eventNames || viewportEventNames);
    this.onMutateViewport = (() => {
      const cachedSize = new Vector4();
      return () => {
        this.handleResize();
        if (cachedSize.x !== this.size.x || cachedSize.y !== this.size.y) {
          this.dispatchEvent({ type: "reposition" });
          this.view.dispatchEvent({ type: "render" });
        }
        cachedSize.copy(this.size);
      };
    })();
    this.view = view;
    this.name = name;
    this.size = new Vector4();
    this.domElement = domElement;
    if (!this.domElement) console.error(`[Viewport] Reference ${name} was not found`);
    this.cameraController = new CameraController(this, cameraControllerProps);
    this.toolController = new ToolsController(this);
    this.rect = this.domElement.getBoundingClientRect();
    this.handleResize = this.handleResize.bind(this);
    this.onResizeViewport = this.onResizeViewport.bind(this);
    this.resizeObserver = new ResizeObserver(this.onResizeViewport);
    this.mutationObserver = new MutationObserver(this.onMutateViewport);
    this.enable = false;
    this.cameraLayerID = 0;
    this.setCameraLayer(threeLayer);
    this.clearColor = 0;
    this.alpha = 1;
    this.viewportRenderer = new StandardViewportRenderer(this);
    this.mutationObserverTarget = mutationObserverTarget;
  }
  init() {
    if (this.enable) return;
    this.resizeObserver.observe(this.domElement);
    this.mutationObserver.observe(this.mutationObserverTarget || this.view.getDomElement(), {
      attributeFilter: ["style", "class"],
      subtree: true,
      childList: true
    });
    this.handleResize();
    this.cameraController.init();
    this.toolController.init();
    const modeName = this.view.toolController.getModeName();
    if (modeName) {
      this.activateMode(modeName);
    }
    this.enable = true;
  }
  dispose() {
    if (!this.enable) return;
    this.requestID && cancelAnimationFrame(this.requestID);
    this.requestID = void 0;
    this.cameraController.dispose();
    this.toolController.dispose();
    this.resizeObserver.unobserve(this.domElement);
    this.mutationObserver.disconnect();
    this.enable = false;
  }
  setDomElement(domElement) {
    if (this.domElement === domElement) return;
    if (this.domElement) {
      this.resizeObserver.unobserve(this.domElement);
    }
    this.resizeObserver.observe(domElement);
    App.Instance().mouseController.redefineDomElement(domElement, this.domElement);
    this.domElement = domElement;
    this.handleResize();
  }
  getDomElement() {
    return this.domElement;
  }
  handleResize(contentBoxSize) {
    this.rect = this.domElement.getBoundingClientRect();
    const { left, bottom, width, height } = this.rect;
    const { height: parentHeight, top: parentTop, left: parentLeft } = this.view.getDomElement().getBoundingClientRect();
    this.size.set(left - parentLeft, parentHeight - bottom + parentTop, Math.ceil(contentBoxSize?.width ?? width), Math.ceil(contentBoxSize?.height ?? height));
  }
  onResizeViewport(e) {
    const contentBoxSize = e.find((x) => x.target === this.domElement)?.contentBoxSize[0];
    const width = contentBoxSize?.inlineSize;
    const height = contentBoxSize?.blockSize;
    this.handleResize({ width, height });
    this.dispatchEvent({ type: "resize" });
    this.view.dispatchEvent({ type: "render" });
  }
  get x() {
    return this.size.x;
  }
  set x(val) {
    this.size.x = val;
  }
  get y() {
    return this.size.y;
  }
  set y(val) {
    this.size.y = val;
  }
  get width() {
    return this.size.z;
  }
  set width(val) {
    this.size.z = val;
  }
  get height() {
    return this.size.w;
  }
  set height(val) {
    this.size.w = val;
  }
  get camera() {
    return this.cameraController.camera;
  }
  get cameraLayer() {
    return this.cameraLayerID;
  }
  setCameraLayer(layerID) {
    if (this.cameraLayerID === layerID) {
      return;
    }
    if (this.cameraLayerID > 0) {
      this.camera.layers.disable(this.cameraLayerID);
    }
    this.cameraLayerID = layerID;
    if (layerID > 0) {
      this.camera.layers.enable(layerID);
    }
  }
  activateMode(modeName) {
    this.toolController.activateMode(modeName);
  }
  renderImmediately() {
    this.viewportRenderer.render();
    this.requestID = void 0;
    this.waitForRender?.resolve();
    this.waitForRender = void 0;
  }
  render() {
    if (this.requestID !== void 0) return;
    this.requestID = requestAnimationFrame(() => this.renderImmediately());
  }
  isEnable() {
    return this.enable;
  }
  waitForRenderComplete() {
    if (this.requestID) {
      if (!this.waitForRender) {
        this.waitForRender = promiseWithResolvers();
      }
      return this.waitForRender.promise;
    } else {
      return Promise.resolve();
    }
  }
}

class SelectContainer extends Container {
  constructor(hostObject, objects) {
    super(hostObject, objects);
  }
  add(selectComponent) {
    if (Array.isArray(selectComponent)) {
      selectComponent.forEach((child) => {
        this.addOne(child);
      });
    } else {
      this.addOne(selectComponent);
    }
  }
  delete(selectComponent) {
    if (Array.isArray(selectComponent)) {
      selectComponent.forEach((child) => {
        this.deleteOne(child);
      });
    } else {
      this.deleteOne(selectComponent);
    }
  }
  size() {
    return this.objects.size;
  }
  addOne(selectComponent) {
    selectComponent.select();
    const layer = selectComponent.entity.getLayer();
    if (layer) {
      const selectables = this.objects.get(layer);
      if (selectables) {
        selectables.add(selectComponent);
      } else {
        this.objects.set(layer, /* @__PURE__ */ new Set([selectComponent]));
      }
    }
  }
  deleteOne(selectComponent) {
    selectComponent.deselect();
    const layer = selectComponent.entity.getLayer();
    if (layer) {
      const selectables = this.objects.get(layer);
      if (selectables) {
        selectables.delete(selectComponent);
      }
    }
  }
}

class SelectController {
  constructor(view) {
    this.objects = /* @__PURE__ */ new Map();
    this.container = new SelectContainer(this, this.objects);
    this.view = view;
  }
  getSelected(layer) {
    if (layer) {
      const selected = this.objects.get(layer);
      return selected ? [...selected] : [];
    } else {
      const selected = [];
      this.objects.forEach((objects) => {
        selected.push(...objects);
      });
      return selected;
    }
  }
  add(selectables) {
    this.container.add(selectables);
    this.view.dispatchEvent({
      type: "select",
      objects: Array.isArray(selectables) ? selectables : [selectables]
    });
  }
  delete(selectables) {
    this.container.delete(selectables);
    this.view.dispatchEvent({ type: "deselect", objects: Array.isArray(selectables) ? selectables : [selectables] });
  }
  clear(layer) {
    const objects = this.getSelected(layer);
    objects?.forEach((object) => object.deselect());
    if (layer) {
      this.objects.delete(layer);
    } else {
      this.objects.clear();
    }
    this.view.dispatchEvent({ type: "deselect", objects });
  }
  selectedCount(layer) {
    if (layer) {
      const selected = this.objects.get(layer);
      return selected ? selected.size : 0;
    } else {
      let selectedCount = 0;
      this.objects.forEach((objects) => {
        selectedCount += objects.size;
      });
      return selectedCount;
    }
  }
  has(obj) {
    const map = this.objects;
    for (let set of map.values()) {
      if (set.has(obj)) {
        return true;
      }
    }
    return false;
  }
}

const undoStackEventNames = ["add", "undo", "redo", "clear"];
class UndoStack extends EventDispatcher {
  constructor(view) {
    super(undoStackEventNames);
    this.stack = [];
    this.index = -1;
    this.lastIndex = -1;
    this.view = view;
  }
  add(command) {
    if (this.index !== this.lastIndex) {
      this.stack.splice(this.index + 1);
    }
    this.stack.push(command);
    this.index++;
    this.lastIndex = this.index;
    this.dispatchEvent({ type: "add" });
  }
  getCommandList() {
    return [...this.stack];
  }
  undo() {
    if (this.index >= 0) {
      this.stack[this.index].undo(this.view);
      this.index--;
      this.dispatchEvent({ type: "undo" });
    }
  }
  redo() {
    if (this.index !== this.lastIndex) {
      this.stack[this.index + 1].execute(this.view);
      this.index++;
      this.dispatchEvent({ type: "redo" });
    }
  }
  undoAll() {
    do {
      this.undo();
    } while (this.index >= 0);
  }
  getIndex() {
    return this.index;
  }
  getLastIndex() {
    return this.lastIndex;
  }
  clear() {
    this.stack = [];
    this.index = -1;
    this.lastIndex = -1;
    this.dispatchEvent({ type: "clear" });
  }
}

class Component {
  constructor(entity) {
    this.entity = entity;
  }
}

class Raycast extends Component {
  constructor(entity) {
    super(entity);
  }
  isRaycast(raycaster, recursive = true) {
    const activeViewport = this.entity.getLayer()?.getView().getActiveViewport();
    if (activeViewport) {
      raycaster.layers.mask = activeViewport.camera.layers.mask;
      const intersections = raycaster.intersectObject(this.entity.object3d, recursive);
      return intersections.length ? intersections : false;
    }
    return false;
  }
  *intersect(raycaster) {
    if (!this.entity.object3d.visible) return;
    const intersections = this.isRaycast(raycaster, true);
    if (intersections) {
      yield { raycast: this, intersections };
    }
  }
}
class RaycastNoneRecursive extends Raycast {
  *intersect(raycaster) {
    if (!this.entity.object3d.visible) return;
    const intersections = this.isRaycast(raycaster, false);
    if (intersections) {
      yield { raycast: this, intersections };
    }
  }
}
class RaycastChildrenOnly extends Raycast {
  *intersect(raycaster) {
    for (const child of this.entity.children) {
      const raycast = child.getComponent(Raycast);
      if (raycast) {
        yield* raycast.intersect(raycaster);
      }
    }
  }
}

var EventPriority = /* @__PURE__ */ ((EventPriority2) => {
  EventPriority2[EventPriority2["None"] = 0] = "None";
  EventPriority2[EventPriority2["Tool"] = 10] = "Tool";
  EventPriority2[EventPriority2["RaycastController"] = 20] = "RaycastController";
  EventPriority2[EventPriority2["CursorController"] = 30] = "CursorController";
  return EventPriority2;
})(EventPriority || {});

class Highlighter {
  constructor(entity) {
    this.entity = entity;
  }
}

class ColorHighlighter extends Highlighter {
  constructor(object3d, color = 16711680) {
    super(object3d);
    this.material = /* @__PURE__ */ new Map();
    this.color = color;
  }
  highlight() {
    this.saveColor();
    this.setColor(this.color);
  }
  unHighlight() {
    this.restoreColor();
  }
  setColor(color) {
    if (this.entity.object3d instanceof Mesh) {
      this.makeSetColor(this.entity.object3d.material, color);
    } else if (this.entity.object3d instanceof Object3D) {
      this.entity.object3d.traverse((obj) => {
        if (obj.material) this.makeSetColor(obj.material, color);
      });
    }
  }
  saveColor() {
    if (this.entity.object3d instanceof Mesh) {
      this.makeSaveColor(this.entity.object3d.material, this.entity);
    } else if (this.entity.object3d instanceof Object3D) {
      this.entity.object3d.traverse((obj) => {
        if (obj.material) this.makeSaveColor(obj.material, obj);
      });
    }
  }
  restoreColor() {
    if (this.entity.object3d instanceof Mesh) {
      this.makeRestoreColor(this.entity.object3d.material, this.material.get(this.entity));
    } else if (this.entity.object3d instanceof Object3D) {
      this.entity.object3d.traverse((obj) => {
        if (obj.material) this.makeRestoreColor(obj.material, this.material.get(obj));
      });
    }
  }
  makeSetColor(mat, color) {
    const material = mat;
    if (material instanceof ShaderMaterial) {
      material.uniforms.diffuse?.value?.set(color);
      material.uniforms.specular?.value?.set(color);
      material.uniforms.emissive?.value?.set(color);
    } else {
      material.color?.set(color);
      material.specular?.set(color);
      material.emissive?.set(color);
    }
  }
  makeSaveColor(mat, obj) {
    const material = mat;
    if (material instanceof ShaderMaterial) {
      this.material.set(obj, {
        color: material.uniforms.diffuse?.value?.toArray(),
        specular: material.uniforms.specular?.value?.toArray(),
        emissive: material.uniforms.emissive?.value?.toArray()
      });
    } else {
      this.material.set(obj, {
        color: material.color?.toArray(),
        specular: material.specular?.toArray(),
        emissive: material.emissive?.toArray()
      });
    }
  }
  makeRestoreColor(mat, data) {
    const material = mat;
    if (data) {
      if (material instanceof ShaderMaterial) {
        material.uniforms.diffuse?.value?.fromArray(data.color);
        material.uniforms.specular?.value?.fromArray(data.specular);
        material.uniforms.emissive?.value?.fromArray(data.emissive);
      } else {
        material.color?.fromArray(data.color);
        material.specular?.fromArray(data.specular);
        material.emissive?.fromArray(data.emissive);
      }
    }
  }
}

class HoverComponent extends Component {
  constructor(entity, highlighter = new ColorHighlighter(entity, void 0)) {
    super(entity);
    this.hovered = false;
    this.highlighter = highlighter;
  }
  hover() {
    this.hovered = true;
    this.highlighter.highlight();
  }
  unhover() {
    this.hovered = false;
    this.highlighter.unHighlight();
  }
  isHovered() {
    return this.hovered;
  }
}

class RaycastController {
  constructor(view) {
    this.raycaster = new Raycaster();
    this.raycaster.params.Line = { threshold: 0.01 };
    this.raycaster.params.Line2 = { threshold: 5 };
    this.view = view;
    this.state = {
      transformObject: false,
      transformCamera: false
    };
    this.handleChangeActiveViewport = this.handleChangeActiveViewport.bind(this);
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
    this.onTransformCameraStart = this.onTransformCameraStart.bind(this);
    this.onTransformCameraEnd = this.onTransformCameraEnd.bind(this);
    this.onTransformCamera = this.onTransformCamera.bind(this);
    this.update = this.update.bind(this);
  }
  get position() {
    return this.view.cursorController.position;
  }
  init() {
    this.changeViewport(this.view.getActiveViewport());
    this.view.addEventListener("ActiveViewportWillChange", this.handleChangeActiveViewport, 20);
    this.view.addEventListener("transformStart", this.onTransformStart);
    this.view.addEventListener("transformCamera", this.onTransformCamera);
    this.view.addEventListener("transformCameraStart", this.onTransformCameraStart);
  }
  dispose() {
    this.changeViewport();
    this.view.removeEventListener("ActiveViewportWillChange", this.handleChangeActiveViewport);
    this.view.removeEventListener("transformStart", this.onTransformStart);
    this.view.removeEventListener("transformEnd", this.onTransformEnd);
    this.view.removeEventListener("transformCamera", this.onTransformCamera);
    this.view.removeEventListener("transformCameraStart", this.onTransformCameraStart);
    this.raycastData = void 0;
    this.object = void 0;
  }
  getObject() {
    return this.object;
  }
  changeViewport(viewport) {
    if (this.viewport === viewport) {
      return;
    }
    if (this.viewport) {
      App.Instance().mouseController.removeListener("pointermove", this.update, this.viewport.getDomElement(), true);
      App.Instance().mouseController.removeListener("pointerdown", this.update, this.viewport.getDomElement(), "left", true);
      this.viewport.removeEventListener("resize", this.update);
      this.viewport.removeEventListener("reposition", this.update);
    }
    if (viewport) {
      App.Instance().mouseController.addListener("pointermove", this.update, viewport.getDomElement(), EventPriority.RaycastController);
      App.Instance().mouseController.addListener("pointerdown", this.update, viewport.getDomElement(), "left", EventPriority.RaycastController);
      viewport.addEventListener("resize", this.update);
      viewport.addEventListener("reposition", this.update);
      this.updateLineThreshold(viewport);
    } else if (this.object) {
      const previousObject = this.object;
      this.raycastData = void 0;
      this.object = void 0;
      this.view.dispatchEvent({ type: "unhover", entity: previousObject });
    }
    this.viewport = viewport;
    if (this.viewport) {
      this.update();
    }
  }
  start() {
    const currentViewport = this.view.getActiveViewport();
    if (this.viewport && this.viewport !== currentViewport) {
      App.Instance().mouseController.removeListener("pointerdown", this.update, this.viewport.getDomElement(), "left");
      if (currentViewport) {
        App.Instance().mouseController.addListener("pointerdown", this.update, currentViewport.getDomElement(), "left", EventPriority.RaycastController);
      }
    }
    this.viewport = currentViewport;
    this.view.addEventListener("ActiveViewportWillChange", this.handleChangeActiveViewport, 20);
    if (this.viewport) {
      App.Instance().mouseController.addListener("pointermove", this.update, this.viewport.getDomElement(), EventPriority.RaycastController);
      this.update();
    }
  }
  stop() {
    this.viewport;
    if (this.viewport) {
      App.Instance().mouseController.removeListener("pointermove", this.update, this.viewport.getDomElement(), true);
      this.viewport.removeEventListener("resize", this.update);
      this.viewport.removeEventListener("reposition", this.update);
    }
  }
  handleChangeActiveViewport(event) {
    this.changeViewport(event.next);
  }
  updateLineThreshold(viewport) {
    const zoom = viewport.cameraController.camera.zoom;
    this.raycaster.params.Line.threshold = 60 / zoom;
  }
  onTransformCamera(event) {
    if (event.viewport === this.viewport) this.updateLineThreshold(this.viewport);
  }
  onTransformCameraStart() {
    if (!this.state.transformCamera && !this.state.transformObject) {
      this.stop();
    }
    this.view.addEventListener("transformCameraEnd", this.onTransformCameraEnd);
    this.state.transformCamera = true;
  }
  onTransformCameraEnd() {
    this.state.transformCamera = false;
    if (!this.state.transformObject) {
      this.start();
    }
    this.view.removeEventListener("transformCameraEnd", this.onTransformCameraEnd);
  }
  onTransformStart() {
    if (!this.state.transformCamera && !this.state.transformObject) {
      this.stop();
    }
    this.view.addEventListener("transformEnd", this.onTransformEnd);
    this.state.transformObject = true;
  }
  onTransformEnd() {
    this.state.transformObject = false;
    if (!this.state.transformCamera) {
      this.start();
    }
    this.view.removeEventListener("transformEnd", this.onTransformEnd);
  }
  getNearest(generator) {
    const results = [];
    for (const intersection of generator) {
      intersection.intersections.sort((a, b) => a.distance - b.distance);
      results.push(intersection);
    }
    if (results.length) {
      results.sort((a, b) => a.intersections[0].distance - b.intersections[0].distance);
      return results[0];
    }
  }
  *raycast() {
    this.raycaster.setFromCamera(this.position, this.viewport.camera);
    for (const layer of this.view.getLayers()) {
      const objects = layer.getComponent(Raycast);
      if (objects) {
        for (const object of objects) {
          const generator = object.intersect(this.raycaster);
          for (const intersection of generator) {
            yield intersection;
          }
        }
      }
    }
  }
  *raycastObject(object) {
    this.raycaster.setFromCamera(this.position, this.viewport.camera);
    let generator;
    if (object instanceof Raycast) {
      generator = object.intersect(this.raycaster);
    } else {
      const raycast = object.getComponent(Raycast);
      if (!raycast) return;
      generator = raycast.intersect(this.raycaster);
    }
    for (const intersection of generator) {
      yield intersection;
    }
  }
  getPositionOnZeroPlane(v) {
    this.raycaster.setFromCamera(this.position, this.viewport.camera);
    const distance = -this.raycaster.ray.origin.z / this.raycaster.ray.direction.z;
    v.x = this.raycaster.ray.origin.x + this.raycaster.ray.direction.x * distance;
    v.y = this.raycaster.ray.origin.y + this.raycaster.ray.direction.y * distance;
    v.z = 0;
    return v;
  }
  update() {
    this.raycastData = this.getNearest(this.raycast());
    if (this.object === this.raycastData?.raycast.entity) return;
    if (this.object) {
      this.view.dispatchEvent({ type: "unhover", entity: this.object });
    }
    this.object = this.raycastData?.raycast.entity;
    if (this.object && this.object.getComponent(HoverComponent)) {
      this.view.dispatchEvent({ type: "hover", entity: this.object });
    }
  }
}

class CursorController {
  constructor(view) {
    this.position = new Vector2(0, 0);
    this.clientPosition = new Vector2(0, 0);
    this.view = view;
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleChangePosition = this.handleChangePosition.bind(this);
    this.updatePosition = this.updatePosition.bind(this);
    this.onChangeActiveViewport = this.onChangeActiveViewport.bind(this);
    this.onActiveViewportChange = this.onActiveViewportChange.bind(this);
  }
  init() {
    this.view.addEventListener("ActiveViewportWillChange", this.onChangeActiveViewport);
    this.view.addEventListener("ActiveViewportChanged", this.onActiveViewportChange);
    App.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    if (this.viewport) {
      this.viewport.addEventListener("resize", this.updatePosition, EventPriority.CursorController);
      this.viewport.addEventListener("reposition", this.updatePosition, EventPriority.CursorController);
      App.Instance().mouseController.addListener("pointermove", this.handleChangePosition, this.viewport.getDomElement(), EventPriority.CursorController);
      App.Instance().mouseController.addListener("pointerdown", this.handleChangePosition, this.viewport.getDomElement(), "left", EventPriority.CursorController);
    }
  }
  dispose() {
    this.view.removeEventListener("ActiveViewportWillChange", this.onChangeActiveViewport);
    this.view.removeEventListener("ActiveViewportChanged", this.onActiveViewportChange);
    App.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    if (this.viewport) {
      App.Instance().mouseController.removeListener("pointermove", this.handleChangePosition, this.viewport.getDomElement());
      App.Instance().mouseController.removeListener("pointerdown", this.handleChangePosition, this.viewport.getDomElement(), "left");
      this.viewport.removeEventListener("resize", this.updatePosition);
      this.viewport.removeEventListener("reposition", this.updatePosition);
    }
  }
  get viewport() {
    return this.view?.getActiveViewport();
  }
  onChangeActiveViewport(event) {
    event.current?.removeEventListener("resize", this.updatePosition);
    event.next?.addEventListener("resize", this.updatePosition, EventPriority.CursorController);
    event.current?.removeEventListener("reposition", this.updatePosition);
    event.next?.addEventListener("reposition", this.updatePosition, EventPriority.CursorController);
    if (event.current) {
      App.Instance().mouseController.removeListener("pointermove", this.handleChangePosition, event.current.getDomElement());
      App.Instance().mouseController.removeListener("pointerdown", this.handleChangePosition, event.current.getDomElement(), "left");
    }
    if (event.next) {
      App.Instance().mouseController.addListener("pointermove", this.handleChangePosition, event.next.getDomElement(), EventPriority.CursorController);
      App.Instance().mouseController.addListener("pointerdown", this.handleChangePosition, event.next.getDomElement(), "left", EventPriority.CursorController);
    }
  }
  setPosition(x, y) {
    this.position.set(
      x / this.viewport.size.width * 2 - 1,
      -y / this.viewport.size.height * 2 + 1
    );
  }
  handleChangePosition(event) {
    this.setPosition(event.offsetX, event.offsetY);
  }
  handleMouseMove(event) {
    this.clientPosition.set(event.clientX, event.clientY);
  }
  updatePosition() {
    this.setPosition(this.clientPosition.x, this.clientPosition.y);
  }
  onActiveViewportChange(event) {
    if (event.current) {
      this.updatePosition();
    }
  }
}

class View extends EventDispatcher {
  constructor() {
    super();
    this.id = MathUtils.generateUUID();
    this.layers = /* @__PURE__ */ new Map();
    this.scene = new Scene();
    this.undoStack = new UndoStack(this);
    this.toolController = new ToolsController(this);
    this.selectController = new SelectController(this);
    this.cursorController = new CursorController(this);
    this.raycastController = new RaycastController(this);
    this.domElement = HTML.div({ style: { width: "100%", height: "100%" } });
    this.freeCameraLayers = [];
    this.nextCameraLayer = 1;
    this.viewports = {};
    this.handleResize = this.handleResize.bind(this);
    this.onResizeView = this.onResizeView.bind(this);
    this.resizeObserver = new ResizeObserver(this.onResizeView);
  }
  init(props) {
    this.name = props.name;
    this.container = props.container;
    this.cursorController.init();
    this.raycastController.init();
    this.renderer = this.createRenderer(props?.rendererProps);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.canvas = this.createCanvas();
    this.canvas.dataset.belongsTo = this.name;
    this.container?.appendChild(this.domElement);
    if (!props?.rendererProps?.canvas) {
      this.domElement.appendChild(this.canvas);
    }
    this.handleResize();
    this.toolController.init();
    this.resizeObserver.observe(this.domElement);
    App.Instance().addEventListener("resize", this.handleResize, 1);
    this.dispatchEvent({ type: "render" });
    this.domElement.addEventListener("contextmenu", (event) => event.preventDefault(), false);
  }
  dispose() {
    this.cursorController.dispose();
    this.raycastController.dispose();
    this.undoStack.clearListeners();
    this.toolController.dispose();
    this.selectController.clear();
    this.clearLayers();
    Object.values(this.viewports).forEach((viewport) => {
      viewport.dispose();
    });
    if (this.domElement) this.resizeObserver.unobserve(this.domElement);
    this.container?.removeChild(this.domElement);
    App.Instance().removeEventListener("resize", this.handleResize);
  }
  clear() {
    this.dispose();
    let keys = Object.keys(this.viewports);
    keys.forEach((key) => {
      this.deleteViewport(key);
    });
    this.layers.forEach((layer) => {
      this.removeLayer(layer.getName());
    });
    this.toolController.clear();
    this.undoStack.clear();
  }
  clearLayers() {
    if (!this.layers.size) return;
    const layers = this.getLayers();
    for (const layer of layers) {
      layer.getObjects()?.forEach((obj) => obj.dispose());
      layer.clear();
    }
    this.dispatchEvent({ type: "render" });
  }
  executeCommand(command, immediately = true) {
    if (immediately) {
      if (command.execute(this)) {
        this.undoStack.add(command);
      }
    } else {
      this.undoStack.add(command);
    }
  }
  getRenderer() {
    return this.renderer;
  }
  createRenderer(props) {
    return new WebGLRenderer(props);
  }
  createCanvas() {
    return this.renderer.domElement;
  }
  resizeCanvas(width, height) {
    this.renderer.setSize(width, height, true);
  }
  renderViewport(viewport, draw) {
    draw();
  }
  getDomElement() {
    return this.domElement;
  }
  getContainer() {
    return this.container;
  }
  setContainer(container) {
    if (this.container !== container) {
      container.appendChild(this.domElement);
      this.container = container;
      this.handleResize();
      App.Instance().dispatchEvent({ type: "resize" });
    }
  }
  getCanvas() {
    return this.canvas;
  }
  onResizeView() {
    this.handleResize();
  }
  handleResize() {
    const width = this.domElement.clientWidth || 0;
    const height = this.domElement.clientHeight || 0;
    this.resizeCanvas(width, height);
  }
  addViewportInstance(name, viewport) {
    if (this.viewports[name]) {
      console.error(`viewport ${String(name)} already exist`);
      return false;
    }
    if (viewport.view !== this) {
      console.error(`viewport ${String(name)} already exist`);
      return false;
    }
    this.viewports[name] = viewport;
    viewport.setCameraLayer(this.getEmptyCameraLayer());
    this.dispatchEvent({ type: "viewportAdded", viewport });
    return true;
  }
  addViewport(name, domElement, options) {
    const viewport = new Viewport(this, name, domElement, this.getEmptyCameraLayer(), options);
    this.viewports[name] = viewport;
    this.dispatchEvent({ type: "viewportAdded", viewport });
    return viewport;
  }
  deleteViewport(name) {
    const viewport = this.viewports[name];
    if (viewport) {
      this.releaseCameraLayer(viewport.cameraLayer);
      viewport.dispose();
      delete this.viewports[name];
      this.dispatchEvent({ type: "viewportRemoved", viewport });
    }
  }
  getEmptyCameraLayer() {
    const freeLayer = this.freeCameraLayers.pop();
    if (typeof freeLayer === "number") {
      return freeLayer;
    }
    return this.nextCameraLayer++;
  }
  releaseCameraLayer(layer) {
    if (layer <= 0 || this.freeCameraLayers.includes(layer)) {
      return;
    }
    this.freeCameraLayers.push(layer);
  }
  getViewport(name) {
    return this.viewports[name];
  }
  *getViewports() {
    for (const name in this.viewports) {
      const viewport = this.viewports[name];
      if (!viewport) {
        continue;
      }
      yield viewport;
    }
  }
  addLayer(name, objects) {
    let layer = this.layers.get(name);
    if (layer) {
      objects && layer.add(objects);
      return layer;
    }
    layer = new Layer(this, name, objects);
    this.layers.set(name, layer);
    this.scene.add(layer.getGroup());
    this.dispatchEvent({ type: "addLayer", container: layer });
    return layer;
  }
  removeLayer(name) {
    const layer = this.layers.get(name);
    if (!layer) return;
    layer.getObjects()?.forEach((obj) => obj.dispose());
    layer.clear();
    this.scene.remove(layer.getGroup());
    this.layers.delete(name);
    this.dispatchEvent({ type: "deleteLayer", container: layer });
  }
  getLayer(name) {
    if (!this.layers.has(name)) console.warn("getLayer: no layer=", name);
    return this.layers.get(name);
  }
  *getLayers() {
    for (const layer of this.layers) {
      yield layer[1];
    }
  }
  *getVisibleLayers() {
    for (const layer of this.layers) {
      if (layer[1].getGroup().visible) {
        yield layer[1];
      }
    }
  }
  activateMode(modeName) {
    const previous = this.toolController.getModeName();
    if (previous === modeName) return;
    this.toolController.activateMode(modeName);
    Object.values(this.viewports).forEach((viewport) => viewport.activateMode(modeName));
    const mode = this.toolController.getModeName();
    this.dispatchEvent({ type: "modeChanged", previous: previous !== void 0 ? previous : "", mode: mode !== void 0 ? mode : "" });
  }
  setActiveViewport(viewportName) {
    const viewport = typeof viewportName === "undefined" ? viewportName : this.viewports[viewportName];
    if (this.activeViewport === viewport) return;
    this.dispatchEvent({ type: "ActiveViewportWillChange", current: this.activeViewport, next: viewport });
    const previousViewport = this.activeViewport;
    this.activeViewport = viewport;
    this.dispatchEvent({ type: "ActiveViewportChanged", previous: previousViewport, current: viewport });
  }
  getActiveViewport() {
    return this.activeViewport;
  }
}

const getMax3DTextureSize = (renderer) => {
  const gl = renderer.getContext();
  if (typeof WebGL2RenderingContext === "undefined" || !(gl instanceof WebGL2RenderingContext)) {
    return 0;
  }
  return gl.getParameter(gl.MAX_3D_TEXTURE_SIZE);
};
const isMobilePlatform = () => {
  if (typeof navigator === "undefined") {
    return false;
  }
  return /Android|iP(hone|ad|od)|Mobile/.test(navigator.userAgent);
};
const DESKTOP_CPU_MEMORY_BUDGET_BYTES = 1024 * 1024 * 1024;
const MOBILE_CPU_MEMORY_BUDGET_BYTES = 384 * 1024 * 1024;
const getPlatformCpuMemoryBudgetBytes = () => isMobilePlatform() ? MOBILE_CPU_MEMORY_BUDGET_BYTES : DESKTOP_CPU_MEMORY_BUDGET_BYTES;

const DESKTOP_DECODED_VOLUME_CACHE_LIMIT_BYTES = 256 * 1024 * 1024;
const MOBILE_DECODED_VOLUME_CACHE_LIMIT_BYTES = 128 * 1024 * 1024;
const VOLUME_STAGING_LIMIT_BYTES = 64 * 1024 * 1024;
const usage = {
  cpuBytes: 0,
  decodedBytes: 0,
  gpuBytes: 0,
  stagingBytes: 0
};
const reclaimers = /* @__PURE__ */ new Set();
const stagingWaiters = /* @__PURE__ */ new Set();
let wasmMemory = null;
const getDecodedVolumeCacheLimitBytes = () => isMobilePlatform() ? MOBILE_DECODED_VOLUME_CACHE_LIMIT_BYTES : DESKTOP_DECODED_VOLUME_CACHE_LIMIT_BYTES;
const trackVolumeWasmMemory = (memory) => {
  wasmMemory = memory;
};
const getPendingDecodedBytes = () => [...reclaimers].reduce(
  (bytes, reclaimer) => bytes + reclaimer.getPendingBytes(),
  0
);
const getRequiredDecodedBytes = () => [...reclaimers].reduce(
  (bytes, reclaimer) => bytes + reclaimer.getRequiredBytes(),
  0
);
const getVolumeMemoryUsage = () => ({
  ...usage,
  pendingDecodedBytes: getPendingDecodedBytes(),
  requiredDecodedBytes: getRequiredDecodedBytes(),
  wasmBytes: wasmMemory?.buffer.byteLength ?? 0
});
const getVolumeMemoryBytes = () => usage.cpuBytes + usage.decodedBytes + usage.stagingBytes + getPendingDecodedBytes() + (wasmMemory?.buffer.byteLength ?? 0);
const registerVolumeMemoryReclaimer = (reclaimer) => {
  reclaimers.add(reclaimer);
};
const reclaimVolumeMemory = (bytes = 0, stagingBytes = 0) => {
  const budgetBytes = getPlatformCpuMemoryBudgetBytes();
  const additionalBytes = bytes + Math.max(
    0,
    VOLUME_STAGING_LIMIT_BYTES - usage.stagingBytes - stagingBytes
  );
  reclaimers.forEach((reclaimer) => {
    const missingBytes = getVolumeMemoryBytes() + additionalBytes - budgetBytes;
    if (missingBytes > 0) {
      reclaimer.reclaim(missingBytes);
    }
  });
  return getVolumeMemoryBytes() + additionalBytes <= budgetBytes;
};
const ensureVolumeMemoryAvailable = (bytes = 0, stagingBytes = 0) => {
  if (!reclaimVolumeMemory(bytes, stagingBytes)) {
    throw new RangeError("Volume allocation exceeds the memory budget");
  }
};
const reserveVolumeMemory = (request) => {
  const allocations = Object.entries(request);
  const bytes = allocations.reduce((total, [, value]) => total + value, 0);
  if (!Number.isSafeInteger(bytes) || allocations.some(
    ([, value]) => !Number.isSafeInteger(value) || value < 0
  )) {
    throw new RangeError("Invalid volume allocation size");
  }
  if (usage.stagingBytes + (request.stagingBytes ?? 0) > VOLUME_STAGING_LIMIT_BYTES) {
    throw new RangeError("DCCT range exceeds the staging memory budget");
  }
  ensureVolumeMemoryAvailable(
    bytes - (request.gpuBytes ?? 0),
    request.stagingBytes ?? 0
  );
  allocations.forEach(([key, value]) => {
    usage[key] += value;
  });
  let released = false;
  return () => {
    if (released) {
      return;
    }
    released = true;
    allocations.forEach(([key, value]) => {
      usage[key] -= value;
    });
    stagingWaiters.forEach((wake) => wake());
  };
};
const acquireVolumeStaging = async (bytes, signal) => {
  if (!Number.isSafeInteger(bytes) || bytes < 0 || bytes > VOLUME_STAGING_LIMIT_BYTES) {
    throw new RangeError("DCCT range exceeds the staging memory budget");
  }
  while (usage.stagingBytes + bytes > VOLUME_STAGING_LIMIT_BYTES) {
    signal?.throwIfAborted();
    await new Promise((resolve) => {
      const wake = () => {
        stagingWaiters.delete(wake);
        signal?.removeEventListener("abort", wake);
        resolve();
      };
      stagingWaiters.add(wake);
      signal?.addEventListener("abort", wake, { once: true });
    });
  }
  signal?.throwIfAborted();
  return reserveVolumeMemory({ stagingBytes: bytes });
};

const EMPTY_HU_SENTINEL = -32768;
const configureTexture = (texture) => {
  texture.format = RedFormat;
  texture.minFilter = LinearFilter;
  texture.magFilter = LinearFilter;
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.wrapR = ClampToEdgeWrapping;
  texture.unpackAlignment = 1;
  texture.needsUpdate = true;
};
const adaptWindowForNormalization = (colorProfile, normalization) => {
  if (!normalization) {
    return colorProfile;
  }
  const range = Math.max(1, normalization.max - normalization.min);
  return {
    ww: Math.max(1, colorProfile.ww) / range,
    wc: (colorProfile.wc - normalization.min) / range
  };
};

const now$6 = () => typeof performance !== "undefined" ? performance.now() : Date.now();
class VolumeGpuError extends Error {
  constructor(code, message = "Full-resolution GPU rendering failed") {
    super(code === null ? message : message + ": " + code);
    this.code = code;
    this.name = "VolumeGpuError";
  }
}
class MonolithTexture {
  constructor(renderer, size, origin = { x: 0, y: 0, z: 0 }) {
    this.renderer = renderer;
    this.origin = origin;
    this.normalization = null;
    const release = reserveVolumeMemory({
      gpuBytes: size.x * size.y * size.z * Uint16Array.BYTES_PER_ELEMENT
    });
    try {
      this.target = new WebGL3DRenderTarget(size.x, size.y, size.z);
      this.target.depthBuffer = false;
      this.target.stencilBuffer = false;
      this.texture = this.target.texture;
      configureTexture(this.texture);
      this.texture.type = ShortType;
      this.texture.format = RedIntegerFormat;
      this.texture.internalFormat = "R16I";
      this.texture.minFilter = NearestFilter;
      this.texture.magFilter = NearestFilter;
      this.target.addEventListener("dispose", release);
      this.initialize();
    } catch (error) {
      this.target?.dispose();
      release();
      throw error;
    }
  }
  uploadChunks(chunks) {
    const startedAt = now$6();
    chunks.forEach((chunk) => {
      const { width, height, depth } = this.texture.image;
      const start = {
        x: Math.max(this.origin.x, chunk.origin.x),
        y: Math.max(this.origin.y, chunk.origin.y),
        z: Math.max(this.origin.z, chunk.origin.z)
      };
      const size = {
        x: Math.min(this.origin.x + width, chunk.origin.x + chunk.shape.x) - start.x,
        y: Math.min(this.origin.y + height, chunk.origin.y + chunk.shape.y) - start.y,
        z: Math.min(this.origin.z + depth, chunk.origin.z + chunk.shape.z) - start.z
      };
      if (size.x <= 0 || size.y <= 0 || size.z <= 0) {
        return;
      }
      this.withTextureBinding((gl) => {
        gl.pixelStorei(gl.UNPACK_ROW_LENGTH, chunk.shape.x);
        gl.pixelStorei(gl.UNPACK_IMAGE_HEIGHT, chunk.shape.y);
        gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, start.x - chunk.origin.x);
        gl.pixelStorei(gl.UNPACK_SKIP_ROWS, start.y - chunk.origin.y);
        gl.pixelStorei(gl.UNPACK_SKIP_IMAGES, start.z - chunk.origin.z);
        gl.texSubImage3D(
          gl.TEXTURE_3D,
          0,
          start.x - this.origin.x,
          start.y - this.origin.y,
          start.z - this.origin.z,
          size.x,
          size.y,
          size.z,
          gl.RED_INTEGER,
          gl.SHORT,
          chunk.data
        );
      });
    });
    return {
      didUpload: chunks.length > 0,
      uploadMs: now$6() - startedAt,
      uploadedChunkCount: chunks.length
    };
  }
  isAvailable() {
    const gl = this.renderer.getContext();
    const texture = this.renderer.properties.get(this.texture)?.__webglTexture;
    return !gl.isContextLost() && !!texture && gl.isTexture(texture);
  }
  dispose() {
    this.target.dispose();
  }
  initialize() {
    const previousTarget = this.renderer.getRenderTarget();
    const previousLayer = this.renderer.getActiveCubeFace();
    const previousLevel = this.renderer.getActiveMipmapLevel();
    try {
      this.renderer.setRenderTarget(this.target);
    } finally {
      this.renderer.setRenderTarget(previousTarget, previousLayer, previousLevel);
    }
    const gl = this.renderer.getContext();
    const allocationError = gl.getError();
    if (allocationError !== gl.NO_ERROR) {
      throw new VolumeGpuError(allocationError);
    }
    const { width, height, depth } = this.texture.image;
    const release = reserveVolumeMemory({
      cpuBytes: width * height * Uint16Array.BYTES_PER_ELEMENT
    });
    try {
      const emptySlice = new Int16Array(width * height);
      emptySlice.fill(EMPTY_HU_SENTINEL);
      this.withTextureBinding((gl2) => {
        Array.from({ length: depth }, (_, z) => {
          gl2.texSubImage3D(
            gl2.TEXTURE_3D,
            0,
            0,
            0,
            z,
            width,
            height,
            1,
            gl2.RED_INTEGER,
            gl2.SHORT,
            emptySlice
          );
        });
      });
    } finally {
      release();
    }
  }
  withTextureBinding(upload) {
    const gl = this.renderer.getContext();
    if (!(gl instanceof WebGL2RenderingContext)) {
      throw new VolumeGpuError(null, "Full-resolution volume upload requires WebGL2");
    }
    const texture = this.renderer.properties.get(this.texture)?.__webglTexture;
    if (!texture) {
      throw new VolumeGpuError(gl.getError() || null, "Full-resolution volume texture is unavailable");
    }
    const previousBinding = gl.getParameter(gl.TEXTURE_BINDING_3D);
    const unpackSettings = [
      [gl.UNPACK_ALIGNMENT, 1],
      [gl.UNPACK_ROW_LENGTH, 0],
      [gl.UNPACK_IMAGE_HEIGHT, 0],
      [gl.UNPACK_SKIP_PIXELS, 0],
      [gl.UNPACK_SKIP_ROWS, 0],
      [gl.UNPACK_SKIP_IMAGES, 0]
    ];
    const previousSettings = unpackSettings.map(([setting]) => [setting, gl.getParameter(setting)]);
    try {
      gl.bindTexture(gl.TEXTURE_3D, texture);
      unpackSettings.forEach(([setting, value]) => gl.pixelStorei(setting, value));
      upload(gl);
      const error = gl.getError();
      if (error !== gl.NO_ERROR) {
        throw new VolumeGpuError(error);
      }
    } finally {
      previousSettings.forEach(([setting, value]) => gl.pixelStorei(setting, value));
      gl.bindTexture(gl.TEXTURE_3D, previousBinding);
    }
  }
}

class Tool {
  constructor(viewport) {
    this.removeEventsCallbacks = [];
    this.hostObject = viewport;
    this.enable = false;
  }
  unsubscribeAll() {
    this.removeEventsCallbacks.forEach((cb) => cb());
    this.removeEventsCallbacks.splice(0);
  }
  init() {
    if (!this.enable && this.hostObject) {
      this.activate();
      this.enable = true;
    } else {
      console.error("tool is not initialized", this.enable, typeof this.hostObject === "undefined");
    }
  }
  dispose() {
    if (this.enable) {
      this.deactivate();
      this.enable = false;
    }
  }
  getHostObject() {
    return this.hostObject;
  }
}
class ViewportTool extends Tool {
  constructor(viewport) {
    super(viewport);
  }
  get view() {
    return this.hostObject.view;
  }
  getHostObject() {
    return this.hostObject;
  }
}
class ViewTool extends Tool {
  constructor(view) {
    super(view);
  }
  getHostObject() {
    return this.hostObject;
  }
}

class Render extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.render = this.render.bind(this);
  }
  activate() {
    this.view.addEventListener("delete", this.render);
    App.Instance().addEventListener("resize", this.render);
    this.view.addEventListener("transformCamera", this.render);
    this.view.addEventListener("select", this.render);
    this.view.addEventListener("deselect", this.render);
    this.view.addEventListener("transform", this.render);
    this.view.addEventListener("viewportSchemeChanged", this.render);
    this.view.addEventListener("render", this.render);
  }
  deactivate() {
    this.view.removeEventListener("delete", this.render);
    App.Instance().removeEventListener("resize", this.render);
    this.view.removeEventListener("transformCamera", this.render);
    this.view.removeEventListener("select", this.render);
    this.view.removeEventListener("deselect", this.render);
    this.view.removeEventListener("transform", this.render);
    this.view.removeEventListener("viewportSchemeChanged", this.render);
    this.view.removeEventListener("render", this.render);
  }
  render() {
    this.hostObject.render();
  }
}

class GpuTimer {
  constructor(renderer, getLabel, onSample) {
    this.getLabel = getLabel;
    this.onSample = onSample;
    this.active = null;
    this.activeLabel = "";
    this.pending = [];
    const gl = renderer.getContext();
    if (typeof WebGL2RenderingContext !== "undefined" && gl instanceof WebGL2RenderingContext) {
      this.gl = gl;
      this.ext = gl.getExtension("EXT_disjoint_timer_query_webgl2");
    } else {
      this.gl = null;
      this.ext = null;
    }
  }
  available() {
    return !!this.ext;
  }
  begin() {
    if (!this.gl || !this.ext || this.active) {
      return;
    }
    const query = this.gl.createQuery();
    if (!query) {
      return;
    }
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
    this.active = query;
    this.activeLabel = this.getLabel();
  }
  end() {
    if (!this.gl || !this.ext || !this.active) {
      return;
    }
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push({ label: this.activeLabel, query: this.active });
    this.active = null;
  }
  // Harvest finished queries. A disjoint event (GPU clock interrupted, e.g.
  // context switch / power transition) invalidates outstanding timings, so we
  // drop them rather than report garbage.
  poll() {
    if (!this.gl || !this.ext || this.pending.length === 0) {
      return;
    }
    const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT);
    this.pending = this.pending.filter(({ label, query }) => {
      const available = this.gl.getQueryParameter(
        query,
        this.gl.QUERY_RESULT_AVAILABLE
      );
      if (!available) {
        return true;
      }
      if (!disjoint) {
        const elapsedNs = this.gl.getQueryParameter(
          query,
          this.gl.QUERY_RESULT
        );
        this.onSample(label, elapsedNs / 1e6);
      }
      this.gl.deleteQuery(query);
      return false;
    });
  }
  dispose() {
    if (this.gl) {
      if (this.active) {
        this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
        this.gl.deleteQuery(this.active);
      }
      this.pending.forEach(({ query }) => this.gl.deleteQuery(query));
    }
    this.active = null;
    this.pending = [];
  }
}

const FULL_HEAD_MPR_PLANE_TOLERANCE = 1e-6;
const dot$4 = (left, right) => left.x * right.x + left.y * right.y + left.z * right.z;
const subtract$2 = (left, right) => ({
  x: left.x - right.x,
  y: left.y - right.y,
  z: left.z - right.z
});
const length$3 = (point) => Math.hypot(point.x, point.y, point.z);
const isSameFullHeadMprPhysicalPlane = (left, right) => {
  const leftLength = length$3(left.normal);
  const rightLength = length$3(right.normal);
  const direction = dot$4(left.normal, right.normal) < 0 ? -1 : 1;
  const normalDifference = Math.hypot(
    left.normal.x / leftLength - direction * right.normal.x / rightLength,
    left.normal.y / leftLength - direction * right.normal.y / rightLength,
    left.normal.z / leftLength - direction * right.normal.z / rightLength
  );
  return left.volumeFileID === right.volumeFileID && left.projection === right.projection && normalDifference <= FULL_HEAD_MPR_PLANE_TOLERANCE && Math.abs(dot$4(subtract$2(right.centerMm, left.centerMm), left.normal) / leftLength) <= FULL_HEAD_MPR_PLANE_TOLERANCE;
};
const isSameFullHeadMprPlane = (left, right) => {
  const direction = dot$4(left.normal, right.normal) < 0 ? -1 : 1;
  return isSameFullHeadMprPhysicalPlane(left, right) && left.sampling === right.sampling && left.thicknessMm === right.thicknessMm && left.sampleOffsetsMm.length === right.sampleOffsetsMm.length && left.sampleOffsetsMm.every(
    (offset, index) => Math.abs(offset - direction * right.sampleOffsetsMm[direction < 0 ? right.sampleOffsetsMm.length - 1 - index : index]) <= FULL_HEAD_MPR_PLANE_TOLERANCE
  );
};
const isSameFullHeadMprAnnotationPlane = ({
  isSeries,
  left,
  right
}) => isSeries ? isSameFullHeadMprPhysicalPlane(left, right) : isSameFullHeadMprPlane(left, right);
const makeFullHeadMprPointTransform = ({
  from,
  to
}) => (point) => {
  const u = (point.x / from.widthPx - 0.5) * from.view.widthMm;
  const v = (0.5 - point.y / from.heightPx) * from.view.heightMm;
  const delta = subtract$2({
    x: from.view.centerMm.x + from.view.uAxis.x * u + from.view.vAxis.x * v,
    y: from.view.centerMm.y + from.view.uAxis.y * u + from.view.vAxis.y * v,
    z: from.view.centerMm.z + from.view.uAxis.z * u + from.view.vAxis.z * v
  }, to.view.centerMm);
  return {
    x: (dot$4(delta, to.view.uAxis) / to.view.widthMm + 0.5) * to.widthPx,
    y: (0.5 - dot$4(delta, to.view.vAxis) / to.view.heightMm) * to.heightPx
  };
};
const freezeFullHeadMPRView = (source) => {
  const view = {
    ...source,
    centerMm: Object.freeze({ ...source.centerMm }),
    normal: Object.freeze({ ...source.normal }),
    sampleOffsetsMm: [...source.sampleOffsetsMm],
    uAxis: Object.freeze({ ...source.uAxis }),
    vAxis: Object.freeze({ ...source.vAxis }),
    window: Object.freeze({ ...source.window })
  };
  Object.freeze(view.sampleOffsetsMm);
  return Object.freeze(view);
};
const makeFullHeadMPRAnnotationMatrix = ({
  camera,
  from,
  to
}) => {
  const transform = makeFullHeadMprPointTransform({ from, to });
  const unproject = (x, y) => {
    const point = transform({ x, y: -y });
    return new Vector3(
      point.x / to.widthPx * 2 - 1,
      1 - point.y / to.heightPx * 2,
      0
    ).unproject(camera);
  };
  const origin = unproject(0, 0);
  return new Matrix4().makeBasis(
    unproject(1, 0).sub(origin),
    unproject(0, 1).sub(origin),
    new Vector3(0, 0, 1)
  ).setPosition(origin);
};

class Command {
  constructor() {
  }
}

class ContrastBrightnessCommand extends Command {
  constructor(initialBrightness, initialContrast, brightness, contrast, image) {
    super();
    this.initialBrightness = initialBrightness;
    this.initialContrast = initialContrast;
    this.brightness = brightness;
    this.contrast = contrast;
    this.image = image;
  }
  execute(view) {
    this.image.brightness = this.brightness;
    this.image.contrast = this.contrast;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "brightnessContrastChanged", id: this.image.id, meta: this.image.meta, brightness: this.brightness, contrast: this.contrast });
    return true;
  }
  undo(view) {
    this.image.brightness = this.initialBrightness;
    this.image.contrast = this.initialContrast;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "brightnessContrastChanged", id: this.image.id, meta: this.image.meta, brightness: this.initialBrightness, contrast: this.initialContrast });
    return true;
  }
}

class SharpnessCommand extends Command {
  constructor(initialSharpness, sharpness, image) {
    super();
    this.initialSharpness = initialSharpness;
    this.sharpness = sharpness;
    this.image = image;
  }
  execute(view) {
    this.image.sharpness = this.sharpness;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "sharpnessChanged", id: this.image.id, meta: this.image.meta, sharpness: this.sharpness });
    return true;
  }
  undo(view) {
    this.image.sharpness = this.initialSharpness;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "sharpnessChanged", id: this.image.id, meta: this.image.meta, sharpness: this.initialSharpness });
    return true;
  }
}

class EntityContainer extends Container {
  constructor(parent, objects = []) {
    super(parent, objects);
  }
  size() {
    return this.objects.length;
  }
  add(entity) {
    if (Array.isArray(entity)) {
      const added = [...entity].filter(this.addOne.bind(this));
      this.hostObject.getLayer()?.getView().dispatchEvent({ type: "add", entities: added, container: this.hostObject });
      this.hostObject.dispatchEvent({ type: "add", entities: added });
    } else {
      if (this.addOne(entity)) {
        this.hostObject.getLayer()?.getView().dispatchEvent({ type: "add", entities: [entity], container: this.hostObject });
        this.hostObject.dispatchEvent({ type: "add", entities: [entity] });
      }
    }
  }
  delete(entity) {
    if (Array.isArray(entity)) {
      const deleted = entity.filter((child) => this.deleteOne(child));
      if (deleted.length) {
        this.hostObject.getLayer()?.getView().dispatchEvent({ type: "delete", entities: deleted, container: this.hostObject });
        this.hostObject.dispatchEvent({ type: "delete", entities: deleted });
      }
    } else {
      if (this.deleteOne(entity)) {
        this.hostObject.getLayer()?.getView().dispatchEvent({ type: "delete", entities: [entity], container: this.hostObject });
        this.hostObject.dispatchEvent({ type: "delete", entities: [entity] });
      }
    }
  }
  addOne(entity) {
    if (entity.parent !== this.hostObject) {
      if (entity.parent) {
        entity.parent.delete(entity);
      }
      this.objects.push(entity);
      entity.parent = this.hostObject;
      this.hostObject.object3d?.add(entity.object3d);
      return true;
    }
    return false;
  }
  deleteOne(entity) {
    if (entity.parent === this.hostObject) {
      const index = this.hostObject.children.indexOf(entity);
      if (index >= 0) {
        this.hostObject.children.splice(index, 1);
      }
      entity.parent = void 0;
      this.hostObject.object3d?.remove(entity.object3d);
      return true;
    }
    return false;
  }
}

const entityEventNames = ["transform", "update", "delete", "add"];
const safeDispose = (dispose) => {
  try {
    dispose();
  } catch {
  }
};
class Entity extends EventDispatcher {
  constructor(object3D) {
    super(entityEventNames);
    this.disposed = false;
    this.object3d = object3D;
    this.children = [];
    this.container = new EntityContainer(this, this.children);
    this.components = /* @__PURE__ */ new Map();
  }
  add(children) {
    this.container.add(children);
  }
  delete(children) {
    this.container.delete(children);
  }
  getBaseClass(component) {
    const prototype = Object.getPrototypeOf(component);
    if (prototype === Component) {
      return component;
    } else {
      return this.getBaseClass(prototype);
    }
  }
  getComponent(type) {
    return this.components.get(type);
  }
  setComponent(component, instance) {
    const BaseConcreteClass = this.getBaseClass(component);
    if (instance) {
      this.components.set(BaseConcreteClass, instance);
    } else if (component) {
      const instance2 = new component(this);
      !this.components.has(component) && this.components.set(BaseConcreteClass, instance2);
      return instance2;
    }
  }
  removeComponents() {
    this.components.clear();
  }
  removeComponent(component) {
    const BaseConcreteClass = this.getBaseClass(component);
    this.components.delete(BaseConcreteClass);
  }
  getLayer() {
    return this.layer || this.parent?.getLayer();
  }
  setLayer(layer) {
    if (!this.layer?.has(this)) {
      if (layer?.has(this)) {
        this.layer = layer;
      } else {
        this.layer = void 0;
      }
    }
  }
  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.children.slice().forEach((child) => safeDispose(() => child.dispose()));
    this.object3d.traverse((obj) => {
      if (obj.geometry) {
        safeDispose(() => obj.geometry.dispose());
      }
      if (obj.material) {
        if (obj.material instanceof Material) {
          safeDispose(() => obj.material.dispose());
        } else if (Array.isArray(obj.material)) {
          obj.material.forEach((material) => material instanceof Material && safeDispose(() => material.dispose()));
        }
      }
    });
    safeDispose(() => this.removeComponents());
    safeDispose(() => this.deleteListeners());
    safeDispose(() => this.object3d.clear());
  }
  traverse(callback) {
    callback(this);
    this.children.forEach((child) => child.traverse(callback));
  }
}

var vertexShader$7 = "#define GLSLIFY 1\nattribute vec3 position;attribute vec2 uv;uniform mat4 modelViewMatrix;uniform mat4 projectionMatrix;uniform mat3 uvMatrix;varying vec2 out_uv;varying vec2 out_transformed_uv;void main(){gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);out_transformed_uv=(uvMatrix*vec3(uv,1)).xy;out_uv=uv;}"; // eslint-disable-line

var fragmentShader$7 = "precision highp float;\n#define GLSLIFY 1\nuniform sampler2D dataTexture;uniform vec2 resolution;uniform float sharpness;uniform float brightness;uniform float contrast;uniform float invert;varying vec2 out_uv;varying vec2 out_transformed_uv;vec4 texsample(int x,int y,in vec2 uv){return texture2D(dataTexture,(uv+vec2(x,y)/resolution));}vec4 sharpnessFilter(vec2 uv){vec4 sum=texsample(-1,-1,uv)*-1.+texsample(-1,0,uv)*-1.+texsample(-1,1,uv)*-1.+texsample(0,-1,uv)*-1.+texsample(0,0,uv)*9.+texsample(0,1,uv)*-1.+texsample(1,-1,uv)*-1.+texsample(1,0,uv)*-1.+texsample(1,1,uv)*-1.;return sum;}void main(){vec4 color=sharpnessFilter(out_transformed_uv);color=mix(texture2D(dataTexture,out_transformed_uv),color,sharpness);color.rgb+=brightness;if(contrast>0.0){color.rgb=(color.rgb-0.5)/(1.0-contrast)+0.5;}else{color.rgb=(color.rgb-0.5)*(1.0+contrast)+0.5;}color=mix(color,vec4((1.0-color.xyz),color.w),invert);gl_FragColor=color;}"; // eslint-disable-line

class RasterImageMaterial extends RawShaderMaterial {
  constructor(texture, width, height, uvMatrix) {
    super({
      vertexShader: vertexShader$7,
      fragmentShader: fragmentShader$7,
      side: DoubleSide,
      uniforms: {
        dataTexture: { value: texture },
        uvMatrix: { value: uvMatrix },
        brightness: { value: 0 },
        contrast: { value: 0 },
        sharpness: { value: 0 },
        resolution: { value: new Vector2(width, height) },
        invert: { value: 0 }
      }
    });
  }
  dispose() {
    super.dispose();
    this.uniforms.dataTexture.value.dispose();
  }
}

class ViewportComponent extends Component {
  constructor(entity) {
    super(entity);
    this.viewports = /* @__PURE__ */ new Set();
    this.recursiveViewports = /* @__PURE__ */ new Set();
    this.onAdd = this.onAdd.bind(this);
    this.entity.addEventListener("add", this.onAdd);
  }
  onAdd(event) {
    event.entities.forEach((entity) => {
      let entityComponent = entity.getComponent(ViewportComponent);
      if (!entityComponent) {
        entityComponent = entity.setComponent(ViewportComponent);
      }
      this.recursiveViewports.forEach((viewport) => {
        if (!entityComponent.hasViewport(viewport)) {
          entityComponent.addViewport(viewport, true);
        }
      });
    });
  }
  addViewport(name, recursive = true) {
    const viewport = name instanceof Viewport ? name : App.Instance().view.getViewport(name);
    if (viewport) {
      const cameraLayer = viewport.cameraLayer;
      if (cameraLayer) {
        this.viewports.add(viewport);
        this.entity.object3d.layers.enable(cameraLayer);
        this.entity.object3d.layers.disable(0);
        if (recursive) {
          this.entity.children.forEach((entity) => {
            const component = entity.getComponent(ViewportComponent);
            component?.addViewport(name, true);
          });
          this.recursiveViewports.add(viewport);
        }
      }
    }
  }
  deleteViewport(name, recursive = true) {
    const viewport = name instanceof Viewport ? name : App.Instance().view.getViewport(name);
    if (viewport) {
      const cameraLayer = viewport.cameraLayer;
      if (cameraLayer) {
        this.viewports.delete(viewport);
        this.entity.object3d.layers.disable(cameraLayer);
        if (recursive) {
          this.entity.children.forEach((entity) => {
            const component = entity.getComponent(ViewportComponent);
            component?.deleteViewport(name, true);
          });
        }
      }
    }
  }
  deleteAllViewports(recursive = true) {
    this.viewports.clear();
    this.entity.object3d.layers.set(0);
    if (recursive) {
      this.entity.children.forEach((entity) => {
        const component = entity.getComponent(ViewportComponent);
        component?.deleteAllViewports(true);
      });
    }
  }
  hasViewport(name) {
    const viewport = name instanceof Viewport ? name : App.Instance().view.getViewport(name);
    return this.viewports.has(viewport);
  }
}

var vertexShader$6 = "#define GLSLIFY 1\nin vec3 position;uniform mat4 modelViewMatrix;uniform mat4 projectionMatrix;uniform mat3 uvMatrix;in vec2 uv;out vec2 out_uv;out vec2 out_transformed_uv;void main(){gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);out_transformed_uv=(uvMatrix*vec3(uv,1)).xy;out_uv=uv;}"; // eslint-disable-line

var fragmentShader$6 = "precision highp float;\n#define GLSLIFY 1\nuniform sampler2D dataTexture;uniform vec2 resolution;uniform float sharpness;uniform float brightness;uniform float contrast;uniform float ww;uniform float wmin;uniform float invert;uniform float isNormalized;in vec2 out_uv;in vec2 out_transformed_uv;out vec4 out_color;const vec2 offsets[9]=vec2[](vec2(-1.0f,-1.0f),vec2(0.0f,-1.0f),vec2(1.0f,-1.0f),vec2(-1.0f,0.0f),vec2(0.0f,0.0f),vec2(1.0f,0.0f),vec2(-1.0f,1.0f),vec2(0.0f,1.0f),vec2(1.0f,1.0f));const float kernel[9]=float[](-1.0f,-1.0f,-1.0f,-1.0f,9.0f,-1.0f,-1.0f,-1.0f,-1.0f);vec3 processImageValue(vec3 value){if(isNormalized>0.5){value=value*255.0;}value=(value-wmin)/max(ww,0.001);value+=brightness;if(contrast>0.0f){value=(value-0.5f)/(1.0f-contrast)+0.5f;}else{value=(value-0.5f)*(1.0f+contrast)+0.5f;}value=mix(value,1.0f-value,clamp(invert,0.0f,1.0f));return value;}void main(){\n#ifdef GRAYSCALE\nfloat value=0.0f;for(int i=0;i<9;i++){value+=texture(dataTexture,out_transformed_uv+offsets[i]/resolution).r*kernel[i];}value=mix(texture(dataTexture,out_transformed_uv).r,value,clamp(sharpness,0.0f,1.0f));value=processImageValue(vec3(value)).r;out_color=vec4(value,value,value,1.0f);\n#else\nvec3 value=vec3(0.0f);for(int i=0;i<9;i++){value+=texture(dataTexture,out_transformed_uv+offsets[i]/resolution).rgb*kernel[i];}value=mix(texture(dataTexture,out_transformed_uv).rgb,value,clamp(sharpness,0.0f,1.0f));value=processImageValue(value);out_color=vec4(value,1.0f);\n#endif\n}"; // eslint-disable-line

class DicomImageMaterial extends RawShaderMaterial {
  constructor(texture, width, height, uvMatrix) {
    const { ww, wc } = texture;
    super({
      vertexShader: vertexShader$6,
      fragmentShader: fragmentShader$6,
      side: DoubleSide,
      defines: {
        GRAYSCALE: texture.format === RedFormat
      },
      uniforms: {
        dataTexture: { value: texture },
        uvMatrix: { value: uvMatrix },
        brightness: { value: 0 },
        contrast: { value: 0 },
        sharpness: { value: 0 },
        resolution: { value: new Vector2(width, height) },
        ww: { value: ww },
        wmin: { value: wc - ww / 2 },
        invert: { value: 0 },
        isNormalized: { value: texture.isNormalized ? 1 : 0 }
      },
      glslVersion: GLSL3
    });
  }
  dispose() {
    super.dispose();
    this.uniforms.dataTexture.value.dispose();
  }
}

class ImageEntity extends Entity {
  constructor(texture, props) {
    super(new Mesh(new PlaneGeometry()));
    this.object3d.geometry.computeBoundingBox();
    if (texture) {
      const width = props?.width || texture.image?.width || 0;
      const height = props?.height || texture.image?.height || 0;
      this.setMaterial(texture, width, height);
    }
    this.setComponent(ViewportComponent);
  }
  setMaterial(texture, width = texture.image?.width || 0, height = texture.image?.height || 0) {
    this.object3d.material = texture instanceof DicomTexture ? new DicomImageMaterial(texture, width, height, new Matrix3()) : new RasterImageMaterial(texture, width, height, new Matrix3());
    this.object3d.scale.set(this.width, this.height, 1);
    this.object3d.material.needsUpdate = true;
  }
  invertColor() {
    this.object3d.material.uniforms.invert.value = 1;
  }
  straightColor() {
    this.object3d.material.uniforms.invert.value = 0;
  }
  toggleInversion() {
    this.object3d.material.uniforms.invert.value = Number(!this.object3d.material.uniforms.invert.value);
  }
  getInversion() {
    return this.object3d.material.uniforms.invert.value ? "invert" : "straight";
  }
  get width() {
    return this.object3d.material.uniforms.resolution.value.x;
  }
  get height() {
    return this.object3d.material.uniforms.resolution.value.y;
  }
  set texture(texture) {
    this.object3d.material.uniforms.dataTexture.value = texture;
  }
  get texture() {
    return this.object3d.material.uniforms.dataTexture.value;
  }
  set brightness(brightness) {
    this.object3d.material.uniforms.brightness.value = brightness;
  }
  set contrast(contrast) {
    this.object3d.material.uniforms.contrast.value = contrast;
  }
  set sharpness(sharpness) {
    this.object3d.material.uniforms.sharpness.value = sharpness;
  }
  get brightness() {
    return this.object3d.material.uniforms.brightness.value;
  }
  get contrast() {
    return this.object3d.material.uniforms.contrast.value;
  }
  get sharpness() {
    return this.object3d.material.uniforms.sharpness.value;
  }
  useUVMatrix(x0, y0) {
    if (!this.texture.image) return;
    const { uvMatrix } = this.computeUvMatrix(this.texture, x0, y0);
    this.object3d.material.uniforms.uvMatrix.value = uvMatrix;
    this.texture.wrapS = RepeatWrapping;
    this.texture.wrapT = RepeatWrapping;
    this.texture.needsUpdate = true;
    this.object3d.material.needsUpdate = true;
  }
  computeUvMatrix(texture, x0 = 0, y0 = 0, cropWidth = texture.image.width - x0, cropHeight = texture.image.height - y0, offset) {
    const actualWidth = texture.image.width;
    const actualHeight = texture.image.height;
    y0 = actualHeight - this.height - y0;
    if (offset) {
      x0 -= offset.x;
      y0 += offset.y;
    }
    const xFactor = 1 / this.width;
    const yFactor = 1 / this.height;
    const offsetX = offset ? offset.x : 0;
    const offsetY = offset ? this.height - (offset.y + cropHeight) : this.height - cropHeight;
    const w = cropWidth * xFactor;
    const h = cropHeight * yFactor;
    const minX = offsetX * xFactor;
    const minY = offsetY * yFactor;
    const cropMatrix = new Matrix3().set(1 / w, 0, -minX / w, 0, 1 / h, -minY / h, 0, 0, 1);
    const uvMatrix = new Matrix3().setUvTransform(x0 / actualWidth, y0 / actualHeight, this.width / actualWidth, this.height / actualHeight, 0, 0, 0);
    return { uvMatrix, cropMatrix };
  }
  toJson() {
    throw new Error("Method not implemented.");
  }
}

const targets$1 = /* @__PURE__ */ new WeakMap();
const registerImageOptionsToolTarget = (viewport, resolve) => {
  targets$1.set(viewport, resolve);
  return () => targets$1.delete(viewport);
};
const resolveImageOptionsToolTarget = (viewport) => {
  const resolve = targets$1.get(viewport);
  if (resolve) {
    return resolve();
  }
  const view = viewport.view;
  const imageController = view.imageController;
  const assetID = imageController?.viewportToImageMap.get(viewport);
  const image = assetID ? imageController.imageContainers.get(assetID)?.getImage() : void 0;
  if (!image || !(image instanceof ImageEntity)) {
    return null;
  }
  const isCurrent = () => imageController.viewportToImageMap.get(viewport) === assetID && imageController.imageContainers.get(assetID)?.getImage() === image;
  const applyOptions = (options) => {
    image.brightness = options.brightness;
    image.contrast = options.contrast;
    image.sharpness = options.sharpness;
    view.dispatchEvent({ type: "render" });
  };
  return {
    readonly: false,
    getOptions: () => ({
      brightness: image.brightness,
      contrast: image.contrast,
      invert: image.getInversion() === "invert",
      sharpness: image.sharpness
    }),
    isCurrent,
    preview: applyOptions,
    cancel: (initialOptions) => {
      if (isCurrent()) {
        applyOptions(initialOptions);
      }
    },
    commit: (tool, initialOptions) => {
      if (tool === "brightnessContrast") {
        view.executeCommand(new ContrastBrightnessCommand(
          initialOptions.brightness,
          initialOptions.contrast,
          image.brightness,
          image.contrast,
          image
        ), false);
        view.dispatchEvent({
          type: "brightnessContrastChanged",
          id: image.id,
          meta: image.meta,
          brightness: image.brightness,
          contrast: image.contrast
        });
      } else {
        view.executeCommand(new SharpnessCommand(initialOptions.sharpness, image.sharpness, image), false);
        view.dispatchEvent({ type: "sharpnessChanged", id: image.id, meta: image.meta, sharpness: image.sharpness });
      }
    }
  };
};

class ImageContrastBrightness extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.initialCursor = "";
    this.start = new Vector2();
    this.delta = new Vector2();
    this.target = null;
    this.speed = 1.5;
    this.mouseDown = this.mouseDown.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseUp = this.mouseUp.bind(this);
  }
  activate() {
    App.Instance().mouseController.addListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left", 20);
    this.initialCursor = this.hostObject.getDomElement().style.cursor;
    this.hostObject.getDomElement().style.cursor = "all-scroll";
  }
  deactivate() {
    this.cancelInteraction();
    App.Instance().mouseController.removeListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.removeListener("pointermove", this.mouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerup", this.mouseUp, this.hostObject.getDomElement(), "left");
    this.hostObject.getDomElement().style.cursor = this.initialCursor;
  }
  mouseDown(event) {
    if (event.shiftKey || event.ctrlKey || event.metaKey) return;
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    const target = resolveImageOptionsToolTarget(this.hostObject);
    if (target && !target.readonly) {
      this.target = target;
      this.initialOptions = target.getOptions();
      MIRender.mouseController.addListener("pointermove", this.mouseMove, document, 9);
      MIRender.mouseController.addListener("pointerup", this.mouseUp, document, "left", 20);
    }
  }
  mouseMove(event) {
    if (!this.target?.isCurrent()) {
      this.cancelInteraction();
      return;
    }
    event.preventDefault();
    this.delta.x = event.clientX - this.start.x;
    this.delta.y = -(event.clientY - this.start.y);
    this.delta.multiplyScalar(this.speed);
    const options = this.target.getOptions();
    this.target.preview({
      ...options,
      brightness: MathUtils.clamp(options.brightness + this.delta.y / this.hostObject.rect.height, -1, 1),
      contrast: MathUtils.clamp(options.contrast + this.delta.x / this.hostObject.rect.width, -1, 1)
    });
    this.start.x = event.clientX;
    this.start.y = event.clientY;
  }
  mouseUp() {
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, document);
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, document, "left");
    const target = this.target;
    this.target = null;
    if (target?.isCurrent()) {
      target.commit("brightnessContrast", this.initialOptions);
    }
  }
  cancelInteraction() {
    const target = this.target;
    if (!target) return;
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, document);
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, document, "left");
    this.target = null;
    target?.cancel?.(this.initialOptions);
  }
}

class ImageSharpness extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.initialCursor = "";
    this.start = new Vector2();
    this.delta = new Vector2();
    this.target = null;
    this.speed = 5;
    this.mouseDown = this.mouseDown.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseUp = this.mouseUp.bind(this);
  }
  activate() {
    MIRender.mouseController.addListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left", 20);
    this.initialCursor = this.hostObject.getDomElement().style.cursor;
    this.hostObject.getDomElement().style.cursor = "all-scroll";
  }
  deactivate() {
    this.cancelInteraction();
    MIRender.mouseController.removeListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left");
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, this.hostObject.getDomElement());
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, this.hostObject.getDomElement(), "left");
    this.hostObject.getDomElement().style.cursor = this.initialCursor;
  }
  mouseDown(event) {
    if (event.shiftKey || event.ctrlKey || event.metaKey) return;
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    const target = resolveImageOptionsToolTarget(this.hostObject);
    if (target && !target.readonly) {
      this.target = target;
      this.initialOptions = target.getOptions();
      App.Instance().mouseController.addListener("pointermove", this.mouseMove, document, 9);
      App.Instance().mouseController.addListener("pointerup", this.mouseUp, document, "left", 20);
    }
  }
  mouseMove(event) {
    if (!this.target?.isCurrent()) {
      this.cancelInteraction();
      return;
    }
    event.preventDefault();
    this.delta.x = event.clientX - this.start.x;
    this.delta.y = -(event.clientY - this.start.y);
    this.delta.multiplyScalar(this.speed);
    const options = this.target.getOptions();
    this.target.preview({
      ...options,
      sharpness: MathUtils.clamp(options.sharpness + this.delta.y / this.hostObject.rect.height, 0, 3)
    });
    this.start.x = event.clientX;
    this.start.y = event.clientY;
  }
  mouseUp() {
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, document);
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, document, "left");
    const target = this.target;
    this.target = null;
    if (target?.isCurrent()) {
      target.commit("sharpness", this.initialOptions);
    }
  }
  cancelInteraction() {
    const target = this.target;
    if (!target) return;
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, document);
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, document, "left");
    this.target = null;
    target?.cancel?.(this.initialOptions);
  }
}

const sameOptions = (left, right) => left.brightness === right.brightness && left.contrast === right.contrast && left.invert === right.invert && left.sharpness === right.sharpness;
class FullHeadMPRImageOptionsController {
  constructor(host) {
    this.host = host;
    this.panes = /* @__PURE__ */ new Map();
    this.targets = /* @__PURE__ */ new Map();
    this.tool = null;
  }
  attach(projection, viewport) {
    const tools = {
      brightnessContrast: new ImageContrastBrightness(viewport),
      sharpness: new ImageSharpness(viewport)
    };
    const unregister = registerImageOptionsToolTarget(viewport, () => this.resolveTarget(projection));
    this.panes.set(projection, { tools, unregister });
    if (this.tool) {
      tools[this.tool].init();
    }
  }
  setTool(tool) {
    if (this.tool === tool) {
      return;
    }
    this.panes.forEach(({ tools }) => {
      if (this.tool) {
        tools[this.tool].dispose();
      }
      if (tool) {
        tools[tool].init();
      }
    });
    this.tool = tool;
  }
  hasTool() {
    return this.tool !== null;
  }
  setTarget(projection, target) {
    const previous = this.targets.get(projection);
    if (JSON.stringify(previous ?? null) === JSON.stringify(target)) {
      return;
    }
    this.cancel(projection);
    if (target) {
      this.targets.set(projection, { ...target, view: freezeFullHeadMPRView(target.view) });
    } else {
      this.targets.delete(projection);
    }
  }
  update(projection) {
    const pane = this.panes.get(projection);
    if (pane?.isGestureCurrent && !pane.isGestureCurrent()) {
      this.cancel(projection);
    }
  }
  cancel(projection) {
    this.panes.forEach((pane, candidate) => {
      if (!projection || projection === candidate) {
        Object.values(pane.tools).forEach((tool) => tool.cancelInteraction());
        pane.isGestureCurrent = void 0;
      }
    });
  }
  clear() {
    this.cancel();
    this.targets.clear();
  }
  dispose() {
    this.setTool(null);
    this.clear();
    this.panes.forEach(({ unregister }) => unregister());
    this.panes.clear();
  }
  resolveTarget(projection) {
    const target = this.targets.get(projection);
    const pane = this.panes.get(projection);
    const snapshot = this.host.getSnapshot(projection);
    if (!target || target.readonly || !pane || !snapshot || !this.host.isReady(projection) || !isSameFullHeadMprPlane(target.view, snapshot.view)) {
      return null;
    }
    this.host.onStart(projection);
    const frozen = { ...snapshot, view: freezeFullHeadMPRView(snapshot.view) };
    const frameKey = JSON.stringify(frozen);
    const optionsState = { preview: this.host.getOptions(projection) };
    const isCurrent = () => this.targets.get(projection) === target && JSON.stringify(this.host.getSnapshot(projection)) === frameKey;
    pane.isGestureCurrent = isCurrent;
    return {
      readonly: false,
      getOptions: () => this.host.getOptions(projection),
      isCurrent,
      preview: (options) => {
        optionsState.preview = options;
        this.host.setOptions(projection, options);
      },
      commit: (_tool, initialOptions) => {
        pane.isGestureCurrent = void 0;
        const options = this.host.getOptions(projection);
        if (isCurrent() && !sameOptions(initialOptions, options)) {
          this.host.view.dispatchEvent({
            type: "fullHeadMPRImageOptionsChanged",
            heightPx: frozen.heightPx,
            options: { ...options },
            projection,
            targetID: target.id,
            view: frozen.view,
            widthPx: frozen.widthPx
          });
        }
      },
      cancel: (initialOptions) => {
        pane.isGestureCurrent = void 0;
        if (this.targets.get(projection) === target && sameOptions(this.host.getOptions(projection), optionsState.preview)) {
          this.host.setOptions(projection, initialOptions);
        }
      }
    };
  }
}

class SelectComponent extends Component {
  constructor(entity, highlighter = new ColorHighlighter(entity)) {
    super(entity);
    this.selected = false;
    this.highlighter = highlighter;
  }
  select() {
    this.selected = true;
    this.highlighter.highlight();
  }
  deselect() {
    this.selected = false;
    this.highlighter.unHighlight();
  }
  isSelected() {
    return this.selected;
  }
}

class Hover extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onHover = this.onHover.bind(this);
    this.onUnhover = this.onUnhover.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onChangeActiveViewport = this.onChangeActiveViewport.bind(this);
  }
  activate() {
    this.view.addEventListener("ActiveViewportWillChange", this.onChangeActiveViewport);
    if (this.view.getActiveViewport() == this.hostObject) {
      this.view.addEventListener("hover", this.onHover);
      this.view.addEventListener("unhover", this.onUnhover);
    }
    App.Instance().mouseController.addListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left", 15);
  }
  deactivate() {
    this.view.removeEventListener("hover", this.onHover);
    this.view.removeEventListener("unhover", this.onUnhover);
    this.view.removeEventListener("ActiveViewportWillChange", this.onChangeActiveViewport);
    App.Instance().mouseController.removeListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left");
  }
  onChangeActiveViewport(event) {
    if (event.next === this.hostObject) {
      this.view.addEventListener("hover", this.onHover);
      this.view.addEventListener("unhover", this.onUnhover);
    } else {
      this.view.removeEventListener("hover", this.onHover);
      this.view.removeEventListener("unhover", this.onUnhover);
    }
  }
  onHover(event) {
    if (this.isSelected(event.entity)) return;
    const hoverComponent = event.entity.getComponent(HoverComponent);
    if (hoverComponent) {
      hoverComponent.hover();
      this.view.dispatchEvent({ type: "render" });
    }
  }
  onUnhover(event) {
    if (this.isSelected(event.entity)) return;
    const hoverComponent = event.entity.getComponent(HoverComponent);
    if (hoverComponent) {
      hoverComponent.unhover();
      this.view.dispatchEvent({ type: "render" });
    }
  }
  onMouseDown() {
    const entity = this.view.raycastController.getObject();
    entity && entity.getComponent(Raycast) && entity.getComponent(SelectComponent) && this.onUnhover({ entity });
  }
  isSelected(entity) {
    const selectComponent = entity.getComponent(SelectComponent);
    return selectComponent && this.hostObject.view.selectController.has(selectComponent);
  }
}

class Select extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onStart = this.onStart.bind(this);
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
  }
  activate() {
    App.Instance().mouseController.addListener("pointerdown", this.onStart, this.hostObject.getDomElement(), "left", 10);
    this.view.addEventListener("transformStart", this.onTransformStart);
  }
  deactivate() {
    App.Instance().mouseController.removeListener("pointerdown", this.onStart, this.hostObject.getDomElement(), "left");
    this.view.removeEventListener("transformStart", this.onTransformStart);
    this.view.removeEventListener("transformEnd", this.onTransformEnd);
  }
  onStart(event) {
    if (!event.shiftKey) {
      const object = this.view.raycastController.getObject();
      if (object) {
        const selectComponent = object.getComponent(SelectComponent);
        if (selectComponent) {
          if (!this.view.selectController.has(selectComponent)) {
            this.select(selectComponent);
          }
        }
      }
    }
  }
  onTransformStart() {
    App.Instance().mouseController.removeListener("pointerdown", this.onStart, this.hostObject.getDomElement(), "left", true);
    this.view.addEventListener("transformEnd", this.onTransformEnd);
  }
  onTransformEnd() {
    App.Instance().mouseController.addListener("pointerdown", this.onStart, this.hostObject.getDomElement(), "left", 10);
    this.view.removeEventListener("transformEnd", this.onTransformEnd);
  }
  select(objects) {
    this.view.selectController.clear();
    this.view.selectController.add(objects);
  }
}

class Primitive extends Entity {
  constructor(object3D, viewport) {
    super(object3D);
    this.onBeforeRenderSet = /* @__PURE__ */ new Set();
    const viewportComponent = this.setComponent(ViewportComponent);
    viewport && viewportComponent.addViewport(viewport);
    const superOnBeforeRender = this.object3d.onBeforeRender.bind(this.object3d);
    this.onBeforeRenderSet.add(superOnBeforeRender);
    this.object3d.onBeforeRender = (...args) => {
      this.onBeforeRenderSet.forEach((fn) => fn(...args));
    };
  }
  getViewport() {
    const viewports = this.getComponent(ViewportComponent)?.viewports;
    return viewports && viewports.size ? [...viewports][0] : void 0;
  }
  dispose() {
    this.onBeforeRenderSet.clear();
    super.dispose();
  }
  toJson() {
    throw new Error("Method not implemented.");
  }
}

const viewport = new Vector4();
class LineLikeEntity extends Primitive {
  constructor(props, type, viewport2) {
    let linewidth, dashed, dashScale, dashSize, dashOffset, gapSize;
    let color;
    let opacity;
    if (typeof props === "function") {
      super(new props(), type);
      linewidth = 2;
      color = new Color(16777215);
      opacity = 1;
    } else {
      super(new type(), viewport2);
      linewidth = props.width;
      dashed = props.dashed;
      dashScale = props.dashScale;
      dashSize = props.dashSize;
      dashOffset = props.dashOffset;
      gapSize = props.gapSize;
      this.object3d.renderOrder = props.renderOrder || 0;
      color = new Color(props.color || 16777215);
      opacity = props.opacity ?? 1;
    }
    this.onBeforeRender = this.onBeforeRender.bind(this);
    if (this.object3d instanceof Line2) {
      this.object3d.geometry = new LineGeometry();
      this.object3d.material = new LineMaterial({
        linewidth,
        dashed: !!dashed,
        dashScale: dashScale ?? 1,
        dashSize: dashSize ?? 1,
        dashOffset: dashOffset ?? 0,
        gapSize: gapSize ?? 1,
        side: DoubleSide,
        vertexColors: false,
        worldUnits: false
      });
      this.onResize = this.onResize.bind(this);
      this.onBeforeRenderSet.add(this.onBeforeRender);
    } else {
      if (dashed) {
        this.object3d.material = new LineDashedMaterial({
          dashSize,
          gapSize,
          scale: dashScale
        });
      }
      this.object3d.geometry.setAttribute("position", new BufferAttribute(new Float32Array([]), 3));
    }
    this.object3d.material.color = color;
    this.object3d.material.opacity = opacity;
    if (opacity !== 1) {
      this.object3d.material.transparent = true;
    }
  }
  update() {
    if (this.object3d instanceof Line$1) {
      this.updateLine();
    } else {
      this.updateLine2();
    }
  }
  onBeforeRender(renderer) {
    renderer.getViewport(viewport);
    this.object3d.material.resolution.set(viewport.z, viewport.w);
  }
  onResize() {
    const [firstViewport] = this.getComponent(ViewportComponent).viewports;
    const { z, w } = firstViewport.size;
    this.object3d.material.resolution.set(z, w);
  }
  toJson() {
    return {
      color: this.object3d.material.color,
      width: this.object3d.material.linewidth
    };
  }
}

class Arc extends LineLikeEntity {
  constructor(props, type, viewport) {
    super(props, type, viewport);
    this.start = new Vector3(props.start.x, props.start.y, props.start.z);
    this.end = new Vector3(props.end.x, props.end.y, props.end.z);
    if ("radius" in props) {
      this.radius = props.radius;
    } else {
      this.center = props.center;
      this.radius = this.start.distanceTo(props.center);
    }
    this.segmentsNumber = props.segmentsNumber || 32;
  }
  computeMiddlePoint(point = new Vector3()) {
    let center = this.center ?? this.findCenter();
    if (!center) {
      const chord = this.end.clone().sub(this.start);
      return this.start.clone().add(chord.multiplyScalar(0.5));
    }
    const startAngle = Math.atan2(this.start.y - center.y, this.start.x - center.x);
    let endAngle = Math.atan2(this.end.y - center.y, this.end.x - center.x);
    let angleSize = endAngle - startAngle;
    if (Math.abs(angleSize) > Math.PI) {
      angleSize = angleSize - Math.sign(angleSize) * 2 * Math.PI;
    }
    const middleAngle = startAngle + angleSize / 2;
    const x = center.x + this.radius * Math.cos(middleAngle);
    const y = center.y + this.radius * Math.sin(middleAngle);
    return point.set(x, y, this.start.z);
  }
  updateLine() {
    const positions = this.object3d.geometry.getAttribute("position");
    const points = [...this.getSegments().map((p) => p.toArray()).flat()];
    if (points.length > 0) {
      positions.array.set([...points]);
      positions.needsUpdate = true;
    }
  }
  updateLine2() {
    const points = [...this.getSegments().map((p) => p.toArray()).flat()];
    if (points.length > 0) {
      this.object3d.geometry.setPositions([...points]);
    }
  }
  findCenter() {
    const chord = this.end.clone().sub(this.start);
    const chordLength = chord.length();
    if (chordLength / 2 > this.radius) {
      return null;
    }
    const centerHeight = Math.sqrt(this.radius * this.radius - chordLength * chordLength / 4);
    const midPoint = this.start.clone().add(chord.multiplyScalar(0.5));
    const normal = chord.clone().cross(new Vector3(0, 0, 1)).normalize();
    return midPoint.clone().add(normal.multiplyScalar(centerHeight));
  }
  computeArcParameters(center) {
    const startAngle = Math.atan2(this.start.y - center.y, this.start.x - center.x);
    const endAngle = Math.atan2(this.end.y - center.y, this.end.x - center.x);
    let angleSize = endAngle - startAngle;
    if (Math.abs(angleSize) > Math.PI) {
      angleSize = angleSize - Math.sign(angleSize) * 2 * Math.PI;
    }
    return {
      startAngle,
      angleSize,
      segments: Math.max(this.segmentsNumber, 2)
    };
  }
  computePointAtAngle(center, angle) {
    const x = center.x + this.radius * Math.cos(angle);
    const y = center.y + this.radius * Math.sin(angle);
    return new Vector3(x, y, this.start.z);
  }
  getSegments() {
    const points = [];
    let center;
    if (this.center) {
      this.radius = this.start.distanceTo(this.center);
      center = this.center;
    } else {
      center = this.findCenter();
    }
    if (!center) {
      points.push(this.start.clone());
      points.push(this.end.clone());
      return points;
    }
    const { startAngle, angleSize, segments } = this.computeArcParameters(center);
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const angle = startAngle + angleSize * t;
      points.push(this.computePointAtAngle(center, angle));
    }
    return points;
  }
}

const _Observer = class _Observer {
  constructor(object, property) {
    this.listeners = /* @__PURE__ */ new Set();
    this.object = object;
    this.property = property;
  }
  static observe(object, property, cb) {
    let map = _Observer.observables.get(object);
    let observer = map?.get(property);
    if (!observer) {
      observer = new _Observer(object, property);
      observer.listeners.add(cb);
      observer.value = object[property];
      Object.defineProperty(object, property, {
        writable: true,
        configurable: true,
        enumerable: true
      });
      Object.defineProperty(object, property, {
        set(x) {
          observer.value = x;
          observer.listeners.forEach((listener) => listener(x));
        },
        get() {
          return observer.value;
        }
      });
      if (map) {
        map.set(property, observer);
      } else {
        _Observer.observables.set(object, /* @__PURE__ */ new Map([[property, observer]]));
      }
    }
    observer.listeners.add(cb);
    return () => _Observer.stopObserving(object, property, cb);
  }
  static stopObserving(object, property, cb) {
    const map = _Observer.observables.get(object);
    const observer = map?.get(property);
    if (!map || !observer) {
      return;
    }
    if (cb) {
      observer.listeners.delete(cb);
      if (observer.listeners.size) {
        return;
      }
    }
    Object.defineProperty(object, property, { value: observer.value, writable: true, configurable: true, enumerable: true });
    map.delete(property);
    if (!map.size) {
      _Observer.observables.delete(object);
    }
  }
};
// Non-owning registry: each value strongly references its key (observer.object),
// so a FinalizationRegistry-based map would never release entries; WeakMap
// ephemeron semantics collect the key<->value cycle once the object dies.
_Observer.observables = /* @__PURE__ */ new WeakMap();
let Observer = _Observer;

const parentWorldScale = new Vector3();
const projectedPoint = new Vector3();
const inverseParent = new Matrix4();
const screenOrigin = new Vector3();
const screenXAxis = new Vector3();
const screenYAxis = new Vector3();
const screenNormal = new Vector3();
const parentOrigin = new Vector3();
const annotationWorldUnitsPerPixel = (camera, heightPx) => heightPx > 0 ? Math.abs(2 / (camera.projectionMatrix.elements[5] * heightPx)) : 0;
const annotationParentScaleInverse = (object, target) => {
  const worldScale = object.getWorldScale(parentWorldScale);
  return target.set(
    worldScale.x ? Math.abs(object.scale.x / worldScale.x) : 1,
    worldScale.y ? Math.abs(object.scale.y / worldScale.y) : 1
  );
};
const projectAnnotationPoint = (point, frame) => {
  const projected = frame.parent.localToWorld(projectedPoint.copy(point)).project(frame.camera);
  return { x: (projected.x + 1) * frame.widthPx / 2, y: (1 - projected.y) * frame.heightPx / 2 };
};
const unprojectAnnotationPoint = (x, y, depth, frame, target) => target.set(
  x / frame.widthPx * 2 - 1,
  1 - y / frame.heightPx * 2,
  depth
).unproject(frame.camera).applyMatrix4(inverseParent);
const placeAnnotationLabel = ({
  frame,
  label,
  object
}) => {
  if (frame.widthPx <= 0 || frame.heightPx <= 0) return;
  inverseParent.copy(frame.parent.matrixWorld).invert();
  const depth = parentOrigin.setFromMatrixPosition(frame.parent.matrixWorld).project(frame.camera).z;
  const angle = label.rotationDeg * Math.PI / 180;
  const origin = unprojectAnnotationPoint(label.x, label.y, depth, frame, screenOrigin);
  const xAxis = unprojectAnnotationPoint(label.x + Math.cos(angle), label.y + Math.sin(angle), depth, frame, screenXAxis).sub(origin);
  const yAxis = unprojectAnnotationPoint(label.x + Math.sin(angle), label.y - Math.cos(angle), depth, frame, screenYAxis).sub(origin);
  object.matrixAutoUpdate = false;
  object.matrix.makeBasis(xAxis, yAxis, screenNormal.crossVectors(xAxis, yAxis).normalize()).setPosition(origin);
  object.updateMatrixWorld(true);
};

class Handle extends Primitive {
  constructor(point, viewport) {
    super(new Mesh(), viewport);
    this.vector = new Vector4();
    // Enable only when the material and its shader expose the screenScale uniform.
    this.screenSized = false;
    this.point = point;
    this.object3d.position.copy(point);
    this.onBeforeRender = this.onBeforeRender.bind(this);
    this.onBeforeRenderSet.add(this.onBeforeRender);
  }
  bind() {
    Observer.observe(this.object3d.position, "x", () => this.point.x = this.object3d.position.x);
    Observer.observe(this.object3d.position, "y", () => this.point.y = this.object3d.position.y);
    Observer.observe(this.object3d.position, "z", () => this.point.z = this.object3d.position.z);
  }
  unBind() {
    Observer.stopObserving(this.object3d.position, "x");
    Observer.stopObserving(this.object3d.position, "y");
    Observer.stopObserving(this.object3d.position, "z");
  }
  dispose() {
    this.unBind();
    super.dispose();
  }
  update() {
    this.object3d.position.copy(this.point);
  }
  getShaderScale(camera, heightPx, target = new Vector2()) {
    const height = !this.screenSized && heightPx < 200 ? heightPx * 1.5 : heightPx;
    const zoom = Math.min(annotationWorldUnitsPerPixel(camera, height), this.object3d.material.uniforms.maxZoom.value);
    return this.getScreenScale(camera, heightPx, target).multiplyScalar(zoom);
  }
  getScreenScale(_camera, _heightPx, target) {
    return this.screenSized ? annotationParentScaleInverse(this.object3d, target) : target.set(1, 1);
  }
  onBeforeRender(renderer, _scene, camera) {
    const viewport = renderer.getViewport(this.object3d.material.uniforms?.viewport.value);
    const screenScale = this.object3d.material.uniforms.screenScale?.value;
    if (screenScale) {
      this.getScreenScale(camera, viewport.w, screenScale);
    }
    if (!this.screenSized && viewport.w < 200) {
      viewport.w *= 1.5;
    }
  }
}

var vertexShader$5 = "#version 100\n#define GLSLIFY 1\nuniform mat4 projectionMatrix;uniform mat4 modelViewMatrix;uniform vec4 viewport;uniform float maxZoom;uniform vec2 screenScale;attribute vec3 position;attribute vec2 uv;varying vec2 out_uv;void main(){float factor=viewport.w/600.0;float zoom=abs(1.0/(projectionMatrix[1][1]*300.0)/factor);zoom=min(zoom,maxZoom);out_uv=uv-0.5;vec4 mvPosition=projectionMatrix*modelViewMatrix*(vec4(position,1.0)*vec4(zoom*screenScale,1.0,1.0));gl_Position=mvPosition;}"; // eslint-disable-line

var fragmentShader$5 = "#version 100\nprecision highp float;\n#define GLSLIFY 1\nuniform vec4 color;uniform vec4 outlineColor;varying vec2 out_uv;void main(){float r=0.5;float outlineRadius=0.4;float smoothness=0.01;float distFromCenter=length(out_uv);if(distFromCenter>0.5){discard;}float col=smoothstep(outlineRadius+smoothness,outlineRadius,distFromCenter);gl_FragColor=mix(color,outlineColor,col);}"; // eslint-disable-line

class RoundHandleMaterial extends RawShaderMaterial {
  constructor(color, maxZoom = 0.3, viewport = new Vector4()) {
    color = new Color(color);
    super({
      vertexShader: vertexShader$5,
      fragmentShader: fragmentShader$5,
      uniforms: {
        color: { value: new Vector4(color.r, color.g, color.b, 1) },
        outlineColor: { value: new Vector4(color.r, color.g, color.b, 1) },
        viewport: { value: viewport },
        maxZoom: { value: maxZoom },
        screenScale: { value: new Vector2(1, 1) }
      }
    });
    this.toneMapped = false;
  }
}

class RoundHandle extends Handle {
  constructor(point, props = { size: 10 }, viewport) {
    super(point, viewport);
    this.object3d.geometry = new PlaneGeometry();
    this.screenSized = props.screenSized ?? false;
    this.object3d.material = new RoundHandleMaterial(props.color || "red", props.maxZoom);
    this.object3d.material.side = DoubleSide;
    this.object3d.material.depthTest = false;
    this.object3d.material.depthWrite = false;
    this.size = props.size;
  }
  set size(size) {
    this.object3d.scale.set(size, size, 1);
  }
  get size() {
    return this.object3d.scale.x;
  }
}

const vector$3 = new Vector3();
const position$2 = new Vector3();
const scale$5 = new Vector3();
const shaderScale$1 = new Vector2();
class RoundHandleRaycast extends Raycast {
  constructor(entity, offset = 0) {
    super(entity);
    if (!(entity instanceof RoundHandle)) {
      throw new Error("RoundHandle-only component");
    }
    this.offset = offset;
  }
  isRaycast(raycaster) {
    const activeViewport = this.entity.getLayer()?.getView().getActiveViewport();
    if (activeViewport && this.entity.object3d.layers.test(activeViewport.camera.layers)) {
      this.entity.getShaderScale(activeViewport.camera, activeViewport.size.w, shaderScale$1);
      const zoom = Math.min(
        annotationWorldUnitsPerPixel(activeViewport.camera, activeViewport.size.w),
        this.entity.object3d.material.uniforms.maxZoom.value
      );
      this.entity.object3d.getWorldScale(scale$5);
      const handleRadius = Math.max(Math.abs(scale$5.x * shaderScale$1.x), Math.abs(scale$5.y * shaderScale$1.y)) / 2;
      const radius = handleRadius + this.offset * zoom;
      position$2.setFromMatrixColumn(this.entity.object3d.matrixWorld, 3);
      vector$3.set(raycaster.ray.origin.x, raycaster.ray.origin.y, position$2.z);
      if (vector$3.sub(position$2).length() < radius) {
        return [{
          object: this.entity.object3d,
          distance: 0,
          point: new Vector3().set(raycaster.ray.origin.x, raycaster.ray.origin.y, position$2.z)
        }];
      } else {
        return false;
      }
    }
    return false;
  }
}

class IncreaseSizeHighlighter extends Highlighter {
  constructor(entity, increaseFactor = 1.3) {
    super(entity);
    this.scale = new Vector3();
    this.increaseFactor = increaseFactor;
  }
  highlight() {
    this.scale.copy(this.entity.object3d.scale);
    this.entity.object3d.scale.multiplyScalar(this.increaseFactor);
  }
  unHighlight() {
    this.entity.object3d.scale.copy(this.scale);
  }
}

class StraightLineEntity extends LineLikeEntity {
  constructor(props, end, type, viewport) {
    let color;
    if (props instanceof Vector3) {
      super(type, viewport);
      this.start = props;
      this.end = end;
      color = new Color(16777215);
    } else {
      super(props, end, type);
      this.start = new Vector3(props.start.x, props.start.y, props.start.z);
      this.end = new Vector3(props.end.x, props.end.y, props.end.z);
      color = new Color(props.color || 16777215);
    }
    this.object3d.material.color = color;
  }
  updateLine() {
    const positions = this.object3d.geometry.getAttribute("position");
    positions.array.set([...this.start.toArray(), ...this.end.toArray()]);
    positions.needsUpdate = true;
  }
  updateLine2() {
    this.object3d.geometry.setPositions([...this.start.toArray(), ...this.end.toArray()]);
  }
  getSegments() {
    return [this.start, this.end];
  }
}

class LineWithHandles extends StraightLineEntity {
  constructor(props, startHandle, endHandle, viewport, extending) {
    props.width = props.width ?? 2;
    super(props, Line2, viewport);
    this.startHandle = new startHandle(this.start, props.startHandleProps, viewport);
    this.endHandle = new endHandle(this.end, props.endHandleProps, viewport);
    this.update = this.update.bind(this);
    if (!extending) {
      this.setRenderOrders();
      this.setComponents();
      this.setBehavior();
      this.makeTransparent();
      this.add([this.startHandle, this.endHandle]);
    }
  }
  makeTransparent() {
    this.object3d.material.transparent = true;
    this.startHandle.object3d.material.transparent = true;
    this.endHandle.object3d.material.transparent = true;
  }
  setRenderOrders() {
    this.object3d.renderOrder = 10;
    this.startHandle.object3d.renderOrder = 11;
    this.endHandle.object3d.renderOrder = 11;
  }
  update() {
    super.update();
    this.startHandle.update();
    this.endHandle.update();
  }
  setBehavior() {
    this.startHandle.bind();
    this.endHandle.bind();
    this.startHandle.addEventListener("transform", () => this.update());
    this.endHandle.addEventListener("transform", () => this.update());
    this.addEventListener("transform", () => this.update());
  }
}

class IncreaseLineWidthHighlighter extends Highlighter {
  constructor(entity, increaseFactor = 1.3) {
    super(entity);
    this.increaseFactor = increaseFactor;
  }
  highlight() {
    this.width = this.entity.object3d.material.linewidth;
    this.entity.object3d.material.linewidth = this.width * this.increaseFactor;
  }
  unHighlight() {
    this.entity.object3d.material.linewidth = this.width;
  }
}

class Line extends LineWithHandles {
  constructor(props, viewport, extending) {
    props.width = props.width ?? 2;
    const startHandleProps = props.startHandleProps || {
      color: props.color,
      maxZoom: props.handleMaxZoom,
      size: props.handleSize || 5,
      screenSized: props.screenSized
    };
    const endHandleProps = props.endHandleProps || {
      color: props.color,
      maxZoom: props.handleMaxZoom,
      size: props.handleSize || 5,
      screenSized: props.screenSized
    };
    const LineWithHandlesProps = {
      ...props,
      startHandleProps,
      endHandleProps
    };
    super(LineWithHandlesProps, RoundHandle, RoundHandle, viewport, extending);
    this.update = this.update.bind(this);
  }
  setComponents() {
    this.setComponent(RaycastChildrenOnly);
    this.startHandle.setComponent(RoundHandleRaycast, new RoundHandleRaycast(this.startHandle, 7.5));
    this.startHandle.setComponent(HoverComponent, new HoverComponent(this.startHandle, new IncreaseSizeHighlighter(this.startHandle, 2)));
    this.endHandle.setComponent(RoundHandleRaycast, new RoundHandleRaycast(this.endHandle, 7.5));
    this.endHandle.setComponent(HoverComponent, new HoverComponent(this.endHandle, new IncreaseSizeHighlighter(this.endHandle, 2)));
    this.setComponent(HoverComponent, new HoverComponent(this, new IncreaseLineWidthHighlighter(this, 2.5)));
  }
}

class TextEntity extends Primitive {
  constructor(props = "", viewport) {
    if (props instanceof Text) {
      super(props, viewport);
    } else {
      super(new Text(), viewport);
    }
    this.object3d.font = "/ALS_Sirius_Bold_1_6.otf";
    this.object3d.material.side = DoubleSide;
    this.object3d.material.depthTest = false;
    this.object3d.material.depthWrite = false;
    this.promises = /* @__PURE__ */ new Set();
    this.syncCounter = 0;
    this.object3d.addEventListener("synccomplete", async () => {
      this.syncCounter--;
      if (this.syncCounter === 0) {
        await sleep(1);
        this.onSyncComplete.forEach((callback) => callback());
        this.promises.forEach((promise) => promise.resolve());
        this.promises.clear();
      }
    });
    this.object3d.addEventListener("syncstart", () => {
      this.syncCounter++;
      this.onSyncStart.forEach((callback) => callback());
    });
    this.onSyncStart = /* @__PURE__ */ new Set();
    this.onSyncComplete = /* @__PURE__ */ new Set();
    if (typeof props === "string") {
      this.setProps({ text: props });
    } else if (!(props instanceof Text)) {
      this.setProps(props);
    }
  }
  waitSync() {
    if (this.syncCounter === 0) {
      return Promise.resolve();
    }
    const promise = promiseWithResolvers();
    this.promises.add(promise);
    return promise.promise;
  }
  setProps(props, callback) {
    let key;
    for (key in props) {
      this.object3d[key] = props[key];
    }
    const promise = promiseWithResolvers();
    this.object3d.sync(() => {
      callback && callback();
      this.getLayer()?.getView().dispatchEvent({ type: "render" });
      promise.resolve();
    });
    return promise;
  }
  getSize() {
    const bBox = this.object3d.geometry.boundingBox;
    return {
      width: bBox.max.x - bBox.min.x,
      height: bBox.max.y - bBox.min.y
    };
  }
  toJson() {
    return {
      text: this.object3d.text,
      fontSize: this.object3d.fontSize,
      maxWidth: this.object3d.maxWidth,
      color: this.object3d.color,
      font: this.object3d.font,
      outlineBlur: this.object3d.outlineBlur,
      outlineOpacity: this.object3d.outlineOpacity,
      outlineColor: this.object3d.outlineColor,
      outlineWidth: this.object3d.outlineWidth,
      anchorX: this.object3d.anchorX,
      anchorY: this.object3d.anchorY,
      whiteSpace: this.object3d.whiteSpace
    };
  }
}

const editor = {
  arrowHeadLengthPx: 20,
  arrowStartRadiusPx: 1.25,
  color: "lime",
  handleRadiusPx: 2.5,
  labelSizePx: 16,
  strokeWidthPx: 2
};
const ANNOTATION_PRESENTATION = {
  editor,
  thumbnail: {
    ...editor,
    arrowHeadLengthPx: editor.arrowHeadLengthPx * 2 / 3,
    arrowStartRadiusPx: editor.arrowStartRadiusPx * 2 / 3,
    handleRadiusPx: editor.handleRadiusPx * 2 / 3,
    labelSizePx: editor.labelSizePx * 2 / 3,
    strokeWidthPx: editor.strokeWidthPx * 2 / 3
  }
};
const ANNOTATION_ANGLE_RADIUS = 10;
const measureAnnotationLength = ({
  end,
  scaleMmPerPixel,
  start
}) => Math.hypot(end.x - start.x, end.y - start.y, (end.z ?? 0) - (start.z ?? 0)) * scaleMmPerPixel;
const formatAnnotationLength = (lengthMm) => `${lengthMm.toFixed(2)} mm`;
const formatAnnotationAngle = (degrees) => `${Math.round(degrees)}\u02DA`;
const uprightAnnotationRotation = (rotationDeg) => {
  const normalized = ((rotationDeg + 180) % 360 + 360) % 360 - 180;
  return normalized > 90 ? normalized - 180 : normalized < -90 ? normalized + 180 : normalized;
};
const makeAnnotationRulerLabel = ({
  end,
  fontSize,
  start
}) => {
  const rotationDeg = uprightAnnotationRotation(Math.atan2(end.y - start.y, end.x - start.x) * 180 / Math.PI);
  const rotation = rotationDeg * Math.PI / 180;
  return {
    rotationDeg,
    x: (start.x + end.x) / 2 + Math.sin(rotation) * fontSize * 0.3,
    y: (start.y + end.y) / 2 - Math.cos(rotation) * fontSize * 0.3
  };
};
const makeAnnotationArrowGeometry = ({
  end,
  headLength,
  start
}) => {
  const length = Math.hypot(end.x - start.x, end.y - start.y);
  const resolvedHeadLength = Math.min(headLength, length / 2);
  const unitX = length ? (end.x - start.x) / length : 0;
  const unitY = length ? (end.y - start.y) / length : 0;
  const base = { x: end.x - unitX * resolvedHeadLength, y: end.y - unitY * resolvedHeadLength };
  const halfWidth = resolvedHeadLength / 4;
  return {
    base,
    headLength: resolvedHeadLength,
    left: { x: base.x - unitY * halfWidth, y: base.y + unitX * halfWidth },
    right: { x: base.x + unitY * halfWidth, y: base.y - unitX * halfWidth }
  };
};
const makeAnnotationAngleGeometry = ({
  end,
  radius = ANNOTATION_ANGLE_RADIUS,
  start,
  vertex
}) => {
  const startLength = Math.hypot(start.x - vertex.x, start.y - vertex.y);
  const endLength = Math.hypot(end.x - vertex.x, end.y - vertex.y);
  const resolvedRadius = Math.min(radius, startLength / 2, endLength / 2);
  const startAngle = Math.atan2(start.y - vertex.y, start.x - vertex.x);
  const endAngle = Math.atan2(end.y - vertex.y, end.x - vertex.x);
  const delta = Math.atan2(Math.sin(endAngle - startAngle), Math.cos(endAngle - startAngle));
  return {
    bisectorAngle: startAngle + delta / 2,
    degrees: startLength && endLength ? Math.abs(delta) * 180 / Math.PI : 0,
    end: { x: vertex.x + Math.cos(endAngle) * resolvedRadius, y: vertex.y + Math.sin(endAngle) * resolvedRadius },
    radius: resolvedRadius,
    start: { x: vertex.x + Math.cos(startAngle) * resolvedRadius, y: vertex.y + Math.sin(startAngle) * resolvedRadius },
    sweep: delta > 0 ? 1 : 0,
    vertex
  };
};
const makeAnnotationAngleLabel = ({
  bisectorAngle,
  fontSize,
  radius,
  vertex
}) => {
  const rotationDeg = uprightAnnotationRotation(bisectorAngle * 180 / Math.PI + 90);
  const rotation = rotationDeg * Math.PI / 180;
  const x = vertex.x + Math.cos(bisectorAngle) * (radius + fontSize * 0.5);
  const y = vertex.y + Math.sin(bisectorAngle) * (radius + fontSize * 0.5);
  return {
    rotationDeg,
    x: x - Math.sin(rotation) * fontSize * 0.35,
    y: y + Math.cos(rotation) * fontSize * 0.35
  };
};

class Angle extends Line {
  constructor(props, viewport) {
    const style = ANNOTATION_PRESENTATION.editor;
    const centerHandleProps = { color: props.color, size: style.handleRadiusPx * 2, ...props.centerHandleProps, maxZoom: Infinity, screenSized: true };
    super({ ...props, handleMaxZoom: Infinity, handleSize: style.handleRadiusPx * 2, screenSized: true }, viewport, true);
    this.arcRadius = ANNOTATION_ANGLE_RADIUS;
    this.center = new Vector3(props.center.x, props.center.y, props.center.z);
    const { start, end } = this.getArcPoints(new Vector3(0, 0, 0), new Vector3(0, 0, 0));
    this.arc = new Arc({ start, end, center: this.center, color: props.color, width: props.width ?? style.strokeWidthPx }, Line2, viewport);
    this.centerHandle = new RoundHandle(this.center, centerHandleProps, viewport);
    this.text = new TextEntity({
      text: formatAnnotationAngle(this.getMeasure()),
      fontSize: style.labelSizePx,
      color: style.color,
      anchorX: "center",
      anchorY: "bottom-baseline"
    });
    this.update = this.update.bind(this);
    this.setRenderOrders();
    this.setComponents();
    this.setBehavior();
    this.makeTransparent();
    this.add([this.startHandle, this.endHandle, this.arc, this.centerHandle, this.text]);
    this.text.object3d.frustumCulled = false;
    this.text.object3d.renderOrder = 12;
    const viewportSize = new Vector4();
    this.text.onBeforeRenderSet.add((renderer, _scene, camera) => {
      renderer.getViewport(viewportSize);
      const frame = { camera, heightPx: viewportSize.w, parent: this.object3d, widthPx: viewportSize.z };
      const vertex = projectAnnotationPoint(this.center, frame);
      const arcStart = projectAnnotationPoint(this.arc.start, frame);
      const geometry = makeAnnotationAngleGeometry({
        end: projectAnnotationPoint(this.end, frame),
        radius: Math.hypot(arcStart.x - vertex.x, arcStart.y - vertex.y),
        start: projectAnnotationPoint(this.start, frame),
        vertex
      });
      placeAnnotationLabel({
        frame,
        label: makeAnnotationAngleLabel({ ...geometry, fontSize: style.labelSizePx }),
        object: this.text.object3d
      });
    });
    this.update();
  }
  update() {
    super.update();
    this.centerHandle.update();
    this.getArcPoints(this.arc.start, this.arc.end);
    this.arc.update();
    const degrees = this.getMeasure();
    const measure = Math.round(degrees);
    if (measure === 0 || this.start.distanceTo(this.center) === 0 || this.end.distanceTo(this.center) === 0) {
      this.text.object3d.visible = false;
      return;
    } else {
      this.text.object3d.visible = true;
    }
    const text = formatAnnotationAngle(degrees);
    if (this.text.object3d.text !== text) {
      this.text.setProps({ text });
    }
  }
  setRenderOrders() {
    super.setRenderOrders();
    this.arc.object3d.renderOrder = 10;
    this.centerHandle.object3d.renderOrder = 11;
  }
  makeTransparent() {
    super.makeTransparent();
    this.arc.object3d.material.transparent = true;
    this.centerHandle.object3d.material.transparent = true;
  }
  setComponents() {
    super.setComponents();
    this.centerHandle.setComponent(RoundHandleRaycast, new RoundHandleRaycast(this.centerHandle, 7.5));
    this.centerHandle.setComponent(HoverComponent, new HoverComponent(this.centerHandle, new IncreaseSizeHighlighter(this.centerHandle, 2)));
    this.setComponent(HoverComponent, new HoverComponent(this, new IncreaseLineWidthHighlighter(this, 2.5)));
  }
  setBehavior() {
    super.setBehavior();
    this.centerHandle.bind();
    this.centerHandle.addEventListener("transform", () => this.update());
  }
  getArcPoints(start, end) {
    const geometry = makeAnnotationAngleGeometry({ end: this.end, radius: this.arcRadius, start: this.start, vertex: this.center });
    start.set(geometry.start.x, geometry.start.y, this.center.z);
    end.set(geometry.end.x, geometry.end.y, this.center.z);
    return { start, end };
  }
  updateLine2() {
    this.object3d.geometry.setPositions([...this.start.toArray(), ...this.center.toArray(), ...this.end.toArray()]);
  }
  getMeasure() {
    return makeAnnotationAngleGeometry({ end: this.end, start: this.start, vertex: this.center }).degrees;
  }
  getSegments() {
    return [this.start, this.center, this.end];
  }
}

var vertexShader$4 = "#version 300 es\n#define GLSLIFY 1\nuniform mat4 projectionMatrix;uniform mat4 modelViewMatrix;uniform vec4 viewport;uniform float maxZoom;uniform vec2 screenScale;in vec3 position;void main(){float factor=viewport.w/600.0f;float zoom=abs(1.0f/(projectionMatrix[1][1]*300.0f)/factor);zoom=min(zoom,maxZoom);vec4 mvPosition=projectionMatrix*modelViewMatrix*(vec4(position,1.0f)*vec4(zoom*screenScale,1.0f,1.0f));gl_Position=mvPosition;}"; // eslint-disable-line

var fragmentShader$4 = "#version 300 es\nprecision highp float;\n#define GLSLIFY 1\nuniform vec4 color;out vec4 out_color;void main(){out_color=color;}"; // eslint-disable-line

class TriangleHandleMaterial extends RawShaderMaterial {
  constructor(maxZoom, color, viewport = new Vector4()) {
    color = new Color(color || "red");
    super({
      vertexShader: vertexShader$4,
      fragmentShader: fragmentShader$4,
      uniforms: {
        color: { value: new Vector4(color.r, color.g, color.b, 1) },
        viewport: { value: viewport },
        maxZoom: { value: maxZoom },
        screenScale: { value: new Vector2(1, 1) }
      }
    });
    this.toneMapped = false;
  }
}

const getAngle2D = (v1, v2) => {
  return Math.atan2(v1.x * v2.y - v1.y * v2.x, v1.x * v2.x + v1.y * v2.y);
};
(() => {
  const tmpMatrix = new Matrix4();
  const tmpQuaternion = new Quaternion();
  return function makeRotationAroundPointAndAxis2(angle, point, axis, target) {
    return target.makeTranslation(point.x, point.y, point.z).multiply(tmpMatrix.makeRotationFromQuaternion(tmpQuaternion.setFromAxisAngle(axis, angle))).multiply(tmpMatrix.makeTranslation(-point.x, -point.y, -point.z));
  };
})();
const triangulate = (shapeVertices, indexOffset = 0, shapeHoles = []) => {
  const indices = [];
  const points = [];
  const faces = ShapeUtils.triangulateShape(shapeVertices, shapeHoles);
  for (let i = 0, l = faces.length; i < l; i++) {
    const face = faces[i];
    const a = face[0] + indexOffset;
    const b = face[1] + indexOffset;
    const c = face[2] + indexOffset;
    indices.push(a, b, c);
  }
  for (let i = 0, l = shapeHoles.length; i < l; i++) {
    shapeVertices.push(...shapeHoles[i]);
  }
  for (let i = 0, l = shapeVertices.length; i < l; i++) {
    points.push(shapeVertices[i].x, shapeVertices[i].y, 0);
  }
  return { points, indices };
};
const isPointInsidePolygon = (inPt, inPolygon) => {
  const polyLen = inPolygon.length;
  let inside = false;
  for (let p = polyLen - 1, q = 0; q < polyLen; p = q++) {
    let edgeLowPt = inPolygon[p];
    let edgeHighPt = inPolygon[q];
    let edgeDx = edgeHighPt.x - edgeLowPt.x;
    let edgeDy = edgeHighPt.y - edgeLowPt.y;
    if (Math.abs(edgeDy) > Number.EPSILON) {
      if (edgeDy < 0) {
        edgeLowPt = inPolygon[q];
        edgeDx = -edgeDx;
        edgeHighPt = inPolygon[p];
        edgeDy = -edgeDy;
      }
      if (inPt.y < edgeLowPt.y || inPt.y > edgeHighPt.y) continue;
      if (inPt.y === edgeLowPt.y) {
        if (inPt.x === edgeLowPt.x) return true;
      } else {
        const perpEdge = edgeDy * (inPt.x - edgeLowPt.x) - edgeDx * (inPt.y - edgeLowPt.y);
        if (perpEdge === 0) return true;
        if (perpEdge < 0) continue;
        inside = !inside;
      }
    } else {
      if (inPt.y !== edgeLowPt.y) continue;
      if (edgeHighPt.x <= inPt.x && inPt.x <= edgeLowPt.x || edgeLowPt.x <= inPt.x && inPt.x <= edgeHighPt.x) return true;
    }
  }
  return inside;
};

const segmentStart = new Vector3();
const segmentEnd = new Vector3();
class TriangleHandle extends Handle {
  constructor(point, props, viewport) {
    super(point, viewport);
    this.update = (() => {
      const x = new Vector3(0, 1, 0);
      return () => {
        this.dir.normalize();
        this.object3d.position.copy(this.point);
        this.object3d.rotation.z = -getAngle2D(this.dir, x);
      };
    })();
    this.base = props.base;
    this.height = props.height;
    this.dir = new Vector3(props.dir.x, props.dir.y, 0);
    this.screenSized = props.screenSized ?? false;
    this.object3d.scale.set(props.size, props.size, 1);
    this.object3d.material = new TriangleHandleMaterial(props.maxZoom || 0.3, props.color);
    this.object3d.material.side = DoubleSide;
    this.object3d.material.transparent = true;
    this.object3d.material.depthTest = false;
    this.object3d.material.depthWrite = false;
    this.updateGeometry();
  }
  set size(size) {
    this.object3d.scale.set(size, size, 1);
  }
  get size() {
    return this.object3d.scale.x;
  }
  getScreenScale(camera, heightPx, target) {
    super.getScreenScale(camera, heightPx, target);
    if (!this.screenSized || !this.limitFrom || !this.object3d.parent) return target;
    const start = this.object3d.parent.localToWorld(segmentStart.copy(this.limitFrom));
    const end = this.object3d.parent.localToWorld(segmentEnd.copy(this.point));
    const worldUnitsPerPixel = annotationWorldUnitsPerPixel(camera, heightPx);
    const headLength = Math.abs(this.object3d.scale.y) * this.height;
    if (!worldUnitsPerPixel || !headLength) return target;
    const geometry = makeAnnotationArrowGeometry({
      end: { x: start.distanceTo(end) / worldUnitsPerPixel, y: 0 },
      headLength,
      start: { x: 0, y: 0 }
    });
    return target.multiplyScalar(geometry.headLength / headLength);
  }
  updateGeometry() {
    this.object3d.geometry.setAttribute("position", new Float32BufferAttribute([this.base / 2, -this.height, 0, 0, 0, 0, -this.base / 2, -this.height, 0], 3));
  }
}

const vector$2 = new Vector3();
const position$1 = new Vector3();
const scale$4 = new Vector3();
const shaderScale = new Vector2();
class TriangleHandleRaycastComponent extends Raycast {
  constructor(entity) {
    super(entity);
    if (!(entity instanceof TriangleHandle)) {
      throw new Error("TriangleHandle-only component");
    }
    this.raycaster = new Raycaster();
  }
  isRaycast(raycaster) {
    const activeViewport = this.entity.getLayer()?.getView().getActiveViewport();
    if (activeViewport && this.entity.object3d.layers.test(activeViewport.camera.layers)) {
      this.entity.getShaderScale(activeViewport.camera, activeViewport.size.w, shaderScale);
      if (!shaderScale.x || !shaderScale.y) return false;
      this.entity.object3d.getWorldScale(scale$4);
      const radius = Math.max(Math.abs(scale$4.x * shaderScale.x), Math.abs(scale$4.y * shaderScale.y)) / 2;
      position$1.setFromMatrixColumn(this.entity.object3d.matrixWorld, 3);
      vector$2.set(raycaster.ray.origin.x, raycaster.ray.origin.y, position$1.z);
      if (vector$2.sub(position$1).length() < radius) {
        return [{
          object: this.entity.object3d,
          distance: 0,
          point: new Vector3().set(raycaster.ray.origin.x, raycaster.ray.origin.y, position$1.z)
        }];
      } else {
        this.raycaster.ray.copy(raycaster.ray);
        this.entity.object3d.worldToLocal(this.raycaster.ray.origin);
        this.raycaster.ray.origin.x /= shaderScale.x;
        this.raycaster.ray.origin.y /= shaderScale.y;
        this.entity.object3d.localToWorld(this.raycaster.ray.origin);
        return super.isRaycast(this.raycaster);
      }
    }
    return false;
  }
}

const handleShaderScale = new Vector2();
class Arrow extends LineWithHandles {
  constructor(props, viewport) {
    viewport = viewport;
    const style = ANNOTATION_PRESENTATION.editor;
    props.width = props.width ?? style.strokeWidthPx;
    const LineWithHandlesProps = {
      ...props,
      startHandleProps: { color: props.color, maxZoom: Infinity, screenSized: true, size: style.arrowStartRadiusPx * 2 },
      endHandleProps: { color: props.color, size: style.arrowHeadLengthPx / 2, base: 1, height: 2, dir: { x: 0, y: 1 }, maxZoom: Infinity, screenSized: true }
    };
    super(LineWithHandlesProps, RoundHandle, TriangleHandle, viewport);
    this.endHandle.limitFrom = this.start;
    this.update = this.update.bind(this);
    this.update();
  }
  setComponents() {
    this.setComponent(RaycastChildrenOnly);
    this.startHandle.setComponent(RoundHandleRaycast, new RoundHandleRaycast(this.startHandle, 9));
    this.startHandle.setComponent(HoverComponent, new HoverComponent(this.endHandle, new IncreaseSizeHighlighter(this.startHandle, 5)));
    this.endHandle.setComponent(TriangleHandleRaycastComponent);
    this.endHandle.setComponent(HoverComponent, new HoverComponent(this.endHandle, new IncreaseSizeHighlighter(this.endHandle)));
    this.setComponent(HoverComponent, new HoverComponent(this, new IncreaseLineWidthHighlighter(this, 2.5)));
  }
  update() {
    this.startHandle.update();
    this.endHandle.dir.subVectors(this.end, this.start).normalize();
    this.endHandle.update();
    this.updateLine2();
  }
  setBehavior() {
    this.startHandle.bind();
    this.endHandle.bind();
    this.startHandle.addEventListener("transform", () => this.update());
    this.endHandle.addEventListener("transform", () => this.update());
    this.addEventListener("transform", () => this.update());
    const vector = new Vector3();
    const viewportSize = new Vector4();
    this.onBeforeRenderSet.add((...args) => {
      const camera = args[2];
      args[0].getViewport(viewportSize);
      this.endHandle.dir.subVectors(this.end, this.start).normalize();
      const end = this.computeEndPosition(camera, viewportSize.w, vector);
      this.object3d.geometry.attributes.instanceEnd.setXYZ(0, end.x, end.y, end.z);
      this.object3d.geometry.attributes.instanceStart.setXYZ(0, this.start.x, this.start.y, this.start.z);
      this.object3d.geometry.attributes.instanceEnd.needsUpdate = true;
      this.object3d.geometry.attributes.instanceStart.needsUpdate = true;
    });
  }
  updateLine2() {
    const viewport = this.getViewport();
    let end;
    if (viewport) {
      end = this.computeEndPosition(viewport.camera, viewport.size.w, new Vector3());
    } else {
      end = this.end;
    }
    this.object3d.geometry.setPositions([...this.start.toArray(), ...end.toArray()]);
  }
  computeEndPosition(camera, heightPx, vector) {
    const shaderScale = this.endHandle.getShaderScale(camera, heightPx, handleShaderScale);
    const geometry = makeAnnotationArrowGeometry({
      end: this.end,
      headLength: this.endHandle.height * Math.abs(this.endHandle.object3d.scale.y) * shaderScale.y,
      start: this.start
    });
    return vector.set(geometry.base.x, geometry.base.y, this.end.z);
  }
}

class Ruler extends Line {
  constructor(props, viewport) {
    const style = ANNOTATION_PRESENTATION.editor;
    super({ ...props, handleMaxZoom: Infinity, handleSize: style.handleRadiusPx * 2, screenSized: true, width: props.width ?? style.strokeWidthPx }, viewport);
    this.scale = props.scale ?? 1;
    this.text = new TextEntity({
      text: this.getLabel(),
      fontSize: style.labelSizePx,
      color: style.color,
      anchorX: "center",
      anchorY: "bottom-baseline"
    });
    this.add(this.text);
    this.text.object3d.frustumCulled = false;
    this.text.object3d.renderOrder = 12;
    const viewportSize = new Vector4();
    this.text.onBeforeRenderSet.add((renderer, _scene, camera) => {
      renderer.getViewport(viewportSize);
      const frame = { camera, heightPx: viewportSize.w, parent: this.object3d, widthPx: viewportSize.z };
      placeAnnotationLabel({
        frame,
        label: makeAnnotationRulerLabel({
          end: projectAnnotationPoint(this.end, frame),
          fontSize: style.labelSizePx,
          start: projectAnnotationPoint(this.start, frame)
        }),
        object: this.text.object3d
      });
    });
    this.update();
  }
  update() {
    super.update();
    const text = this.getLabel();
    if (this.text.object3d.text !== text) {
      this.text.setProps({ text });
    }
  }
  getLabel() {
    return formatAnnotationLength(measureAnnotationLength({ end: this.end, scaleMmPerPixel: this.scale, start: this.start }));
  }
}

const flipY = (point) => {
  point.y = -1 * (point.y || 0);
  return point;
};

const entityToAnnotation = (entities) => {
  return entities.map((annotation) => {
    if (annotation instanceof Ruler || annotation instanceof Arrow) {
      return {
        kind: annotation instanceof Ruler ? "ruler" : "arrow",
        x1: annotation.start.x,
        y1: -annotation.start.y,
        x2: annotation.end.x,
        y2: -annotation.end.y
      };
    }
    if (annotation instanceof Angle) {
      return {
        kind: "angle",
        x1: annotation.start.x,
        y1: -annotation.start.y,
        x2: annotation.center.x,
        y2: -annotation.center.y,
        x3: annotation.end.x,
        y3: -annotation.end.y
      };
    }
  }).filter((annotation) => typeof annotation !== "undefined");
};
const addAnnotationLayer = async ({
  container,
  layer,
  onCreate,
  scale
}) => {
  await Promise.all(layer.Annotations.map(async (annotation) => {
    switch (annotation.Type.case) {
      case "Ruler":
        const ruler = new Ruler({
          color: ANNOTATION_PRESENTATION.editor.color,
          end: flipY({ x: annotation.Type.value.End?.X, y: annotation.Type.value.End?.Y, z: 0 }),
          scale,
          start: flipY({ x: annotation.Type.value.Start?.X, y: annotation.Type.value.Start?.Y, z: 0 }),
          width: ANNOTATION_PRESENTATION.editor.strokeWidthPx
        });
        onCreate?.(ruler);
        await container.addAnnotationWhenReady(ruler);
        break;
      case "Arrow":
        const arrow = new Arrow({
          color: ANNOTATION_PRESENTATION.editor.color,
          end: flipY({ x: annotation.Type.value.End?.X, y: annotation.Type.value.End?.Y, z: 0 }),
          start: flipY({ x: annotation.Type.value.Start?.X, y: annotation.Type.value.Start?.Y, z: 0 }),
          width: ANNOTATION_PRESENTATION.editor.strokeWidthPx
        });
        onCreate?.(arrow);
        await container.addAnnotationWhenReady(arrow);
        break;
      case "Angle":
        const angle = new Angle({
          center: flipY({ x: annotation.Type.value.End?.X, y: annotation.Type.value.End?.Y, z: 0 }),
          color: ANNOTATION_PRESENTATION.editor.color,
          end: flipY({ x: annotation.Type.value.Corner?.X, y: annotation.Type.value.Corner?.Y, z: 0 }),
          start: flipY({ x: annotation.Type.value.Start?.X, y: annotation.Type.value.Start?.Y, z: 0 }),
          width: ANNOTATION_PRESENTATION.editor.strokeWidthPx
        });
        onCreate?.(angle);
        await container.addAnnotationWhenReady(angle);
        break;
    }
  }));
};

class Wrapper extends Entity {
  constructor(entities, id) {
    super(new Group());
    this.id = id;
    entities.length && this.add(entities);
  }
  add(children) {
    this.container.add(children);
  }
  delete(children) {
    this.container.delete(children);
  }
  toJson() {
    throw new Error("Method not implemented.");
  }
}

const DEFAULT_PIXEL_SPACING_MM = {
  xMmPerPixel: 1,
  yMmPerPixel: 1
};
const normalizePixelSpacingMm = (pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => ({
  xMmPerPixel: Number.isFinite(pixelSpacingMm.xMmPerPixel) && pixelSpacingMm.xMmPerPixel > 0 ? pixelSpacingMm.xMmPerPixel : DEFAULT_PIXEL_SPACING_MM.xMmPerPixel,
  yMmPerPixel: Number.isFinite(pixelSpacingMm.yMmPerPixel) && pixelSpacingMm.yMmPerPixel > 0 ? pixelSpacingMm.yMmPerPixel : DEFAULT_PIXEL_SPACING_MM.yMmPerPixel
});

class XRayImage extends ImageEntity {
  constructor(texture, props) {
    if (typeof texture === "string") {
      super();
      this.id = texture;
    } else {
      super(texture, { width: props.originalSize.width, height: props.originalSize.height });
      this.meta = props.meta;
      this.id = props.ImageID;
      this.brightness = props.MedicalImageFeatures?.ViewOptions?.Brightness || 0;
      this.sharpness = props.MedicalImageFeatures?.ViewOptions?.Sharpness || 0;
      this.contrast = props.MedicalImageFeatures?.ViewOptions?.Contrast || 0;
      this.pixelSpacing = props?.pixelSpacing;
    }
    this.object3d.geometry.translate(0.5, -0.5, 0);
  }
  normalBlendingInverseRender() {
    this.object3d.material.transparent = true;
    this.object3d.material.blending = CustomBlending;
    this.object3d.material.blendEquation = AddEquation;
    this.object3d.material.blendSrc = OneMinusDstAlphaFactor;
    this.object3d.material.blendDst = OneFactor;
  }
  alphaBlendingInverseRender() {
    this.object3d.material.transparent = true;
    this.object3d.material.blending = CustomBlending;
    this.object3d.material.blendEquation = AddEquation;
    this.object3d.material.blendSrc = OneMinusDstAlphaFactor;
    this.object3d.material.blendDst = DstAlphaFactor;
  }
  clone() {
    const image = new XRayImage(this.id);
    image.object3d.scale.copy(this.object3d.scale);
    image.object3d.position.copy(this.object3d.position);
    image.object3d.quaternion.copy(this.object3d.quaternion);
    image.object3d.material = this.object3d.material;
    return image;
  }
  getPixelSpacing() {
    const texture = this.texture;
    return this.pixelSpacing || (texture instanceof DicomTexture ? texture.pixelSpacing : void 0) || DEFAULT_PIXEL_SPACING_MM;
  }
}

class Block extends Wrapper {
  constructor(width, height, id, entities = []) {
    super(entities, id);
    this.layoutX = 0;
    this.layoutY = 0;
    this.layoutRotation = 0;
    this.width = width;
    this.height = height;
  }
  add(children) {
    super.add(children);
    this.syncTransform();
  }
  setLayoutTransform(x, y, rotation = 0) {
    this.layoutX = x;
    this.layoutY = y;
    this.layoutRotation = rotation;
    this.syncTransform();
  }
  syncTransform() {
    const image = this.children.find((child) => child instanceof XRayImage);
    if (!image) {
      this.object3d.position.set(this.layoutX, this.layoutY, 0);
      this.object3d.rotation.set(0, 0, this.layoutRotation);
      return false;
    }
    const baseWidth = image.width;
    const baseHeight = image.height;
    if (!(baseWidth > 0) || !(baseHeight > 0)) {
      return false;
    }
    const sin = Math.abs(Math.sin(this.layoutRotation));
    const cos = Math.abs(Math.cos(this.layoutRotation));
    const rotatedWidth = baseWidth * cos + baseHeight * sin;
    const rotatedHeight = baseWidth * sin + baseHeight * cos;
    const widthScale = rotatedWidth ? this.width / rotatedWidth : NaN;
    const heightScale = rotatedHeight ? this.height / rotatedHeight : NaN;
    let scale = Number.isFinite(widthScale) && widthScale > 0 ? widthScale : heightScale;
    if (Number.isFinite(heightScale) && heightScale > 0 && Number.isFinite(scale) && scale > 0) {
      scale = (scale + heightScale) / 2;
    }
    if (!(Number.isFinite(scale) && scale > 0)) {
      return false;
    }
    const identity = new Matrix4();
    identity.scale(new Vector3(scale, scale, 1));
    identity.setPosition(this.layoutX, this.layoutY, 0);
    if (this.layoutRotation) {
      const center1 = new Vector3(this.layoutX + baseWidth * scale / 2, this.layoutY - baseHeight * scale / 2, 0);
      const center2 = new Vector3(this.layoutX + this.width / 2, this.layoutY - this.height / 2, 0);
      const m = new Matrix4().makeTranslation(center2.x, center2.y, 0).multiply(new Matrix4().makeRotationZ(this.layoutRotation)).multiply(new Matrix4().makeTranslation(-center1.x, -center1.y, 0));
      identity.premultiply(m);
    }
    identity.decompose(this.object3d.position, this.object3d.quaternion, this.object3d.scale);
    return true;
  }
}

class MaskWrapper extends Wrapper {
  constructor(id) {
    super([], id);
    this.maskIds = /* @__PURE__ */ new Set();
  }
  add(children) {
    if (Array.isArray(children)) {
      children = children.filter((child) => {
        if (this.maskIds.has(child.id)) {
          console.error(`mask with id ${child.id} already exist`);
          return false;
        }
        return true;
      });
      if (!children.length) {
        return;
      }
    } else {
      if (this.maskIds.has(children.id)) {
        console.error(`mask with id ${children.id} already exist`);
        return;
      }
    }
    this.container.add(children);
  }
  delete(children) {
    this.container.delete(children);
    if (Array.isArray(children)) {
      children.forEach((child) => {
        this.maskIds.delete(child.id);
      });
    } else {
      this.maskIds.delete(children.id);
    }
  }
}

const defaultEasing = (t) => 1 - Math.pow(1 - t, 3);
function findAllMaterials(obj, materials = [], path = "root") {
  if (obj && obj.material && typeof obj.material.opacity === "number") {
    materials.push({
      object: obj,
      key: "material",
      material: obj.material,
      originalOpacity: obj.material.opacity
    });
  }
  if (obj && obj.object3d) {
    if (obj.object3d.material && typeof obj.object3d.material.opacity === "number") {
      materials.push({
        object: obj.object3d,
        key: "material",
        material: obj.object3d.material,
        originalOpacity: obj.object3d.material.opacity
      });
    }
    if (obj.object3d.children && Array.isArray(obj.object3d.children)) {
      obj.object3d.children.forEach((child, index) => {
        findAllMaterials(child, materials, `${path}.object3d.children[${index}]`);
      });
    }
  }
  if (obj && typeof obj === "object") {
    Object.keys(obj).forEach((key) => {
      if (key !== "object3d" && key !== "material" && key !== "children" && key !== "parent" && key !== "originalOpacity") {
        const value = obj[key];
        if (value && typeof value === "object" && value.material && typeof value.material.opacity === "number") {
          materials.push({
            object: value,
            key: "material",
            material: value.material,
            originalOpacity: value.material.opacity
          });
        }
      }
    });
  }
  if (obj && obj.children && Array.isArray(obj.children)) {
    obj.children.forEach((child, index) => {
      findAllMaterials(child, materials, `${path}.children[${index}]`);
    });
  }
  return materials;
}
function animateHide(entity, options = {}) {
  const {
    duration = 200,
    easing = defaultEasing,
    onComplete
  } = options;
  const entities = Array.isArray(entity) ? entity : [entity];
  const allMaterials = [];
  entities.forEach((ent) => {
    findAllMaterials(ent, allMaterials);
  });
  return new Promise((resolve) => {
    const startTime = performance.now();
    const animate = () => {
      const currentTime = performance.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);
      allMaterials.forEach((info) => {
        if (info.material && info.originalOpacity !== void 0) {
          info.material.opacity = info.originalOpacity * (1 - easedProgress);
          info.material.transparent = true;
        }
      });
      const activeView = MIRender.getActiveView();
      if (activeView) {
        activeView.dispatchEvent({ type: "render" });
      }
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        entities.forEach((ent) => {
          if (ent.object3d) {
            ent.object3d.visible = false;
          }
        });
        allMaterials.forEach((info) => {
          if (info.material && info.originalOpacity !== void 0) {
            info.material.opacity = info.originalOpacity;
          }
        });
        if (onComplete) onComplete();
        resolve();
      }
    };
    requestAnimationFrame(animate);
  });
}
function animateShow(entity, options = {}) {
  const {
    duration = 200,
    easing = defaultEasing,
    onComplete
  } = options;
  const entities = Array.isArray(entity) ? entity : [entity];
  const allMaterials = [];
  entities.forEach((ent) => {
    findAllMaterials(ent, allMaterials);
  });
  return new Promise((resolve) => {
    allMaterials.forEach((info) => {
      if (info.material && info.originalOpacity !== void 0) {
        info.material.opacity = 0;
        info.material.transparent = true;
      }
    });
    entities.forEach((ent) => {
      if (ent.object3d) {
        ent.object3d.visible = true;
      }
    });
    const startTime = performance.now();
    const animate = () => {
      const currentTime = performance.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easing(progress);
      allMaterials.forEach((info) => {
        if (info.material && info.originalOpacity !== void 0) {
          info.material.opacity = info.originalOpacity * easedProgress;
        }
      });
      const activeView = MIRender.getActiveView();
      if (activeView) {
        activeView.dispatchEvent({ type: "render" });
      }
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        if (onComplete) onComplete();
        resolve();
      }
    };
    requestAnimationFrame(animate);
  });
}

const isAnnotation = (entity) => {
  return entity instanceof Ruler || entity instanceof Angle || entity instanceof Arrow;
};
class ImageContainer extends Block {
  constructor(width, height, id, image) {
    super(width, height, id, image ? [image] : []);
    this.maskContainers = /* @__PURE__ */ new Map();
    this.maskDisposers = /* @__PURE__ */ new Map();
    this.conditionBoxes = /* @__PURE__ */ new Map();
    this.conditionBoxEntities = /* @__PURE__ */ new Set();
    this.annotationsAbortController = new AbortController();
    this.disposers = [];
    image && (this.image = image);
    this.annotations = new Wrapper([], id);
    this.annotations.setComponent(RaycastChildrenOnly);
    this.pbls = new Wrapper([], id);
    this.pbls.setComponent(RaycastChildrenOnly);
    this.teethNumbers = new Wrapper([], id);
    this.teethNumbers.setComponent(RaycastChildrenOnly);
    this.setComponent(ViewportComponent);
    this.setComponent(RaycastChildrenOnly);
    this.dispatchRender = this.dispatchRender.bind(this);
    this.init();
    this.showAnnotations();
  }
  init() {
    this.disposers = [
      this.addEventListener("add", this.dispatchRender),
      this.addEventListener("delete", this.dispatchRender),
      this.annotations.addEventListener("add", this.dispatchRender),
      this.annotations.addEventListener("delete", this.dispatchRender),
      this.pbls.addEventListener("add", this.dispatchRender),
      this.pbls.addEventListener("delete", this.dispatchRender)
    ];
  }
  dispose() {
    this.annotationsAbortController.abort();
    this.disposers.forEach((disposer) => disposer());
    this.disposers = [];
    this.pbls.dispose();
    this.teethNumbers.dispose();
    this.annotations.dispose();
    this.maskDisposers.forEach((disposers) => disposers.forEach((disposer) => disposer()));
    this.maskDisposers.clear();
    this.maskContainers.forEach((wrapper) => wrapper.dispose());
    this.maskContainers.clear();
    super.dispose();
  }
  setImage(image) {
    if (this.image && this.image !== image) {
      this.delete(this.image);
      this.image.dispose();
    }
    this.image = image;
    this.add(image);
  }
  getImage() {
    return this.image;
  }
  getAnnotationHandleMaxZoom() {
    return Math.max(this.width, this.height);
  }
  *getPBLs() {
    for (let pbl of this.pbls.children) {
      yield pbl;
    }
  }
  addPBL(pbl) {
    this.pbls.add(pbl);
  }
  showPBLs() {
    this.add(this.pbls);
  }
  hidePBLs() {
    this.delete(this.pbls);
  }
  addAnnotation(annotation) {
    this.annotations.add(annotation);
  }
  async addAnnotationWhenReady(annotation) {
    const signal = this.annotationsAbortController.signal;
    const cancellation = promiseWithResolvers();
    const cancel = () => cancellation.resolve();
    let added = false;
    signal.addEventListener("abort", cancel, { once: true });
    try {
      if (this.disposed || signal.aborted) {
        return;
      }
      if (annotation instanceof Ruler || annotation instanceof Angle) {
        await Promise.race([annotation.text.waitSync(), cancellation.promise]);
      }
      if (this.disposed || signal.aborted) {
        return;
      }
      this.addAnnotation(annotation);
      added = true;
    } finally {
      signal.removeEventListener("abort", cancel);
      if (!added) {
        annotation.dispose();
      }
    }
  }
  deleteAnnotation(annotation) {
    this.annotations.delete(annotation);
    annotation.dispose();
  }
  deleteAllAnnotations() {
    this.annotationsAbortController.abort();
    this.annotationsAbortController = new AbortController();
    if (this.annotations.children.length) {
      const removed = [...this.annotations.children];
      this.annotations.delete(removed);
      removed.forEach((annotation) => annotation.dispose());
      return true;
    }
    return false;
  }
  showAnnotations() {
    this.add(this.annotations);
  }
  hideAnnotations() {
    this.delete(this.annotations);
  }
  *getAnnotations() {
    for (let annotation of this.annotations.children) {
      yield annotation;
    }
  }
  *getMasks() {
    for (let mask of this.maskContainers.values()) {
      yield mask;
    }
  }
  specifyMaskGroups(groupIDs) {
    groupIDs.forEach((groupID) => {
      const maskWrapper = this.maskContainers.get(groupID);
      if (!maskWrapper) {
        this.addMaskWrapper(groupID);
      }
    });
  }
  addMaskWrapper(groupID) {
    const wrapper = new MaskWrapper(groupID);
    wrapper.setComponent(RaycastChildrenOnly);
    this.maskContainers.set(groupID, wrapper);
    this.maskDisposers.set(groupID, [
      wrapper.addEventListener("add", this.dispatchRender),
      wrapper.addEventListener("delete", this.dispatchRender)
    ]);
    return wrapper;
  }
  addMask(masks, groupID) {
    let maskWrapper = this.maskContainers.get(groupID) || this.addMaskWrapper(groupID);
    maskWrapper.add(masks);
  }
  deleteMasks(groupIDs) {
    if (groupIDs) {
      groupIDs.forEach((groupID) => {
        const maskWrapper = this.maskContainers.get(groupID);
        if (maskWrapper) {
          this.delete(maskWrapper);
          this.maskContainers.delete(groupID);
          this.disposeMaskListeners(groupID);
          maskWrapper.dispose();
        }
      });
    } else {
      const groups = [...this.maskContainers.entries()];
      this.delete(groups.map(([, wrapper]) => wrapper));
      this.maskContainers.clear();
      groups.forEach(([groupID, wrapper]) => {
        this.disposeMaskListeners(groupID);
        wrapper.dispose();
      });
    }
  }
  disposeMaskListeners(groupID) {
    this.maskDisposers.get(groupID)?.forEach((disposer) => disposer());
    this.maskDisposers.delete(groupID);
  }
  deleteMak(maskIDs, groupID) {
    if (groupID) {
      const maskWrapper = this.maskContainers.get(groupID);
      if (maskWrapper) {
        maskIDs.forEach((maskID) => {
          const mask = maskWrapper.children.find((child) => child.id === maskID);
          if (mask) {
            maskWrapper.delete(mask);
            mask.dispose();
          }
        });
      }
    } else {
      maskIDs.forEach((maskID) => {
        this.maskContainers.forEach((maskWrapper) => {
          const mask = maskWrapper.children.find((child) => child.id === maskID);
          if (mask) {
            maskWrapper.delete(mask);
            mask.dispose();
            return;
          }
        });
      });
    }
  }
  async showMasks(groupIDs, duration = 0) {
    const masksToShow = [];
    if (groupIDs) {
      groupIDs.forEach((groupID) => {
        const masks = this.maskContainers.get(groupID);
        if (masks && !this.children.includes(masks)) {
          masksToShow.push(masks);
          this.add(masks);
        }
      });
    } else {
      this.maskContainers.forEach((masks) => {
        if (!this.children.includes(masks)) {
          masksToShow.push(masks);
          this.add(masks);
        }
      });
    }
    if (duration && masksToShow.length > 0) {
      const allMasks = masksToShow.flatMap((wrapper) => wrapper.children);
      await animateShow(allMasks, { duration });
    }
  }
  async hideMasks(groupIDs, duration = 0) {
    const masksToHide = [];
    if (groupIDs) {
      groupIDs.forEach((groupID) => {
        const masks = this.maskContainers.get(groupID);
        if (masks && this.children.includes(masks)) {
          masksToHide.push(masks);
        }
      });
    } else {
      this.maskContainers.forEach((masks) => {
        if (this.children.includes(masks)) {
          masksToHide.push(masks);
        }
      });
    }
    if (masksToHide.length > 0) {
      if (!duration) {
        this.delete(masksToHide);
        return;
      }
      const allMasks = masksToHide.flatMap((wrapper) => wrapper.children);
      await animateHide(allMasks, {
        duration,
        onComplete: () => {
          this.delete(masksToHide);
        }
      });
    }
  }
  addConditionBoxes(boxes) {
    boxes.forEach((box) => {
      this.conditionBoxes.set(box.id, box);
      this.conditionBoxEntities.add(box);
    });
    this.add(boxes);
  }
  deleteConditionBoxes() {
    const boxes = [...this.conditionBoxEntities.values()];
    this.delete(boxes);
    this.conditionBoxes.clear();
    this.conditionBoxEntities.clear();
    boxes.forEach((box) => box.dispose());
  }
  getConditionBox(id) {
    return this.conditionBoxes.get(id);
  }
  addTeethNumber(teethNumber) {
    this.teethNumbers.add(teethNumber);
  }
  showTeethNumbers() {
    this.add(this.teethNumbers);
  }
  hideTeethNumbers() {
    this.delete(this.teethNumbers);
  }
  dispatchRender() {
    this.getLayer()?.getView()?.dispatchEvent({ type: "render" });
  }
  lookAtImage(camera, box) {
    const size = new Vector3();
    if (box) {
      this.object3d.updateMatrixWorld();
      const boxTemp = box.clone();
      boxTemp.applyMatrix4(this.object3d.matrixWorld);
      boxTemp.getCenter(camera.position);
      size.subVectors(boxTemp.max, boxTemp.min);
    } else {
      const image = this.getImage();
      if (!image) {
        return;
      }
      image.object3d.updateWorldMatrix(true, false);
      const x = new Vector3().setFromMatrixColumn(image.object3d.matrixWorld, 0);
      const y = new Vector3().setFromMatrixColumn(image.object3d.matrixWorld, 1);
      const diagonalA = x.clone().add(y);
      const diagonalB = x.clone().sub(y);
      size.set(Math.max(Math.abs(diagonalA.x), Math.abs(diagonalB.x)), Math.max(Math.abs(diagonalA.y), Math.abs(diagonalB.y)), 0);
      const box2 = image.object3d.geometry.boundingBox.clone();
      box2.applyMatrix4(image.object3d.matrixWorld);
      box2.getCenter(camera.position);
    }
    const cameraWidth = camera.right - camera.left;
    const cameraHeight = camera.top - camera.bottom;
    camera.zoom = Math.min(cameraHeight / size.y, cameraWidth / size.x);
    camera.updateMatrix();
    camera.updateProjectionMatrix();
  }
}

const targets = /* @__PURE__ */ new WeakMap();
const registerAnnotationToolTarget = (viewport, resolve) => {
  targets.set(viewport, resolve);
  return () => targets.delete(viewport);
};
const resolveAnnotationToolTarget = (viewport) => {
  const resolve = targets.get(viewport);
  if (resolve) {
    return resolve();
  }
  const imageController = viewport.view.imageController;
  const assetID = imageController?.viewportToImageMap.get(viewport);
  const container = assetID && imageController.imageContainers.get(assetID);
  return container ? {
    container,
    readonly: false,
    scaleMmPerPixel: container.getImage()?.meta?.scale || 1
  } : null;
};

class Drag extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.entities = [];
    this.isDragging = false;
    this.originalPositions = [];
    this.dragOffset = new Vector3();
    this.dragStart = new Vector2();
    this.dragEnd = new Vector2();
    this.dragDelta = new Vector2();
    this.vector = new Vector3();
    this.entities = [];
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseMoveInitial = this.onMouseMoveInitial.bind(this);
  }
  activate() {
    App.Instance().mouseController.addListener("pointermove", this.onMouseMoveInitial, this.hostObject.getDomElement());
    App.Instance().mouseController.addListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left", 10);
  }
  deactivate() {
    this.cancelInteraction();
    App.Instance().mouseController.removeListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMoveInitial, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerup", this.onMouseUp, document, "left");
    App.Instance().mouseController.listeners.get(this.hostObject.getDomElement())?.enableButton("pointerdown", "left");
  }
  get camera() {
    return this.hostObject.camera;
  }
  get cameraX() {
    return this.hostObject.cameraController.xDir;
  }
  get cameraY() {
    return this.hostObject.cameraController.yDir;
  }
  setObjects() {
    const raycasted = this.view.raycastController.getObject();
    if (!raycasted) {
      this.entities.length = 0;
      return;
    }
    if (raycasted instanceof Handle && raycasted.getComponent(HoverComponent)) {
      this.entities = [raycasted];
    } else if (raycasted?.getComponent(SelectComponent)) {
      this.entities = this.hostObject.view.selectController.getSelected().map((selectComponent) => selectComponent.entity);
    } else {
      this.entities.length = 0;
    }
  }
  onMouseMoveInitial() {
    this.setObjects();
    if (this.entities.length) {
      this.hostObject.getDomElement().style.cursor = "pointer";
    } else {
      this.hostObject.getDomElement().style.cursor = "default";
    }
  }
  onMouseDown(event) {
    this.setObjects();
    if (this.entities.length) {
      this.isDragging = true;
      this.originalPositions = this.entities.map((entity) => entity.object3d.position.clone());
      App.Instance().mouseController.listeners.get(this.hostObject.getDomElement())?.disableButton("pointerdown", "left");
      App.Instance().mouseController.removeListener("pointermove", this.onMouseMoveInitial, this.hostObject.getDomElement());
      App.Instance().mouseController.addListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
      App.Instance().mouseController.addListener("pointerup", this.onMouseUp, document, "left");
      this.dragStart.set(event.clientX, event.clientY);
      this.hostObject.view.dispatchEvent({ type: "transformStart", entities: [...this.entities] });
      this.hostObject.getDomElement().style.cursor = "grabbing";
    }
  }
  onMouseMove(event) {
    this.dragEnd.set(event.clientX, event.clientY);
    this.dragDelta.subVectors(this.dragEnd, this.dragStart);
    this.drag(this.dragDelta.x, this.dragDelta.y);
    this.dragStart.copy(this.dragEnd);
    this.hostObject.view.dispatchEvent({ type: "transform", entities: [...this.entities] });
  }
  onMouseUp() {
    if (!this.isDragging) return;
    this.isDragging = false;
    App.Instance().mouseController.listeners.get(this.hostObject.getDomElement())?.enableButton("pointerdown", "left");
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerup", this.onMouseUp, document, "left");
    App.Instance().mouseController.addListener("pointermove", this.onMouseMoveInitial, this.hostObject.getDomElement());
    this.hostObject.view.dispatchEvent({ type: "transformEnd", entities: [...this.entities] });
    this.dragOffset.set(0, 0, 0);
    this.entities.length = 0;
    this.originalPositions = [];
    this.hostObject.getDomElement().style.cursor = "pointer";
  }
  cancelInteraction() {
    if (!this.isDragging) return false;
    this.entities.forEach((entity, index) => {
      entity.object3d.position.copy(this.originalPositions[index]);
      entity.dispatchEvent({ type: "transform", entity });
    });
    this.onMouseUp();
    return true;
  }
  drag(deltaX, deltaY) {
    this.dragOffset.set(0, 0, 0);
    const target = resolveAnnotationToolTarget(this.hostObject);
    if (target?.getDragOffset) {
      this.dragOffset.copy(target.getDragOffset(deltaX, deltaY));
    } else {
      const sizeRatio = this.hostObject.cameraController.orthographicCameraHelper.getSizeRatio();
      this.dragOffset.copy(this.cameraX).multiplyScalar(deltaX * sizeRatio / this.camera.zoom);
      this.dragOffset.add(this.vector.copy(this.cameraY).multiplyScalar(-deltaY * sizeRatio / this.camera.zoom));
    }
    this.entities.forEach((entity) => {
      if (entity.object3d.parent) {
        const worldPosition = new Vector3();
        entity.object3d.getWorldPosition(worldPosition);
        const newWorldPosition = worldPosition.add(this.dragOffset);
        entity.object3d.parent.worldToLocal(newWorldPosition);
        entity.object3d.position.copy(newWorldPosition);
      } else {
        entity.object3d.position.add(this.dragOffset);
      }
      entity.dispatchEvent({ type: "transform", entity });
    });
  }
}

class DrawAngle extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.isDrawing = false;
    this.addFirstPoint = this.addFirstPoint.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.addNextPoint = this.addNextPoint.bind(this);
    this.raycaster = new Raycaster();
  }
  activate() {
    App.Instance().mouseController.addListener("pointerdown", this.addFirstPoint, this.hostObject.getDomElement(), "left", 10);
    this.hostObject.getDomElement().style.cursor = "crosshair";
  }
  deactivate() {
    this.cancelInteraction();
    App.Instance().mouseController.removeListener("pointerdown", this.addFirstPoint, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerdown", this.addNextPoint, this.hostObject.getDomElement(), "left");
    this.hostObject.getDomElement().style.cursor = "default";
  }
  get pointerPosition() {
    this.raycaster.setFromCamera(this.view.cursorController.position, this.hostObject.camera);
    return this.raycaster.ray.origin;
  }
  getContainer() {
    const target = resolveAnnotationToolTarget(this.hostObject);
    return target && !target.readonly ? target.container : void 0;
  }
  addFirstPoint() {
    const container = this.getContainer();
    if (!container || this.isDrawing) return;
    this.container = container;
    const startPosition = { x: this.pointerPosition.x, y: this.pointerPosition.y, z: 0 };
    this.angle = new Angle({
      start: startPosition,
      end: startPosition,
      center: startPosition,
      color: ANNOTATION_PRESENTATION.editor.color,
      width: ANNOTATION_PRESENTATION.editor.strokeWidthPx
    }, this.hostObject);
    container.object3d.worldToLocal(this.angle.start);
    container.object3d.worldToLocal(this.angle.end);
    container.object3d.worldToLocal(this.angle.center);
    container.addAnnotation(this.angle);
    this.isDrawing = true;
    this.pointsForMove = [this.angle.end, this.angle.start];
    App.Instance().mouseController.addListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerdown", this.addFirstPoint, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.addListener("pointerdown", this.addNextPoint, this.hostObject.getDomElement(), "left", 10);
    this.view.dispatchEvent({ type: "transformStart", entities: [this.angle] });
  }
  addNextPoint() {
    this.pointsForMove.pop();
    if (this.pointsForMove.length === 0) {
      this.isDrawing = false;
      App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
      App.Instance().mouseController.removeListener("pointerdown", this.addNextPoint, this.hostObject.getDomElement(), "left");
      App.Instance().mouseController.addListener("pointerdown", this.addFirstPoint, this.hostObject.getDomElement(), "left", 10);
      this.view.dispatchEvent({ type: "transformEnd", entities: [this.angle] });
    }
  }
  cancelInteraction() {
    if (!this.isDrawing) return false;
    this.isDrawing = false;
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerdown", this.addNextPoint, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.addListener("pointerdown", this.addFirstPoint, this.hostObject.getDomElement(), "left", 10);
    this.container.deleteAnnotation(this.angle);
    this.view.dispatchEvent({ type: "transformEnd", entities: [this.angle] });
    return true;
  }
  onMouseMove() {
    this.pointsForMove.forEach((point) => {
      point.set(this.pointerPosition.x, this.pointerPosition.y, 0);
      this.container.object3d.worldToLocal(point);
    });
    this.angle.dispatchEvent({ type: "transform", entity: this.angle });
    this.view.dispatchEvent({ type: "render" });
  }
}

class DrawStraightLine extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.isDrawing = false;
    this.props = {
      color: ANNOTATION_PRESENTATION.editor.color,
      width: ANNOTATION_PRESENTATION.editor.strokeWidthPx
    };
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.raycaster = new Raycaster();
    this.lineType = Line;
  }
  activate() {
    App.Instance().mouseController.addListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left", 10);
    this.hostObject.getDomElement().style.cursor = "crosshair";
  }
  deactivate() {
    this.cancelInteraction();
    App.Instance().mouseController.removeListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    this.hostObject.getDomElement().style.cursor = "default";
  }
  get pointerPosition() {
    this.raycaster.setFromCamera(this.view.cursorController.position, this.hostObject.camera);
    return this.raycaster.ray.origin;
  }
  getContainer() {
    const target = resolveAnnotationToolTarget(this.hostObject);
    return target && !target.readonly ? target.container : void 0;
  }
  onMouseDown() {
    const container = this.getContainer();
    if (!container || this.isDrawing) return;
    this.container = container;
    const startPosition = { x: this.pointerPosition.x, y: this.pointerPosition.y, z: 0 };
    this.line = this.makeLine(startPosition);
    this.container.object3d.worldToLocal(this.line.start);
    this.container.object3d.worldToLocal(this.line.end);
    this.container.addAnnotation(this.line);
    this.isDrawing = true;
    App.Instance().mouseController.addListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.addListener("pointerup", this.onMouseUp, document, "left");
    this.view.dispatchEvent({ type: "transformStart", entities: [this.line] });
  }
  //TODO: make it generic
  makeLine(startPosition) {
    return new this.lineType({
      start: startPosition,
      end: startPosition,
      ...this.props,
      handleMaxZoom: this.container.getAnnotationHandleMaxZoom()
    }, this.hostObject);
  }
  onMouseMove() {
    this.line.end.set(this.pointerPosition.x, this.pointerPosition.y, 0);
    this.container.object3d.worldToLocal(this.line.end);
    this.line.dispatchEvent({ type: "transform", entity: this.line });
    this.view.dispatchEvent({ type: "render" });
  }
  onMouseUp() {
    if (!this.isDrawing) return;
    this.isDrawing = false;
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    App.Instance().mouseController.removeListener("pointerup", this.onMouseUp, document, "left");
    this.view.dispatchEvent({ type: "transformEnd", entities: [this.line] });
  }
  cancelInteraction() {
    if (!this.isDrawing) return false;
    this.container.deleteAnnotation(this.line);
    this.onMouseUp();
    return true;
  }
}
class DrawArrow extends DrawStraightLine {
  constructor(viewport) {
    super(viewport);
    this.lineType = Arrow;
  }
}
class DrawRuler extends DrawStraightLine {
  constructor(viewport) {
    super(viewport);
    this.lineType = Ruler;
  }
  onMouseDown() {
    const target = resolveAnnotationToolTarget(this.hostObject);
    if (!target || target.readonly) return;
    this.props.scale = target.scaleMmPerPixel;
    super.onMouseDown();
  }
}

const belongsTo = (entity, container) => entity === container || !!entity.parent && belongsTo(entity.parent, container);
const annotationOf = (entity) => entity && (isAnnotation(entity) ? entity : annotationOf(entity.parent));
class FullHeadMPRAnnotationController {
  constructor(view) {
    this.view = view;
    this.panes = /* @__PURE__ */ new Map();
    this.suppressChanges = false;
    this.tool = null;
    this.visible = true;
    this.layer = view.addLayer("fullHeadMPRAnnotations");
    this.subscriptions = [
      view.addEventListener("add", ({ entities }) => {
        entities.filter(isAnnotation).forEach((annotation) => {
          const pane = this.paneForEntity(annotation);
          if (!pane) {
            return;
          }
          this.setupAnnotation(pane, annotation);
          this.emitPreview(pane);
        });
      }),
      view.addEventListener("delete", ({ entities }) => {
        entities.filter(isAnnotation).forEach((annotation) => {
          this.panes.forEach((pane) => {
            pane.annotationSubscriptions.get(annotation)?.();
            pane.annotationSubscriptions.delete(annotation);
            pane.readonlyAnnotations.delete(annotation);
          });
        });
      }),
      view.addEventListener("transformEnd", ({ entities }) => {
        const pane = entities.map((entity) => this.paneForEntity(entity)).find(Boolean);
        if (pane && !this.suppressChanges && pane.visible && !pane.target?.readonly) {
          this.emitChange(pane);
        }
      }),
      view.addEventListener("ActiveViewportWillChange", () => this.cancelInteraction()),
      view.addEventListener("ActiveViewportChanged", () => this.refresh()),
      view.addEventListener("fullHeadMPRViewUpdated", ({ projection }) => this.update(projection)),
      view.addEventListener("fullHeadMPRViewportRendered", ({ projection }) => this.update(projection)),
      view.addEventListener("fullHeadMPRFocusChanged", () => this.refresh())
    ];
  }
  addViewport(projection, viewport) {
    const pane = {
      angle: new DrawAngle(viewport),
      annotationSubscriptions: /* @__PURE__ */ new Map(),
      arrow: new DrawArrow(viewport),
      container: null,
      drag: new Drag(viewport),
      hover: new Hover(viewport),
      key: null,
      projection,
      ready: false,
      readonlyAnnotations: /* @__PURE__ */ new Set(),
      ruler: new DrawRuler(viewport),
      select: new Select(viewport),
      target: null,
      tools: [],
      viewport,
      visible: false
    };
    this.panes.set(projection, pane);
    this.subscriptions.push(registerAnnotationToolTarget(viewport, () => {
      const { container, target } = pane;
      if (!container || !target || !pane.ready || !pane.visible) {
        return null;
      }
      return {
        container,
        getDragOffset: (deltaX, deltaY) => {
          const rect = viewport.getDomElement().getBoundingClientRect();
          return new Vector3(2 * deltaX / rect.width, -2 * deltaY / rect.height, 0).unproject(viewport.camera).sub(new Vector3(0, 0, 0).unproject(viewport.camera));
        },
        readonly: target.readonly,
        scaleMmPerPixel: target.view.widthMm / target.widthPx
      };
    }));
  }
  async setTarget(projection, target) {
    const pane = this.panes.get(projection);
    if (!pane) {
      return;
    }
    const key = target ? JSON.stringify(target) : null;
    if (pane.key === key) {
      this.update(projection);
      return;
    }
    this.clearPane(pane);
    if (!target) {
      return;
    }
    const frozenTarget = { ...target, view: freezeFullHeadMPRView(target.view) };
    const container = new ImageContainer(target.widthPx, target.heightPx, target.id);
    container.object3d.matrixAutoUpdate = false;
    container.getComponent(ViewportComponent)?.addViewport(pane.viewport);
    pane.container = container;
    pane.key = key;
    pane.target = frozenTarget;
    this.layer.add(container);
    this.update(projection);
    await Promise.all(target.annotations.map((layer) => addAnnotationLayer({
      container,
      layer,
      onCreate: (annotation) => {
        if (layer.Readonly) {
          pane.readonlyAnnotations.add(annotation);
        }
        this.setupAnnotation(pane, annotation);
      },
      scale: frozenTarget.view.widthMm / frozenTarget.widthPx
    })));
    if (pane.container !== container) {
      return;
    }
    pane.ready = true;
    this.update(projection);
  }
  setTool(tool) {
    if (this.tool === tool) {
      return;
    }
    this.cancelInteraction();
    this.tool = tool;
    this.refresh();
  }
  setVisible(visible) {
    if (this.visible !== visible) {
      this.cancelInteraction();
      this.visible = visible;
      this.refresh();
    }
  }
  ownsPointer(projection) {
    const pane = this.panes.get(projection);
    return !this.view.controller.isImageToolActive() && !!pane?.visible && !pane.target?.readonly && (this.tool !== null || pane.drag.isDragging || !!this.paneForEntity(this.view.raycastController.getObject()));
  }
  cancelInteraction({ preserveSelection = false } = {}) {
    this.suppressChanges = true;
    const cancelled = [...this.panes.values()].map((pane) => {
      const active = [pane.ruler, pane.arrow, pane.angle, pane.drag].map((tool) => tool.cancelInteraction()).some(Boolean);
      if (active) {
        this.emitPreview(pane);
      }
      return active;
    }).some(Boolean);
    this.suppressChanges = false;
    const selected = this.view.selectController.getSelected().length > 0;
    if (!preserveSelection) {
      this.view.selectController.clear();
    }
    return cancelled || !preserveSelection && selected;
  }
  deleteSelectedAnnotation() {
    const annotation = annotationOf(this.view.selectController.getSelected()[0]?.entity);
    const pane = annotation && this.paneForEntity(annotation);
    if (!annotation || !pane?.visible || pane.target?.readonly || pane.readonlyAnnotations.has(annotation) || !pane.container) {
      return false;
    }
    this.cancelInteraction();
    pane.container.deleteAnnotation(annotation);
    this.emitPreview(pane);
    this.emitChange(pane);
    return true;
  }
  update(projection) {
    const pane = this.panes.get(projection);
    if (!pane?.target || !pane.container) {
      return;
    }
    const snapshot = this.view.controller.getViewSnapshot(projection);
    const visible = !!snapshot && this.visible && this.view.controller.getVisibleProjections().includes(projection) && isSameFullHeadMprAnnotationPlane({
      isSeries: pane.target.isSeries,
      left: pane.target.view,
      right: snapshot.view
    });
    if (pane.visible && !visible) {
      this.cancelInteraction();
    }
    pane.visible = visible;
    if (visible && snapshot) {
      pane.container.object3d.matrix.copy(makeFullHeadMPRAnnotationMatrix({
        camera: pane.viewport.camera,
        from: pane.target,
        to: snapshot
      }));
      pane.container.object3d.updateMatrixWorld(true);
      pane.container.showAnnotations();
    } else {
      pane.container.hideAnnotations();
    }
    this.updateTools(pane);
    this.emitPreview(pane);
  }
  refresh() {
    this.panes.forEach((pane) => this.update(pane.projection));
  }
  clear() {
    this.panes.forEach((pane) => this.clearPane(pane));
  }
  dispose() {
    this.clear();
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    this.view.removeLayer(this.layer.getName());
    this.panes.clear();
  }
  paneForEntity(entity) {
    return entity && [...this.panes.values()].find(
      (pane) => pane.container && belongsTo(entity, pane.container)
    );
  }
  setupAnnotation(pane, annotation) {
    if (pane.annotationSubscriptions.has(annotation)) {
      return;
    }
    if (pane.target?.readonly || pane.readonlyAnnotations.has(annotation)) {
      annotation.removeComponent(Raycast);
    } else {
      annotation.children.forEach((child) => {
        if (child instanceof Handle) {
          child.setComponent(SelectComponent, new SelectComponent(
            child,
            new IncreaseSizeHighlighter(child, 2)
          ));
        }
      });
    }
    pane.annotationSubscriptions.set(annotation, annotation.addEventListener(
      "transform",
      () => this.emitPreview(pane)
    ));
  }
  updateTools(pane) {
    const tools = pane.visible && pane.ready && !pane.target?.readonly && !this.view.controller.isImageToolActive() && pane.viewport === this.view.getActiveViewport() ? this.tool ? [pane[this.tool]] : [pane.hover, pane.select, pane.drag] : [];
    if (tools.length === pane.tools.length && tools.every((tool, index) => tool === pane.tools[index])) {
      return;
    }
    this.suppressChanges = true;
    pane.tools.forEach((tool) => tool.dispose());
    pane.tools = tools;
    tools.forEach((tool) => tool.init());
    this.suppressChanges = false;
  }
  clearPane(pane) {
    this.cancelInteraction();
    pane.visible = false;
    pane.ready = false;
    this.updateTools(pane);
    this.emitPreview(pane);
    pane.annotationSubscriptions.forEach((unsubscribe) => unsubscribe());
    pane.annotationSubscriptions.clear();
    pane.readonlyAnnotations.clear();
    if (pane.container) {
      this.layer.delete(pane.container);
      pane.container.dispose();
    }
    pane.container = null;
    pane.target = null;
    pane.key = null;
  }
  payload(pane) {
    if (!pane.target || !pane.container) {
      return null;
    }
    return {
      annotations: entityToAnnotation([...pane.container.getAnnotations()]),
      heightPx: pane.target.heightPx,
      projection: pane.projection,
      targetID: pane.target.id,
      view: freezeFullHeadMPRView(pane.target.view),
      widthPx: pane.target.widthPx
    };
  }
  emitChange(pane) {
    const payload = this.payload(pane);
    if (payload && pane.container) {
      this.view.dispatchEvent({
        ...payload,
        annotations: entityToAnnotation([...pane.container.getAnnotations()].filter((annotation) => !pane.readonlyAnnotations.has(annotation))),
        type: "fullHeadMPRAnnotationsChanged"
      });
    }
  }
  emitPreview(pane) {
    pane.container?.object3d.updateMatrixWorld(true);
    const payload = this.payload(pane);
    if (payload) {
      this.view.dispatchEvent({
        ...payload,
        annotations: pane.visible ? payload.annotations : [],
        isRenderedByEngine: !this.view.getRenderer().getContext().isContextLost(),
        type: "fullHeadMPRAnnotationsPreview"
      });
      this.view.dispatchEvent({ type: "render" });
    }
  }
}

const getChunkBytes = (data) => data.byteLength;
const _ChunkCache = class _ChunkCache {
  constructor(chunks, metrics, onEmpty) {
    this.entries = /* @__PURE__ */ new Map();
    this.pins = /* @__PURE__ */ new Map();
    this.disposed = false;
    this.currentBytes = 0;
    this.inflight = /* @__PURE__ */ new Map();
    this.cacheHits = 0;
    this.cacheMisses = 0;
    this.decodedChunks = /* @__PURE__ */ new Map();
    this.chunkBytesByIndex = new Map(chunks.map((chunk) => [
      chunk.index,
      chunk.shape.x * chunk.shape.y * chunk.shape.z * Int16Array.BYTES_PER_ELEMENT
    ]));
    this.metrics = metrics;
    this.onEmpty = onEmpty;
    this.publishStats();
  }
  has(chunkIndex) {
    return this.decodedChunks.has(chunkIndex);
  }
  get(chunkIndex) {
    const chunkData = this.decodedChunks.get(chunkIndex) ?? null;
    if (chunkData) {
      this.cacheHits += 1;
      const entry = this.entries.get(chunkIndex);
      _ChunkCache.lru.delete(entry.key);
      _ChunkCache.lru.set(entry.key, { cache: this, index: chunkIndex });
    } else if (this.chunkBytesByIndex.has(chunkIndex)) {
      this.cacheMisses += 1;
    }
    this.publishStats();
    return chunkData;
  }
  set(chunkIndex, data) {
    if (this.disposed) {
      return;
    }
    this.delete(chunkIndex);
    const limitBytes = getDecodedVolumeCacheLimitBytes();
    _ChunkCache.evict(getVolumeMemoryUsage().decodedBytes + data.byteLength - limitBytes);
    this.decodedChunks.set(chunkIndex, data);
    let release;
    try {
      release = reserveVolumeMemory({ decodedBytes: data.byteLength });
    } catch (error) {
      this.decodedChunks.delete(chunkIndex);
      throw error;
    }
    const key = {};
    this.entries.set(chunkIndex, { key, release });
    _ChunkCache.lru.set(key, { cache: this, index: chunkIndex });
    this.currentBytes += getChunkBytes(data);
    this.publishStats();
  }
  getInflight(chunkIndex) {
    return this.inflight.get(chunkIndex);
  }
  trackInflight(chunkIndex, factory) {
    const existingPromise = this.inflight.get(chunkIndex);
    if (existingPromise) {
      return existingPromise;
    }
    const promise = factory().finally(() => {
      if (this.inflight.get(chunkIndex) === promise) {
        this.inflight.delete(chunkIndex);
      }
    });
    this.inflight.set(chunkIndex, promise);
    return promise;
  }
  clear() {
    this.entries.forEach((_, index) => this.delete(index));
    this.currentBytes = 0;
    this.cacheHits = 0;
    this.cacheMisses = 0;
    this.inflight.clear();
    this.decodedChunks.clear();
    this.publishStats();
  }
  retain(chunkIndices) {
    if (this.disposed) {
      return () => {
      };
    }
    const indices = [...new Set(chunkIndices)].filter((index) => this.chunkBytesByIndex.has(index));
    indices.forEach((index) => this.pins.set(index, (this.pins.get(index) ?? 0) + 1));
    if (this.pins.size > 0) {
      _ChunkCache.pinnedCaches.add(this);
    }
    let released = false;
    const release = () => {
      if (released) {
        return;
      }
      released = true;
      indices.forEach((index) => {
        const count = (this.pins.get(index) ?? 1) - 1;
        if (count > 0) {
          this.pins.set(index, count);
        } else {
          this.pins.delete(index);
        }
      });
      if (this.pins.size === 0) {
        _ChunkCache.pinnedCaches.delete(this);
      }
      _ChunkCache.evict(getVolumeMemoryUsage().decodedBytes - getDecodedVolumeCacheLimitBytes());
    };
    try {
      ensureVolumeMemoryAvailable();
    } catch (error) {
      release();
      throw error;
    }
    return release;
  }
  getPinnedBytes(pendingOnly = false) {
    let bytes = 0;
    for (const index of this.pins.keys()) {
      if (!pendingOnly || !this.decodedChunks.has(index)) {
        bytes += this.chunkBytesByIndex.get(index) ?? 0;
      }
    }
    return bytes;
  }
  static getRequiredBytes() {
    return [..._ChunkCache.pinnedCaches].reduce((bytes, cache) => bytes + cache.getPinnedBytes(), 0);
  }
  dispose() {
    this.disposed = true;
    this.pins.clear();
    _ChunkCache.pinnedCaches.delete(this);
    this.clear();
  }
  delete(index) {
    const entry = this.entries.get(index);
    if (!entry) {
      return;
    }
    this.currentBytes -= this.decodedChunks.get(index).byteLength;
    this.decodedChunks.delete(index);
    this.entries.delete(index);
    _ChunkCache.lru.delete(entry.key);
    entry.release();
    if (this.entries.size === 0) {
      this.onEmpty?.();
    }
  }
  static evict(bytes) {
    let remaining = bytes;
    for (const { cache, index } of _ChunkCache.lru.values()) {
      if (remaining <= 0) {
        break;
      }
      if (cache.pins.has(index)) {
        continue;
      }
      remaining -= cache.decodedChunks.get(index).byteLength;
      cache.delete(index);
      cache.metrics.incrementCounter("chunkEvictions");
      cache.publishStats();
    }
  }
  getBytes() {
    return this.currentBytes;
  }
  getLoadedCount() {
    return this.decodedChunks.size;
  }
  publishStats() {
    const totalLookups = this.cacheHits + this.cacheMisses;
    this.metrics.setGauge(
      "chunkCacheHitRate",
      totalLookups > 0 ? this.cacheHits / totalLookups : 0
    );
    this.metrics.setGauge("chunkCacheBytes", this.currentBytes);
  }
};
_ChunkCache.lru = /* @__PURE__ */ new Map();
_ChunkCache.pinnedCaches = /* @__PURE__ */ new Set();
registerVolumeMemoryReclaimer({
  getPendingBytes: () => [..._ChunkCache.pinnedCaches].reduce(
    (bytes, cache) => bytes + cache.getPinnedBytes(true),
    0
  ),
  getRequiredBytes: () => _ChunkCache.getRequiredBytes(),
  reclaim: (bytes) => _ChunkCache.evict(bytes)
});
let ChunkCache = _ChunkCache;

let initPromise = null;
const ensureWasmInit = () => {
  if (!initPromise) {
    initPromise = init().then(({ memory }) => trackVolumeWasmMemory(memory));
  }
  return initPromise;
};

const now$5 = () => typeof performance !== "undefined" ? performance.now() : Date.now();
const createChunkBuffer = (chunk) => new Int16Array(chunk.shape.x * chunk.shape.y * chunk.shape.z);
const mergeConsecutiveChunks = (chunks, options) => {
  if (!chunks.length) {
    return [];
  }
  const maxMergeGapBytes = Math.max(0, options?.maxMergeGapBytes ?? 0);
  const maxMergedRangeBytes = Math.max(
    1,
    options?.maxMergedRangeBytes ?? Number.POSITIVE_INFINITY
  );
  const sortedChunks = [...chunks].sort(
    (left, right) => left.fileOffset - right.fileOffset
  );
  const mergedRanges = [[sortedChunks[0]]];
  sortedChunks.slice(1).forEach((chunk) => {
    const currentRange = mergedRanges[mergedRanges.length - 1];
    const firstChunk = currentRange[0];
    const previousChunk = currentRange[currentRange.length - 1];
    const previousEnd = previousChunk.fileOffset + previousChunk.contentSize;
    const gapBytes = chunk.fileOffset - previousEnd;
    const mergedRangeBytes = chunk.fileOffset + chunk.contentSize - firstChunk.fileOffset;
    if (gapBytes >= 0 && gapBytes <= maxMergeGapBytes && mergedRangeBytes <= maxMergedRangeBytes) {
      currentRange.push(chunk);
      return;
    }
    mergedRanges.push([chunk]);
  });
  const requestOrder = new Map(
    chunks.map((chunk, index) => [chunk.index, index])
  );
  return mergedRanges.sort((left, right) => {
    const leftRank = Math.min(
      ...left.map((chunk) => requestOrder.get(chunk.index) ?? Number.MAX_SAFE_INTEGER)
    );
    const rightRank = Math.min(
      ...right.map((chunk) => requestOrder.get(chunk.index) ?? Number.MAX_SAFE_INTEGER)
    );
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    return left[0].fileOffset - right[0].fileOffset;
  });
};
const isExpiredSignatureStatus = (status) => status === 401 || status === 403;
const fetchRangeWithRetry = async (urlSource, start, end, signal) => {
  let hasRefreshedUrl = false;
  while (true) {
    const url = await urlSource.getUrl();
    const response = await fetchWithStorageRetry(
      url,
      {
        headers: { Range: `bytes=${start}-${end}` },
        signal
      },
      { onRetry: logStorageRetry("DCCT", url) }
    );
    if (response.ok) {
      return response;
    }
    if (isExpiredSignatureStatus(response.status) && !hasRefreshedUrl) {
      hasRefreshedUrl = true;
      await urlSource.refreshUrl();
      continue;
    }
    throw new Error(`DCCT range request failed: ${response.status}`);
  }
};
const fetchAndDecompressChunks = async (urlSource, chunks, signal, options) => {
  await ensureWasmInit();
  const shouldReturnChunks = options.returnChunks !== false;
  const uniqueChunks = Array.from(
    new Map(chunks.map((chunk) => [chunk.index, chunk])).values()
  );
  const mergedRanges = mergeConsecutiveChunks(uniqueChunks, {
    ...options,
    maxMergedRangeBytes: Math.min(
      options.maxMergedRangeBytes ?? Infinity,
      VOLUME_STAGING_LIMIT_BYTES / 4
    )
  });
  const batchStartedAt = now$5();
  const returnedChunks = shouldReturnChunks ? /* @__PURE__ */ new Map() : null;
  const maxConcurrentRanges = Math.max(
    1,
    options.maxConcurrentRanges ?? mergedRanges.length
  );
  let nextRangeIndex = 0;
  const processRange = async (range) => {
    const firstChunk = range[0];
    const lastChunk = range[range.length - 1];
    const start = firstChunk.fileOffset;
    const end = lastChunk.fileOffset + lastChunk.contentSize - 1;
    const rangeSizeBytes = end - start + 1;
    const maxChunkBytes = Math.max(...range.map(
      (chunk) => chunk.shape.x * chunk.shape.y * chunk.shape.z * Int16Array.BYTES_PER_ELEMENT
    ));
    const maxCompressedChunkBytes = Math.max(...range.map((chunk) => chunk.contentSize));
    const releaseStaging = await acquireVolumeStaging(
      rangeSizeBytes + maxCompressedChunkBytes + 2 * maxChunkBytes,
      signal
    );
    try {
      const fetchStartedAt = now$5();
      const rangeQueueWaitMs = fetchStartedAt - batchStartedAt;
      const response = await fetchRangeWithRetry(urlSource, start, end, signal);
      const fetchMs = now$5() - fetchStartedAt;
      const body = response.body?.getReader();
      if (!body) {
        throw new Error("DCCT range response has no body");
      }
      const rangeData = new Uint8Array(rangeSizeBytes);
      let offset = 0;
      try {
        while (true) {
          signal?.throwIfAborted();
          const { done, value } = await body.read();
          if (done) {
            break;
          }
          if (offset + value.byteLength > rangeSizeBytes) {
            throw new Error("DCCT range response exceeds the requested size");
          }
          rangeData.set(value, offset);
          offset += value.byteLength;
        }
        if (offset !== rangeSizeBytes) {
          throw new Error("DCCT range response is incomplete");
        }
      } finally {
        await body.cancel();
        body.releaseLock();
      }
      options.metrics?.recordSample("rangeQueueWaitMs", rangeQueueWaitMs);
      options.metrics?.recordSample("rangeFetchMs", fetchMs);
      options.metrics?.recordSample("rangeSizeBytes", rangeSizeBytes);
      options.metrics?.recordSample("mergedRangeChunkCount", range.length);
      range.forEach((chunk) => {
        signal?.throwIfAborted();
        if (options.shouldLoadChunk && !options.shouldLoadChunk(chunk.index)) {
          return;
        }
        const rangeOffset = chunk.fileOffset - start;
        const compressedChunk = rangeData.subarray(
          rangeOffset,
          rangeOffset + chunk.contentSize
        );
        const decompressedChunk = createChunkBuffer(chunk);
        const decompressStartedAt = now$5();
        decompressChunkInto(
          compressedChunk,
          chunk.shape,
          options.version,
          decompressedChunk
        );
        options.metrics?.recordSample(
          "wasmDecompressMs",
          now$5() - decompressStartedAt
        );
        options.onChunk?.(chunk.index, decompressedChunk);
        returnedChunks?.set(chunk.index, decompressedChunk);
      });
    } finally {
      releaseStaging();
    }
  };
  const workerCount = Math.min(maxConcurrentRanges, mergedRanges.length);
  const workers = Array.from({ length: workerCount }, async () => {
    while (nextRangeIndex < mergedRanges.length) {
      const range = mergedRanges[nextRangeIndex].filter(
        (chunk) => options.shouldLoadChunk?.(chunk.index) ?? true
      );
      nextRangeIndex += 1;
      if (range.length > 0) {
        await processRange(range);
      }
    }
  });
  await Promise.all(workers);
  return returnedChunks ?? /* @__PURE__ */ new Map();
};

const chunkToBox3 = (chunk, geometry) => {
  const minX = geometry.patientCsOrigin.x + chunk.origin.x * geometry.spacing.x;
  const minY = geometry.patientCsOrigin.y + chunk.origin.y * geometry.spacing.y;
  const minZ = geometry.patientCsOrigin.z + chunk.origin.z * geometry.spacing.z;
  const maxX = minX + chunk.shape.x * geometry.spacing.x;
  const maxY = minY + chunk.shape.y * geometry.spacing.y;
  const maxZ = minZ + chunk.shape.z * geometry.spacing.z;
  return {
    min: { x: minX, y: minY, z: minZ },
    max: { x: maxX, y: maxY, z: maxZ }
  };
};
const chunkCenter = (chunk, geometry) => {
  const box = chunkToBox3(chunk, geometry);
  return {
    x: (box.min.x + box.max.x) / 2,
    y: (box.min.y + box.max.y) / 2,
    z: (box.min.z + box.max.z) / 2
  };
};
const distanceSq = (a, b) => (a.x - b.x) ** 2 + (a.y - b.y) ** 2 + (a.z - b.z) ** 2;
const sortChunksByProximity = (chunks, toothOrigin, geometry) => [...chunks].sort(
  (a, b) => distanceSq(chunkCenter(a, geometry), toothOrigin) - distanceSq(chunkCenter(b, geometry), toothOrigin)
);

const BASE_URI = __REACT_APP_ROOT_URI__ || __REACT_APP_BASE_URI__ || window.location.origin;
const getFileSrc = (fileID) => `${BASE_URI}/api/storage/download/file/${fileID}`;
const getImageSrc = (imageID, imageSize) => `${BASE_URI}/api/storage/download/image/${imageSize}/${imageID}`;

const DEFAULT_DCCT_RUNTIME_CONFIG = {
  demandWaveSize: {
    focus: 32,
    visible: 14,
    interactive: 8,
    background: 3
  },
  demandMaxConcurrentRanges: {
    focus: void 0,
    visible: void 0,
    interactive: void 0,
    background: void 0
  },
  rangeMergePolicy: {
    focus: {
      maxGapBytes: 512 * 1024,
      maxRangeBytes: 6 * 1024 * 1024
    },
    visible: {
      maxGapBytes: 512 * 1024,
      maxRangeBytes: 6 * 1024 * 1024
    },
    interactive: {
      maxGapBytes: 512 * 1024,
      maxRangeBytes: 6 * 1024 * 1024
    },
    background: {
      maxGapBytes: 0,
      maxRangeBytes: Number.POSITIVE_INFINITY
    }
  }
};
let currentDCCTRuntimeConfig = DEFAULT_DCCT_RUNTIME_CONFIG;
let currentDCCTFetchCredentials = "same-origin";
const getSharedDCCTRuntimeConfig = () => currentDCCTRuntimeConfig;
const volumeSizeByFileID = /* @__PURE__ */ new Map();
const configureSharedDCCTVolumeSize = ({
  fileID,
  sizeBytes
}) => {
  if (!fileID || !Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return;
  }
  volumeSizeByFileID.set(fileID, sizeBytes);
};
const getSharedDCCTVolumeSize = (fileID) => volumeSizeByFileID.get(fileID);
const getSharedDCCTFetchCredentials = () => currentDCCTFetchCredentials;
const configureSharedDCCTFetchCredentials = (credentials) => {
  currentDCCTFetchCredentials = credentials;
};

const readTrailerVersion = (trailerView) => {
  const minor = trailerView.getUint16(DCCTConstants.footerSizeSize, true);
  const major = trailerView.getUint16(
    DCCTConstants.footerSizeSize + DCCTConstants.versionSize / 2,
    true
  );
  const [maxMajor, maxMinor] = DCCTConstants.version;
  if (major !== maxMajor || minor > maxMinor) {
    throw new Error(
      `Unsupported DCCT version: ${major}.${minor} (max supported: ${maxMajor}.${maxMinor})`
    );
  }
  return [major, minor];
};

const now$4 = () => typeof performance !== "undefined" ? performance.now() : Date.now();
const fetchByteRange = async (url, start, end, signal) => {
  const expectedLength = end - start + 1;
  const response = await fetchWithStorageRetry(
    url,
    {
      headers: { Range: `bytes=${start}-${end}` },
      signal
    },
    { onRetry: logStorageRetry("DCCT", url) }
  );
  if (response.status !== 206) {
    await response.body?.cancel();
    throw new Error(
      `DCCT range request was not answered with a partial response: ${response.status}`
    );
  }
  const contentRange = response.headers.get("Content-Range");
  const rangeMatch = contentRange?.match(
    /^bytes (\d+)-(\d+)\/(?:\d+|\*)$/
  );
  if (contentRange && (!rangeMatch || Number.parseInt(rangeMatch[1], 10) !== start || Number.parseInt(rangeMatch[2], 10) !== end)) {
    await response.body?.cancel();
    throw new Error(
      `DCCT range response declared ${contentRange}, expected bytes ${start}-${end}`
    );
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength !== expectedLength) {
    throw new Error(
      `DCCT range request returned ${buffer.byteLength} bytes, expected ${expectedLength}`
    );
  }
  return buffer;
};
const fetchContentLength = async (url, signal) => {
  const probeResponse = await fetchWithStorageRetry(
    url,
    {
      headers: { Range: "bytes=0-0" },
      signal
    },
    { onRetry: logStorageRetry("DCCT", url) }
  );
  await probeResponse.body?.cancel().catch(() => {
  });
  if (!probeResponse.ok) {
    throw new Error(`Failed to determine DCCT file size: ${probeResponse.status}`);
  }
  const contentRange = probeResponse.headers.get("Content-Range");
  const totalSizeFromRange = contentRange?.match(/\/(\d+)$/)?.[1];
  const rangeSize = totalSizeFromRange ? Number.parseInt(totalSizeFromRange, 10) : Number.NaN;
  if (Number.isFinite(rangeSize) && rangeSize > 0) {
    return rangeSize;
  }
  const contentLength = probeResponse.status === 200 ? Number.parseInt(probeResponse.headers.get("Content-Length") ?? "", 10) : Number.NaN;
  if (Number.isFinite(contentLength) && contentLength > 0) {
    return contentLength;
  }
  throw new Error("Failed to determine DCCT file size");
};
const fetchPresignedUrl = async (fileID, signal, metrics) => {
  const startedAt = now$4();
  const presignedUrlEndpoint = `${BASE_URI}/api/storage/download/file/${fileID}?returnURL=true`;
  const response = await fetchWithStorageRetry(
    presignedUrlEndpoint,
    {
      credentials: getSharedDCCTFetchCredentials(),
      signal
    },
    { onRetry: logStorageRetry("DCCT", presignedUrlEndpoint) }
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch DCCT presigned URL for file ${fileID}: ${response.status}`
    );
  }
  const text = (await response.text()).trim();
  metrics?.recordSample("presignedUrlResolveMs", now$4() - startedAt);
  return text;
};
const SIGV4_DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/;
const parsePresignedUrlExpiresAt = (presignedUrl) => {
  try {
    const params = new URL(presignedUrl).searchParams;
    const signedAt = params.get("X-Amz-Date");
    const expiresInSeconds = Number.parseInt(
      params.get("X-Amz-Expires") ?? "",
      10
    );
    const dateMatch = signedAt?.match(SIGV4_DATE_PATTERN);
    if (!dateMatch || !Number.isFinite(expiresInSeconds) || expiresInSeconds <= 0) {
      return null;
    }
    const [, year, month, day, hours, minutes, seconds] = dateMatch;
    const signedAtMs = Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      Number(seconds)
    );
    return signedAtMs + expiresInSeconds * 1e3;
  } catch {
    return null;
  }
};
const FOOTER_WINDOW_BYTES = 512 * 1024;
const fetchDCCTVolumeMetadata = async (presignedUrl, signal, metrics, knownFileSize) => {
  const startedAt = now$4();
  const fileSize = knownFileSize && knownFileSize > 0 ? knownFileSize : await fetchContentLength(presignedUrl, signal);
  if (fileSize < DCCTConstants.trailerSize + DCCTConstants.magicBytesSize) {
    throw new Error(`Invalid DCCT file size: ${fileSize}`);
  }
  const windowLength = Math.min(FOOTER_WINDOW_BYTES, fileSize);
  const windowStart = fileSize - windowLength;
  const [leadingMagicBuffer, windowBuffer] = await Promise.all([
    fetchByteRange(presignedUrl, 0, DCCTConstants.magicBytesSize - 1, signal),
    fetchByteRange(presignedUrl, windowStart, fileSize - 1, signal)
  ]);
  const trailerStart = fileSize - DCCTConstants.trailerSize;
  const trailerBuffer = windowBuffer.slice(trailerStart - windowStart);
  const leadingMagicView = new DataView(leadingMagicBuffer);
  for (let index = 0; index < DCCTConstants.magicBytesSize; index += 1) {
    if (leadingMagicView.getUint8(index) !== DCCTConstants.magicBytes[index]) {
      throw new Error("Invalid DCCT file: leading magic bytes mismatch");
    }
  }
  const trailerView = new DataView(trailerBuffer);
  const footerSize = trailerView.getUint32(0, true);
  for (let index = 0; index < DCCTConstants.magicBytesSize; index += 1) {
    const trailerMagicOffset = DCCTConstants.footerSizeSize + DCCTConstants.versionSize + index;
    if (trailerView.getUint8(trailerMagicOffset) !== DCCTConstants.magicBytes[index]) {
      throw new Error("Invalid DCCT file: trailing magic bytes mismatch");
    }
  }
  const version = readTrailerVersion(trailerView);
  const footerStart = fileSize - DCCTConstants.trailerSize - footerSize;
  if (footerStart < DCCTConstants.magicBytesSize) {
    throw new Error(`Invalid DCCT footer size: ${footerSize}`);
  }
  const footerEnd = fileSize - DCCTConstants.trailerSize - 1;
  const footerBuffer = footerStart >= windowStart ? windowBuffer.slice(
    footerStart - windowStart,
    footerEnd - windowStart + 1
  ) : await fetchByteRange(presignedUrl, footerStart, footerEnd, signal);
  const parsedFooter = parseDCCTFooter(new Uint8Array(footerBuffer));
  let currentOffset = DCCTConstants.magicBytesSize;
  const chunks = parsedFooter.chunks.map(
    (chunk, index) => {
      const nextChunk = {
        contentSize: chunk.contentSize,
        fileOffset: currentOffset,
        index,
        origin: chunk.origin,
        shape: chunk.shape
      };
      currentOffset += chunk.contentSize;
      return nextChunk;
    }
  );
  metrics?.recordSample("footerParseMs", now$4() - startedAt);
  return {
    chunks,
    geometry: {
      patientCsOrigin: parsedFooter.geometry.patientCSOrigin,
      shape: parsedFooter.geometry.shape,
      spacing: parsedFooter.geometry.spacing
    },
    version
  };
};

const now$3 = () => typeof performance !== "undefined" ? performance.now() : Date.now();
const quantile$1 = (values, ratio) => {
  if (!values.length) {
    return 0;
  }
  const sortedValues = [...values].sort((left, right) => left - right);
  const index = Math.min(
    sortedValues.length - 1,
    Math.max(0, Math.floor((sortedValues.length - 1) * ratio))
  );
  return sortedValues[index];
};
const buildSummary$1 = (values) => {
  const count = values.length;
  const sum = values.reduce((total, value) => total + value, 0);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const last = values[count - 1];
  return {
    avg: count > 0 ? sum / count : 0,
    count,
    last,
    max,
    min,
    p50: quantile$1(values, 0.5),
    p95: quantile$1(values, 0.95),
    sum
  };
};
class DCCTMetricsStore {
  constructor() {
    this.counters = {};
    this.gauges = {};
    this.lastUpdatedAt = now$3();
    this.listeners = /* @__PURE__ */ new Set();
    this.sampleValues = /* @__PURE__ */ new Map();
    this.sessionStartedAt = now$3();
  }
  recordSample(name, value) {
    if (!Number.isFinite(value)) {
      return;
    }
    const values = this.sampleValues.get(name) ?? [];
    values.push(value);
    this.sampleValues.set(name, values);
    this.touch();
  }
  incrementCounter(name, value = 1) {
    if (!Number.isFinite(value)) {
      return;
    }
    this.counters[name] = (this.counters[name] ?? 0) + value;
    this.touch();
  }
  setGauge(name, value) {
    if (!Number.isFinite(value)) {
      return;
    }
    this.gauges[name] = value;
    this.touch();
  }
  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }
  getSnapshot() {
    const samples = Array.from(this.sampleValues.entries()).reduce(
      (result, [name, values]) => ({
        ...result,
        [name]: buildSummary$1(values)
      }),
      {}
    );
    return {
      counters: { ...this.counters },
      gauges: { ...this.gauges },
      lastUpdatedAt: this.lastUpdatedAt,
      samples,
      sessionStartedAt: this.sessionStartedAt
    };
  }
  touch() {
    this.lastUpdatedAt = now$3();
    const snapshot = this.getSnapshot();
    this.listeners.forEach((listener) => {
      listener(snapshot);
    });
  }
}

const linkAbortSignals = (...signals) => {
  const controller = new AbortController();
  const subscriptions = signals.map((signal) => {
    const abort = () => controller.abort(signal?.reason);
    if (signal?.aborted) {
      abort();
    } else {
      signal?.addEventListener("abort", abort, { once: true });
    }
    return () => signal?.removeEventListener("abort", abort);
  });
  return {
    release: () => subscriptions.forEach((unsubscribe) => unsubscribe()),
    signal: controller.signal
  };
};

const sharedDCCTVolumeCache = /* @__PURE__ */ new Map();
const sharedDCCTVolumeLoadPromises = /* @__PURE__ */ new Map();
let nextReaderID = 0;
const releaseVolumeOwner = (volumeFileID, owner) => {
  const load = sharedDCCTVolumeLoadPromises.get(volumeFileID);
  const volume = sharedDCCTVolumeCache.get(volumeFileID);
  const owners = volume?.owners ?? load?.owners;
  owners?.delete(owner);
  if (owners?.size !== 0) {
    return;
  }
  if (!volume) {
    load?.abortController.abort();
    sharedDCCTVolumeLoadPromises.delete(volumeFileID);
    return;
  }
  volume.generation += 1;
  volume.abortController.abort();
  volume.consumerDemands.forEach((demand) => demand.release());
  volume.consumerDemands.clear();
  volume.pendingChunkLoadRequests.forEach((request) => request.reject(createAbortError()));
  volume.pendingChunkLoadRequests = [];
  volume.voxelDataLoadPromises.clear();
  volume.presignedUrlRefreshPromise = null;
  volume.detachedWave = null;
  volume.chunkLoadFlushScheduled = false;
  volume.isFlushingChunkLoadRequests = false;
  if (volume.chunkCache.getLoadedCount() === 0) {
    disposeSharedDCCTVolume(volumeFileID);
  }
};
const isCurrentVolume = (volume, generation = volume.generation) => sharedDCCTVolumeCache.get(volume.volumeFileID) === volume && volume.generation === generation && !volume.abortController.signal.aborted && volume.owners.size > 0;
const SHARED_CHUNK_LOAD_PRIORITY_RANK = {
  background: 0,
  interactive: 1,
  visible: 2,
  focus: 3
};
const MAX_DETACHED_WAVE_FAILURES = 3;
const SHARED_CHUNK_LOAD_PRIORITY_BY_RANK = [
  "background",
  "interactive",
  "visible",
  "focus"
];
const createAbortError = () => {
  if (typeof DOMException !== "undefined") {
    return new DOMException("The operation was aborted.", "AbortError");
  }
  const error = new Error("The operation was aborted.");
  error.name = "AbortError";
  return error;
};
const raceWithAbort = async (promise, signal) => {
  if (!signal) {
    return promise;
  }
  if (signal.aborted) {
    throw createAbortError();
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      reject(createAbortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error) => {
        signal.removeEventListener("abort", onAbort);
        reject(error);
      }
    );
  });
};
const uniqueOrderedChunkIndices = (chunkIndices) => [
  ...new Set(chunkIndices)
];
const boxIntersectsBox = (left, right) => left.min.x <= right.max.x && left.max.x >= right.min.x && left.min.y <= right.max.y && left.max.y >= right.min.y && left.min.z <= right.max.z && left.max.z >= right.min.z;
const findChunkOriginAtOrBefore = (sortedOrigins, voxelCoordinate) => {
  if (!sortedOrigins.length) {
    return null;
  }
  let left = 0;
  let right = sortedOrigins.length - 1;
  let resultIndex = -1;
  while (left <= right) {
    const middle = Math.floor((left + right) / 2);
    const origin = sortedOrigins[middle];
    if (origin <= voxelCoordinate) {
      resultIndex = middle;
      left = middle + 1;
    } else {
      right = middle - 1;
    }
  }
  return resultIndex >= 0 ? sortedOrigins[resultIndex] : null;
};
const hasMissingChunk = (cachedVolume, chunkIndex) => !cachedVolume.chunkCache.has(chunkIndex);
const isChunkWanted = (cachedVolume, chunkIndex) => [...cachedVolume.activeReads.values()].some(
  (read) => !read.signal?.aborted && read.indices.has(chunkIndex)
) || [...cachedVolume.consumerDemands.values()].some(
  (demand) => demand.pendingChunkIndices?.has(chunkIndex) ?? demand.chunkIndices.includes(chunkIndex)
);
const updateVisibleChunkGauge = (cachedVolume) => {
  const visibleChunkIndices = /* @__PURE__ */ new Set();
  cachedVolume.consumerDemands.forEach((demandState) => {
    if (demandState.priority !== "visible") {
      return;
    }
    demandState.chunkIndices.forEach((chunkIndex) => {
      visibleChunkIndices.add(chunkIndex);
    });
  });
  cachedVolume.metrics.setGauge("visibleChunkCount", visibleChunkIndices.size);
};
const getHighestDemandPriorityRank = (cachedVolume) => {
  let highestPriorityRank = null;
  cachedVolume.consumerDemands.forEach((demandState) => {
    const hasMissingDemand = demandState.chunkIndices.some(
      (chunkIndex) => demandState.pendingChunkIndices ? demandState.pendingChunkIndices.has(chunkIndex) : hasMissingChunk(cachedVolume, chunkIndex)
    );
    if (!hasMissingDemand) {
      return;
    }
    const priorityRank = SHARED_CHUNK_LOAD_PRIORITY_RANK[demandState.priority];
    if (highestPriorityRank === null || priorityRank > highestPriorityRank) {
      highestPriorityRank = priorityRank;
    }
  });
  return highestPriorityRank;
};
const getHighestPendingRequestPriorityRank = (cachedVolume) => cachedVolume.pendingChunkLoadRequests.length ? Math.max(
  ...cachedVolume.pendingChunkLoadRequests.map(
    (request) => SHARED_CHUNK_LOAD_PRIORITY_RANK[request.priority]
  )
) : null;
const getPriorityForRank = (priorityRank) => {
  if (priorityRank === null) {
    return null;
  }
  return SHARED_CHUNK_LOAD_PRIORITY_BY_RANK[priorityRank] ?? null;
};
const getDemandMaxConcurrentRanges = (cachedVolume, priority) => {
  const runtimeConfig = getSharedDCCTRuntimeConfig();
  let maxConcurrentRanges = 0;
  cachedVolume.consumerDemands.forEach((demandState) => {
    if (demandState.priority !== priority || !demandState.maxConcurrentRanges) {
      return;
    }
    maxConcurrentRanges = Math.max(
      maxConcurrentRanges,
      demandState.maxConcurrentRanges
    );
  });
  return (maxConcurrentRanges > 0 ? maxConcurrentRanges : void 0) ?? runtimeConfig.demandMaxConcurrentRanges[priority];
};
const getDemandWaveSize = (cachedVolume, priority) => {
  const runtimeConfig = getSharedDCCTRuntimeConfig();
  let waveSize = 0;
  cachedVolume.consumerDemands.forEach((demandState) => {
    if (demandState.priority !== priority || !demandState.waveSize) {
      return;
    }
    waveSize = Math.max(waveSize, demandState.waveSize);
  });
  return Math.max(waveSize, runtimeConfig.demandWaveSize[priority]);
};
const isDetachableWave = (cachedVolume, priority) => priority === "background" && [...cachedVolume.consumerDemands.values()].some(isDetachableDemand);
const sortChunkIndicesByDemand = (cachedVolume, requests, priority) => {
  const demandByChunkIndex = /* @__PURE__ */ new Map();
  let nextOrder = 0;
  const registerDemand = (chunkIndex) => {
    if (cachedVolume.chunkCache.has(chunkIndex)) {
      return;
    }
    const existingDemand = demandByChunkIndex.get(chunkIndex);
    if (existingDemand) {
      existingDemand.count += 1;
      return;
    }
    demandByChunkIndex.set(chunkIndex, {
      count: 1,
      firstSeenOrder: nextOrder
    });
    nextOrder += 1;
  };
  cachedVolume.consumerDemands.forEach((demandState) => {
    if (demandState.priority !== priority) {
      return;
    }
    demandState.chunkIndices.filter(
      (index) => !demandState.pendingChunkIndices || demandState.pendingChunkIndices.has(index)
    ).forEach(registerDemand);
  });
  requests.forEach((request) => {
    request.chunkIndices.forEach(registerDemand);
  });
  return [...demandByChunkIndex.entries()].sort((left, right) => {
    const countDelta = right[1].count - left[1].count;
    if (countDelta !== 0) {
      return countDelta;
    }
    return left[1].firstSeenOrder - right[1].firstSeenOrder;
  }).map(([chunkIndex]) => chunkIndex);
};
const getRequestedChunkIndexSet = (cachedVolume, requests) => new Set(
  requests.flatMap(
    (request) => request.chunkIndices.filter(
      (chunkIndex) => hasMissingChunk(cachedVolume, chunkIndex)
    )
  )
);
const takePendingChunkLoadRequestsForPriority = (cachedVolume, priority) => {
  const retainedRequests = [];
  const takenRequests = [];
  cachedVolume.pendingChunkLoadRequests.forEach((request) => {
    if (request.priority === priority) {
      takenRequests.push(request);
      return;
    }
    retainedRequests.push(request);
  });
  cachedVolume.pendingChunkLoadRequests = retainedRequests;
  return takenRequests;
};
const isDetachableDemand = (demandState) => demandState.priority === "background" && !!demandState.waveSize && demandState.waveSize > getSharedDCCTRuntimeConfig().demandWaveSize.background;
const dropDetachedWaveDemands = (cachedVolume) => {
  const detachable = [...cachedVolume.consumerDemands.entries()].filter(([, demandState]) => isDetachableDemand(demandState)).map(([consumerId]) => consumerId);
  if (!detachable.length) {
    return;
  }
  detachable.forEach((consumerId) => {
    cachedVolume.consumerDemands.get(consumerId)?.release();
    cachedVolume.consumerDemands.delete(consumerId);
  });
  updateVisibleChunkGauge(cachedVolume);
};
const getDemandWaveChunkIndices = (cachedVolume, batchRequests, priority) => {
  const requestedChunkIndexSet = getRequestedChunkIndexSet(
    cachedVolume,
    batchRequests
  );
  const sortedChunkIndicesByDemand = sortChunkIndicesByDemand(
    cachedVolume,
    batchRequests,
    priority
  );
  if (requestedChunkIndexSet.size > 0) {
    return sortedChunkIndicesByDemand.filter(
      (chunkIndex) => requestedChunkIndexSet.has(chunkIndex)
    );
  }
  return sortedChunkIndicesByDemand.slice(
    0,
    getDemandWaveSize(cachedVolume, priority)
  );
};
const getChunksToLoadForIndices = (cachedVolume, chunkIndices) => chunkIndices.map((chunkIndex) => cachedVolume.header.chunks[chunkIndex]).filter(
  (chunk) => !!chunk && !cachedVolume.chunkCache.has(chunk.index) && !cachedVolume.voxelDataLoadPromises.has(chunk.index)
);
const cacheChunkData = (cachedVolume, chunkIndex, data) => {
  const release = cachedVolume.chunkCache.retain([chunkIndex]);
  try {
    cachedVolume.chunkCache.set(chunkIndex, data);
    cachedVolume.processedChunks.add(chunkIndex);
    cachedVolume.consumerDemands.forEach((demand) => {
      demand.pendingChunkIndices?.delete(chunkIndex);
    });
    const nextLoadedChunkCount = cachedVolume.processedChunks.size;
    cachedVolume.metrics.setGauge("loadedChunkCount", nextLoadedChunkCount);
    cachedVolume.loadedChunkCountListeners.forEach((listener) => {
      listener(nextLoadedChunkCount, cachedVolume.header.chunks.length);
    });
    cachedVolume.chunkLoadedListeners.forEach((listener) => {
      listener(chunkIndex);
    });
  } finally {
    release();
  }
};
const getBatchMaxConcurrentRanges = (batchRequests, fallbackMaxConcurrentRanges, fallbackChunkCount) => Math.max(
  1,
  ...batchRequests.map(
    (request) => request.maxConcurrentRanges ?? Math.max(1, fallbackChunkCount)
  ),
  fallbackMaxConcurrentRanges ?? Math.max(1, fallbackChunkCount)
);
const PRESIGNED_URL_EXPIRY_SAFETY_MARGIN_MS = 6e4;
const refreshSharedVolumePresignedUrl = (cachedVolume) => {
  if (cachedVolume.presignedUrlRefreshPromise) {
    return cachedVolume.presignedUrlRefreshPromise;
  }
  const generation = cachedVolume.generation;
  const refreshPromise = fetchPresignedUrl(
    cachedVolume.volumeFileID,
    cachedVolume.abortController.signal,
    cachedVolume.metrics
  ).then((presignedUrl) => {
    if (!isCurrentVolume(cachedVolume, generation)) {
      throw createAbortError();
    }
    cachedVolume.presignedUrl = presignedUrl;
    cachedVolume.presignedUrlExpiresAt = parsePresignedUrlExpiresAt(presignedUrl);
    return presignedUrl;
  }).finally(() => {
    if (cachedVolume.presignedUrlRefreshPromise === refreshPromise) {
      cachedVolume.presignedUrlRefreshPromise = null;
    }
  });
  cachedVolume.presignedUrlRefreshPromise = refreshPromise;
  return refreshPromise;
};
const getFreshSharedVolumePresignedUrl = (cachedVolume) => {
  const { presignedUrlExpiresAt } = cachedVolume;
  const isExpiringSoon = presignedUrlExpiresAt !== null && Date.now() >= presignedUrlExpiresAt - PRESIGNED_URL_EXPIRY_SAFETY_MARGIN_MS;
  if (isExpiringSoon) {
    return refreshSharedVolumePresignedUrl(cachedVolume);
  }
  return Promise.resolve(cachedVolume.presignedUrl);
};
const fetchMissingChunks = async (cachedVolume, chunksToLoad, priority, batchRequests, demandMaxConcurrentRanges) => {
  if (!chunksToLoad.length) {
    return;
  }
  const runtimeConfig = getSharedDCCTRuntimeConfig();
  const generation = cachedVolume.generation;
  const mergePolicy = runtimeConfig.rangeMergePolicy[priority];
  const maxConcurrentRanges = getBatchMaxConcurrentRanges(
    batchRequests,
    demandMaxConcurrentRanges,
    chunksToLoad.length
  );
  const loadPromise = fetchAndDecompressChunks(
    {
      getUrl: () => getFreshSharedVolumePresignedUrl(cachedVolume),
      refreshUrl: () => refreshSharedVolumePresignedUrl(cachedVolume)
    },
    chunksToLoad,
    cachedVolume.abortController.signal,
    {
      version: cachedVolume.header.version,
      maxConcurrentRanges,
      maxMergeGapBytes: mergePolicy.maxGapBytes,
      maxMergedRangeBytes: mergePolicy.maxRangeBytes,
      metrics: cachedVolume.metrics,
      onChunk: (chunkIndex, data) => {
        if (isCurrentVolume(cachedVolume, generation)) {
          cacheChunkData(cachedVolume, chunkIndex, data);
        }
      },
      returnChunks: false,
      shouldLoadChunk: (index) => isCurrentVolume(cachedVolume, generation) && isChunkWanted(cachedVolume, index)
    }
  ).then(() => void 0);
  chunksToLoad.forEach((chunk) => {
    cachedVolume.voxelDataLoadPromises.set(chunk.index, loadPromise);
  });
  try {
    await loadPromise;
  } finally {
    chunksToLoad.forEach((chunk) => {
      if (cachedVolume.voxelDataLoadPromises.get(chunk.index) === loadPromise) {
        cachedVolume.voxelDataLoadPromises.delete(chunk.index);
      }
    });
  }
};
const scheduleSharedVolumeChunkLoadFlush = (cachedVolume) => {
  if (cachedVolume.chunkLoadFlushScheduled || !isCurrentVolume(cachedVolume)) {
    return;
  }
  cachedVolume.chunkLoadFlushScheduled = true;
  const generation = cachedVolume.generation;
  const flush = async () => {
    if (!isCurrentVolume(cachedVolume, generation)) {
      return;
    }
    cachedVolume.chunkLoadFlushScheduled = false;
    if (cachedVolume.isFlushingChunkLoadRequests) {
      return;
    }
    cachedVolume.isFlushingChunkLoadRequests = true;
    let activeBatchRequests = [];
    try {
      while (isCurrentVolume(cachedVolume, generation)) {
        const highestRequestPriorityRank = getHighestPendingRequestPriorityRank(cachedVolume);
        const highestDemandPriorityRank = getHighestDemandPriorityRank(cachedVolume);
        if (highestRequestPriorityRank === null && highestDemandPriorityRank === null) {
          break;
        }
        const highestPriorityRank = Math.max(
          highestRequestPriorityRank ?? Number.NEGATIVE_INFINITY,
          highestDemandPriorityRank ?? Number.NEGATIVE_INFINITY
        );
        const batchPriority = getPriorityForRank(highestPriorityRank);
        if (!batchPriority) {
          break;
        }
        const rawBatchRequests = takePendingChunkLoadRequestsForPriority(
          cachedVolume,
          batchPriority
        );
        const batchRequests = rawBatchRequests.filter(
          (request) => request.chunkIndices.some(
            (chunkIndex) => hasMissingChunk(cachedVolume, chunkIndex)
          )
        );
        const demandMaxConcurrentRanges = getDemandMaxConcurrentRanges(
          cachedVolume,
          batchPriority
        );
        activeBatchRequests = batchRequests;
        rawBatchRequests.filter((request) => !batchRequests.includes(request)).forEach((request) => request.resolve());
        const prioritizedChunkIndices = getDemandWaveChunkIndices(
          cachedVolume,
          batchRequests,
          batchPriority
        );
        const inFlightPromises = prioritizedChunkIndices.map((chunkIndex) => cachedVolume.voxelDataLoadPromises.get(chunkIndex)).filter((promise) => !!promise);
        const chunksToLoad = getChunksToLoadForIndices(
          cachedVolume,
          prioritizedChunkIndices
        );
        if (batchRequests.length === 0 && isDetachableWave(cachedVolume, batchPriority)) {
          if (cachedVolume.detachedWave || cachedVolume.detachedWaveFailureCount >= MAX_DETACHED_WAVE_FAILURES) {
            break;
          }
          cachedVolume.detachedWave = fetchMissingChunks(
            cachedVolume,
            chunksToLoad,
            batchPriority,
            batchRequests,
            demandMaxConcurrentRanges
          ).then(
            () => inFlightPromises.length ? Promise.all(inFlightPromises).then(() => void 0) : void 0
          ).then(
            () => {
              if (!isCurrentVolume(cachedVolume, generation)) {
                return;
              }
              cachedVolume.detachedWave = null;
              cachedVolume.detachedWaveFailureCount = 0;
              scheduleSharedVolumeChunkLoadFlush(cachedVolume);
            },
            // Nothing awaits a detached wave, so a failure has no caller to
            // surface it: without a retry the demand would sit unfinished and
            // silent. The range fetcher has already retried the transfer
            // itself, so these are whole-wave attempts and they are counted —
            // an endpoint that keeps refusing must not spin the loop forever.
            // A demand update clears the count, which is how a new page or a
            // refreshed URL gets a fresh set of attempts.
            () => {
              if (!isCurrentVolume(cachedVolume, generation)) {
                return;
              }
              cachedVolume.detachedWave = null;
              cachedVolume.detachedWaveFailureCount += 1;
              if (cachedVolume.detachedWaveFailureCount < MAX_DETACHED_WAVE_FAILURES) {
                scheduleSharedVolumeChunkLoadFlush(cachedVolume);
                return;
              }
              console.warn(
                "[DCCT] whole-volume prefetch gave up after",
                MAX_DETACHED_WAVE_FAILURES,
                "failed waves"
              );
              dropDetachedWaveDemands(cachedVolume);
              cachedVolume.detachedWaveGaveUpListeners.forEach((listener) => {
                listener();
              });
              if (cachedVolume.pendingChunkLoadRequests.length > 0 || getHighestDemandPriorityRank(cachedVolume) !== null) {
                scheduleSharedVolumeChunkLoadFlush(cachedVolume);
              }
            }
          );
          break;
        }
        await fetchMissingChunks(
          cachedVolume,
          chunksToLoad,
          batchPriority,
          batchRequests,
          demandMaxConcurrentRanges
        );
        if (inFlightPromises.length) {
          await Promise.all(inFlightPromises);
        }
        batchRequests.forEach((request) => request.resolve());
        activeBatchRequests = [];
      }
    } catch (error) {
      activeBatchRequests.forEach((request) => request.reject(error));
      if (!isCurrentVolume(cachedVolume, generation)) {
        return;
      }
      const pendingRequests = [...cachedVolume.pendingChunkLoadRequests];
      cachedVolume.pendingChunkLoadRequests = [];
      pendingRequests.forEach((request) => request.reject(error));
    } finally {
      if (!isCurrentVolume(cachedVolume, generation)) {
        return;
      }
      cachedVolume.isFlushingChunkLoadRequests = false;
      if (cachedVolume.pendingChunkLoadRequests.length > 0 || getHighestDemandPriorityRank(cachedVolume) !== null && !cachedVolume.detachedWave) {
        scheduleSharedVolumeChunkLoadFlush(cachedVolume);
      }
    }
  };
  if (typeof queueMicrotask === "function") {
    queueMicrotask(() => {
      void flush();
    });
    return;
  }
  Promise.resolve().then(() => {
    void flush();
  });
};
const enqueueSharedVolumeChunkLoad = (cachedVolume, chunkIndices, options, signal) => {
  if (!chunkIndices.length) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      cachedVolume.pendingChunkLoadRequests = cachedVolume.pendingChunkLoadRequests.filter((pending) => pending !== request);
      reject(createAbortError());
    };
    const request = {
      chunkIndices,
      maxConcurrentRanges: options?.maxConcurrentRanges,
      priority: options?.priority ?? "interactive",
      signal,
      reject: (error) => {
        signal?.removeEventListener("abort", onAbort);
        reject(error);
      },
      resolve: () => {
        signal?.removeEventListener("abort", onAbort);
        resolve();
      }
    };
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }
    signal?.addEventListener("abort", onAbort, { once: true });
    cachedVolume.pendingChunkLoadRequests.push(request);
    scheduleSharedVolumeChunkLoadFlush(cachedVolume);
  });
};
const loadSharedDCCTVolumeState = async (volumeFileID, signal, owner = Symbol(volumeFileID)) => {
  if (signal?.aborted) {
    throw createAbortError();
  }
  sharedDCCTVolumeCache.forEach((volume, id) => {
    if (id !== volumeFileID && volume.owners.size === 0 && volume.chunkCache.getLoadedCount() === 0) {
      disposeSharedDCCTVolume(id);
    }
  });
  const cachedVolume = sharedDCCTVolumeCache.get(volumeFileID);
  if (cachedVolume) {
    if (cachedVolume.owners.size === 0) {
      cachedVolume.abortController = new AbortController();
    }
    cachedVolume.owners.add(owner);
    return cachedVolume;
  }
  const inFlightLoad = sharedDCCTVolumeLoadPromises.get(volumeFileID);
  if (inFlightLoad) {
    inFlightLoad.owners.add(owner);
    return raceWithAbort(inFlightLoad.promise, signal);
  }
  const abortController = new AbortController();
  const owners = /* @__PURE__ */ new Set([owner]);
  const loadPromise = (async () => {
    await ensureWasmInit();
    const metrics = new DCCTMetricsStore();
    metrics.setGauge("chunkCacheBytes", 0);
    metrics.setGauge("chunkCacheHitRate", 0);
    metrics.setGauge("loadedChunkCount", 0);
    metrics.setGauge("visibleChunkCount", 0);
    const presignedUrl = await fetchPresignedUrl(volumeFileID, abortController.signal, metrics);
    const header = await fetchDCCTVolumeMetadata(
      presignedUrl,
      abortController.signal,
      metrics,
      getSharedDCCTVolumeSize(volumeFileID)
    );
    const chunkCache = new ChunkCache(header.chunks, metrics, () => {
      const volume = sharedDCCTVolumeCache.get(volumeFileID);
      if (volume?.chunkCache === chunkCache && volume.owners.size === 0) {
        disposeSharedDCCTVolume(volumeFileID);
      }
    });
    const sortedChunkOrigins = {
      x: [...new Set(header.chunks.map((chunk) => chunk.origin.x))].sort(
        (left, right) => left - right
      ),
      y: [...new Set(header.chunks.map((chunk) => chunk.origin.y))].sort(
        (left, right) => left - right
      ),
      z: [...new Set(header.chunks.map((chunk) => chunk.origin.z))].sort(
        (left, right) => left - right
      )
    };
    const chunkOriginKeyToIndex = /* @__PURE__ */ new Map();
    header.chunks.forEach((chunk) => {
      chunkOriginKeyToIndex.set(
        `${chunk.origin.x},${chunk.origin.y},${chunk.origin.z}`,
        chunk.index
      );
    });
    const nextCachedVolume = {
      abortController,
      activeReads: /* @__PURE__ */ new Map(),
      chunkCache,
      chunkLoadedListeners: /* @__PURE__ */ new Set(),
      chunkLoadFlushScheduled: false,
      chunkOriginKeyToIndex,
      consumerDemands: /* @__PURE__ */ new Map(),
      detachedWave: null,
      detachedWaveFailureCount: 0,
      detachedWaveGaveUpListeners: /* @__PURE__ */ new Set(),
      generation: 0,
      header,
      isFlushingChunkLoadRequests: false,
      loadedChunkCountListeners: /* @__PURE__ */ new Set(),
      metrics,
      owners,
      pendingChunkLoadRequests: [],
      presignedUrl,
      presignedUrlExpiresAt: parsePresignedUrlExpiresAt(presignedUrl),
      presignedUrlRefreshPromise: null,
      processedChunks: /* @__PURE__ */ new Set(),
      sortedChunkOrigins,
      volumeFileID,
      voxelDataLoadPromises: /* @__PURE__ */ new Map()
    };
    if (abortController.signal.aborted || owners.size === 0 || sharedDCCTVolumeLoadPromises.get(volumeFileID)?.owners !== owners) {
      chunkCache.dispose();
      throw createAbortError();
    }
    sharedDCCTVolumeCache.set(volumeFileID, nextCachedVolume);
    return nextCachedVolume;
  })().finally(() => {
    if (sharedDCCTVolumeLoadPromises.get(volumeFileID)?.promise === loadPromise) {
      sharedDCCTVolumeLoadPromises.delete(volumeFileID);
    }
  });
  sharedDCCTVolumeLoadPromises.set(volumeFileID, { abortController, owners, promise: loadPromise });
  return raceWithAbort(loadPromise, signal);
};
const ensureSharedVolumeChunksLoaded = async (cachedVolume, chunkIndices, signal, options) => {
  signal?.throwIfAborted();
  const readID = Symbol();
  cachedVolume.activeReads.set(readID, { indices: new Set(chunkIndices), signal });
  try {
    const uniqueChunkIndices = uniqueOrderedChunkIndices(chunkIndices);
    const missingChunkIndices = uniqueChunkIndices.filter(
      (chunkIndex) => hasMissingChunk(cachedVolume, chunkIndex)
    );
    if (!missingChunkIndices.length) {
      return;
    }
    const pendingPromises = missingChunkIndices.map((chunkIndex) => cachedVolume.voxelDataLoadPromises.get(chunkIndex)).filter((promise) => !!promise);
    const chunkIndicesToEnqueue = missingChunkIndices.filter(
      (chunkIndex) => !cachedVolume.voxelDataLoadPromises.has(chunkIndex)
    );
    const queuePromise = enqueueSharedVolumeChunkLoad(
      cachedVolume,
      chunkIndicesToEnqueue,
      options,
      signal
    );
    await raceWithAbort(
      Promise.all([...pendingPromises, queuePromise]).then(() => void 0),
      signal
    );
  } finally {
    cachedVolume.activeReads.delete(readID);
  }
};
const updateSharedVolumeConsumerDemand = (cachedVolume, consumerId, chunkIndices, options) => {
  const nextChunkIndices = uniqueOrderedChunkIndices(chunkIndices).filter(
    (chunkIndex) => !!cachedVolume.header.chunks[chunkIndex]
  );
  if (!nextChunkIndices.length) {
    cachedVolume.consumerDemands.get(consumerId)?.release();
    cachedVolume.consumerDemands.delete(consumerId);
    updateVisibleChunkGauge(cachedVolume);
    return;
  }
  if (options?.waveSize) {
    cachedVolume.detachedWaveFailureCount = 0;
  }
  const stream = options?.retention === "stream" || options?.priority === "background";
  const previous = cachedVolume.consumerDemands.get(consumerId);
  const release = stream ? () => {
  } : cachedVolume.chunkCache.retain(nextChunkIndices);
  previous?.release();
  cachedVolume.consumerDemands.set(consumerId, {
    chunkIndices: nextChunkIndices,
    maxConcurrentRanges: options?.maxConcurrentRanges,
    priority: options?.priority ?? "interactive",
    pendingChunkIndices: stream ? new Set(nextChunkIndices.filter(
      (index) => !cachedVolume.chunkCache.has(index) && (previous?.pendingChunkIndices ? previous.pendingChunkIndices.has(index) || !previous.chunkIndices.includes(index) : true)
    )) : null,
    release,
    waveSize: options?.waveSize
  });
  updateVisibleChunkGauge(cachedVolume);
  scheduleSharedVolumeChunkLoadFlush(cachedVolume);
};
const releaseSharedVolumeConsumerDemand = (cachedVolume, consumerId) => {
  cachedVolume.consumerDemands.get(consumerId)?.release();
  cachedVolume.consumerDemands.delete(consumerId);
  updateVisibleChunkGauge(cachedVolume);
};
const getSharedDCCTVolumeReader = async (volumeFileID, signal) => {
  const owner = Symbol(volumeFileID);
  const onAbort = () => releaseVolumeOwner(volumeFileID, owner);
  signal?.addEventListener("abort", onAbort, { once: true });
  let cachedVolume;
  try {
    cachedVolume = await loadSharedDCCTVolumeState(volumeFileID, signal, owner);
    if (signal?.aborted) {
      throw createAbortError();
    }
  } catch (error) {
    signal?.removeEventListener("abort", onAbort);
    releaseVolumeOwner(volumeFileID, owner);
    throw error;
  }
  const readerID = ++nextReaderID;
  const consumerIDs = /* @__PURE__ */ new Set();
  const subscriptions = /* @__PURE__ */ new Set();
  const reads = new AbortController();
  let released = false;
  const trackSubscription = (unsubscribe) => {
    if (released) {
      unsubscribe();
      return () => {
      };
    }
    subscriptions.add(unsubscribe);
    return () => {
      subscriptions.delete(unsubscribe);
      unsubscribe();
    };
  };
  const release = () => {
    if (released) {
      return;
    }
    released = true;
    reads.abort();
    signal?.removeEventListener("abort", onAbort);
    signal?.removeEventListener("abort", release);
    subscriptions.forEach((unsubscribe) => unsubscribe());
    subscriptions.clear();
    consumerIDs.forEach((id) => releaseSharedVolumeConsumerDemand(cachedVolume, id));
    consumerIDs.clear();
    releaseVolumeOwner(volumeFileID, owner);
  };
  signal?.removeEventListener("abort", onAbort);
  signal?.addEventListener("abort", release, { once: true });
  const loadChunks = async (chunkIndices, nextSignal, options) => {
    if (released) {
      throw createAbortError();
    }
    const linked = linkAbortSignals(reads.signal, nextSignal);
    try {
      linked.signal.throwIfAborted();
      await ensureSharedVolumeChunksLoaded(cachedVolume, chunkIndices, linked.signal, options);
    } finally {
      linked.release();
    }
  };
  return {
    chunks: cachedVolume.header.chunks,
    geometry: cachedVolume.header.geometry,
    metrics: cachedVolume.metrics,
    volumeFileID: cachedVolume.volumeFileID,
    ensureChunksLoaded: loadChunks,
    findChunkIndexByVoxel: (voxel) => {
      const originX = findChunkOriginAtOrBefore(
        cachedVolume.sortedChunkOrigins.x,
        voxel.x
      );
      const originY = findChunkOriginAtOrBefore(
        cachedVolume.sortedChunkOrigins.y,
        voxel.y
      );
      const originZ = findChunkOriginAtOrBefore(
        cachedVolume.sortedChunkOrigins.z,
        voxel.z
      );
      if (originX === null || originY === null || originZ === null) {
        return null;
      }
      const chunkIndex = cachedVolume.chunkOriginKeyToIndex.get(
        `${originX},${originY},${originZ}`
      );
      if (chunkIndex === void 0) {
        return null;
      }
      const chunk = cachedVolume.header.chunks[chunkIndex];
      if (!chunk) {
        return null;
      }
      if (voxel.x < chunk.origin.x || voxel.y < chunk.origin.y || voxel.z < chunk.origin.z || voxel.x >= chunk.origin.x + chunk.shape.x || voxel.y >= chunk.origin.y + chunk.shape.y || voxel.z >= chunk.origin.z + chunk.shape.z) {
        return null;
      }
      return chunkIndex;
    },
    findChunksIntersectingBox: (box) => cachedVolume.header.chunks.filter(
      (chunk) => boxIntersectsBox(chunkToBox3(chunk, cachedVolume.header.geometry), box)
    ),
    getChunkData: (chunkIndex) => released ? null : cachedVolume.chunkCache.get(chunkIndex),
    getHeader: () => cachedVolume.header,
    getLoadedChunkCount: () => cachedVolume.processedChunks.size,
    getMetrics: () => cachedVolume.metrics.getSnapshot(),
    isChunkLoaded: (chunkIndex) => cachedVolume.chunkCache.has(chunkIndex),
    release,
    releaseConsumerDemand: (consumerId) => {
      const id = `${readerID}:${consumerId}`;
      consumerIDs.delete(id);
      releaseSharedVolumeConsumerDemand(cachedVolume, id);
    },
    retainChunks: (chunkIndices) => released ? () => {
    } : trackSubscription(cachedVolume.chunkCache.retain(chunkIndices)),
    subscribeToPrefetchGaveUp: (listener) => {
      cachedVolume.detachedWaveGaveUpListeners.add(listener);
      return trackSubscription(() => {
        cachedVolume.detachedWaveGaveUpListeners.delete(listener);
      });
    },
    subscribeToChunkLoads: (listener) => {
      cachedVolume.chunkLoadedListeners.add(listener);
      return trackSubscription(() => {
        cachedVolume.chunkLoadedListeners.delete(listener);
      });
    },
    subscribeToLoadedChunkCount: (listener) => {
      cachedVolume.loadedChunkCountListeners.add(listener);
      listener(
        cachedVolume.processedChunks.size,
        cachedVolume.header.chunks.length
      );
      return trackSubscription(() => {
        cachedVolume.loadedChunkCountListeners.delete(listener);
      });
    },
    subscribeToMetrics: (listener) => trackSubscription(cachedVolume.metrics.subscribe(listener)),
    updateConsumerDemand: (consumerId, chunkIndices, options) => {
      if (released) {
        return;
      }
      const id = `${readerID}:${consumerId}`;
      consumerIDs.add(id);
      updateSharedVolumeConsumerDemand(
        cachedVolume,
        id,
        chunkIndices,
        options
      );
    }
  };
};
const disposeSharedDCCTVolume = (volumeFileID) => {
  const cachedVolume = sharedDCCTVolumeCache.get(volumeFileID);
  if (!cachedVolume || cachedVolume.owners.size > 0) {
    return;
  }
  cachedVolume.generation += 1;
  cachedVolume.abortController.abort();
  sharedDCCTVolumeCache.delete(volumeFileID);
  cachedVolume.chunkCache.dispose();
};
const clearSharedDCCTVolumes = () => {
  sharedDCCTVolumeLoadPromises.forEach((load) => load.abortController.abort());
  sharedDCCTVolumeLoadPromises.clear();
  sharedDCCTVolumeCache.forEach((volume) => {
    volume.generation += 1;
    volume.abortController.abort();
    volume.consumerDemands.forEach((demand) => demand.release());
    volume.consumerDemands.clear();
    volume.pendingChunkLoadRequests.forEach((request) => request.reject(createAbortError()));
    volume.pendingChunkLoadRequests = [];
    volume.chunkCache.dispose();
  });
  sharedDCCTVolumeCache.clear();
};
const getSharedDCCTVolumeInfo = (volumeFileID) => {
  const cachedVolume = sharedDCCTVolumeCache.get(volumeFileID);
  if (!cachedVolume) {
    return null;
  }
  const { chunks, geometry } = cachedVolume.header;
  const contentSizeSumBytes = chunks.reduce(
    (total, chunk) => total + chunk.contentSize,
    0
  );
  return {
    activeReaderCount: cachedVolume.owners.size,
    cachedChunkCount: cachedVolume.chunkCache.getLoadedCount(),
    chunkCount: chunks.length,
    chunkShape: chunks[0]?.shape ?? null,
    contentSizeAvgBytes: chunks.length ? contentSizeSumBytes / chunks.length : 0,
    contentSizeMaxBytes: chunks.reduce(
      (max, chunk) => Math.max(max, chunk.contentSize),
      0
    ),
    contentSizeSumBytes,
    decodedBytes: cachedVolume.chunkCache.getBytes(),
    geometry
  };
};
const getSharedDCCTVolumeMetrics = (volumeFileID) => sharedDCCTVolumeCache.get(volumeFileID)?.metrics.getSnapshot() ?? null;

const ORTHOGONAL_PANE_AXES = {
  axial: {
    horizontalAxis: "x",
    sliceAxis: "z",
    verticalAxis: "y",
    verticalSign: -1
  },
  coronal: {
    horizontalAxis: "x",
    sliceAxis: "y",
    verticalAxis: "z",
    verticalSign: 1
  },
  sagittal: {
    horizontalAxis: "y",
    sliceAxis: "x",
    verticalAxis: "z",
    verticalSign: 1
  }
};
const paneRowForVoxel = ({
  axes,
  heightPx,
  voxelIndex
}) => axes.verticalSign === 1 ? heightPx - 1 - voxelIndex : voxelIndex;
const paneFractionForVoxel = ({
  axes,
  extentVoxels,
  voxelIndex
}) => {
  if (!(extentVoxels > 0)) {
    return 0.5;
  }
  const fraction = voxelIndex / extentVoxels;
  return axes.verticalSign === 1 ? 1 - fraction : fraction;
};
const voxelForPaneFraction = ({
  axes,
  extentVoxels,
  fraction
}) => (axes.verticalSign === 1 ? 1 - fraction : fraction) * Math.max(0, extentVoxels);

const now$2 = () => typeof performance !== "undefined" ? performance.now() : Date.now();
const clamp$5 = (value, min, max) => Math.min(max, Math.max(min, value));
const getProjectionShape = (geometry, projection) => {
  switch (projection) {
    case "axial":
      return {
        aspectRatio: geometry.shape.x * geometry.spacing.x / Math.max(geometry.shape.y * geometry.spacing.y, 1e-6),
        height: geometry.shape.y,
        maxSliceIndex: Math.max(0, geometry.shape.z - 1),
        width: geometry.shape.x
      };
    case "coronal":
      return {
        aspectRatio: geometry.shape.x * geometry.spacing.x / Math.max(geometry.shape.z * geometry.spacing.z, 1e-6),
        height: geometry.shape.z,
        maxSliceIndex: Math.max(0, geometry.shape.y - 1),
        width: geometry.shape.x
      };
    case "sagittal":
      return {
        aspectRatio: geometry.shape.y * geometry.spacing.y / Math.max(geometry.shape.z * geometry.spacing.z, 1e-6),
        height: geometry.shape.z,
        maxSliceIndex: Math.max(0, geometry.shape.x - 1),
        width: geometry.shape.y
      };
  }
};
const sortChunkIndicesForSlice = (chunks, chunkIndices, sliceAxis, sliceIndex) => [...chunkIndices].sort((leftIndex, rightIndex) => {
  const leftChunk = chunks[leftIndex];
  const rightChunk = chunks[rightIndex];
  const leftCenter = leftChunk.origin[sliceAxis] + leftChunk.shape[sliceAxis] / 2;
  const rightCenter = rightChunk.origin[sliceAxis] + rightChunk.shape[sliceAxis] / 2;
  const leftDistance = Math.abs(leftCenter - sliceIndex);
  const rightDistance = Math.abs(rightCenter - sliceIndex);
  if (leftDistance !== rightDistance) {
    return leftDistance - rightDistance;
  }
  return leftChunk.index - rightChunk.index;
});
const applyWindow = (value, colorProfile) => {
  const windowWidth = Math.max(1, colorProfile.ww);
  const windowMinimum = colorProfile.wc - windowWidth / 2;
  const normalized = clamp$5((value - windowMinimum) / windowWidth, 0, 1);
  return Math.round(normalized * 255);
};
const writeVoxel = (voxelValues, mask, width, x, y, value) => {
  const pixelIndex = y * width + x;
  voxelValues[pixelIndex] = value;
  mask[pixelIndex] = 1;
};
const renderAxialSlice = (voxelValues, mask, width, height, chunk, data, sliceIndex) => {
  const localZ = sliceIndex - chunk.origin.z;
  const shapeX = chunk.shape.x;
  const shapeY = chunk.shape.y;
  const shapeXY = shapeX * shapeY;
  const zOffset = localZ * shapeXY;
  const axes = ORTHOGONAL_PANE_AXES.axial;
  for (let localY = 0; localY < shapeY; localY += 1) {
    const voxelY = chunk.origin.y + localY;
    const outputY = paneRowForVoxel({ axes, heightPx: height, voxelIndex: voxelY });
    const rowOffset = zOffset + localY * shapeX;
    for (let localX = 0; localX < shapeX; localX += 1) {
      const voxelX = chunk.origin.x + localX;
      writeVoxel(
        voxelValues,
        mask,
        width,
        voxelX,
        outputY,
        data[rowOffset + localX]
      );
    }
  }
};
const renderCoronalSlice = (voxelValues, mask, width, height, chunk, data, sliceIndex) => {
  const localY = sliceIndex - chunk.origin.y;
  const shapeX = chunk.shape.x;
  const shapeXY = shapeX * chunk.shape.y;
  const axes = ORTHOGONAL_PANE_AXES.coronal;
  for (let localZ = 0; localZ < chunk.shape.z; localZ += 1) {
    const voxelZ = chunk.origin.z + localZ;
    const outputY = paneRowForVoxel({ axes, heightPx: height, voxelIndex: voxelZ });
    const zOffset = localZ * shapeXY + localY * shapeX;
    for (let localX = 0; localX < shapeX; localX += 1) {
      const voxelX = chunk.origin.x + localX;
      writeVoxel(
        voxelValues,
        mask,
        width,
        voxelX,
        outputY,
        data[zOffset + localX]
      );
    }
  }
};
const renderSagittalSlice = (voxelValues, mask, width, height, chunk, data, sliceIndex) => {
  const localX = sliceIndex - chunk.origin.x;
  const shapeX = chunk.shape.x;
  const shapeY = chunk.shape.y;
  const shapeXY = shapeX * shapeY;
  const axes = ORTHOGONAL_PANE_AXES.sagittal;
  for (let localZ = 0; localZ < chunk.shape.z; localZ += 1) {
    const voxelZ = chunk.origin.z + localZ;
    const outputY = paneRowForVoxel({ axes, heightPx: height, voxelIndex: voxelZ });
    const zOffset = localZ * shapeXY;
    for (let localY = 0; localY < shapeY; localY += 1) {
      const voxelY = chunk.origin.y + localY;
      writeVoxel(
        voxelValues,
        mask,
        width,
        voxelY,
        outputY,
        data[zOffset + localY * shapeX + localX]
      );
    }
  }
};
const convertToPixels = (voxelValues, mask, colorProfile) => {
  const pixels = new Uint8ClampedArray(voxelValues.length * 4);
  for (let index = 0; index < voxelValues.length; index += 1) {
    const outputIndex = index * 4;
    if (!mask[index]) {
      pixels[outputIndex + 3] = 255;
      continue;
    }
    const grayscale = applyWindow(voxelValues[index], colorProfile);
    pixels[outputIndex] = grayscale;
    pixels[outputIndex + 1] = grayscale;
    pixels[outputIndex + 2] = grayscale;
    pixels[outputIndex + 3] = 255;
  }
  return pixels;
};
const getLoadedChunkSources = (reader, chunkIndices) => chunkIndices.reduce((sources, chunkIndex) => {
  const chunk = reader.chunks[chunkIndex];
  const data = reader.getChunkData(chunkIndex);
  if (!chunk || !data) {
    return sources;
  }
  return sources.concat({ chunk, data });
}, []);
const getOrthogonalProjectionShape = getProjectionShape;
const getOrthogonalSliceChunkIndices = (reader, projection, sliceIndex) => {
  const geometry = reader.geometry;
  const { maxSliceIndex } = getProjectionShape(geometry, projection);
  const normalizedSliceIndex = clamp$5(sliceIndex, 0, maxSliceIndex);
  const sliceAxis = projection === "axial" ? "z" : projection === "coronal" ? "y" : "x";
  const chunkIndices = reader.chunks.filter((chunk) => {
    const chunkStart = chunk.origin[sliceAxis];
    const chunkEndExclusive = chunkStart + chunk.shape[sliceAxis];
    return normalizedSliceIndex >= chunkStart && normalizedSliceIndex < chunkEndExclusive;
  }).map((chunk) => chunk.index);
  return sortChunkIndicesForSlice(
    reader.chunks,
    chunkIndices,
    sliceAxis,
    normalizedSliceIndex
  );
};
const renderOrthogonalSlice = async (reader, projection, sliceIndex, colorProfile, loadOptions, mode = "loaded-only", signal) => {
  const frameStartedAt = now$2();
  const shape = getProjectionShape(reader.geometry, projection);
  const normalizedSliceIndex = clamp$5(sliceIndex, 0, shape.maxSliceIndex);
  const chunkSelectionStartedAt = now$2();
  const chunkIndices = getOrthogonalSliceChunkIndices(
    reader,
    projection,
    normalizedSliceIndex
  );
  reader.metrics.recordSample(
    "sliceChunkSelectionMs",
    now$2() - chunkSelectionStartedAt
  );
  const releaseChunks = reader.retainChunks(chunkIndices);
  try {
    if (mode === "ensure-all" && chunkIndices.length > 0) {
      await reader.ensureChunksLoaded(chunkIndices, signal, loadOptions);
    }
    const loadedChunkSources = getLoadedChunkSources(reader, chunkIndices);
    const voxelValues = new Int16Array(shape.width * shape.height);
    const mask = new Uint8Array(shape.width * shape.height);
    const assembleStartedAt = now$2();
    loadedChunkSources.forEach(({ chunk, data }) => {
      if (projection === "axial") {
        renderAxialSlice(
          voxelValues,
          mask,
          shape.width,
          shape.height,
          chunk,
          data,
          normalizedSliceIndex
        );
        return;
      }
      if (projection === "coronal") {
        renderCoronalSlice(
          voxelValues,
          mask,
          shape.width,
          shape.height,
          chunk,
          data,
          normalizedSliceIndex
        );
        return;
      }
      renderSagittalSlice(
        voxelValues,
        mask,
        shape.width,
        shape.height,
        chunk,
        data,
        normalizedSliceIndex
      );
    });
    reader.metrics.recordSample("sliceAssembleMs", now$2() - assembleStartedAt);
    const windowingStartedAt = now$2();
    const pixels = convertToPixels(voxelValues, mask, colorProfile);
    reader.metrics.recordSample("windowingMs", now$2() - windowingStartedAt);
    const imageData = new ImageData(pixels, shape.width, shape.height);
    reader.metrics.recordSample("frameRenderMs", now$2() - frameStartedAt);
    return {
      aspectRatio: shape.aspectRatio,
      bitmap: null,
      chunkIndices,
      height: shape.height,
      imageData,
      maxSliceIndex: shape.maxSliceIndex,
      missingChunkCount: Math.max(0, chunkIndices.length - loadedChunkSources.length),
      renderedChunkCount: loadedChunkSources.length,
      width: shape.width
    };
  } finally {
    releaseChunks();
  }
};

const NEUTRAL_DISPLAY_ADJUST = {
  brightness: 0,
  contrast: 0
};
const ADJUST_LIMIT = 1;
const CONTRAST_SCALE_MIN = 1e-3;
const CONTRAST_SCALE_MAX = 1e3;
const clamp$4 = (value, min, max) => Math.min(Math.max(value, min), max);
const isFinitePositive$4 = (value) => Number.isFinite(value) && value > 0;
const isNeutralDisplayAdjust = (adjust) => adjust.brightness === 0 && adjust.contrast === 0;
const contrastScale = (contrast) => {
  const bounded = clamp$4(contrast, -ADJUST_LIMIT, ADJUST_LIMIT);
  const scale = bounded > 0 ? 1 / (1 - bounded) : 1 + bounded;
  if (!Number.isFinite(scale)) {
    return CONTRAST_SCALE_MAX;
  }
  return clamp$4(scale, CONTRAST_SCALE_MIN, CONTRAST_SCALE_MAX);
};
const applyDisplayAdjust = ({
  adjust,
  minimumWindowWidth,
  window
}) => {
  if (isNeutralDisplayAdjust(adjust)) {
    return window;
  }
  const brightness = clamp$4(adjust.brightness, -ADJUST_LIMIT, ADJUST_LIMIT);
  return {
    wc: window.wc - window.ww * brightness,
    ww: Math.max(minimumWindowWidth, window.ww / contrastScale(adjust.contrast))
  };
};
const accumulateDisplayAdjust = ({
  adjust,
  deltaXPx,
  deltaYPx,
  paneHeightPx,
  paneWidthPx
}) => {
  const contrastStep = isFinitePositive$4(paneWidthPx) ? deltaXPx / paneWidthPx : 0;
  const brightnessStep = isFinitePositive$4(paneHeightPx) ? -deltaYPx / paneHeightPx : 0;
  return {
    brightness: clamp$4(
      adjust.brightness + brightnessStep,
      -ADJUST_LIMIT,
      ADJUST_LIMIT
    ),
    contrast: clamp$4(adjust.contrast + contrastStep, -ADJUST_LIMIT, ADJUST_LIMIT)
  };
};

var vs = "#version 300 es\nprecision highp float;\n#define GLSLIFY 1\nuniform mat4 modelMatrix;uniform mat4 projectionMatrix;uniform mat4 viewMatrix;uniform mat4 xyztoijk;in vec4 position;out vec3 ijk;void main(){vec4 xyz=modelMatrix*position;ijk=(xyztoijk*xyz).xyz;gl_Position=projectionMatrix*viewMatrix*xyz;}"; // eslint-disable-line

var fs = "#version 300 es\nprecision highp float;\n#define GLSLIFY 1\n#ifdef SLICE_INTEGER\n#ifdef SLICE_BLOCK_COUNT\nuniform highp isampler3D volumeBlocks[SLICE_BLOCK_COUNT];uniform ivec3 volumeShape;uniform ivec3 volumeBlockShape;uniform ivec3 volumeBlockGrid;float readVolumeVoxel(ivec3 index);\n#else\nuniform highp isampler3D volume;float readVolumeVoxel(ivec3 index){return float(texelFetch(volume,index,0).r);}\n#endif\nuniform float emptyHuSentinel;\n#else\nuniform highp sampler3D volume;\n#endif\nuniform float ww;uniform float wmin;uniform float contrast;uniform float brightness;\n#ifdef SLICE_SLAB\nuniform int slabSampleCount;uniform vec3 slabStartIjk;uniform vec3 slabStepIjk;uniform int slabMode;\n#endif\nin vec3 ijk;layout(location=0)out vec4 fragColor;bool sampleVolume(vec3 coordinate,out float value){\n#ifdef SLICE_INTEGER\n#ifdef SLICE_BLOCK_COUNT\nivec3 dimensions=volumeShape;\n#else\nivec3 dimensions=textureSize(volume,0);\n#endif\nvec3 voxel=coordinate*vec3(dimensions)-0.5;ivec3 base=ivec3(floor(voxel));vec3 fraction=fract(voxel);ivec3 low=clamp(base,ivec3(0),dimensions-1);ivec3 high=clamp(base+1,ivec3(0),dimensions-1);float samples[8];for(int corner=0;corner<8;++corner){bvec3 upper=bvec3((corner&1)!=0,(corner&2)!=0,(corner&4)!=0);ivec3 index=ivec3(upper.x ? high.x : low.x,upper.y ? high.y : low.y,upper.z ? high.z : low.z);vec3 weight=vec3(upper.x ? fraction.x : 1.0-fraction.x,upper.y ? fraction.y : 1.0-fraction.y,upper.z ? fraction.z : 1.0-fraction.z);samples[corner]=readVolumeVoxel(index);if(samples[corner]==emptyHuSentinel&&weight.x*weight.y*weight.z>0.0){return false;}}float bottom=mix(mix(samples[0],samples[1],fraction.x),mix(samples[2],samples[3],fraction.x),fraction.y);float top=mix(mix(samples[4],samples[5],fraction.x),mix(samples[6],samples[7],fraction.x),fraction.y);value=mix(bottom,top,fraction.z);\n#else\nvalue=texture(volume,coordinate).r;\n#endif\nreturn true;}void main(){\n#ifdef SLICE_SLAB\nfloat accumulated=0.0;int validSampleCount=0;for(int sampleIndex=0;sampleIndex<slabSampleCount;++sampleIndex){vec3 sampleIjk=ijk+slabStartIjk+slabStepIjk*float(sampleIndex);vec3 inBounds=step(vec3(0.0),sampleIjk)*step(sampleIjk,vec3(1.0));if(inBounds.x*inBounds.y*inBounds.z<1.0){continue;}float sampled;if(!sampleVolume(sampleIjk,sampled)){continue;}validSampleCount+=1;if(slabMode==1){accumulated=validSampleCount==1 ? sampled : max(accumulated,sampled);}else{accumulated+=sampled;}}if(validSampleCount==0){discard;}float value=slabMode==1 ? accumulated : accumulated/float(validSampleCount);\n#else\nvec3 inBounds=step(vec3(0.0),ijk)*step(ijk,vec3(1.0));if(inBounds.x*inBounds.y*inBounds.z<1.0){discard;}float value;if(!sampleVolume(ijk,value)){discard;}\n#endif\nfloat v=(value-wmin)/ww;fragColor=vec4(v,v,v,1.0);fragColor.rgb+=brightness;if(contrast>0.0){fragColor.rgb=(fragColor.rgb-0.5)/(1.0-contrast)+0.5;}else{fragColor.rgb=(fragColor.rgb-0.5)*(1.0+contrast)+0.5;}}"; // eslint-disable-line

const VERSION_DIRECTIVE = "#version 300 es";
const withSliceDefines = (source, defines, blockCount = 0) => {
  if (!defines.length && !blockCount) return source;
  if (!source.startsWith(VERSION_DIRECTIVE)) throw new Error("SliceMaterial: shader must start with " + VERSION_DIRECTIVE);
  const body = source.slice(VERSION_DIRECTIVE.length).replace(/^\n/, "");
  const blockSampler = blockCount ? `
float readVolumeVoxel(ivec3 index) {
    ivec3 block = index / volumeBlockShape;
    int blockIndex = block.x + volumeBlockGrid.x * (block.y + volumeBlockGrid.y * block.z);
    ivec3 local = index - block * volumeBlockShape;
    ${Array.from(
    { length: blockCount },
    (_, index) => `if (blockIndex == ${index}) return float(texelFetch(volumeBlocks[${index}], local, 0).r);`
  ).join("\n    ")}
    return emptyHuSentinel;
}` : "";
  return [
    VERSION_DIRECTIVE,
    ...defines.map((define) => `#define ${define} 1`),
    ...blockCount ? [`#define SLICE_BLOCK_COUNT ${blockCount}`] : [],
    body,
    blockSampler
  ].join("\n");
};
class SliceMaterial extends RawShaderMaterial {
  constructor(data3DTexture, box, ww = 3200, wl = 1e3, { slab = false } = {}) {
    const size = box.getSize(new Vector3());
    const scale = new Vector3(1, 1, 1).divide(size);
    const translate = box.min.clone().negate().divide(size);
    const xyz_to_ijk = new Matrix4().makeScale(scale.x, scale.y, scale.z).setPosition(translate);
    super({
      side: DoubleSide,
      vertexShader: vs,
      fragmentShader: withSliceDefines(fs, [
        ...slab ? ["SLICE_SLAB"] : [],
        ...data3DTexture.format === RedIntegerFormat ? ["SLICE_INTEGER"] : []
      ]),
      uniforms: {
        volume: { value: data3DTexture },
        volumeBlocks: { value: [] },
        volumeBlockGrid: { value: new Vector3() },
        volumeBlockShape: { value: new Vector3() },
        volumeShape: { value: new Vector3() },
        xyztoijk: { value: xyz_to_ijk },
        ww: { value: ww },
        wmin: { value: wl - ww / 2 },
        contrast: { value: 0 },
        brightness: { value: 0 },
        emptyHuSentinel: { value: EMPTY_HU_SENTINEL },
        ...slab ? {
          slabSampleCount: { value: 1 },
          slabStartIjk: { value: new Vector3() },
          slabStepIjk: { value: new Vector3() },
          slabMode: { value: 0 }
        } : {}
      }
    });
    this.users = [];
    this.hasSlab = slab;
  }
  getVolumeBlocks() {
    return this.volumeBlocks;
  }
  setVolume(texture, blocks) {
    const previous = this.uniforms.volume.value;
    if (previous.format === RedIntegerFormat !== (texture.format === RedIntegerFormat) || this.volumeBlocks?.textures.length !== blocks?.textures.length) {
      this.fragmentShader = withSliceDefines(fs, [
        ...this.hasSlab ? ["SLICE_SLAB"] : [],
        ...texture.format === RedIntegerFormat ? ["SLICE_INTEGER"] : []
      ], blocks?.textures.length);
      this.needsUpdate = true;
    }
    this.uniforms.volume.value = texture;
    this.volumeBlocks = blocks;
    this.uniforms.volumeBlocks.value = blocks?.textures ?? [];
    if (blocks) {
      this.uniforms.volumeBlockGrid.value.copy(blocks.grid);
      this.uniforms.volumeBlockShape.value.copy(blocks.blockShape);
      this.uniforms.volumeShape.value.copy(blocks.shape);
    }
  }
  // Tooth mode walks a slab of samples along the plane normal; every other
  // consumer keeps the single-sample program, which has no slab uniforms at all.
  setSlab(slab) {
    if (!this.hasSlab) {
      console.error("SliceMaterial.setSlab: this material was compiled without slab support");
      return;
    }
    this.uniforms.slabSampleCount.value = slab.slabSampleCount;
    this.uniforms.slabMode.value = slab.slabMode;
    this.uniforms.slabStartIjk.value.set(slab.slabStartIjk.x, slab.slabStartIjk.y, slab.slabStartIjk.z);
    this.uniforms.slabStepIjk.value.set(slab.slabStepIjk.x, slab.slabStepIjk.y, slab.slabStepIjk.z);
  }
  resetSlab() {
    if (!this.hasSlab) return;
    this.uniforms.slabSampleCount.value = 1;
    this.uniforms.slabMode.value = 0;
    this.uniforms.slabStartIjk.value.set(0, 0, 0);
    this.uniforms.slabStepIjk.value.set(0, 0, 0);
  }
  increaseUsers(user) {
    if (!this.users.includes(user)) this.users.push(user);
    else console.warn(this.constructor.name + ".increaseUsers: user is already added = ", user, this);
  }
  decreaseUsers(user) {
    const userIndex = this.users.indexOf(user);
    if (userIndex !== -1) this.users.splice(userIndex, 1);
    else console.warn(this.constructor.name + ".decreaseUsers: no user = ", user, this);
  }
  destroy(user) {
    this.decreaseUsers(user);
    if (this.users.length === 0) {
      this.dispose();
    }
  }
}

const VOLUME_TEXTURE_LIMIT_BYTES = 1024 ** 3;
const voxelCount$1 = ({ x, y, z }) => x * y * z;
const getVolumeTextureLayout = ({
  maxTextureBytes,
  maxTextureCount,
  maxTextureSize,
  shape
}) => {
  if (maxTextureSize < 1 || maxTextureBytes < Int16Array.BYTES_PER_ELEMENT) {
    return null;
  }
  const fitGrid = (grid) => {
    if (voxelCount$1(grid) > maxTextureCount) {
      return null;
    }
    const blockShape = {
      x: Math.ceil(shape.x / grid.x),
      y: Math.ceil(shape.y / grid.y),
      z: Math.ceil(shape.z / grid.z)
    };
    if (voxelCount$1(blockShape) * Int16Array.BYTES_PER_ELEMENT > maxTextureBytes) {
      const axes = ["x", "y", "z"];
      const axis = axes.reduce(
        (longest, candidate) => blockShape[candidate] > blockShape[longest] ? candidate : longest
      );
      return fitGrid({ ...grid, [axis]: Math.min(shape[axis], grid[axis] * 2) });
    }
    const fittedGrid = {
      x: Math.ceil(shape.x / blockShape.x),
      y: Math.ceil(shape.y / blockShape.y),
      z: Math.ceil(shape.z / blockShape.z)
    };
    const blocks = Array.from({ length: voxelCount$1(fittedGrid) }, (_, index) => {
      const origin = {
        x: index % fittedGrid.x * blockShape.x,
        y: Math.floor(index / fittedGrid.x) % fittedGrid.y * blockShape.y,
        z: Math.floor(index / (fittedGrid.x * fittedGrid.y)) * blockShape.z
      };
      return {
        origin,
        shape: {
          x: Math.min(blockShape.x, shape.x - origin.x),
          y: Math.min(blockShape.y, shape.y - origin.y),
          z: Math.min(blockShape.z, shape.z - origin.z)
        }
      };
    });
    return { blockShape, blocks, grid: fittedGrid };
  };
  return fitGrid({
    x: Math.ceil(shape.x / maxTextureSize),
    y: Math.ceil(shape.y / maxTextureSize),
    z: Math.ceil(shape.z / maxTextureSize)
  });
};

const allocateTextures = ({
  allowOutOfMemorySplit,
  maxTextureBytes,
  maxTextureCount,
  maxTextureSize,
  renderer,
  shape
}) => {
  const layout = getVolumeTextureLayout({ maxTextureBytes, maxTextureCount, maxTextureSize, shape });
  if (!layout) {
    throw new VolumeGpuError(null, "Full-resolution volume exceeds GPU texture limits");
  }
  const textures = [];
  try {
    layout.blocks.forEach((block) => {
      textures.push(new MonolithTexture(renderer, block.shape, block.origin));
    });
    return { layout, textures };
  } catch (error) {
    textures.forEach((texture) => texture.dispose());
    const gl = renderer.getContext();
    if (!(error instanceof VolumeGpuError) || gl.isContextLost() || error.code === gl.OUT_OF_MEMORY && !allowOutOfMemorySplit) {
      throw error;
    }
    const { x, y, z } = layout.blockShape;
    const smallerTextureBytes = Math.floor(x * y * z * Int16Array.BYTES_PER_ELEMENT / 2);
    if (!getVolumeTextureLayout({
      maxTextureBytes: smallerTextureBytes,
      maxTextureCount,
      maxTextureSize,
      shape
    })) {
      throw error;
    }
    return allocateTextures({
      allowOutOfMemorySplit,
      maxTextureBytes: smallerTextureBytes,
      maxTextureCount,
      maxTextureSize,
      renderer,
      shape
    });
  }
};
class GpuVolumeTexture {
  constructor(renderer, shape, { allowOutOfMemorySplit = true } = {}) {
    this.normalization = null;
    const { layout, textures } = allocateTextures({
      allowOutOfMemorySplit,
      maxTextureBytes: VOLUME_TEXTURE_LIMIT_BYTES,
      maxTextureCount: renderer.capabilities.maxTextures,
      maxTextureSize: getMax3DTextureSize(renderer),
      renderer,
      shape
    });
    this.textures = textures;
    this.texture = textures[0].texture;
    this.byteLength = shape.x * shape.y * shape.z * Int16Array.BYTES_PER_ELEMENT;
    if (textures.length > 1) {
      this.blocks = {
        blockShape: layout.blockShape,
        grid: layout.grid,
        shape,
        textures: textures.map((texture) => texture.texture)
      };
    }
  }
  uploadChunks(chunks) {
    const uploadMs = this.textures.reduce(
      (duration, texture) => duration + texture.uploadChunks(chunks).uploadMs,
      0
    );
    return {
      didUpload: chunks.length > 0,
      uploadMs,
      uploadedChunkCount: chunks.length
    };
  }
  isAvailable() {
    return this.textures.every((texture) => texture.isAvailable());
  }
  dispose() {
    this.textures.forEach((texture) => texture.dispose());
  }
}

const buildProjectionMatrix = (projection, sliceWorld, boxMin, boxSize, worldHeight) => {
  const ax = 1 / boxSize.x;
  const ay = 1 / boxSize.y;
  const az = 1 / boxSize.z;
  const matrix = new Matrix4();
  const { verticalSign } = ORTHOGONAL_PANE_AXES[projection];
  const verticalScale = (axisScale) => verticalSign === 1 ? -axisScale : axisScale;
  const verticalOffset = (axisScale, boxMinComponent) => verticalSign === 1 ? (worldHeight - boxMinComponent) * axisScale : -boxMinComponent * axisScale;
  if (projection === "axial") {
    matrix.set(
      ax,
      0,
      0,
      -boxMin.x * ax,
      0,
      verticalScale(ay),
      0,
      verticalOffset(ay, boxMin.y),
      0,
      0,
      0,
      (sliceWorld - boxMin.z) * az,
      0,
      0,
      0,
      1
    );
    return matrix;
  }
  if (projection === "coronal") {
    matrix.set(
      ax,
      0,
      0,
      -boxMin.x * ax,
      0,
      0,
      0,
      (sliceWorld - boxMin.y) * ay,
      0,
      verticalScale(az),
      0,
      verticalOffset(az, boxMin.z),
      0,
      0,
      0,
      1
    );
    return matrix;
  }
  matrix.set(
    0,
    0,
    0,
    (sliceWorld - boxMin.x) * ax,
    ay,
    0,
    0,
    -boxMin.y * ay,
    0,
    verticalScale(az),
    0,
    verticalOffset(az, boxMin.z),
    0,
    0,
    0,
    1
  );
  return matrix;
};

let sharedRenderer = null;
let sharedSliceScene = null;
const createSliceScene = () => {
  const placeholderTexture = new Data3DTexture(new Int16Array(1), 1, 1, 1);
  placeholderTexture.format = RedIntegerFormat;
  placeholderTexture.type = ShortType;
  const geometry = new PlaneGeometry(1, 1);
  const material = new SliceMaterial(
    placeholderTexture,
    new Box3(new Vector3(), new Vector3(1, 1, 1)),
    1,
    0,
    { slab: true }
  );
  const mesh = new Mesh(geometry, material);
  const scene = new Scene();
  scene.add(mesh);
  return {
    camera: new OrthographicCamera(0, 1, 0, 1, -1, 1),
    geometry,
    material,
    mesh,
    placeholderTexture,
    scene
  };
};
const getFullHeadMPRSliceScene = () => {
  sharedSliceScene ?? (sharedSliceScene = createSliceScene());
  return sharedSliceScene;
};
const clearFullHeadMPRSliceScene = () => {
  if (!sharedSliceScene) {
    return;
  }
  sharedSliceScene.geometry.dispose();
  sharedSliceScene.material.dispose();
  sharedSliceScene.placeholderTexture.dispose();
  sharedSliceScene = null;
};
const getFullHeadMPRWebGLRenderer = () => {
  if (!sharedRenderer) {
    try {
      sharedRenderer = new WebGLRenderer({
        antialias: false,
        preserveDrawingBuffer: true
      });
    } catch {
      throw new VolumeGpuError(
        null,
        "Full-resolution GPU renderer is unavailable"
      );
    }
    sharedRenderer.domElement.addEventListener(
      "webglcontextlost",
      clearFullHeadMPRSliceScene
    );
    sharedRenderer.setPixelRatio(window.devicePixelRatio);
  }
  return sharedRenderer;
};
const clamp$3 = (value, min, max) => Math.min(max, Math.max(min, value));
class FullHeadMPRGpuRenderer {
  constructor(renderer, metrics) {
    this.renderer = renderer;
    this.metrics = metrics;
    this.monolithIncremental = null;
    this.uploadedChunkIndices = /* @__PURE__ */ new Set();
    this.chunkSubscriptions = /* @__PURE__ */ new Map();
    this.uploadError = null;
    this.pendingUploadMs = 0;
    this.pendingUploadCount = 0;
    this.lastRepack = {
      didRepack: false,
      repackMs: 0
    };
  }
  getLastRepack() {
    return this.lastRepack;
  }
  getMonolithBytes() {
    return this.monolithIncremental?.byteLength ?? 0;
  }
  hasUploadedChunk(chunkIndex) {
    return this.uploadedChunkIndices.has(chunkIndex);
  }
  canReuse() {
    return !this.uploadError && !!this.monolithIncremental?.isAvailable();
  }
  suspend() {
    this.chunkSubscriptions.forEach((unsubscribe) => unsubscribe());
    this.chunkSubscriptions.clear();
  }
  detachReader(reader) {
    this.chunkSubscriptions.get(reader)?.();
    this.chunkSubscriptions.delete(reader);
  }
  // Whole-volume textures are shared by oblique and orthogonal panes. Ownership
  // of every block stays here.
  prepareVolumeTexture(reader, options = {}) {
    return this.prepareMonolith(reader, options);
  }
  async renderSlice(reader, projection, sliceIndex, colorProfile, loadOptions, mode = "loaded-only", signal) {
    this.lastRepack = { didRepack: false, repackMs: 0 };
    const shape = getOrthogonalProjectionShape(reader.geometry, projection);
    const normalizedSliceIndex = clamp$3(sliceIndex, 0, shape.maxSliceIndex);
    const chunkIndices = getOrthogonalSliceChunkIndices(
      reader,
      projection,
      normalizedSliceIndex
    );
    this.prepareMonolith(reader);
    if (mode === "ensure-all" && chunkIndices.length > 0) {
      const missingIndices = chunkIndices.filter(
        (index) => !this.hasUploadedChunk(index)
      );
      await reader.ensureChunksLoaded(missingIndices, signal, loadOptions);
    }
    const prepared = this.prepareMonolith(reader);
    const renderedChunkCount = chunkIndices.reduce(
      (count, chunkIndex) => this.uploadedChunkIndices.has(chunkIndex) ? count + 1 : count,
      0
    );
    const baseResult = {
      aspectRatio: shape.aspectRatio,
      chunkIndices,
      height: shape.height,
      maxSliceIndex: shape.maxSliceIndex,
      missingChunkCount: Math.max(0, chunkIndices.length - renderedChunkCount),
      renderedChunkCount,
      width: shape.width
    };
    if (!prepared || signal?.aborted) {
      return {
        ...baseResult,
        texture: null,
        matrix: null,
        window: colorProfile,
        minimumWindowWidth: 1
      };
    }
    const window2 = adaptWindowForNormalization(
      colorProfile,
      prepared.normalization
    );
    const matrix = buildProjectionMatrix(
      projection,
      normalizedSliceIndex + 0.5,
      prepared.boxMin,
      prepared.boxSize,
      shape.height
    );
    return {
      ...baseResult,
      texture: prepared.texture,
      textureBlocks: prepared.blocks,
      matrix,
      window: window2,
      minimumWindowWidth: prepared.normalization ? Number.EPSILON : 1
    };
  }
  dispose() {
    this.disposeMonolith();
  }
  prepareMonolith(reader, options = {}) {
    if (this.uploadError) {
      throw this.uploadError;
    }
    if (!this.monolithIncremental) {
      const texture = new GpuVolumeTexture(
        this.renderer,
        reader.geometry.shape,
        options
      );
      this.monolithIncremental = texture;
    }
    if (!this.chunkSubscriptions.has(reader)) {
      const texture = this.monolithIncremental;
      this.metrics = reader.metrics;
      const stageChunk = (chunkIndex) => {
        if (this.uploadError || this.uploadedChunkIndices.has(chunkIndex)) {
          return;
        }
        const chunk = reader.chunks[chunkIndex];
        const data = reader.getChunkData(chunkIndex);
        if (chunk && data) {
          try {
            const result = texture.uploadChunks([
              { data, origin: chunk.origin, shape: chunk.shape }
            ]);
            this.pendingUploadMs += result.uploadMs;
            this.pendingUploadCount += result.uploadedChunkCount;
            this.uploadedChunkIndices.add(chunkIndex);
          } catch (error) {
            this.uploadError = error;
          }
        }
      };
      this.chunkSubscriptions.set(
        reader,
        reader.subscribeToChunkLoads(stageChunk)
      );
      reader.chunks.forEach((chunk) => stageChunk(chunk.index));
    }
    const incremental = this.monolithIncremental;
    if (this.uploadError) {
      throw this.uploadError;
    }
    this.lastRepack = {
      didRepack: this.pendingUploadCount > 0,
      repackMs: this.pendingUploadMs
    };
    if (this.pendingUploadCount > 0) {
      this.metrics.recordSample("monolithUploadMs", this.pendingUploadMs);
    }
    this.pendingUploadMs = 0;
    this.pendingUploadCount = 0;
    return {
      blocks: incremental.blocks,
      boxMin: { x: 0, y: 0, z: 0 },
      boxSize: reader.geometry.shape,
      normalization: incremental.normalization,
      texture: incremental.texture
    };
  }
  disposeMonolith() {
    this.suspend();
    this.monolithIncremental?.dispose();
    this.monolithIncremental = null;
    this.uploadedChunkIndices.clear();
    this.uploadError = null;
    this.pendingUploadMs = 0;
    this.pendingUploadCount = 0;
  }
}

const GPU_VOLUME_CACHE_LIMIT_BYTES = Math.floor(1.6 * 1024 ** 3);
const GPU_VOLUME_CACHE_LIMIT = 2;
const CONSUMER_ID = "full-head-mpr-gpu-volume";
const volumes = /* @__PURE__ */ new Map();
let selectedVolumeFileID = null;
const removeVolume = (volumeFileID, volume, reason) => {
  if (volumes.get(volumeFileID) === volume) {
    volumes.delete(volumeFileID);
  }
  volume.abortController.abort(reason);
  volume.readers.forEach(
    (_, reader) => reader.releaseConsumerDemand(CONSUMER_ID)
  );
  volume.renderer.dispose();
};
const clearInactiveFullHeadMPRGpuVolumes = () => {
  Array.from(volumes).forEach(([volumeFileID, volume]) => {
    if (!volume.readers.size) {
      removeVolume(volumeFileID, volume);
    }
  });
};
const trimVolumes = (incomingBytes = 0, incomingCount = 0) => {
  const selectedBytes = selectedVolumeFileID ? volumes.get(selectedVolumeFileID)?.renderer.getMonolithBytes() ?? 0 : 0;
  const budget = Math.max(
    GPU_VOLUME_CACHE_LIMIT_BYTES,
    incomingBytes,
    selectedBytes
  );
  Array.from(volumes).forEach(([volumeFileID, volume]) => {
    if (!volume.readers.size && volumeFileID !== selectedVolumeFileID && (volumes.size + incomingCount > GPU_VOLUME_CACHE_LIMIT || Array.from(volumes.values()).reduce(
      (bytes, entry) => bytes + entry.renderer.getMonolithBytes(),
      incomingBytes
    ) > budget)) {
      removeVolume(volumeFileID, volume);
    }
  });
};
const acquireFullHeadMPRGpuVolume = (reader) => {
  if (typeof document === "undefined") {
    return null;
  }
  const webglRenderer = getFullHeadMPRWebGLRenderer();
  const gl = webglRenderer.getContext();
  const { shape } = reader.geometry;
  if (gl.isContextLost()) {
    return null;
  }
  selectedVolumeFileID = reader.volumeFileID;
  const cached = volumes.get(reader.volumeFileID);
  if (cached && !cached.renderer.canReuse()) {
    removeVolume(
      reader.volumeFileID,
      cached,
      new VolumeGpuError(gl.CONTEXT_LOST_WEBGL)
    );
  }
  const existing = volumes.get(reader.volumeFileID);
  const volume = existing ?? {
    abortController: new AbortController(),
    colorProfile: null,
    readers: /* @__PURE__ */ new Map(),
    renderer: new FullHeadMPRGpuRenderer(webglRenderer, reader.metrics)
  };
  if (!existing) {
    trimVolumes(shape.x * shape.y * shape.z * Int16Array.BYTES_PER_ELEMENT, 1);
  }
  try {
    volume.renderer.prepareVolumeTexture(reader, {
      allowOutOfMemorySplit: false
    });
  } catch (error) {
    volume.renderer.dispose();
    if (!(error instanceof VolumeGpuError) || error.code !== gl.OUT_OF_MEMORY || gl.isContextLost()) {
      throw error;
    }
    clearInactiveFullHeadMPRGpuVolumes();
    try {
      volume.renderer.prepareVolumeTexture(reader);
    } catch (retryError) {
      volume.renderer.dispose();
      throw retryError;
    }
  }
  volumes.delete(reader.volumeFileID);
  volumes.set(reader.volumeFileID, volume);
  volume.readers.set(reader, (volume.readers.get(reader) ?? 0) + 1);
  reader.updateConsumerDemand(
    CONSUMER_ID,
    reader.chunks.filter((chunk) => !volume.renderer.hasUploadedChunk(chunk.index)).map((chunk) => chunk.index),
    { priority: "background" }
  );
  let released = false;
  return {
    getColorProfile: () => volume.colorProfile,
    release: () => {
      if (released) {
        return;
      }
      released = true;
      const remaining = (volume.readers.get(reader) ?? 1) - 1;
      if (remaining > 0) {
        volume.readers.set(reader, remaining);
      } else {
        volume.readers.delete(reader);
        reader.releaseConsumerDemand(CONSUMER_ID);
        volume.renderer.detachReader(reader);
      }
      if (!volume.readers.size && !volume.renderer.canReuse()) {
        removeVolume(
          reader.volumeFileID,
          volume,
          new VolumeGpuError(gl.CONTEXT_LOST_WEBGL)
        );
      }
      trimVolumes();
    },
    renderer: volume.renderer,
    setColorProfile: (profile) => {
      volume.colorProfile = profile;
    },
    signal: volume.abortController.signal
  };
};
const tryAcquireFullHeadMPRGpuVolume = (reader) => {
  try {
    return acquireFullHeadMPRGpuVolume(reader);
  } catch (error) {
    if (!(error instanceof VolumeGpuError)) {
      throw error;
    }
    return null;
  }
};
const clearFullHeadMPRGpuVolumes = (reason) => {
  Array.from(volumes).forEach(
    ([volumeFileID, volume]) => removeVolume(volumeFileID, volume, reason)
  );
  selectedVolumeFileID = null;
  clearFullHeadMPRSliceScene();
};

const PRIMARY_DRAG_INTENT_BY_MODE = {
  brightnessContrast: "brightnessContrast",
  navigate: "none",
  pan: "pan",
  rotate: "rotate",
  scroll: "scroll",
  zoom: "zoom"
};
const PANE_CURSOR_BY_MODE = {
  brightnessContrast: "all-scroll",
  navigate: "crosshair",
  pan: "grab",
  rotate: "alias",
  scroll: "ns-resize",
  zoom: "ns-resize"
};
const TOOTH_LINE_CURSOR = {
  crossing: "move",
  line: "grab"
};
const resolveRightButtonIntent = (mode) => mode === "brightnessContrast" ? "resetDisplayAdjust" : "rotate";
const resolvePrimaryDragIntent = (mode) => PRIMARY_DRAG_INTENT_BY_MODE[mode];
const isPointerFocusMode = (mode) => resolvePrimaryDragIntent(mode) === "none";
const resolveDragZoomFactor = ({
  deltaYPx,
  pixelsPerStep,
  stepFactor
}) => {
  if (!Number.isFinite(deltaYPx) || !Number.isFinite(pixelsPerStep) || pixelsPerStep <= 0) {
    return 1;
  }
  return Math.pow(stepFactor, deltaYPx / pixelsPerStep);
};
const resolveDragSliceSteps = ({
  accumulatedPx,
  thresholdPx
}) => {
  if (!Number.isFinite(accumulatedPx) || !Number.isFinite(thresholdPx) || thresholdPx <= 0) {
    return { remainderPx: 0, steps: 0 };
  }
  const steps = Math.trunc(accumulatedPx / thresholdPx);
  return { remainderPx: accumulatedPx - steps * thresholdPx, steps };
};
const clampPanOffset = ({
  offset,
  sliceExtent
}) => {
  if (!Number.isFinite(offset) || !Number.isFinite(sliceExtent) || sliceExtent <= 0) {
    return 0;
  }
  const limit = sliceExtent / 2;
  return Math.min(Math.max(offset, -limit), limit);
};

const add$2 = (left, right) => ({
  x: left.x + right.x,
  y: left.y + right.y,
  z: left.z + right.z
});
const scale$3 = (vector, factor) => ({
  x: vector.x * factor,
  y: vector.y * factor,
  z: vector.z * factor
});
const getToothPlaneFrame = (basis, orientation) => {
  if (orientation === "axial") {
    return { u: basis.md, v: basis.vo, n: basis.cr };
  }
  if (orientation === "vestibuloOral") {
    return { u: basis.cr, v: basis.md, n: basis.vo };
  }
  return { u: basis.vo, v: basis.cr, n: basis.md };
};
const buildSliceLayout = ({
  basis,
  cursor,
  heightPx,
  mmPerPixel,
  orientation,
  origin,
  widthPx
}) => {
  if (!Number.isFinite(mmPerPixel) || mmPerPixel <= 0 || !Number.isInteger(widthPx) || !Number.isInteger(heightPx) || widthPx <= 0 || heightPx <= 0 || ![cursor.md, cursor.vo, cursor.cr].every(Number.isFinite)) {
    return null;
  }
  const frame = getToothPlaneFrame(basis, orientation);
  const centerPatientMm = add$2(
    origin,
    add$2(
      scale$3(basis.md, cursor.md),
      add$2(scale$3(basis.vo, cursor.vo), scale$3(basis.cr, cursor.cr))
    )
  );
  return {
    centerPatientMm,
    uAxis: frame.u,
    vAxis: frame.v,
    normal: frame.n,
    width: widthPx,
    height: heightPx,
    mmPerPixelU: mmPerPixel,
    mmPerPixelV: mmPerPixel
  };
};
const pixelCenterToPatient = (layout, pixelX, pixelY) => {
  const du = (pixelX + 0.5 - layout.width / 2) * layout.mmPerPixelU;
  const dv = (layout.height / 2 - (pixelY + 0.5)) * layout.mmPerPixelV;
  return add$2(
    layout.centerPatientMm,
    add$2(scale$3(layout.uAxis, du), scale$3(layout.vAxis, dv))
  );
};

const DENSE_ROI_NO_DATA = -32768;
const copyChunkIntoDenseRoi = (volume, chunk) => {
  const { data, originIJK, shape } = volume;
  const sliceStride = shape.x * shape.y;
  const offsetX = chunk.origin.x - originIJK.x;
  const offsetY = chunk.origin.y - originIJK.y;
  const offsetZ = chunk.origin.z - originIJK.z;
  for (let localZ = 0; localZ < chunk.shape.z; localZ += 1) {
    const destZ = offsetZ + localZ;
    if (destZ < 0 || destZ >= shape.z) continue;
    const srcSlice = localZ * chunk.shape.x * chunk.shape.y;
    const destSlice = destZ * sliceStride;
    for (let localY = 0; localY < chunk.shape.y; localY += 1) {
      const destY = offsetY + localY;
      if (destY < 0 || destY >= shape.y) continue;
      const srcRow = srcSlice + localY * chunk.shape.x;
      const destRow = destSlice + destY * shape.x;
      for (let localX = 0; localX < chunk.shape.x; localX += 1) {
        const destX = offsetX + localX;
        if (destX < 0 || destX >= shape.x) continue;
        data[destRow + destX] = chunk.data[srcRow + localX];
      }
    }
  }
};
const packDenseRoiVolume = (chunks, voxelBox, { fill } = {}) => {
  const shape = {
    x: voxelBox.maxExclusive.x - voxelBox.minInclusive.x,
    y: voxelBox.maxExclusive.y - voxelBox.minInclusive.y,
    z: voxelBox.maxExclusive.z - voxelBox.minInclusive.z
  };
  const releaseMemory = reserveVolumeMemory({
    cpuBytes: shape.x * shape.y * shape.z * Int16Array.BYTES_PER_ELEMENT
  });
  let data;
  try {
    data = new Int16Array(shape.x * shape.y * shape.z);
  } catch (error) {
    releaseMemory();
    throw error;
  }
  if (fill !== void 0 && fill !== 0) {
    data.fill(fill);
  }
  const volume = {
    data,
    originIJK: { ...voxelBox.minInclusive },
    release: () => {
      volume.data = new Int16Array(0);
      releaseMemory();
    },
    shape
  };
  chunks.forEach((chunk) => copyChunkIntoDenseRoi(volume, chunk));
  return volume;
};
const clampIndex = (value, maxIndex) => Math.min(Math.max(value, 0), maxIndex);
const sampleDenseRoiTrilinear = (volume, roiLocalVoxel) => {
  const { data, shape } = volume;
  const { x, y, z } = roiLocalVoxel;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z) || x < -0.5 || y < -0.5 || z < -0.5 || x > shape.x - 0.5 || y > shape.y - 0.5 || z > shape.z - 0.5) {
    return null;
  }
  const floorX = Math.floor(x);
  const floorY = Math.floor(y);
  const floorZ = Math.floor(z);
  const fractionX = x - floorX;
  const fractionY = y - floorY;
  const fractionZ = z - floorZ;
  const x0 = clampIndex(floorX, shape.x - 1);
  const x1 = clampIndex(floorX + 1, shape.x - 1);
  const y0 = clampIndex(floorY, shape.y - 1);
  const y1 = clampIndex(floorY + 1, shape.y - 1);
  const z0 = clampIndex(floorZ, shape.z - 1);
  const z1 = clampIndex(floorZ + 1, shape.z - 1);
  const sliceStride = shape.x * shape.y;
  const valueAt = (ix, iy, iz) => data[iz * sliceStride + iy * shape.x + ix];
  const lerp = (from, to, fraction) => from + (to - from) * fraction;
  const contributing = (low, high, fraction) => fraction === 0 ? [low] : fraction === 1 ? [high] : [low, high];
  const hasMissingNeighbour = contributing(z0, z1, fractionZ).some(
    (iz) => contributing(y0, y1, fractionY).some(
      (iy) => contributing(x0, x1, fractionX).some(
        (ix) => valueAt(ix, iy, iz) === DENSE_ROI_NO_DATA
      )
    )
  );
  if (hasMissingNeighbour) {
    return null;
  }
  const bottom = lerp(
    lerp(valueAt(x0, y0, z0), valueAt(x1, y0, z0), fractionX),
    lerp(valueAt(x0, y1, z0), valueAt(x1, y1, z0), fractionX),
    fractionY
  );
  const top = lerp(
    lerp(valueAt(x0, y0, z1), valueAt(x1, y0, z1), fractionX),
    lerp(valueAt(x0, y1, z1), valueAt(x1, y1, z1), fractionX),
    fractionY
  );
  return lerp(bottom, top, fractionZ);
};

const prepareToothSources = ({
  geometry,
  sources
}) => ({
  originMm: geometry.patientCsOrigin,
  roiVolume: sources.roi,
  spacingMm: geometry.spacing
});
const SCRATCH_VOXEL = { x: 0, y: 0, z: 0 };
const sampleToothSources = (prepared, point) => {
  const { originMm, roiVolume, spacingMm } = prepared;
  if (roiVolume) {
    SCRATCH_VOXEL.x = (point.x - originMm.x) / spacingMm.x - roiVolume.originIJK.x;
    SCRATCH_VOXEL.y = (point.y - originMm.y) / spacingMm.y - roiVolume.originIJK.y;
    SCRATCH_VOXEL.z = (point.z - originMm.z) / spacingMm.z - roiVolume.originIJK.z;
    return sampleDenseRoiTrilinear(roiVolume, SCRATCH_VOXEL);
  }
  return null;
};

const MIN_WINDOW_WIDTH_HU = 1;
const renderToothSlice = ({
  geometry,
  layout,
  slabMode,
  slabOffsetsMm,
  volume,
  window,
  signal
}) => {
  const { width, height } = layout;
  if (slabOffsetsMm.length === 0 || !volume) {
    return null;
  }
  const sources = prepareToothSources({
    geometry,
    sources: { roi: volume }
  });
  const windowWidth = Math.max(MIN_WINDOW_WIDTH_HU, window.ww);
  const windowMin = window.wc - windowWidth / 2;
  const data = new Uint8ClampedArray(width * height * 4);
  for (let pixelY = 0; pixelY < height; pixelY += 1) {
    if (signal?.aborted) {
      return null;
    }
    for (let pixelX = 0; pixelX < width; pixelX += 1) {
      const basePoint = pixelCenterToPatient(layout, pixelX, pixelY);
      let validSampleCount = 0;
      let accumulated = 0;
      slabOffsetsMm.forEach((offsetMm) => {
        const point = {
          x: basePoint.x + layout.normal.x * offsetMm,
          y: basePoint.y + layout.normal.y * offsetMm,
          z: basePoint.z + layout.normal.z * offsetMm
        };
        const sample = sampleToothSources(sources, point);
        if (sample === null) {
          return;
        }
        validSampleCount += 1;
        accumulated = slabMode === "mip" ? validSampleCount === 1 ? sample : Math.max(accumulated, sample) : accumulated + sample;
      });
      const pixelOffset = (pixelY * width + pixelX) * 4;
      if (validSampleCount === 0) {
        continue;
      }
      const reduced = slabMode === "mip" ? accumulated : accumulated / validSampleCount;
      const normalized = Math.min(
        Math.max((reduced - windowMin) / windowWidth, 0),
        1
      );
      const intensity = Math.round(normalized * 255);
      data[pixelOffset] = intensity;
      data[pixelOffset + 1] = intensity;
      data[pixelOffset + 2] = intensity;
      data[pixelOffset + 3] = 255;
    }
  }
  return { data, width, height };
};

const isFinitePositive$3 = (value) => Number.isFinite(value) && value > 0;
const paneCanvasRect = ({
  canvasHeightPx,
  canvasWidthPx,
  pixelRatio,
  size
}) => {
  if (!isFinitePositive$3(pixelRatio) || !isFinitePositive$3(canvasHeightPx) || !isFinitePositive$3(canvasWidthPx) || !isFinitePositive$3(size.widthPx) || !isFinitePositive$3(size.heightPx) || !Number.isFinite(size.leftPx) || !Number.isFinite(size.bottomPx)) {
    return null;
  }
  const widthPx = Math.round(size.widthPx * pixelRatio);
  const heightPx = Math.round(size.heightPx * pixelRatio);
  const leftPx = Math.round(size.leftPx * pixelRatio);
  const bottomPx = Math.round(size.bottomPx * pixelRatio);
  const topPx = canvasHeightPx - (bottomPx + heightPx);
  const clampedLeft = Math.min(Math.max(leftPx, 0), canvasWidthPx - 1);
  const clampedTop = Math.min(Math.max(topPx, 0), canvasHeightPx - 1);
  const clampedWidth = Math.min(widthPx, canvasWidthPx - clampedLeft);
  const clampedHeight = Math.min(heightPx, canvasHeightPx - clampedTop);
  if (clampedWidth < 1 || clampedHeight < 1) {
    return null;
  }
  return {
    heightPx: clampedHeight,
    leftPx: clampedLeft,
    topPx: clampedTop,
    widthPx: clampedWidth
  };
};

const createFullHeadMPRPresentation = (options) => {
  const renderedProjections = /* @__PURE__ */ new Set();
  let visibleProjections = [...options.projections];
  let completed = false;
  const completeIfReady = () => {
    if (completed || visibleProjections.length === 0 || visibleProjections.some((projection) => !renderedProjections.has(projection))) {
      return false;
    }
    completed = true;
    return true;
  };
  return {
    markRendered: (projection) => {
      renderedProjections.add(projection);
      return completeIfReady();
    },
    presentationID: options.presentationID,
    setProjections: (projections) => {
      visibleProjections = [...projections];
      return completeIfReady();
    },
    volumeFileID: options.volumeFileID
  };
};

const MAX_SLAB_INTERVAL_COUNT = 32;
const CONFIGURED_MAX_SLAB_THICKNESS_MM = 2;
const isFinitePositive$2 = (value) => Number.isFinite(value) && value > 0;
const isValidSpacing = (spacing) => isFinitePositive$2(spacing.x) && isFinitePositive$2(spacing.y) && isFinitePositive$2(spacing.z);
const clamp$2 = (value, min, max) => Math.min(Math.max(value, min), max);
const directionalNativeSpacingMm = ({
  normal,
  spacing
}) => {
  if (!isValidSpacing(spacing)) {
    return null;
  }
  const normalLength = Math.sqrt(
    normal.x * normal.x + normal.y * normal.y + normal.z * normal.z
  );
  if (!Number.isFinite(normalLength) || normalLength === 0) {
    return null;
  }
  const unitX = normal.x / normalLength;
  const unitY = normal.y / normalLength;
  const unitZ = normal.z / normalLength;
  const inverseSpacing = Math.sqrt(
    (unitX / spacing.x) ** 2 + (unitY / spacing.y) ** 2 + (unitZ / spacing.z) ** 2
  );
  if (!isFinitePositive$2(inverseSpacing)) {
    return null;
  }
  return 1 / inverseSpacing;
};
const resolveSlabSampling = ({
  configuredMaxSlabThicknessMm,
  normal,
  spacing,
  thickness
}) => {
  const nativeSpacingMm = directionalNativeSpacingMm({ normal, spacing });
  if (nativeSpacingMm === null) {
    return null;
  }
  if (thickness.kind === "native") {
    return {
      effectiveThicknessMm: nativeSpacingMm,
      offsetsMm: [0],
      sampleCount: 1,
      sampleSpacingMm: 0
    };
  }
  if (!isFinitePositive$2(thickness.thicknessMm) || !isFinitePositive$2(configuredMaxSlabThicknessMm)) {
    return null;
  }
  const orientationMaxMm = Math.max(configuredMaxSlabThicknessMm, nativeSpacingMm);
  const effectiveThicknessMm = clamp$2(
    thickness.thicknessMm,
    nativeSpacingMm,
    orientationMaxMm
  );
  const intervalCount = clamp$2(
    Math.ceil(effectiveThicknessMm / nativeSpacingMm),
    1,
    MAX_SLAB_INTERVAL_COUNT
  );
  const sampleCount = intervalCount + 1;
  const sampleSpacingMm = effectiveThicknessMm / intervalCount;
  const offsetsMm = Array.from(
    { length: sampleCount },
    (_, sampleIndex) => -effectiveThicknessMm / 2 + sampleIndex * sampleSpacingMm
  );
  return {
    effectiveThicknessMm,
    offsetsMm,
    sampleCount,
    sampleSpacingMm
  };
};

const add$1 = (left, right) => ({
  x: left.x + right.x,
  y: left.y + right.y,
  z: left.z + right.z
});
const scale$2 = (vector, factor) => ({
  x: vector.x * factor,
  y: vector.y * factor,
  z: vector.z * factor
});
const isFiniteVec3$3 = (vector) => Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
const inPlanePoint = ({
  frame,
  offsetUMm,
  offsetVMm,
  originMm
}) => add$1(originMm, add$1(scale$2(frame.u, offsetUMm), scale$2(frame.v, offsetVMm)));
const resolveToothPaneFitZoom = ({
  frustumPx,
  heightMm,
  mmPerPixel,
  widthMm
}) => Math.min(
  (frustumPx.right - frustumPx.left) * mmPerPixel / widthMm,
  (frustumPx.bottom - frustumPx.top) * mmPerPixel / heightMm
);
const resolveToothPaneRefitZoom = ({
  initialZoom,
  nextFitZoom,
  zoomPan
}) => zoomPan.zoom === initialZoom && zoomPan.panX === 0 && zoomPan.panY === 0 ? nextFitZoom : null;
const resolveToothPaneVisibleRect = ({
  centerMm,
  frame,
  frustumPx,
  spec,
  zoomPan
}) => {
  const { bottom, left, right, top } = frustumPx;
  if (!isFiniteVec3$3(centerMm) || !isFiniteVec3$3(frame.u) || !isFiniteVec3$3(frame.v) || ![
    bottom,
    left,
    right,
    top,
    zoomPan.panX,
    zoomPan.panY,
    zoomPan.zoom
  ].every(Number.isFinite) || zoomPan.zoom <= 0 || !(spec.mmPerPixel > 0)) {
    return null;
  }
  const visibleCenterXPx = (left + right) / 2 + zoomPan.panX;
  const visibleCenterYPx = (top + bottom) / 2 + zoomPan.panY;
  const halfWidthPx = Math.abs(right - left) / (2 * zoomPan.zoom);
  const halfHeightPx = Math.abs(bottom - top) / (2 * zoomPan.zoom);
  return {
    centerMm: inPlanePoint({
      frame,
      offsetUMm: (visibleCenterXPx - spec.widthPx / 2) * spec.mmPerPixel,
      offsetVMm: -(visibleCenterYPx - spec.heightPx / 2) * spec.mmPerPixel,
      originMm: centerMm
    }),
    halfUMm: halfWidthPx * spec.mmPerPixel,
    halfVMm: halfHeightPx * spec.mmPerPixel
  };
};
const resolveToothPaneRasterRectPx = ({
  frustumPx,
  zoomPan
}) => {
  const { bottom, left, right, top } = frustumPx;
  if (![
    bottom,
    left,
    right,
    top,
    zoomPan.panX,
    zoomPan.panY,
    zoomPan.zoom
  ].every(Number.isFinite) || zoomPan.zoom <= 0) {
    return null;
  }
  const widthPx = Math.abs(right - left) / zoomPan.zoom;
  const heightPx = Math.abs(bottom - top) / zoomPan.zoom;
  return {
    heightPx,
    widthPx,
    xPx: (left + right) / 2 + zoomPan.panX - widthPx / 2,
    yPx: (top + bottom) / 2 + zoomPan.panY - heightPx / 2
  };
};
const buildToothPaneVisibleLayout = ({
  frame,
  maxRasterPx,
  minMmPerPixel,
  rect,
  surfaceHeightPx,
  surfaceWidthPx
}) => {
  if (!(rect.halfUMm > 0) || !(rect.halfVMm > 0) || !(surfaceWidthPx > 0) || !(surfaceHeightPx > 0) || !(maxRasterPx >= 1) || !(minMmPerPixel > 0)) {
    return null;
  }
  const widthMm = rect.halfUMm * 2;
  const heightMm = rect.halfVMm * 2;
  const mmPerPixel = Math.max(
    widthMm / Math.min(surfaceWidthPx, maxRasterPx),
    heightMm / Math.min(surfaceHeightPx, maxRasterPx),
    minMmPerPixel
  );
  if (!(mmPerPixel > 0)) {
    return null;
  }
  const widthPx = Math.max(1, Math.round(widthMm / mmPerPixel));
  const heightPx = Math.max(1, Math.round(heightMm / mmPerPixel));
  return {
    centerPatientMm: rect.centerMm,
    height: heightPx,
    mmPerPixelU: mmPerPixel,
    mmPerPixelV: mmPerPixel,
    normal: frame.n,
    uAxis: frame.u,
    vAxis: frame.v,
    width: widthPx
  };
};

const MAX_SNAPSHOT_SIDE_PX = 2048;
const resolveFullHeadMPRViewSampling = ({
  spacing,
  thickness,
  view
}) => {
  const sampling = resolveSlabSampling({
    configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
    normal: view.normal,
    spacing,
    thickness
  });
  return sampling ? {
    ...view,
    sampleOffsetsMm: [...sampling.offsetsMm],
    sampling: thickness.kind === "native" ? "native" : "mean-slab",
    thicknessMm: sampling.effectiveThicknessMm
  } : null;
};
const makeFullHeadMPRViewSnapshot = ({
  heightPx,
  presentationID,
  view,
  widthPx
}) => {
  const maxSidePx = Math.min(MAX_SNAPSHOT_SIDE_PX, Math.max(widthPx, heightPx));
  const scale = Math.max(view.widthMm, view.heightMm) / maxSidePx;
  return {
    heightPx: Math.max(1, Math.round(view.heightMm / scale)),
    presentationID,
    view,
    widthPx: Math.max(1, Math.round(view.widthMm / scale))
  };
};
const describeToothView = ({
  centerMm,
  frame,
  frustumPx,
  projection,
  spacing,
  spec,
  thickness,
  volumeFileID,
  window,
  zoomPan
}) => {
  const rect = resolveToothPaneVisibleRect({
    centerMm,
    frame,
    frustumPx,
    spec,
    zoomPan
  });
  if (!rect) {
    return null;
  }
  return resolveFullHeadMPRViewSampling({
    spacing,
    thickness,
    view: {
      centerMm: { ...rect.centerMm },
      heightMm: rect.halfVMm * 2,
      normal: { ...frame.n },
      projection,
      uAxis: { ...frame.u },
      vAxis: { ...frame.v },
      volumeFileID,
      widthMm: rect.halfUMm * 2,
      window: { ...window }
    }
  });
};
const describeOrthogonalView = ({
  focus,
  frustum,
  geometry,
  projection,
  volumeFileID,
  window,
  zoomPan
}) => {
  const axes = ORTHOGONAL_PANE_AXES[projection];
  const centerX = (frustum.left + frustum.right) / 2 + zoomPan.panX;
  const centerY = (frustum.top + frustum.bottom) / 2 + zoomPan.panY;
  const centerIJK = {
    ...focus,
    [axes.horizontalAxis]: centerX - 0.5,
    [axes.verticalAxis]: (axes.verticalSign === 1 ? geometry.shape[axes.verticalAxis] - centerY : centerY) - 0.5
  };
  return {
    centerMm: {
      x: geometry.patientCsOrigin.x + centerIJK.x * geometry.spacing.x,
      y: geometry.patientCsOrigin.y + centerIJK.y * geometry.spacing.y,
      z: geometry.patientCsOrigin.z + centerIJK.z * geometry.spacing.z
    },
    heightMm: Math.abs(frustum.bottom - frustum.top) * geometry.spacing[axes.verticalAxis] / zoomPan.zoom,
    normal: { x: 0, y: 0, z: 0, [axes.sliceAxis]: 1 },
    projection,
    sampleOffsetsMm: [0],
    sampling: "native",
    thicknessMm: geometry.spacing[axes.sliceAxis],
    uAxis: { x: 0, y: 0, z: 0, [axes.horizontalAxis]: 1 },
    vAxis: { x: 0, y: 0, z: 0, [axes.verticalAxis]: axes.verticalSign },
    volumeFileID,
    widthMm: Math.abs(frustum.right - frustum.left) * geometry.spacing[axes.horizontalAxis] / zoomPan.zoom,
    window: { ...window }
  };
};

const vector$1 = ({ x, y, z }) => new Vector3(x, y, z);
const point = ({ x, y, z }) => ({ x, y, z });
const resolveSavedViewFrames = (view) => {
  const source = ORTHOGONAL_PANE_AXES[view.projection];
  const sourceU = vector$1({ x: 0, y: 0, z: 0, [source.horizontalAxis]: 1 });
  const sourceV = vector$1({ x: 0, y: 0, z: 0, [source.verticalAxis]: source.verticalSign });
  const targetU = vector$1(view.uAxis);
  const targetV = vector$1(view.vAxis);
  const rotation = new Matrix4().makeBasis(targetU, targetV, targetU.clone().cross(targetV)).multiply(new Matrix4().makeBasis(sourceU, sourceV, sourceU.clone().cross(sourceV)).invert());
  const sourceNormal = vector$1({ x: 0, y: 0, z: 0, [source.sliceAxis]: 1 }).transformDirection(rotation);
  const normalSign = sourceNormal.dot(vector$1(view.normal)) < 0 ? -1 : 1;
  return Object.fromEntries(
    Object.entries(ORTHOGONAL_PANE_AXES).map(
      ([projection, axes]) => {
        const u = vector$1({ x: 0, y: 0, z: 0, [axes.horizontalAxis]: 1 }).transformDirection(
          rotation
        );
        const v = vector$1({
          x: 0,
          y: 0,
          z: 0,
          [axes.verticalAxis]: axes.verticalSign
        }).transformDirection(rotation);
        const normal = vector$1({ x: 0, y: 0, z: 0, [axes.sliceAxis]: 1 }).transformDirection(rotation).multiplyScalar(normalSign);
        return [
          projection,
          {
            n: projection === view.projection ? view.normal : point(normal),
            u: point(u),
            v: point(v)
          }
        ];
      }
    )
  );
};

const enclosingVoxelBox = (boxes) => ({
  minInclusive: {
    x: Math.min(...boxes.map((box) => box.minInclusive.x)),
    y: Math.min(...boxes.map((box) => box.minInclusive.y)),
    z: Math.min(...boxes.map((box) => box.minInclusive.z))
  },
  maxExclusive: {
    x: Math.max(...boxes.map((box) => box.maxExclusive.x)),
    y: Math.max(...boxes.map((box) => box.maxExclusive.y)),
    z: Math.max(...boxes.map((box) => box.maxExclusive.z))
  }
});
const dot$3 = (left, right) => left.x * right.x + left.y * right.y + left.z * right.z;
const boxCorners$2 = (box) => [box.min.x, box.max.x].flatMap(
  (x) => [box.min.y, box.max.y].flatMap(
    (y) => [box.min.z, box.max.z].map((z) => ({ x, y, z }))
  )
);
const isFiniteVec3$2 = (vector) => Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
const projectToToothLocalBounds = ({
  basis,
  corners,
  origin
}) => {
  const locals = corners.map((corner) => {
    const offset = {
      x: corner.x - origin.x,
      y: corner.y - origin.y,
      z: corner.z - origin.z
    };
    return {
      md: dot$3(offset, basis.md),
      vo: dot$3(offset, basis.vo),
      cr: dot$3(offset, basis.cr)
    };
  });
  const axisBounds = (axis) => ({
    min: Math.min(...locals.map((local) => local[axis])),
    max: Math.max(...locals.map((local) => local[axis]))
  });
  return { md: axisBounds("md"), vo: axisBounds("vo"), cr: axisBounds("cr") };
};
const buildToothSampleRoi = ({
  geometry,
  paddingMm,
  patientPoints
}) => {
  if (!patientPoints.length || !patientPoints.every(isFiniteVec3$2)) {
    return null;
  }
  if (!Number.isFinite(paddingMm) || paddingMm < 0) {
    return null;
  }
  const voxels = patientPoints.map((point) => ({
    x: (point.x - geometry.patientCsOrigin.x) / geometry.spacing.x,
    y: (point.y - geometry.patientCsOrigin.y) / geometry.spacing.y,
    z: (point.z - geometry.patientCsOrigin.z) / geometry.spacing.z
  }));
  if (!voxels.every(isFiniteVec3$2)) {
    return null;
  }
  const padVoxels = {
    x: paddingMm / geometry.spacing.x,
    y: paddingMm / geometry.spacing.y,
    z: paddingMm / geometry.spacing.z
  };
  const axisBox = (axis) => {
    const values = voxels.map((voxel) => voxel[axis]);
    return {
      minInclusive: Math.max(
        0,
        Math.floor(Math.min(...values) - padVoxels[axis]) - 1
      ),
      maxExclusive: Math.min(
        geometry.shape[axis],
        Math.ceil(Math.max(...values) + padVoxels[axis]) + 2
      )
    };
  };
  const x = axisBox("x");
  const y = axisBox("y");
  const z = axisBox("z");
  if (x.minInclusive >= x.maxExclusive || y.minInclusive >= y.maxExclusive || z.minInclusive >= z.maxExclusive) {
    return null;
  }
  return {
    minInclusive: { x: x.minInclusive, y: y.minInclusive, z: z.minInclusive },
    maxExclusive: { x: x.maxExclusive, y: y.maxExclusive, z: z.maxExclusive }
  };
};
const patientBoxCorners = (box) => boxCorners$2(box);
const TOOTH_ROI_BUDGET_VOXELS = 40 * 1024 * 1024 / 2;
const voxelBoxSize = ({ maxExclusive, minInclusive }) => (maxExclusive.x - minInclusive.x) * (maxExclusive.y - minInclusive.y) * (maxExclusive.z - minInclusive.z);
const quantizeToothLocalBox = ({
  basis,
  bounds,
  geometry,
  origin
}) => {
  const [mdBounds, voBounds, crBounds] = bounds;
  const localCorners = boxCorners$2({
    min: { x: mdBounds.min, y: voBounds.min, z: crBounds.min },
    max: { x: mdBounds.max, y: voBounds.max, z: crBounds.max }
  });
  const patientCorners = localCorners.map((local) => ({
    x: origin.x + basis.md.x * local.x + basis.vo.x * local.y + basis.cr.x * local.z,
    y: origin.y + basis.md.y * local.x + basis.vo.y * local.y + basis.cr.y * local.z,
    z: origin.z + basis.md.z * local.x + basis.vo.z * local.y + basis.cr.z * local.z
  }));
  const voxelCorners = patientCorners.map((corner) => ({
    x: (corner.x - geometry.patientCsOrigin.x) / geometry.spacing.x,
    y: (corner.y - geometry.patientCsOrigin.y) / geometry.spacing.y,
    z: (corner.z - geometry.patientCsOrigin.z) / geometry.spacing.z
  }));
  if (!voxelCorners.every(isFiniteVec3$2)) {
    return null;
  }
  const minVoxel = {
    x: Math.min(...voxelCorners.map((corner) => corner.x)),
    y: Math.min(...voxelCorners.map((corner) => corner.y)),
    z: Math.min(...voxelCorners.map((corner) => corner.z))
  };
  const maxVoxel = {
    x: Math.max(...voxelCorners.map((corner) => corner.x)),
    y: Math.max(...voxelCorners.map((corner) => corner.y)),
    z: Math.max(...voxelCorners.map((corner) => corner.z))
  };
  const minInclusive = {
    x: Math.max(0, Math.floor(minVoxel.x) - 1),
    y: Math.max(0, Math.floor(minVoxel.y) - 1),
    z: Math.max(0, Math.floor(minVoxel.z) - 1)
  };
  const maxExclusive = {
    x: Math.min(geometry.shape.x, Math.ceil(maxVoxel.x) + 2),
    y: Math.min(geometry.shape.y, Math.ceil(maxVoxel.y) + 2),
    z: Math.min(geometry.shape.z, Math.ceil(maxVoxel.z) + 2)
  };
  if (minInclusive.x >= maxExclusive.x || minInclusive.y >= maxExclusive.y || minInclusive.z >= maxExclusive.z) {
    return null;
  }
  return { minInclusive, maxExclusive };
};
const buildStableToothRoi = ({
  basis,
  configuredMaxSlabThicknessMm,
  geometry,
  origin,
  outerBox
}) => {
  if (!isFiniteVec3$2(origin) || !isFiniteVec3$2(outerBox.min) || !isFiniteVec3$2(outerBox.max) || !Number.isFinite(configuredMaxSlabThicknessMm) || configuredMaxSlabThicknessMm <= 0) {
    return null;
  }
  const localBounds = projectToToothLocalBounds({
    basis,
    corners: boxCorners$2(outerBox),
    origin
  });
  const axes = [
    { axis: basis.md, bounds: localBounds.md },
    { axis: basis.vo, bounds: localBounds.vo },
    { axis: basis.cr, bounds: localBounds.cr }
  ];
  const slabMarginsMm = axes.map(({ axis }) => {
    const nativeSpacingMm = directionalNativeSpacingMm({
      normal: axis,
      spacing: geometry.spacing
    });
    return nativeSpacingMm === null ? null : Math.max(configuredMaxSlabThicknessMm, nativeSpacingMm) / 2;
  });
  if (slabMarginsMm.some((margin) => margin === null)) {
    return null;
  }
  const symmetricHalvesMm = axes.map(
    ({ bounds }) => Math.max(Math.abs(bounds.min), Math.abs(bounds.max))
  );
  const isotropicHalfMm = Math.max(...symmetricHalvesMm);
  const boundsFromHalves = (halvesMm) => halvesMm.map((halfMm, index) => {
    const expandedMm = halfMm + slabMarginsMm[index];
    return { min: -expandedMm, max: expandedMm };
  });
  const isotropicCandidate = quantizeToothLocalBox({
    basis,
    bounds: boundsFromHalves(symmetricHalvesMm.map(() => isotropicHalfMm)),
    geometry,
    origin
  });
  if (isotropicCandidate && voxelBoxSize(isotropicCandidate) <= TOOTH_ROI_BUDGET_VOXELS) {
    return isotropicCandidate;
  }
  const [mdBounds, voBounds, crBounds] = boundsFromHalves(symmetricHalvesMm);
  return quantizeToothLocalBox({
    basis,
    bounds: [mdBounds, voBounds, crBounds],
    geometry,
    origin
  });
};
const voxelBoxToPatientBox = (voxelBox, geometry) => ({
  min: {
    x: geometry.patientCsOrigin.x + voxelBox.minInclusive.x * geometry.spacing.x,
    y: geometry.patientCsOrigin.y + voxelBox.minInclusive.y * geometry.spacing.y,
    z: geometry.patientCsOrigin.z + voxelBox.minInclusive.z * geometry.spacing.z
  },
  max: {
    x: geometry.patientCsOrigin.x + voxelBox.maxExclusive.x * geometry.spacing.x,
    y: geometry.patientCsOrigin.y + voxelBox.maxExclusive.y * geometry.spacing.y,
    z: geometry.patientCsOrigin.z + voxelBox.maxExclusive.z * geometry.spacing.z
  }
});
const voxelBoxToTextureBox = (voxelBox, geometry) => ({
  min: {
    x: geometry.patientCsOrigin.x + (voxelBox.minInclusive.x - 0.5) * geometry.spacing.x,
    y: geometry.patientCsOrigin.y + (voxelBox.minInclusive.y - 0.5) * geometry.spacing.y,
    z: geometry.patientCsOrigin.z + (voxelBox.minInclusive.z - 0.5) * geometry.spacing.z
  },
  max: {
    x: geometry.patientCsOrigin.x + (voxelBox.maxExclusive.x - 0.5) * geometry.spacing.x,
    y: geometry.patientCsOrigin.y + (voxelBox.maxExclusive.y - 0.5) * geometry.spacing.y,
    z: geometry.patientCsOrigin.z + (voxelBox.maxExclusive.z - 0.5) * geometry.spacing.z
  }
});
const volumeVoxelBox = (geometry) => ({
  maxExclusive: { ...geometry.shape },
  minInclusive: { x: 0, y: 0, z: 0 }
});
const volumeBoxMm = (geometry) => voxelBoxToTextureBox(volumeVoxelBox(geometry), geometry);

const toVector3 = (vector) => new Vector3(vector.x, vector.y, vector.z);
const buildToothPixelToIjkMatrix = ({
  geometry,
  layout,
  roi
}) => {
  const uStep = toVector3(layout.uAxis).multiplyScalar(layout.mmPerPixelU);
  const vStep = toVector3(layout.vAxis).multiplyScalar(-layout.mmPerPixelV);
  const origin = toVector3(layout.centerPatientMm).add(uStep.clone().multiplyScalar(-layout.width / 2)).add(vStep.clone().multiplyScalar(-layout.height / 2));
  const pixelToPatient = new Matrix4().makeBasis(uStep, vStep, new Vector3().crossVectors(uStep, vStep).normalize()).setPosition(origin);
  const textureBox = voxelBoxToTextureBox(roi, geometry);
  const size = toVector3(textureBox.max).sub(toVector3(textureBox.min));
  const patientToIjk = new Matrix4().makeScale(1 / size.x, 1 / size.y, 1 / size.z).setPosition(
    -textureBox.min.x / size.x,
    -textureBox.min.y / size.y,
    -textureBox.min.z / size.z
  );
  return patientToIjk.multiply(pixelToPatient);
};

const TOOTH_SLAB_MODE_MEAN = 0;
const TOOTH_SLAB_MODE_MIP = 1;
const toIjkDelta = ({
  displacement,
  geometry,
  roi
}) => ({
  x: displacement.x / geometry.spacing.x / (roi.maxExclusive.x - roi.minInclusive.x),
  y: displacement.y / geometry.spacing.y / (roi.maxExclusive.y - roi.minInclusive.y),
  z: displacement.z / geometry.spacing.z / (roi.maxExclusive.z - roi.minInclusive.z)
});
const scaleNormal = (normal, lengthMm) => ({
  x: normal.x * lengthMm,
  y: normal.y * lengthMm,
  z: normal.z * lengthMm
});
const buildToothSlabUniforms = ({
  geometry,
  layout,
  roi,
  slabMode,
  slabOffsetsMm
}) => {
  if (!slabOffsetsMm.length) {
    return null;
  }
  const stepMm = slabOffsetsMm.length > 1 ? slabOffsetsMm[1] - slabOffsetsMm[0] : 0;
  return {
    slabMode: slabMode === "mip" ? TOOTH_SLAB_MODE_MIP : TOOTH_SLAB_MODE_MEAN,
    slabSampleCount: slabOffsetsMm.length,
    slabStartIjk: toIjkDelta({
      displacement: scaleNormal(layout.normal, slabOffsetsMm[0]),
      geometry,
      roi
    }),
    slabStepIjk: toIjkDelta({
      displacement: scaleNormal(layout.normal, stepMm),
      geometry,
      roi
    })
  };
};

const yieldBetweenRenderPasses = async (signal) => {
  signal?.throwIfAborted();
  await new Promise((resolve) => setTimeout(resolve, 0));
  signal?.throwIfAborted();
};

let renderQueue = Promise.resolve();
const readPixelsAsync = async ({
  gl,
  height,
  signal,
  width
}) => {
  const buffer = gl.createBuffer();
  if (!buffer) {
    throw new VolumeGpuError(gl.OUT_OF_MEMORY);
  }
  const previousBuffer = gl.getParameter(gl.PIXEL_PACK_BUFFER_BINDING);
  let fence = null;
  try {
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer);
    gl.bufferData(gl.PIXEL_PACK_BUFFER, width * height * 4, gl.STREAM_READ);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, 0);
    fence = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
    gl.flush();
    gl.bindBuffer(gl.PIXEL_PACK_BUFFER, previousBuffer);
    const error = gl.getError();
    if (error !== gl.NO_ERROR || !fence) {
      throw new VolumeGpuError(error || gl.INVALID_OPERATION);
    }
    while (true) {
      await yieldBetweenRenderPasses(signal);
      const status = gl.clientWaitSync(fence, 0, 0);
      if (gl.isContextLost() || status === gl.WAIT_FAILED) {
        throw new VolumeGpuError(
          gl.isContextLost() ? gl.CONTEXT_LOST_WEBGL : gl.INVALID_OPERATION
        );
      }
      if (status !== gl.TIMEOUT_EXPIRED) {
        break;
      }
    }
    const pixels = new Uint8ClampedArray(width * height * 4);
    const currentBuffer = gl.getParameter(gl.PIXEL_PACK_BUFFER_BINDING);
    try {
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, buffer);
      gl.getBufferSubData(gl.PIXEL_PACK_BUFFER, 0, pixels);
    } finally {
      gl.bindBuffer(gl.PIXEL_PACK_BUFFER, currentBuffer);
    }
    return pixels;
  } finally {
    gl.deleteSync(fence);
    gl.deleteBuffer(buffer);
  }
};
const drawSlice = async ({
  layout,
  reader,
  signal,
  slabOffsetsMm,
  volume,
  window
}) => {
  signal?.throwIfAborted();
  const renderer = getFullHeadMPRWebGLRenderer();
  const gl = renderer.getContext();
  if (gl.isContextLost()) {
    throw new VolumeGpuError(gl.CONTEXT_LOST_WEBGL);
  }
  const prepared = volume.renderer.prepareVolumeTexture(reader);
  const roi = volumeVoxelBox(reader.geometry);
  const slab = buildToothSlabUniforms({
    geometry: reader.geometry,
    layout,
    roi,
    slabMode: "mean",
    slabOffsetsMm
  });
  if (!prepared) {
    throw new VolumeGpuError(gl.INVALID_OPERATION);
  }
  if (!slab) {
    throw new RangeError("A tooth slab requires at least one sample");
  }
  const { height, width } = layout;
  const { camera, material, mesh, placeholderTexture, scene } = getFullHeadMPRSliceScene();
  const releaseMemory = reserveVolumeMemory({
    cpuBytes: width * height * 4,
    gpuBytes: width * height * 8
  });
  let target = null;
  try {
    target = new WebGLRenderTarget(width, height, {
      depthBuffer: false,
      stencilBuffer: false
    });
    material.setVolume(prepared.texture, prepared.blocks);
    material.uniforms.ww.value = Math.max(1, window.ww);
    material.uniforms.wmin.value = window.wc - Math.max(1, window.ww) / 2;
    material.uniforms.xyztoijk.value = buildToothPixelToIjkMatrix({
      geometry: reader.geometry,
      layout,
      roi
    });
    material.setSlab(slab);
    mesh.scale.set(width, height, 1);
    mesh.position.set(width / 2, height / 2, 0);
    camera.right = width;
    camera.bottom = height;
    camera.updateProjectionMatrix();
    const previousTarget = renderer.getRenderTarget();
    const previousLayer = renderer.getActiveCubeFace();
    const previousLevel = renderer.getActiveMipmapLevel();
    const previousViewport = renderer.getViewport(new Vector4());
    const previousScissor = renderer.getScissor(new Vector4());
    const previousScissorTest = renderer.getScissorTest();
    const previousColor = renderer.getClearColor(new Color());
    const previousAlpha = renderer.getClearAlpha();
    const startedAt = performance.now();
    const submitReadback = () => {
      try {
        renderer.setRenderTarget(target);
        renderer.setScissorTest(false);
        renderer.setClearColor(0, 0);
        renderer.render(scene, camera);
        return readPixelsAsync({ gl, height, signal, width });
      } finally {
        renderer.setRenderTarget(previousTarget, previousLayer, previousLevel);
        renderer.setViewport(previousViewport);
        renderer.setScissor(previousScissor);
        renderer.setScissorTest(previousScissorTest);
        renderer.setClearColor(previousColor, previousAlpha);
      }
    };
    const pixels = await submitReadback();
    signal?.throwIfAborted();
    const rowBytes = width * 4;
    const row = new Uint8ClampedArray(rowBytes);
    for (let top = 0; top < Math.floor(height / 2); top++) {
      const bottom = height - top - 1;
      row.set(pixels.subarray(top * rowBytes, (top + 1) * rowBytes));
      pixels.copyWithin(
        top * rowBytes,
        bottom * rowBytes,
        (bottom + 1) * rowBytes
      );
      pixels.set(row, bottom * rowBytes);
    }
    reader.metrics.recordSample(
      "monolithReadbackMs",
      performance.now() - startedAt
    );
    reader.metrics.incrementCounter("monolithFrameCount");
    return { data: pixels, height, width };
  } finally {
    material.setVolume(placeholderTexture);
    target?.dispose();
    releaseMemory();
  }
};
const renderGpuSlice = async (request) => {
  const { layout, reader, slabOffsetsMm, volume } = request;
  const linked = linkAbortSignals(request.signal, volume.signal);
  const { signal } = linked;
  try {
    signal.throwIfAborted();
    const box = buildToothSampleRoi({
      geometry: reader.geometry,
      paddingMm: Math.max(...slabOffsetsMm.map(Math.abs)),
      patientPoints: [0, layout.width - 1].flatMap(
        (x) => [0, layout.height - 1].map((y) => pixelCenterToPatient(layout, x, y))
      )
    });
    if (box) {
      const indices = reader.findChunksIntersectingBox(voxelBoxToPatientBox(box, reader.geometry)).filter((chunk) => !volume.renderer.hasUploadedChunk(chunk.index)).map((chunk) => chunk.index);
      await reader.ensureChunksLoaded(indices, signal, {
        priority: "visible",
        retention: "stream"
      });
    }
    const result = renderQueue.then(async () => {
      await yieldBetweenRenderPasses(signal);
      return drawSlice({ ...request, signal });
    });
    renderQueue = result.then(
      () => void 0,
      () => void 0
    );
    return await result;
  } catch (error) {
    signal.throwIfAborted();
    throw error;
  } finally {
    linked.release();
  }
};

const TOOTH_ROI_CONSUMER_ID = "full-head-mpr-tooth-roi";
const TOOTH_SERIES_CONSUMER_ID = "full-head-mpr-tooth-series";
const TOOTH_PREFETCH_CONSUMER_ID = "full-head-mpr-tooth-prefetch";
const TOOTH_ZOOM_CONSUMER_ID = "full-head-mpr-tooth-zoom";
const stampToothRoiPhase = (phase, onProgress) => onProgress && ((progress) => onProgress({ ...progress, phase }));
const roiChunkIndices = (reader, voxelBox) => [
  ...reader.findChunksIntersectingBox(
    voxelBoxToPatientBox(voxelBox, reader.geometry)
  )
].sort((left, right) => left.index - right.index).map((chunk) => chunk.index);
const releaseToothRoiDemand = (reader, consumerID = TOOTH_ROI_CONSUMER_ID) => {
  reader.releaseConsumerDemand(consumerID);
};
const findToothRoiChunks = ({
  reader,
  voxelBox
}) => [
  ...reader.findChunksIntersectingBox(
    voxelBoxToPatientBox(voxelBox, reader.geometry)
  )
].sort((left, right) => left.index - right.index).map((chunk) => ({
  index: chunk.index,
  origin: chunk.origin,
  shape: chunk.shape
}));
const loadStableToothRoi = async ({
  consumerID = TOOTH_ROI_CONSUMER_ID,
  onProgress,
  priority = "focus",
  reader,
  signal,
  voxelBox
}) => {
  const [volume] = await loadToothRoiPlanes({
    consumerID,
    onProgress,
    priority,
    reader,
    signal,
    voxelBoxes: [voxelBox]
  });
  return volume;
};
const loadToothRoiPlanes = async ({
  consumerID = TOOTH_ROI_CONSUMER_ID,
  onProgress,
  priority = "focus",
  reader,
  signal,
  voxelBoxes
}) => {
  const chunksPerBox = voxelBoxes.map(
    (voxelBox) => [
      ...reader.findChunksIntersectingBox(
        voxelBoxToPatientBox(voxelBox, reader.geometry)
      )
    ].sort((left, right) => left.index - right.index)
  );
  const requiredIndices = [
    ...new Set(chunksPerBox.flatMap((chunks) => chunks.map((chunk) => chunk.index)))
  ];
  const processed = /* @__PURE__ */ new Set();
  const volumes = [];
  let complete = false;
  const copyChunk = (chunkIndex) => {
    if (processed.has(chunkIndex) || signal?.aborted) {
      return;
    }
    const data = reader.getChunkData(chunkIndex);
    if (!data) {
      return;
    }
    chunksPerBox.forEach((chunks, boxIndex) => {
      const chunk = chunks.find((candidate) => candidate.index === chunkIndex);
      if (chunk) {
        copyChunkIntoDenseRoi(volumes[boxIndex], { ...chunk, data });
      }
    });
    processed.add(chunkIndex);
  };
  const publishProgress = () => {
    onProgress?.({
      loaded: processed.size,
      total: requiredIndices.length
    });
  };
  const unsubscribe = reader.subscribeToChunkLoads((chunkIndex) => {
    if (requiredIndices.includes(chunkIndex)) {
      copyChunk(chunkIndex);
      publishProgress();
    }
  });
  try {
    voxelBoxes.forEach((box) => volumes.push(packDenseRoiVolume([], box)));
    requiredIndices.forEach(copyChunk);
    publishProgress();
    reader.updateConsumerDemand(consumerID, requiredIndices, {
      priority,
      retention: "stream"
    });
    await reader.ensureChunksLoaded(requiredIndices, signal, {
      priority,
      retention: "stream"
    });
    signal?.throwIfAborted();
    requiredIndices.forEach(copyChunk);
    const missingIndex = requiredIndices.find(
      (chunkIndex) => !processed.has(chunkIndex)
    );
    if (missingIndex !== void 0) {
      throw new Error(
        `Tooth ROI chunk ${missingIndex} is not loaded after ensureChunksLoaded`
      );
    }
    complete = true;
    return volumes;
  } catch (error) {
    if (!signal?.aborted) {
      releaseToothRoiDemand(reader, consumerID);
    }
    throw error;
  } finally {
    unsubscribe();
    if (!complete) {
      volumes.forEach((volume) => volume.release());
    }
  }
};
const prefetchStableToothRoi = async ({
  consumerID = TOOTH_PREFETCH_CONSUMER_ID,
  onProgress,
  priority = "interactive",
  reader,
  signal,
  voxelBox
}) => {
  const requiredIndices = roiChunkIndices(reader, voxelBox);
  const requiredIndexSet = new Set(requiredIndices);
  const total = requiredIndices.length;
  const processed = new Set(requiredIndices.filter((index) => reader.isChunkLoaded(index)));
  const countLoaded = () => processed.size;
  const outcome = (reason) => ({
    loaded: countLoaded(),
    reason,
    total
  });
  onProgress?.({ loaded: countLoaded(), total });
  if (countLoaded() === total) {
    return outcome("complete");
  }
  if (signal?.aborted) {
    return outcome("aborted");
  }
  const cleanups = [];
  try {
    const reason = await new Promise((resolve) => {
      cleanups.push(
        reader.subscribeToChunkLoads((chunkIndex) => {
          if (!requiredIndexSet.has(chunkIndex)) {
            return;
          }
          processed.add(chunkIndex);
          onProgress?.({ loaded: countLoaded(), total });
          if (countLoaded() === total) {
            resolve("complete");
          }
        })
      );
      if (signal) {
        const onAbort = () => resolve("aborted");
        signal.addEventListener("abort", onAbort, { once: true });
        cleanups.push(() => signal.removeEventListener("abort", onAbort));
      }
      reader.updateConsumerDemand(consumerID, requiredIndices, {
        priority,
        retention: "stream"
      });
      if (countLoaded() === total) {
        resolve("complete");
      }
    });
    return outcome(reason);
  } finally {
    cleanups.forEach((cleanup) => cleanup());
    releaseToothRoiDemand(reader, consumerID);
  }
};

const MAX_TILE_VOXELS = 8 * 1024 * 1024;
const MAX_RASTER_SIDE_PX = 4096;
const MAX_TILE_SIDE_PX = 128;
let nextConsumerID = 0;
const voxelCount = (box) => (box.maxExclusive.x - box.minInclusive.x) * (box.maxExclusive.y - box.minInclusive.y) * (box.maxExclusive.z - box.minInclusive.z);
const renderFullHeadMPRViewWithReader = async ({
  heightPx,
  opaque = true,
  rasteriser,
  reader,
  signal,
  view,
  widthPx
}) => {
  if (![widthPx, heightPx].every(
    (size) => Number.isInteger(size) && size > 0 && size <= MAX_RASTER_SIDE_PX
  )) {
    throw new Error("Invalid saved MPR raster size");
  }
  signal?.throwIfAborted();
  const layout = {
    centerPatientMm: view.centerMm,
    height: heightPx,
    mmPerPixelU: view.widthMm / widthPx,
    mmPerPixelV: view.heightMm / heightPx,
    normal: view.normal,
    uAxis: view.uAxis,
    vAxis: view.vAxis,
    width: widthPx
  };
  const makeOpaque = (data) => {
    if (opaque) {
      for (let offset = 0; offset < data.length; offset += 4) {
        const alpha = data[offset + 3] / 255;
        data[offset] *= alpha;
        data[offset + 1] *= alpha;
        data[offset + 2] *= alpha;
        data[offset + 3] = 255;
      }
    }
    return data;
  };
  if (rasteriser) {
    const raster = await rasteriser({ layout, signal, slabOffsetsMm: view.sampleOffsetsMm, window: view.window });
    signal?.throwIfAborted();
    if (!raster) {
      throw new Error("Failed to render the saved MPR view");
    }
    return { height: heightPx, pixels: makeOpaque(raster.data), scaleMmPerPixel: layout.mmPerPixelU, width: widthPx };
  }
  const consumerID = `full-head-mpr-saved-view-${++nextConsumerID}`;
  const pixels = new Uint8ClampedArray(widthPx * heightPx * 4);
  const renderRegion = async (region) => {
    signal?.throwIfAborted();
    const regionLayout = {
      ...layout,
      centerPatientMm: pixelCenterToPatient(
        layout,
        region.x + region.width / 2 - 0.5,
        region.y + region.height / 2 - 0.5
      ),
      height: region.height,
      width: region.width
    };
    const voxelBox = buildToothSampleRoi({
      geometry: reader.geometry,
      paddingMm: Math.max(...view.sampleOffsetsMm.map(Math.abs)),
      patientPoints: [0, region.width - 1].flatMap(
        (x) => [0, region.height - 1].map(
          (y) => pixelCenterToPatient(regionLayout, x, y)
        )
      )
    });
    if (!voxelBox) {
      return;
    }
    if ((voxelCount(voxelBox) > MAX_TILE_VOXELS || region.width * region.height > MAX_TILE_SIDE_PX ** 2) && (region.width > 1 || region.height > 1)) {
      const horizontal = region.width >= region.height;
      const firstSize = Math.floor(
        (horizontal ? region.width : region.height) / 2
      );
      await renderRegion({
        ...region,
        ...horizontal ? { width: firstSize } : { height: firstSize }
      });
      await renderRegion({
        ...region,
        ...horizontal ? { width: region.width - firstSize, x: region.x + firstSize } : { height: region.height - firstSize, y: region.y + firstSize }
      });
      return;
    }
    if (voxelCount(voxelBox) > MAX_TILE_VOXELS) {
      throw new Error("Saved MPR sampling region exceeds the memory budget");
    }
    const volume = await loadStableToothRoi({
      consumerID,
      priority: "visible",
      reader,
      signal,
      voxelBox
    });
    try {
      signal?.throwIfAborted();
      const raster = renderToothSlice({
        geometry: reader.geometry,
        layout: regionLayout,
        signal,
        slabMode: "mean",
        slabOffsetsMm: view.sampleOffsetsMm,
        volume,
        window: view.window
      });
      if (!raster) {
        throw new Error("Failed to render the saved MPR view");
      }
      for (let row = 0; row < region.height; row++) {
        pixels.set(
          raster.data.subarray(
            row * region.width * 4,
            (row + 1) * region.width * 4
          ),
          ((region.y + row) * widthPx + region.x) * 4
        );
      }
    } finally {
      volume.release();
    }
    await yieldBetweenRenderPasses(signal);
  };
  try {
    await renderRegion({ height: heightPx, width: widthPx, x: 0, y: 0 });
    signal?.throwIfAborted();
    return {
      height: heightPx,
      pixels: makeOpaque(pixels),
      scaleMmPerPixel: layout.mmPerPixelU,
      width: widthPx
    };
  } finally {
    releaseToothRoiDemand(reader, consumerID);
  }
};
const createFullHeadMPRSliceRasteriser = (reader) => {
  const state = { volume: tryAcquireFullHeadMPRGpuVolume(reader) };
  const release = () => {
    state.volume?.release();
    state.volume = null;
  };
  const render = async (request) => {
    request.signal?.throwIfAborted();
    if (state.volume) {
      try {
        return await renderGpuSlice({ ...request, reader, volume: state.volume });
      } catch (error) {
        if (!(error instanceof VolumeGpuError)) {
          throw error;
        }
        release();
      }
    }
    const { layout, signal, slabOffsetsMm, window } = request;
    const raster = await renderFullHeadMPRViewWithReader({
      heightPx: layout.height,
      opaque: false,
      reader,
      signal,
      view: {
        centerMm: layout.centerPatientMm,
        heightMm: layout.height * layout.mmPerPixelV,
        normal: layout.normal,
        projection: "axial",
        sampleOffsetsMm: slabOffsetsMm,
        sampling: slabOffsetsMm.length > 1 ? "mean-slab" : "native",
        thicknessMm: Math.max(...slabOffsetsMm) - Math.min(...slabOffsetsMm),
        uAxis: layout.uAxis,
        vAxis: layout.vAxis,
        volumeFileID: reader.volumeFileID,
        widthMm: layout.width * layout.mmPerPixelU,
        window
      },
      widthPx: layout.width
    });
    reader.metrics.incrementCounter("cpuFrameCount");
    return { data: raster.pixels, height: raster.height, width: raster.width };
  };
  return { release, render };
};
const renderFullHeadMPRView = async ({
  heightPx,
  signal,
  thicknessMm,
  view,
  widthPx
}) => {
  const reader = await getSharedDCCTVolumeReader(view.volumeFileID, signal);
  try {
    const displayView = thicknessMm === void 0 ? view : resolveFullHeadMPRViewSampling({
      spacing: reader.geometry.spacing,
      thickness: { kind: "slab", thicknessMm },
      view
    });
    if (!displayView) {
      throw new Error("Failed to render the saved MPR view");
    }
    const rasteriser = createFullHeadMPRSliceRasteriser(reader);
    try {
      const raster = await renderFullHeadMPRViewWithReader({
        heightPx,
        rasteriser: rasteriser.render,
        reader,
        signal,
        view: displayView,
        widthPx
      });
      return { ...raster, view: displayView };
    } finally {
      rasteriser.release();
    }
  } finally {
    reader.release();
  }
};

const resolveToothPaneSampling = ({
  normal,
  overrideThicknessMm,
  projection,
  restoredView,
  spacing
}) => {
  if (overrideThicknessMm === void 0 && restoredView && projection === restoredView.projection) {
    return {
      effectiveThicknessMm: restoredView.thicknessMm,
      offsetsMm: [...restoredView.sampleOffsetsMm],
      sampling: restoredView.sampling
    };
  }
  const slabThicknessMm = overrideThicknessMm ?? (restoredView?.sampling === "mean-slab" ? restoredView.thicknessMm : void 0);
  const sampling = resolveSlabSampling({
    configuredMaxSlabThicknessMm: Math.max(CONFIGURED_MAX_SLAB_THICKNESS_MM, slabThicknessMm ?? 0),
    normal,
    spacing,
    thickness: slabThicknessMm === void 0 ? { kind: "native" } : { kind: "slab", thicknessMm: slabThicknessMm }
  });
  return sampling && {
    ...sampling,
    sampling: slabThicknessMm === void 0 ? "native" : "mean-slab"
  };
};

const UNIT_BOX = new Box3(new Vector3(0, 0, 0), new Vector3(1, 1, 1));
class SliceViewportScene {
  constructor(placeholderTexture, window) {
    this.geometry = new PlaneGeometry(1, 1);
    this.slabSliceMaterial = null;
    this.adjustedImageMaterial = null;
    this.imageOptions = null;
    this.cpuImageData = null;
    this.cpuCanvas = null;
    this.cpuCanvasDirty = true;
    this.placeholderTexture = placeholderTexture;
    this.sliceMaterial = new SliceMaterial(
      placeholderTexture,
      UNIT_BOX,
      window.ww,
      window.wc
    );
    this.sliceMesh = new Mesh(this.geometry, this.sliceMaterial);
    this.imageTexture = new DataTexture(
      new Uint8Array([0, 0, 0, 255]),
      1,
      1,
      RGBAFormat,
      UnsignedByteType
    );
    this.imageTexture.minFilter = LinearFilter;
    this.imageTexture.magFilter = LinearFilter;
    this.imageMaterial = new MeshBasicMaterial({
      map: this.imageTexture,
      side: DoubleSide,
      toneMapped: false
    });
    this.imageMesh = new Mesh(this.geometry, this.imageMaterial);
    this.sliceMesh.visible = false;
    this.imageMesh.visible = false;
  }
  setLayer(layer) {
    this.sliceMesh.layers.set(layer);
    this.imageMesh.layers.set(layer);
  }
  // Camera maps world [0,width]x[0,height] to the scissor region; the meshes
  // fill exactly that world rect.
  setShape(width, height) {
    this.sliceMesh.scale.set(width, height, 1);
    this.sliceMesh.position.set(width / 2, height / 2, 0);
    this.imageMesh.scale.set(width, height, 1);
    this.imageMesh.position.set(width / 2, height / 2, 0);
  }
  showGpu(texture, matrix, window, minimumWindowWidth, textureBlocks) {
    const windowWidth = Math.max(minimumWindowWidth, window.ww);
    this.sliceMaterial.setVolume(texture, textureBlocks);
    this.sliceMaterial.uniforms.xyztoijk.value = matrix;
    this.sliceMaterial.uniforms.ww.value = windowWidth;
    this.sliceMaterial.uniforms.wmin.value = window.wc - windowWidth / 2;
    this.sliceMesh.material = this.sliceMaterial;
    this.sliceMesh.visible = true;
    this.imageMesh.visible = false;
  }
  // Tooth panes walk a slab of samples per fragment, which is a different compiled
  // program; every other caller stays on the single-sample one.
  setSlab(slab) {
    const material = this.slabSliceMaterial ?? (this.slabSliceMaterial = new SliceMaterial(
      this.placeholderTexture,
      UNIT_BOX,
      void 0,
      void 0,
      { slab: true }
    ));
    material.setVolume(
      this.sliceMaterial.uniforms.volume.value,
      this.sliceMaterial.getVolumeBlocks()
    );
    material.uniforms.xyztoijk.value = this.sliceMaterial.uniforms.xyztoijk.value;
    material.uniforms.ww.value = this.sliceMaterial.uniforms.ww.value;
    material.uniforms.wmin.value = this.sliceMaterial.uniforms.wmin.value;
    material.setSlab(slab);
    this.sliceMesh.material = material;
  }
  // A CPU tooth pane rasterises only the rect the camera shows, so its image quad
  // covers that rect instead of the whole raster. Pane pixels grow down the screen
  // and the quad is positioned by its centre.
  setImageRect(rect) {
    this.imageMesh.scale.set(rect.widthPx, rect.heightPx, 1);
    this.imageMesh.position.set(
      rect.xPx + rect.widthPx / 2,
      rect.yPx + rect.heightPx / 2,
      0
    );
  }
  showCpu(imageData) {
    this.cpuImageData = imageData;
    this.cpuCanvasDirty = true;
    this.sliceMesh.material = this.sliceMaterial;
    this.imageTexture.dispose();
    this.imageTexture = new DataTexture(
      imageData.data,
      imageData.width,
      imageData.height,
      RGBAFormat,
      UnsignedByteType
    );
    this.imageTexture.minFilter = LinearFilter;
    this.imageTexture.magFilter = LinearFilter;
    this.imageTexture.needsUpdate = true;
    this.imageMaterial.map = this.imageTexture;
    this.imageMaterial.needsUpdate = true;
    if (this.adjustedImageMaterial) {
      this.adjustedImageMaterial.uniforms.dataTexture.value = this.imageTexture;
    }
    this.applyImageOptions();
    this.imageMesh.visible = true;
    this.sliceMesh.visible = false;
  }
  applyImageOptions() {
    const imageData = this.cpuImageData;
    if (!imageData) {
      return;
    }
    if (this.imageOptions) {
      const material = this.adjustedImageMaterial ?? (this.adjustedImageMaterial = new RasterImageMaterial(
        this.imageTexture,
        imageData.width,
        imageData.height,
        new Matrix3()
      ));
      material.uniforms.dataTexture.value = this.imageTexture;
      material.uniforms.resolution.value.set(imageData.width, imageData.height);
      material.uniforms.brightness.value = this.imageOptions.brightness;
      material.uniforms.contrast.value = this.imageOptions.contrast;
      material.uniforms.invert.value = Number(this.imageOptions.invert);
      material.uniforms.sharpness.value = this.imageOptions.sharpness;
      this.imageMesh.material = material;
    } else {
      this.imageMesh.material = this.imageMaterial;
    }
  }
  hideAll() {
    this.sliceMesh.visible = false;
    this.imageMesh.visible = false;
  }
  clearFrame() {
    this.hideAll();
    this.cpuImageData = null;
    if (this.cpuCanvas) {
      this.cpuCanvas.width = 0;
      this.cpuCanvas.height = 0;
      this.cpuCanvas = null;
    }
    this.cpuCanvasDirty = true;
    this.imageTexture.dispose();
    this.imageTexture.image = new ImageData(1, 1);
    this.sliceMaterial.setVolume(this.placeholderTexture);
    this.slabSliceMaterial?.setVolume(this.placeholderTexture);
  }
  setImageOptions(options) {
    this.imageOptions = options ? { ...options } : null;
    this.cpuCanvasDirty = true;
    this.applyImageOptions();
  }
  drawCpu({
    camera,
    context,
    rect
  }) {
    const { heightPx, leftPx, topPx, widthPx } = rect;
    context.clearRect(leftPx, topPx, widthPx, heightPx);
    const imageData = this.cpuImageData;
    if (!imageData || !this.imageMesh.visible) {
      return;
    }
    const canvas = this.cpuCanvas ?? (this.cpuCanvas = document.createElement("canvas"));
    const sourceContext = canvas.getContext("2d");
    if (!sourceContext) {
      return;
    }
    if (this.cpuCanvasDirty) {
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      sourceContext.putImageData(this.adjustCpuImage(imageData), 0, 0);
      this.cpuCanvasDirty = false;
    }
    this.imageMesh.updateWorldMatrix(true, false);
    camera.updateWorldMatrix(true, false);
    const point = (x, y) => {
      const projected = new Vector3(x, y, 0).applyMatrix4(this.imageMesh.matrixWorld).project(camera);
      return {
        x: leftPx + (projected.x + 1) * widthPx / 2,
        y: topPx + (1 - projected.y) * heightPx / 2
      };
    };
    const origin = point(-0.5, -0.5);
    const horizontal = point(0.5, -0.5);
    const vertical = point(-0.5, 0.5);
    context.save();
    context.beginPath();
    context.rect(leftPx, topPx, widthPx, heightPx);
    context.clip();
    context.transform(
      (horizontal.x - origin.x) / canvas.width,
      (horizontal.y - origin.y) / canvas.width,
      (vertical.x - origin.x) / canvas.height,
      (vertical.y - origin.y) / canvas.height,
      origin.x,
      origin.y
    );
    context.drawImage(canvas, 0, 0);
    context.restore();
  }
  adjustCpuImage(imageData) {
    const options = this.imageOptions;
    if (!options) {
      return imageData;
    }
    const { brightness, contrast, invert, sharpness } = options;
    const { data, height, width } = imageData;
    const adjusted = new Uint8ClampedArray(data.length);
    for (let y = 0; y < height; y++) {
      const row = y * width * 4;
      const above = Math.max(0, y - 1) * width * 4;
      const below = Math.min(height - 1, y + 1) * width * 4;
      for (let x = 0; x < width; x++) {
        const column = x * 4;
        const left = Math.max(0, x - 1) * 4;
        const right = Math.min(width - 1, x + 1) * 4;
        for (let channel = 0; channel < 4; channel++) {
          const index = row + column + channel;
          const value = data[index];
          const neighbours = sharpness === 0 ? 0 : data[above + left + channel] + data[above + column + channel] + data[above + right + channel] + data[row + left + channel] + data[row + right + channel] + data[below + left + channel] + data[below + column + channel] + data[below + right + channel];
          const sharpened = value + sharpness * (value * 8 - neighbours);
          if (channel === 3) {
            adjusted[index] = sharpened;
            continue;
          }
          const brightened = sharpened / 255 + brightness;
          const contrasted = contrast > 0 ? (brightened - 0.5) / (1 - contrast) + 0.5 : (brightened - 0.5) * (1 + contrast) + 0.5;
          adjusted[index] = (invert ? 1 - contrasted : contrasted) * 255;
        }
      }
    }
    return new ImageData(adjusted, width, height);
  }
  dispose() {
    this.cpuImageData = null;
    this.cpuCanvas = null;
    this.geometry.dispose();
    this.sliceMaterial.dispose();
    this.slabSliceMaterial?.dispose();
    this.imageMaterial.dispose();
    if (this.adjustedImageMaterial) {
      this.adjustedImageMaterial.dispose();
    } else {
      this.imageTexture.dispose();
    }
  }
}

const VALID_TOOTH_ISO_NUMBERS = /* @__PURE__ */ new Set([
  11,
  12,
  13,
  14,
  15,
  16,
  17,
  18,
  21,
  22,
  23,
  24,
  25,
  26,
  27,
  28,
  31,
  32,
  33,
  34,
  35,
  36,
  37,
  38,
  41,
  42,
  43,
  44,
  45,
  46,
  47,
  48,
  51,
  52,
  53,
  54,
  55,
  61,
  62,
  63,
  64,
  65,
  71,
  72,
  73,
  74,
  75,
  81,
  82,
  83,
  84,
  85
]);
const MIN_CROWN_ROOT_LENGTH = 1e-6;
const MIN_MESIO_DISTAL_RESIDUAL_LENGTH = 1e-4;
const MIN_VESTIBULO_ORAL_RESIDUAL_LENGTH = 1e-6;
const QUADRANT_SIGNS = {
  1: [-1, 1, 1],
  2: [1, 1, 1],
  3: [1, 1, -1],
  4: [-1, 1, -1]
};
const isFiniteVec3$1 = (vector) => Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
const scale$1 = (vector, factor) => ({
  x: vector.x * factor,
  y: vector.y * factor,
  z: vector.z * factor
});
const subtract$1 = (left, right) => ({
  x: left.x - right.x,
  y: left.y - right.y,
  z: left.z - right.z
});
const dot$2 = (left, right) => left.x * right.x + left.y * right.y + left.z * right.z;
const cross$2 = (left, right) => ({
  x: left.y * right.z - left.z * right.y,
  y: left.z * right.x - left.x * right.z,
  z: left.x * right.y - left.y * right.x
});
const length$2 = (vector) => Math.sqrt(dot$2(vector, vector));
const normalize = (vector) => {
  const vectorLength = length$2(vector);
  if (!Number.isFinite(vectorLength) || vectorLength === 0) {
    return null;
  }
  return scale$1(vector, 1 / vectorLength);
};
const projectPerpendicular = (vector, unitAxis) => subtract$1(vector, scale$1(unitAxis, dot$2(vector, unitAxis)));
const isValidToothIso = (iso) => VALID_TOOTH_ISO_NUMBERS.has(iso);
const resolveToothQuadrant = (iso) => {
  if (!isValidToothIso(iso)) {
    return null;
  }
  const sourceQuadrant = Math.floor(iso / 10);
  const quadrant = sourceQuadrant > 4 ? sourceQuadrant - 4 : sourceQuadrant;
  return quadrant;
};
const resolveToothBasis = ({
  anatomicalBasis,
  iso
}) => {
  const quadrant = resolveToothQuadrant(iso);
  if (!quadrant) {
    return null;
  }
  const rawMd = anatomicalBasis.MesioDistal;
  const rawVo = anatomicalBasis.VestibuloOral;
  const rawCr = anatomicalBasis.Axial;
  if (!isFiniteVec3$1(rawMd) || !isFiniteVec3$1(rawVo) || !isFiniteVec3$1(rawCr)) {
    return null;
  }
  if (length$2(rawMd) === 0 || length$2(rawVo) === 0 || length$2(rawCr) === 0) {
    return null;
  }
  const [mdSign, voSign, crSign] = QUADRANT_SIGNS[quadrant];
  const signedMd = scale$1(rawMd, mdSign);
  const signedVo = scale$1(rawVo, voSign);
  const signedCr = scale$1(rawCr, crSign);
  if (length$2(signedCr) <= MIN_CROWN_ROOT_LENGTH) {
    return null;
  }
  const cr = normalize(signedCr);
  const mdSource = normalize(signedMd);
  if (!cr || !mdSource) {
    return null;
  }
  const mdResidual = projectPerpendicular(mdSource, cr);
  if (length$2(mdResidual) <= MIN_MESIO_DISTAL_RESIDUAL_LENGTH) {
    return null;
  }
  const md = normalize(mdResidual);
  if (!md) {
    return null;
  }
  const vo = normalize(cross$2(cr, md));
  if (!vo || !isFiniteVec3$1(vo)) {
    return null;
  }
  const voResidual = projectPerpendicular(signedVo, cr);
  if (length$2(voResidual) <= MIN_VESTIBULO_ORAL_RESIDUAL_LENGTH) {
    return null;
  }
  const voReference = normalize(voResidual);
  if (!voReference || dot$2(vo, voReference) <= 0) {
    return null;
  }
  if (dot$2(cross$2(md, vo), cr) <= 0) {
    return null;
  }
  return {
    md,
    vo,
    cr,
    crownToRootSign: crSign > 0 ? 1 : -1,
    sideSign: quadrant === 1 || quadrant === 4 ? 1 : -1
  };
};

const TOOTH_FOCUS_AXES = [
  "MesioDistal",
  "VestibuloOral",
  "Axial"
];
const SLICE_ORIENTATION_ORDER = [
  "axial",
  "vestibuloOral",
  "mesioDistal"
];
const SLICE_ORIENTATION_TO_NORMAL_AXIS = {
  axial: "Axial",
  mesioDistal: "MesioDistal",
  vestibuloOral: "VestibuloOral"
};
const TOOTH_FOCUS_AXIS_BASE_HALF_EXTENT_MM = {
  present: {
    MesioDistal: 5.5,
    VestibuloOral: 6.5,
    Axial: 9
  },
  missing: {
    MesioDistal: 2.75,
    VestibuloOral: 3.75,
    Axial: 6.5
  }
};
const TOOTH_FOCUS_AXIS_MAX_HALF_EXTENT_MM = {
  present: {
    MesioDistal: 7,
    VestibuloOral: 8,
    Axial: 12
  },
  missing: {
    MesioDistal: 3.75,
    VestibuloOral: 4.75,
    Axial: 8
  }
};
const TOOTH_FOCUS_AXIS_CONTEXT_MARGIN_MM = 0.75;
const TOOTH_FOCUS_AXIS_SIDE_CONTEXT_MAX_MM = 1.5;
const TOOTH_FOCUS_AXIS_PRESENT_CONTEXT_MAX_MM = 0.9;
const TOOTH_FOCUS_AXIS_PRESENT_AXIAL_MARGIN_MM = 0.6;
const TOOTH_FOCUS_DEFAULT_SLICE_HEIGHTS = {
  axial: [0.25, 0.5, 0.75],
  mesioDistal: [0.35],
  vestibuloOral: [0.3, 0.46]
};
const finiteNumber = (value) => typeof value === "number" && Number.isFinite(value) ? value : null;
const clamp$1 = (value, min, max) => Math.min(max, Math.max(min, value));
const normalizeVector = (vector) => {
  const length = Math.hypot(vector.x, vector.y, vector.z) || 1;
  return {
    x: vector.x / length,
    y: vector.y / length,
    z: vector.z / length
  };
};
const normalizeToothBasis = (anatomicalBasis) => ({
  MesioDistal: normalizeVector(anatomicalBasis.MesioDistal),
  VestibuloOral: normalizeVector(anatomicalBasis.VestibuloOral),
  Axial: normalizeVector(anatomicalBasis.Axial)
});
const dotVector = (left, right) => left.x * right.x + left.y * right.y + left.z * right.z;
const boxCorners$1 = (box) => [
  { x: box.min.x, y: box.min.y, z: box.min.z },
  { x: box.min.x, y: box.min.y, z: box.max.z },
  { x: box.min.x, y: box.max.y, z: box.min.z },
  { x: box.min.x, y: box.max.y, z: box.max.z },
  { x: box.max.x, y: box.min.y, z: box.min.z },
  { x: box.max.x, y: box.min.y, z: box.max.z },
  { x: box.max.x, y: box.max.y, z: box.min.z },
  { x: box.max.x, y: box.max.y, z: box.max.z }
];
const cloneAxisBounds = (bounds) => ({ ...bounds });
const cloneLocalBounds = (bounds) => ({
  Axial: cloneAxisBounds(bounds.Axial),
  MesioDistal: cloneAxisBounds(bounds.MesioDistal),
  VestibuloOral: cloneAxisBounds(bounds.VestibuloOral)
});
const projectBoxToToothLocalBounds = (box, origin, anatomicalBasis) => {
  if (!box) {
    return null;
  }
  const basis = normalizeToothBasis(anatomicalBasis);
  const initialBounds = {
    MesioDistal: { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY },
    VestibuloOral: { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY },
    Axial: { min: Number.POSITIVE_INFINITY, max: Number.NEGATIVE_INFINITY }
  };
  return boxCorners$1(box).reduce((bounds, corner) => {
    const relative = {
      x: corner.x - origin.x,
      y: corner.y - origin.y,
      z: corner.z - origin.z
    };
    TOOTH_FOCUS_AXES.forEach((axis) => {
      const coordinate = dotVector(relative, basis[axis]);
      bounds[axis].min = Math.min(bounds[axis].min, coordinate);
      bounds[axis].max = Math.max(bounds[axis].max, coordinate);
    });
    return bounds;
  }, initialBounds);
};
const expandInnerBoundsWithOuterContext = (innerBounds, outerBounds) => expandInnerBoundsWithOuterContextLimit(
  innerBounds,
  outerBounds,
  TOOTH_FOCUS_AXIS_SIDE_CONTEXT_MAX_MM
);
const expandInnerBoundsWithOuterContextLimit = (innerBounds, outerBounds, maxContextMm) => {
  if (!innerBounds && !outerBounds) {
    return null;
  }
  if (!innerBounds) {
    return outerBounds ?? null;
  }
  if (!outerBounds) {
    return innerBounds;
  }
  return {
    min: innerBounds.min - Math.min(maxContextMm, Math.max(0, innerBounds.min - outerBounds.min)),
    max: innerBounds.max + Math.min(maxContextMm, Math.max(0, outerBounds.max - innerBounds.max))
  };
};
const expandAxisBounds = (bounds, marginMm) => {
  if (!bounds) {
    return null;
  }
  return {
    min: bounds.min - marginMm,
    max: bounds.max + marginMm
  };
};
const clampToothAxisBounds = (isMissingTooth, axis, bounds) => {
  const maxHalfExtent = TOOTH_FOCUS_AXIS_MAX_HALF_EXTENT_MM[isMissingTooth ? "missing" : "present"][axis];
  return {
    min: Math.max(bounds.min, -maxHalfExtent),
    max: Math.min(bounds.max, maxHalfExtent)
  };
};
const clampLocalBounds = (isMissingTooth, bounds) => ({
  Axial: clampToothAxisBounds(isMissingTooth, "Axial", bounds.Axial),
  MesioDistal: clampToothAxisBounds(
    isMissingTooth,
    "MesioDistal",
    bounds.MesioDistal
  ),
  VestibuloOral: clampToothAxisBounds(
    isMissingTooth,
    "VestibuloOral",
    bounds.VestibuloOral
  )
});
const localToWorldPoint = (origin, anatomicalBasis, local) => ({
  x: origin.x + anatomicalBasis.MesioDistal.x * local.MesioDistal + anatomicalBasis.VestibuloOral.x * local.VestibuloOral + anatomicalBasis.Axial.x * local.Axial,
  y: origin.y + anatomicalBasis.MesioDistal.y * local.MesioDistal + anatomicalBasis.VestibuloOral.y * local.VestibuloOral + anatomicalBasis.Axial.y * local.Axial,
  z: origin.z + anatomicalBasis.MesioDistal.z * local.MesioDistal + anatomicalBasis.VestibuloOral.z * local.VestibuloOral + anatomicalBasis.Axial.z * local.Axial
});
const localBoundsToWorldBox = (origin, anatomicalBasis, localBounds) => {
  const basis = normalizeToothBasis(anatomicalBasis);
  const corners = [localBounds.MesioDistal.min, localBounds.MesioDistal.max].flatMap(
    (mesioDistal) => [localBounds.VestibuloOral.min, localBounds.VestibuloOral.max].flatMap(
      (vestibuloOral) => [localBounds.Axial.min, localBounds.Axial.max].map(
        (axial) => localToWorldPoint(origin, basis, {
          Axial: axial,
          MesioDistal: mesioDistal,
          VestibuloOral: vestibuloOral
        })
      )
    )
  );
  const [firstCorner, ...restCorners] = corners;
  return restCorners.reduce(
    (box, point) => ({
      min: {
        x: Math.min(box.min.x, point.x),
        y: Math.min(box.min.y, point.y),
        z: Math.min(box.min.z, point.z)
      },
      max: {
        x: Math.max(box.max.x, point.x),
        y: Math.max(box.max.y, point.y),
        z: Math.max(box.max.z, point.z)
      }
    }),
    {
      min: { ...firstCorner },
      max: { ...firstCorner }
    }
  );
};
const getLegacySliceAxisBounds = (orientation, legacyProjectedBounds) => {
  const normalAxis = SLICE_ORIENTATION_TO_NORMAL_AXIS[orientation];
  if (orientation === "axial") {
    return legacyProjectedBounds.outer?.[normalAxis] ?? legacyProjectedBounds.inner?.[normalAxis] ?? null;
  }
  return legacyProjectedBounds.inner?.[normalAxis] ?? legacyProjectedBounds.outer?.[normalAxis] ?? null;
};
const getSliceDisplacement = (slice, imageIndex, totalImages, isMissingTooth, normalAxisBounds) => {
  const sourceImageCount = totalImages;
  const sourceIndex = finiteNumber(slice.order) ?? imageIndex;
  if (normalAxisBounds && sourceImageCount > 1) {
    const t = clamp$1(sourceIndex / Math.max(sourceImageCount - 1, 1), 0, 1);
    const span = normalAxisBounds.max - normalAxisBounds.min;
    if (slice.orientation === "axial") {
      return normalAxisBounds.max - t * span;
    }
    return normalAxisBounds.min + t * span;
  }
  if (normalAxisBounds && sourceImageCount <= 1) {
    return (normalAxisBounds.min + normalAxisBounds.max) / 2;
  }
  const defaultHeights = TOOTH_FOCUS_DEFAULT_SLICE_HEIGHTS[slice.orientation];
  if (imageIndex < defaultHeights.length && totalImages <= defaultHeights.length) {
    const normalAxis = SLICE_ORIENTATION_TO_NORMAL_AXIS[slice.orientation];
    const baseHalfExtentMm = TOOTH_FOCUS_AXIS_BASE_HALF_EXTENT_MM[isMissingTooth ? "missing" : "present"][normalAxis];
    const height = defaultHeights[imageIndex];
    return -baseHalfExtentMm + 2 * baseHalfExtentMm * height;
  }
  const positionMm = finiteNumber(slice.positionMm);
  if (positionMm !== null) {
    return positionMm;
  }
  const strideMm = finiteNumber(slice.strideMm) ?? 0;
  if (strideMm !== 0) {
    const centerIndex = (sourceImageCount - 1) / 2;
    return (sourceIndex - centerIndex) * strideMm;
  }
  return 0;
};
const buildSliceDescriptors = (slices, legacyProjectedBounds, isMissingTooth) => SLICE_ORIENTATION_ORDER.flatMap((orientation) => {
  const orientationSlices = (slices ?? []).filter((slice) => slice.orientation === orientation).sort((left, right) => (left.order ?? 0) - (right.order ?? 0));
  const normalAxisBounds = getLegacySliceAxisBounds(
    orientation,
    legacyProjectedBounds
  );
  return orientationSlices.map((slice, imageIndex) => {
    const strideMm = finiteNumber(slice.strideMm) ?? 0;
    const thicknessMm = finiteNumber(slice.thicknessMm);
    const resolvedThicknessMm = thicknessMm !== null && thicknessMm > 0 ? thicknessMm : Math.abs(strideMm) || 0.6;
    return {
      ...slice,
      displacementMm: getSliceDisplacement(
        slice,
        imageIndex,
        orientationSlices.length,
        isMissingTooth,
        normalAxisBounds
      ),
      normalAxis: SLICE_ORIENTATION_TO_NORMAL_AXIS[orientation],
      sourceImageCount: orientationSlices.length,
      sourceIndex: finiteNumber(slice.order) ?? imageIndex,
      thicknessMm: resolvedThicknessMm
    };
  });
});
const expandLocalBoundsBySliceDescriptors = (bounds, descriptors) => descriptors.reduce((currentBounds, descriptor) => {
  const normalAxis = descriptor.normalAxis;
  const sliceMin = descriptor.displacementMm - descriptor.thicknessMm / 2 - TOOTH_FOCUS_AXIS_CONTEXT_MARGIN_MM;
  const sliceMax = descriptor.displacementMm + descriptor.thicknessMm / 2 + TOOTH_FOCUS_AXIS_CONTEXT_MARGIN_MM;
  return {
    ...currentBounds,
    [normalAxis]: {
      min: Math.min(currentBounds[normalAxis].min, sliceMin),
      max: Math.max(currentBounds[normalAxis].max, sliceMax)
    }
  };
}, cloneLocalBounds(bounds));
const buildPreferredLocalBoundsByOrientation = (legacyProjectedBounds, isMissingTooth) => {
  if (!legacyProjectedBounds.inner && !legacyProjectedBounds.outer) {
    return null;
  }
  const contextLimit = isMissingTooth ? TOOTH_FOCUS_AXIS_CONTEXT_MARGIN_MM : TOOTH_FOCUS_AXIS_PRESENT_CONTEXT_MAX_MM;
  const mesioDistalBounds = expandInnerBoundsWithOuterContextLimit(
    legacyProjectedBounds.inner?.MesioDistal,
    legacyProjectedBounds.outer?.MesioDistal,
    contextLimit
  );
  const vestibuloOralBounds = expandInnerBoundsWithOuterContextLimit(
    legacyProjectedBounds.inner?.VestibuloOral,
    legacyProjectedBounds.outer?.VestibuloOral,
    contextLimit
  );
  const axialFocusedBounds = expandAxisBounds(
    legacyProjectedBounds.inner?.Axial ?? legacyProjectedBounds.outer?.Axial,
    isMissingTooth ? TOOTH_FOCUS_AXIS_CONTEXT_MARGIN_MM : TOOTH_FOCUS_AXIS_PRESENT_AXIAL_MARGIN_MM
  );
  const axialOuterBounds = legacyProjectedBounds.outer?.Axial ?? axialFocusedBounds;
  if (!mesioDistalBounds || !vestibuloOralBounds || !axialFocusedBounds) {
    return null;
  }
  return {
    axial: {
      Axial: axialOuterBounds ?? axialFocusedBounds,
      MesioDistal: mesioDistalBounds,
      VestibuloOral: vestibuloOralBounds
    },
    mesioDistal: {
      Axial: axialFocusedBounds,
      VestibuloOral: vestibuloOralBounds
    },
    vestibuloOral: {
      Axial: axialFocusedBounds,
      MesioDistal: mesioDistalBounds
    }
  };
};
const buildBaseLocalBounds = (legacyProjectedBounds, isMissingTooth) => {
  const { inner: innerBounds, outer: outerBounds } = legacyProjectedBounds;
  if (!isMissingTooth && (outerBounds || innerBounds)) {
    const mesioDistalBounds = expandInnerBoundsWithOuterContext(
      innerBounds?.MesioDistal,
      outerBounds?.MesioDistal
    ) ?? outerBounds?.MesioDistal ?? innerBounds?.MesioDistal;
    const vestibuloOralBounds = expandInnerBoundsWithOuterContext(
      innerBounds?.VestibuloOral,
      outerBounds?.VestibuloOral
    ) ?? outerBounds?.VestibuloOral ?? innerBounds?.VestibuloOral;
    const axialBounds = outerBounds?.Axial ?? innerBounds?.Axial;
    if (mesioDistalBounds && vestibuloOralBounds && axialBounds) {
      return {
        bounds: {
          Axial: cloneAxisBounds(axialBounds),
          MesioDistal: cloneAxisBounds(mesioDistalBounds),
          VestibuloOral: cloneAxisBounds(vestibuloOralBounds)
        },
        shouldClampAfterDescriptors: false
      };
    }
  }
  const sourceBounds = outerBounds ?? innerBounds;
  const baseExtents = TOOTH_FOCUS_AXIS_BASE_HALF_EXTENT_MM[isMissingTooth ? "missing" : "present"];
  return {
    bounds: {
      Axial: {
        min: isMissingTooth ? -baseExtents.Axial : Math.min(
          sourceBounds?.Axial.min ?? -baseExtents.Axial,
          -baseExtents.Axial
        ),
        max: isMissingTooth ? baseExtents.Axial : Math.max(
          sourceBounds?.Axial.max ?? baseExtents.Axial,
          baseExtents.Axial
        )
      },
      MesioDistal: {
        min: -baseExtents.MesioDistal,
        max: baseExtents.MesioDistal
      },
      VestibuloOral: {
        min: -baseExtents.VestibuloOral,
        max: baseExtents.VestibuloOral
      }
    },
    shouldClampAfterDescriptors: true
  };
};
const buildToothFocusGeometry = (payload) => {
  const {
    anatomicalBasis,
    innerBox,
    isMissingTooth = false,
    origin,
    outerBox
  } = payload;
  if (!anatomicalBasis) {
    return {
      focusBox: null,
      innerBounds: null,
      localBoundsAfterDescriptors: null,
      localBoundsBeforeDescriptors: null,
      mode: "legacy",
      outerBounds: null,
      preferredLocalBoundsByOrientation: null,
      sliceDescriptors: []
    };
  }
  const outerBounds = projectBoxToToothLocalBounds(
    outerBox,
    origin,
    anatomicalBasis
  );
  const innerBounds = projectBoxToToothLocalBounds(
    innerBox,
    origin,
    anatomicalBasis
  );
  const legacyProjectedBounds = { inner: innerBounds, outer: outerBounds };
  const sliceDescriptors = buildSliceDescriptors(
    payload.slices,
    legacyProjectedBounds,
    isMissingTooth
  );
  const baseLocalBounds = buildBaseLocalBounds(
    legacyProjectedBounds,
    isMissingTooth
  );
  if (!baseLocalBounds) {
    return {
      focusBox: null,
      innerBounds,
      localBoundsAfterDescriptors: null,
      localBoundsBeforeDescriptors: null,
      mode: "legacy",
      outerBounds,
      preferredLocalBoundsByOrientation: buildPreferredLocalBoundsByOrientation(
        legacyProjectedBounds,
        isMissingTooth
      ),
      sliceDescriptors
    };
  }
  const expandedLocalBounds = expandLocalBoundsBySliceDescriptors(
    baseLocalBounds.bounds,
    sliceDescriptors
  );
  const finalLocalBounds = baseLocalBounds.shouldClampAfterDescriptors ? clampLocalBounds(isMissingTooth, expandedLocalBounds) : expandedLocalBounds;
  const mode = sliceDescriptors.length > 0 ? "slice-preview" : outerBounds || innerBounds ? "roi-fallback" : "legacy";
  return {
    focusBox: localBoundsToWorldBox(origin, anatomicalBasis, finalLocalBounds),
    innerBounds,
    localBoundsAfterDescriptors: finalLocalBounds,
    localBoundsBeforeDescriptors: cloneLocalBounds(baseLocalBounds.bounds),
    mode,
    outerBounds,
    preferredLocalBoundsByOrientation: buildPreferredLocalBoundsByOrientation(
      legacyProjectedBounds,
      isMissingTooth
    ),
    sliceDescriptors
  };
};

const CROSSING_TOLERANCE_FACTOR = 2;
const EMPTY_PICK = { grabbed: [], isOnCrossing: false };
const isFinitePositive$1 = (value) => Number.isFinite(value) && value > 0;
const pickToothCrosshairLines = ({
  crosshair,
  heightPx,
  pointerPx,
  tolerancePx,
  widthPx
}) => {
  if (!isFinitePositive$1(widthPx) || !isFinitePositive$1(heightPx) || !isFinitePositive$1(tolerancePx) || !Number.isFinite(pointerPx.x) || !Number.isFinite(pointerPx.y)) {
    return EMPTY_PICK;
  }
  const measured = Object.entries(crosshair).flatMap(([pane, trace]) => {
    if (!trace) {
      return [];
    }
    const deltaX = pointerPx.x - trace.leftFraction * widthPx;
    const deltaY = pointerPx.y - trace.topFraction * heightPx;
    const angleRad = trace.angleDeg * Math.PI / 180;
    return [
      {
        crossingDistancePx: Math.sqrt(deltaX * deltaX + deltaY * deltaY),
        lineDistancePx: Math.abs(
          -Math.sin(angleRad) * deltaX + Math.cos(angleRad) * deltaY
        ),
        pane
      }
    ];
  });
  return {
    grabbed: measured.filter((entry) => entry.lineDistancePx <= tolerancePx).map((entry) => entry.pane),
    isOnCrossing: measured.some(
      (entry) => entry.crossingDistancePx <= tolerancePx * CROSSING_TOLERANCE_FACTOR
    )
  };
};

const isFinitePositive = (value) => Number.isFinite(value) && value > 0;
const paneFractionDeltaToWorldDelta = ({
  frame,
  fromFraction,
  spec,
  toFraction
}) => {
  const deltaU = (toFraction.fractionX - fromFraction.fractionX) * spec.halfUMm * 2;
  const deltaV = (fromFraction.fractionY - toFraction.fractionY) * spec.halfVMm * 2;
  return {
    x: frame.u.x * deltaU + frame.v.x * deltaV,
    y: frame.u.y * deltaU + frame.v.y * deltaV,
    z: frame.u.z * deltaU + frame.v.z * deltaV
  };
};
const letterboxVisibleHeightMm = ({
  halfUMm,
  halfVMm,
  surfaceAspect
}) => {
  const widthMm = halfUMm * 2;
  const heightMm = halfVMm * 2;
  return surfaceAspect >= widthMm / heightMm ? heightMm : widthMm / surfaceAspect;
};
const TOOTH_MPR_VIEW_HEIGHT_MM = 33;
const resolveToothInitialZoom = ({
  spec,
  surfaceAspect
}) => {
  if (!isFinitePositive(surfaceAspect) || !isFinitePositive(spec.halfUMm) || !isFinitePositive(spec.halfVMm)) {
    return 1;
  }
  const rasterHeightMm = letterboxVisibleHeightMm({
    halfUMm: spec.halfUMm,
    halfVMm: spec.halfVMm,
    surfaceAspect
  });
  return rasterHeightMm / TOOTH_MPR_VIEW_HEIGHT_MM;
};
const VOLUME_ZOOM_LIMITS = { maxZoom: 16, minZoom: 0.5 };
const TOOTH_ZOOM_IN_HEADROOM = 8;
const TOOTH_ZOOM_CEILING = 256;
const resolveToothZoomLimits = ({
  initialZoom
}) => isFinitePositive(initialZoom) ? {
  maxZoom: Math.min(
    initialZoom * TOOTH_ZOOM_IN_HEADROOM,
    TOOTH_ZOOM_CEILING
  ),
  minZoom: 1
} : VOLUME_ZOOM_LIMITS;

const MIN_VECTOR_LENGTH = 1e-6;
const dot$1 = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross$1 = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x
});
const length$1 = (vector) => Math.sqrt(dot$1(vector, vector));
const normalizeVec3 = (vector) => {
  const magnitude = length$1(vector);
  if (!Number.isFinite(magnitude) || magnitude < MIN_VECTOR_LENGTH) {
    return null;
  }
  return {
    x: vector.x / magnitude,
    y: vector.y / magnitude,
    z: vector.z / magnitude
  };
};
const rotateVec3AroundUnitAxis = ({
  angleRad,
  axis,
  vector
}) => {
  const cosAngle = Math.cos(angleRad);
  const sinAngle = Math.sin(angleRad);
  const perpendicular = cross$1(axis, vector);
  const alongAxis = dot$1(axis, vector) * (1 - cosAngle);
  return {
    x: vector.x * cosAngle + perpendicular.x * sinAngle + axis.x * alongAxis,
    y: vector.y * cosAngle + perpendicular.y * sinAngle + axis.y * alongAxis,
    z: vector.z * cosAngle + perpendicular.z * sinAngle + axis.z * alongAxis
  };
};
const rotateCrossingPanes = ({
  angleRad,
  cursor,
  frames,
  origin,
  pivot,
  projection
}) => {
  const axis = normalizeVec3(frames[projection].n);
  if (!axis || !Number.isFinite(angleRad)) {
    return null;
  }
  const pivotOffset = {
    x: pivot.x - origin.x,
    y: pivot.y - origin.y,
    z: pivot.z - origin.z
  };
  const rotate = (vector) => rotateVec3AroundUnitAxis({ angleRad, axis, vector });
  return Object.keys(frames).reduce(
    (result, pane) => {
      if (pane === projection) {
        result.frames[pane] = {
          n: frames[pane].n,
          u: rotate(frames[pane].u),
          v: rotate(frames[pane].v)
        };
        result.cursor[pane] = cursor[pane];
        return result;
      }
      const turned = {
        n: rotate(frames[pane].n),
        u: rotate(frames[pane].u),
        v: rotate(frames[pane].v)
      };
      result.frames[pane] = turned;
      result.cursor[pane] = dot$1(pivotOffset, turned.n);
      return result;
    },
    { cursor: {}, frames: {} }
  );
};
const screenSweepAngleRad = ({
  center,
  from,
  to
}) => {
  const fromX = from.x - center.x;
  const fromY = from.y - center.y;
  const toX = to.x - center.x;
  const toY = to.y - center.y;
  if (Math.sqrt(fromX * fromX + fromY * fromY) < MIN_VECTOR_LENGTH || Math.sqrt(toX * toX + toY * toY) < MIN_VECTOR_LENGTH) {
    return 0;
  }
  return Math.atan2(fromX * toY - fromY * toX, fromX * toX + fromY * toY);
};

const TOOTH_PANES = [
  "axial",
  "coronal",
  "sagittal"
];
const TOOTH_PANE_CONTEXT_MARGIN_MM = 1.5;
const TOOTH_PANE_MAX_RASTER_PX = 512;
const MIN_UPRIGHT_AXIS_VERTICALITY = 0.2;
const MIN_HORIZONTAL_RESIDUAL_LENGTH = 1e-4;
const MIN_UPRIGHT_HORIZONTAL_DETERMINANT = 0.01;
const MIN_PANE_NORMAL_DETERMINANT = 1e-3;
const MIN_TRACE_DIRECTION_LENGTH = 1e-6;
const TOOTH_PANE_ORIENTATION = {
  axial: "axial",
  coronal: "vestibuloOral",
  sagittal: "mesioDistal"
};
const add = (left, right) => ({
  x: left.x + right.x,
  y: left.y + right.y,
  z: left.z + right.z
});
const subtract = (left, right) => ({
  x: left.x - right.x,
  y: left.y - right.y,
  z: left.z - right.z
});
const scale = (vector, factor) => ({
  x: vector.x * factor,
  y: vector.y * factor,
  z: vector.z * factor
});
const dot = (left, right) => left.x * right.x + left.y * right.y + left.z * right.z;
const cross = (left, right) => ({
  x: left.y * right.z - left.z * right.y,
  y: left.z * right.x - left.x * right.z,
  z: left.x * right.y - left.y * right.x
});
const length = (vector) => Math.sqrt(dot(vector, vector));
const isFiniteVec3 = (vector) => Number.isFinite(vector.x) && Number.isFinite(vector.y) && Number.isFinite(vector.z);
const boxCorners = (box) => [box.min.x, box.max.x].flatMap(
  (x) => [box.min.y, box.max.y].flatMap(
    (y) => [box.min.z, box.max.z].map((z) => ({ x, y, z }))
  )
);
const resolveUprightAxes = (basis) => {
  if (Math.abs(basis.cr.z) < MIN_UPRIGHT_AXIS_VERTICALITY) {
    return null;
  }
  const mdResidual = { x: basis.md.x, y: basis.md.y, z: 0 };
  const voResidual = { x: basis.vo.x, y: basis.vo.y, z: 0 };
  const mdResidualLength = length(mdResidual);
  const voResidualLength = length(voResidual);
  if (mdResidualLength < MIN_HORIZONTAL_RESIDUAL_LENGTH || voResidualLength < MIN_HORIZONTAL_RESIDUAL_LENGTH) {
    return null;
  }
  const mdH = scale(mdResidual, 1 / mdResidualLength);
  const voH = scale(voResidual, 1 / voResidualLength);
  const determinant = mdH.x * voH.y - mdH.y * voH.x;
  if (Math.abs(determinant) < MIN_UPRIGHT_HORIZONTAL_DETERMINANT) {
    return null;
  }
  return { mdH, voH };
};
const frameFromCameraAxes = ({
  forward,
  up
}) => {
  const forwardLength = length(forward);
  if (!Number.isFinite(forwardLength) || forwardLength < 1e-6) {
    return null;
  }
  const n = scale(forward, 1 / forwardLength);
  const right = cross(up, n);
  const rightLength = length(right);
  if (!Number.isFinite(rightLength) || rightLength < 1e-6) {
    return null;
  }
  const u = scale(right, 1 / rightLength);
  return { n, u, v: cross(n, u) };
};
const resolveToothCrossPaneFrames = ({
  cameraAxes,
  up
}) => {
  const resolveFrame = (pane) => frameFromCameraAxes({
    forward: scale(cameraAxes[TOOTH_PANE_ORIENTATION[pane]], -1),
    up
  });
  const coronal = resolveFrame("coronal");
  const sagittal = resolveFrame("sagittal");
  return coronal && sagittal ? { coronal, sagittal } : null;
};
const resolveToothPaneFrames = ({
  anatomicalBasis,
  basis,
  mode,
  seriesFrames
}) => {
  if (mode === "series") {
    if (!seriesFrames) {
      return null;
    }
    const toPaneFrame = ({ plane }) => ({
      n: cross(plane.uAxis, plane.vAxis),
      u: { ...plane.uAxis },
      v: { ...plane.vAxis }
    });
    return {
      axial: toPaneFrame(seriesFrames.axial),
      coronal: toPaneFrame(seriesFrames.frontal),
      sagittal: toPaneFrame(seriesFrames.sagittal)
    };
  }
  if (mode === "orthogonal") {
    return TOOTH_PANES.reduce((frames, pane) => {
      const axes = ORTHOGONAL_PANE_AXES[pane];
      const u = { x: 0, y: 0, z: 0, [axes.horizontalAxis]: 1 };
      const v = { x: 0, y: 0, z: 0, [axes.verticalAxis]: axes.verticalSign };
      return { ...frames, [pane]: { u, v, n: cross(u, v) } };
    }, {});
  }
  if (mode === "axis") {
    const crownRoot = anatomicalBasis?.Axial ?? scale(basis.cr, basis.crownToRootSign);
    const mesioDistal = anatomicalBasis?.MesioDistal ?? scale(basis.md, -basis.sideSign);
    const vestibuloOral = anatomicalBasis?.VestibuloOral ?? basis.vo;
    const verticalUp2 = scale(crownRoot, basis.crownToRootSign);
    const axial = frameFromCameraAxes({
      forward: scale(verticalUp2, -1),
      up: { x: 0, y: -1, z: 0 }
    });
    const crossPaneFrames2 = resolveToothCrossPaneFrames({
      cameraAxes: { mesioDistal, vestibuloOral },
      up: verticalUp2
    });
    return axial && crossPaneFrames2 ? { axial, ...crossPaneFrames2 } : null;
  }
  const uprightAxes = resolveUprightAxes(basis);
  if (!uprightAxes) {
    return null;
  }
  const verticalUp = { x: 0, y: 0, z: 1 };
  const crossPaneFrames = resolveToothCrossPaneFrames({
    cameraAxes: {
      mesioDistal: scale(uprightAxes.mdH, -basis.sideSign),
      vestibuloOral: uprightAxes.voH
    },
    up: verticalUp
  });
  return crossPaneFrames ? {
    axial: {
      u: { x: 1, y: 0, z: 0 },
      v: { x: 0, y: -1, z: 0 },
      n: { x: 0, y: 0, z: -1 }
    },
    ...crossPaneFrames
  } : null;
};
const resolveToothPaneCenter = ({
  cursor,
  frames,
  origin,
  pane
}) => add(origin, scale(frames[pane].n, cursor[pane]));
const cursorWorldPoint = ({
  cursor,
  frames,
  origin
}) => {
  const [a, b, c] = TOOTH_PANES.map((pane) => frames[pane].n);
  const [da, db, dc] = TOOTH_PANES.map((pane) => cursor[pane]);
  const bc = cross(b, c);
  const determinant = dot(a, bc);
  if (Math.abs(determinant) < MIN_PANE_NORMAL_DETERMINANT) {
    return null;
  }
  const offset = scale(
    add(
      add(scale(bc, da), scale(cross(c, a), db)),
      scale(cross(a, b), dc)
    ),
    1 / determinant
  );
  return isFiniteVec3(offset) ? add(origin, offset) : null;
};
const worldPointToCursor = ({
  frames,
  origin,
  point
}) => {
  if (!isFiniteVec3(point)) {
    return null;
  }
  const offset = subtract(point, origin);
  return {
    axial: dot(offset, frames.axial.n),
    coronal: dot(offset, frames.coronal.n),
    sagittal: dot(offset, frames.sagittal.n)
  };
};
const buildToothPaneLayoutSpec = ({
  origin,
  spacing,
  volumeBoxMm
}) => {
  if (!isFiniteVec3(origin) || !isFiniteVec3(volumeBoxMm.min) || !isFiniteVec3(volumeBoxMm.max) || !isFiniteVec3(spacing) || spacing.x <= 0 || spacing.y <= 0 || spacing.z <= 0) {
    return null;
  }
  const cornerRadiusMm = Math.max(
    ...boxCorners(volumeBoxMm).map(
      (corner) => length(subtract(corner, origin))
    )
  );
  const halfSideMm = cornerRadiusMm + TOOTH_PANE_CONTEXT_MARGIN_MM;
  if (!Number.isFinite(halfSideMm) || halfSideMm <= 0) {
    return null;
  }
  const nativeMmPerPixel = Math.min(spacing.x, spacing.y, spacing.z);
  const mmPerPixel = Math.max(
    nativeMmPerPixel,
    halfSideMm * 2 / TOOTH_PANE_MAX_RASTER_PX
  );
  const sidePx = Math.max(1, Math.ceil(halfSideMm * 2 / mmPerPixel));
  const halfExactMm = sidePx * mmPerPixel / 2;
  return {
    halfUMm: halfExactMm,
    halfVMm: halfExactMm,
    heightPx: sidePx,
    mmPerPixel,
    widthPx: sidePx
  };
};
const buildToothPaneLayout = ({
  center,
  frame,
  spec
}) => ({
  centerPatientMm: center,
  uAxis: frame.u,
  vAxis: frame.v,
  normal: frame.n,
  width: spec.widthPx,
  height: spec.heightPx,
  mmPerPixelU: spec.mmPerPixel,
  mmPerPixelV: spec.mmPerPixel
});
const resolveToothScrollStepMm = ({
  descriptors,
  frame,
  pane,
  spacing
}) => {
  const orientation = TOOTH_PANE_ORIENTATION[pane];
  const descriptorStride = descriptors.find(
    (descriptor) => descriptor.orientation === orientation && Number.isFinite(descriptor.strideMm) && Math.abs(descriptor.strideMm ?? 0) > 0
  )?.strideMm;
  if (descriptorStride !== void 0) {
    return Math.abs(descriptorStride);
  }
  return directionalNativeSpacingMm({ normal: frame.n, spacing });
};
const resolveToothScrollBounds = ({
  boxMm,
  frame,
  origin
}) => {
  if (!isFiniteVec3(origin) || !isFiniteVec3(boxMm.min) || !isFiniteVec3(boxMm.max)) {
    return null;
  }
  const offsets = boxCorners(boxMm).map(
    (corner) => dot(subtract(corner, origin), frame.n)
  );
  const min = Math.min(...offsets);
  const max = Math.max(...offsets);
  if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
    return null;
  }
  return { min, max };
};
const clampToothCursorComponent = ({
  bounds,
  cursor,
  pane
}) => ({
  ...cursor,
  [pane]: Math.min(
    Math.max(cursor[pane], bounds[pane].min),
    bounds[pane].max
  )
});
const clampToothCursor = ({
  bounds,
  cursor
}) => ({
  axial: Math.min(Math.max(cursor.axial, bounds.axial.min), bounds.axial.max),
  coronal: Math.min(
    Math.max(cursor.coronal, bounds.coronal.min),
    bounds.coronal.max
  ),
  sagittal: Math.min(
    Math.max(cursor.sagittal, bounds.sagittal.min),
    bounds.sagittal.max
  )
});
const resolveToothPaneCenters = ({
  cursor,
  frames,
  origin
}) => Object.fromEntries(
  TOOTH_PANES.map((pane) => [
    pane,
    resolveToothPaneCenter({ cursor, frames, origin, pane })
  ])
);
const resolveToothCrosshairs = ({
  centers,
  frames,
  point,
  spec
}) => {
  const paneEntries = TOOTH_PANES.map((pane) => {
    const frame = frames[pane];
    const centerOffset = subtract(point, centers[pane]);
    const leftFraction = 0.5 + dot(centerOffset, frame.u) / (spec.halfUMm * 2);
    const topFraction = 0.5 - dot(centerOffset, frame.v) / (spec.halfVMm * 2);
    const traces = TOOTH_PANES.filter((other) => other !== pane).map((other) => {
      const direction = cross(frame.n, frames[other].n);
      if (length(direction) < MIN_TRACE_DIRECTION_LENGTH) {
        return [other, null];
      }
      const angleDeg = Math.atan2(-dot(direction, frame.v), dot(direction, frame.u)) * 180 / Math.PI;
      return [other, { angleDeg, leftFraction, topFraction }];
    });
    return [pane, Object.fromEntries(traces)];
  });
  return Object.fromEntries(paneEntries);
};

const BACKENDS = ["cpu", "gpu-monolith"];
const now$1 = () => typeof performance !== "undefined" ? performance.now() : Date.now();
const quantile = (values, ratio) => {
  if (!values.length) {
    return 0;
  }
  const sorted = [...values].sort((left, right) => left - right);
  const index = Math.min(
    sorted.length - 1,
    Math.max(0, Math.floor((sorted.length - 1) * ratio))
  );
  return sorted[index];
};
const buildSummary = (values) => {
  const count = values.length;
  const sum = values.reduce((total, value) => total + value, 0);
  return {
    avg: count > 0 ? sum / count : 0,
    count,
    last: values[count - 1],
    max: Math.max(...values),
    min: Math.min(...values),
    p50: quantile(values, 0.5),
    p95: quantile(values, 0.95),
    sum
  };
};
class FullHeadMPRBenchmark {
  constructor() {
    this.viewerSessionId = "";
    this.backendEpoch = 0;
    this.selectedBackend = "cpu";
    this.phase = "mixed";
    this.sessionStartedAt = now$1();
    this.sampleValues = /* @__PURE__ */ new Map();
    this.counterValues = /* @__PURE__ */ new Map();
  }
  startSession(viewerSessionId) {
    this.viewerSessionId = viewerSessionId;
    this.backendEpoch = 0;
    this.phase = "mixed";
    this.sessionStartedAt = now$1();
    this.sampleValues.clear();
    this.counterValues.clear();
  }
  startEpoch(backend) {
    this.backendEpoch += 1;
    this.selectedBackend = backend;
    this.resetBackend(backend);
  }
  setPhase(phase) {
    this.phase = phase;
  }
  getPhase() {
    return this.phase;
  }
  recordSample(backend, name, value) {
    if (!Number.isFinite(value)) {
      return;
    }
    const key = `${backend}:${name}`;
    const values = this.sampleValues.get(key) ?? [];
    values.push(value);
    this.sampleValues.set(key, values);
  }
  incrementCounter(backend, name, value = 1) {
    const key = `${backend}:${name}`;
    this.counterValues.set(key, (this.counterValues.get(key) ?? 0) + value);
  }
  getSnapshot() {
    return {
      viewerSessionId: this.viewerSessionId,
      backendEpoch: this.backendEpoch,
      selectedBackend: this.selectedBackend,
      phase: this.phase,
      sessionStartedAt: this.sessionStartedAt,
      byBackend: BACKENDS.reduce(
        (result, backend) => ({
          ...result,
          [backend]: this.buildBackendStats(backend)
        }),
        {}
      )
    };
  }
  resetBackend(backend) {
    const prefix = `${backend}:`;
    Array.from(this.sampleValues.keys()).filter((key) => key.startsWith(prefix)).forEach((key) => this.sampleValues.delete(key));
    Array.from(this.counterValues.keys()).filter((key) => key.startsWith(prefix)).forEach((key) => this.counterValues.delete(key));
  }
  buildBackendStats(backend) {
    const prefix = `${backend}:`;
    const samples = Array.from(this.sampleValues.entries()).reduce(
      (result, [key, values]) => key.startsWith(prefix) ? { ...result, [key.slice(prefix.length)]: buildSummary(values) } : result,
      {}
    );
    const counters = Array.from(this.counterValues.entries()).reduce(
      (result, [key, value]) => key.startsWith(prefix) ? { ...result, [key.slice(prefix.length)]: value } : result,
      {}
    );
    return { samples, counters };
  }
}

const VIEWPORT_ORDER = ["coronal", "sagittal", "axial"];
const ORIENTATION_COLOR = {
  axial: "var(--yellow)",
  coronal: "var(--magenta)",
  sagittal: "var(--cian)"
};
const isPaneChromeTinted = (paneLayout, paneFrame) => paneLayout === "one_plus_two" || paneFrame === "outlined";
const CROSSHAIR_COLOR = {
  axial: {
    vertical: ORIENTATION_COLOR.sagittal,
    horizontal: ORIENTATION_COLOR.coronal
  },
  coronal: {
    vertical: ORIENTATION_COLOR.sagittal,
    horizontal: ORIENTATION_COLOR.axial
  },
  sagittal: {
    vertical: ORIENTATION_COLOR.coronal,
    horizontal: ORIENTATION_COLOR.axial
  }
};
const TOOTH_TRACE_BY_LINE = {
  axial: { vertical: "sagittal", horizontal: "coronal" },
  coronal: { vertical: "sagittal", horizontal: "axial" },
  sagittal: { vertical: "coronal", horizontal: "axial" }
};
const DEFAULT_COLOR_PROFILE = {
  wc: 1e3,
  ww: 3200
};
const DEFAULT_DEBUG_OPTIONS = {
  emitMetricsOnEveryUpdate: true
};
const CPU_TOOTH_RENDER_PATHS = ["cpu-saved-view-int16"];
const STUDIO_ZOOM_FACTOR = Math.pow(0.95, 0.4);
const DEFAULT_CHROME_OPTIONS = {
  paneFrame: "outlined",
  showDevChrome: false
};
const DRAG_SLICE_THRESHOLD_PX = 6;
const STUDIO_WHEEL_SLICE_STEP_MM = 0.2;
const DRAG_PIXELS_PER_ZOOM_STEP = 8;
const TOOTH_LINE_TOLERANCE_SCREEN_PX = 6;
const WHEEL_DELTA_PIXELS_PER_ZOOM_STEP = 100;
const WHEEL_DELTA_MODE_LINE = 1;
const WHEEL_DELTA_MODE_PAGE = 2;
const MIDDLE_MOUSE_BUTTON = 1;
const RIGHT_MOUSE_BUTTON = 2;
const PRIMARY_MOUSE_BUTTONS_MASK = 1;
const TOOTH_FOCUS_LEGACY_FOV_MM = 33;
const DEMOTE_WHEEL_P95_MS = 80;
const MIN_DEMOTE_SAMPLES = 8;
const DEMOTE_COOLDOWN_MS = 5e3;
const now = () => typeof performance !== "undefined" ? performance.now() : Date.now();
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round = (value) => Math.round(value);
const isSliceScrollWheelEvent = (event) => event.ctrlKey || event.metaKey;
const getWheelSliceDirection = (event) => {
  if (event.deltaY === 0) {
    return 0;
  }
  return event.deltaY > 0 ? 1 : -1;
};
const getNormalizedWheelDeltaY = (event, pageHeight) => {
  if (event.deltaMode === WHEEL_DELTA_MODE_LINE) {
    return event.deltaY * 16;
  }
  if (event.deltaMode === WHEEL_DELTA_MODE_PAGE) {
    return event.deltaY * pageHeight;
  }
  return event.deltaY;
};
const getStudioWheelZoomFactor = (event, pageHeight) => Math.pow(
  STUDIO_ZOOM_FACTOR,
  getNormalizedWheelDeltaY(event, pageHeight) / WHEEL_DELTA_PIXELS_PER_ZOOM_STEP
);
const projectionWorldAxes = (projection) => {
  switch (projection) {
    case "axial":
      return { horizontal: "x", vertical: "y" };
    case "coronal":
      return { horizontal: "x", vertical: "z" };
    case "sagittal":
      return { horizontal: "y", vertical: "z" };
  }
};
const isAbortError = (error) => typeof error === "object" && error !== null && "name" in error && error.name === "AbortError";
const createCenteredFocusVoxel = (reader) => ({
  x: Math.floor(reader.geometry.shape.x / 2),
  y: Math.floor(reader.geometry.shape.y / 2),
  z: Math.floor(reader.geometry.shape.z / 2)
});
const clampFocusVoxel = (reader, focus) => ({
  x: clamp(round(focus.x), 0, Math.max(0, reader.geometry.shape.x - 1)),
  y: clamp(round(focus.y), 0, Math.max(0, reader.geometry.shape.y - 1)),
  z: clamp(round(focus.z), 0, Math.max(0, reader.geometry.shape.z - 1))
});
const worldToVoxel = (reader, point) => {
  const { patientCsOrigin, spacing } = reader.geometry;
  if (spacing.x <= 0 || spacing.y <= 0 || spacing.z <= 0) {
    return null;
  }
  return {
    x: (point.x - patientCsOrigin.x) / spacing.x,
    y: (point.y - patientCsOrigin.y) / spacing.y,
    z: (point.z - patientCsOrigin.z) / spacing.z
  };
};
const getSliceIndexForProjection = (focus, projection) => focus[ORTHOGONAL_PANE_AXES[projection].sliceAxis];
const getCrosshairPercentages = (reader, focus, projection) => {
  const shape = reader.geometry.shape;
  const axes = ORTHOGONAL_PANE_AXES[projection];
  const horizontalExtent = shape[axes.horizontalAxis] - 1;
  return {
    left: horizontalExtent <= 0 ? 0.5 : focus[axes.horizontalAxis] / horizontalExtent,
    top: paneFractionForVoxel({
      axes,
      extentVoxels: shape[axes.verticalAxis] - 1,
      voxelIndex: focus[axes.verticalAxis]
    })
  };
};
const offsetFocusVoxelAlongProjection = (reader, focus, projection, direction) => {
  const { sliceAxis } = ORTHOGONAL_PANE_AXES[projection];
  return clampFocusVoxel(reader, {
    ...focus,
    [sliceAxis]: focus[sliceAxis] + direction
  });
};
class FullHeadMPRController {
  constructor(view) {
    this.abortController = null;
    this.activeProjection = "axial";
    this.paneLayout = "three_columns";
    this.paneOrder = ["axial", "sagittal", "coronal"];
    this.visibleProjections = [...VIEWPORT_ORDER];
    this.colorProfile = DEFAULT_COLOR_PROFILE;
    this.debugOptions = DEFAULT_DEBUG_OPTIONS;
    this.focus = null;
    this.firstFramePainted = false;
    this.loadedChunkCount = 0;
    this.renderEpochByProjection = /* @__PURE__ */ new Map();
    this.chrome = DEFAULT_CHROME_OPTIONS;
    this.crosshairsVisible = true;
    this.displayAdjust = NEUTRAL_DISPLAY_ADJUST;
    this.initialColorProfile = DEFAULT_COLOR_PROFILE;
    this.inputMode = "navigate";
    this.zoomSyncEnabled = false;
    this.metricsUnsubscribe = null;
    this.mounted = false;
    this.selectedBackend = "cpu";
    this.restoreGpuAfterContextLoss = false;
    this.monolithDownsampleFactor = 1;
    // One-way latch: once the session demotes a struggling/failed GPU monolith to
    // CPU, it never promotes back within the session (anti-thrash).
    this.demotedToCpu = false;
    this.gpuRendererInstance = null;
    this.gpuVolumeLease = null;
    this.pendingInteractionMetric = null;
    this.benchmark = new FullHeadMPRBenchmark();
    // Measures REAL GPU draw time per render via timer queries (when supported),
    // attributed to whichever backend was active at draw time → honest drawMs.
    this.gpuTimer = null;
    this.lastBackendSwitchAt = 0;
    this.framesSinceLastRepack = Number.MAX_SAFE_INTEGER;
    this.lastInteractionMissingChunkCount = 0;
    // Defer rendering until the viewport scissor box stops changing — the modal's
    // framer-motion open animation (scale 0→1) makes the surface's visual rect
    // (position) animate while its layout size stays full, so a render mid-anim
    // lands at a mismatched box and the image "jumps" to center when it settles.
    this.lastViewportSizeKey = /* @__PURE__ */ new Map();
    this.viewportDeferCount = /* @__PURE__ */ new Map();
    this.benchmarkEmitScheduled = false;
    this.reader = null;
    this.readerRunID = 0;
    this.presentation = null;
    this.subscriptions = [];
    this.annotations = null;
    this.totalChunkCount = 0;
    this.zoomPanByProjection = {
      axial: { zoom: 1, panX: 0, panY: 0 },
      coronal: { zoom: 1, panX: 0, panY: 0 },
      sagittal: { zoom: 1, panX: 0, panY: 0 }
    };
    this.drag = null;
    this.toothSession = null;
    this.restoredView = null;
    this.sliceThicknessMmByProjection = {};
    this.sliceStepMmByProjection = {};
    this.imageOptionsByProjection = /* @__PURE__ */ new Map();
    this.savedRenderAborts = /* @__PURE__ */ new Map();
    // User preference; survives tooth switches and volume sessions.
    this.toothSliceGeometryMode = "series";
    this.toothRenderPath = null;
    this.toothRoiGeneration = 0;
    this.toothRoiAbort = null;
    this.handleDragMove = (event) => {
      const drag = this.drag;
      if (!drag) {
        return;
      }
      const viewportState = this.viewportStateByProjection[drag.projection];
      const rect = viewportState.surface.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }
      const previousClientX = drag.prevClientX;
      const previousClientY = drag.prevClientY;
      const deltaX = event.clientX - previousClientX;
      const deltaY = event.clientY - previousClientY;
      drag.prevClientX = event.clientX;
      drag.prevClientY = event.clientY;
      if (drag.intent === "rotate") {
        const session = this.toothSession;
        if (!session) {
          return;
        }
        const center = this.toothPaneCrossingPx(session, drag.projection);
        const pointer = this.clientToPanePx(
          drag.projection,
          event.clientX,
          event.clientY,
          { clampToSurface: false }
        );
        const previous = this.clientToPanePx(
          drag.projection,
          previousClientX,
          previousClientY,
          { clampToSurface: false }
        );
        if (!center || !pointer || !previous) {
          return;
        }
        this.applyToothRotation(
          session,
          drag.projection,
          screenSweepAngleRad({ center, from: previous, to: pointer })
        );
        return;
      }
      if (drag.intent === "toothLine") {
        const session = this.toothSession;
        const grabbedPane = drag.grabbedPane;
        if (!session || !grabbedPane || !drag.startCursor || !drag.startFraction) {
          return;
        }
        const fraction = this.clientToPaneFraction(
          drag.projection,
          event.clientX,
          event.clientY,
          { clampToSurface: false }
        );
        if (!fraction) {
          return;
        }
        const worldDelta = paneFractionDeltaToWorldDelta({
          frame: session.frames[drag.projection],
          fromFraction: drag.startFraction,
          spec: session.spec,
          toFraction: fraction
        });
        const normal = session.frames[grabbedPane].n;
        this.applyToothCursor(
          session,
          {
            ...session.cursor,
            [grabbedPane]: drag.startCursor[grabbedPane] + worldDelta.x * normal.x + worldDelta.y * normal.y + worldDelta.z * normal.z
          },
          "pointer",
          grabbedPane
        );
        return;
      }
      if (drag.intent === "brightnessContrast") {
        this.displayAdjust = accumulateDisplayAdjust({
          adjust: this.displayAdjust,
          deltaXPx: deltaX,
          deltaYPx: deltaY,
          paneHeightPx: rect.height,
          paneWidthPx: rect.width
        });
        this.scheduleRenderAll();
        return;
      }
      if (drag.intent === "zoom") {
        const zoomView = this.zoomPanByProjection[drag.projection];
        this.applyZoom(
          drag.projection,
          zoomView.zoom * resolveDragZoomFactor({
            deltaYPx: deltaY,
            pixelsPerStep: DRAG_PIXELS_PER_ZOOM_STEP,
            stepFactor: STUDIO_ZOOM_FACTOR
          })
        );
        this.clampPan(drag.projection);
        this.applyViewportCamera(drag.projection);
        viewportState.viewport?.render();
        this.updateCrosshairs();
        return;
      }
      if (drag.intent === "scroll") {
        const { remainderPx, steps } = resolveDragSliceSteps({
          accumulatedPx: drag.scrollAccumulatorPx + deltaY,
          thresholdPx: DRAG_SLICE_THRESHOLD_PX
        });
        drag.scrollAccumulatorPx = remainderPx;
        if (steps !== 0) {
          this.moveSlice(drag.projection, steps);
        }
        return;
      }
      const camera = viewportState.viewport?.camera;
      if (!(camera instanceof OrthographicCamera)) {
        return;
      }
      const curNDC = {
        x: (event.clientX - rect.left) / rect.width * 2 - 1,
        y: -((event.clientY - rect.top) / rect.height * 2 - 1)
      };
      const before = new Vector3(drag.prevNDC.x, drag.prevNDC.y, 0).unproject(
        camera
      );
      const after = new Vector3(curNDC.x, curNDC.y, 0).unproject(camera);
      const view = this.zoomPanByProjection[drag.projection];
      view.panX += before.x - after.x;
      view.panY += before.y - after.y;
      this.clampPan(drag.projection);
      this.applyViewportCamera(drag.projection);
      viewportState.viewport?.render();
      this.updateCrosshairs();
      drag.prevNDC = curNDC;
    };
    this.handleDragEnd = () => {
      if (!this.drag) {
        return;
      }
      this.drag = null;
      window.removeEventListener("mousemove", this.handleDragMove);
      window.removeEventListener("mouseup", this.handleDragEnd);
      if (this.toothSession && (this.restoredView || this.toothRenderPath !== null && CPU_TOOTH_RENDER_PATHS.includes(this.toothRenderPath))) {
        this.scheduleRenderAll();
      }
    };
    this.view = view;
    this.imageOptionsController = new FullHeadMPRImageOptionsController({
      view,
      getOptions: (projection) => this.imageOptionsByProjection.get(projection) ?? {
        brightness: 0,
        contrast: 0,
        invert: false,
        sharpness: 0
      },
      getSnapshot: (projection) => this.getViewSnapshot(projection),
      isReady: (projection) => this.isViewReady(projection),
      onStart: (projection) => this.setActiveProjection(projection),
      setOptions: (projection, options) => this.setImageOptions(projection, options)
    });
    this.rootElement = document.createElement("div");
    this.summaryElement = document.createElement("div");
    this.summaryTitleElement = document.createElement("span");
    this.summaryValueElement = document.createElement("span");
    this.viewportGridElement = document.createElement("div");
    this.viewportStateByProjection = {
      axial: this.createViewport("axial"),
      coronal: this.createViewport("coronal"),
      sagittal: this.createViewport("sagittal")
    };
  }
  mount() {
    if (this.mounted) {
      return;
    }
    this.rootElement.style.display = "flex";
    this.rootElement.style.flexDirection = "column";
    this.rootElement.style.height = "100%";
    this.rootElement.style.padding = "0";
    this.rootElement.style.boxSizing = "border-box";
    this.rootElement.style.position = "relative";
    this.rootElement.style.zIndex = "1";
    this.summaryElement.style.display = "flex";
    this.summaryElement.style.alignItems = "center";
    this.summaryElement.style.justifyContent = "space-between";
    this.summaryElement.style.border = "1px solid rgba(255, 255, 255, 0.08)";
    this.summaryElement.style.borderRadius = "12px";
    this.summaryElement.style.background = "linear-gradient(135deg, rgba(26,31,41,0.96), rgba(15,18,24,0.96))";
    this.summaryElement.style.padding = "10px 12px";
    this.summaryElement.style.gap = "8px";
    this.summaryTitleElement.textContent = "Full-head MPR";
    this.summaryTitleElement.style.fontSize = "12px";
    this.summaryTitleElement.style.fontWeight = "700";
    this.summaryTitleElement.style.letterSpacing = "0.08em";
    this.summaryTitleElement.style.textTransform = "uppercase";
    this.summaryValueElement.style.fontSize = "12px";
    this.summaryValueElement.style.opacity = "0.82";
    this.summaryElement.appendChild(this.summaryTitleElement);
    this.summaryElement.appendChild(this.summaryValueElement);
    const viewportGrid = this.viewportGridElement;
    viewportGrid.style.display = "grid";
    viewportGrid.style.flex = "1 1 auto";
    viewportGrid.style.minHeight = "0";
    VIEWPORT_ORDER.forEach((projection) => {
      viewportGrid.appendChild(this.viewportStateByProjection[projection].pane);
    });
    this.rootElement.appendChild(viewportGrid);
    this.view.domElement.appendChild(this.rootElement);
    this.setupViewports();
    this.applyChrome();
    this.updateSummary();
    this.mounted = true;
  }
  setupViewports() {
    this.annotations = new FullHeadMPRAnnotationController(this.view);
    const placeholder = new Data3DTexture(new Uint8Array(1), 1, 1, 1);
    placeholder.format = RedFormat;
    placeholder.needsUpdate = true;
    VIEWPORT_ORDER.forEach((projection) => {
      const state = this.viewportStateByProjection[projection];
      const viewport = this.view.addViewport(projection, state.surface, {
        eventNames: viewportEventNames
      });
      viewport.clearColor = 0;
      viewport.alpha = 1;
      viewport.toolController.setServiceTools(/* @__PURE__ */ new Set([Render]));
      viewport.init();
      viewport.addEventListener("resize", () => {
        this.settleToothInitialZoom(projection);
        this.applyViewportCamera(projection);
        this.updateCrosshairs();
      });
      const scene = new SliceViewportScene(placeholder, this.colorProfile);
      scene.setLayer(viewport.cameraLayer);
      this.view.scene.add(scene.sliceMesh);
      this.view.scene.add(scene.imageMesh);
      state.viewport = viewport;
      state.scene = scene;
      this.imageOptionsController.attach(projection, viewport);
      this.annotations?.addViewport(projection, viewport);
    });
    this.setGpuTimingEnabled(true);
  }
  async start(options) {
    this.stop();
    this.readerRunID += 1;
    const runID = this.readerRunID;
    this.abortController = new AbortController();
    this.colorProfile = options.colorProfile ?? DEFAULT_COLOR_PROFILE;
    this.initialColorProfile = this.colorProfile;
    this.displayAdjust = NEUTRAL_DISPLAY_ADJUST;
    this.focus = null;
    VIEWPORT_ORDER.forEach((projection) => {
      this.zoomPanByProjection[projection] = { zoom: 1, panX: 0, panY: 0 };
    });
    this.firstFramePainted = false;
    this.demotedToCpu = false;
    this.pendingInteractionMetric = null;
    this.loadedChunkCount = 0;
    this.totalChunkCount = 0;
    this.reader = null;
    this.lastViewportSizeKey.clear();
    this.viewportDeferCount.clear();
    this.clearViewportFrames();
    this.setAllViewportStatus("loading", "Loading volume...");
    this.updateSummary("Loading DCCT volume...");
    try {
      const reader = await getSharedDCCTVolumeReader(
        options.volumeFileID,
        this.abortController.signal
      );
      if (this.readerRunID !== runID) {
        reader.release();
        return;
      }
      this.reader = reader;
      this.beginPresentation(options.presentationID);
      this.totalChunkCount = reader.chunks.length;
      this.focus = clampFocusVoxel(
        reader,
        options.initialFocus ?? createCenteredFocusVoxel(reader)
      );
      this.setActiveProjection(this.getPrimaryProjection());
      this.selectedBackend = this.resolveBackend(options.preferredBackend);
      if (this.selectedBackend === "gpu-monolith") {
        try {
          this.ensureGpuRenderer();
        } catch (error) {
          this.disposeGpuRendererInstance();
          if (!(error instanceof VolumeGpuError)) {
            throw error;
          }
          this.selectedBackend = "cpu";
        }
      }
      this.benchmark.startSession(String(this.readerRunID));
      this.benchmark.startEpoch(this.selectedBackend);
      this.lastBackendSwitchAt = now();
      this.framesSinceLastRepack = Number.MAX_SAFE_INTEGER;
      this.attachReaderListeners(reader);
      this.updateViewportShapes();
      this.updateCrosshairs();
      await this.warmupInitialViewport(reader);
      if (this.readerRunID !== runID || this.reader !== reader) {
        return;
      }
      if (!options.colorProfile) {
        const autoProfile = this.gpuVolumeLease?.getColorProfile() ?? this.computeAutoColorProfile(reader);
        if (autoProfile) {
          this.colorProfile = autoProfile;
          this.initialColorProfile = autoProfile;
          this.gpuVolumeLease?.setColorProfile(autoProfile);
        }
      }
      await this.renderAll("loaded-only");
      if (this.readerRunID !== runID || this.reader !== reader) {
        return;
      }
      this.view.dispatchEvent({
        type: "fullHeadMPRReady",
        backend: this.selectedBackend,
        downsampleFactor: this.selectedBackend === "gpu-monolith" ? this.monolithDownsampleFactor : 1,
        geometry: reader.geometry,
        volumeFileID: reader.volumeFileID
      });
      this.dispatchActivePaneChanged();
    } catch (error) {
      if (isAbortError(error) || this.readerRunID !== runID) {
        return;
      }
      this.handleError(error, "Failed to initialize full-head MPR.");
    }
  }
  stop() {
    this.imageOptionsController.clear();
    this.imageOptionsByProjection.clear();
    VIEWPORT_ORDER.forEach(
      (projection) => this.viewportStateByProjection[projection].scene?.setImageOptions(null)
    );
    this.annotations?.clear();
    this.handleDragEnd();
    this.restoredView = null;
    this.savedRenderAborts.forEach((abort) => abort.abort());
    this.savedRenderAborts.clear();
    this.presentation = null;
    this.exitToothSession();
    this.abortController?.abort();
    this.abortController = null;
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    this.subscriptions = [];
    this.metricsUnsubscribe?.();
    this.metricsUnsubscribe = null;
    if (this.reader) {
      VIEWPORT_ORDER.forEach((projection) => {
        this.reader?.releaseConsumerDemand(
          this.viewportStateByProjection[projection].consumerId
        );
      });
    }
    this.disposeGpuRendererInstance();
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      if (viewportState.scheduledFrameId !== null) {
        cancelAnimationFrame(viewportState.scheduledFrameId);
        viewportState.scheduledFrameId = null;
      }
      this.renderEpochByProjection.set(
        projection,
        (this.renderEpochByProjection.get(projection) ?? 0) + 1
      );
    });
    this.reader?.release();
    this.reader = null;
    this.focus = null;
    this.pendingInteractionMetric = null;
    this.clearViewportFrames();
    this.updateSummary("Viewer is idle");
    this.setAllViewportStatus("idle", "Waiting for volume...");
  }
  dispose() {
    this.stop();
    this.imageOptionsController.dispose();
    this.annotations?.dispose();
    this.annotations = null;
    this.setGpuTimingEnabled(false);
    VIEWPORT_ORDER.forEach((projection) => {
      const state = this.viewportStateByProjection[projection];
      if (state.scene) {
        this.view.scene.remove(state.scene.sliceMesh);
        this.view.scene.remove(state.scene.imageMesh);
        state.scene.dispose();
        state.scene = null;
      }
      if (state.viewport) {
        state.viewport.viewportRenderer.gpuProbe = void 0;
        this.view.deleteViewport(projection);
        state.viewport = null;
      }
    });
    if (this.mounted) {
      this.view.domElement.removeChild(this.rootElement);
      this.mounted = false;
    }
  }
  getFocus() {
    return this.focus ? { ...this.focus } : null;
  }
  getMetrics() {
    return this.reader?.getMetrics() ?? null;
  }
  setColorProfile(colorProfile) {
    this.colorProfile = colorProfile;
    this.initialColorProfile = colorProfile;
    this.displayAdjust = NEUTRAL_DISPLAY_ADJUST;
    this.scheduleRenderAll();
  }
  setDebugOptions(debugOptions) {
    this.debugOptions = {
      ...this.debugOptions,
      ...debugOptions
    };
  }
  setInputMode(mode) {
    if (this.inputMode === mode) {
      return;
    }
    this.handleDragEnd();
    this.inputMode = mode;
    VIEWPORT_ORDER.forEach((projection) => {
      this.viewportStateByProjection[projection].surface.style.cursor = PANE_CURSOR_BY_MODE[mode];
    });
  }
  setAnnotationTool(tool) {
    this.handleDragEnd();
    this.annotations?.setTool(tool);
  }
  setAnnotationTarget(projection, target) {
    return this.annotations?.setTarget(projection, target) ?? Promise.resolve();
  }
  setAnnotationsVisible(visible) {
    this.annotations?.setVisible(visible);
  }
  cancelAnnotationInteraction() {
    return this.annotations?.cancelInteraction() ?? false;
  }
  deleteSelectedAnnotation() {
    return this.annotations?.deleteSelectedAnnotation() ?? false;
  }
  setCrosshairsVisible(visible) {
    if (this.crosshairsVisible === visible) {
      return;
    }
    this.crosshairsVisible = visible;
    this.applyCrosshairVisibility();
  }
  setZoomSyncEnabled(enabled) {
    this.zoomSyncEnabled = enabled;
  }
  setSliceThicknessMm(thickness) {
    const clamped = Object.fromEntries(
      VIEWPORT_ORDER.map((projection) => {
        const thicknessMm = thickness[projection];
        return [
          projection,
          typeof thicknessMm === "number" && Number.isFinite(thicknessMm) ? Math.min(thicknessMm, CONFIGURED_MAX_SLAB_THICKNESS_MM) : void 0
        ];
      })
    );
    const changedProjections = VIEWPORT_ORDER.filter(
      (projection) => this.sliceThicknessMmByProjection[projection] !== clamped[projection]
    );
    this.sliceThicknessMmByProjection = clamped;
    changedProjections.forEach(
      (projection) => this.scheduleViewportRender(projection)
    );
  }
  setSliceStepMm(steps) {
    this.sliceStepMmByProjection = { ...steps };
  }
  getVisibleProjections() {
    return [...this.visibleProjections];
  }
  getPaneLayout() {
    return this.paneLayout;
  }
  setPaneLayout(layout) {
    if (this.paneLayout === layout) {
      return;
    }
    this.cancelPaneLayoutInteraction();
    this.paneLayout = layout;
    this.refreshPaneLayout();
  }
  getPrimaryProjection() {
    return this.paneOrder[0];
  }
  setPrimaryProjection(projection) {
    const primary = this.getPrimaryProjection();
    if (primary === projection || !this.visibleProjections.includes(projection)) {
      return;
    }
    this.cancelPaneLayoutInteraction();
    this.swapPrimaryProjection(projection);
    this.refreshPaneLayout();
    this.setActiveProjection(projection);
    const pane = this.viewportStateByProjection[projection].pane;
    const focusedElement = pane.ownerDocument.activeElement;
    if (focusedElement && this.viewportGridElement.contains(focusedElement) && !pane.contains(focusedElement)) {
      pane.focus({ preventScroll: true });
    }
  }
  setVisibleProjections(projections) {
    const visible = VIEWPORT_ORDER.filter(
      (projection) => projections.includes(projection)
    );
    if (visible.length === 0 || visible.length === this.visibleProjections.length && visible.every(
      (projection, index) => projection === this.visibleProjections[index]
    )) {
      return;
    }
    this.cancelPaneLayoutInteraction();
    this.visibleProjections = visible;
    if (!visible.includes(this.getPrimaryProjection())) {
      this.swapPrimaryProjection(visible[0]);
    }
    VIEWPORT_ORDER.forEach((projection) => {
      const state = this.viewportStateByProjection[projection];
      const isVisible = visible.includes(projection);
      state.pane.style.display = isVisible ? "" : "none";
      if (!isVisible) {
        if (state.scheduledFrameId !== null) {
          cancelAnimationFrame(state.scheduledFrameId);
          state.scheduledFrameId = null;
        }
        this.savedRenderAborts.get(projection)?.abort();
        this.renderEpochByProjection.set(
          projection,
          (this.renderEpochByProjection.get(projection) ?? 0) + 1
        );
        this.reader?.releaseConsumerDemand(state.consumerId);
      }
    });
    if (!visible.includes(this.activeProjection)) {
      this.setActiveProjection(this.getPrimaryProjection());
    }
    this.dispatchActivePaneChanged();
    this.refreshPaneLayout();
    const presentation = this.presentation;
    if (presentation?.setProjections(this.visibleProjections)) {
      this.dispatchPresentationReady(presentation);
    }
  }
  stepSlice(direction, projection = this.activeProjection) {
    if (!this.visibleProjections.includes(projection)) {
      return;
    }
    this.cancelPaneLayoutInteraction();
    this.setActiveProjection(projection);
    this.moveSlice(projection, direction);
  }
  setChrome(options) {
    this.chrome = { ...this.chrome, ...options };
    if (this.mounted) {
      this.applyChrome();
    }
  }
  getActivePane() {
    if (!this.mounted) {
      return null;
    }
    return {
      element: this.viewportStateByProjection[this.activeProjection].pane,
      projection: this.activeProjection
    };
  }
  getPaneSurface(projection) {
    return this.viewportStateByProjection[projection].surface;
  }
  getPaneElement(projection) {
    return this.viewportStateByProjection[projection].pane;
  }
  isViewReady(projection = this.activeProjection) {
    const state = this.viewportStateByProjection[projection];
    return this.mounted && this.visibleProjections.includes(projection) && state.hasFrame && state.status === "ready" && state.scheduledFrameId === null && !this.savedRenderAborts.has(projection);
  }
  setImageTool(tool) {
    this.handleDragEnd();
    if (tool) {
      this.annotations?.cancelInteraction();
    }
    this.imageOptionsController.setTool(tool);
    this.annotations?.refresh();
  }
  isImageToolActive() {
    return this.imageOptionsController.hasTool();
  }
  setImageOptionsTarget(projection, target) {
    this.imageOptionsController.setTarget(projection, target);
  }
  setImageOptions(projection, options) {
    const current = this.imageOptionsByProjection.get(projection);
    const next = options && (options.brightness !== 0 || options.contrast !== 0 || options.invert || options.sharpness !== 0) ? options : null;
    if (!current && !next || current && next && current.brightness === next.brightness && current.contrast === next.contrast && current.invert === next.invert && current.sharpness === next.sharpness) {
      return;
    }
    const viewportState = this.viewportStateByProjection[projection];
    const hasReadyRaster = viewportState.scene?.imageMesh.visible && this.isViewReady(projection);
    if (next) {
      this.imageOptionsByProjection.set(projection, { ...next });
    } else {
      this.imageOptionsByProjection.delete(projection);
    }
    viewportState.scene?.setImageOptions(next);
    if (next && (current || hasReadyRaster)) {
      if (viewportState.scene?.imageMesh.visible) {
        viewportState.viewport?.render();
      }
      return;
    }
    this.savedRenderAborts.get(projection)?.abort();
    this.savedRenderAborts.delete(projection);
    this.renderEpochByProjection.set(
      projection,
      (this.renderEpochByProjection.get(projection) ?? 0) + 1
    );
    this.scheduleViewportRender(projection);
  }
  restoreSavedView(view, options) {
    const reader = this.reader;
    if (!reader || reader.volumeFileID !== view.volumeFileID) {
      return;
    }
    this.beginPresentation(options?.presentationID);
    this.handleDragEnd();
    this.exitToothSession();
    const frames = resolveSavedViewFrames(view);
    const boxMm = volumeBoxMm(reader.geometry);
    const spec = buildToothPaneLayoutSpec({
      origin: view.centerMm,
      spacing: reader.geometry.spacing,
      volumeBoxMm: boxMm
    });
    const panes = VIEWPORT_ORDER.map((projection) => ({
      bounds: resolveToothScrollBounds({
        boxMm,
        frame: frames[projection],
        origin: view.centerMm
      }),
      projection,
      stepMm: resolveToothScrollStepMm({
        descriptors: [],
        frame: frames[projection],
        pane: projection,
        spacing: reader.geometry.spacing
      })
    }));
    if (!spec || panes.some(({ bounds, stepMm }) => !bounds || stepMm === null)) {
      this.handleError(
        new Error("Invalid saved MPR geometry"),
        "Failed to restore MPR"
      );
      return;
    }
    this.restoredView = view;
    this.displayAdjust = NEUTRAL_DISPLAY_ADJUST;
    const session = {
      cursor: { axial: 0, coronal: 0, sagittal: 0 },
      frames,
      initialZoom: { axial: 1, coronal: 1, sagittal: 1 },
      origin: view.centerMm,
      outerBox: boxMm,
      payload: { origin: view.centerMm },
      roiBox: volumeVoxelBox(reader.geometry),
      roiStatus: "ready",
      rotatedBasis: null,
      scrollBounds: Object.fromEntries(
        panes.map(({ bounds, projection }) => [projection, bounds])
      ),
      scrollStepMm: Object.fromEntries(
        panes.map(({ projection, stepMm }) => [projection, stepMm])
      ),
      sliceDescriptors: [],
      spec,
      toothIso: null,
      window: view.window
    };
    this.toothSession = session;
    this.focus = worldToVoxel(reader, view.centerMm);
    VIEWPORT_ORDER.forEach((projection) => {
      const { consumerId } = this.viewportStateByProjection[projection];
      reader.releaseConsumerDemand(consumerId);
      session.initialZoom[projection] = resolveToothPaneFitZoom({
        frustumPx: this.computeLetterboxFrustum(projection),
        heightMm: view.heightMm,
        mmPerPixel: spec.mmPerPixel,
        widthMm: view.widthMm
      });
    });
    this.activeProjection = this.visibleProjections.includes(view.projection) ? view.projection : this.visibleProjections[0];
    this.applyToothPaneShapes(session);
    this.updateCrosshairs();
    this.dispatchActivePaneChanged();
    this.scheduleRenderAll();
  }
  resolvePaneSampling(projection) {
    const session = this.toothSession;
    const reader = this.reader;
    if (!session || !reader) {
      return null;
    }
    return resolveToothPaneSampling({
      normal: session.frames[projection].n,
      overrideThicknessMm: this.sliceThicknessMmByProjection[projection],
      projection,
      restoredView: this.restoredView,
      spacing: reader.geometry.spacing
    });
  }
  getViewSnapshot(projection = this.activeProjection) {
    const reader = this.reader;
    const focus = this.focus;
    const { surface, hasFrame } = this.viewportStateByProjection[projection];
    if (!this.mounted || !reader || !focus || !hasFrame || !surface.clientWidth || !surface.clientHeight) {
      return null;
    }
    const frustum = this.computeLetterboxFrustum(projection);
    const zoomPan = this.zoomPanByProjection[projection];
    const session = this.toothSession;
    const view = session ? describeToothView({
      centerMm: this.toothPaneCenters(session)[projection],
      frame: session.frames[projection],
      frustumPx: frustum,
      projection,
      spacing: reader.geometry.spacing,
      spec: session.spec,
      thickness: { kind: "native" },
      volumeFileID: reader.volumeFileID,
      window: this.effectiveWindow(session.window ?? this.colorProfile),
      zoomPan
    }) : describeOrthogonalView({
      focus,
      frustum,
      geometry: reader.geometry,
      projection,
      volumeFileID: reader.volumeFileID,
      window: this.effectiveWindow(this.colorProfile),
      zoomPan
    });
    if (!view) {
      return null;
    }
    if (session) {
      const sampling = this.resolvePaneSampling(projection);
      if (sampling) {
        view.sampling = sampling.sampling;
        view.thicknessMm = sampling.effectiveThicknessMm;
        view.sampleOffsetsMm = [...sampling.offsetsMm];
      }
    }
    return makeFullHeadMPRViewSnapshot({
      heightPx: surface.clientHeight,
      presentationID: this.presentation?.presentationID ?? null,
      view,
      widthPx: surface.clientWidth
    });
  }
  async captureScreenshot(projection = this.activeProjection) {
    const viewport = this.viewportStateByProjection[projection].viewport;
    if (!viewport || !this.reader) {
      return null;
    }
    viewport.renderImmediately();
    const renderer = this.view.getRenderer();
    const canvas = this.view.getCanvas();
    const rect = paneCanvasRect({
      canvasHeightPx: canvas.height,
      canvasWidthPx: canvas.width,
      pixelRatio: renderer.getPixelRatio(),
      size: {
        bottomPx: viewport.size.y,
        heightPx: viewport.size.w,
        leftPx: viewport.size.x,
        widthPx: viewport.size.z
      }
    });
    if (!rect) {
      return null;
    }
    const output = document.createElement("canvas");
    output.width = rect.widthPx;
    output.height = rect.heightPx;
    const context = output.getContext("2d");
    if (!context) {
      return null;
    }
    context.drawImage(
      canvas,
      rect.leftPx,
      rect.topPx,
      rect.widthPx,
      rect.heightPx,
      0,
      0,
      rect.widthPx,
      rect.heightPx
    );
    return new Promise((resolve) => {
      output.toBlob((blob) => resolve(blob), "image/png");
    });
  }
  setToothFocus(payload) {
    this.restoredView = null;
    this.beginPresentation(payload.presentationID);
    const geometry = buildToothFocusGeometry(payload);
    if (this.tryStartToothSession(payload, geometry.sliceDescriptors)) {
      return;
    }
    this.exitToothSession();
    this.applyWorldFocus(payload.origin, "tooth", geometry.focusBox);
  }
  clearToothFocus() {
    if (!this.toothSession) {
      return;
    }
    this.exitToothSession();
    this.resetView();
    this.scheduleRenderAll();
  }
  resetToDefaultView(options) {
    this.beginPresentation(options?.presentationID);
    const reader = this.reader;
    if (!reader) {
      return;
    }
    this.handleDragEnd();
    this.displayAdjust = NEUTRAL_DISPLAY_ADJUST;
    this.colorProfile = this.initialColorProfile;
    const keptToothPayload = options?.keepToothFocus ? this.toothSession?.payload ?? null : null;
    this.exitToothSession();
    if (keptToothPayload) {
      this.resetView();
      this.setToothFocus({
        ...keptToothPayload,
        presentationID: options?.presentationID
      });
      return;
    }
    this.setActiveProjection(this.getPrimaryProjection());
    this.setFocus(createCenteredFocusVoxel(reader), "api");
    this.resetView();
    this.scheduleRenderAll();
  }
  setToothSliceMode(mode) {
    if (this.toothSliceGeometryMode === mode) {
      return;
    }
    const session = this.toothSession;
    if (session && !session.rotatedBasis) {
      return;
    }
    if (!session) {
      this.toothSliceGeometryMode = mode;
      this.dispatchToothModeChanged();
      return;
    }
    const previousPoint = cursorWorldPoint({
      cursor: session.cursor,
      frames: session.frames,
      origin: session.origin
    });
    const paneSetup = this.resolveToothPaneSetup(
      {
        ...session,
        anatomicalBasis: session.payload.anatomicalBasis,
        basis: session.rotatedBasis,
        seriesFrames: session.payload.seriesFrames
      },
      mode
    );
    if (!paneSetup) {
      this.dispatchToothModeChanged();
      return;
    }
    this.toothSliceGeometryMode = mode;
    session.frames = paneSetup.frames;
    session.spec = paneSetup.spec;
    session.scrollBounds = paneSetup.scrollBounds;
    session.scrollStepMm = paneSetup.scrollStepMm;
    const nextCursor = previousPoint ? worldPointToCursor({
      frames: paneSetup.frames,
      origin: session.origin,
      point: previousPoint
    }) : null;
    session.cursor = clampToothCursor({
      bounds: paneSetup.scrollBounds,
      cursor: nextCursor ?? { axial: 0, coronal: 0, sagittal: 0 }
    });
    this.applyToothPaneShapes(session, { preserveView: true });
    this.updateCrosshairs();
    this.scheduleRenderAll();
    this.dispatchToothModeChanged();
  }
  dispatchToothModeChanged() {
    const session = this.toothSession;
    this.view.dispatchEvent({
      type: "fullHeadMPRToothModeChanged",
      active: session !== null,
      geometryMode: this.toothSliceGeometryMode,
      renderPath: session ? this.toothRenderPath : null,
      roiStatus: session?.roiStatus ?? "idle",
      toothIso: session?.toothIso ?? null
    });
  }
  // Reported so a silent CPU fallback (a degenerate slab, a refused texture) is
  // visible instead of looking like a working GPU path.
  setToothRenderPath(path) {
    if (this.toothRenderPath === path) {
      return;
    }
    this.toothRenderPath = path;
    this.dispatchToothModeChanged();
  }
  resolveToothPaneSetup(context, mode) {
    const reader = this.reader;
    if (!reader) {
      return null;
    }
    const frames = resolveToothPaneFrames({
      anatomicalBasis: context.anatomicalBasis,
      basis: context.basis,
      mode,
      seriesFrames: context.seriesFrames
    });
    if (!frames) {
      return null;
    }
    const spacing = reader.geometry.spacing;
    const boxMm = volumeBoxMm(reader.geometry);
    const panes = VIEWPORT_ORDER.map((pane) => {
      const frame = frames[pane];
      const bounds = resolveToothScrollBounds({
        boxMm,
        frame,
        origin: context.origin
      });
      const stepMm = resolveToothScrollStepMm({
        descriptors: context.sliceDescriptors,
        frame,
        pane,
        spacing
      });
      if (!bounds || stepMm === null) {
        return null;
      }
      return { pane, bounds, stepMm };
    });
    if (panes.some((pane) => pane === null)) {
      return null;
    }
    const scrollBounds = {};
    const scrollStepMm = {};
    panes.forEach((pane) => {
      scrollBounds[pane.pane] = pane.bounds;
      scrollStepMm[pane.pane] = pane.stepMm;
    });
    const spec = buildToothPaneLayoutSpec({
      origin: context.origin,
      spacing,
      volumeBoxMm: boxMm
    });
    if (!spec) {
      return null;
    }
    return { frames, scrollBounds, scrollStepMm, spec };
  }
  tryStartToothSession(payload, sliceDescriptors) {
    const reader = this.reader;
    if (!reader || !payload.anatomicalBasis || !payload.outerBox || payload.iso === void 0) {
      return false;
    }
    const basis = resolveToothBasis({
      anatomicalBasis: payload.anatomicalBasis,
      iso: payload.iso
    });
    if (!basis) {
      return false;
    }
    const context = {
      anatomicalBasis: payload.anatomicalBasis,
      basis,
      origin: payload.origin,
      outerBox: payload.outerBox,
      sliceDescriptors,
      seriesFrames: payload.seriesFrames
    };
    const paneSetup = this.resolveToothPaneSetup(
      context,
      this.toothSliceGeometryMode
    );
    if (!paneSetup) {
      return false;
    }
    const roiBox = buildStableToothRoi({
      basis,
      configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
      geometry: reader.geometry,
      origin: payload.origin,
      outerBox: payload.outerBox
    });
    if (!roiBox) {
      return false;
    }
    const window2 = payload.window && Number.isFinite(payload.window.wc) && Number.isFinite(payload.window.ww) && payload.window.ww > 0 ? payload.window : null;
    this.toothRoiAbort?.abort();
    this.toothRoiAbort = null;
    this.toothRoiGeneration += 1;
    const session = {
      rotatedBasis: basis,
      cursor: { axial: 0, coronal: 0, sagittal: 0 },
      frames: paneSetup.frames,
      initialZoom: this.resolveToothInitialZooms(paneSetup.spec),
      origin: payload.origin,
      outerBox: payload.outerBox,
      payload,
      roiBox,
      roiStatus: "loading",
      scrollBounds: paneSetup.scrollBounds,
      scrollStepMm: paneSetup.scrollStepMm,
      sliceDescriptors,
      spec: paneSetup.spec,
      toothIso: payload.iso,
      window: window2
    };
    this.toothSession = session;
    this.pendingInteractionMetric = null;
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      viewportState.requiredChunkIndices = [];
      reader.releaseConsumerDemand(viewportState.consumerId);
    });
    const originVoxel = worldToVoxel(reader, payload.origin);
    if (originVoxel) {
      this.focus = clampFocusVoxel(reader, originVoxel);
      this.view.dispatchEvent({
        type: "fullHeadMPRFocusChanged",
        focus: this.focus,
        reason: "tooth"
      });
    }
    this.applyToothPaneShapes(session);
    this.updateCrosshairs();
    this.dispatchToothModeChanged();
    this.setAllViewportStatus("loading", "Loading tooth region...");
    void this.loadToothRoi(session);
    return true;
  }
  async loadToothRoi(session) {
    const reader = this.reader;
    if (!reader) {
      return;
    }
    const generation = this.toothRoiGeneration;
    const abort = new AbortController();
    this.toothRoiAbort = abort;
    const requiredChunks = findToothRoiChunks({
      reader,
      voxelBox: session.roiBox
    });
    try {
      if (this.selectedBackend === "cpu") {
        session.roiStatus = "ready";
        this.dispatchToothModeChanged();
        this.scheduleRenderAll();
        return;
      }
      const gpuRenderer = this.ensureGpuRenderer();
      await reader.ensureChunksLoaded(
        requiredChunks.map((chunk) => chunk.index).filter((index) => !gpuRenderer.hasUploadedChunk(index)),
        abort.signal,
        { priority: "focus", retention: "stream" }
      );
      if (this.toothRoiGeneration === generation && this.toothSession === session) {
        session.roiStatus = "ready";
        this.dispatchToothModeChanged();
        this.scheduleRenderAll();
      }
    } catch (error) {
      if (isAbortError(error) || this.toothRoiGeneration !== generation || this.toothSession !== session) {
        return;
      }
      if (this.demoteToCpu(error)) {
        return;
      }
      const payload = session.payload;
      this.exitToothSession();
      this.handleError(error, "Failed to load the tooth region.");
      this.applyWorldFocus(
        payload.origin,
        "tooth",
        buildToothFocusGeometry(payload).focusBox
      );
    } finally {
      if (this.toothRoiAbort === abort) {
        this.toothRoiAbort = null;
      }
    }
  }
  exitToothSession() {
    if (!this.toothSession) {
      return;
    }
    this.restoredView = null;
    this.savedRenderAborts.forEach((abort) => abort.abort());
    this.savedRenderAborts.clear();
    this.toothRoiGeneration += 1;
    this.toothRoiAbort?.abort();
    this.toothRoiAbort = null;
    this.toothSession = null;
    this.toothRenderPath = null;
    if (this.reader) {
      this.clearViewportFrames();
      this.updateViewportShapes();
    }
    this.updateCrosshairs();
    this.dispatchToothModeChanged();
  }
  // One-way, and it must stay reachable from every path that can find the
  // monolith unusable: a refused allocation reported but not demoted leaves the
  // backend claiming gpu-monolith, so the next frame asks the same monolith
  // again and repeats the failure instead of falling back.
  demoteToCpu(error) {
    if (!(error instanceof VolumeGpuError) || this.selectedBackend !== "gpu-monolith" || this.demotedToCpu) {
      return false;
    }
    const reason = error.message;
    this.demotedToCpu = true;
    console.warn(`[FullHeadMPR] ${reason} \u2192 demoting to cpu`);
    this.setBackend("cpu");
    return true;
  }
  // The whole-volume texture a tooth pane samples when the monolith is available.
  // Going through ensureGpuRenderer also publishes the background demand for every
  // chunk, which is what fills the volume around the tooth in.
  prepareToothVolumeTexture() {
    if (this.selectedBackend !== "gpu-monolith" || !this.reader) {
      return null;
    }
    return this.ensureGpuRenderer().prepareVolumeTexture(this.reader);
  }
  applyToothPaneShapes(session, { preserveView = false } = {}) {
    VIEWPORT_ORDER.forEach((projection) => {
      const spec = session.spec;
      const viewportState = this.viewportStateByProjection[projection];
      viewportState.aspectRatio = spec.widthPx / spec.heightPx;
      viewportState.scene?.clearFrame();
      viewportState.hasFrame = false;
      viewportState.scene?.setShape(spec.widthPx, spec.heightPx);
      if (!preserveView) {
        this.zoomPanByProjection[projection] = {
          zoom: session.initialZoom[projection],
          panX: 0,
          panY: 0
        };
      }
      this.clampPan(projection);
      this.applyViewportCamera(projection);
      viewportState.viewport?.render();
    });
  }
  applyToothCursor(session, cursor, reason, pane) {
    session.cursor = pane ? clampToothCursorComponent({
      bounds: session.scrollBounds,
      cursor,
      pane
    }) : clampToothCursor({
      bounds: session.scrollBounds,
      cursor
    });
    const point = cursorWorldPoint({
      cursor: session.cursor,
      frames: session.frames,
      origin: session.origin
    });
    if (point && this.reader) {
      const voxel = worldToVoxel(this.reader, point);
      if (voxel) {
        this.focus = clampFocusVoxel(this.reader, voxel);
        this.view.dispatchEvent({
          type: "fullHeadMPRFocusChanged",
          focus: this.focus,
          reason
        });
      }
    }
    this.updateCrosshairs();
    this.scheduleRenderAll();
  }
  applyToothRotation(session, projection, angleRad) {
    const pivot = cursorWorldPoint({
      cursor: session.cursor,
      frames: session.frames,
      origin: session.origin
    });
    if (!pivot) {
      return;
    }
    const rotated = rotateCrossingPanes({
      angleRad,
      cursor: session.cursor,
      frames: session.frames,
      origin: session.origin,
      pivot,
      projection
    });
    if (!rotated) {
      return;
    }
    session.frames = rotated.frames;
    session.cursor = rotated.cursor;
    if (this.toothSliceGeometryMode === "orthogonal" && angleRad !== 0) {
      this.toothSliceGeometryMode = "series";
      this.dispatchToothModeChanged();
    }
    const boxMm = volumeBoxMm(this.reader.geometry);
    VIEWPORT_ORDER.filter((pane) => pane !== projection).forEach((pane) => {
      const bounds = resolveToothScrollBounds({
        boxMm,
        frame: rotated.frames[pane],
        origin: session.origin
      });
      const stepMm = resolveToothScrollStepMm({
        descriptors: session.sliceDescriptors,
        frame: rotated.frames[pane],
        pane,
        spacing: this.reader.geometry.spacing
      });
      if (bounds) {
        session.scrollBounds[pane] = bounds;
      }
      if (stepMm !== null) {
        session.scrollStepMm[pane] = stepMm;
      }
    });
    this.updateCrosshairs();
    this.scheduleRenderAll();
  }
  resolveToothInitialZooms(spec) {
    return Object.fromEntries(
      VIEWPORT_ORDER.map((pane) => {
        const surface = this.viewportStateByProjection[pane].surface;
        if (surface.clientHeight <= 0 || surface.clientWidth <= 0) {
          return [pane, 1];
        }
        return [
          pane,
          clamp(
            resolveToothInitialZoom({
              spec,
              surfaceAspect: surface.clientWidth / surface.clientHeight
            }),
            VOLUME_ZOOM_LIMITS.minZoom,
            TOOTH_ZOOM_CEILING
          )
        ];
      })
    );
  }
  // A pane whose surface had no height when the session opened kept zoom 1, which
  // frames the whole volume instead of the tooth — the surface can gain its size
  // later, from a collapsed panel or a settling layout. The same resize pass now
  // also refits a restored saved view whose untouched fit zoom assumed a different
  // pane aspect, while a customized zoom/pan is always left alone.
  settleToothInitialZoom(projection) {
    const session = this.toothSession;
    const restoredView = this.restoredView;
    const { surface } = this.viewportStateByProjection[projection];
    const view = this.zoomPanByProjection[projection];
    if (!session || !restoredView && session.initialZoom[projection] !== 1 || surface.clientWidth <= 0 || surface.clientHeight <= 0) {
      return;
    }
    const nextFitZoom = restoredView ? resolveToothPaneFitZoom({
      frustumPx: this.computeLetterboxFrustum(projection),
      heightMm: restoredView.heightMm,
      mmPerPixel: session.spec.mmPerPixel,
      widthMm: restoredView.widthMm
    }) : this.resolveToothInitialZooms(session.spec)[projection];
    const settled = resolveToothPaneRefitZoom({
      initialZoom: session.initialZoom[projection],
      nextFitZoom,
      zoomPan: view
    });
    if (settled === null || settled === session.initialZoom[projection]) {
      return;
    }
    session.initialZoom[projection] = settled;
    view.zoom = settled;
  }
  toothPaneCenters(session) {
    return resolveToothPaneCenters({
      cursor: session.cursor,
      frames: session.frames,
      origin: session.origin
    });
  }
  toothCrosshairs(session) {
    const point = cursorWorldPoint({
      cursor: session.cursor,
      frames: session.frames,
      origin: session.origin
    });
    if (!point) {
      return null;
    }
    return resolveToothCrosshairs({
      centers: this.toothPaneCenters(session),
      frames: session.frames,
      point,
      spec: session.spec
    });
  }
  toothPaneCrossingPx(session, projection) {
    const crosshairs = this.toothCrosshairs(session);
    const trace = Object.values(crosshairs?.[projection] ?? {}).find(
      (candidate) => candidate
    );
    if (!trace) {
      return null;
    }
    return {
      x: trace.leftFraction * session.spec.widthPx,
      y: trace.topFraction * session.spec.heightPx
    };
  }
  pickToothLine(session, projection, event) {
    const crosshairs = this.toothCrosshairs(session);
    const crosshair = crosshairs?.[projection];
    const pointer = this.clientToPanePx(
      projection,
      event.clientX,
      event.clientY
    );
    if (!crosshair || !pointer) {
      return null;
    }
    return pickToothCrosshairLines({
      crosshair,
      heightPx: session.spec.heightPx,
      pointerPx: pointer,
      tolerancePx: this.toothLineTolerancePx(projection),
      widthPx: session.spec.widthPx
    });
  }
  clientToPanePx(projection, clientX, clientY, options) {
    const fraction = this.clientToPaneFraction(
      projection,
      clientX,
      clientY,
      options
    );
    if (!fraction) {
      return null;
    }
    const { width, height } = this.getPaneShape(projection);
    return {
      x: fraction.fractionX * width,
      y: fraction.fractionY * height
    };
  }
  updateToothLineCursor(projection, event) {
    if (this.annotations?.ownsPointer(projection)) {
      return;
    }
    const surface = this.viewportStateByProjection[projection].surface;
    const session = this.toothSession;
    if (!session || !isPointerFocusMode(this.inputMode)) {
      surface.style.cursor = PANE_CURSOR_BY_MODE[this.inputMode];
      return;
    }
    const pick = this.pickToothLine(session, projection, event);
    if (pick?.isOnCrossing) {
      surface.style.cursor = TOOTH_LINE_CURSOR.crossing;
      return;
    }
    surface.style.cursor = pick?.grabbed.length === 1 ? TOOTH_LINE_CURSOR.line : PANE_CURSOR_BY_MODE[this.inputMode];
  }
  resolveGrabbedToothLine(session, projection, event) {
    const pick = this.pickToothLine(session, projection, event);
    if (!pick || pick.isOnCrossing || pick.grabbed.length !== 1) {
      return null;
    }
    return pick.grabbed[0];
  }
  toothLineTolerancePx(projection) {
    const { left, right } = this.computeLetterboxFrustum(projection);
    const surfaceWidth = this.viewportStateByProjection[projection].surface.clientWidth;
    const zoom = this.zoomPanByProjection[projection].zoom;
    if (surfaceWidth <= 0 || zoom <= 0) {
      return TOOTH_LINE_TOLERANCE_SCREEN_PX;
    }
    return TOOTH_LINE_TOLERANCE_SCREEN_PX * (right - left) / (surfaceWidth * zoom);
  }
  toothCursorFromPointer(session, projection, event) {
    const fraction = this.pointerToPaneFraction(projection, event);
    if (!fraction) {
      return null;
    }
    const frame = session.frames[projection];
    const spec = session.spec;
    const center = this.toothPaneCenters(session)[projection];
    const inPlaneU = (fraction.fractionX - 0.5) * spec.halfUMm * 2;
    const inPlaneV = (0.5 - fraction.fractionY) * spec.halfVMm * 2;
    const point = {
      x: center.x + frame.u.x * inPlaneU + frame.v.x * inPlaneV,
      y: center.y + frame.u.y * inPlaneU + frame.v.y * inPlaneV,
      z: center.z + frame.u.z * inPlaneU + frame.v.z * inPlaneV
    };
    return worldPointToCursor({
      frames: session.frames,
      origin: session.origin,
      point
    });
  }
  // Accepts a point in patient-CS world coordinates (mm) and converts it to voxel
  // space here (single source of truth for the geometry math), then delegates to
  // setFocus. Tooth calls without ROI metadata keep the legacy 33 mm fallback.
  setFocusWorld(point, reason = "api") {
    this.applyWorldFocus(point, reason, null);
  }
  applyWorldFocus(point, reason, focusBox) {
    if (!this.reader) {
      return;
    }
    const voxel = worldToVoxel(this.reader, point);
    if (!voxel) {
      return;
    }
    this.setFocus(voxel, reason);
    if (!this.focus) {
      return;
    }
    if (reason === "tooth") {
      this.prioritizeToothChunks(point, focusBox);
    }
    VIEWPORT_ORDER.forEach((projection) => {
      const view = this.zoomPanByProjection[projection];
      if (reason === "tooth") {
        view.zoom = focusBox ? this.focusZoomForBox(projection, point, focusBox) : this.focusZoomForProjection(projection);
      } else if (view.zoom <= 1) {
        return;
      }
      const shape = getOrthogonalProjectionShape(
        this.reader.geometry,
        projection
      );
      const { left, top } = getCrosshairPercentages(
        this.reader,
        this.focus,
        projection
      );
      view.panX = left * shape.width - shape.width / 2;
      view.panY = top * shape.height - shape.height / 2;
      this.clampPan(projection);
      this.applyViewportCamera(projection);
      this.viewportStateByProjection[projection].viewport?.render();
    });
    this.updateCrosshairs();
  }
  setFocus(focus, reason = "api") {
    if (!this.reader) {
      return;
    }
    const nextFocus = clampFocusVoxel(this.reader, focus);
    const previousFocus = this.focus;
    if (previousFocus && previousFocus.x === nextFocus.x && previousFocus.y === nextFocus.y && previousFocus.z === nextFocus.z) {
      return;
    }
    this.focus = nextFocus;
    this.pendingInteractionMetric = reason === "init" ? null : {
      reason,
      startedAt: now(),
      backend: this.selectedBackend,
      projection: this.activeProjection
    };
    this.updateCrosshairs();
    this.scheduleRenderAll();
    this.view.dispatchEvent({
      type: "fullHeadMPRFocusChanged",
      focus: nextFocus,
      reason
    });
  }
  attachReaderListeners(reader) {
    this.subscriptions.push(
      reader.subscribeToLoadedChunkCount(
        (loadedChunkCount, totalChunkCount) => {
          this.loadedChunkCount = loadedChunkCount;
          this.totalChunkCount = totalChunkCount;
          this.updateSummary();
          this.view.dispatchEvent({
            type: "fullHeadMPRChunkLoadProgress",
            loadedChunkCount,
            totalChunkCount
          });
        }
      )
    );
    this.subscriptions.push(
      reader.subscribeToChunkLoads((chunkIndex) => {
        const monolithBackend = this.selectedBackend === "gpu-monolith";
        if (this.toothSession && !monolithBackend) {
          return;
        }
        VIEWPORT_ORDER.forEach((projection) => {
          const viewportState = this.viewportStateByProjection[projection];
          if (this.imageOptionsByProjection.has(projection)) {
            return;
          }
          if (!monolithBackend && !viewportState.requiredChunkIndices.includes(chunkIndex)) {
            return;
          }
          this.scheduleViewportRender(projection);
        });
      })
    );
    this.metricsUnsubscribe = reader.subscribeToMetrics((metrics) => {
      if (this.debugOptions.emitMetricsOnEveryUpdate === false) {
        return;
      }
      this.view.dispatchEvent({
        type: "fullHeadMPRMetricsUpdated",
        metrics
      });
    });
  }
  async warmupInitialViewport(reader) {
    const focus = this.focus;
    if (!focus) {
      return;
    }
    const initialChunkIndices = this.getMissingRenderChunkIndices(
      getOrthogonalSliceChunkIndices(
        reader,
        this.activeProjection,
        getSliceIndexForProjection(focus, this.activeProjection)
      )
    );
    if (!initialChunkIndices.length) {
      return;
    }
    await reader.ensureChunksLoaded(
      initialChunkIndices,
      this.abortController?.signal,
      {
        maxConcurrentRanges: 4,
        priority: "visible"
      }
    );
  }
  resolveBackend(preferredBackend) {
    const requested = preferredBackend ?? "auto";
    if (requested === "cpu") {
      return "cpu";
    }
    return this.resolveMonolithOrCpu();
  }
  resolveMonolithOrCpu() {
    return this.canUseFullResolutionMonolith() ? "gpu-monolith" : "cpu";
  }
  canUseFullResolutionMonolith() {
    if (!this.reader) {
      return false;
    }
    const max3DTextureSize = getMax3DTextureSize(this.view.getRenderer());
    if (max3DTextureSize <= 0) {
      return false;
    }
    return !this.view.getRenderer().getContext().isContextLost();
  }
  // Auto window from the data's 2nd..98th percentile — robust to outliers
  // (metal/artifact at +7000, air clamp at -2047) that would otherwise blow the
  // window up (min/max gave ww~9000 centered on metal, crushing anatomy dark).
  computeAutoColorProfile(reader) {
    const samples = [];
    reader.chunks.forEach((chunk) => {
      const data = reader.getChunkData(chunk.index);
      if (!data) {
        return;
      }
      for (let i = 0; i < data.length; i += 257) {
        samples.push(data[i]);
      }
    });
    if (samples.length < 16) {
      return null;
    }
    samples.sort((left, right) => left - right);
    const lo = samples[Math.floor(samples.length * 0.02)];
    const hi = samples[Math.floor(samples.length * 0.98)];
    if (hi <= lo) {
      return null;
    }
    return { wc: (lo + hi) / 2, ww: Math.max(1, hi - lo) };
  }
  ensureGpuRenderer() {
    if (!this.gpuRendererInstance) {
      if (this.selectedBackend !== "gpu-monolith") {
        throw new VolumeGpuError(
          null,
          "GPU renderer requested for the cpu backend"
        );
      }
      const lease = acquireFullHeadMPRGpuVolume(this.reader);
      if (!lease) {
        throw new VolumeGpuError(
          null,
          "Full-resolution GPU volume is unavailable"
        );
      }
      this.gpuVolumeLease = lease;
      this.gpuRendererInstance = lease.renderer;
    }
    return this.gpuRendererInstance;
  }
  getMissingRenderChunkIndices(indices) {
    const gpuRenderer = this.selectedBackend === "gpu-monolith" ? this.gpuRendererInstance : null;
    return gpuRenderer ? indices.filter((index) => !gpuRenderer.hasUploadedChunk(index)) : indices;
  }
  async renderSliceWithBackend(projection, sliceIndex, loadOptions, mode) {
    const reader = this.reader;
    const signal = this.abortController?.signal;
    const startedAt = now();
    if (this.selectedBackend === "cpu") {
      const result2 = await renderOrthogonalSlice(
        reader,
        projection,
        sliceIndex,
        this.effectiveWindow(this.colorProfile),
        loadOptions,
        mode,
        signal
      );
      this.benchmark.recordSample("cpu", "frameRenderMs", now() - startedAt);
      this.benchmark.incrementCounter("cpu", "frameCount");
      return result2;
    }
    const gpu = this.ensureGpuRenderer();
    const backend = this.selectedBackend;
    const result = await gpu.renderSlice(
      reader,
      projection,
      sliceIndex,
      this.effectiveWindow(this.colorProfile),
      loadOptions,
      mode,
      signal
    );
    this.benchmark.recordSample(backend, "frameRenderMs", now() - startedAt);
    this.benchmark.incrementCounter(backend, "frameCount");
    const repack = gpu.getLastRepack();
    if (repack.didRepack) {
      this.benchmark.recordSample(backend, "repackMs", repack.repackMs);
      this.benchmark.incrementCounter(backend, "repackCount");
      this.framesSinceLastRepack = 0;
    } else {
      this.framesSinceLastRepack += 1;
    }
    return result;
  }
  handleContextLost() {
    this.annotations?.refresh();
    this.setGpuTimingEnabled(false);
    this.restoreGpuAfterContextLoss = this.selectedBackend !== "cpu";
    this.setBackend("cpu");
  }
  handleContextRestored() {
    this.annotations?.refresh();
    this.setGpuTimingEnabled(true);
    const restoreGpu = this.restoreGpuAfterContextLoss;
    this.restoreGpuAfterContextLoss = false;
    if (restoreGpu && this.reader) {
      this.setBackend("auto");
    }
  }
  drawCpuViewport(viewport, context, rect) {
    const state = Object.values(this.viewportStateByProjection).find(
      (candidate) => candidate.viewport === viewport
    );
    state?.scene?.drawCpu({ camera: viewport.camera, context, rect });
  }
  setGpuTimingEnabled(enabled) {
    this.gpuTimer?.dispose();
    this.gpuTimer = enabled ? new GpuTimer(
      this.view.getRenderer(),
      () => this.selectedBackend,
      (label, ms) => this.benchmark.recordSample(
        label,
        "drawMs",
        ms
      )
    ) : null;
    Object.values(this.viewportStateByProjection).forEach(({ viewport }) => {
      if (viewport) {
        viewport.viewportRenderer.gpuProbe = this.gpuTimer ?? void 0;
      }
    });
  }
  setBackend(backend) {
    const resolved = this.resolveBackend(backend);
    if (resolved === this.selectedBackend) {
      this.view.dispatchEvent({
        type: "fullHeadMPRBackendChanged",
        backend: resolved
      });
      return;
    }
    this.selectedBackend = resolved;
    this.benchmark.startEpoch(resolved);
    this.lastBackendSwitchAt = now();
    this.framesSinceLastRepack = Number.MAX_SAFE_INTEGER;
    this.disposeGpuRendererInstance();
    this.firstFramePainted = false;
    const session = this.toothSession;
    if (session) {
      this.toothRoiGeneration += 1;
      this.toothRoiAbort?.abort();
      this.toothRoiAbort = null;
      if (!this.restoredView) {
        void this.loadToothRoi(session);
      }
    }
    this.view.dispatchEvent({
      type: "fullHeadMPRBackendChanged",
      backend: resolved
    });
    this.scheduleRenderAll();
  }
  disposeGpuRendererInstance() {
    this.gpuVolumeLease?.release();
    this.gpuVolumeLease = null;
    this.gpuRendererInstance = null;
  }
  updateViewportShapes() {
    if (!this.reader) {
      return;
    }
    VIEWPORT_ORDER.forEach((projection) => {
      const shape = getOrthogonalProjectionShape(
        this.reader.geometry,
        projection
      );
      const viewportState = this.viewportStateByProjection[projection];
      viewportState.aspectRatio = shape.aspectRatio;
      viewportState.maxSliceIndex = shape.maxSliceIndex;
      viewportState.scene?.setShape(shape.width, shape.height);
      this.applyViewportCamera(projection);
    });
  }
  // World rect of the pane's quad: the tooth raster while a tooth session is
  // active, the orthogonal slice shape otherwise. Keeps the camera, pan and
  // pointer math shared between the two rendering paths.
  getPaneShape(projection) {
    const session = this.toothSession;
    if (session) {
      return { width: session.spec.widthPx, height: session.spec.heightPx };
    }
    const shape = getOrthogonalProjectionShape(
      this.reader.geometry,
      projection
    );
    return { width: shape.width, height: shape.height };
  }
  // Letterbox ortho frustum matching the surface aspect (bars on the longer
  // axis, centered) so the slice fits without distortion; top<bottom keeps the
  // inverted Y. Pure (no camera mutation) — also used to derive tooth zoom.
  computeLetterboxFrustum(projection) {
    const { width, height } = this.getPaneShape(projection);
    const { surface } = this.viewportStateByProjection[projection];
    const surfW = surface.clientWidth;
    const surfH = surface.clientHeight;
    let left = 0;
    let right = width;
    let top = 0;
    let bottom = height;
    if (surfW > 0 && surfH > 0) {
      const aspect = surfW / surfH;
      if (aspect >= width / height) {
        const frustumWidth = height * aspect;
        left = width / 2 - frustumWidth / 2;
        right = width / 2 + frustumWidth / 2;
      } else {
        const frustumHeight = width / aspect;
        top = height / 2 - frustumHeight / 2;
        bottom = height / 2 + frustumHeight / 2;
      }
    }
    return { left, right, top, bottom };
  }
  // Re-applied from a default-priority 'resize' listener so it wins over the
  // CameraHelper's priority-10 resize handler.
  applyViewportCamera(projection) {
    if (!this.reader) {
      return;
    }
    const viewportState = this.viewportStateByProjection[projection];
    const camera = viewportState.viewport?.camera;
    if (!(camera instanceof OrthographicCamera)) {
      return;
    }
    const { left, right, top, bottom } = this.computeLetterboxFrustum(projection);
    const view = this.zoomPanByProjection[projection];
    camera.left = left;
    camera.right = right;
    camera.top = top;
    camera.bottom = bottom;
    camera.near = -1e3;
    camera.far = 1e3;
    camera.zoom = view.zoom;
    camera.position.set(view.panX, view.panY, 0);
    camera.updateProjectionMatrix();
    camera.updateMatrixWorld(true);
    this.scheduleRasterRepaint(projection);
    this.view.dispatchEvent({ type: "fullHeadMPRViewUpdated", projection });
  }
  scheduleRasterRepaint(projection) {
    const hasToothRaster = this.toothSession && (this.restoredView || this.toothRenderPath && CPU_TOOTH_RENDER_PATHS.includes(this.toothRenderPath));
    if (!hasToothRaster && !this.imageOptionsByProjection.has(projection)) {
      return;
    }
    this.scheduleViewportRender(projection);
  }
  // Fallback zoom for old clients that only pass a focus point. The letterbox
  // frustum's vertical extent is zoom-independent, so read it directly.
  focusZoomForProjection(projection) {
    if (!this.reader) {
      return 1;
    }
    const { top, bottom } = this.computeLetterboxFrustum(projection);
    const mmPerVoxel = projection === "axial" ? this.reader.geometry.spacing.y : this.reader.geometry.spacing.z;
    const frustumVerticalMm = (bottom - top) * mmPerVoxel;
    if (!(frustumVerticalMm > 0)) {
      return this.zoomPanByProjection[projection].zoom;
    }
    return clamp(
      frustumVerticalMm / TOOTH_FOCUS_LEGACY_FOV_MM,
      VOLUME_ZOOM_LIMITS.minZoom,
      VOLUME_ZOOM_LIMITS.maxZoom
    );
  }
  focusZoomForBox(projection, origin, focusBox) {
    if (!this.reader) {
      return 1;
    }
    const { left, right, top, bottom } = this.computeLetterboxFrustum(projection);
    const axes = projectionWorldAxes(projection);
    const spacing = this.reader.geometry.spacing;
    const frustumWidthMm = (right - left) * spacing[axes.horizontal];
    const frustumHeightMm = (bottom - top) * spacing[axes.vertical];
    const targetWidthMm = 2 * Math.max(
      Math.abs(focusBox.min[axes.horizontal] - origin[axes.horizontal]),
      Math.abs(focusBox.max[axes.horizontal] - origin[axes.horizontal])
    );
    const targetHeightMm = 2 * Math.max(
      Math.abs(focusBox.min[axes.vertical] - origin[axes.vertical]),
      Math.abs(focusBox.max[axes.vertical] - origin[axes.vertical])
    );
    if (!(frustumWidthMm > 0) || !(frustumHeightMm > 0)) {
      return this.zoomPanByProjection[projection].zoom;
    }
    if (!(targetWidthMm > 0) || !(targetHeightMm > 0)) {
      return this.focusZoomForProjection(projection);
    }
    return clamp(
      Math.min(
        frustumWidthMm / targetWidthMm,
        frustumHeightMm / targetHeightMm
      ),
      VOLUME_ZOOM_LIMITS.minZoom,
      VOLUME_ZOOM_LIMITS.maxZoom
    );
  }
  // A batch request at "focus" priority (above the active slice's "visible")
  // loads the whole tooth ROI cube first, in one wave, so it is not bundled
  // behind the active pane's full-slice visible batch.
  prioritizeToothChunks(point, focusBox) {
    const reader = this.reader;
    if (!reader) {
      return;
    }
    const half = TOOTH_FOCUS_LEGACY_FOV_MM / 2;
    const toothBox = focusBox ?? {
      min: { x: point.x - half, y: point.y - half, z: point.z - half },
      max: { x: point.x + half, y: point.y + half, z: point.z + half }
    };
    const indices = sortChunksByProximity(
      reader.findChunksIntersectingBox(toothBox),
      point,
      reader.geometry
    ).map((chunk) => chunk.index);
    void reader.ensureChunksLoaded(
      this.getMissingRenderChunkIndices(indices),
      this.abortController?.signal,
      {
        priority: "focus",
        maxConcurrentRanges: 8
      }
    ).catch((error) => {
      if (!isAbortError(error)) {
        this.handleError(error, "Failed to prioritize tooth chunks.");
      }
    });
  }
  // Pointer position → fraction of the pane's world rect (accounts for the
  // camera zoom/pan). Shared by the orthogonal and tooth pointer paths.
  pointerToPaneFraction(projection, event) {
    return this.clientToPaneFraction(projection, event.clientX, event.clientY);
  }
  clientToPaneFraction(projection, clientX, clientY, { clampToSurface = true } = {}) {
    if (!this.reader) {
      return null;
    }
    const viewportState = this.viewportStateByProjection[projection];
    const rect = viewportState.surface.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return null;
    }
    const rawX = (clientX - rect.left) / rect.width;
    const rawY = (clientY - rect.top) / rect.height;
    const normalizedX = clampToSurface ? clamp(rawX, 0, 1) : rawX;
    const normalizedY = clampToSurface ? clamp(rawY, 0, 1) : rawY;
    const camera = viewportState.viewport?.camera;
    if (!(camera instanceof OrthographicCamera)) {
      return { fractionX: normalizedX, fractionY: normalizedY };
    }
    const shape = this.getPaneShape(projection);
    const world = new Vector3(
      normalizedX * 2 - 1,
      -(normalizedY * 2 - 1),
      0
    ).unproject(camera);
    return {
      fractionX: world.x / shape.width,
      fractionY: world.y / shape.height
    };
  }
  focusFromPointer(projection, event) {
    if (!this.reader || !this.focus) {
      return null;
    }
    const fraction = this.pointerToPaneFraction(projection, event);
    if (!fraction) {
      return null;
    }
    const { fractionX, fractionY } = fraction;
    const voxelShape = this.reader.geometry.shape;
    const axes = ORTHOGONAL_PANE_AXES[projection];
    const nextFocus = {
      ...this.focus,
      [axes.horizontalAxis]: fractionX * Math.max(0, voxelShape[axes.horizontalAxis] - 1),
      [axes.verticalAxis]: voxelForPaneFraction({
        axes,
        extentVoxels: voxelShape[axes.verticalAxis] - 1,
        fraction: fractionY
      })
    };
    return clampFocusVoxel(this.reader, nextFocus);
  }
  clampPan(projection) {
    if (!this.reader) {
      return;
    }
    const { width, height } = this.getPaneShape(projection);
    const view = this.zoomPanByProjection[projection];
    view.panX = clampPanOffset({ offset: view.panX, sliceExtent: width });
    view.panY = clampPanOffset({ offset: view.panY, sliceExtent: height });
  }
  setActiveProjection(projection) {
    this.view.setActiveViewport(projection);
    if (this.activeProjection === projection) {
      return;
    }
    this.imageOptionsController.cancel();
    this.activeProjection = projection;
    this.dispatchActivePaneChanged();
  }
  dispatchActivePaneChanged() {
    if (!this.mounted) {
      return;
    }
    this.updateActivePaneFrames();
    this.view.dispatchEvent({
      type: "fullHeadMPRActivePaneChanged",
      element: this.viewportStateByProjection[this.activeProjection].pane,
      projection: this.activeProjection
    });
  }
  effectiveWindow(base) {
    return applyDisplayAdjust({
      adjust: this.displayAdjust,
      minimumWindowWidth: MIN_WINDOW_WIDTH_HU,
      window: base
    });
  }
  resetDisplayAdjust() {
    if (isNeutralDisplayAdjust(this.displayAdjust)) {
      return;
    }
    this.displayAdjust = NEUTRAL_DISPLAY_ADJUST;
    this.scheduleRenderAll();
  }
  applyCrosshairVisibility() {
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      const visibility = this.crosshairsVisible ? "" : "hidden";
      viewportState.crosshairVertical.style.visibility = visibility;
      viewportState.crosshairHorizontal.style.visibility = visibility;
    });
  }
  cancelPaneLayoutInteraction() {
    this.imageOptionsController.cancel();
    this.annotations?.cancelInteraction({ preserveSelection: true });
    this.handleDragEnd();
  }
  swapPrimaryProjection(projection) {
    const primary = this.getPrimaryProjection();
    this.paneOrder = this.paneOrder.map(
      (pane) => pane === projection ? primary : pane === primary ? projection : pane
    );
  }
  applyPaneLayout() {
    const isOnePlusTwo = this.paneLayout === "one_plus_two";
    const isOutlined = isPaneChromeTinted(
      this.paneLayout,
      this.chrome.paneFrame
    );
    const ordered = this.paneOrder.filter(
      (projection) => this.visibleProjections.includes(projection)
    );
    const hasPrimaryColumn = isOnePlusTwo && ordered.length > 1;
    const grid = this.viewportGridElement;
    const focusedElement = grid.ownerDocument.activeElement;
    const focusOrder = hasPrimaryColumn ? ordered : VIEWPORT_ORDER.filter(
      (projection) => this.visibleProjections.includes(projection)
    );
    focusOrder.forEach((projection, index) => {
      const pane = this.viewportStateByProjection[projection].pane;
      const current = grid.children[index];
      if (current !== pane) {
        grid.insertBefore(pane, current ?? null);
      }
    });
    if (focusedElement instanceof HTMLElement && grid.contains(focusedElement)) {
      if (grid.ownerDocument.activeElement !== focusedElement) {
        focusedElement.focus({ preventScroll: true });
      }
    }
    grid.style.gridTemplateColumns = hasPrimaryColumn ? "minmax(0, 2fr) minmax(0, 1fr)" : `repeat(${ordered.length}, minmax(0, 1fr))`;
    grid.style.gridTemplateRows = hasPrimaryColumn ? ordered.length === 3 ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)" : "";
    grid.style.gridTemplateAreas = hasPrimaryColumn ? ordered.length === 3 ? '"primary secondaryTop" "primary secondaryBottom"' : '"primary secondaryTop"' : "";
    grid.style.gap = isOutlined ? "4px" : "0";
    const areas = ["primary", "secondaryTop", "secondaryBottom"];
    VIEWPORT_ORDER.forEach((projection) => {
      const index = ordered.indexOf(projection);
      const { pane, surface } = this.viewportStateByProjection[projection];
      pane.style.gridArea = hasPrimaryColumn && index >= 0 ? areas[index] : "";
      pane.style.borderRadius = isOutlined ? "16px" : "0";
      pane.style.border = "none";
      surface.style.borderRadius = "inherit";
    });
    this.updateActivePaneFrames();
  }
  updateActivePaneFrames() {
    const isOutlined = isPaneChromeTinted(
      this.paneLayout,
      this.chrome.paneFrame
    );
    VIEWPORT_ORDER.forEach((projection) => {
      const { frameOverlay, hasKeyboardFocus, isHovered } = this.viewportStateByProjection[projection];
      frameOverlay.style.boxShadow = hasKeyboardFocus || isOutlined && isHovered && this.visibleProjections.length > 1 ? `inset 0 0 0 2px ${ORIENTATION_COLOR[projection]}` : "none";
    });
  }
  refreshPaneLayout() {
    this.applyPaneLayout();
    if (!this.mounted) {
      return;
    }
    const canvas = this.view.getCanvas();
    canvas.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
    this.visibleProjections.forEach((projection) => {
      this.savedRenderAborts.get(projection)?.abort();
      this.renderEpochByProjection.set(
        projection,
        (this.renderEpochByProjection.get(projection) ?? 0) + 1
      );
    });
    this.scheduleRenderAll();
    this.view.dispatchEvent({
      type: "fullHeadMPRPaneLayoutChanged",
      layout: this.paneLayout,
      primaryProjection: this.getPrimaryProjection(),
      visibleProjections: this.getVisibleProjections()
    });
  }
  applyChrome() {
    const showDevChrome = this.chrome.showDevChrome;
    this.rootElement.style.fontFamily = showDevChrome ? '"SFMono-Regular", ui-monospace, monospace' : "";
    this.rootElement.style.color = showDevChrome ? "#e8edf5" : "";
    this.rootElement.style.gap = showDevChrome ? "12px" : "0";
    this.applyPaneLayout();
    if (showDevChrome && this.summaryElement.parentElement !== this.rootElement) {
      this.rootElement.insertBefore(
        this.summaryElement,
        this.viewportGridElement
      );
    }
    if (!showDevChrome && this.summaryElement.parentElement === this.rootElement) {
      this.rootElement.removeChild(this.summaryElement);
    }
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      viewportState.statusLabel.style.display = showDevChrome ? "" : "none";
      viewportState.errorLabel.style.display = showDevChrome && viewportState.status === "error" ? "block" : "none";
    });
  }
  // A tooth pane frames the tooth inside a volume-wide raster, so its limits come
  // from that framing instead of the orthogonal slice's fixed ones.
  paneZoomLimits(projection) {
    const session = this.toothSession;
    return session ? resolveToothZoomLimits({ initialZoom: session.initialZoom[projection] }) : VOLUME_ZOOM_LIMITS;
  }
  applyZoom(projection, nextZoom) {
    const view = this.zoomPanByProjection[projection];
    const limits = this.paneZoomLimits(projection);
    view.zoom = clamp(nextZoom, limits.minZoom, limits.maxZoom);
    if (!this.zoomSyncEnabled) {
      return;
    }
    VIEWPORT_ORDER.filter((current) => current !== projection).forEach(
      (current) => {
        const currentLimits = this.paneZoomLimits(current);
        this.zoomPanByProjection[current].zoom = clamp(
          view.zoom,
          currentLimits.minZoom,
          currentLimits.maxZoom
        );
        this.clampPan(current);
        this.applyViewportCamera(current);
        this.viewportStateByProjection[current].viewport?.render();
      }
    );
  }
  zoomAtCursor(projection, event) {
    const viewportState = this.viewportStateByProjection[projection];
    const camera = viewportState.viewport?.camera;
    if (!(camera instanceof OrthographicCamera)) {
      return;
    }
    const rect = viewportState.surface.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    const nx = clamp((event.clientX - rect.left) / rect.width, 0, 1);
    const ny = clamp((event.clientY - rect.top) / rect.height, 0, 1);
    const cursorNDC = () => new Vector3(nx * 2 - 1, -(ny * 2 - 1), 0);
    const view = this.zoomPanByProjection[projection];
    const before = cursorNDC().unproject(camera);
    this.applyZoom(
      projection,
      view.zoom * getStudioWheelZoomFactor(event, rect.height)
    );
    this.applyViewportCamera(projection);
    const after = cursorNDC().unproject(camera);
    view.panX += before.x - after.x;
    view.panY += before.y - after.y;
    this.clampPan(projection);
    this.applyViewportCamera(projection);
    viewportState.viewport?.render();
    this.updateCrosshairs();
  }
  resetView(projection) {
    const session = this.toothSession;
    const projections = projection ? [projection] : VIEWPORT_ORDER;
    projections.forEach((current) => {
      const view = this.zoomPanByProjection[current];
      view.zoom = session ? session.initialZoom[current] : 1;
      view.panX = 0;
      view.panY = 0;
      this.settleToothInitialZoom(current);
      this.applyViewportCamera(current);
      this.viewportStateByProjection[current].viewport?.render();
    });
    this.updateCrosshairs();
  }
  resolveDragIntent(event) {
    if (event.button === MIDDLE_MOUSE_BUTTON) {
      return "pan";
    }
    const isModified = event.shiftKey || event.ctrlKey || event.metaKey;
    if (event.button === RIGHT_MOUSE_BUTTON) {
      return !isModified && resolveRightButtonIntent(this.inputMode) === "rotate" ? "rotate" : "none";
    }
    if (event.button !== 0) {
      return "none";
    }
    const intent = resolvePrimaryDragIntent(this.inputMode);
    if (isModified && (intent === "brightnessContrast" || intent === "zoom")) {
      return "none";
    }
    return intent;
  }
  resolveToothDragIntent(projection, event) {
    const session = this.toothSession;
    const requestedIntent = this.resolveDragIntent(event);
    if (session && requestedIntent === "none" && isPointerFocusMode(this.inputMode)) {
      const grabbedPane = this.resolveGrabbedToothLine(
        session,
        projection,
        event
      );
      return grabbedPane ? { grabbedPane, intent: "toothLine" } : { grabbedPane: null, intent: "none" };
    }
    if (requestedIntent === "rotate" && !session) {
      return { grabbedPane: null, intent: "none" };
    }
    return { grabbedPane: null, intent: requestedIntent };
  }
  startDrag(projection, event) {
    if (this.imageOptionsController.hasTool() && event.button === 0 && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
      this.setActiveProjection(projection);
      return;
    }
    this.imageOptionsController.cancel();
    if (event.button === 0 && this.annotations?.ownsPointer(projection)) {
      return;
    }
    this.annotations?.cancelInteraction();
    if (!this.reader || !this.focus) {
      return;
    }
    const { grabbedPane, intent } = this.resolveToothDragIntent(
      projection,
      event
    );
    if (intent === "none") {
      if (event.button === 0 && isPointerFocusMode(this.inputMode)) {
        this.setActiveProjection(projection);
        this.applyPointerFocus(projection, event);
      }
      return;
    }
    const viewportState = this.viewportStateByProjection[projection];
    const rect = viewportState.surface.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return;
    }
    event.preventDefault();
    this.handleDragEnd();
    this.setActiveProjection(projection);
    this.drag = {
      intent,
      projection,
      grabbedPane,
      prevClientX: event.clientX,
      prevClientY: event.clientY,
      prevNDC: {
        x: (event.clientX - rect.left) / rect.width * 2 - 1,
        y: -((event.clientY - rect.top) / rect.height * 2 - 1)
      },
      scrollAccumulatorPx: 0,
      startCursor: this.toothSession ? { ...this.toothSession.cursor } : null,
      startFraction: this.clientToPaneFraction(
        projection,
        event.clientX,
        event.clientY,
        { clampToSurface: false }
      )
    };
    window.addEventListener("mousemove", this.handleDragMove);
    window.addEventListener("mouseup", this.handleDragEnd);
  }
  moveSlice(projection, steps, stepMm) {
    if (!this.reader || !this.focus || steps === 0) {
      return;
    }
    const session = this.toothSession;
    if (session) {
      this.applyToothCursor(
        session,
        {
          ...session.cursor,
          [projection]: session.cursor[projection] + steps * (stepMm ?? this.sliceStepMmByProjection[projection] ?? session.scrollStepMm[projection])
        },
        "wheel",
        projection
      );
      return;
    }
    this.setFocus(
      offsetFocusVoxelAlongProjection(
        this.reader,
        this.focus,
        projection,
        steps
      ),
      "wheel"
    );
  }
  handleViewportWheel(projection, event) {
    this.imageOptionsController.cancel();
    this.annotations?.cancelInteraction();
    if (!this.reader || !this.focus) {
      return;
    }
    event.preventDefault();
    this.setActiveProjection(projection);
    if (isSliceScrollWheelEvent(event)) {
      this.moveSlice(
        projection,
        getWheelSliceDirection(event),
        this.restoredView ? void 0 : STUDIO_WHEEL_SLICE_STEP_MM
      );
      return;
    }
    this.zoomAtCursor(projection, event);
  }
  applyPointerFocus(projection, event) {
    const session = this.toothSession;
    if (session) {
      const cursor = this.toothCursorFromPointer(session, projection, event);
      if (cursor) {
        this.applyToothCursor(session, cursor, "pointer");
      }
      return;
    }
    const next = this.focusFromPointer(projection, event);
    if (next) {
      this.setFocus(next, "pointer");
    }
  }
  updateCrosshairs() {
    if (!this.reader || !this.focus) {
      return;
    }
    const session = this.toothSession;
    if (session) {
      this.updateToothCrosshairs(session);
      return;
    }
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      this.resetCrosshairLineGeometry(viewportState);
      const { left, top } = getCrosshairPercentages(
        this.reader,
        this.focus,
        projection
      );
      const camera = viewportState.viewport?.camera;
      let fractionX = left;
      let fractionY = top;
      if (camera instanceof OrthographicCamera) {
        const shape = getOrthogonalProjectionShape(
          this.reader.geometry,
          projection
        );
        const point = new Vector3(
          left * shape.width,
          top * shape.height,
          0
        ).project(camera);
        fractionX = (point.x + 1) / 2;
        fractionY = (1 - point.y) / 2;
      }
      viewportState.crosshairVertical.style.left = `${fractionX * 100}%`;
      viewportState.crosshairHorizontal.style.top = `${fractionY * 100}%`;
      viewportState.meta.textContent = `${getSliceIndexForProjection(this.focus, projection) + 1} / ${viewportState.maxSliceIndex + 1}`;
    });
  }
  // Undo the tooth-mode oversizing/rotation so the orthogonal path gets the
  // original full-span axis-aligned lines back.
  resetCrosshairLineGeometry(viewportState) {
    const vertical = viewportState.crosshairVertical;
    vertical.style.display = "";
    vertical.style.top = "0";
    vertical.style.bottom = "0";
    vertical.style.transform = "";
    vertical.style.transformOrigin = "";
    const horizontal = viewportState.crosshairHorizontal;
    horizontal.style.display = "";
    horizontal.style.left = "0";
    horizontal.style.right = "0";
    horizontal.style.transform = "";
    horizontal.style.transformOrigin = "";
  }
  // Tooth-mode reference lines are plane traces: generally oblique, so each
  // line is oversized to 200% of the pane and rotated around the crossing
  // point. The div-to-plane assignment mirrors CROSSHAIR_COLOR.
  updateToothCrosshairs(session) {
    const crosshairs = this.toothCrosshairs(session);
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      const paneCrosshair = crosshairs?.[projection];
      const lineTargets = TOOTH_TRACE_BY_LINE[projection];
      const camera = viewportState.viewport?.camera;
      const shape = this.getPaneShape(projection);
      const lines = [
        {
          element: viewportState.crosshairVertical,
          trace: paneCrosshair?.[lineTargets.vertical] ?? null,
          baseAngleDeg: 90
        },
        {
          element: viewportState.crosshairHorizontal,
          trace: paneCrosshair?.[lineTargets.horizontal] ?? null,
          baseAngleDeg: 0
        }
      ];
      lines.forEach(({ element, trace, baseAngleDeg }) => {
        if (!trace) {
          element.style.display = "none";
          return;
        }
        let screenLeft = trace.leftFraction;
        let screenTop = trace.topFraction;
        if (camera instanceof OrthographicCamera) {
          const point = new Vector3(
            trace.leftFraction * shape.width,
            trace.topFraction * shape.height,
            0
          ).project(camera);
          screenLeft = (point.x + 1) / 2;
          screenTop = (1 - point.y) / 2;
        }
        element.style.display = "";
        if (baseAngleDeg === 90) {
          element.style.top = "-50%";
          element.style.bottom = "-50%";
          element.style.left = `${screenLeft * 100}%`;
          element.style.transformOrigin = `50% ${(screenTop + 0.5) / 2 * 100}%`;
        } else {
          element.style.left = "-50%";
          element.style.right = "-50%";
          element.style.top = `${screenTop * 100}%`;
          element.style.transformOrigin = `${(screenLeft + 0.5) / 2 * 100}% 50%`;
        }
        element.style.transform = `rotate(${trace.angleDeg - baseAngleDeg}deg)`;
      });
    });
  }
  scheduleRenderAll() {
    this.visibleProjections.forEach((projection) => {
      this.scheduleViewportRender(projection);
    });
  }
  async renderAll(mode) {
    await Promise.all(
      this.visibleProjections.map(
        (projection) => this.renderViewport(projection, mode)
      )
    );
  }
  scheduleViewportRender(projection) {
    this.imageOptionsController.update(projection);
    this.annotations?.update(projection);
    const viewportState = this.viewportStateByProjection[projection];
    if (!this.visibleProjections.includes(projection) || viewportState.scheduledFrameId !== null) {
      return;
    }
    viewportState.scheduledFrameId = requestAnimationFrame(() => {
      viewportState.scheduledFrameId = null;
      void this.renderViewport(projection, "loaded-only");
    });
    if (this.restoredView || this.selectedBackend === "cpu" || this.imageOptionsByProjection.has(projection)) {
      this.savedRenderAborts.get(projection)?.abort();
      this.renderEpochByProjection.set(
        projection,
        (this.renderEpochByProjection.get(projection) ?? 0) + 1
      );
    }
    this.view.dispatchEvent({ type: "fullHeadMPRViewUpdated", projection });
  }
  // Defer rendering until the surface is laid out AND its scissor box has
  // stopped changing. On reopen with a cached volume the first render fires
  // before layout (0×0); during the modal's scale-in animation the box
  // animates. Rendering then leaves the pane blank/cornered or off-center
  // (image "jumps" to center when it settles). Chunk demand is already set by
  // the caller, so data keeps loading while we wait for a stable size. A frame
  // cap guards against a box that never settles.
  shouldDeferViewportRender(projection) {
    const viewport = this.viewportStateByProjection[projection].viewport;
    if (!viewport) {
      return false;
    }
    const sizeKey = `${Math.round(viewport.size.x)},${Math.round(viewport.size.y)},${Math.round(viewport.size.z)},${Math.round(viewport.size.w)}`;
    const degenerate = viewport.size.z < 1 || viewport.size.w < 1;
    const stable = sizeKey === this.lastViewportSizeKey.get(projection);
    const deferCount = this.viewportDeferCount.get(projection) ?? 0;
    this.lastViewportSizeKey.set(projection, sizeKey);
    if ((degenerate || !stable) && deferCount < 90) {
      this.viewportDeferCount.set(projection, deferCount + 1);
      this.scheduleViewportRender(projection);
      return true;
    }
    this.viewportDeferCount.set(projection, 0);
    return false;
  }
  renderToothViewport(projection) {
    const reader = this.reader;
    const session = this.toothSession;
    if (!reader || !session) {
      return;
    }
    const viewportState = this.viewportStateByProjection[projection];
    this.renderEpochByProjection.set(
      projection,
      (this.renderEpochByProjection.get(projection) ?? 0) + 1
    );
    if (this.shouldDeferViewportRender(projection)) {
      return;
    }
    if (this.selectedBackend === "cpu" || this.imageOptionsByProjection.has(projection)) {
      void this.renderFullResolutionViewport(projection);
      return;
    }
    let volumeTexture = null;
    try {
      volumeTexture = this.prepareToothVolumeTexture();
    } catch (error) {
      if (isAbortError(error) || this.demoteToCpu(error)) {
        return;
      }
      this.setViewportStatus(projection, "error");
      this.handleError(error, "Failed to prepare the tooth volume.");
      return;
    }
    const frame = session.frames[projection];
    const spec = session.spec;
    const center = this.toothPaneCenters(session)[projection];
    const layout = buildToothPaneLayout({ center, frame, spec });
    const sampling = this.resolvePaneSampling(projection);
    if (!sampling) {
      this.setViewportStatus(projection, "error");
      viewportState.errorLabel.textContent = "Tooth slab sampling failed.";
      return;
    }
    const window2 = this.effectiveWindow(session.window ?? this.colorProfile);
    if (volumeTexture) {
      const roi = volumeVoxelBox(reader.geometry);
      const slab = buildToothSlabUniforms({
        geometry: reader.geometry,
        layout,
        roi,
        slabMode: "mean",
        slabOffsetsMm: sampling.offsetsMm
      });
      if (slab) {
        this.presentFrame(viewportState, {
          aspectRatio: spec.widthPx / spec.heightPx,
          chunkIndices: [],
          height: spec.heightPx,
          matrix: buildToothPixelToIjkMatrix({
            geometry: reader.geometry,
            layout,
            roi
          }),
          maxSliceIndex: 0,
          minimumWindowWidth: volumeTexture.normalization ? Number.EPSILON : MIN_WINDOW_WIDTH_HU,
          missingChunkCount: 0,
          renderedChunkCount: 1,
          slab,
          texture: volumeTexture.texture,
          textureBlocks: volumeTexture.blocks,
          width: spec.widthPx,
          window: adaptWindowForNormalization(
            window2,
            volumeTexture.normalization
          )
        });
        viewportState.hasFrame = true;
        viewportState.isPending = false;
        this.setViewportStatus(projection, "ready");
        this.setToothRenderPath("gpu-volume-int16");
        return;
      }
    }
    void this.renderFullResolutionViewport(projection);
  }
  async renderFullResolutionViewport(projection) {
    const reader = this.reader;
    const session = this.toothSession;
    const focus = this.focus;
    if (!reader || !focus) {
      return;
    }
    const source = session ? this.restoredView ?? describeToothView({
      centerMm: this.toothPaneCenters(session)[projection],
      frame: session.frames[projection],
      frustumPx: this.computeLetterboxFrustum(projection),
      projection,
      spacing: reader.geometry.spacing,
      spec: session.spec,
      thickness: { kind: "native" },
      volumeFileID: reader.volumeFileID,
      window: this.effectiveWindow(session.window ?? this.colorProfile),
      zoomPan: this.zoomPanByProjection[projection]
    }) : describeOrthogonalView({
      focus,
      frustum: this.computeLetterboxFrustum(projection),
      geometry: reader.geometry,
      projection,
      volumeFileID: reader.volumeFileID,
      window: this.effectiveWindow(this.colorProfile),
      zoomPan: this.zoomPanByProjection[projection]
    });
    if (!source) {
      return;
    }
    const sampling = session ? this.resolvePaneSampling(projection) : null;
    if (session && !sampling) {
      return;
    }
    this.savedRenderAborts.get(projection)?.abort();
    const abort = new AbortController();
    this.savedRenderAborts.set(projection, abort);
    const epoch = this.renderEpochByProjection.get(projection);
    const viewportState = this.viewportStateByProjection[projection];
    const cpu = session ? this.resolveToothCpuRaster({
      center: this.toothPaneCenters(session)[projection],
      frame: session.frames[projection],
      projection,
      spec: session.spec
    }) : null;
    const layout = cpu?.layout;
    const visibleRect = session ? resolveToothPaneVisibleRect({
      centerMm: this.toothPaneCenters(session)[projection],
      frame: session.frames[projection],
      frustumPx: this.computeLetterboxFrustum(projection),
      spec: session.spec,
      zoomPan: this.zoomPanByProjection[projection]
    }) : null;
    const pixelRatio = this.view.getRenderer().getPixelRatio();
    const widthPx = layout?.width ?? Math.ceil(viewportState.surface.clientWidth * pixelRatio);
    const heightPx = layout?.height ?? Math.ceil(viewportState.surface.clientHeight * pixelRatio);
    const imageRectPx = cpu?.rectPx ?? resolveToothPaneRasterRectPx({
      frustumPx: this.computeLetterboxFrustum(projection),
      zoomPan: this.zoomPanByProjection[projection]
    });
    const view = layout && sampling ? {
      ...source,
      window: this.effectiveWindow(session?.window ?? this.colorProfile),
      centerMm: visibleRect?.centerMm ?? layout.centerPatientMm,
      heightMm: visibleRect ? visibleRect.halfVMm * 2 : layout.height * layout.mmPerPixelV,
      normal: layout.normal,
      projection,
      sampleOffsetsMm: [...sampling.offsetsMm],
      sampling: sampling.sampling,
      thicknessMm: sampling.effectiveThicknessMm,
      uAxis: layout.uAxis,
      vAxis: layout.vAxis,
      widthMm: visibleRect ? visibleRect.halfUMm * 2 : layout.width * layout.mmPerPixelU
    } : source;
    const shape = getOrthogonalProjectionShape(reader.geometry, projection);
    this.setViewportStatus(projection, "loading");
    if (session) {
      this.setToothRenderPath(
        this.selectedBackend === "cpu" ? "cpu-saved-view-int16" : "gpu-volume-int16"
      );
    }
    const resources = {};
    try {
      const rasteriser = this.selectedBackend === "cpu" ? null : createFullHeadMPRSliceRasteriser(reader);
      resources.rasteriser = rasteriser ?? void 0;
      const raster = await renderFullHeadMPRViewWithReader({
        heightPx,
        rasteriser: rasteriser?.render,
        reader,
        signal: abort.signal,
        view,
        widthPx
      });
      if (abort.signal.aborted || this.reader !== reader || this.toothSession !== session || this.renderEpochByProjection.get(projection) !== epoch) {
        return;
      }
      this.presentFrame(viewportState, {
        aspectRatio: session ? session.spec.widthPx / session.spec.heightPx : shape.aspectRatio,
        chunkIndices: [],
        height: raster.height,
        imageData: new ImageData(
          new Uint8ClampedArray(raster.pixels),
          raster.width,
          raster.height
        ),
        imageRectPx,
        maxSliceIndex: session ? 0 : shape.maxSliceIndex,
        missingChunkCount: 0,
        renderedChunkCount: 1,
        width: raster.width
      });
      viewportState.hasFrame = true;
      viewportState.isPending = false;
      this.setViewportStatus(projection, "ready");
      if (!session) {
        const sliceIndex = getSliceIndexForProjection(focus, projection);
        viewportState.aspectRatio = shape.aspectRatio;
        viewportState.maxSliceIndex = shape.maxSliceIndex;
        viewportState.meta.textContent = `${sliceIndex + 1} / ${shape.maxSliceIndex + 1}`;
        this.recordPaintMetrics(projection, 1, 0);
        this.view.dispatchEvent({
          type: "fullHeadMPRViewportRendered",
          chunkIndices: [],
          missingChunkCount: 0,
          mode: "ensure-all",
          projection,
          renderedChunkCount: 1,
          sliceIndex
        });
      }
    } catch (error) {
      if (!abort.signal.aborted && this.reader === reader && this.toothSession === session && this.renderEpochByProjection.get(projection) === epoch) {
        this.setViewportStatus(projection, "error");
        this.handleError(error, "Failed to render the saved MPR view");
      }
    } finally {
      resources.rasteriser?.release();
      if (this.savedRenderAborts.get(projection) === abort) {
        this.savedRenderAborts.delete(projection);
      }
    }
  }
  // What a CPU tooth pane rasterises: the rect the camera shows, at surface
  // density, so zooming into the tooth keeps full detail even though the raster
  // spans the whole volume. Falls back to the whole raster when the visible rect
  // cannot be resolved, which is the framing this path used before.
  resolveToothCpuRaster({
    center,
    frame,
    projection,
    spec
  }) {
    const wholeRaster = {
      layout: buildToothPaneLayout({
        center,
        frame,
        spec
      }),
      rectPx: {
        heightPx: spec.heightPx,
        widthPx: spec.widthPx,
        xPx: 0,
        yPx: 0
      }
    };
    const { surface } = this.viewportStateByProjection[projection];
    const frustumPx = this.computeLetterboxFrustum(projection);
    const zoomPan = this.zoomPanByProjection[projection];
    const rect = resolveToothPaneVisibleRect({
      centerMm: center,
      frame,
      frustumPx,
      spec,
      zoomPan
    });
    const rectPx = resolveToothPaneRasterRectPx({ frustumPx, zoomPan });
    if (!rect || !rectPx) {
      return wholeRaster;
    }
    const pixelRatio = this.view.getRenderer().getPixelRatio();
    const surfaceWidthPx = Math.ceil(surface.clientWidth * pixelRatio);
    const surfaceHeightPx = Math.ceil(surface.clientHeight * pixelRatio);
    const layout = buildToothPaneVisibleLayout({
      frame,
      maxRasterPx: Math.max(surfaceWidthPx, surfaceHeightPx),
      minMmPerPixel: Number.EPSILON,
      rect,
      surfaceHeightPx,
      surfaceWidthPx
    });
    return layout ? { layout, rectPx } : wholeRaster;
  }
  async renderViewport(projection, mode) {
    const reader = this.reader;
    const focus = this.focus;
    if (!reader || !focus || !this.visibleProjections.includes(projection)) {
      return;
    }
    if (this.toothSession) {
      try {
        this.renderToothViewport(projection);
      } catch (error) {
        this.setViewportStatus(projection, "error");
        this.handleError(error, "Failed to render the tooth pane");
      }
      return;
    }
    const viewportState = this.viewportStateByProjection[projection];
    const renderEpoch = (this.renderEpochByProjection.get(projection) ?? 0) + 1;
    this.renderEpochByProjection.set(projection, renderEpoch);
    if (this.imageOptionsByProjection.has(projection)) {
      viewportState.requiredChunkIndices = [];
      reader.updateConsumerDemand(viewportState.consumerId, []);
      if (!this.shouldDeferViewportRender(projection)) {
        await this.renderFullResolutionViewport(projection);
      }
      return;
    }
    const sliceIndex = getSliceIndexForProjection(focus, projection);
    const loadOptions = {
      maxConcurrentRanges: projection === this.activeProjection ? 4 : 2,
      priority: projection === this.activeProjection ? "visible" : "background",
      retention: this.selectedBackend === "gpu-monolith" ? "stream" : "read"
    };
    const chunkIndices = getOrthogonalSliceChunkIndices(
      reader,
      projection,
      sliceIndex
    );
    viewportState.requiredChunkIndices = chunkIndices;
    this.setViewportStatus(
      projection,
      viewportState.hasFrame ? "ready" : "loading"
    );
    try {
      reader.updateConsumerDemand(
        viewportState.consumerId,
        this.getMissingRenderChunkIndices(chunkIndices),
        loadOptions
      );
      if (this.shouldDeferViewportRender(projection)) {
        return;
      }
      const result = await this.renderSliceWithBackend(
        projection,
        sliceIndex,
        loadOptions,
        mode
      );
      if (this.renderEpochByProjection.get(projection) !== renderEpoch) {
        return;
      }
      this.presentFrame(viewportState, result);
      viewportState.aspectRatio = result.aspectRatio;
      viewportState.maxSliceIndex = result.maxSliceIndex;
      viewportState.hasFrame = result.renderedChunkCount > 0 || viewportState.hasFrame;
      viewportState.isPending = result.missingChunkCount > 0;
      viewportState.meta.textContent = `${sliceIndex + 1} / ${result.maxSliceIndex + 1}`;
      this.setViewportStatus(
        projection,
        viewportState.hasFrame ? "ready" : "loading",
        result.missingChunkCount > 0 ? "Loading missing chunks..." : void 0
      );
      this.recordPaintMetrics(
        projection,
        result.renderedChunkCount,
        result.missingChunkCount
      );
      this.view.dispatchEvent({
        type: "fullHeadMPRViewportRendered",
        chunkIndices: result.chunkIndices,
        missingChunkCount: result.missingChunkCount,
        mode,
        projection,
        renderedChunkCount: result.renderedChunkCount,
        sliceIndex
      });
    } catch (error) {
      if (isAbortError(error) || this.renderEpochByProjection.get(projection) !== renderEpoch) {
        return;
      }
      if (this.demoteToCpu(error)) {
        return;
      }
      this.setViewportStatus(
        projection,
        viewportState.hasFrame ? "ready" : "error"
      );
      viewportState.errorLabel.textContent = error instanceof Error ? error.message : "Render failed.";
      this.handleError(error, `Failed to render ${projection} viewport.`);
    }
  }
  beginPresentation(presentationID) {
    this.presentation = presentationID && this.reader ? createFullHeadMPRPresentation({
      presentationID,
      projections: this.visibleProjections,
      volumeFileID: this.reader.volumeFileID
    }) : null;
    VIEWPORT_ORDER.forEach((projection) => {
      this.renderEpochByProjection.set(
        projection,
        (this.renderEpochByProjection.get(projection) ?? 0) + 1
      );
    });
  }
  dispatchPresentationReady(presentation) {
    this.view.dispatchEvent({
      type: "fullHeadMPRPresentationReady",
      presentationID: presentation.presentationID,
      volumeFileID: presentation.volumeFileID
    });
  }
  presentFrame(viewportState, frame) {
    const { scene, viewport } = viewportState;
    if (!scene || !viewport) {
      return;
    }
    if (frame.imageData) {
      scene.setImageRect(
        frame.imageRectPx ?? {
          heightPx: frame.height,
          widthPx: frame.width,
          xPx: 0,
          yPx: 0
        }
      );
      scene.showCpu(frame.imageData);
    } else if (frame.texture && frame.matrix && frame.window) {
      scene.showGpu(
        frame.texture,
        frame.matrix,
        frame.window,
        frame.minimumWindowWidth ?? 1,
        frame.textureBlocks
      );
      if (frame.slab) {
        scene.setSlab(frame.slab);
      }
    } else {
      scene.hideAll();
    }
    this.gpuTimer?.poll();
    const submitStartedAt = now();
    const presentation = this.presentation;
    const projection = VIEWPORT_ORDER.find(
      (candidate) => this.viewportStateByProjection[candidate] === viewportState
    );
    viewport.render();
    void viewport.waitForRenderComplete().then(() => {
      if (this.mounted && projection) {
        this.view.dispatchEvent({ type: "fullHeadMPRViewUpdated", projection });
      }
      if (presentation && this.presentation === presentation && projection && frame.renderedChunkCount > 0 && presentation.markRendered(projection)) {
        this.dispatchPresentationReady(presentation);
      }
      this.benchmark.recordSample(
        this.selectedBackend,
        "submitToRafMs",
        now() - submitStartedAt
      );
    });
  }
  recordPaintMetrics(projection, renderedChunkCount, missingChunkCount) {
    if (!this.reader || renderedChunkCount <= 0) {
      return;
    }
    this.lastInteractionMissingChunkCount = missingChunkCount;
    if (!this.firstFramePainted) {
      this.firstFramePainted = true;
      this.benchmark.recordSample(
        this.selectedBackend,
        "timeToFirstFrameMs",
        now() - this.lastBackendSwitchAt
      );
    }
    const pending = this.pendingInteractionMetric;
    if (pending && pending.projection === projection) {
      this.benchmark.recordSample(
        pending.backend,
        pending.reason === "wheel" ? "wheelToPaintMs" : "focusChangeToPaintMs",
        now() - pending.startedAt
      );
      this.pendingInteractionMetric = null;
    }
    this.updatePhaseAndEmit();
  }
  updatePhaseAndEmit() {
    const sinceSwitchMs = now() - this.lastBackendSwitchAt;
    const steadyInteractive = sinceSwitchMs >= 1e3 && this.framesSinceLastRepack >= 5 && this.lastInteractionMissingChunkCount === 0;
    const steadyFullVolume = steadyInteractive && this.totalChunkCount > 0 && this.loadedChunkCount === this.totalChunkCount;
    const phase = steadyFullVolume ? "steadyFullVolume" : steadyInteractive ? "steadyInteractive" : "mixed";
    this.benchmark.setPhase(phase);
    this.maybeDemoteBackend(phase);
    this.scheduleBenchmarkEmit();
  }
  // Runtime GPU→CPU demote. OFF unless debugOptions.enableAutoCpuDemote is set —
  // the steady-interaction p95 is compared against an ABSOLUTE budget (no live
  // CPU baseline exists), so the threshold must be tuned on real telemetry before
  // enabling. Guards: warm phase only, enough samples, cooldown since last switch,
  // one-way latch.
  maybeDemoteBackend(phase) {
    if (!this.debugOptions.enableAutoCpuDemote || this.demotedToCpu || this.selectedBackend !== "gpu-monolith") {
      return;
    }
    if (phase !== "steadyInteractive" && phase !== "steadyFullVolume") {
      return;
    }
    if (now() - this.lastBackendSwitchAt < DEMOTE_COOLDOWN_MS) {
      return;
    }
    const stats = this.benchmark.getSnapshot().byBackend["gpu-monolith"];
    const wheel = stats.samples.wheelToPaintMs;
    const focus = stats.samples.focusChangeToPaintMs;
    const sample = wheel && wheel.count >= MIN_DEMOTE_SAMPLES ? wheel : focus && focus.count >= MIN_DEMOTE_SAMPLES ? focus : null;
    if (!sample || sample.p95 <= DEMOTE_WHEEL_P95_MS) {
      return;
    }
    this.demotedToCpu = true;
    console.warn(
      "[FullHeadMPR] gpu-monolith interaction p95 over budget \u2192 demoting to cpu",
      { p95: sample.p95, thresholdMs: DEMOTE_WHEEL_P95_MS }
    );
    this.setBackend("cpu");
  }
  scheduleBenchmarkEmit() {
    if (this.benchmarkEmitScheduled) {
      return;
    }
    this.benchmarkEmitScheduled = true;
    setTimeout(() => {
      this.benchmarkEmitScheduled = false;
      if (this.debugOptions.emitMetricsOnEveryUpdate === false) {
        return;
      }
      this.view.dispatchEvent({
        type: "fullHeadMPRBenchmarkUpdated",
        benchmark: this.benchmark.getSnapshot()
      });
    }, 300);
  }
  getBenchmark() {
    return this.reader ? this.benchmark.getSnapshot() : null;
  }
  clearViewportFrames() {
    VIEWPORT_ORDER.forEach((projection) => {
      const viewportState = this.viewportStateByProjection[projection];
      viewportState.scene?.hideAll();
      viewportState.viewport?.render();
      viewportState.hasFrame = false;
      viewportState.requiredChunkIndices = [];
      viewportState.errorLabel.textContent = "";
    });
  }
  setAllViewportStatus(status, message) {
    VIEWPORT_ORDER.forEach((projection) => {
      this.setViewportStatus(projection, status, message);
    });
  }
  setViewportStatus(projection, status, message) {
    const viewportState = this.viewportStateByProjection[projection];
    viewportState.status = status;
    viewportState.statusLabel.textContent = message ?? (status === "loading" ? "Loading..." : status === "idle" ? "Idle" : status === "error" ? "Error" : "");
    viewportState.statusLabel.style.opacity = status === "ready" && !message ? "0" : "0.9";
    viewportState.statusLabel.style.display = this.chrome.showDevChrome ? "" : "none";
    viewportState.errorLabel.style.display = this.chrome.showDevChrome && status === "error" ? "block" : "none";
  }
  updateSummary(message) {
    this.summaryValueElement.textContent = message ?? `${this.loadedChunkCount} / ${this.totalChunkCount || 0} chunks loaded`;
  }
  handleError(error, fallbackMessage) {
    const message = error instanceof Error && error.message ? error.message : fallbackMessage;
    this.updateSummary(message);
    this.view.dispatchEvent({
      type: "fullHeadMPRError",
      message
    });
  }
  createViewport(projection) {
    const pane = document.createElement("div");
    const title = document.createElement("span");
    const meta = document.createElement("span");
    const surface = document.createElement("div");
    const crosshairVertical = document.createElement("div");
    const crosshairHorizontal = document.createElement("div");
    const statusLabel = document.createElement("div");
    const errorLabel = document.createElement("div");
    const frameOverlay = document.createElement("div");
    pane.dataset.fullHeadMprPane = projection;
    pane.tabIndex = 0;
    pane.setAttribute("role", "group");
    pane.style.position = "relative";
    pane.style.minHeight = "0";
    pane.style.overflow = "hidden";
    pane.style.background = "transparent";
    pane.style.outline = "none";
    pane.addEventListener("pointerenter", () => {
      this.viewportStateByProjection[projection].isHovered = true;
      this.updateActivePaneFrames();
    });
    pane.addEventListener("pointerleave", () => {
      this.viewportStateByProjection[projection].isHovered = false;
      this.updateActivePaneFrames();
    });
    pane.addEventListener(
      "pointerdown",
      (event) => {
        this.viewportStateByProjection[projection].hasKeyboardFocus = false;
        this.setActiveProjection(projection);
        this.updateActivePaneFrames();
        if (event.target instanceof Element && !event.target.closest(
          'input, textarea, select, button, a[href], [contenteditable], [role="button"], [role="slider"]'
        )) {
          pane.focus({ preventScroll: true });
        }
      },
      { capture: true }
    );
    pane.addEventListener("focusin", () => {
      this.viewportStateByProjection[projection].hasKeyboardFocus = pane.matches(":focus-visible");
      this.setActiveProjection(projection);
      this.updateActivePaneFrames();
    });
    pane.addEventListener("focusout", () => {
      this.viewportStateByProjection[projection].hasKeyboardFocus = false;
      this.updateActivePaneFrames();
    });
    pane.addEventListener("keydown", (event) => {
      if (event.target === pane) {
        this.viewportStateByProjection[projection].hasKeyboardFocus = true;
        this.updateActivePaneFrames();
      }
    });
    surface.style.position = "relative";
    surface.style.width = "100%";
    surface.style.height = "100%";
    surface.style.overflow = "hidden";
    surface.style.background = "transparent";
    surface.style.cursor = PANE_CURSOR_BY_MODE[this.inputMode];
    surface.style.userSelect = "none";
    frameOverlay.style.position = "absolute";
    frameOverlay.style.inset = "0";
    frameOverlay.style.borderRadius = "inherit";
    frameOverlay.style.pointerEvents = "none";
    frameOverlay.style.zIndex = "1";
    crosshairVertical.style.position = "absolute";
    crosshairVertical.style.top = "0";
    crosshairVertical.style.bottom = "0";
    crosshairVertical.style.width = "1px";
    crosshairVertical.style.background = CROSSHAIR_COLOR[projection].vertical;
    crosshairVertical.style.pointerEvents = "none";
    crosshairHorizontal.style.position = "absolute";
    crosshairHorizontal.style.left = "0";
    crosshairHorizontal.style.right = "0";
    crosshairHorizontal.style.height = "1px";
    crosshairHorizontal.style.background = CROSSHAIR_COLOR[projection].horizontal;
    crosshairHorizontal.style.pointerEvents = "none";
    statusLabel.style.position = "absolute";
    statusLabel.style.left = "10px";
    statusLabel.style.bottom = "10px";
    statusLabel.style.padding = "4px 8px";
    statusLabel.style.borderRadius = "999px";
    statusLabel.style.background = "rgba(4, 7, 10, 0.78)";
    statusLabel.style.fontSize = "11px";
    statusLabel.style.transition = "opacity 120ms ease";
    statusLabel.style.pointerEvents = "none";
    errorLabel.style.position = "absolute";
    errorLabel.style.left = "10px";
    errorLabel.style.top = "10px";
    errorLabel.style.right = "10px";
    errorLabel.style.padding = "8px 10px";
    errorLabel.style.borderRadius = "10px";
    errorLabel.style.background = "rgba(160, 32, 32, 0.85)";
    errorLabel.style.fontSize = "12px";
    errorLabel.style.display = "none";
    errorLabel.style.pointerEvents = "none";
    surface.addEventListener("mousedown", (event) => {
      this.startDrag(projection, event);
    });
    surface.addEventListener("contextmenu", (event) => {
      event.preventDefault();
      if (resolveRightButtonIntent(this.inputMode) !== "resetDisplayAdjust") {
        return;
      }
      this.setActiveProjection(projection);
      this.resetDisplayAdjust();
    });
    surface.addEventListener("dblclick", (event) => {
      this.imageOptionsController.cancel();
      this.annotations?.cancelInteraction();
      if (!this.reader) {
        return;
      }
      event.preventDefault();
      this.setActiveProjection(projection);
      this.resetView(projection);
    });
    surface.addEventListener("mousemove", (event) => {
      if (this.imageOptionsController.hasTool() && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        return;
      }
      if (this.annotations?.ownsPointer(projection)) {
        return;
      }
      if (event.buttons === 0) {
        this.updateToothLineCursor(projection, event);
        return;
      }
      if (event.buttons !== PRIMARY_MOUSE_BUTTONS_MASK || this.drag || !this.reader || !this.focus || !isPointerFocusMode(this.inputMode)) {
        return;
      }
      this.setActiveProjection(projection);
      this.applyPointerFocus(projection, event);
    });
    surface.addEventListener(
      "wheel",
      (event) => {
        this.handleViewportWheel(projection, event);
      },
      { passive: false }
    );
    surface.appendChild(crosshairVertical);
    surface.appendChild(crosshairHorizontal);
    surface.appendChild(statusLabel);
    surface.appendChild(errorLabel);
    pane.appendChild(surface);
    pane.appendChild(frameOverlay);
    return {
      aspectRatio: 1,
      scene: null,
      viewport: null,
      consumerId: `full-head:${projection}:${Math.random().toString(36).slice(2)}`,
      crosshairHorizontal,
      crosshairVertical,
      errorLabel,
      frameOverlay,
      hasFrame: false,
      hasKeyboardFocus: false,
      isHovered: false,
      isPending: false,
      maxSliceIndex: 0,
      meta,
      pane,
      requiredChunkIndices: [],
      scheduledFrameId: null,
      status: "idle",
      statusLabel,
      surface,
      title
    };
  }
}

const MEASUREMENT_CACHE_LIMIT = 8;
const measurementBySignature = /* @__PURE__ */ new Map();
const signatureOf = (request) => JSON.stringify([
  request.volumeFileID,
  request.iso,
  request.origin.x,
  request.origin.y,
  request.origin.z,
  request.searchBox.min.x,
  request.searchBox.min.y,
  request.searchBox.min.z,
  request.searchBox.max.x,
  request.searchBox.max.y,
  request.searchBox.max.z,
  request.anatomicalBasis.Axial.x,
  request.anatomicalBasis.Axial.y,
  request.anatomicalBasis.Axial.z,
  request.anatomicalBasis.MesioDistal.x,
  request.anatomicalBasis.MesioDistal.y,
  request.anatomicalBasis.MesioDistal.z,
  request.anatomicalBasis.VestibuloOral.x,
  request.anatomicalBasis.VestibuloOral.y,
  request.anatomicalBasis.VestibuloOral.z
]);
const getCachedToothBasisMeasurement = (request) => measurementBySignature.get(signatureOf(request));
const cacheToothBasisMeasurement = ({
  measurement,
  request
}) => {
  const signature = signatureOf(request);
  measurementBySignature.delete(signature);
  measurementBySignature.set(signature, measurement);
  if (measurementBySignature.size > MEASUREMENT_CACHE_LIMIT) {
    const oldest = measurementBySignature.keys().next().value;
    if (oldest !== void 0) {
      measurementBySignature.delete(oldest);
    }
  }
};
const clearToothBasisMeasurementCache = () => {
  measurementBySignature.clear();
};

const views = /* @__PURE__ */ new Set();
const clearFullHeadMPRSession = () => {
  views.forEach((view) => view.controller.stop());
  clearFullHeadMPRGpuVolumes();
  clearSharedDCCTVolumes();
  clearToothBasisMeasurementCache();
};
class FullHeadMPRView extends View {
  constructor() {
    super();
    this.detachedContainer = document.createElement("div");
    this.handleContextLost = (event) => {
      event.preventDefault();
      const gl = this.getRenderer().getContext();
      this.controller.handleContextLost();
      clearFullHeadMPRGpuVolumes(new VolumeGpuError(gl.CONTEXT_LOST_WEBGL));
    };
    this.handleContextRestored = () => {
      this.controller.handleContextRestored();
    };
    this.controller = new FullHeadMPRController(this);
  }
  init(props) {
    super.init(props);
    this.canvas.style.position = "absolute";
    this.canvas.style.inset = "0";
    this.canvas.style.zIndex = "0";
    this.domElement.style.background = "transparent";
    this.domElement.style.height = "100%";
    this.domElement.style.overflow = "hidden";
    this.domElement.style.position = "relative";
    this.domElement.style.width = "100%";
    this.controller.mount();
    const canvas = this.getRenderer().domElement;
    canvas.addEventListener("webglcontextlost", this.handleContextLost);
    canvas.addEventListener("webglcontextrestored", this.handleContextRestored);
    views.add(this);
  }
  detach() {
    this.setContainer(this.detachedContainer);
  }
  createRenderer() {
    return getFullHeadMPRWebGLRenderer();
  }
  createCanvas() {
    return document.createElement("canvas");
  }
  resizeCanvas(width, height) {
    const ratio = this.getRenderer().getPixelRatio();
    this.canvas.width = Math.floor(width * ratio);
    this.canvas.height = Math.floor(height * ratio);
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
  }
  renderViewport(viewport, draw) {
    const renderer = this.getRenderer();
    const context = this.canvas.getContext("2d");
    const ratio = renderer.getPixelRatio();
    const { x, y, z: width, w: height } = viewport.size;
    const surface = viewport.getDomElement();
    const surfaceRect = surface.getBoundingClientRect();
    const canvasRect = this.canvas.getBoundingClientRect();
    if (!context || width <= 0 || height <= 0 || surfaceRect.width <= 0 || surfaceRect.height <= 0 || canvasRect.width <= 0 || canvasRect.height <= 0 || !this.canvas.width || !this.canvas.height) {
      return;
    }
    const left = Math.floor(x * ratio);
    const bottom = Math.floor(y * ratio);
    const widthPx = Math.floor(width * ratio);
    const heightPx = Math.floor(height * ratio);
    const isContextLost = renderer.getContext().isContextLost();
    const source = renderer.domElement;
    if (!isContextLost) {
      const requiredWidth = Math.max(source.width, this.canvas.width);
      const requiredHeight = Math.max(source.height, this.canvas.height);
      if (source.width !== requiredWidth || source.height !== requiredHeight) {
        renderer.setSize((requiredWidth + 0.5) / ratio, (requiredHeight + 0.5) / ratio, false);
      }
      renderer.setRenderTarget(null);
      draw();
    }
    const surfaceStyle = getComputedStyle(surface);
    const borderRadiusPx = Number.parseFloat(surfaceStyle.borderTopLeftRadius) || 0;
    const surfaceWidth = Number.parseFloat(surfaceStyle.width);
    const surfaceHeight = Number.parseFloat(surfaceStyle.height);
    const targetLeft = surfaceRect.left - canvasRect.left;
    const targetTop = surfaceRect.top - canvasRect.top;
    context.save();
    context.setTransform(
      this.canvas.width / canvasRect.width,
      0,
      0,
      this.canvas.height / canvasRect.height,
      0,
      0
    );
    context.clearRect(targetLeft, targetTop, surfaceRect.width, surfaceRect.height);
    context.beginPath();
    context.roundRect(targetLeft, targetTop, surfaceRect.width, surfaceRect.height, {
      x: borderRadiusPx * surfaceRect.width / surfaceWidth,
      y: borderRadiusPx * surfaceRect.height / surfaceHeight
    });
    context.clip();
    if (isContextLost) {
      this.controller.drawCpuViewport(viewport, context, {
        heightPx: surfaceRect.height,
        leftPx: targetLeft,
        topPx: targetTop,
        widthPx: surfaceRect.width
      });
    } else {
      context.drawImage(
        source,
        left,
        source.height - bottom - heightPx,
        widthPx,
        heightPx,
        targetLeft,
        targetTop,
        surfaceRect.width,
        surfaceRect.height
      );
    }
    context.restore();
  }
  dispose() {
    views.delete(this);
    const canvas = this.getRenderer().domElement;
    canvas.removeEventListener("webglcontextlost", this.handleContextLost);
    canvas.removeEventListener(
      "webglcontextrestored",
      this.handleContextRestored
    );
    this.controller.dispose();
    super.dispose();
  }
}

const defaultContainer = HTML.div({
  style: {
    height: "100px",
    width: "100px"
  }
});
const setupFullHeadMPRView = (preserveDrawingBuffer = true, container = defaultContainer) => {
  const view = new FullHeadMPRView();
  view.setListeners(FullHeadMPREventNames);
  view.init({
    container,
    name: "fullHeadMPR",
    rendererProps: {
      antialias: true,
      preserveDrawingBuffer
    }
  });
  return view;
};

class Mode {
  constructor(options) {
    if (Array.isArray(options)) {
      this.options = { tools: { toolList: options, include: true } };
    } else {
      this.options = options;
    }
    this.toolConstructors = /* @__PURE__ */ new Set();
    this.init = this.init.bind(this);
    this.name = "";
    this.enable = false;
  }
  setHostObject(hostObject, modeName) {
    this.hostObject = hostObject;
    this.name = modeName;
    const rootTools = this.hostObject.toolController.getTools();
    if (!rootTools) {
      console.error("Tools are not defined!");
      return;
    }
    if (!this.options) {
      Array.from(rootTools.keys()).forEach((item) => this.toolConstructors.add(item));
    } else {
      if (this.options.tools) {
        if (this.options.tools.include) {
          this.options.tools.toolList.forEach((item) => this.toolConstructors.add(item));
        } else {
          Array.from(rootTools.keys()).forEach((item) => this.toolConstructors.add(item));
          this.options.tools.toolList.forEach((toolName) => this.toolConstructors.delete(toolName));
        }
      } else {
        Array.from(rootTools.keys()).forEach((item) => this.toolConstructors.add(item));
      }
    }
  }
  init() {
    if (!this.hostObject) {
      console.error("Mode hostObject is not defined before init");
      return;
    }
    const prevMode = this.hostObject.toolController.getMode();
    if (prevMode && prevMode.enable) {
      prevMode.toolConstructors.forEach((toolName) => {
        if (!this.toolConstructors.has(toolName)) {
          this.hostObject.toolController.getTool(toolName)?.dispose();
        }
      });
      this.toolConstructors.forEach((toolName) => {
        if (!prevMode.toolConstructors.has(toolName)) {
          this.hostObject.toolController.getTool(toolName)?.init();
        }
      });
      prevMode.enable = false;
    } else {
      this.toolConstructors.forEach((toolName) => {
        this.hostObject.toolController.getTool(toolName)?.init();
      });
    }
    this.enable = true;
  }
  dispose() {
    if (!this.hostObject) {
      console.error("Mode hostObject is not defined while disposing");
      return;
    }
    this.toolConstructors.forEach((toolName) => {
      this.hostObject.toolController.getTool(toolName)?.dispose();
    });
    this.enable = false;
  }
  getName() {
    return this.name;
  }
  getToolNames() {
    return this.toolConstructors;
  }
}

const tuple = (...args) => args;

class ActiveViewSwitcher extends ViewTool {
  constructor(view) {
    super(view);
    this.onAddViewport = this.onAddViewport.bind(this);
    this.onMouseOver = this.onMouseOver.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
    this.onMouseOverWhileTransform = this.onMouseOverWhileTransform.bind(this);
  }
  activate() {
    this.viewports = [...this.hostObject.getViewports()];
    this.viewports.forEach((viewport) => {
      App.Instance().mouseController.addListener("pointermove", this.onMouseMove, viewport.getDomElement());
    });
    this.hostObject.addEventListener("viewportAdded", this.onAddViewport);
    this.hostObject.addEventListener("transformStart", this.onTransformStart);
  }
  deactivate() {
    [...this.hostObject.getViewports()].forEach((viewport) => {
      App.Instance().mouseController.removeListener("pointerover", this.onMouseOver, viewport.getDomElement());
      App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, viewport.getDomElement());
      App.Instance().mouseController.removeListener("pointerleave", this.onMouseLeave, viewport.getDomElement());
    });
    this.hostObject.removeEventListener("viewportAdded", this.onAddViewport);
    this.hostObject.removeEventListener("transformStart", this.onTransformStart);
    this.hostObject.removeEventListener("transformEnd", this.onTransformEnd);
    this.viewports.length = 0;
  }
  onAddViewport(event) {
    App.Instance().mouseController.addListener("pointermove", this.onMouseMove, event.viewport.getDomElement());
    this.viewports.push(event.viewport);
  }
  onRemoveViewport(event) {
    App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, event.viewport.getDomElement());
    this.viewports.splice(this.viewports.indexOf(event.viewport));
  }
  onTransformStart() {
    this.event = void 0;
    [...this.hostObject.getViewports()].forEach((viewport) => {
      App.Instance().mouseController.removeListener("pointerover", this.onMouseOver, viewport.getDomElement());
      App.Instance().mouseController.addListener("pointerover", this.onMouseOverWhileTransform, viewport.getDomElement());
    });
    this.hostObject.addEventListener("transformEnd", this.onTransformEnd);
  }
  onTransformEnd() {
    this.viewports.forEach((viewport) => {
      App.Instance().mouseController.removeListener("pointerover", this.onMouseOverWhileTransform, viewport.getDomElement());
    });
    this.hostObject.removeEventListener("transformEnd", this.onTransformEnd);
    if (this.event) {
      this.onMouseOver(this.event);
    }
  }
  onMouseOverWhileTransform(event) {
    this.event = event;
  }
  onMouseOver(event) {
    for (let viewport of this.viewports) {
      if (viewport.isEnable() && viewport.getDomElement() === event.target) {
        App.Instance().setActiveView(this.hostObject);
        this.hostObject.setActiveViewport(viewport.name);
        this.viewports.forEach((viewport2) => {
          App.Instance().mouseController.removeListener("pointerover", this.onMouseOver, viewport2.getDomElement());
          App.Instance().mouseController.addListener("pointerleave", this.onMouseLeave, viewport2.getDomElement());
        });
        return true;
      }
    }
    this.viewports.forEach((viewport) => {
      App.Instance().mouseController.removeListener("pointerleave", this.onMouseLeave, viewport.getDomElement());
      App.Instance().mouseController.addListener("pointerover", this.onMouseOver, viewport.getDomElement());
    });
    return false;
  }
  onMouseLeave(event) {
    for (let viewport of this.viewports) {
      if (viewport.isEnable() && viewport.getDomElement() === event.relatedTarget) {
        App.Instance().setActiveView(this.hostObject);
        this.hostObject.setActiveViewport(viewport.name);
        return true;
      }
    }
    this.viewports.forEach((viewport) => {
      App.Instance().mouseController.removeListener("pointerleave", this.onMouseLeave, viewport.getDomElement());
      App.Instance().mouseController.addListener("pointerover", this.onMouseOver, viewport.getDomElement());
    });
    this.hostObject.setActiveViewport(void 0);
    return false;
  }
  onMouseMove(event) {
    if (this.onMouseOver(event)) {
      this.viewports.forEach((viewport) => {
        App.Instance().mouseController.removeListener("pointermove", this.onMouseMove, viewport.getDomElement());
      });
    }
  }
}

class UndoRedo extends ViewTool {
  constructor(view) {
    super(view);
    this.onKeyDown = this.onKeyDown.bind(this);
  }
  activate() {
    App.Instance().keyboardController.addListener("keydown", this.onKeyDown, "KeyZ");
  }
  deactivate() {
    App.Instance().keyboardController.removeListener("keydown", this.onKeyDown, "KeyZ");
  }
  onKeyDown(event) {
    if (event.ctrlKey || event.metaKey) {
      if (event.shiftKey) {
        this.hostObject.undoStack.redo();
      } else {
        this.hostObject.undoStack.undo();
      }
    }
  }
}

class Polyline extends LineLikeEntity {
  constructor(props, type, viewport) {
    if (Array.isArray(props)) {
      super(type, viewport);
      this.points = props;
    } else {
      super(props, type, viewport);
      this.points = props.points.map((point) => new Vector3(point.x, point.y, point.z));
    }
    this.mathLines = [];
    this.setMathLines();
  }
  getPoints() {
    return this.points;
  }
  setPoints(points) {
    this.points = points.map((point) => new Vector3(point.x, point.y, point.z));
    this.mathLines.length = 0;
    this.setMathLines();
    this.update();
  }
  setMathLines() {
    for (let i = 0; i < this.points.length - 1; i++) {
      this.mathLines.push(new Line3(this.points[i], this.points[i + 1]));
    }
  }
  updateLine() {
    const positions = new Float32Array(this.points.flatMap((point) => point.toArray()));
    const geometry = this.object3d.geometry;
    const position = geometry.getAttribute("position");
    if (position instanceof BufferAttribute && position.itemSize === 3 && position.array.length === positions.length) {
      const positionArray = position.array;
      positionArray.set(positions);
      position.needsUpdate = true;
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      return;
    }
    const nextGeometry = new BufferGeometry();
    nextGeometry.setAttribute("position", new BufferAttribute(positions, 3));
    nextGeometry.computeBoundingBox();
    nextGeometry.computeBoundingSphere();
    this.object3d.geometry = nextGeometry;
    geometry.dispose();
  }
  updateLine2() {
    const line = this.object3d;
    const geometry = line.geometry;
    const instanceStart = geometry.getAttribute("instanceStart");
    const segmentCount = Math.max(0, this.points.length - 1);
    const segmentPositions = new Float32Array(segmentCount * 6);
    this.points.slice(0, -1).forEach((point, segmentIndex) => {
      const nextPoint = this.points[segmentIndex + 1];
      const offset = segmentIndex * 6;
      segmentPositions.set([
        point.x,
        point.y,
        point.z,
        nextPoint.x,
        nextPoint.y,
        nextPoint.z
      ], offset);
    });
    if (instanceStart instanceof InterleavedBufferAttribute && instanceStart.count === segmentCount && instanceStart.data.array.length === segmentPositions.length) {
      const instanceArray = instanceStart.data.array;
      instanceArray.set(segmentPositions);
      instanceStart.data.needsUpdate = true;
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      return;
    }
    const nextGeometry = new LineGeometry();
    nextGeometry.setPositions(this.points.flatMap((point) => point.toArray()));
    line.geometry = nextGeometry;
    geometry.dispose();
  }
  updateLineDistances() {
    if (this.object3d instanceof Line2) {
      const instanceStart = this.object3d.geometry.getAttribute("instanceStart");
      const instanceEnd = this.object3d.geometry.getAttribute("instanceEnd");
      const distanceStart = this.object3d.geometry.getAttribute("instanceDistanceStart");
      if (instanceStart instanceof InterleavedBufferAttribute && instanceEnd instanceof InterleavedBufferAttribute && distanceStart instanceof InterleavedBufferAttribute && distanceStart.count === instanceStart.count) {
        const distances = distanceStart.data.array;
        let distance = 0;
        const start = new Vector3();
        const end = new Vector3();
        for (let index = 0; index < instanceStart.count; index++) {
          start.fromBufferAttribute(instanceStart, index);
          end.fromBufferAttribute(instanceEnd, index);
          distances[index * 2] = distance;
          distance += start.distanceTo(end);
          distances[index * 2 + 1] = distance;
        }
        distanceStart.data.needsUpdate = true;
        return;
      }
    } else {
      const position = this.object3d.geometry.getAttribute("position");
      const lineDistance = this.object3d.geometry.getAttribute("lineDistance");
      if (position instanceof BufferAttribute && lineDistance instanceof BufferAttribute && lineDistance.count === position.count) {
        let distance = 0;
        const current = new Vector3();
        const previous = new Vector3();
        for (let index = 0; index < position.count; index++) {
          if (index > 0) {
            previous.fromBufferAttribute(position, index - 1);
            current.fromBufferAttribute(position, index);
            distance += previous.distanceTo(current);
          }
          lineDistance.setX(index, distance);
        }
        lineDistance.needsUpdate = true;
        return;
      }
    }
    this.object3d.computeLineDistances();
  }
  getSegments() {
    return this.points;
  }
}

class Polygon extends Polyline {
  constructor(props, type, viewport) {
    if (Array.isArray(props)) {
      if (props[0].x !== props.at(-1)?.x || props[0].y !== props.at(-1)?.y) {
        props.push(new Vector3().copy(props[0]));
      }
      super(props, type, viewport);
      this.fill = new Mesh();
    } else {
      if (props.points[0].x !== props.points.at(-1)?.x || props.points[0].y !== props.points.at(-1)?.y) {
        props.points.push({ x: props.points[0].x, y: props.points[0].y, z: props.points[0].z });
      }
      super(props, type, viewport);
      this.fill = new Mesh();
      this.fill.material.color = props.fillColor ? new Color(props.fillColor) : new Color("red");
      this.fill.material.opacity = props.fillOpacity ? props.fillOpacity : 1;
    }
    this.fill.material.transparent = true;
    this.fill.material.side = DoubleSide;
    this.fill.layers = this.object3d.layers;
    this.object3d.add(this.fill);
  }
  update() {
    super.update();
    this.updateFillGeometry();
  }
  updateFillGeometry() {
    const { points, indices } = triangulate(this.points.map((point) => new Vector2(point.x, point.y)));
    const geometry = this.fill.geometry;
    const position = geometry.getAttribute("position");
    const index = geometry.getIndex();
    if (position instanceof Float32BufferAttribute && position.array.length === points.length && index && index.count === indices.length) {
      const positionArray = position.array;
      const indexArray = index.array;
      positionArray.set(points);
      indexArray.set(indices);
      position.needsUpdate = true;
      index.needsUpdate = true;
      geometry.computeBoundingBox();
      geometry.computeBoundingSphere();
      return;
    }
    const nextGeometry = new BufferGeometry();
    nextGeometry.setAttribute("position", new Float32BufferAttribute(points, 3));
    nextGeometry.setIndex(indices);
    nextGeometry.computeBoundingBox();
    nextGeometry.computeBoundingSphere();
    this.fill.geometry = nextGeometry;
    geometry.dispose();
  }
  showFill() {
    this.object3d.add(this.fill);
  }
  hideFill() {
    this.object3d.remove(this.fill);
  }
}

class MaskHighlighter extends Highlighter {
  constructor(entity) {
    super(entity);
    if (!(entity instanceof Mask)) {
      throw new Error("Mask-only component");
    }
  }
  highlight() {
    this.entity.hideFill();
  }
  unHighlight() {
    this.entity.showFill();
  }
}

const invMatrix = new Matrix4();
const pointerPosition = new Vector3();
class MaskRaycastComponent extends Raycast {
  constructor(entity) {
    super(entity);
    if (!("getPoints" in entity)) {
      throw new Error("Polygon-like-only component");
    }
  }
  *intersect(raycaster) {
    pointerPosition.copy(raycaster.ray.origin);
    const activeViewport = App.Instance().getActiveView()?.getActiveViewport();
    if (!activeViewport?.camera.layers.test(this.entity.object3d.layers)) {
      return;
    }
    invMatrix.copy(this.entity.object3d.matrixWorld).invert();
    const isIntersect = isPointInsidePolygon(pointerPosition.applyMatrix4(invMatrix), this.entity.getPoints());
    if (isIntersect) {
      yield {
        raycast: this,
        intersections: [
          {
            point: pointerPosition.clone(),
            distance: pointerPosition.z,
            object: this.entity.object3d
          }
        ]
      };
    }
  }
}
class EditableMaskRaycastComponent extends MaskRaycastComponent {
  *intersect(raycaster) {
    if (!this.entity.object3d.visible) {
      return;
    }
    for (const child of this.entity.children) {
      if (!child.object3d.visible) {
        continue;
      }
      const raycast = child.getComponent(Raycast);
      if (raycast) {
        yield* raycast.intersect(raycaster);
      }
    }
    yield* super.intersect(raycaster);
  }
}

class MaskOutlineHighlighter extends Highlighter {
  constructor(entity) {
    super(entity);
    if (!(entity instanceof Mask)) {
      throw new Error("Mask-only component");
    }
  }
  highlight() {
    if (!this.initialLinewidth) {
      this.initialLinewidth = this.entity.object3d.material.linewidth;
    }
    this.entity.object3d.material.linewidth = 3 * this.initialLinewidth;
  }
  unHighlight() {
    if (this.initialLinewidth) {
      this.entity.object3d.material.linewidth = this.initialLinewidth;
      this.initialLinewidth = void 0;
    }
  }
}

const LINE_HIT_TOLERANCE_PX = 5;
const NUMERIC_EPSILON$1 = 1e-10;
class MaskContourHandleRaycast extends Raycast {
  constructor(entity, getContourPoints, onIntersection) {
    super(entity);
    if (!(entity instanceof RoundHandle)) {
      throw new Error("MaskContourHandleRaycast requires a RoundHandle");
    }
    this.getContourPoints = getContourPoints;
    this.onIntersection = onIntersection;
  }
  *intersect(raycaster) {
    const parent = this.entity.parent;
    const points = this.getContourPoints();
    const activeViewport = App.Instance().getActiveView()?.getActiveViewport();
    if (!this.entity.object3d.visible || !parent || points.length < 2 || !activeViewport?.camera.layers.test(parent.object3d.layers)) {
      return;
    }
    parent.object3d.updateWorldMatrix(true, false);
    const inverseMatrix = new Matrix4().copy(parent.object3d.matrixWorld).invert();
    const localRay = raycaster.ray.clone().applyMatrix4(inverseMatrix);
    const contourZ = points[0].z;
    const localPointer = localRay.origin.clone();
    if (Math.abs(localRay.direction.z) > NUMERIC_EPSILON$1) {
      localRay.at(
        (contourZ - localRay.origin.z) / localRay.direction.z,
        localPointer
      );
    } else {
      localPointer.z = contourZ;
    }
    const parentScale = parent.object3d.getWorldScale(new Vector3());
    const smallestWorldScale = Math.max(
      NUMERIC_EPSILON$1,
      Math.min(Math.abs(parentScale.x), Math.abs(parentScale.y))
    );
    const worldUnitsPerPixel = raycaster.camera instanceof OrthographicCamera ? 2 / Math.max(
      Math.abs(raycaster.camera.projectionMatrix.elements[5]) * activeViewport.size.w,
      NUMERIC_EPSILON$1
    ) : 1;
    const localTolerance = LINE_HIT_TOLERANCE_PX * worldUnitsPerPixel / smallestWorldScale;
    const segment = new Line3();
    const closestPoint = new Vector3();
    let closestDistanceSq = Infinity;
    let closestLocation;
    let closestLocalPoint;
    points.forEach((point, segmentStartIndex) => {
      segment.set(point, points[(segmentStartIndex + 1) % points.length]);
      const interpolation = segment.closestPointToPointParameter(localPointer, true);
      segment.at(interpolation, closestPoint);
      const distanceSq = closestPoint.distanceToSquared(localPointer);
      if (distanceSq < closestDistanceSq) {
        closestDistanceSq = distanceSq;
        closestLocation = { interpolation, segmentStartIndex };
        closestLocalPoint = closestPoint.clone();
      }
    });
    if (!closestLocation || !closestLocalPoint || closestDistanceSq > localTolerance ** 2) {
      return;
    }
    this.entity.object3d.position.copy(closestLocalPoint);
    this.entity.point.copy(closestLocalPoint);
    this.onIntersection(closestLocation);
    yield {
      intersections: [{
        distance: 0,
        object: this.entity.object3d,
        point: closestLocalPoint.clone().applyMatrix4(parent.object3d.matrixWorld)
      }],
      raycast: this
    };
  }
}

const MAX_MASK_NORMAL_MOVEMENT_RADIUS_MM = 5;
const MAX_MASK_AFFECTED_RADIUS_MM = 10;
const DEFAULT_MASK_DEFORMATION_RADII = {
  affectedRadiusMm: 3,
  normalMovementRadiusMm: 1
};
const NUMERIC_EPSILON = 1e-10;
const validateMaskDeformationRadii = (normalMovementRadiusMm, affectedRadiusMm) => {
  if (!Number.isFinite(normalMovementRadiusMm) || normalMovementRadiusMm < 0 || normalMovementRadiusMm > MAX_MASK_NORMAL_MOVEMENT_RADIUS_MM) {
    throw new RangeError(
      `normalMovementRadiusMm must be between 0 and ${MAX_MASK_NORMAL_MOVEMENT_RADIUS_MM}`
    );
  }
  if (!Number.isFinite(affectedRadiusMm) || affectedRadiusMm < normalMovementRadiusMm || affectedRadiusMm > MAX_MASK_AFFECTED_RADIUS_MM) {
    throw new RangeError(
      `affectedRadiusMm must be between normalMovementRadiusMm and ${MAX_MASK_AFFECTED_RADIUS_MM}`
    );
  }
  return { normalMovementRadiusMm, affectedRadiusMm };
};
const getContourPointDistanceMm = (first, second, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => {
  const { xMmPerPixel, yMmPerPixel } = normalizePixelSpacingMm(pixelSpacingMm);
  return Math.hypot(
    (first.x - second.x) * xMmPerPixel,
    (first.y - second.y) * yMmPerPixel
  );
};
const getClosedContourArcDistancesMm = (points, selectedIndex, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM, maximumDistanceMm = Infinity) => {
  if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= points.length) {
    throw new RangeError("selectedIndex must reference a contour vertex");
  }
  if (Number.isNaN(maximumDistanceMm) || maximumDistanceMm < 0) {
    throw new RangeError("maximumDistanceMm must be a non-negative number");
  }
  const normalizedPixelSpacingMm = normalizePixelSpacingMm(pixelSpacingMm);
  const distancesMm = new Array(points.length).fill(Infinity);
  distancesMm[selectedIndex] = 0;
  for (const direction of [-1, 1]) {
    let currentIndex = selectedIndex;
    let accumulatedDistanceMm = 0;
    for (let step = 1; step < points.length; step++) {
      const nextIndex = (currentIndex + direction + points.length) % points.length;
      accumulatedDistanceMm += getContourPointDistanceMm(
        points[currentIndex],
        points[nextIndex],
        normalizedPixelSpacingMm
      );
      if (accumulatedDistanceMm > maximumDistanceMm) {
        break;
      }
      distancesMm[nextIndex] = Math.min(
        distancesMm[nextIndex],
        accumulatedDistanceMm
      );
      currentIndex = nextIndex;
    }
  }
  return distancesMm;
};
const getContourVertexInfluence = (distanceMm, radii) => {
  validateMaskDeformationRadii(
    radii.normalMovementRadiusMm,
    radii.affectedRadiusMm
  );
  if (distanceMm <= radii.normalMovementRadiusMm) {
    return { region: "move", strength: 1 };
  }
  if (distanceMm >= radii.affectedRadiusMm || radii.affectedRadiusMm === radii.normalMovementRadiusMm) {
    return { region: "unaffected", strength: 0 };
  }
  return {
    region: "affected",
    strength: (radii.affectedRadiusMm - distanceMm) / (radii.affectedRadiusMm - radii.normalMovementRadiusMm)
  };
};
const addCoefficient = (row, index, value) => {
  row.set(index, (row.get(index) ?? 0) + value);
};
const buildClosedContourLaplacian = (points, pixelSpacingMm) => points.map((point, index) => {
  const previousIndex = (index - 1 + points.length) % points.length;
  const nextIndex = (index + 1) % points.length;
  const previousLength = Math.max(
    getContourPointDistanceMm(point, points[previousIndex], pixelSpacingMm),
    NUMERIC_EPSILON
  );
  const nextLength = Math.max(
    getContourPointDistanceMm(point, points[nextIndex], pixelSpacingMm),
    NUMERIC_EPSILON
  );
  const previousWeight = 1 / previousLength;
  const nextWeight = 1 / nextLength;
  const weightSum = previousWeight + nextWeight;
  const row = /* @__PURE__ */ new Map();
  addCoefficient(row, index, 1);
  addCoefficient(row, previousIndex, -previousWeight / weightSum);
  addCoefficient(row, nextIndex, -nextWeight / weightSum);
  return row;
});
const solveLinearSystem = (matrix, vector) => {
  const size = vector.length;
  const augmented = matrix.map((row, index) => [...row, vector[index]]);
  for (let column = 0; column < size; column++) {
    let pivotRow = column;
    for (let row = column + 1; row < size; row++) {
      if (Math.abs(augmented[row][column]) > Math.abs(augmented[pivotRow][column])) {
        pivotRow = row;
      }
    }
    if (pivotRow !== column) {
      [augmented[column], augmented[pivotRow]] = [augmented[pivotRow], augmented[column]];
    }
    const pivot = augmented[column][column];
    if (Math.abs(pivot) <= NUMERIC_EPSILON) {
      continue;
    }
    for (let row = column + 1; row < size; row++) {
      const multiplier = augmented[row][column] / pivot;
      if (Math.abs(multiplier) <= NUMERIC_EPSILON) {
        continue;
      }
      for (let entry = column; entry <= size; entry++) {
        augmented[row][entry] -= multiplier * augmented[column][entry];
      }
    }
  }
  const solution = new Array(size).fill(0);
  for (let row = size - 1; row >= 0; row--) {
    let value = augmented[row][size];
    for (let column = row + 1; column < size; column++) {
      value -= augmented[row][column] * solution[column];
    }
    const pivot = augmented[row][row];
    solution[row] = Math.abs(pivot) > NUMERIC_EPSILON ? value / pivot : 0;
  }
  return solution;
};
const getLaplacianContourWeights = (points, selectedIndex, radii, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => {
  validateMaskDeformationRadii(
    radii.normalMovementRadiusMm,
    radii.affectedRadiusMm
  );
  const normalizedPixelSpacingMm = normalizePixelSpacingMm(pixelSpacingMm);
  if (!Number.isInteger(selectedIndex) || selectedIndex < 0 || selectedIndex >= points.length) {
    throw new RangeError("selectedIndex must reference a contour vertex");
  }
  if (points.length === 1) {
    return [1];
  }
  const contourDistancesMm = getClosedContourArcDistancesMm(
    points,
    selectedIndex,
    normalizedPixelSpacingMm,
    radii.affectedRadiusMm
  );
  return getLaplacianContourWeightsFromDistances(
    points,
    contourDistancesMm,
    radii,
    normalizedPixelSpacingMm
  );
};
const getLaplacianContourWeightsFromDistances = (points, contourDistancesMm, radii, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => {
  validateMaskDeformationRadii(
    radii.normalMovementRadiusMm,
    radii.affectedRadiusMm
  );
  if (contourDistancesMm.length !== points.length) {
    throw new RangeError("A contour distance is required for every vertex");
  }
  if (contourDistancesMm.some((distanceMm) => Number.isNaN(distanceMm) || distanceMm < 0)) {
    throw new RangeError("Contour distances must be non-negative numbers");
  }
  const normalizedPixelSpacingMm = normalizePixelSpacingMm(pixelSpacingMm);
  const constraints = /* @__PURE__ */ new Map();
  const freeIndices = [];
  points.forEach((_point, index) => {
    const distanceMm = contourDistancesMm[index];
    if (distanceMm <= radii.normalMovementRadiusMm) {
      constraints.set(index, 1);
    } else if (distanceMm >= radii.affectedRadiusMm) {
      constraints.set(index, 0);
    } else {
      freeIndices.push(index);
    }
  });
  const result = new Array(points.length).fill(0);
  constraints.forEach((value, index) => result[index] = value);
  if (!freeIndices.length) {
    return result;
  }
  const freePositionByVertex = new Map(
    freeIndices.map((vertexIndex, freePosition) => [vertexIndex, freePosition])
  );
  const matrix = freeIndices.map(() => new Array(freeIndices.length).fill(0));
  const rightHandSide = new Array(freeIndices.length).fill(0);
  for (const row of buildClosedContourLaplacian(points, normalizedPixelSpacingMm)) {
    let constrainedValue = 0;
    const freeCoefficients = [];
    row.forEach((coefficient, vertexIndex) => {
      const freePosition = freePositionByVertex.get(vertexIndex);
      if (freePosition === void 0) {
        constrainedValue += coefficient * (constraints.get(vertexIndex) ?? 0);
      } else {
        freeCoefficients.push([freePosition, coefficient]);
      }
    });
    for (const [leftPosition, leftCoefficient] of freeCoefficients) {
      rightHandSide[leftPosition] -= leftCoefficient * constrainedValue;
      for (const [rightPosition, rightCoefficient] of freeCoefficients) {
        matrix[leftPosition][rightPosition] += leftCoefficient * rightCoefficient;
      }
    }
  }
  matrix.forEach((row, index) => row[index] += NUMERIC_EPSILON);
  const freeValues = solveLinearSystem(matrix, rightHandSide);
  freeIndices.forEach((vertexIndex, freePosition) => {
    result[vertexIndex] = Math.min(1, Math.max(0, freeValues[freePosition]));
  });
  return result;
};

const POSITION_EPSILON = 1e-8;
const normalizeIndex = (index, pointCount) => (index % pointCount + pointCount) % pointCount;
const normalizeLocation = (location, pointCount) => {
  if (!pointCount) {
    throw new RangeError("A closed contour must contain at least one point");
  }
  if (!Number.isFinite(location.interpolation)) {
    throw new RangeError("Contour interpolation must be finite");
  }
  const segmentStartIndex = normalizeIndex(location.segmentStartIndex, pointCount);
  const interpolation = Math.min(1, Math.max(0, location.interpolation));
  if (interpolation >= 1 - POSITION_EPSILON) {
    return {
      interpolation: 0,
      segmentStartIndex: (segmentStartIndex + 1) % pointCount
    };
  }
  return {
    interpolation: interpolation <= POSITION_EPSILON ? 0 : interpolation,
    segmentStartIndex
  };
};
const getClosedContourLocationPoint = (points, location) => {
  const normalized = normalizeLocation(location, points.length);
  const nextIndex = (normalized.segmentStartIndex + 1) % points.length;
  return points[normalized.segmentStartIndex].clone().lerp(
    points[nextIndex],
    normalized.interpolation
  );
};
const getClosedContourPerimeterMm = (points, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => {
  if (points.length < 2) {
    return 0;
  }
  const spacing = normalizePixelSpacingMm(pixelSpacingMm);
  return points.reduce((perimeterMm, point, index) => perimeterMm + getContourPointDistanceMm(
    point,
    points[(index + 1) % points.length],
    spacing
  ), 0);
};
const getDirectionalContourArc = (points, location, direction, maximumDistanceMm, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => {
  if (points.length < 2) {
    return [];
  }
  if (!Number.isFinite(maximumDistanceMm) || maximumDistanceMm < 0) {
    throw new RangeError("maximumDistanceMm must be a finite non-negative number");
  }
  const spacing = normalizePixelSpacingMm(pixelSpacingMm);
  const normalized = normalizeLocation(location, points.length);
  const startPoint = getClosedContourLocationPoint(points, normalized);
  const startVertexIndex = normalized.interpolation === 0 ? normalized.segmentStartIndex : void 0;
  const arc = [{
    distanceMm: 0,
    location: normalized,
    point: startPoint,
    vertexIndex: startVertexIndex
  }];
  if (maximumDistanceMm <= POSITION_EPSILON) {
    return arc;
  }
  let accumulatedDistanceMm = 0;
  let edgeIndex = normalized.segmentStartIndex;
  let interpolation = normalized.interpolation;
  for (let step = 0; step <= points.length; step++) {
    const edgeStart = points[edgeIndex];
    const edgeEnd = points[(edgeIndex + 1) % points.length];
    const fromPoint = edgeStart.clone().lerp(edgeEnd, interpolation);
    const targetInterpolation = direction === 1 ? 1 : 0;
    const targetPoint = direction === 1 ? edgeEnd : edgeStart;
    const segmentLengthMm = getContourPointDistanceMm(
      fromPoint,
      targetPoint,
      spacing
    );
    const remainingDistanceMm = maximumDistanceMm - accumulatedDistanceMm;
    if (segmentLengthMm > remainingDistanceMm + POSITION_EPSILON) {
      const ratio = remainingDistanceMm / segmentLengthMm;
      const nextInterpolation = interpolation + (targetInterpolation - interpolation) * ratio;
      arc.push({
        distanceMm: maximumDistanceMm,
        location: { interpolation: nextInterpolation, segmentStartIndex: edgeIndex },
        point: fromPoint.clone().lerp(targetPoint, ratio)
      });
      break;
    }
    if (segmentLengthMm > POSITION_EPSILON) {
      accumulatedDistanceMm += segmentLengthMm;
      const vertexIndex = direction === 1 ? (edgeIndex + 1) % points.length : edgeIndex;
      arc.push({
        distanceMm: Math.min(accumulatedDistanceMm, maximumDistanceMm),
        location: { interpolation: 0, segmentStartIndex: vertexIndex },
        point: points[vertexIndex].clone(),
        vertexIndex
      });
      if (accumulatedDistanceMm >= maximumDistanceMm - POSITION_EPSILON) {
        break;
      }
    }
    if (direction === 1) {
      edgeIndex = (edgeIndex + 1) % points.length;
      interpolation = 0;
    } else {
      edgeIndex = (edgeIndex - 1 + points.length) % points.length;
      interpolation = 1;
    }
  }
  return arc;
};
const getClosedContourArcDistancesFromLocationMm = (points, location, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM, maximumDistanceMm = Infinity) => {
  if (Number.isNaN(maximumDistanceMm) || maximumDistanceMm < 0) {
    throw new RangeError("maximumDistanceMm must be a non-negative number");
  }
  const distancesMm = new Array(points.length).fill(Infinity);
  const directionalMaximumDistanceMm = Number.isFinite(maximumDistanceMm) ? maximumDistanceMm : getClosedContourPerimeterMm(points, pixelSpacingMm);
  for (const direction of [-1, 1]) {
    getDirectionalContourArc(
      points,
      location,
      direction,
      directionalMaximumDistanceMm,
      pixelSpacingMm
    ).forEach(({ distanceMm, vertexIndex }) => {
      if (vertexIndex !== void 0) {
        distancesMm[vertexIndex] = Math.min(
          distancesMm[vertexIndex],
          distanceMm
        );
      }
    });
  }
  return distancesMm;
};
const addPendingInsertion = (insertions, location, isSelected = false) => {
  const existing = insertions.find((insertion) => insertion.segmentStartIndex === location.segmentStartIndex && Math.abs(insertion.interpolation - location.interpolation) <= POSITION_EPSILON);
  if (existing) {
    existing.isSelected || (existing.isSelected = isSelected);
    return;
  }
  insertions.push({ ...location, isSelected });
};
const refineContourForDeformation = (points, location, normalMovementRadiusMm, affectedRadiusMm, pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM) => {
  if (points.length < 2) {
    throw new RangeError("A deformable contour must contain at least two points");
  }
  if (!Number.isFinite(affectedRadiusMm) || affectedRadiusMm < 0) {
    throw new RangeError("affectedRadiusMm must be a finite non-negative number");
  }
  if (!Number.isFinite(normalMovementRadiusMm) || normalMovementRadiusMm < 0 || normalMovementRadiusMm > affectedRadiusMm) {
    throw new RangeError(
      "normalMovementRadiusMm must be between zero and affectedRadiusMm"
    );
  }
  const spacing = normalizePixelSpacingMm(pixelSpacingMm);
  const normalized = normalizeLocation(location, points.length);
  const insertions = [];
  let selectedOriginalIndex;
  if (normalized.interpolation === 0) {
    selectedOriginalIndex = normalized.segmentStartIndex;
  } else {
    const point = getClosedContourLocationPoint(points, normalized);
    const previousIndex = normalized.segmentStartIndex;
    const nextIndex = (previousIndex + 1) % points.length;
    const previousDistanceMm = getContourPointDistanceMm(
      point,
      points[previousIndex],
      spacing
    );
    const nextDistanceMm = getContourPointDistanceMm(
      point,
      points[nextIndex],
      spacing
    );
    if (previousDistanceMm > normalMovementRadiusMm + POSITION_EPSILON && nextDistanceMm > normalMovementRadiusMm + POSITION_EPSILON) {
      addPendingInsertion(insertions, normalized, true);
    } else {
      selectedOriginalIndex = previousDistanceMm <= nextDistanceMm ? previousIndex : nextIndex;
    }
  }
  const insertionIndices = [];
  const originalIndices = /* @__PURE__ */ new Map();
  let selectedIndex;
  let deformationLocation;
  const refinedPoints = [];
  for (let pointIndex = 0; pointIndex < points.length; pointIndex++) {
    originalIndices.set(pointIndex, refinedPoints.length);
    refinedPoints.push(points[pointIndex].clone());
    const edgeInsertions = insertions.filter((insertion) => insertion.segmentStartIndex === pointIndex).sort((first, second) => first.interpolation - second.interpolation);
    for (const insertion of edgeInsertions) {
      if (insertion.interpolation <= POSITION_EPSILON || insertion.interpolation >= 1 - POSITION_EPSILON) {
        continue;
      }
      const insertionIndex = refinedPoints.length;
      refinedPoints.push(points[pointIndex].clone().lerp(
        points[(pointIndex + 1) % points.length],
        insertion.interpolation
      ));
      insertionIndices.push(insertionIndex);
      if (insertion.isSelected) {
        selectedIndex = insertionIndex;
        deformationLocation = {
          interpolation: 0,
          segmentStartIndex: insertionIndex
        };
      }
    }
  }
  selectedIndex ?? (selectedIndex = selectedOriginalIndex === void 0 ? void 0 : originalIndices.get(selectedOriginalIndex));
  deformationLocation ?? (deformationLocation = normalized.interpolation === 0 ? {
    interpolation: 0,
    segmentStartIndex: originalIndices.get(normalized.segmentStartIndex)
  } : {
    interpolation: normalized.interpolation,
    segmentStartIndex: originalIndices.get(normalized.segmentStartIndex)
  });
  if (selectedIndex === void 0) {
    throw new Error("Could not locate the deformation point in the refined contour");
  }
  return {
    deformationLocation,
    insertedVertexIndices: insertionIndices,
    points: refinedPoints,
    selectedIndex
  };
};

const normalizeCircularValue = (value, period) => period ? (value % period + period) % period : value;
const interpolateValue = (start, end, interpolation, period) => {
  const forwardEnd = period && end < start ? end + period : end;
  return normalizeCircularValue(
    start + (forwardEnd - start) * interpolation,
    period
  );
};
const interpolateVertex = (start, end, interpolation, valuePeriod) => ({
  point: start.point.clone().lerp(end.point, interpolation),
  value: interpolateValue(start.value, end.value, interpolation, valuePeriod)
});
const smoothPathWithValues = (points, values, times = 1, valuePeriod) => {
  if (values.length !== points.length) {
    throw new RangeError("A value is required for every path point");
  }
  if (valuePeriod !== void 0 && (!Number.isFinite(valuePeriod) || valuePeriod <= 0)) {
    throw new RangeError("valuePeriod must be a finite positive number");
  }
  let vertices = points.map((point, index) => ({
    point: new Vector2(point.x, point.y),
    value: normalizeCircularValue(values[index], valuePeriod)
  }));
  for (let iteration = 0; iteration < times; iteration++) {
    for (let index = vertices.length - 1; index >= 0; index--) {
      const current = vertices[index];
      const next = vertices[(index + 1) % vertices.length];
      const vector = next.point.clone().sub(current.point);
      const length = vector.length();
      if (length > 3) {
        vertices.splice(
          index + 1,
          0,
          interpolateVertex(current, next, 1 - 1 / length, valuePeriod)
        );
        vertices.splice(
          index + 1,
          0,
          interpolateVertex(current, next, 1 / length, valuePeriod)
        );
      }
    }
    vertices = vertices.map((vertex, index, pathVertices) => interpolateVertex(
      vertex,
      pathVertices[(index + 1) % pathVertices.length],
      0.5,
      valuePeriod
    ));
  }
  return {
    points: vertices.map(({ point }) => point),
    values: vertices.map(({ value }) => value)
  };
};
const smoothPath = (points, times = 1) => smoothPathWithValues(points, new Array(points.length).fill(0), times).points;

const CONTOUR_DISTANCE_EPSILON_MM = 1e-8;
const getContourSourceCoordinates = (points, pixelSpacingMm) => {
  const distancesMm = points.map(() => 0);
  for (let index = 1; index < points.length; index++) {
    distancesMm[index] = distancesMm[index - 1] + getContourPointDistanceMm(
      points[index - 1],
      points[index],
      pixelSpacingMm
    );
  }
  const closingDistanceMm = points.length > 1 ? getContourPointDistanceMm(
    points.at(-1),
    points[0],
    pixelSpacingMm
  ) : 0;
  return {
    distancesMm,
    perimeterMm: (distancesMm.at(-1) ?? 0) + closingDistanceMm
  };
};
const normalizeContourDistance = (distanceMm, perimeterMm) => perimeterMm > 0 ? (distanceMm % perimeterMm + perimeterMm) % perimeterMm : 0;
const getForwardContourDistance = (startDistanceMm, endDistanceMm, perimeterMm) => normalizeContourDistance(endDistanceMm - startDistanceMm, perimeterMm);
const getContourLocationSourceDistance = (points, location, coordinates, pixelSpacingMm) => {
  if (!points.length || coordinates.perimeterMm <= 0) {
    return 0;
  }
  const segmentStartIndex = (location.segmentStartIndex % points.length + points.length) % points.length;
  const nextIndex = (segmentStartIndex + 1) % points.length;
  const segmentLengthMm = getContourPointDistanceMm(
    points[segmentStartIndex],
    points[nextIndex],
    pixelSpacingMm
  );
  return normalizeContourDistance(
    coordinates.distancesMm[segmentStartIndex] + segmentLengthMm * Math.min(1, Math.max(0, location.interpolation)),
    coordinates.perimeterMm
  );
};
const getContourLocationAtSourceDistance = (points, sourceDistanceMm, coordinates) => {
  if (!points.length || coordinates.perimeterMm <= 0) {
    return;
  }
  const normalizedDistanceMm = normalizeContourDistance(
    sourceDistanceMm,
    coordinates.perimeterMm
  );
  for (let index = 0; index < points.length; index++) {
    const startDistanceMm = coordinates.distancesMm[index];
    if (Math.abs(normalizedDistanceMm - startDistanceMm) <= CONTOUR_DISTANCE_EPSILON_MM) {
      return { interpolation: 0, segmentStartIndex: index };
    }
    const endDistanceMm = index === points.length - 1 ? coordinates.perimeterMm : coordinates.distancesMm[index + 1];
    if (normalizedDistanceMm < endDistanceMm) {
      const segmentLengthMm = endDistanceMm - startDistanceMm;
      return {
        interpolation: segmentLengthMm > 0 ? (normalizedDistanceMm - startDistanceMm) / segmentLengthMm : 0,
        segmentStartIndex: index
      };
    }
  }
  return { interpolation: 0, segmentStartIndex: 0 };
};
const getMappedContourPointAtSourceDistance = (points, sourceDistancesMm, sourceDistanceMm, perimeterMm) => {
  if (!points.length || points.length !== sourceDistancesMm.length || perimeterMm <= 0) {
    return;
  }
  const normalizedDistanceMm = normalizeContourDistance(
    sourceDistanceMm,
    perimeterMm
  );
  for (let index = 0; index < points.length; index++) {
    const nextIndex = (index + 1) % points.length;
    const segmentSourceLengthMm = getForwardContourDistance(
      sourceDistancesMm[index],
      sourceDistancesMm[nextIndex],
      perimeterMm
    );
    const targetDistanceMm = getForwardContourDistance(
      sourceDistancesMm[index],
      normalizedDistanceMm,
      perimeterMm
    );
    if (targetDistanceMm <= segmentSourceLengthMm + CONTOUR_DISTANCE_EPSILON_MM) {
      if (targetDistanceMm <= CONTOUR_DISTANCE_EPSILON_MM) {
        return points[index].clone();
      }
      if (Math.abs(targetDistanceMm - segmentSourceLengthMm) <= CONTOUR_DISTANCE_EPSILON_MM) {
        return points[nextIndex].clone();
      }
      const interpolation = segmentSourceLengthMm > 0 ? Math.min(1, targetDistanceMm / segmentSourceLengthMm) : 0;
      return points[index].clone().lerp(points[nextIndex], interpolation);
    }
  }
};
const getMappedContourRangePoints = (points, sourceDistancesMm, startDistanceMm, rangeLengthMm, perimeterMm) => {
  const start = getMappedContourPointAtSourceDistance(
    points,
    sourceDistancesMm,
    startDistanceMm,
    perimeterMm
  );
  if (!start) {
    return [];
  }
  const normalizedRangeLengthMm = Math.min(
    Math.max(0, rangeLengthMm),
    perimeterMm
  );
  if (normalizedRangeLengthMm <= CONTOUR_DISTANCE_EPSILON_MM) {
    return [start];
  }
  const end = getMappedContourPointAtSourceDistance(
    points,
    sourceDistancesMm,
    startDistanceMm + normalizedRangeLengthMm,
    perimeterMm
  );
  if (!end) {
    return [];
  }
  const normalizedStartDistanceMm = normalizeContourDistance(
    startDistanceMm,
    perimeterMm
  );
  const intermediatePoints = points.map((point, index) => ({
    distanceMm: getForwardContourDistance(
      normalizedStartDistanceMm,
      sourceDistancesMm[index],
      perimeterMm
    ),
    point
  })).filter(({ distanceMm }) => distanceMm > CONTOUR_DISTANCE_EPSILON_MM && distanceMm < normalizedRangeLengthMm - CONTOUR_DISTANCE_EPSILON_MM).sort((first, second) => first.distanceMm - second.distanceMm).map(({ point }) => point.clone());
  return [start, ...intermediatePoints, end];
};
const createDisplayContour = (points, referencePoints, smoothingIterations, pixelSpacingMm) => {
  const sourcePoints = points.length === referencePoints.length ? referencePoints : points;
  const coordinates = getContourSourceCoordinates(sourcePoints, pixelSpacingMm);
  if (smoothingIterations <= 0 || coordinates.perimeterMm <= 0 || points.length < 2) {
    return {
      ...coordinates,
      points: points.map((point) => point.clone())
    };
  }
  const smoothed = smoothPathWithValues(
    points,
    coordinates.distancesMm,
    smoothingIterations,
    coordinates.perimeterMm
  );
  const z = points[0]?.z ?? 0;
  return {
    distancesMm: smoothed.values,
    perimeterMm: coordinates.perimeterMm,
    points: smoothed.points.map((point) => new Vector3(point.x, point.y, z))
  };
};

const VERTEX_HANDLE_SIZE = 10;
const VERTEX_HANDLE_INFLUENCED_SIZE = 15;
const VERTEX_HANDLE_COLOR = "#7564ed";
const MASK_DEFORMATION_AFFECTED_COLOR = "#8bc580";
const MASK_DEFORMATION_MOVE_COLOR = "#e98390";
const VERTEX_POSITION_EPSILON_SQ = 1e-12;
const EDIT_CONTOUR_RENDER_ORDER = 97;
const DEFORMATION_BASELINE_RENDER_ORDER = 99;
const DEFORMATION_PREVIEW_RENDER_ORDER = 98;
const DEFORMATION_PREVIEW_MARKER_RENDER_ORDER = 101;
const DEFORMATION_PREVIEW_MARKER_SIZE = 12;
const DEFORMATION_PREVIEW_RED_WIDTH_INCREASE = 4;
const DEFORMATION_FIXED_WEIGHT_EPSILON = 1e-8;
class VertexHandleHighlighter extends Highlighter {
  constructor(entity, onHighlight, onUnHighlight) {
    super(entity);
    this.onHighlight = onHighlight;
    this.onUnHighlight = onUnHighlight;
  }
  highlight() {
    this.onHighlight();
  }
  unHighlight() {
    this.onUnHighlight();
  }
}
const getDirectionalContourArcThroughFixedVertex = (points, location, direction, weights, pixelSpacingMm) => {
  const perimeterMm = getClosedContourPerimeterMm(points, pixelSpacingMm);
  const hasFixedVertex = weights.some(
    (weight) => weight <= DEFORMATION_FIXED_WEIGHT_EPSILON
  );
  const arc = getDirectionalContourArc(
    points,
    location,
    direction,
    hasFixedVertex ? perimeterMm : perimeterMm / 2,
    pixelSpacingMm
  );
  const fixedVertexArcIndex = arc.findIndex(({ distanceMm, vertexIndex }) => distanceMm > 0 && vertexIndex !== void 0 && weights[vertexIndex] <= DEFORMATION_FIXED_WEIGHT_EPSILON);
  return fixedVertexArcIndex >= 0 ? arc.slice(0, fixedVertexArcIndex + 1) : arc;
};
class Mask extends Polygon {
  constructor(props, viewport, lineType = Line2) {
    super(props, lineType, viewport);
    this.displayedContourSourceDistancesMm = [];
    this.displayedContourSourcePerimeterMm = 0;
    this.initialEditablePoints = [];
    this.isContourEditing = false;
    this.smoothingIterations = 0;
    this.vertexHandlesVisible = true;
    this.contourDeformationPreviewLines = [];
    this.contourDeformationPreviewMarkers = [];
    this.editContourOverlayWidth = 1;
    this.vertexDeformationRadii = {
      ...DEFAULT_MASK_DEFORMATION_RADII
    };
    this.pixelSpacingMm = DEFAULT_PIXEL_SPACING_MM;
    let hover;
    if (!Array.isArray(props)) {
      this.id = props.id;
      this.contourEditID = props.contourEditID ?? props.id;
      this.meta = props.meta;
      hover = props.hover;
      this.controlPoints = props.controlPoints?.map((point) => new Vector3(point.x, point.y, point.z));
      this.smoothingIterations = props.smoothingIterations ?? 0;
      this.pixelSpacingMm = normalizePixelSpacingMm(props.pixelSpacingMm);
      if (this.controlPoints && this.controlPoints.length > 1 && this.controlPoints[0].equals(this.controlPoints.at(-1))) {
        this.controlPoints.pop();
      }
      if (props.blending === "NoBlending") {
        this.noBlending();
      } else if (props.blending === "AlphaBlending") {
        this.alphaBlending();
      }
      this.fill.renderOrder = typeof props.fillRenderOrder === "number" ? props.fillRenderOrder : this.object3d.renderOrder - 0.5;
    } else {
      this.contourEditID = "";
      this.fill.renderOrder = this.object3d.renderOrder - 0.5;
    }
    this.object3d.material.transparent = true;
    this.object3d.material.blending = CustomBlending;
    this.object3d.material.blendEquation = AddEquation;
    this.object3d.material.blendSrc = SrcAlphaFactor;
    this.object3d.material.blendDst = ZeroFactor;
    this.initialEditablePoints = this.getEditablePointReferences().map((point) => point.clone());
    this.setComponents(hover);
    this.updateDisplayedContour();
  }
  setComponents(hover) {
    if (hover === "fill") {
      this.setComponent(MaskRaycastComponent);
      this.setComponent(HoverComponent, new HoverComponent(this, new MaskHighlighter(this)));
    } else if (hover === "outline") {
      this.setComponent(RaycastNoneRecursive);
      this.setComponent(HoverComponent, new HoverComponent(this, new MaskOutlineHighlighter(this)));
    }
  }
  showVertices(showDots = true) {
    this.isContourEditing = true;
    this.vertexHandlesVisible = showDots;
    this.updateDisplayedContour();
    const handles = this.getVertexHandles();
    this.keepVertexHandlesScreenSized(handles);
    handles.forEach((handle) => handle.object3d.visible = showDots);
    this.getContourDragHandle().object3d.visible = true;
  }
  hideVertices() {
    this.endVertexDeformation();
    this.isContourEditing = false;
    this.vertexHandlesVisible = false;
    this.clearVertexDeformationBaseline();
    this.vertexHandles?.forEach((handle) => {
      const hover = handle.getComponent(HoverComponent);
      if (hover?.isHovered()) {
        hover.unhover();
      }
      handle.object3d.visible = false;
    });
    const contourDragHover = this.contourDragHandle?.getComponent(
      HoverComponent
    );
    if (contourDragHover?.isHovered()) {
      contourDragHover.unhover();
    }
    if (this.contourDragHandle) {
      this.contourDragHandle.object3d.visible = false;
    }
    this.clearVertexDeformationPreview();
    this.disposeContourDeformationPreview();
    this.updateDisplayedContour();
  }
  getVertexHandles() {
    if (!this.vertexHandles) {
      const editablePoints = this.getEditablePointReferences();
      this.vertexHandles = editablePoints.map((point, index) => {
        const handle = new RoundHandle(point, {
          color: VERTEX_HANDLE_COLOR,
          size: VERTEX_HANDLE_SIZE,
          maxZoom: Infinity
        });
        handle.object3d.material.uniforms.outlineColor.value.set(1, 1, 1, 1);
        handle.object3d.material.transparent = true;
        handle.object3d.renderOrder = 100;
        handle.object3d.visible = this.vertexHandlesVisible;
        handle.setComponent(RoundHandleRaycast, new RoundHandleRaycast(handle, 5));
        handle.setComponent(HoverComponent, new HoverComponent(
          handle,
          new VertexHandleHighlighter(
            handle,
            () => this.previewVertexDeformation(handle),
            () => this.stopPreviewingVertexDeformation(handle)
          )
        ));
        handle.bind();
        handle.addEventListener("transform", () => this.updateAfterVertexTransform(index));
        return handle;
      });
      this.add(this.vertexHandles);
    }
    return this.vertexHandles;
  }
  setVertexHandlesVisible(visible) {
    this.vertexHandlesVisible = visible;
    this.getVertexHandles().forEach((handle) => {
      const hover = handle.getComponent(HoverComponent);
      if (!visible && hover?.isHovered()) {
        hover.unhover();
      }
      handle.object3d.visible = visible;
    });
    this.updateDisplayedContour();
    if (this.vertexDeformationPreviewHandle) {
      this.previewVertexDeformation(this.vertexDeformationPreviewHandle);
    }
  }
  showEditContourOverlay(width) {
    this.editContourOverlayWidth = width;
    let overlay = this.editContourOverlay;
    if (!overlay) {
      overlay = new Polyline({
        color: this.object3d.material.color.getHex(),
        opacity: 1,
        points: this.points.map((point) => point.clone()),
        renderOrder: EDIT_CONTOUR_RENDER_ORDER,
        width
      }, Line2);
      overlay.object3d.material.depthTest = false;
      overlay.object3d.material.depthWrite = false;
      overlay.object3d.material.transparent = true;
      overlay.object3d.layers.mask = this.object3d.layers.mask;
      overlay.update();
      this.add(overlay);
      this.editContourOverlay = overlay;
    }
    overlay.object3d.visible = true;
    this.updateEditContourOverlay();
  }
  hideEditContourOverlay() {
    if (this.editContourOverlay) {
      this.editContourOverlay.object3d.visible = false;
    }
  }
  getContourDragHandle() {
    if (!this.contourDragHandle) {
      const handle = new RoundHandle(new Vector3(), {
        color: VERTEX_HANDLE_COLOR,
        maxZoom: Infinity,
        size: 1
      });
      handle.object3d.material.uniforms.color.value.set(0, 0, 0, 0);
      handle.object3d.material.uniforms.outlineColor.value.set(0, 0, 0, 0);
      handle.object3d.material.transparent = true;
      handle.object3d.renderOrder = 100;
      handle.object3d.visible = false;
      handle.setComponent(
        Raycast,
        new MaskContourHandleRaycast(
          handle,
          () => this.getDisplayedContourPointReferences(),
          (location) => this.contourDragLocation = this.getEditableLocationForDisplayedLocation(location)
        )
      );
      handle.setComponent(HoverComponent, new HoverComponent(
        handle,
        new VertexHandleHighlighter(
          handle,
          () => this.previewVertexDeformation(handle),
          () => this.stopPreviewingVertexDeformation(handle)
        )
      ));
      handle.bind();
      handle.addEventListener(
        "transform",
        () => this.updateFromDeformationDriver(handle)
      );
      this.add(handle);
      this.contourDragHandle = handle;
    }
    return this.contourDragHandle;
  }
  getEditablePoints() {
    return this.getEditablePointReferences().map((point) => point.clone());
  }
  getContourSmoothingIterations() {
    return this.controlPoints ? this.smoothingIterations : 0;
  }
  getEditableContourPosition(displayedPosition) {
    const segmentStartIndex = Math.floor(displayedPosition);
    const location = this.getEditableLocationForDisplayedLocation({
      interpolation: displayedPosition - segmentStartIndex,
      segmentStartIndex
    });
    return location.segmentStartIndex + location.interpolation;
  }
  getInitialEditablePoints() {
    return this.initialEditablePoints.map((point) => point.clone());
  }
  hasEditablePointChanges() {
    const points = this.getEditablePointReferences();
    return points.length !== this.initialEditablePoints.length || points.some(
      (point, index) => point.distanceToSquared(this.initialEditablePoints[index]) > VERTEX_POSITION_EPSILON_SQ
    );
  }
  resetEditablePoints() {
    this.restoreEditablePoints(this.initialEditablePoints);
  }
  setVertexDeformationRadii(radii) {
    this.vertexDeformationRadii = validateMaskDeformationRadii(
      radii.normalMovementRadiusMm,
      radii.affectedRadiusMm
    );
    if (this.vertexDeformationPreviewHandle) {
      this.previewVertexDeformation(this.vertexDeformationPreviewHandle);
    }
  }
  beginVertexDeformation(handle, radii) {
    const isContourDrag = handle === this.contourDragHandle;
    let selectedIndex = this.getVertexHandles().indexOf(handle);
    if (selectedIndex < 0 && !isContourDrag) {
      return false;
    }
    this.setVertexDeformationRadii(radii);
    const pointsBeforeRefinement = this.getEditablePoints();
    const contourLocationBeforeRefinement = isContourDrag && this.contourDragLocation ? { ...this.contourDragLocation } : void 0;
    if (isContourDrag) {
      if (!this.contourDragLocation) {
        return false;
      }
      const refined = refineContourForDeformation(
        pointsBeforeRefinement,
        this.contourDragLocation,
        this.vertexDeformationRadii.normalMovementRadiusMm,
        this.vertexDeformationRadii.affectedRadiusMm,
        this.pixelSpacingMm
      );
      selectedIndex = refined.selectedIndex;
      if (refined.points.length !== pointsBeforeRefinement.length) {
        this.restoreEditablePoints(refined.points);
      }
      this.contourDragLocation = refined.deformationLocation;
      handle.point = getClosedContourLocationPoint(
        this.getEditablePointReferences(),
        refined.deformationLocation
      );
      handle.update();
    }
    const initialPoints = this.getEditablePoints();
    const deformationLocation = isContourDrag ? this.contourDragLocation : { interpolation: 0, segmentStartIndex: selectedIndex };
    this.vertexDeformation = {
      affectedRadiusMm: this.vertexDeformationRadii.affectedRadiusMm,
      contourLocationBeforeRefinement,
      driver: handle,
      driverInitialPoint: handle.object3d.position.clone(),
      initialPoints,
      isContourDrag,
      location: deformationLocation,
      pointsBeforeRefinement: initialPoints.length === pointsBeforeRefinement.length ? void 0 : pointsBeforeRefinement,
      selectedIndex,
      weights: isContourDrag ? getLaplacianContourWeightsFromDistances(
        initialPoints,
        getClosedContourArcDistancesFromLocationMm(
          initialPoints,
          deformationLocation,
          this.pixelSpacingMm,
          this.vertexDeformationRadii.affectedRadiusMm
        ),
        this.vertexDeformationRadii,
        this.pixelSpacingMm
      ) : getLaplacianContourWeights(
        initialPoints,
        selectedIndex,
        this.vertexDeformationRadii,
        this.pixelSpacingMm
      )
    };
    this.showVertexDeformationBaseline(this.vertexDeformation);
    this.previewVertexDeformation(handle);
    return true;
  }
  endVertexDeformation() {
    const deformation = this.vertexDeformation;
    this.vertexDeformation = void 0;
    this.clearVertexDeformationBaseline();
    if (!deformation) {
      return;
    }
    const {
      contourLocationBeforeRefinement,
      driver,
      isContourDrag,
      pointsBeforeRefinement,
      selectedIndex
    } = deformation;
    const point = this.getEditablePointReferences()[selectedIndex].clone();
    const changed = driver.object3d.position.distanceToSquared(
      deformation.driverInitialPoint
    ) > VERTEX_POSITION_EPSILON_SQ;
    if (!changed && pointsBeforeRefinement) {
      this.restoreEditablePoints(pointsBeforeRefinement);
      if (contourLocationBeforeRefinement) {
        this.contourDragLocation = contourLocationBeforeRefinement;
        driver.point = getClosedContourLocationPoint(
          pointsBeforeRefinement,
          contourLocationBeforeRefinement
        );
        driver.update();
      }
    }
    this.updateDisplayedContour();
    const hover = driver.getComponent(HoverComponent);
    if (hover?.isHovered()) {
      this.previewVertexDeformation(driver);
    } else {
      this.clearVertexDeformationPreview();
    }
    return { changed, isContourDrag, point, selectedIndex };
  }
  clearVertexDeformationPreview() {
    this.vertexDeformationPreviewHandle = void 0;
    this.vertexHandles?.forEach((handle) => this.setVertexHandleAppearance(
      handle,
      VERTEX_HANDLE_COLOR,
      VERTEX_HANDLE_SIZE
    ));
    this.contourDeformationPreviewLines.forEach(
      (line) => line.object3d.visible = false
    );
    this.contourDeformationPreviewMarkers.forEach(
      (marker) => marker.object3d.visible = false
    );
  }
  restoreEditablePoints(points) {
    this.endVertexDeformation();
    this.clearVertexDeformationBaseline();
    this.clearVertexDeformationPreview();
    if (this.controlPoints) {
      if (this.controlPoints.length === points.length) {
        this.controlPoints.forEach((point, index) => point.copy(points[index]));
        this.vertexHandles?.forEach((handle) => handle.update());
      } else {
        this.controlPoints = points.map((point) => point.clone());
        this.rebuildVertexHandles();
      }
      this.updateDisplayedContour();
      return;
    }
    const editablePoints = this.points.slice(0, -1);
    if (editablePoints.length === points.length) {
      editablePoints.forEach((point, index) => point.copy(points[index]));
      this.points.at(-1)?.copy(this.points[0]);
      this.vertexHandles?.forEach((handle) => handle.update());
    } else {
      this.points = points.map((point) => point.clone());
      if (this.points.length) {
        this.points.push(this.points[0].clone());
      }
      this.rebuildVertexHandles();
    }
    this.updateDisplayedContour();
  }
  rebuildVertexHandles() {
    const handles = this.vertexHandles;
    if (!handles) {
      return;
    }
    handles.forEach((handle) => {
      this.delete(handle);
      handle.dispose();
    });
    this.vertexHandles = void 0;
    this.getVertexHandles().forEach(
      (handle) => handle.object3d.visible = this.vertexHandlesVisible
    );
  }
  showVertexDeformationBaseline(deformation) {
    this.clearVertexDeformationBaseline();
    const points = this.getVertexDeformationBaselinePoints(deformation);
    if (points.length < 2) {
      return;
    }
    const baseline = new Polyline({
      color: this.object3d.material.color.getHex(),
      dashed: true,
      dashScale: this.pixelSpacingMm.xMmPerPixel,
      dashSize: 1,
      gapSize: 0.75,
      opacity: 1,
      points,
      renderOrder: DEFORMATION_BASELINE_RENDER_ORDER,
      width: Math.max(2, this.object3d.material.linewidth ?? 0)
    }, Line2);
    baseline.object3d.material.depthTest = false;
    baseline.object3d.material.depthWrite = false;
    baseline.object3d.material.transparent = true;
    baseline.update();
    baseline.object3d.computeLineDistances();
    this.add(baseline);
    this.vertexDeformationBaseline = baseline;
  }
  getVertexDeformationBaselinePoints(deformation) {
    const { affectedRadiusMm, initialPoints, location } = deformation;
    const sourcePoints = initialPoints;
    const pointCount = sourcePoints.length;
    if (pointCount < 2) {
      return sourcePoints.map((point) => point.clone());
    }
    const coordinates = getContourSourceCoordinates(
      sourcePoints,
      this.pixelSpacingMm
    );
    const display = createDisplayContour(
      sourcePoints,
      sourcePoints,
      this.isSmoothedContourDisplayed() ? this.smoothingIterations : 0,
      this.pixelSpacingMm
    );
    const sourceDistanceMm = getContourLocationSourceDistance(
      sourcePoints,
      location,
      coordinates,
      this.pixelSpacingMm
    );
    if (affectedRadiusMm <= 0) {
      const point = getMappedContourPointAtSourceDistance(
        display.points,
        display.distancesMm,
        sourceDistanceMm,
        display.perimeterMm
      );
      return point ? [point] : [];
    }
    const backwardArc = getDirectionalContourArcThroughFixedVertex(
      sourcePoints,
      location,
      -1,
      deformation.weights,
      this.pixelSpacingMm
    );
    const forwardArc = getDirectionalContourArcThroughFixedVertex(
      sourcePoints,
      location,
      1,
      deformation.weights,
      this.pixelSpacingMm
    );
    const backwardLengthMm = backwardArc.at(-1)?.distanceMm ?? 0;
    const forwardLengthMm = forwardArc.at(-1)?.distanceMm ?? 0;
    return getMappedContourRangePoints(
      display.points,
      display.distancesMm,
      sourceDistanceMm - backwardLengthMm,
      backwardLengthMm + forwardLengthMm,
      display.perimeterMm
    );
  }
  clearVertexDeformationBaseline() {
    const baseline = this.vertexDeformationBaseline;
    if (!baseline) {
      return;
    }
    this.delete(baseline);
    baseline.dispose();
    this.vertexDeformationBaseline = void 0;
  }
  keepVertexHandlesScreenSized(handles) {
    handles.forEach((handle) => this.setVertexHandleAppearance(
      handle,
      VERTEX_HANDLE_COLOR,
      VERTEX_HANDLE_SIZE
    ));
  }
  setVertexHandleAppearance(handle, color, size) {
    const parentWorldScale = this.object3d.getWorldScale(new Vector3());
    const scaleX = Math.abs(parentWorldScale.x) || 1;
    const scaleY = Math.abs(parentWorldScale.y) || 1;
    const resolvedColor = color instanceof Color ? color : new Color(color);
    handle.object3d.scale.set(size / scaleX, size / scaleY, 1);
    handle.object3d.material.uniforms.color.value.set(
      resolvedColor.r,
      resolvedColor.g,
      resolvedColor.b,
      1
    );
  }
  previewVertexDeformation(handle) {
    const selectedIndex = this.getVertexHandles().indexOf(handle);
    const activeDeformation = this.vertexDeformation?.driver === handle ? this.vertexDeformation : void 0;
    const location = activeDeformation?.location ?? (handle === this.contourDragHandle ? this.contourDragLocation : selectedIndex >= 0 ? { interpolation: 0, segmentStartIndex: selectedIndex } : void 0);
    if (!location) {
      return;
    }
    this.vertexDeformationPreviewHandle = handle;
    const points = this.getEditablePointReferences();
    const referencePoints = activeDeformation?.initialPoints ?? points;
    const contourDistancesMm = getClosedContourArcDistancesFromLocationMm(
      referencePoints,
      location,
      this.pixelSpacingMm,
      this.vertexDeformationRadii.affectedRadiusMm
    );
    this.getVertexHandles().forEach((vertexHandle, index) => {
      const influence = getContourVertexInfluence(
        contourDistancesMm[index],
        this.vertexDeformationRadii
      );
      if (influence.region === "move") {
        this.setVertexHandleAppearance(
          vertexHandle,
          MASK_DEFORMATION_MOVE_COLOR,
          VERTEX_HANDLE_INFLUENCED_SIZE
        );
        return;
      }
      if (influence.region === "affected") {
        this.setVertexHandleAppearance(
          vertexHandle,
          MASK_DEFORMATION_AFFECTED_COLOR,
          VERTEX_HANDLE_SIZE + (VERTEX_HANDLE_INFLUENCED_SIZE - VERTEX_HANDLE_SIZE) * influence.strength
        );
        return;
      }
      this.setVertexHandleAppearance(
        vertexHandle,
        VERTEX_HANDLE_COLOR,
        VERTEX_HANDLE_SIZE
      );
    });
    if (this.vertexHandlesVisible) {
      this.updateContourDeformationPreviewLines([]);
      this.updateContourDeformationPreviewMarkers([]);
      return;
    }
    this.showContourDeformationPreview(
      referencePoints,
      location,
      activeDeformation?.weights
    );
  }
  showContourDeformationPreview(referencePoints, location, activeWeights) {
    const coordinates = getContourSourceCoordinates(
      referencePoints,
      this.pixelSpacingMm
    );
    const perimeterMm = coordinates.perimeterMm;
    const affectedRadiusMm = Math.min(
      this.vertexDeformationRadii.affectedRadiusMm,
      perimeterMm / 2
    );
    const moveRadiusMm = Math.min(
      this.vertexDeformationRadii.normalMovementRadiusMm,
      affectedRadiusMm
    );
    if (affectedRadiusMm <= 0) {
      this.updateContourDeformationPreviewLines([]);
      this.updateContourDeformationPreviewMarkers([]);
      return;
    }
    const baseWidth = Math.max(1, this.object3d.material.linewidth ?? 1);
    const moveWidth = baseWidth + DEFORMATION_PREVIEW_RED_WIDTH_INCREASE;
    const previewLines = [];
    const previewMarkers = [];
    const sourceDistanceMm = getContourLocationSourceDistance(
      referencePoints,
      location,
      coordinates,
      this.pixelSpacingMm
    );
    const displayPoints = this.getDisplayedContourPointReferences();
    const moveCenter = getMappedContourPointAtSourceDistance(
      displayPoints,
      this.displayedContourSourceDistancesMm,
      sourceDistanceMm,
      perimeterMm
    );
    if (moveCenter) {
      previewMarkers.push({
        color: MASK_DEFORMATION_MOVE_COLOR,
        point: moveCenter
      });
    }
    const backwardMovePoints = getMappedContourRangePoints(
      displayPoints,
      this.displayedContourSourceDistancesMm,
      sourceDistanceMm - moveRadiusMm,
      moveRadiusMm,
      perimeterMm
    );
    const forwardMovePoints = getMappedContourRangePoints(
      displayPoints,
      this.displayedContourSourceDistancesMm,
      sourceDistanceMm,
      moveRadiusMm,
      perimeterMm
    );
    const movePoints = [
      ...backwardMovePoints,
      ...forwardMovePoints.slice(1)
    ];
    if (movePoints.length >= 2) {
      previewLines.push({
        color: MASK_DEFORMATION_MOVE_COLOR,
        points: movePoints,
        width: moveWidth
      });
    }
    const transitionLengthMm = affectedRadiusMm - moveRadiusMm;
    if (transitionLengthMm > 0) {
      const affectedLengthsMm = activeWeights ? [-1, 1].map((direction) => getDirectionalContourArcThroughFixedVertex(
        referencePoints,
        location,
        direction,
        activeWeights,
        this.pixelSpacingMm
      ).at(-1)?.distanceMm ?? 0) : [affectedRadiusMm, affectedRadiusMm];
      const affectedRanges = [
        {
          lengthMm: affectedLengthsMm[0] - moveRadiusMm,
          reverse: true,
          startDistanceMm: sourceDistanceMm - affectedLengthsMm[0]
        },
        {
          lengthMm: affectedLengthsMm[1] - moveRadiusMm,
          reverse: false,
          startDistanceMm: sourceDistanceMm + moveRadiusMm
        }
      ];
      affectedRanges.forEach(({ lengthMm, reverse, startDistanceMm }) => {
        const rangePoints = getMappedContourRangePoints(
          displayPoints,
          this.displayedContourSourceDistancesMm,
          startDistanceMm,
          lengthMm,
          perimeterMm
        );
        const affectedPoints = reverse ? rangePoints.reverse() : rangePoints;
        if (affectedPoints.length >= 2) {
          previewLines.push({
            color: MASK_DEFORMATION_AFFECTED_COLOR,
            points: affectedPoints,
            width: moveWidth
          });
        }
        const rangeEnd = affectedPoints.at(-1);
        if (rangeEnd) {
          previewMarkers.push({
            color: MASK_DEFORMATION_AFFECTED_COLOR,
            point: rangeEnd
          });
        }
      });
    }
    this.updateContourDeformationPreviewLines(previewLines);
    this.updateContourDeformationPreviewMarkers(previewMarkers);
  }
  updateContourDeformationPreviewLines(previewLines) {
    previewLines.forEach((preview, index) => {
      let line = this.contourDeformationPreviewLines[index];
      if (!line) {
        line = new Polyline({
          color: preview.color,
          opacity: 1,
          points: preview.points,
          renderOrder: DEFORMATION_PREVIEW_RENDER_ORDER,
          width: preview.width
        }, Line2);
        line.object3d.material.depthTest = false;
        line.object3d.material.depthWrite = false;
        line.object3d.material.transparent = true;
        line.object3d.layers.mask = this.object3d.layers.mask;
        line.update();
        this.add(line);
        this.contourDeformationPreviewLines.push(line);
      } else {
        line.setPoints(preview.points);
        line.object3d.material.color.set(preview.color);
        line.object3d.material.linewidth = preview.width;
      }
      line.object3d.visible = true;
    });
    this.contourDeformationPreviewLines.slice(previewLines.length).forEach(
      (line) => line.object3d.visible = false
    );
  }
  updateContourDeformationPreviewMarkers(previewMarkers) {
    previewMarkers.forEach((preview, index) => {
      let marker = this.contourDeformationPreviewMarkers[index];
      if (!marker) {
        marker = new RoundHandle(preview.point.clone(), {
          color: preview.color,
          maxZoom: Infinity,
          size: DEFORMATION_PREVIEW_MARKER_SIZE
        });
        marker.object3d.material.transparent = true;
        marker.object3d.renderOrder = DEFORMATION_PREVIEW_MARKER_RENDER_ORDER;
        marker.object3d.layers.mask = this.object3d.layers.mask;
        this.add(marker);
        this.contourDeformationPreviewMarkers.push(marker);
      }
      const color = new Color(preview.color);
      marker.point.copy(preview.point);
      marker.update();
      marker.object3d.material.uniforms.color.value.set(color.r, color.g, color.b, 1);
      marker.object3d.material.uniforms.outlineColor.value.set(
        color.r,
        color.g,
        color.b,
        1
      );
      this.setVertexHandleAppearance(
        marker,
        color,
        DEFORMATION_PREVIEW_MARKER_SIZE
      );
      marker.object3d.visible = true;
    });
    this.contourDeformationPreviewMarkers.slice(previewMarkers.length).forEach(
      (marker) => marker.object3d.visible = false
    );
  }
  disposeContourDeformationPreview() {
    this.contourDeformationPreviewLines.forEach((line) => {
      this.delete(line);
      line.dispose();
    });
    this.contourDeformationPreviewLines.length = 0;
    this.contourDeformationPreviewMarkers.forEach((marker) => {
      this.delete(marker);
      marker.dispose();
    });
    this.contourDeformationPreviewMarkers.length = 0;
  }
  stopPreviewingVertexDeformation(handle) {
    if (this.vertexDeformation?.driver === handle) {
      return;
    }
    if (this.vertexDeformationPreviewHandle === handle) {
      this.clearVertexDeformationPreview();
    }
  }
  previewDeformationForEntity(entity) {
    const handle = entity instanceof RoundHandle ? entity : void 0;
    if (handle && (handle === this.contourDragHandle || this.getVertexHandles().includes(handle))) {
      this.previewVertexDeformation(handle);
      return true;
    }
    if (!this.vertexDeformation && this.vertexDeformationPreviewHandle) {
      this.clearVertexDeformationPreview();
      return true;
    }
    return false;
  }
  updateAfterVertexTransform(index) {
    const handle = this.vertexHandles?.[index];
    if (handle && this.updateFromDeformationDriver(handle)) {
      return;
    }
    if (!this.controlPoints && index === 0) {
      this.points.at(-1)?.copy(this.points[0]);
    }
    this.updateDisplayedContour();
  }
  updateFromDeformationDriver(handle) {
    const deformation = this.vertexDeformation;
    if (deformation?.driver !== handle) {
      return false;
    }
    const editablePoints = this.getEditablePointReferences();
    const dragDelta = handle.object3d.position.clone().sub(
      deformation.driverInitialPoint
    );
    editablePoints.forEach((point, pointIndex) => {
      point.copy(deformation.initialPoints[pointIndex]).addScaledVector(dragDelta, deformation.weights[pointIndex]);
    });
    this.vertexHandles?.forEach((vertexHandle) => vertexHandle.update());
    if (!this.controlPoints) {
      this.points.at(-1)?.copy(this.points[0]);
    }
    this.updateDisplayedContour();
    this.previewVertexDeformation(handle);
    return true;
  }
  getEditablePointReferences() {
    return this.controlPoints ?? this.points.slice(0, -1);
  }
  getDisplayedContourPointReferences() {
    return this.points.slice(0, -1);
  }
  getEditableLocationForDisplayedLocation(displayedLocation) {
    if (!this.isSmoothedContourDisplayed()) {
      return displayedLocation;
    }
    const displayedPointCount = this.displayedContourSourceDistancesMm.length;
    const editablePoints = this.getEditablePointReferences();
    if (!displayedPointCount || !editablePoints.length || this.displayedContourSourcePerimeterMm <= 0) {
      return displayedLocation;
    }
    const segmentStartIndex = (displayedLocation.segmentStartIndex % displayedPointCount + displayedPointCount) % displayedPointCount;
    const nextIndex = (segmentStartIndex + 1) % displayedPointCount;
    const segmentSourceLengthMm = getForwardContourDistance(
      this.displayedContourSourceDistancesMm[segmentStartIndex],
      this.displayedContourSourceDistancesMm[nextIndex],
      this.displayedContourSourcePerimeterMm
    );
    const sourceDistanceMm = normalizeContourDistance(
      this.displayedContourSourceDistancesMm[segmentStartIndex] + segmentSourceLengthMm * Math.min(
        1,
        Math.max(0, displayedLocation.interpolation)
      ),
      this.displayedContourSourcePerimeterMm
    );
    return getContourLocationAtSourceDistance(
      editablePoints,
      sourceDistanceMm,
      getContourSourceCoordinates(editablePoints, this.pixelSpacingMm)
    ) ?? displayedLocation;
  }
  isSmoothedContourDisplayed() {
    return !!this.controlPoints && this.smoothingIterations > 0 && (!this.isContourEditing || !this.vertexHandlesVisible);
  }
  updateDisplayedContour() {
    const editablePoints = this.getEditablePointReferences();
    const referencePoints = this.vertexDeformation?.initialPoints ?? editablePoints;
    const display = createDisplayContour(
      editablePoints,
      referencePoints,
      this.isSmoothedContourDisplayed() ? this.smoothingIterations : 0,
      this.pixelSpacingMm
    );
    this.displayedContourSourceDistancesMm = display.distancesMm;
    this.displayedContourSourcePerimeterMm = display.perimeterMm;
    if (this.controlPoints) {
      this.updateContour(display.points);
    } else {
      this.update();
    }
  }
  updateContour(contourPoints) {
    const z = this.controlPoints?.[0]?.z ?? 0;
    this.points = contourPoints.map((point) => new Vector3(point.x, point.y, point.z ?? z));
    if (this.points.length) {
      this.points.push(this.points[0].clone());
    }
    this.update();
  }
  update() {
    super.update();
    this.updateLineDistances();
    this.updateEditContourOverlay();
  }
  updateEditContourOverlay() {
    const overlay = this.editContourOverlay;
    if (!overlay?.object3d.visible) {
      return;
    }
    overlay.setPoints(this.points.map((point) => point.clone()));
    overlay.object3d.material.color.copy(this.object3d.material.color);
    overlay.object3d.material.linewidth = this.editContourOverlayWidth;
  }
  noBlending() {
    this.fill.material.blending = CustomBlending;
    this.fill.material.blendEquation = AddEquation;
    this.fill.material.blendSrc = SrcAlphaFactor;
    this.fill.material.blendDst = ZeroFactor;
  }
  alphaBlending() {
    this.fill.material.blending = CustomBlending;
    this.fill.material.blendEquation = AddEquation;
    this.fill.material.blendSrc = SrcAlphaFactor;
    this.fill.material.blendDst = OneMinusSrcAlphaFactor;
  }
}

class BoxMaskHighlighter extends Highlighter {
  constructor(entity) {
    super(entity);
    if (!(entity instanceof BoxMask)) {
      throw new Error("Mask-only component");
    }
  }
  highlight() {
    this.entity.object3d.scale.set(1.1, 1.1, 1);
  }
  unHighlight() {
    this.entity.object3d.scale.set(1, 1, 1);
  }
}

var vertexShader$3 = "#define GLSLIFY 1\nuniform vec2 size;uniform vec4 viewport;uniform mat4 modelViewMatrix;uniform mat4 projectionMatrix;attribute vec3 position;attribute vec2 uv;varying vec2 coords;varying float scale;void main(){float objectScale=max(length((modelViewMatrix*vec4(0.0,1.0,0.0,0.0)).xyz),0.000001);scale=objectScale*(projectionMatrix*vec4(0.0,1.0,0.0,1.0)).y*viewport.w;coords=uv*vec2(size.xy);gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0);}"; // eslint-disable-line

var fragmentShader$3 = "precision highp float;\n#define GLSLIFY 1\nuniform vec4 color;uniform float radius;uniform vec2 size;uniform float lineWidth;varying vec2 coords;varying float scale;float roundedBoxSDF(vec2 CenterPosition,vec2 Size,float Radius){return length(max(abs(CenterPosition)-Size+Radius,0.0))-Radius;}void main(){float thickness=lineWidth/scale;vec2 size2=size.xy-thickness*2.0;vec2 location=size.xy/2.0;float edgeSoftness=1.0/scale;float radius2=min(radius/scale,min(size.x,size.y)/3.0);float distance=roundedBoxSDF(location-coords.xy,size2/2.0,radius2);float smoothedAlpha=1.0-smoothstep(-edgeSoftness,edgeSoftness,abs(distance)-thickness);gl_FragColor=mix(vec4(1.0,1.0,1.0,0.0),vec4(color.xyz,smoothedAlpha),smoothedAlpha);}"; // eslint-disable-line

class RoundCornerBBoxMaterial extends RawShaderMaterial {
  constructor(size, radius, color, lineWidth) {
    color = new Color(color);
    super({
      vertexShader: vertexShader$3,
      fragmentShader: fragmentShader$3,
      side: DoubleSide,
      uniforms: {
        lineWidth: { value: lineWidth },
        size: { value: size },
        radius: { value: radius },
        viewport: { value: new Vector4() },
        color: { value: new Vector4(color.r, color.g, color.b, 1) }
      }
    });
    this.toneMapped = false;
  }
  setSize(width, height) {
    this.uniforms.size.value.set(width, height);
  }
}

class RoundCornerBBox extends Primitive {
  constructor(min, max, viewport) {
    let color;
    if (min instanceof Vector3) {
      super(new Mesh(), viewport);
      this.min = min;
      this.max = max;
      color = new Color(16777215);
      this.borderWidth = 2;
      this.borderRadius = 20;
    } else {
      super(new Mesh(), max);
      this.min = new Vector3(min.min.x, min.min.y, min.min.z);
      this.max = new Vector3(min.max.x, min.max.y, min.max.z);
      color = new Color(min.color || 16777215);
      this.borderWidth = min.width ?? 2;
      this.borderRadius = min.borderRadius ?? 20;
    }
    this.viewport = new Vector4();
    this.object3d.material = new RoundCornerBBoxMaterial(new Vector2(), this.borderRadius, color, this.borderWidth);
    this.object3d.material.uniforms.viewport.value = this.viewport;
    this.object3d.material.depthTest = false;
    this.object3d.material.depthWrite = false;
    this.onBeforeRender = this.onBeforeRender.bind(this);
    this.onBeforeRenderSet.add(this.onBeforeRender);
    this.object3d.renderOrder = 20;
    this.object3d.material.transparent = true;
    this.update();
  }
  getViewportSize() {
    return this.getViewport()?.size;
  }
  update() {
    const size = this.max.clone().sub(this.min);
    const center = this.max.clone().add(this.min).divideScalar(2);
    this.object3d.position.set(center.x, center.y, center.z);
    this.object3d.geometry = new PlaneGeometry(Math.abs(size.x), Math.abs(size.y));
    this.object3d.geometry.getAttribute("position").needsUpdate = true;
    this.object3d.material.setSize(Math.abs(size.x), Math.abs(size.y));
  }
  onBeforeRender(renderer) {
    renderer.getViewport(this.viewport);
  }
  getPoints() {
    return this.points;
  }
  toJson() {
    throw new Error("Method not implemented.");
  }
}

class BoxMask extends RoundCornerBBox {
  constructor(props, viewport) {
    super(props, viewport);
    this.id = props.id;
    this.meta = props.meta;
    this.setComponents(props.hover);
  }
  setComponents(hover) {
    if (hover === "fill") {
      this.setComponent(MaskRaycastComponent);
      this.setComponent(HoverComponent, new HoverComponent(this, new BoxMaskHighlighter(this)));
    }
  }
}

class HoverMask extends ViewTool {
  constructor(view) {
    super(view);
    this.onHover = this.onHover.bind(this);
    this.onUnhover = this.onUnhover.bind(this);
  }
  activate() {
    this.hostObject.addEventListener("hover", this.onHover);
    this.hostObject.addEventListener("unhover", this.onUnhover);
  }
  deactivate() {
    this.hostObject.removeEventListener("hover", this.onHover);
    this.hostObject.removeEventListener("unhover", this.onUnhover);
  }
  onHover(event) {
    if (event.entity instanceof Mask || event.entity instanceof BoxMask) {
      this.hostObject.dispatchEvent({ type: "hoverMask", localizationID: event.entity.id, meta: event.entity.meta });
    }
  }
  onUnhover(event) {
    if (event.entity instanceof Mask) {
      this.hostObject.dispatchEvent({ type: "hoverMask", localizationID: void 0 });
    }
  }
}

class PBL extends Line {
  constructor(props, viewport) {
    super(props, viewport, true);
    this.vector = new Vector3();
    this.xVector = new Vector3(1, 0, 0);
    this.offset = 0;
    this.worldScale = new Vector3(1, 1, 1);
    this.endpointsVisible = true;
    this.scale = props.scale ?? 1;
    this.startHandleBaseSize = this.startHandle.size;
    this.endHandleBaseSize = this.endHandle.size;
    this.text = typeof document !== "undefined" ? new TextEntity({
      text: this.getMeasure().toFixed(2),
      fontSize: 18,
      fontWeight: "bold",
      anchorY: "middle",
      anchorX: "center",
      outlineColor: props.textProps?.outlineColor,
      outlineWidth: props.textProps?.outlineWidth,
      outlineOpacity: 1,
      outlineBlur: 0,
      color: props.textProps?.color
    }) : void 0;
    if (this.text) {
      this.add(this.text);
      this.text.object3d.frustumCulled = false;
    }
    this.setRenderOrders();
    this.setComponents();
    this.setBehavior();
    this.makeTransparent();
    this.add([this.startHandle, this.endHandle]);
    this.update();
  }
  dispose() {
    this.text?.dispose();
    super.dispose();
  }
  setRenderOrders() {
    super.setRenderOrders();
    if (this.text) this.text.object3d.renderOrder = 12;
  }
  setBehavior() {
    super.setBehavior();
    this.onBeforeRenderSet.add(() => this.updateEndpointSizes());
    if (!this.text) return;
    const matrix = new Matrix4();
    const rotationMatrix = new Matrix4();
    const tempVector = new Vector3();
    const viewport = new Vector4();
    this.text.object3d.matrixAutoUpdate = false;
    const hideZoom = 0.2 / this.scale;
    const minZoom = 0.05 / this.scale;
    this.onBeforeRenderSet.add((...args) => {
      if (!this.text) return;
      const renderer = args[0];
      const camera = args[2];
      renderer.getViewport(viewport);
      let zoom = 2 / (camera.projectionMatrix.elements[5] * viewport.w);
      if (zoom > hideZoom) {
        this.text.object3d.visible = false;
        return;
      }
      this.text.object3d.visible = true;
      const { width, height } = this.text.getSize();
      const semiMajorAxis = (width + 10) / 2;
      const semiMinorAxis = (height + 2) / 2;
      const containerScale = this.getContainerScale();
      zoom = Math.min(zoom, minZoom);
      this.text.object3d.scale.set(zoom / containerScale, zoom / containerScale, 1);
      tempVector.copy(this.start);
      this.object3d.localToWorld(tempVector);
      const worldStart = tempVector.clone();
      tempVector.copy(this.end);
      this.object3d.localToWorld(tempVector);
      const worldEnd = tempVector.clone();
      const center = new Vector3().addVectors(worldStart, worldEnd).multiplyScalar(0.5);
      const dir = new Vector3().subVectors(worldEnd, worldStart).normalize();
      const perpendicular = new Vector3(-dir.y, dir.x, 0);
      if (perpendicular.y < 0) {
        perpendicular.multiplyScalar(-1);
      }
      this.object3d.matrixWorld.extractRotation(rotationMatrix);
      const angle = Math.atan2(perpendicular.y, perpendicular.x);
      const cosAngle = Math.cos(angle);
      const sinAngle = Math.sin(angle);
      const distance = Math.sqrt(
        semiMajorAxis * semiMajorAxis * cosAngle * cosAngle + semiMinorAxis * semiMinorAxis * sinAngle * sinAngle
      ) * zoom;
      center.add(perpendicular.multiplyScalar(distance));
      this.object3d.worldToLocal(center);
      this.text.object3d.position.copy(center);
      this.text.object3d.updateMatrix();
      this.text.object3d.updateMatrixWorld(true);
      matrix.extractRotation(this.text.object3d.matrixWorld).invert();
      this.text.object3d.matrixWorld.multiply(matrix);
    });
  }
  update() {
    super.update();
    const text = this.getMeasure().toFixed(1).toString();
    if (this.text && this.text.object3d.text !== text) {
      this.text.setProps({ text });
    }
  }
  getMeasure() {
    return this.end.distanceTo(this.start) * this.scale;
  }
  getContainerScale() {
    this.object3d.getWorldScale(this.worldScale);
    const scale = Math.abs(this.worldScale.x);
    return scale > 0 ? scale : 1;
  }
  updateEndpointSizes() {
    if (!this.endpointsVisible) return;
    const containerScale = this.getContainerScale();
    const getHoverFactor = (handle) => {
      const hoverComponent = handle.getComponent(HoverComponent);
      if (hoverComponent?.isHovered() && hoverComponent.highlighter instanceof IncreaseSizeHighlighter) {
        return hoverComponent.highlighter.increaseFactor;
      }
      return 1;
    };
    this.startHandle.size = this.startHandleBaseSize * getHoverFactor(this.startHandle) / containerScale;
    this.endHandle.size = this.endHandleBaseSize * getHoverFactor(this.endHandle) / containerScale;
  }
  showText() {
    this.text && this.add([this.text]);
  }
  hideText() {
    this.text && this.delete([this.text]);
  }
  showEndpoints() {
    this.endpointsVisible = true;
    this.updateEndpointSizes();
  }
  hideEndpoints() {
    this.endpointsVisible = false;
    this.startHandle.size = this.object3d.material.linewidth;
    this.endHandle.size = this.object3d.material.linewidth;
  }
}

class PBLVisibilityTool extends ViewTool {
  constructor(view) {
    super(view);
    this.onChangeLayout = this.onChangeLayout.bind(this);
    this.onAdd = this.onAdd.bind(this);
  }
  activate() {
    const mode = this.hostObject.layoutController.getMode();
    const imageNumber = this.hostObject.imageController.imageContainers.size;
    if (imageNumber > 1 && mode === "matrix" || mode === "fullScreenMatrix") {
      this.hide();
    } else {
      this.show();
    }
    this.hostObject.addEventListener("layout", this.onChangeLayout);
    this.hostObject.addEventListener("add", this.onAdd);
  }
  deactivate() {
    this.hostObject.removeEventListener("layout", this.onChangeLayout);
    this.hostObject.removeEventListener("add", this.onAdd);
  }
  onAdd(event) {
    const imageNumber = this.hostObject.imageController.imageContainers.size;
    const mode = this.hostObject.layoutController.getMode();
    if (!(imageNumber > 1 && mode === "matrix") && mode !== "fullScreenMatrix") {
      return;
    }
    event.entities.forEach((entity) => {
      entity.traverse((child) => {
        if (child instanceof PBL) {
          child.hideEndpoints();
          child.hideText();
        }
      });
    });
  }
  onChangeLayout(event) {
    const imageNumber = this.hostObject.imageController.imageContainers.size;
    if (imageNumber > 1 && event.mode === "matrix" || event.mode === "fullScreenMatrix") {
      this.hide();
    } else {
      this.show();
    }
  }
  hide() {
    this.hostObject.imageController.imageContainers.forEach((container) => {
      [...container.getPBLs()].forEach((pbl) => {
        pbl.hideEndpoints();
        pbl.hideText();
      });
    });
  }
  show() {
    this.hostObject.imageController.imageContainers.forEach((container) => {
      [...container.getPBLs()].forEach((pbl) => {
        pbl.showEndpoints();
        pbl.showText();
      });
    });
  }
}

class InvertColor extends ViewTool {
  constructor(view) {
    super(view);
    this.start = new Vector2();
    this.delta = new Vector2();
    this.invertColors = this.invertColors.bind(this);
    this.straightColors = this.straightColors.bind(this);
    this.toggleInversion = this.toggleInversion.bind(this);
  }
  activate() {
    this.hostObject.addEventListener("invertColors", this.invertColors);
    this.hostObject.addEventListener("straightColors", this.straightColors);
    this.hostObject.addEventListener("toggleInversion", this.toggleInversion);
  }
  deactivate() {
    this.hostObject.removeEventListener("invertColors", this.invertColors);
    this.hostObject.removeEventListener("straightColors", this.straightColors);
    this.hostObject.removeEventListener("toggleInversion", this.toggleInversion);
  }
  invertColors() {
    this.hostObject.imageController.imageContainers.forEach((container) => {
      const inversion = container.getImage().getInversion();
      if (inversion === "straight") {
        container.getImage().invertColor();
        this.hostObject.imageController.updatePictures(container.id);
      }
    });
    this.hostObject.dispatchEvent({ type: "render" });
  }
  straightColors() {
    this.hostObject.imageController.imageContainers.forEach((container) => {
      const inversion = container.getImage().getInversion();
      if (inversion === "invert") {
        container.getImage().straightColor();
        this.hostObject.imageController.updatePictures(container.id);
      }
    });
    this.hostObject.dispatchEvent({ type: "render" });
  }
  toggleInversion() {
    this.hostObject.imageController.imageContainers.forEach((container) => {
      container.getImage().toggleInversion();
      this.hostObject.imageController.updatePictures(container.id);
    });
    this.hostObject.dispatchEvent({ type: "render" });
  }
}

class Zoom2D extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.zoomSpeed = Math.pow(0.95, 0.4);
    this.maxZoom = Infinity;
    this.minZoom = 0;
    this.vector = new Vector3();
    this.handleMouseWheel = this.handleMouseWheel.bind(this);
    this.updateZoomType = this.updateZoomType.bind(this);
  }
  activate() {
    App.Instance().mouseController.addListener("wheel", this.handleMouseWheel, this.hostObject.getDomElement());
    this.hostObject.addEventListener("cameraChanged", this.updateZoomType);
    this.updateZoomType();
  }
  deactivate() {
    App.Instance().mouseController.removeListener("wheel", this.handleMouseWheel, this.hostObject.getDomElement());
    this.hostObject.removeEventListener("cameraChanged", this.updateZoomType);
  }
  get camera() {
    return this.hostObject.camera;
  }
  handleMouseWheel(event) {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoom(this.zoomSpeed);
    } else if (event.deltaY > 0) {
      this.zoom(1 / this.zoomSpeed);
    }
    this.view.dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
    this.hostObject.dispatchEvent({ type: "zoomChanged", viewport: this.hostObject });
  }
  updateZoomType() {
    if (this.camera instanceof OrthographicCamera) {
      this.zoom = this.zoomForOrthographicCamera;
    } else {
      this.zoom = this.zoomForPerspectiveCamera;
    }
  }
  zoomForOrthographicCamera(scale) {
    this.camera.zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom / scale));
    this.camera.updateProjectionMatrix();
  }
  zoomForPerspectiveCamera(scale) {
    const pp = this.hostObject.cameraController.getPivotPoint();
    this.vector.copy(this.camera.position);
    this.camera.position.copy(pp).add(this.vector.sub(pp).multiplyScalar(scale));
    this.hostObject.cameraController.orthographicCameraHelper.updateDistanceFactor();
  }
}

class Zoom2DOnCursor extends Zoom2D {
  constructor() {
    super(...arguments);
    this.zoomForOrthographicCamera = (() => {
      const vector = new Vector3();
      return (scale) => {
        const cameraController = this.hostObject.cameraController;
        const zoom = Math.max(this.minZoom, Math.min(this.maxZoom, this.camera.zoom / scale));
        scale = this.camera.zoom / zoom;
        const width = this.camera.right - this.camera.left;
        const height = this.camera.top - this.camera.bottom;
        const position = this.view.cursorController.position;
        const deltaFactor = (1 - scale) / this.camera.zoom / 2;
        const xDelta = position.x * width * deltaFactor;
        const yDelta = position.y * height * deltaFactor;
        this.camera.position.add(vector.copy(cameraController.xDir).multiplyScalar(xDelta)).add(vector.copy(cameraController.yDir).multiplyScalar(yDelta));
        this.camera.zoom = zoom;
        this.camera.updateProjectionMatrix();
      };
    })();
  }
  get camera() {
    return this.hostObject.cameraController.getOrthographicCamera();
  }
}

class Pan2D extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.panStart = new Vector2();
    this.panEnd = new Vector2();
    this.panDelta = new Vector2();
    this.panOffset = new Vector3();
    this.vector = new Vector3();
    this.handleLeftMouseButtonDown = this.handleLeftMouseButtonDown.bind(this);
    this.handleMiddleMouseButtonDown = this.handleMiddleMouseButtonDown.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseMoveOnce = this.handleMouseMoveOnce.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.updatePanType = this.updatePanType.bind(this);
  }
  get cameraController() {
    return this.hostObject.cameraController;
  }
  get camera() {
    return this.hostObject.camera;
  }
  get cameraX() {
    return this.hostObject.cameraController.xDir;
  }
  get cameraY() {
    return this.hostObject.cameraController.yDir;
  }
  get distanceFactor() {
    return this.hostObject.cameraController.getDistanceFactor();
  }
  activate() {
    App.Instance().mouseController.addListener("pointerdown", this.handleLeftMouseButtonDown, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.addListener("pointerdown", this.handleMiddleMouseButtonDown, this.hostObject.getDomElement(), "middle");
    this.updatePanType();
    this.hostObject.addEventListener("cameraChanged", this.updatePanType);
  }
  deactivate() {
    App.Instance().mouseController.removeListener("pointerdown", this.handleLeftMouseButtonDown, this.hostObject.getDomElement(), "left");
    App.Instance().mouseController.removeListener("pointerdown", this.handleMiddleMouseButtonDown, this.hostObject.getDomElement(), "middle");
    App.Instance().mouseController.removeListener("pointermove", this.handleMouseMoveOnce, document);
    this.hostObject?.removeEventListener("cameraChanged", this.updatePanType);
  }
  updatePanType() {
    if (this.camera instanceof OrthographicCamera) {
      this.panType = this.panForOrthographicCamera;
    } else {
      this.panType = this.panForPerspectiveCamera;
    }
  }
  pan(deltaX, deltaY) {
    this.panType(deltaX, deltaY);
    this.hostObject.camera.position.add(this.panOffset);
    this.panOffset.set(0, 0, 0);
    this.hostObject.view.dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
  }
  panForOrthographicCamera(deltaX, deltaY) {
    const sizeRatio = this.cameraController.orthographicCameraHelper.getSizeRatio();
    this.panOffset.copy(this.cameraX).multiplyScalar(-(deltaX * sizeRatio) / this.camera.zoom);
    this.panOffset.add(this.vector.copy(this.cameraY).multiplyScalar(deltaY * sizeRatio / this.camera.zoom));
  }
  panForPerspectiveCamera(deltaX, deltaY) {
    this.cameraController.perspectiveCameraHelper.updateDistanceFactor();
    this.panOffset.copy(this.cameraX).multiplyScalar(-deltaX * this.distanceFactor);
    this.panOffset.add(this.vector.copy(this.cameraY).multiplyScalar(deltaY * this.distanceFactor));
  }
  handleMouseMoveOnce(event) {
    this.panStart.set(event.clientX, event.clientY);
    App.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App.Instance().mouseController.removeListener("pointermove", this.handleMouseMoveOnce, document);
  }
  handleMouseDown(event) {
    event.preventDefault();
    App.Instance().mouseController.addListener("pointermove", this.handleMouseMove, document);
    App.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "left");
    App.Instance().mouseController.addListener("pointerup", this.handleMouseUp, document, "middle");
    this.panStart.set(event.clientX, event.clientY);
    this.hostObject.view.dispatchEvent({ type: "transformCameraStart", viewport: this.hostObject });
  }
  handleLeftMouseButtonDown(event) {
    if (!event.shiftKey && this.enable) {
      this.handleMouseDown(event);
    }
  }
  handleMiddleMouseButtonDown(event) {
    if (!event.shiftKey && this.enable) {
      this.handleMouseDown(event);
    }
  }
  handleMouseMove(event) {
    this.panEnd.set(event.clientX, event.clientY);
    this.panDelta.subVectors(this.panEnd, this.panStart);
    this.pan(this.panDelta.x, this.panDelta.y);
    this.panStart.copy(this.panEnd);
  }
  handleMouseUp(event) {
    App.Instance().mouseController.removeListener("pointermove", this.handleMouseMove, document);
    App.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "left");
    App.Instance().mouseController.removeListener("pointerup", this.handleMouseUp, document, "middle");
    this.panStart.set(event.clientX, event.clientY);
    this.hostObject.view.dispatchEvent({ type: "transformCameraEnd", viewport: this.hostObject });
  }
}

class ImagePropsChangeInterceptor extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onAdd = this.onAdd.bind(this);
    this.onChangeContrastBrightness = this.onChangeContrastBrightness.bind(this);
    this.onChangeSharpness = this.onChangeSharpness.bind(this);
    this.onSetImage = this.onSetImage.bind(this);
  }
  get imageController() {
    return this.view.imageController;
  }
  activate() {
    const image = this.imageController.imageContainers.get(this.hostObject.name)?.getImage();
    if (image instanceof ImageEntity) {
      this.onSetImage({ image });
      this.uniform = image.object3d.material.uniforms;
      this.intercept();
    } else {
      this.hostObject.addEventListener("imageAdded", this.onAdd);
    }
    this.hostObject.addEventListener("imageAdded", this.onSetImage, 10);
    this.view.addEventListener("brightnessContrastChanged", this.onChangeContrastBrightness, 10);
    this.view.addEventListener("sharpnessChanged", this.onChangeSharpness, 10);
  }
  deactivate() {
    this.hostObject.removeEventListener("imageAdded", this.onAdd);
    this.hostObject.removeEventListener("imageAdded", this.onSetImage);
    this.view.removeEventListener("brightnessContrastChanged", this.onChangeContrastBrightness);
    this.view.removeEventListener("sharpnessChanged", this.onChangeSharpness);
    Observer.stopObserving(this.imageController.screenProps, "brightness");
    Observer.stopObserving(this.imageController.screenProps, "contrast");
    Observer.stopObserving(this.imageController.screenProps, "sharpness");
    const image = this.imageController.imageContainers.get(this.hostObject.name)?.getImage();
    if (image instanceof ImageEntity) {
      this.uniform.brightness.value -= this.imageController.screenProps.brightness;
      this.uniform.contrast.value -= this.imageController.screenProps.contrast;
      this.uniform.sharpness.value -= this.imageController.screenProps.sharpness;
      image.object3d.material.uniforms.brightness = { ...this.uniform.brightness };
      image.object3d.material.uniforms.contrast = { ...this.uniform.contrast };
      image.object3d.material.uniforms.sharpness = { ...this.uniform.sharpness };
    }
  }
  onAdd(event) {
    this.uniform = event.image.object3d.material.uniforms;
    this.intercept();
  }
  onChangeContrastBrightness(event) {
    if (event.id !== this.hostObject.name) return;
    event.brightness -= this.imageController.screenProps.brightness;
    event.contrast -= this.imageController.screenProps.contrast;
  }
  onChangeSharpness(event) {
    if (event.id !== this.hostObject.name) return;
    event.sharpness -= this.imageController.screenProps.sharpness;
  }
  onSetImage({ image }) {
    image.brightness += this.imageController.screenProps.brightness;
    image.contrast += this.imageController.screenProps.contrast;
    image.sharpness += this.imageController.screenProps.sharpness;
  }
  intercept() {
    this.uniform.brightness.screenPropsInterceptor = 0;
    this.uniform.contrast.screenPropsInterceptor = 0;
    this.uniform.sharpness.screenPropsInterceptor = 0;
    this.uniform.brightness = this.screenPropsAdditionBehavior(this.uniform.brightness, [-1, 1], "brightness");
    this.uniform.contrast = this.screenPropsAdditionBehavior(this.uniform.contrast, [-1, 1], "contrast");
    this.uniform.sharpness = this.screenPropsAdditionBehavior(this.uniform.sharpness, [-3, 3], "sharpness");
    Observer.observe(this.imageController.screenProps, "brightness", (val) => this.uniform.brightness.screenPropsInterceptor = val);
    Observer.observe(this.imageController.screenProps, "contrast", (val) => this.uniform.contrast.screenPropsInterceptor = val);
    Observer.observe(this.imageController.screenProps, "sharpness", (val) => this.uniform.sharpness.screenPropsInterceptor = val);
  }
  screenPropsAdditionBehavior(object, clampRange, screenProperty) {
    let valueStorage = object.value - this.imageController.screenProps[screenProperty];
    const set = (target, prop, value) => {
      if (prop === "value") {
        valueStorage = value - this.imageController.screenProps[screenProperty];
        return Reflect.set(target, prop, MathUtils.clamp(value, clampRange[0], clampRange[1]));
      } else if (prop === "screenPropsInterceptor") {
        return Reflect.set(target, "value", MathUtils.clamp(valueStorage + value, clampRange[0], clampRange[1]));
      }
      return false;
    };
    return new Proxy(object, { set });
  }
  useGlobalPropertyBehavior(object, clampRange, screenProperty) {
    let valueStorage = object.value;
    let globalValue = this.imageController.screenProps[screenProperty];
    const set = (target, prop, value) => {
      value = MathUtils.clamp(value, clampRange[0], clampRange[1]);
      if (prop === "value") {
        valueStorage = value;
        return Reflect.set(target, prop, MathUtils.clamp(value, clampRange[0], clampRange[1]));
      } else if (prop === "screenPropsInterceptor") {
        if (valueStorage === globalValue) {
          valueStorage = globalValue = value;
        }
        return Reflect.set(target, "value", valueStorage);
      }
      return false;
    };
    return new Proxy(object, { set });
  }
}

class FitCameraToImage extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.lookAtImage = this.lookAtImage.bind(this);
    this.setBox = this.setBox.bind(this);
  }
  async activate() {
    this.hostObject.addEventListener("lookAtBox", this.setBox);
    this.imageContainer = await this.view.getImageContainer(this.hostObject.name);
    this.lookAtImage();
    this.hostObject.addEventListener("resize", this.lookAtImage);
    this.hostObject.view.dispatchEvent({ type: "render" });
  }
  deactivate() {
    this.hostObject.removeEventListener("resize", this.lookAtImage);
    this.hostObject.removeEventListener("lookAtBox", this.setBox);
    this.disposers?.forEach((disposer) => disposer());
    this.box = void 0;
  }
  lookAtImage() {
    this.imageContainer.lookAtImage(this.hostObject.cameraController.getOrthographicCamera(), this.box);
  }
  setBox(event) {
    const box = event.box;
    if (box) {
      this.box = new Box3(new Vector3(box.min.x, box.min.y, 0), new Vector3(box.max.x, box.max.y, 0));
    } else {
      this.box = box;
    }
    this.lookAtImage();
  }
}

class ScreenContrastBrightnessCommand extends Command {
  constructor(initialBrightness, initialContrast, brightness, contrast) {
    super();
    this.initialBrightness = initialBrightness;
    this.initialContrast = initialContrast;
    this.brightness = brightness;
    this.contrast = contrast;
  }
  execute(view) {
    view.imageController.screenProps.brightness = this.brightness;
    view.imageController.screenProps.contrast = this.contrast;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "screenBrightnessContrastChanged", brightness: this.brightness, contrast: this.contrast });
    return true;
  }
  undo(view) {
    view.imageController.screenProps.brightness = this.initialBrightness;
    view.imageController.screenProps.contrast = this.initialContrast;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "screenBrightnessContrastChanged", brightness: this.initialBrightness, contrast: this.initialContrast });
    return true;
  }
}

class ImageContrastBrightnessMultiple extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.initialCursor = "";
    this.start = new Vector2();
    this.delta = new Vector2();
    this.speed = 1.5;
    this.mouseDown = this.mouseDown.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseUp = this.mouseUp.bind(this);
  }
  activate() {
    MIRender.mouseController.addListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left", 20);
    this.initialCursor = this.hostObject.getDomElement().style.cursor;
    this.hostObject.getDomElement().style.cursor = "all-scroll";
  }
  deactivate() {
    MIRender.mouseController.removeListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left");
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, this.hostObject.getDomElement());
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, this.hostObject.getDomElement(), "left");
    this.hostObject.getDomElement().style.cursor = this.initialCursor;
  }
  set brightness(brightness) {
    this.view.imageController.screenProps.brightness = brightness;
  }
  get brightness() {
    return this.view.imageController.screenProps.brightness;
  }
  set contrast(contrast) {
    this.view.imageController.screenProps.contrast = contrast;
  }
  get contrast() {
    return this.view.imageController.screenProps.contrast;
  }
  mouseDown(event) {
    if (event.shiftKey || event.ctrlKey || event.metaKey) return;
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    this.initialBrightness = this.brightness;
    this.initialContrast = this.contrast;
    MIRender.mouseController.addListener("pointermove", this.mouseMove, document, 9);
    MIRender.mouseController.addListener("pointerup", this.mouseUp, document, "left", 20);
  }
  mouseMove(event) {
    event.preventDefault();
    this.delta.x = event.clientX - this.start.x;
    this.delta.y = -(event.clientY - this.start.y);
    this.delta.multiplyScalar(this.speed);
    this.brightness = MathUtils.clamp(this.brightness + this.delta.y / this.hostObject.rect.height, -1, 1);
    this.contrast = MathUtils.clamp(this.contrast + this.delta.x / this.hostObject.rect.width, -1, 1);
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    this.view.dispatchEvent({ type: "render" });
  }
  mouseUp() {
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, document);
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, document, "left");
    const command = new ScreenContrastBrightnessCommand(this.initialBrightness, this.initialContrast, this.brightness, this.contrast);
    this.view.executeCommand(command, false);
    this.view.dispatchEvent({ type: "screenBrightnessContrastChanged", brightness: this.brightness, contrast: this.contrast });
  }
}

class ScreenSharpnessCommand extends Command {
  constructor(initialSharpness, sharpness) {
    super();
    this.initialSharpness = initialSharpness;
    this.sharpness = sharpness;
  }
  execute(view) {
    view.imageController.screenProps.sharpness = this.sharpness;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "screenSharpnessChanged", sharpness: this.sharpness });
    return true;
  }
  undo(view) {
    view.imageController.screenProps.sharpness = this.initialSharpness;
    view.dispatchEvent({ type: "render" });
    view.dispatchEvent({ type: "screenSharpnessChanged", sharpness: this.initialSharpness });
    return true;
  }
}

class ImageSharpnessMultiple extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.initialCursor = "";
    this.start = new Vector2();
    this.delta = new Vector2();
    this.speed = 5;
    this.mouseDown = this.mouseDown.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseUp = this.mouseUp.bind(this);
  }
  activate() {
    MIRender.mouseController.addListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left", 20);
    this.initialCursor = this.hostObject.getDomElement().style.cursor;
    this.hostObject.getDomElement().style.cursor = "all-scroll";
  }
  deactivate() {
    MIRender.mouseController.removeListener("pointerdown", this.mouseDown, this.hostObject.getDomElement(), "left");
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, this.hostObject.getDomElement());
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, this.hostObject.getDomElement(), "left");
    this.hostObject.getDomElement().style.cursor = this.initialCursor;
  }
  set sharpness(sharpness) {
    this.view.imageController.screenProps.sharpness = sharpness;
  }
  get sharpness() {
    return this.view.imageController.screenProps.sharpness;
  }
  mouseDown(event) {
    if (event.shiftKey || event.ctrlKey || event.metaKey) return;
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    this.initialSharpness = this.sharpness;
    MIRender.mouseController.addListener("pointermove", this.mouseMove, document, 9);
    MIRender.mouseController.addListener("pointerup", this.mouseUp, document, "left", 20);
  }
  mouseMove(event) {
    event.preventDefault();
    this.delta.x = event.clientX - this.start.x;
    this.delta.y = -(event.clientY - this.start.y);
    this.delta.multiplyScalar(this.speed);
    this.sharpness = MathUtils.clamp(this.sharpness + this.delta.y / this.hostObject.rect.height, 0, 3);
    this.start.x = event.clientX;
    this.start.y = event.clientY;
    this.view.dispatchEvent({ type: "render" });
  }
  mouseUp() {
    MIRender.mouseController.removeListener("pointermove", this.mouseMove, document);
    MIRender.mouseController.removeListener("pointerup", this.mouseUp, document, "left");
    const command = new ScreenSharpnessCommand(this.initialSharpness, this.sharpness);
    this.view.executeCommand(command, false);
    this.view.dispatchEvent({ type: "screenSharpnessChanged", sharpness: this.sharpness });
  }
}

class FocusByClick extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onClick = this.onClick.bind(this);
    this.onChangeLayout = this.onChangeLayout.bind(this);
  }
  activate() {
    if (this.view.layoutController.getMode() === "matrix") {
      MIRender.mouseController.addListener("click", this.onClick, this.hostObject.getDomElement());
    }
    this.view.addEventListener("layout", this.onChangeLayout);
  }
  deactivate() {
    MIRender.mouseController.removeListener("click", this.onClick, this.hostObject.getDomElement());
    this.view.removeEventListener("layout", this.onChangeLayout);
  }
  onClick() {
    if (MIRender.getView("main")?.imageController.imageContainers?.size !== 1) {
      this.view.layoutController.focus(this.hostObject.name);
    }
  }
  onChangeLayout(event) {
    if (event.mode === "matrix") {
      MIRender.mouseController.addListener("click", this.onClick, this.hostObject.getDomElement());
    } else {
      MIRender.mouseController.removeListener("click", this.onClick, this.hostObject.getDomElement());
    }
  }
}

var vertexShader$2 = "#version 300 es\n#define GLSLIFY 1\nuniform vec4 viewport;in vec3 position;in vec2 uv;out vec2 coords;void main(){coords=uv*vec2(viewport.zw);gl_Position=vec4(position.xy,-0.5f,1.0f);}"; // eslint-disable-line

var fragmentShader$2 = "#version 300 es\nprecision highp float;\n#define GLSLIFY 1\nuniform vec4 color;uniform float radius;uniform vec4 viewport;in vec2 coords;out vec4 out_color;float sdRoundedBox(in vec2 p,in vec2 b,in vec4 r){r.xy=(p.x>0.0f)? r.xy : r.zw;r.x=(p.y>0.0f)? r.x : r.y;vec2 q=abs(p)-b+r.x;return min(max(q.x,q.y),0.0f)+length(max(q,0.0f))-r.x;}void main(){vec2 p=(2.0f*coords-viewport.zw)/viewport.w;vec2 b=vec2(viewport.z/viewport.w,1.0f);float box=sdRoundedBox(p,b,vec4(2.0f*radius/viewport.w));if(box<=0.0f){discard;}out_color=color;}"; // eslint-disable-line

class RoundCornerMaterial extends RawShaderMaterial {
  constructor(viewport, radius, color) {
    color = new Color(color);
    super({
      vertexShader: vertexShader$2,
      fragmentShader: fragmentShader$2,
      uniforms: {
        viewport: { value: viewport },
        radius: { value: radius },
        color: { value: new Vector4(color.r, color.g, color.b, 1) }
      }
    });
    this.toneMapped = false;
  }
}

class RoundCornerViewportEntity extends Entity {
  constructor(viewport, radius, color) {
    super(new Mesh(new PlaneGeometry(2, 2), new RoundCornerMaterial(viewport.size, radius, color)));
    const viewportComponent = this.setComponent(ViewportComponent);
    viewportComponent.addViewport(viewport);
    this.object3d.renderOrder = 1e3;
    this.object3d.frustumCulled = false;
  }
  toJson() {
    throw new Error("Method not implemented.");
  }
}

const downloadImageDataAsPng = (imageData, fileName) => {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const context = canvas.getContext("2d");
  if (!context) return;
  context.putImageData(imageData, 0, 0);
  const link = document.createElement("a");
  link.href = canvas.toDataURL("image/png");
  link.download = `${fileName}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

class DownLoadImage extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.downLoadImage = this.downLoadImage.bind(this);
  }
  activate() {
    this.hostObject.addEventListener("downLoadImage", this.downLoadImage);
  }
  deactivate() {
    this.hostObject.removeEventListener("downLoadImage", this.downLoadImage);
  }
  async downLoadImage(event) {
    const imageContainer = this.view.imageController.imageContainers.get(this.hostObject.name);
    if (!imageContainer) return;
    const imageData = await toImageData(imageContainer, "main", { minWidth: 1800, minHeight: 1e3 });
    downloadImageDataAsPng(imageData, event.fileName || this.hostObject.name || "image");
  }
}

var vertexShader$1 = "#version 300 es\n#define GLSLIFY 1\nuniform mat4 projectionMatrix;uniform mat4 modelViewMatrix;uniform vec4 viewport;uniform vec3 size;uniform float maxZoom;in vec3 position;in vec2 uv;out vec2 coords;void main(){coords=uv*size.xy;float factor=viewport.w/600.0f;float zoom=1.0f/(projectionMatrix[1][1]*300.0f)/factor;zoom=min(zoom,maxZoom);mat4 mat;mat[0]=vec4(size.x,0.0,0.0,0.0);mat[1]=vec4(0.0,size.y,0.0,0.0);mat[2]=vec4(0.0,0.0,1,0.0);mat[3]=vec4(modelViewMatrix[3][0],modelViewMatrix[3][1],modelViewMatrix[3][2],modelViewMatrix[3][3]);vec4 mvPosition=projectionMatrix*mat*(vec4(position,1.0f)*vec4(zoom,zoom,1.0f,1.0f));gl_Position=mvPosition;}"; // eslint-disable-line

var fragmentShader$1 = "#version 300 es\nprecision highp float;\n#define GLSLIFY 1\nuniform vec4 color;uniform vec4 outlineColor;uniform float radius;uniform float outlineWidth;uniform float opacity;uniform vec3 size;in vec2 coords;out vec4 out_color;float sdRoundedBox(in vec2 p,in vec2 b,in vec4 r){r.xy=(p.x>0.0f)? r.xy : r.zw;r.x=(p.y>0.0f)? r.x : r.y;vec2 q=abs(p)-b+r.x;return min(max(q.x,q.y),0.0f)+length(max(q,0.0f))-r.x;}void main(){vec2 p=coords-size.xy*0.5f;vec2 b=size.xy*0.5f;float outerBox=sdRoundedBox(p,b,vec4(radius));if(outerBox>0.0f){discard;}vec2 innerB=b-vec2(outlineWidth,outlineWidth);float innerRadius=max(0.0f,radius-outlineWidth);float innerBox=sdRoundedBox(p,innerB,vec4(innerRadius));if(innerBox>0.0f){out_color=outlineColor;}else{out_color=vec4(color.rgb,color.a*opacity);}}"; // eslint-disable-line

class RectangleHandleMaterialWithOutline extends RawShaderMaterial {
  constructor(size, radius, color, outlineColor = "black", outlineWidth = 2, opacity = 1, maxZoom = Infinity, viewport = new Vector4()) {
    const mainColor = new Color(color);
    const strokeColor = new Color(outlineColor);
    super({
      vertexShader: vertexShader$1,
      fragmentShader: fragmentShader$1,
      uniforms: {
        size: { value: size },
        radius: { value: radius },
        color: { value: new Vector4(mainColor.r, mainColor.g, mainColor.b, 1) },
        outlineColor: { value: new Vector4(strokeColor.r, strokeColor.g, strokeColor.b, 1) },
        outlineWidth: { value: outlineWidth },
        opacity: { value: opacity },
        viewport: { value: viewport },
        maxZoom: { value: maxZoom }
      },
      transparent: true
    });
    this.toneMapped = false;
  }
}

class RectangleHandleWithOutline extends Handle {
  constructor(point, props = {
    size: 1,
    width: 40,
    height: 18,
    radius: 9,
    outlineColor: "black",
    outlineWidth: 2,
    opacity: 1
  }, viewport) {
    super(point, viewport);
    this.object3d.geometry = new PlaneGeometry();
    this.width = props.width;
    this.height = props.height;
    this.object3d.scale.set(this.width * props.size, this.height * props.size, 1);
    this.object3d.material = new RectangleHandleMaterialWithOutline(
      this.object3d.scale,
      props.radius,
      props.color || "red",
      props.outlineColor || "black",
      props.outlineWidth || 2,
      props.opacity || 1,
      props.maxZoom
    );
  }
  set size(size) {
    this.object3d.scale.multiplyScalar(size / this.width);
  }
  get size() {
    return this.object3d.scale.x / this.width;
  }
  set radius(radius) {
    this.object3d.material.uniforms.radius.value = radius;
  }
  get radius() {
    return this.object3d.material.uniforms.radius.value;
  }
  set outlineWidth(width) {
    this.object3d.material.uniforms.outlineWidth.value = width;
  }
  get outlineWidth() {
    return this.object3d.material.uniforms.outlineWidth.value;
  }
  onBeforeRender(renderer) {
    renderer.getViewport(this.object3d.material.uniforms?.viewport.value);
  }
  setOutlineColor(color) {
    const c = new Color(color);
    this.object3d.material.uniforms.outlineColor.value.set(c.r, c.g, c.b, 1);
  }
  setWidth(width) {
    this.object3d.scale.x = width;
    this.width = width;
  }
  setHeight(height) {
    this.object3d.scale.y = height;
    this.height = height;
  }
  getHeight() {
    return this.height;
  }
  getWidth() {
    return this.width;
  }
  set opacity(value) {
    this.object3d.material.uniforms.opacity.value = value;
  }
  get opacity() {
    return this.object3d.material.uniforms.opacity.value;
  }
}

const vector = new Vector3();
const position = new Vector3();
class RectangleHandleWithOutlineRaycast extends Raycast {
  constructor(entity, offsetX = 0, offsetY = 0) {
    super(entity);
    if (!(entity instanceof RectangleHandleWithOutline)) {
      throw new Error("RectangleHandleWithOutline-only component");
    }
    this.offsetX = offsetX;
    this.offsetY = offsetY;
  }
  isRaycast(raycaster) {
    const activeViewport = App.Instance().getActiveView()?.getActiveViewport();
    if (activeViewport && this.entity.object3d.layers.test(activeViewport.camera.layers)) {
      let zoom = 2 / (activeViewport.camera.projectionMatrix.elements[5] * activeViewport.size.w);
      const maxZoom = this.entity.object3d.material.uniforms.maxZoom.value;
      zoom = Math.min(zoom, maxZoom);
      const halfWidth = (this.entity.object3d.scale.x / 2 + this.offsetX) * zoom;
      const halfHeight = (this.entity.object3d.scale.y / 2 + this.offsetY) * zoom;
      position.setFromMatrixColumn(this.entity.object3d.matrixWorld, 3);
      vector.set(raycaster.ray.origin.x, raycaster.ray.origin.y, position.z);
      const dx = Math.abs(vector.x - position.x);
      const dy = Math.abs(vector.y - position.y);
      if (dx <= halfWidth && dy <= halfHeight) {
        const radius = this.entity.radius * zoom;
        const cornerX = halfWidth - radius;
        const cornerY = halfHeight - radius;
        if (dx > cornerX && dy > cornerY) {
          const cornerDx = dx - cornerX;
          const cornerDy = dy - cornerY;
          const distanceFromCorner = Math.sqrt(cornerDx * cornerDx + cornerDy * cornerDy);
          if (distanceFromCorner > radius) {
            return false;
          }
        }
        return [{
          object: this.entity.object3d,
          distance: 0,
          point: new Vector3().set(raycaster.ray.origin.x, raycaster.ray.origin.y, position.z)
        }];
      } else {
        return false;
      }
    }
    return false;
  }
}

class OutlineHighlighter extends Highlighter {
  constructor(entity, increaseFactor = 1.3, outlineColor, outlineScale = 1.5) {
    super(entity);
    this.increaseFactor = increaseFactor;
    this.outlineColor = outlineColor;
    this.outlineScale = outlineScale;
    this.originalOutlineColor = new Vector4();
    this.originalScale = new Vector3();
    this.originalOutlineWidth = 0;
  }
  hasOutlineMaterial() {
    const material = this.entity.object3d?.material;
    return material?.uniforms?.outlineColor && material?.uniforms?.outlineWidth;
  }
  highlight() {
    if (!this.hasOutlineMaterial()) {
      console.warn("Entity does not have outline material uniforms (outlineColor, outlineWidth)");
      return;
    }
    const material = this.entity.object3d.material;
    this.originalScale.copy(this.entity.object3d.scale);
    if (material.uniforms.outlineWidth.value !== void 0) {
      this.originalOutlineWidth = material.uniforms.outlineWidth.value;
      material.uniforms.outlineWidth.value = this.originalOutlineWidth * this.outlineScale;
    }
    if (this.outlineColor && material.uniforms.outlineColor.value) {
      this.originalOutlineColor.copy(material.uniforms.outlineColor.value);
      const newColor = new Color(this.outlineColor);
      material.uniforms.outlineColor.value.set(newColor.r, newColor.g, newColor.b, 1);
    }
    this.entity.object3d.scale.x = this.entity.getWidth() * this.increaseFactor;
    this.entity.object3d.scale.y = this.entity.getHeight() * this.increaseFactor;
  }
  unHighlight() {
    if (!this.hasOutlineMaterial()) {
      return;
    }
    const material = this.entity.object3d.material;
    if (this.outlineColor && material.uniforms.outlineColor.value) {
      material.uniforms.outlineColor.value.copy(this.originalOutlineColor);
    }
    if (material.uniforms.outlineWidth.value !== void 0) {
      material.uniforms.outlineWidth.value = this.originalOutlineWidth;
    }
    this.entity.object3d.scale.x = this.entity.getWidth();
    this.entity.object3d.scale.y = this.entity.getHeight();
  }
}

class ToothNumberHighlighter extends OutlineHighlighter {
  constructor(entity, increaseFactor = 1.3, outlineColor, outlineScale = 1.7) {
    if (!(entity instanceof ToothNumber)) {
      throw new Error("ToothNumberHighlighter can only be used with ToothNumber entities");
    }
    super(entity.handle, increaseFactor, outlineColor, outlineScale);
    this.blinkAnimationId = null;
    this.toothNumber = entity;
  }
  async highlight() {
    super.highlight();
    this.startDuplicatesBlinking();
  }
  async unHighlight() {
    super.unHighlight();
    this.stopDuplicatesBlinking();
  }
  startDuplicatesBlinking() {
    if (this.blinkAnimationId || this.toothNumber.duplicateToothNumbers.length === 0) {
      return;
    }
    const startTime = performance.now();
    const period = 1e3;
    const originalScales = /* @__PURE__ */ new Map();
    this.toothNumber.duplicateToothNumbers.forEach((tooth) => {
      if (tooth.handle) {
        originalScales.set(tooth, {
          width: tooth.handle.object3d.scale.x,
          height: tooth.handle.object3d.scale.y
        });
        tooth.handle.outlineWidth *= this.outlineScale;
      }
    });
    const animate = () => {
      const currentTime = performance.now();
      const elapsed = currentTime - startTime;
      const phase = elapsed % period / period;
      const scaleFactor = 1 + 0.2 * ((Math.sin(phase * Math.PI * 2) + 1) / 2);
      this.toothNumber.duplicateToothNumbers.forEach((tooth) => {
        if (tooth.handle && tooth.handle instanceof RectangleHandleWithOutline) {
          const original = originalScales.get(tooth);
          if (original) {
            tooth.handle.object3d.scale.set(
              original.width * scaleFactor,
              original.height * scaleFactor,
              1
            );
          }
          tooth.handle.object3d.updateMatrix();
          tooth.handle.object3d.updateMatrixWorld(true);
        }
      });
      const activeView = MIRender.getActiveView();
      if (activeView) {
        activeView.dispatchEvent({ type: "render" });
      }
      this.blinkAnimationId = requestAnimationFrame(animate);
    };
    this.blinkAnimationId = requestAnimationFrame(animate);
  }
  stopDuplicatesBlinking() {
    if (this.blinkAnimationId) {
      cancelAnimationFrame(this.blinkAnimationId);
      this.blinkAnimationId = null;
      this.toothNumber.duplicateToothNumbers.forEach((tooth) => {
        if (tooth.handle && tooth.handle instanceof RectangleHandleWithOutline) {
          const originalWidth = tooth.handle.getWidth() || 35;
          const originalHeight = tooth.handle.getHeight() || 35;
          tooth.handle.object3d.scale.set(originalWidth, originalHeight, 1);
          tooth.handle.object3d.updateMatrix();
          tooth.handle.object3d.updateMatrixWorld(true);
          tooth.handle.outlineWidth /= this.outlineScale;
        }
      });
      const activeView = MIRender.getActiveView();
      if (activeView) {
        activeView.dispatchEvent({ type: "render" });
      }
    }
  }
}

const OrganizationJobPosition = proto3.makeEnum(
  "com.diagnocat.model.OrganizationJobPosition",
  [
    { no: 0, name: "OrganizationJobPositionInvalidValue" },
    { no: 1, name: "OrganizationJobPositionChiefDentalOfficer" },
    { no: 2, name: "OrganizationJobPositionTreatmentCoordinator" },
    { no: 3, name: "OrganizationJobPositionDentalAssistant" },
    { no: 4, name: "OrganizationJobPositionDentalTechnician" },
    { no: 5, name: "OrganizationJobPositionGeneralDentist" },
    { no: 6, name: "OrganizationJobPositionPeriodontist" },
    { no: 7, name: "OrganizationJobPositionProsthodontist" },
    { no: 8, name: "OrganizationJobPositionOrthodontist" },
    { no: 9, name: "OrganizationJobPositionPediatricDentist" },
    { no: 10, name: "OrganizationJobPositionOralAndMaxillofacialSurgeon" },
    { no: 11, name: "OrganizationJobPositionOralAndMaxillofacialRadiologist" },
    { no: 12, name: "OrganizationJobPositionAestheticDentist" },
    { no: 13, name: "OrganizationJobPositionDentalHygienist" },
    { no: 16, name: "OrganizationJobPositionEndodontist" },
    { no: 17, name: "OrganizationJobPositionDentalTherapist" },
    { no: 18, name: "OrganizationJobPositionImplantologist" },
    { no: 101, name: "OrganizationJobPositionGeneralPractitioner" },
    { no: 102, name: "OrganizationJobPositionInternist" },
    { no: 103, name: "OrganizationJobPositionPediatrician" },
    { no: 104, name: "OrganizationJobPositionGeneralSurgeon" },
    { no: 105, name: "OrganizationJobPositionCardiologist" },
    { no: 106, name: "OrganizationJobPositionNeurologist" },
    { no: 107, name: "OrganizationJobPositionOphthalmologist" },
    { no: 108, name: "OrganizationJobPositionOtolaryngologist" },
    { no: 109, name: "OrganizationJobPositionDermatologist" },
    { no: 110, name: "OrganizationJobPositionObstetricianGynecologist" },
    { no: 111, name: "OrganizationJobPositionUrologist" },
    { no: 112, name: "OrganizationJobPositionEndocrinologist" },
    { no: 113, name: "OrganizationJobPositionGastroenterologist" },
    { no: 114, name: "OrganizationJobPositionPulmonologist" },
    { no: 115, name: "OrganizationJobPositionRheumatologist" },
    { no: 116, name: "OrganizationJobPositionOncologist" },
    { no: 117, name: "OrganizationJobPositionOrthopedicSurgeon" },
    { no: 118, name: "OrganizationJobPositionPsychiatrist" },
    { no: 119, name: "OrganizationJobPositionAllergistImmunologist" },
    { no: 120, name: "OrganizationJobPositionInfectiousDiseaseSpecialist" },
    { no: 121, name: "OrganizationJobPositionNephrologist" },
    { no: 123, name: "OrganizationJobPositionRadiologist" },
    { no: 124, name: "OrganizationJobPositionEmergencyPhysician" },
    { no: 125, name: "OrganizationJobPositionNeurosurgeon" },
    { no: 126, name: "OrganizationJobPositionPlasticSurgeon" },
    { no: 127, name: "OrganizationJobPositionGeriatrician" },
    { no: 128, name: "OrganizationJobPositionPhysiatrist" },
    { no: 129, name: "OrganizationJobPositionHematologist" },
    { no: 130, name: "OrganizationJobPositionNeonatologist" },
    { no: 14, name: "OrganizationJobPositionOther" },
    { no: 15, name: "OrganizationJobPositionOfficeManager" },
    { no: 122, name: "OrganizationJobPositionAnesthesiologist" }
  ]
);

const OrganizationPermissions = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationPermissions",
  () => [
    {
      no: 1,
      name: "CanView",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "CanEditInfo",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 3,
      name: "CanViewSettings",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 4,
      name: "CanEditSettings",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 5,
      name: "CanEditBranches",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 6,
      name: "CanViewStaff",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 7,
      name: "CanEditStaff",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 8,
      name: "CanViewFeatureFlags",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 9,
      name: "CanEditFeatureFlags",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 11,
      name: "CanCreateTheirOwnPatients",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 12,
      name: "CanCreateOtherDoctorsPatients",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 13,
      name: "CanViewMyPatients",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 14,
      name: "CanViewAllPatients",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 15,
      name: "CanManageComment",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 22,
      name: "CanViewStatistics",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 31,
      name: "Billing_CanViewInventory",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 32,
      name: "Billing_CanViewOrder",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 33,
      name: "Billing_CanViewAccount",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 34,
      name: "Billing_CanEditPaymentMethod",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 35,
      name: "Billing_CanEditAccountSettings",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 36,
      name: "Billing_CanEditAccountContacts",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 37,
      name: "Billing_CanEditProducts",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 42,
      name: "APIKey_CanView",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 43,
      name: "APIKey_CanCreate",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 44,
      name: "APIKey_CanDelete",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 45,
      name: "MFA_CanEdit",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);

const CalledVia = proto3.makeEnum(
  "com.diagnocat.model.CalledVia",
  [
    { no: 0, name: "CalledViaUnspecified" },
    { no: 1, name: "CalledViaClient" },
    { no: 2, name: "CalledViaPartnerAPI" },
    { no: 3, name: "CalledViaMLPipelines" },
    { no: 4, name: "CalledViaDesktop" },
    { no: 5, name: "CalledViaWorker" }
  ]
);
proto3.makeEnum(
  "com.diagnocat.model.Gender",
  [
    { no: 0, name: "GenderUnspecified" },
    { no: 1, name: "GenderMale" },
    { no: 2, name: "GenderFemale" },
    { no: 3, name: "GenderOther" }
  ]
);
proto3.makeEnum(
  "com.diagnocat.model.OptionalBoolean",
  [
    { no: 0, name: "OptionalBooleanUnspecified" },
    { no: 1, name: "OptionalBooleanFalse" },
    { no: 2, name: "OptionalBooleanTrue" }
  ]
);
const Revision = proto3.makeMessageType(
  "com.diagnocat.model.Revision",
  () => [
    {
      no: 1,
      name: "Number",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 2, name: "UpdatedAt", kind: "message", T: Timestamp },
    {
      no: 3,
      name: "UserID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const Created = proto3.makeMessageType(
  "com.diagnocat.model.Created",
  () => [
    {
      no: 1,
      name: "By",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "At", kind: "message", T: Timestamp },
    { no: 3, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
  ]
);
const Deleted = proto3.makeMessageType(
  "com.diagnocat.model.Deleted",
  () => [
    {
      no: 1,
      name: "Deleted",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "By",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "At", kind: "message", T: Timestamp },
    { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
  ]
);
const Archived = proto3.makeMessageType(
  "com.diagnocat.model.Archived",
  () => [
    {
      no: 1,
      name: "Archived",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "By",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "At", kind: "message", T: Timestamp },
    { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
  ]
);
const Attribute = proto3.makeMessageType(
  "com.diagnocat.model.Attribute",
  () => [
    {
      no: 1,
      name: "Key",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Value",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.PersonalData",
  () => [
    {
      no: 1,
      name: "FirstName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "LastName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "DateOfBirth",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 4, name: "Emails", kind: "scalar", T: 9, repeated: true },
    { no: 5, name: "Phones", kind: "scalar", T: 9, repeated: true }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.RevisionNew",
  () => [
    {
      no: 1,
      name: "Number",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 2,
      name: "By",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "At", kind: "message", T: Timestamp },
    { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.DeletedNew",
  () => [
    {
      no: 1,
      name: "IsDeleted",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "By",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "At", kind: "message", T: Timestamp },
    { no: 4, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
  ]
);

const CBCT_SectionOrientation = proto3.makeEnum(
  "com.diagnocat.model.CBCT_SectionOrientation",
  [
    { no: 0, name: "SectionOrientationInvalidValue" },
    { no: 1, name: "SectionOrientationAxial" },
    { no: 2, name: "SectionOrientationVestibuloOral" },
    { no: 3, name: "SectionOrientationMesioDistal" }
  ]
);
const CBCT_SliceOrientation = proto3.makeEnum(
  "com.diagnocat.model.CBCT_SliceOrientation",
  [
    { no: 0, name: "SliceOrientationInvalidValue" },
    { no: 1, name: "SliceOrientationMesial" },
    { no: 2, name: "SliceOrientationDistal" },
    { no: 3, name: "SliceOrientationLingual" },
    { no: 4, name: "SliceOrientationBuccal" }
  ]
);
const File = proto3.makeMessageType(
  "com.diagnocat.model.File",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Path",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "Size",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 4,
      name: "MIME",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "CompressSize",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 6,
      name: "SHA256",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 101, name: "NoMetadata", kind: "message", T: Empty, oneof: "Metadata" },
    { no: 102, name: "Image", kind: "message", T: FileMetadata_Image, oneof: "Metadata" }
  ]
);
const FileMetadata_Image = proto3.makeMessageType(
  "com.diagnocat.model.FileMetadata_Image",
  () => [
    {
      no: 1,
      name: "Width",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 2,
      name: "Height",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    }
  ]
);
const DerivedData_Image = proto3.makeMessageType(
  "com.diagnocat.model.DerivedData_Image",
  () => [
    { no: 1, name: "Thumbnail", kind: "message", T: File },
    { no: 2, name: "Preview", kind: "message", T: File }
  ]
);
const DerivedData_Video = proto3.makeMessageType(
  "com.diagnocat.model.DerivedData_Video",
  []
);
const DerivedData_3DModel = proto3.makeMessageType(
  "com.diagnocat.model.DerivedData_3DModel",
  () => [
    { no: 1, name: "FrontalPreview", kind: "message", T: File },
    { no: 2, name: "LeftPreview", kind: "message", T: File },
    { no: 3, name: "RightPreview", kind: "message", T: File },
    { no: 4, name: "FrontalThumbnail", kind: "message", T: File },
    { no: 5, name: "LeftThumbnail", kind: "message", T: File },
    { no: 6, name: "RightThumbnail", kind: "message", T: File },
    { no: 7, name: "RotatingPreview", kind: "message", T: File }
  ]
);

const AssetContent_Document = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Document",
  () => [
    { no: 1, name: "Document", kind: "message", T: File },
    { no: 2, name: "Image", kind: "message", T: DerivedData_Image, oneof: "Derived" },
    { no: 3, name: "PDF", kind: "message", T: AssetContent_Document_DerivedData_PDF, oneof: "Derived" },
    { no: 4, name: "Doc", kind: "message", T: AssetContent_Document_DerivedData_DOC, oneof: "Derived" },
    { no: 5, name: "Video", kind: "message", T: DerivedData_Video, oneof: "Derived" },
    { no: 6, name: "Archive", kind: "message", T: AssetContent_Document_DerivedData_Archive, oneof: "Derived" },
    { no: 7, name: "Text", kind: "message", T: AssetContent_Document_DerivedData_Text, oneof: "Derived" }
  ]
);
const AssetContent_Document_DerivedData_PDF = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Document.DerivedData_PDF",
  [],
  { localName: "AssetContent_Document_DerivedData_PDF" }
);
const AssetContent_Document_DerivedData_DOC = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Document.DerivedData_DOC",
  [],
  { localName: "AssetContent_Document_DerivedData_DOC" }
);
const AssetContent_Document_DerivedData_Archive = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Document.DerivedData_Archive",
  [],
  { localName: "AssetContent_Document_DerivedData_Archive" }
);
const AssetContent_Document_DerivedData_Text = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Document.DerivedData_Text",
  [],
  { localName: "AssetContent_Document_DerivedData_Text" }
);

const CBCTSeriesGeometryData = proto3.makeMessageType(
  "com.diagnocat.model.CBCTSeriesGeometryData",
  () => [
    { no: 1, name: "WorkingCrop", kind: "message", T: BBox },
    { no: 2, name: "Shape", kind: "message", T: IntTuple3D },
    { no: 3, name: "Scale", kind: "message", T: FloatTuple3D }
  ]
);
const FloatPoint2D = proto3.makeMessageType(
  "com.diagnocat.model.FloatPoint2D",
  () => [
    {
      no: 1,
      name: "X",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.IntPoint3D",
  () => [
    {
      no: 1,
      name: "Z",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 3,
      name: "X",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    }
  ]
);
const FloatPoint3D = proto3.makeMessageType(
  "com.diagnocat.model.FloatPoint3D",
  () => [
    {
      no: 1,
      name: "Z",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 3,
      name: "X",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.OrderedLine2D",
  () => [
    {
      no: 1,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 2, name: "Start", kind: "message", T: FloatPoint2D },
    { no: 3, name: "End", kind: "message", T: FloatPoint2D }
  ]
);
const IntTuple3D = proto3.makeMessageType(
  "com.diagnocat.model.IntTuple3D",
  () => [
    {
      no: 1,
      name: "Z",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 3,
      name: "X",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    }
  ]
);
const FloatTuple2D = proto3.makeMessageType(
  "com.diagnocat.model.FloatTuple2D",
  () => [
    {
      no: 1,
      name: "X",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    }
  ]
);
const FloatTuple3D = proto3.makeMessageType(
  "com.diagnocat.model.FloatTuple3D",
  () => [
    {
      no: 1,
      name: "Z",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 3,
      name: "X",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    }
  ]
);
const AxisSlice = proto3.makeMessageType(
  "com.diagnocat.model.AxisSlice",
  () => [
    {
      no: 1,
      name: "Min",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 2,
      name: "Max",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 3,
      name: "Size",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    }
  ]
);
const BBox = proto3.makeMessageType(
  "com.diagnocat.model.BBox",
  () => [
    { no: 1, name: "Z", kind: "message", T: AxisSlice },
    { no: 2, name: "Y", kind: "message", T: AxisSlice },
    { no: 3, name: "X", kind: "message", T: AxisSlice }
  ]
);
const ProjectionTransform = proto3.makeMessageType(
  "com.diagnocat.model.ProjectionTransform",
  () => [
    { no: 1, name: "Matrix", kind: "scalar", T: 2, repeated: true }
  ]
);
const PlanePosition = proto3.makeMessageType(
  "com.diagnocat.model.PlanePosition",
  () => [
    { no: 1, name: "Origin", kind: "message", T: FloatPoint3D },
    { no: 2, name: "Normal", kind: "message", T: FloatPoint3D }
  ]
);
const StudioColorProfile = proto3.makeMessageType(
  "com.diagnocat.model.StudioColorProfile",
  () => [
    {
      no: 1,
      name: "WindowWidth",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "WindowLevel",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const ClosedPolygon = proto3.makeMessageType(
  "com.diagnocat.model.ClosedPolygon",
  () => [
    { no: 1, name: "Points", kind: "message", T: FloatPoint2D, repeated: true }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.ClosedPolygon_Upcoming",
  () => [
    { no: 1, name: "Points", kind: "scalar", T: 1, repeated: true }
  ]
);
const AnatomicalBasis3D = proto3.makeMessageType(
  "com.diagnocat.model.AnatomicalBasis3D",
  () => [
    { no: 1, name: "MesioDistal", kind: "message", T: FloatPoint3D },
    { no: 2, name: "VestibuloOral", kind: "message", T: FloatPoint3D },
    { no: 3, name: "Axial", kind: "message", T: FloatPoint3D }
  ]
);

const Annotations = proto3.makeMessageType(
  "com.diagnocat.model.Annotations",
  () => [
    { no: 1, name: "Layers", kind: "message", T: AnnotationsLayer, repeated: true }
  ]
);
const AnnotationsLayer = proto3.makeMessageType(
  "com.diagnocat.model.AnnotationsLayer",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Readonly",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 3, name: "Annotations", kind: "message", T: Annotation, repeated: true }
  ]
);
const Annotation = proto3.makeMessageType(
  "com.diagnocat.model.Annotation",
  () => [
    { no: 11, name: "Ruler", kind: "message", T: AnnotationType_Ruler, oneof: "Type" },
    { no: 12, name: "Arrow", kind: "message", T: AnnotationType_Arrow, oneof: "Type" },
    { no: 13, name: "PolyLine", kind: "message", T: AnnotationType_PolyLine, oneof: "Type" },
    { no: 14, name: "Angle", kind: "message", T: AnnotationType_Angle, oneof: "Type" },
    { no: 15, name: "Guide", kind: "message", T: AnnotationType_Guide, oneof: "Type" }
  ]
);
const AnnotationType_Ruler = proto3.makeMessageType(
  "com.diagnocat.model.AnnotationType_Ruler",
  () => [
    { no: 1, name: "Start", kind: "message", T: FloatPoint2D },
    { no: 2, name: "End", kind: "message", T: FloatPoint2D }
  ]
);
const AnnotationType_Arrow = proto3.makeMessageType(
  "com.diagnocat.model.AnnotationType_Arrow",
  () => [
    { no: 1, name: "Start", kind: "message", T: FloatPoint2D },
    { no: 2, name: "End", kind: "message", T: FloatPoint2D },
    {
      no: 3,
      name: "DoubleSided",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);
const AnnotationType_PolyLine = proto3.makeMessageType(
  "com.diagnocat.model.AnnotationType_PolyLine",
  () => [
    { no: 1, name: "Points", kind: "message", T: FloatPoint2D, repeated: true }
  ]
);
const AnnotationType_Angle = proto3.makeMessageType(
  "com.diagnocat.model.AnnotationType_Angle",
  () => [
    { no: 1, name: "Start", kind: "message", T: FloatPoint2D },
    { no: 2, name: "Corner", kind: "message", T: FloatPoint2D },
    { no: 3, name: "End", kind: "message", T: FloatPoint2D }
  ]
);
const AnnotationType_Guide = proto3.makeMessageType(
  "com.diagnocat.model.AnnotationType_Guide",
  () => [
    { no: 1, name: "Start", kind: "message", T: FloatPoint2D },
    { no: 2, name: "End", kind: "message", T: FloatPoint2D },
    {
      no: 3,
      name: "ReferencedAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);

const MedicalImageViewOptions = proto3.makeMessageType(
  "com.diagnocat.model.MedicalImageViewOptions",
  () => [
    { no: 1, name: "WindowWidth", kind: "scalar", T: 1, opt: true },
    { no: 2, name: "WindowLevel", kind: "scalar", T: 1, opt: true },
    { no: 3, name: "Sharpness", kind: "scalar", T: 1, opt: true },
    { no: 4, name: "Invert", kind: "scalar", T: 8, opt: true },
    { no: 5, name: "ViewportCoords", kind: "message", T: MedicalImageViewportCoords, opt: true },
    { no: 11, name: "Brightness", kind: "scalar", T: 1, opt: true },
    { no: 12, name: "Contrast", kind: "scalar", T: 1, opt: true },
    { no: 20, name: "Rotation", kind: "scalar", T: 1, opt: true },
    { no: 21, name: "FlipHorizontal", kind: "scalar", T: 8, opt: true },
    { no: 22, name: "CropBox", kind: "message", T: CropBox, opt: true },
    { no: 23, name: "Zoom", kind: "scalar", T: 1, opt: true },
    { no: 24, name: "PanX", kind: "scalar", T: 1, opt: true },
    { no: 25, name: "PanY", kind: "scalar", T: 1, opt: true }
  ]
);
const MedicalImageViewportCoords = proto3.makeMessageType(
  "com.diagnocat.model.MedicalImageViewportCoords",
  () => [
    { no: 1, name: "Left", kind: "scalar", T: 1, opt: true },
    { no: 2, name: "Top", kind: "scalar", T: 1, opt: true },
    { no: 3, name: "Right", kind: "scalar", T: 1, opt: true },
    { no: 4, name: "Bottom", kind: "scalar", T: 1, opt: true }
  ]
);
const CropBox = proto3.makeMessageType(
  "com.diagnocat.model.CropBox",
  () => [
    {
      no: 1,
      name: "X",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 2,
      name: "Y",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 3,
      name: "Width",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 4,
      name: "Height",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    }
  ]
);
const MedicalImageViewOptions_Legacy = proto3.makeMessageType(
  "com.diagnocat.model.MedicalImageViewOptions_Legacy",
  () => [
    { no: 1, name: "WindowWidth", kind: "message", T: DoubleValue },
    { no: 2, name: "WindowLevel", kind: "message", T: DoubleValue },
    { no: 3, name: "Sharpness", kind: "message", T: DoubleValue },
    { no: 4, name: "Invert", kind: "message", T: BoolValue },
    { no: 5, name: "ColorMap", kind: "message", T: MedicalImageColorMap_Legacy },
    { no: 6, name: "ViewportCoords", kind: "message", T: MedicalImageViewportCoords_Legacy },
    { no: 11, name: "Brightness", kind: "message", T: DoubleValue },
    { no: 12, name: "Contrast", kind: "message", T: DoubleValue }
  ]
);
const MedicalImageViewportCoords_Legacy = proto3.makeMessageType(
  "com.diagnocat.model.MedicalImageViewportCoords_Legacy",
  () => [
    { no: 1, name: "Left", kind: "message", T: DoubleValue },
    { no: 2, name: "Top", kind: "message", T: DoubleValue },
    { no: 3, name: "Right", kind: "message", T: DoubleValue },
    { no: 4, name: "Bottom", kind: "message", T: DoubleValue }
  ]
);
const MedicalImageColorMap_Legacy = proto3.makeMessageType(
  "com.diagnocat.model.MedicalImageColorMap_Legacy",
  []
);

const MedicalImageFeatures = proto3.makeMessageType(
  "com.diagnocat.model.MedicalImageFeatures",
  () => [
    { no: 1, name: "Annotations", kind: "message", T: Annotations },
    { no: 2, name: "ViewOptions", kind: "message", T: MedicalImageViewOptions_Legacy },
    { no: 3, name: "ViewOptions_V2", kind: "message", T: MedicalImageViewOptions }
  ]
);

const AssetContent_Study_CBCT = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_CBCT",
  () => [
    { no: 1, name: "DICOMFiles", kind: "message", T: File, repeated: true },
    { no: 2, name: "CBCTArchive", kind: "message", T: File },
    { no: 3, name: "Metadata", kind: "message", T: AssetContent_Study_CBCT_CBCT_Metadata },
    {
      no: 4,
      name: "SeriesUID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const AssetContent_Study_CBCT_CBCT_Metadata = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_CBCT.CBCT_Metadata",
  () => [
    { no: 12, name: "StudyTime", kind: "message", T: Timestamp },
    {
      no: 2,
      name: "InternalStudyUID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "InternalSeriesUID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "InternalInstanceUID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "ScanUID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 6,
      name: "Manufacturer",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 7,
      name: "ManufacturerModelName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 8,
      name: "SliceThickness",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 9,
      name: "FOV",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 10, name: "VoxelSize", kind: "scalar", T: 1, repeated: true },
    {
      no: 11,
      name: "Exposure",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ],
  { localName: "AssetContent_Study_CBCT_CBCT_Metadata" }
);
const AssetContent_Study_CBCT_DCCT = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_CBCT_DCCT",
  () => [
    { no: 1, name: "Volume", kind: "message", T: File },
    { no: 2, name: "Metadata", kind: "message", T: AssetContent_Study_CBCT_DCCT_SanitizedMetadata },
    { no: 3, name: "RawMetadata", kind: "message", T: File }
  ]
);
const AssetContent_Study_CBCT_DCCT_SanitizedMetadata = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_CBCT_DCCT.SanitizedMetadata",
  () => [
    {
      no: 1,
      name: "Manufacturer",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "ManufacturerModelName", kind: "scalar", T: 9, opt: true },
    {
      no: 3,
      name: "SpacingBetweenSlices",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    { no: 4, name: "PixelSpacing", kind: "scalar", T: 1, repeated: true },
    {
      no: 5,
      name: "NumberOfFrames",
      kind: "scalar",
      T: 5
      /* ScalarType.INT32 */
    },
    {
      no: 6,
      name: "Rows",
      kind: "scalar",
      T: 5
      /* ScalarType.INT32 */
    },
    {
      no: 7,
      name: "Columns",
      kind: "scalar",
      T: 5
      /* ScalarType.INT32 */
    },
    {
      no: 8,
      name: "SeriesInstanceUID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 9, name: "PatientSex", kind: "scalar", T: 9, opt: true },
    { no: 10, name: "PatientAge", kind: "scalar", T: 2, opt: true },
    { no: 11, name: "AcquisitionDateTime", kind: "message", T: Timestamp, opt: true },
    { no: 12, name: "KVP", kind: "scalar", T: 1, opt: true },
    { no: 13, name: "XRayTubeCurrentInmA", kind: "scalar", T: 1, opt: true },
    { no: 14, name: "ExposureInmAs", kind: "scalar", T: 1, opt: true }
  ],
  { localName: "AssetContent_Study_CBCT_DCCT_SanitizedMetadata" }
);
const AssetContent_Study_IOS_Meshes = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_IOS_Meshes",
  () => [
    { no: 1, name: "Jaws", kind: "message", T: File, repeated: true },
    { no: 100, name: "Derived", kind: "message", T: DerivedData_3DModel }
  ]
);
const AssetContent_Study_MeshModel = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_MeshModel",
  () => [
    { no: 1, name: "Geometry", kind: "message", T: File },
    { no: 2, name: "MTL", kind: "message", T: File, opt: true },
    { no: 3, name: "Textures", kind: "message", T: File, repeated: true }
  ]
);
const AssetContent_Study_Panoramic_XRay = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_Panoramic_XRay",
  () => [
    { no: 1, name: "PanoramaImage", kind: "message", T: File },
    { no: 100, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Study_Intraoral_XRay = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_Intraoral_XRay",
  () => [
    { no: 1, name: "Image", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Study_Dental_Photo = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_Dental_Photo",
  () => [
    { no: 1, name: "Image", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Study_Dental_Video = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Study_Dental_Video",
  () => [
    { no: 1, name: "Video", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Video }
  ]
);

const EmptyPayload = proto3.makeMessageType(
  "com.diagnocat.model.EmptyPayload",
  []
);
const ReportLegacyPayload = proto3.makeMessageType(
  "com.diagnocat.model.ReportLegacyPayload",
  () => [
    {
      no: 1,
      name: "Key",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "JSON",
      kind: "scalar",
      T: 12
      /* ScalarType.BYTES */
    }
  ]
);

const Decision = proto3.makeEnum(
  "com.diagnocat.model.Decision",
  [
    { no: 0, name: "NoDecision" },
    { no: 1, name: "NegativeDecision" },
    { no: 2, name: "PositiveDecision" }
  ]
);
const JawType = proto3.makeEnum(
  "com.diagnocat.model.JawType",
  [
    { no: 0, name: "JawType_InvalidVariant" },
    { no: 1, name: "JawType_Maxilla" },
    { no: 2, name: "JawType_Mandible" }
  ]
);
const IOS_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.IOS_Anatomy",
  [
    { no: 0, name: "IOS_Anatomy_InvalidValue" },
    { no: 1, name: "IOS_Anatomy_Original_Mandible" },
    { no: 2, name: "IOS_Anatomy_Original_Maxilla" },
    { no: 3, name: "IOS_Anatomy_GingivaUpper" },
    { no: 4, name: "IOS_Anatomy_GingivaLower" }
  ]
);
const CBCT_Tooth_RootType = proto3.makeEnum(
  "com.diagnocat.model.CBCT_Tooth_RootType",
  [
    { no: 0, name: "RootType_InvalidValue" },
    { no: 1, name: "RootType_Unspecified" },
    { no: 2, name: "RootType_SingleRoot" },
    { no: 3, name: "RootType_Buccal" },
    { no: 4, name: "RootType_Lingual" },
    { no: 5, name: "RootType_Palatal" },
    { no: 6, name: "RootType_Mesial" },
    { no: 7, name: "RootType_MesialBuccal" },
    { no: 8, name: "RootType_MesialBuccal2" },
    { no: 10, name: "RootType_MesialLingual" },
    { no: 11, name: "RootType_Distal" },
    { no: 12, name: "RootType_DistalBuccal" },
    { no: 13, name: "RootType_DistalLingual" },
    { no: 14, name: "RootType_Average" }
  ]
);
const ToothNumeration = proto3.makeMessageType(
  "com.diagnocat.model.ToothNumeration",
  () => [
    {
      no: 1,
      name: "ISO",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 2,
      name: "SupernumeraryIndex",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    }
  ]
);

const AssetContent_Generated_CBCT_GP_PanoramaReformatSplit = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_GP_PanoramaReformatSplit",
  () => [
    { no: 1, name: "PanoramaSplit", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Generated_CBCT_GP_PanoramaReformatGeneral = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_GP_PanoramaReformatGeneral",
  () => [
    { no: 1, name: "PanoramaSingle", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Generated_CBCT_GP_ToothSlice = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_GP_ToothSlice",
  () => [
    { no: 1, name: "Slice", kind: "message", T: File },
    { no: 2, name: "Numeration", kind: "message", T: ToothNumeration },
    { no: 3, name: "Orientation", kind: "enum", T: proto3.getEnumType(CBCT_SectionOrientation) },
    {
      no: 4,
      name: "Scale",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 5,
      name: "WindowWidth",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 6,
      name: "WindowLevel",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 7,
      name: "ProposedToothCardOrder",
      kind: "scalar",
      T: 5
      /* ScalarType.INT32 */
    },
    { no: 101, name: "Regular", kind: "message", T: CBCTGPToothSliceMetadataRegular, oneof: "Metadata" },
    { no: 102, name: "Guide", kind: "message", T: CBCTGPToothSliceMetadataGuide, oneof: "Metadata" },
    { no: 103, name: "GuideFrontal", kind: "message", T: CBCTGPToothSliceMetadataGuideFrontal, oneof: "Metadata" }
  ]
);
const CBCTGPToothSliceMetadataRegular = proto3.makeMessageType(
  "com.diagnocat.model.CBCTGPToothSliceMetadataRegular",
  () => [
    {
      no: 1,
      name: "Thickness",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "Position",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "TotalImages",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 4,
      name: "Stride",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 5,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    }
  ]
);
const CBCTGPToothSliceMetadataGuide = proto3.makeMessageType(
  "com.diagnocat.model.CBCTGPToothSliceMetadataGuide",
  () => [
    {
      no: 1,
      name: "Axis2",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 2,
      name: "OrthoAxis",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    }
  ]
);
const CBCTGPToothSliceMetadataGuideFrontal = proto3.makeMessageType(
  "com.diagnocat.model.CBCTGPToothSliceMetadataGuideFrontal",
  () => [
    {
      no: 1,
      name: "Thickness",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "Position",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "Axis2",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    }
  ]
);
const AssetContent_Generated_CBCT_GP_MPR_SubVolume = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_GP_MPR_SubVolume",
  () => [
    { no: 1, name: "Volume", kind: "message", T: File },
    { no: 2, name: "Numeration", kind: "message", T: ToothNumeration },
    { no: 3, name: "Position", kind: "message", T: BBox },
    { no: 4, name: "PositionInner", kind: "message", T: BBox },
    {
      no: 5,
      name: "WindowWidth",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 6,
      name: "WindowLevel",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const AssetContent_Generated_CBCT_GP_PeriodontiumImage = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_GP_PeriodontiumImage",
  () => [
    { no: 1, name: "Periodontium", kind: "message", T: File },
    { no: 2, name: "Numeration", kind: "message", T: ToothNumeration },
    { no: 3, name: "ImageType", kind: "enum", T: proto3.getEnumType(AssetContent_Generated_CBCT_GP_PeriodontiumImage_Type) }
  ]
);
const AssetContent_Generated_CBCT_GP_PeriodontiumImage_Type = proto3.makeEnum(
  "com.diagnocat.model.AssetContent_Generated_CBCT_GP_PeriodontiumImage.Type",
  [
    { no: 0, name: "Mesiodistal" }
  ]
);

const AssetContent_Generated_CBCT_Endo_ReportSection = proto3.makeEnum(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Endo_ReportSection",
  [
    { no: 0, name: "Endo_ReportSection_RootCanalSpace" },
    { no: 1, name: "Endo_ReportSection_RootCanalSystem" },
    { no: 2, name: "Endo_ReportSection_RootCanalShape" },
    { no: 3, name: "Endo_ReportSection_Furcation" }
  ]
);
const AssetContent_Generated_CBCT_Endo_PanoramaSplit = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Endo_PanoramaSplit",
  () => [
    { no: 1, name: "PanoramaSplit", kind: "message", T: File },
    { no: 2, name: "Type", kind: "enum", T: proto3.getEnumType(AssetContent_Generated_CBCT_Endo_PanoramaSplit_PanoramaSplit_Type) },
    {
      no: 3,
      name: "Thickness",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 4,
      name: "WindowWidth",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 5,
      name: "WindowLevel",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    { no: 101, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Generated_CBCT_Endo_PanoramaSplit_PanoramaSplit_Type = proto3.makeEnum(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Endo_PanoramaSplit.PanoramaSplit_Type",
  [
    { no: 0, name: "Mandible" },
    { no: 1, name: "Maxilla" }
  ]
);
const AssetContent_Generated_CBCT_Endo_CrossSection = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Endo_CrossSection",
  () => [
    { no: 1, name: "CrossSection", kind: "message", T: File },
    {
      no: 2,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 3,
      name: "Thickness",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    { no: 4, name: "RightSideOrient", kind: "enum", T: proto3.getEnumType(CBCT_SliceOrientation) },
    { no: 5, name: "LeftSideOrient", kind: "enum", T: proto3.getEnumType(CBCT_SliceOrientation) },
    { no: 6, name: "Section", kind: "enum", T: proto3.getEnumType(AssetContent_Generated_CBCT_Endo_ReportSection) },
    { no: 7, name: "Orientation", kind: "enum", T: proto3.getEnumType(CBCT_SectionOrientation) },
    { no: 101, name: "Axial", kind: "message", T: CBCTEndoCrossSectionMetadataAxial, oneof: "Metadata" },
    { no: 102, name: "Furcation", kind: "message", T: CBCTEndoCrossSectionMetadataFurcation, oneof: "Metadata" },
    { no: 103, name: "Vertical", kind: "message", T: CBCTEndoCrossSectionMetadataVertical, oneof: "Metadata" }
  ]
);
const CBCTEndoCrossSectionMetadataFurcation = proto3.makeMessageType(
  "com.diagnocat.model.CBCTEndoCrossSectionMetadataFurcation",
  []
);
const CBCTEndoCrossSectionMetadataAxial = proto3.makeMessageType(
  "com.diagnocat.model.CBCTEndoCrossSectionMetadataAxial",
  () => [
    {
      no: 1,
      name: "Stride",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const CBCTEndoCrossSectionMetadataVertical = proto3.makeMessageType(
  "com.diagnocat.model.CBCTEndoCrossSectionMetadataVertical",
  () => [
    {
      no: 1,
      name: "CanalLength",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    { no: 2, name: "CanalCurvature", kind: "message", T: CBCTEndoCrossSectionMetadataVertical_CanalCurvatureTuple },
    { no: 3, name: "RootName", kind: "enum", T: proto3.getEnumType(CBCTEndoCrossSectionMetadataVertical_Root) }
  ]
);
const CBCTEndoCrossSectionMetadataVertical_Root = proto3.makeEnum(
  "com.diagnocat.model.CBCTEndoCrossSectionMetadataVertical.Root",
  [
    { no: 0, name: "MB" },
    { no: 1, name: "DB" },
    { no: 2, name: "P1" },
    { no: 3, name: "P2" },
    { no: 4, name: "P" }
  ]
);
const CBCTEndoCrossSectionMetadataVertical_CanalCurvatureTuple = proto3.makeMessageType(
  "com.diagnocat.model.CBCTEndoCrossSectionMetadataVertical.CanalCurvatureTuple",
  () => [
    {
      no: 1,
      name: "V1",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "V2",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "V3",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ],
  { localName: "CBCTEndoCrossSectionMetadataVertical_CanalCurvatureTuple" }
);
const AssetContent_Generated_CBCT_Endo_Guide = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Endo_Guide",
  () => [
    { no: 1, name: "Guide", kind: "message", T: File },
    { no: 2, name: "Section", kind: "enum", T: proto3.getEnumType(AssetContent_Generated_CBCT_Endo_ReportSection) },
    {
      no: 3,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 4, name: "Orientation", kind: "enum", T: proto3.getEnumType(CBCT_SectionOrientation) }
  ]
);

const AssetContent_Generated_CBCT_Implant_Panorama = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Implant_Panorama",
  () => [
    { no: 1, name: "Panorama", kind: "message", T: File },
    {
      no: 2,
      name: "Annotated",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 101, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Generated_CBCT_Implant_Slice = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Implant_Slice",
  () => [
    { no: 1, name: "Slice", kind: "message", T: File },
    {
      no: 5,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 6,
      name: "TotalImages",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 10, name: "RightSideOrient", kind: "enum", T: proto3.getEnumType(CBCT_SliceOrientation) },
    { no: 11, name: "LeftSideOrient", kind: "enum", T: proto3.getEnumType(CBCT_SliceOrientation) }
  ]
);

const AssetContent_Generated_CBCT_Molar_Panorama = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Molar_Panorama",
  () => [
    { no: 1, name: "Panorama", kind: "message", T: File },
    { no: 2, name: "CanalPenetration", kind: "enum", T: proto3.getEnumType(AssetContent_Generated_CBCT_Molar_Panorama_CanalPenetrationType) },
    { no: 101, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Generated_CBCT_Molar_Panorama_CanalPenetrationType = proto3.makeEnum(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Molar_Panorama.CanalPenetrationType",
  [
    { no: 0, name: "NotTouching" },
    { no: 1, name: "Touching" },
    { no: 2, name: "Penetration" }
  ]
);
const AssetContent_Generated_CBCT_Molar_Guide = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Molar_Guide",
  () => [
    { no: 1, name: "Guide", kind: "message", T: File },
    { no: 2, name: "Section", kind: "enum", T: proto3.getEnumType(CBCT_SectionOrientation) },
    {
      no: 11,
      name: "SliceStep",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 12,
      name: "SliceThickness",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const AssetContent_Generated_CBCT_Molar_CrossSection = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Molar_CrossSection",
  () => [
    { no: 1, name: "CrossSection", kind: "message", T: File },
    { no: 2, name: "Section", kind: "enum", T: proto3.getEnumType(CBCT_SectionOrientation) },
    {
      no: 3,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 11, name: "RightSideOrient", kind: "enum", T: proto3.getEnumType(CBCT_SliceOrientation) },
    { no: 12, name: "LeftSideOrient", kind: "enum", T: proto3.getEnumType(CBCT_SliceOrientation) }
  ]
);

const CBCT_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.CBCT_Anatomy",
  [
    { no: 0, name: "CBCT_Anatomy_InvalidValue" },
    { no: 1, name: "CBCT_Anatomy_Airways" },
    { no: 2, name: "CBCT_Anatomy_Cranium" },
    { no: 3, name: "CBCT_Anatomy_SoftTissue" },
    { no: 4, name: "CBCT_Anatomy_IncisiveCanal" },
    { no: 5, name: "CBCT_Anatomy_Mandible" },
    { no: 6, name: "CBCT_Anatomy_Maxilla" },
    { no: 7, name: "CBCT_Anatomy_MandibularCanal" },
    { no: 8, name: "CBCT_Anatomy_Sinuses" }
  ]
);
const CBCT_Tooth_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.CBCT_Tooth_Anatomy",
  [
    { no: 0, name: "CBCT_Tooth_Anatomy_InvalidValue" },
    { no: 1, name: "CBCT_Tooth_Anatomy_Pulp" },
    { no: 2, name: "CBCT_Tooth_Anatomy_Dentin" },
    { no: 3, name: "CBCT_Tooth_Anatomy_Enamel" },
    { no: 4, name: "CBCT_Tooth_Anatomy_Root" }
  ]
);

const CBCT_Segmented_Tooth_Condition = proto3.makeEnum(
  "com.diagnocat.model.CBCT_Segmented_Tooth_Condition",
  [
    { no: 0, name: "CBCT_Tooth_Condition_InvalidValue" },
    { no: 1, name: "CBCT_Tooth_Condition_CanalFilling" },
    { no: 2, name: "CBCT_Tooth_Condition_ArtificialCrown" },
    { no: 3, name: "CBCT_Tooth_Condition_Filling" },
    { no: 4, name: "CBCT_Tooth_Condition_IndirectRestoration" },
    { no: 5, name: "CBCT_Tooth_Condition_Abfraction" },
    { no: 6, name: "CBCT_Tooth_Condition_CariesSigns" }
  ]
);
const AssetContent_Generated_Mesh_Model = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Mesh_Model",
  () => [
    {
      no: 1,
      name: "SegmentID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Mesh", kind: "message", T: File },
    {
      no: 3,
      name: "CBCTTargetAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 11, name: "CBCTTooth", kind: "message", T: ToothNumeration, oneof: "Structure" },
    { no: 12, name: "IOSTooth", kind: "message", T: ToothNumeration, oneof: "Structure" },
    { no: 13, name: "FusionTooth", kind: "message", T: ToothNumeration, oneof: "Structure" },
    { no: 14, name: "IOSAnatomy", kind: "enum", T: proto3.getEnumType(IOS_Anatomy), oneof: "Structure" },
    { no: 15, name: "CBCTAnatomy", kind: "enum", T: proto3.getEnumType(CBCT_Anatomy), oneof: "Structure" },
    { no: 16, name: "CBCTToothPeriapicalRadiolucency", kind: "message", T: AssetContent_Generated_Mesh_Model_CBCT_Tooth_PeriapicalRadiolucency, oneof: "Structure" },
    { no: 17, name: "ArtificialCrown", kind: "message", T: ToothNumeration, oneof: "Structure" },
    { no: 21, name: "CBCTToothAnatomy", kind: "message", T: AssetContent_Generated_Mesh_Model_CBCT_Tooth_Anatomy, oneof: "Structure" },
    { no: 22, name: "CBCTToothCondition", kind: "message", T: AssetContent_Generated_Mesh_Model_CBCT_Tooth_Condition, oneof: "Structure" },
    { no: 1e4, name: "ToothNumerationWithSurfaceAttributes", kind: "message", T: File },
    { no: 10001, name: "FusedModalityAttributes", kind: "message", T: File }
  ]
);
const AssetContent_Generated_Mesh_Model_CBCT_Tooth_PeriapicalRadiolucency = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Mesh_Model.CBCT_Tooth_PeriapicalRadiolucency",
  () => [
    { no: 1, name: "Numeration", kind: "message", T: ToothNumeration },
    {
      no: 2,
      name: "LesionIndex",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    }
  ],
  { localName: "AssetContent_Generated_Mesh_Model_CBCT_Tooth_PeriapicalRadiolucency" }
);
const AssetContent_Generated_Mesh_Model_CBCT_Tooth_Anatomy = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Mesh_Model.CBCT_Tooth_Anatomy",
  () => [
    { no: 1, name: "Numeration", kind: "message", T: ToothNumeration },
    { no: 2, name: "Anatomy", kind: "enum", T: proto3.getEnumType(CBCT_Tooth_Anatomy) },
    { no: 3, name: "RootType", kind: "enum", T: proto3.getEnumType(CBCT_Tooth_RootType), opt: true }
  ],
  { localName: "AssetContent_Generated_Mesh_Model_CBCT_Tooth_Anatomy" }
);
const AssetContent_Generated_Mesh_Model_CBCT_Tooth_Condition = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Mesh_Model.CBCT_Tooth_Condition",
  () => [
    { no: 1, name: "Numeration", kind: "message", T: ToothNumeration },
    { no: 2, name: "Condition", kind: "enum", T: proto3.getEnumType(CBCT_Segmented_Tooth_Condition) }
  ],
  { localName: "AssetContent_Generated_Mesh_Model_CBCT_Tooth_Condition" }
);

const ConditionCode = proto3.makeEnum(
  "com.diagnocat.model.ConditionCode",
  [
    { no: 0, name: "ConditionCodeInvalidValue" },
    { no: 1e3, name: "CodeTooth" },
    { no: 1001, name: "Missing" },
    { no: 1003, name: "Implant" },
    { no: 1004, name: "Pontic" },
    { no: 1057, name: "RootFragment" },
    { no: 1002, name: "PartiallyVisible" },
    { no: 1007, name: "Filling" },
    { no: 1008, name: "Hypercementosis" },
    { no: 1009, name: "Impaction" },
    { no: 1010, name: "Roots1" },
    { no: 1011, name: "Roots2" },
    { no: 1012, name: "Roots3" },
    { no: 1013, name: "Roots4" },
    { no: 1014, name: "Canals1" },
    { no: 1015, name: "Canals2" },
    { no: 1016, name: "Canals3" },
    { no: 1017, name: "Canals4" },
    { no: 1018, name: "Canals5" },
    { no: 1019, name: "ArtificialCrown" },
    { no: 1021, name: "CariesSigns" },
    { no: 1023, name: "EctopicEruption" },
    { no: 1024, name: "EndoTreated" },
    { no: 1026, name: "VerticalRootFracture" },
    { no: 1033, name: "CanalObliteration" },
    { no: 1036, name: "InternalResorption" },
    { no: 1037, name: "OrthodonticAppliance" },
    { no: 1038, name: "PeriapicalRadiopacity" },
    { no: 1202, name: "PeriapicalRadiolucency" },
    { no: 1039, name: "PDLSpaceWidening_InPeriapicalRegion" },
    { no: 1042, name: "PDLSpaceWidening_AlongTheRoot" },
    { no: 1043, name: "FurcationLesion" },
    { no: 1044, name: "PulpStone" },
    { no: 1046, name: "ExternalResorption" },
    { no: 1051, name: "HorizontalRootFracture" },
    { no: 1052, name: "CrownFracture" },
    { no: 1053, name: "Abfraction" },
    { no: 1060, name: "Attrition" },
    { no: 1065, name: "PeriImplantitis" },
    { no: 1068, name: "DentalCalculus" },
    { no: 1075, name: "HorizontalDisplacement" },
    { no: 1077, name: "Overeruption" },
    { no: 1083, name: "OpenMargin" },
    { no: 1084, name: "Overhang" },
    { no: 1086, name: "LackOfInterproximalContact" },
    { no: 1087, name: "SecondaryCaries" },
    { no: 1022, name: "CrownDefectOver50pct" },
    { no: 1092, name: "PeriodontalBoneLoss" },
    { no: 1148, name: "EndodonticAccess" },
    { no: 1149, name: "Pulpotomy" },
    { no: 1150, name: "Clamp" },
    { no: 1151, name: "ForeignBody" },
    { no: 1152, name: "EndodonticInstrument" },
    { no: 1181, name: "IndirectRestoration" },
    { no: 1182, name: "RestorationFracture" },
    { no: 1185, name: "LateralRadiolucency" },
    { no: 1186, name: "SingsOfCommunicationWithMaxillarySinus" },
    { no: 1187, name: "SignsOfInferiorAlveolarCanalInvolvement" },
    { no: 1189, name: "Apicoectomy" },
    { no: 1197, name: "ToothGerm" },
    { no: 1200, name: "SinusAbnormality" },
    { no: 1201, name: "BoneStructureDisorder" },
    { no: 1204, name: "ToothStump" },
    { no: 1205, name: "VoidsInTheFilling" },
    { no: 1206, name: "Extrusion" },
    { no: 1207, name: "Intrusion" },
    { no: 1208, name: "LateralLuxation" },
    { no: 1218, name: "HealingAbutment" },
    { no: 1223, name: "CervicalResorption" },
    { no: 1224, name: "PreEruptiveResorption" },
    { no: 1235, name: "MucosalThickening" },
    { no: 1240, name: "MaxFaxArtifact" },
    { no: 1241, name: "MaxFaxBoneDisruption" },
    { no: 1242, name: "MaxFaxMetalConstruction" },
    { no: 1243, name: "MaxFaxBoneAugmentation" },
    { no: 1244, name: "MaxFaxSoftTissueThickening" },
    { no: 1245, name: "EndoAntralSyndrome" },
    { no: 1246, name: "OroantralCommunication" },
    { no: 1247, name: "OstialObstruction" },
    { no: 1248, name: "MarginalDiscrepancy" },
    { no: 1250, name: "FDA_Caries" },
    { no: 1251, name: "FDA_RestorativeImperfections" },
    { no: 5e3, name: "Child_DefectDepth_NotDefined" },
    { no: 5001, name: "Child_DefectDepth_Initial" },
    { no: 5002, name: "Child_DefectDepth_Enamel" },
    { no: 5003, name: "Child_DefectDepth_Dentin" },
    { no: 5004, name: "Child_DefectDepth_WithPulpExposure" },
    { no: 5005, name: "Child_DefectDepth_Root" },
    { no: 5006, name: "Child_DefectDepth_CrownRoot" },
    { no: 5050, name: "Child_DefectSurface_NotDefined" },
    { no: 5051, name: "Child_DefectSurface_Mesial" },
    { no: 5052, name: "Child_DefectSurface_Distal" },
    { no: 5053, name: "Child_DefectSurface_Lingual" },
    { no: 5054, name: "Child_DefectSurface_Buccal" },
    { no: 5055, name: "Child_DefectSurface_Occlusial" },
    { no: 5056, name: "Child_DefectSurface_Vestibular" },
    { no: 5057, name: "Child_DefectSurface_IncisalEdge" },
    { no: 5200, name: "Child_PeriodontalBoneLoss_Severity_Mild" },
    { no: 5201, name: "Child_PeriodontalBoneLoss_Severity_Moderate" },
    { no: 5202, name: "Child_PeriodontalBoneLoss_Severity_Severe" },
    { no: 5210, name: "Child_PeriodontalBoneLoss_Type_Vertical" },
    { no: 5211, name: "Child_PeriodontalBoneLoss_Type_Horizontal" },
    { no: 5212, name: "Child_PeriodontalBoneLoss_Type_Mixed" },
    { no: 5250, name: "Child_Filling_BuildUp" },
    { no: 5300, name: "Child_MaxFaxBoneStructureDisorder_Type_Hyperdense" },
    { no: 5301, name: "Child_MaxFaxBoneStructureDisorder_Type_Hypodense" },
    { no: 5302, name: "Child_MaxFaxBoneStructureDisorder_Type_Mix" },
    { no: 5350, name: "Child_Impaction_Subtype_CompleteBony" },
    { no: 5351, name: "Child_Impaction_Subtype_PartiallyBony" },
    { no: 5400, name: "Child_EndoTreated_Obturation_AdequateObturation" },
    { no: 5401, name: "Child_EndoTreated_Obturation_ShortFilling" },
    { no: 5402, name: "Child_EndoTreated_Obturation_Overfilling" },
    { no: 5403, name: "Child_EndoTreated_Obturation_MissedCanal" },
    { no: 5404, name: "FDA_Child_EndoTreated_CanalFilling" },
    { no: 5410, name: "Child_EndoTreated_Quality_AdequateDensity" },
    { no: 5411, name: "Child_EndoTreated_Quality_VoidsPresentInTheRootFilling" },
    { no: 5412, name: "Child_EndoTreated_Quality_VoidsInThePostArea" },
    { no: 5420, name: "Child_EndoTreated_PostAndCore_CastPostAndCore" },
    { no: 5421, name: "Child_EndoTreated_PostAndCore_FiberglassPost" },
    { no: 5422, name: "Child_EndoTreated_PostAndCore_MetalPost" },
    { no: 5423, name: "Child_EndoTreated_PostAndCore_Post" },
    { no: 5424, name: "FDA_Child_EndoTreated_PostAndCore" },
    { no: 5430, name: "Child_EndoTreated_Complications_RootPerforation" },
    { no: 5431, name: "Child_EndoTreated_Complications_SeparatedInstrumentInTheRootCanal" },
    { no: 5450, name: "Child_MucosalThickening_Type_Focal" },
    { no: 5451, name: "Child_MucosalThickening_Type_Diffused" },
    { no: 5452, name: "Child_MucosalThickening_Type_CompleteOrPartialObstruction" }
  ]
);

const Mesh_FaceSegmentation_Instance_PackStrategy = proto3.makeEnum(
  "com.diagnocat.model.Mesh_FaceSegmentation_Instance_PackStrategy",
  [
    { no: 0, name: "Mesh_FaceSegmentation_Instance_PackStrategy_InvalidValue" },
    { no: 1, name: "Mesh_FaceSegmentation_Instance_PackStrategy_UInt32" },
    { no: 2, name: "Mesh_FaceSegmentation_Instance_PackStrategy_RangeUInt32" }
  ]
);
const AssetContent_Generated_Mesh_FaceSegmentation = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Mesh_FaceSegmentation",
  () => [
    {
      no: 1,
      name: "TargetAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "FaceIndices", kind: "message", T: File },
    { no: 3, name: "Instances", kind: "message", T: Mesh_FaceSegmentation_Instance, repeated: true }
  ]
);
const Mesh_FaceSegmentation_Instance = proto3.makeMessageType(
  "com.diagnocat.model.Mesh_FaceSegmentation_Instance",
  () => [
    { no: 1, name: "FaceLocations", kind: "message", T: Mesh_FaceSegmentation_Instance_FaceLocation, repeated: true },
    { no: 2, name: "Tooth", kind: "message", T: ToothNumeration, oneof: "Type" },
    { no: 3, name: "ToothCondition", kind: "message", T: Mesh_FaceSegmentation_ToothConditionInstance, oneof: "Type" },
    { no: 4, name: "MaxFaxAnatomy", kind: "enum", T: proto3.getEnumType(IOS_Anatomy), oneof: "Type" }
  ]
);
const Mesh_FaceSegmentation_ToothConditionInstance = proto3.makeMessageType(
  "com.diagnocat.model.Mesh_FaceSegmentation_ToothConditionInstance",
  () => [
    { no: 1, name: "Tooth", kind: "message", T: ToothNumeration },
    { no: 2, name: "Code", kind: "enum", T: proto3.getEnumType(ConditionCode) }
  ]
);
const Mesh_FaceSegmentation_Instance_FaceLocation = proto3.makeMessageType(
  "com.diagnocat.model.Mesh_FaceSegmentation_Instance_FaceLocation",
  () => [
    {
      no: 1,
      name: "TargetGeometryIndex",
      kind: "scalar",
      T: 5
      /* ScalarType.INT32 */
    },
    { no: 2, name: "PackStrategy", kind: "enum", T: proto3.getEnumType(Mesh_FaceSegmentation_Instance_PackStrategy) },
    {
      no: 3,
      name: "StartByte",
      kind: "scalar",
      T: 4
      /* ScalarType.UINT64 */
    },
    {
      no: 4,
      name: "ContentSize",
      kind: "scalar",
      T: 4
      /* ScalarType.UINT64 */
    }
  ]
);

const AssetContent_Generated_CBCTTile = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCTTile",
  () => [
    { no: 1, name: "Tile", kind: "message", T: File },
    { no: 2, name: "Geometry", kind: "message", T: CBCTSeriesGeometryData }
  ]
);
const AssetContent_Report_Studio_ApplicationStateSnapshot = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Report_Studio_ApplicationStateSnapshot",
  () => [
    { no: 1, name: "ApplicationStateSnapshot", kind: "message", T: File }
  ]
);
const AssetContent_Report_Studio_Geometry = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Report_Studio_Geometry",
  () => [
    { no: 1, name: "Geometry", kind: "message", T: File }
  ]
);

const AssetContent_Generated_Studio_CBCTDownscaled = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Studio_CBCTDownscaled",
  () => [
    { no: 1, name: "Volume", kind: "message", T: File },
    {
      no: 2,
      name: "DownscaleFactor",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    { no: 3, name: "Geometry", kind: "message", T: CBCTSeriesGeometryData }
  ]
);
const AssetContent_Generated_Studio_PanoramaReformatGeneral = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Studio_PanoramaReformatGeneral",
  () => [
    { no: 1, name: "PanoramaSingle", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image },
    { no: 3, name: "Geometry", kind: "message", T: AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry }
  ]
);
const AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry",
  () => [
    { no: 1, name: "Points", kind: "message", T: FloatPoint3D, repeated: true },
    { no: 2, name: "Up", kind: "message", T: FloatPoint3D },
    { no: 3, name: "ColorProfile", kind: "message", T: StudioColorProfile },
    { no: 4, name: "Shape", kind: "message", T: AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry_Geometry_Shape },
    {
      no: 5,
      name: "DepthStep",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry_Geometry_Shape = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry.Geometry_Shape",
  () => [
    {
      no: 1,
      name: "Width",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "Height",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "Depth",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ],
  { localName: "AssetContent_Generated_Studio_PanoramaReformatGeneral_Geometry_Geometry_Shape" }
);

const AssetContent_Report_UserUploaded_ToothSlice = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Report_UserUploaded_ToothSlice",
  () => [
    { no: 1, name: "Slice", kind: "message", T: File }
  ]
);
const AssetContent_Report_UserUploaded_MPRPlane = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Report_UserUploaded_MPRPlane",
  () => [
    { no: 1, name: "MPRPlane", kind: "message", T: File }
  ]
);

const AssetType = proto3.makeEnum(
  "com.diagnocat.model.AssetType",
  [
    { no: 0, name: "AssetType_InvalidValue" },
    { no: 1, name: "AssetType_User_Avatar" },
    { no: 2, name: "AssetType_User_Signature" },
    { no: 4, name: "AssetType_ChatUpload" },
    { no: 11, name: "AssetType_Organization_Logo" },
    { no: 12, name: "AssetType_Organization_Stamp" },
    { no: 13, name: "AssetType_OrganizationStaff_Stamp" },
    { no: 21, name: "AssetType_Document" },
    { no: 22, name: "AssetType_Patient_UploadedImage" },
    { no: 31, name: "AssetType_Study_CBCT" },
    { no: 36, name: "AssetType_Study_CBCT_DCCT" },
    { no: 32, name: "AssetType_Study_IntraoralXRay" },
    { no: 33, name: "AssetType_Study_PanoramicXRay" },
    { no: 34, name: "AssetType_Study_IOS_Meshes" },
    { no: 35, name: "AssetType_Study_DentalPhoto" },
    { no: 37, name: "AssetType_Study_DentalVideo" },
    { no: 40, name: "AssetType_Report_Generated_IOXRayImage" },
    { no: 41, name: "AssetType_Report_Generated_CBCT_ToothPathologySlice" },
    { no: 51, name: "AssetType_Report_Generated_CBCT_GP_PanoramaReformatSplit" },
    { no: 52, name: "AssetType_Report_Generated_CBCT_GP_PanoramaReformatGeneral" },
    { no: 53, name: "AssetType_Report_Generated_CBCT_GP_ToothSlice" },
    { no: 54, name: "AssetType_Report_Generated_CBCT_GP_MPRSubVolume" },
    { no: 55, name: "AssetType_Report_Generated_CBCT_GP_PeriodontiumImage" },
    { no: 61, name: "AssetType_Report_Generated_CBCT_Endo_PanoramaSplit" },
    { no: 62, name: "AssetType_Report_Generated_CBCT_Endo_CrossSection" },
    { no: 63, name: "AssetType_Report_Generated_CBCT_Endo_Guide" },
    { no: 71, name: "AssetType_Report_Generated_CBCT_Implant_Panorama" },
    { no: 72, name: "AssetType_Report_Generated_CBCT_Implant_Slice" },
    { no: 81, name: "AssetType_Report_Generated_CBCT_Molar_Panorama" },
    { no: 82, name: "AssetType_Report_Generated_CBCT_Molar_Guide" },
    { no: 83, name: "AssetType_Report_Generated_CBCT_Molar_CrossSection" },
    { no: 91, name: "AssetType_Report_Generated_CBCT_Ortho_PDF" },
    { no: 111, name: "AssetType_Report_Generated_Mesh_Model" },
    { no: 112, name: "AssetType_Report_Generated_CBCTTile" },
    { no: 113, name: "AssetType_Report_Generated_Studio_CBCTDownscaled" },
    { no: 114, name: "AssetType_Report_Generated_Studio_PanoramaReformatGeneral" },
    { no: 115, name: "AssetType_Report_Generated_Mesh_FaceSegmentation" },
    { no: 121, name: "AssetType_Report_Uploaded_ToothSlice" },
    { no: 122, name: "AssetType_Report_Uploaded_MPRPlane" },
    { no: 131, name: "AssetType_Report_Uploaded_Studio_ApplicationStateSnapshot" },
    { no: 132, name: "AssetType_Report_Uploaded_Studio_Geometry" },
    { no: 133, name: "AssetType_Report_Rendered_DisplayImage" },
    { no: 134, name: "AssetType_Report_Rendered_Document" },
    { no: 1e4, name: "AssetType_Report_Archive" }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.Asset",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "Type", kind: "enum", T: proto3.getEnumType(AssetType) },
    { no: 101, name: "User", kind: "message", T: UserAsset, oneof: "Kind" },
    { no: 109, name: "ChatUpload", kind: "message", T: ChatUploadAsset, oneof: "Kind" },
    { no: 102, name: "Organization", kind: "message", T: OrganizationAsset, oneof: "Kind" },
    { no: 107, name: "OrganizationStaff", kind: "message", T: OrganizationStaffAsset, oneof: "Kind" },
    { no: 103, name: "Patient", kind: "message", T: PatientAsset, oneof: "Kind" },
    { no: 104, name: "Study", kind: "message", T: StudyAsset, oneof: "Kind" },
    { no: 105, name: "GeneratedReport", kind: "message", T: GeneratedReportAsset, oneof: "Kind" },
    { no: 106, name: "Report", kind: "message", T: ReportAsset, oneof: "Kind" },
    { no: 1e3, name: "Attributes", kind: "message", T: Attribute, repeated: true },
    { no: 10030, name: "Created", kind: "message", T: Created },
    { no: 10040, name: "Revision", kind: "message", T: Revision },
    { no: 10050, name: "Deleted", kind: "message", T: Deleted }
  ]
);
const UserAsset = proto3.makeMessageType(
  "com.diagnocat.model.UserAsset",
  () => [
    {
      no: 1,
      name: "UserID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 101, name: "Avatar", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" },
    { no: 102, name: "Signature", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" }
  ]
);
const ChatUploadAsset = proto3.makeMessageType(
  "com.diagnocat.model.ChatUploadAsset",
  () => [
    {
      no: 1,
      name: "UserID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "ThreadID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "PatientID", kind: "scalar", T: 9, opt: true },
    {
      no: 4,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 101, name: "File", kind: "message", T: AssetContent_ChatUploadFile, oneof: "Content" }
  ]
);
const AssetContent_ChatUploadFile = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_ChatUploadFile",
  () => [
    { no: 1, name: "File", kind: "message", T: File },
    {
      no: 2,
      name: "SHA256",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const OrganizationAsset = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationAsset",
  () => [
    {
      no: 1,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "UserID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 101, name: "Logo", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" },
    { no: 102, name: "Stamp", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" }
  ]
);
const OrganizationStaffAsset = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationStaffAsset",
  () => [
    {
      no: 1,
      name: "StaffID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "UserID", kind: "scalar", T: 9, opt: true },
    { no: 101, name: "Stamp", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" }
  ]
);
const PatientAsset = proto3.makeMessageType(
  "com.diagnocat.model.PatientAsset",
  () => [
    {
      no: 1,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 101, name: "Document", kind: "message", T: AssetContent_Document, oneof: "Content" },
    { no: 102, name: "UploadedImage", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" }
  ]
);
const StudyAsset = proto3.makeMessageType(
  "com.diagnocat.model.StudyAsset",
  () => [
    {
      no: 1,
      name: "StudyID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 4, name: "ExternalID", kind: "scalar", T: 9, opt: true },
    {
      no: 5,
      name: "SourceAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 11, name: "MedicalImageFeatures", kind: "message", T: MedicalImageFeatures },
    { no: 101, name: "CBCT", kind: "message", T: AssetContent_Study_CBCT, oneof: "Content" },
    { no: 107, name: "CBCT_DCCT", kind: "message", T: AssetContent_Study_CBCT_DCCT, oneof: "Content" },
    { no: 102, name: "Panoramic_XRay", kind: "message", T: AssetContent_Study_Panoramic_XRay, oneof: "Content" },
    { no: 103, name: "Intraoral_XRay", kind: "message", T: AssetContent_Study_Intraoral_XRay, oneof: "Content" },
    { no: 104, name: "IOS_Meshes", kind: "message", T: AssetContent_Study_IOS_Meshes, oneof: "Content" },
    { no: 105, name: "Dental_Photo", kind: "message", T: AssetContent_Study_Dental_Photo, oneof: "Content" },
    { no: 108, name: "Dental_Video", kind: "message", T: AssetContent_Study_Dental_Video, oneof: "Content" },
    { no: 106, name: "Mesh_Model", kind: "message", T: AssetContent_Study_MeshModel, oneof: "Content" }
  ]
);
const GeneratedReportAsset = proto3.makeMessageType(
  "com.diagnocat.model.GeneratedReportAsset",
  () => [
    { no: 1, name: "Belonging", kind: "message", T: ReportAssetBelonging },
    { no: 11, name: "MedicalImageFeatures", kind: "message", T: MedicalImageFeatures },
    { no: 50, name: "LegacyPayloadOnly", kind: "message", T: EmptyPayload, oneof: "Content" },
    { no: 51, name: "IOXRay_Image", kind: "message", T: AssetContent_Generated_IOXRay_Image, oneof: "Content" },
    { no: 101, name: "CBCT_GP_PanoramaReformatSplit", kind: "message", T: AssetContent_Generated_CBCT_GP_PanoramaReformatSplit, oneof: "Content" },
    { no: 102, name: "CBCT_GP_PanoramaReformatGeneral", kind: "message", T: AssetContent_Generated_CBCT_GP_PanoramaReformatGeneral, oneof: "Content" },
    { no: 103, name: "CBCT_GP_ToothSlice", kind: "message", T: AssetContent_Generated_CBCT_GP_ToothSlice, oneof: "Content" },
    { no: 104, name: "CBCT_GP_MPR_SubVolume", kind: "message", T: AssetContent_Generated_CBCT_GP_MPR_SubVolume, oneof: "Content" },
    { no: 105, name: "CBCT_GP_PeriodontiumImage", kind: "message", T: AssetContent_Generated_CBCT_GP_PeriodontiumImage, oneof: "Content" },
    { no: 111, name: "CBCT_Endo_PanoramaSplit", kind: "message", T: AssetContent_Generated_CBCT_Endo_PanoramaSplit, oneof: "Content" },
    { no: 112, name: "CBCT_Endo_CrossSection", kind: "message", T: AssetContent_Generated_CBCT_Endo_CrossSection, oneof: "Content" },
    { no: 113, name: "CBCT_Endo_Guide", kind: "message", T: AssetContent_Generated_CBCT_Endo_Guide, oneof: "Content" },
    { no: 121, name: "CBCT_Implant_Panorama", kind: "message", T: AssetContent_Generated_CBCT_Implant_Panorama, oneof: "Content" },
    { no: 122, name: "CBCT_Implant_Slice", kind: "message", T: AssetContent_Generated_CBCT_Implant_Slice, oneof: "Content" },
    { no: 131, name: "CBCT_Molar_Panorama", kind: "message", T: AssetContent_Generated_CBCT_Molar_Panorama, oneof: "Content" },
    { no: 132, name: "CBCT_Molar_Guide", kind: "message", T: AssetContent_Generated_CBCT_Molar_Guide, oneof: "Content" },
    { no: 133, name: "CBCT_Molar_CrossSection", kind: "message", T: AssetContent_Generated_CBCT_Molar_CrossSection, oneof: "Content" },
    { no: 141, name: "CBCT_Ortho_PDF", kind: "message", T: AssetContent_Generated_CBCT_Ortho_PDF, oneof: "Content" },
    { no: 161, name: "Mesh_Model", kind: "message", T: AssetContent_Generated_Mesh_Model, oneof: "Content" },
    { no: 162, name: "Mesh_FaceSegmentation", kind: "message", T: AssetContent_Generated_Mesh_FaceSegmentation, oneof: "Content" },
    { no: 171, name: "CBCT_ToothPathologySlice", kind: "message", T: AssetContent_Generated_CBCT_ToothPathologySlice, oneof: "Content" },
    { no: 181, name: "CBCTTile", kind: "message", T: AssetContent_Generated_CBCTTile, oneof: "Content" },
    { no: 182, name: "Studio_CBCTDownscaled", kind: "message", T: AssetContent_Generated_Studio_CBCTDownscaled, oneof: "Content" },
    { no: 183, name: "Studio_PanoramaReformatGeneral", kind: "message", T: AssetContent_Generated_Studio_PanoramaReformatGeneral, oneof: "Content" },
    { no: 1e3, name: "LegacyPayloads", kind: "message", T: ReportLegacyPayload, repeated: true }
  ]
);
const ReportAsset = proto3.makeMessageType(
  "com.diagnocat.model.ReportAsset",
  () => [
    { no: 1, name: "Belonging", kind: "message", T: ReportAssetBelonging },
    { no: 11, name: "MedicalImageFeatures", kind: "message", T: MedicalImageFeatures },
    { no: 101, name: "UserUploadedToothSlice", kind: "message", T: AssetContent_Report_UserUploaded_ToothSlice, oneof: "Content" },
    { no: 102, name: "UserUploadedMPRPlane", kind: "message", T: AssetContent_Report_UserUploaded_MPRPlane, oneof: "Content" },
    { no: 111, name: "Studio_ApplicationStateSnapshot", kind: "message", T: AssetContent_Report_Studio_ApplicationStateSnapshot, oneof: "Content" },
    { no: 112, name: "Studio_Geometry", kind: "message", T: AssetContent_Report_Studio_Geometry, oneof: "Content" },
    { no: 113, name: "Rendered_DisplayImage", kind: "message", T: AssetContent_SimpleImage, oneof: "Content" },
    { no: 114, name: "Rendered_Document", kind: "message", T: AssetContent_Rendered_Document, oneof: "Content" }
  ]
);
const ReportAssetBelonging = proto3.makeMessageType(
  "com.diagnocat.model.ReportAssetBelonging",
  () => [
    {
      no: 1,
      name: "ReportID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "SourceStudyIDs", kind: "scalar", T: 9, repeated: true },
    {
      no: 3,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "ToothID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const AssetContent_Generated_CBCT_ToothPathologySlice = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_ToothPathologySlice",
  () => [
    { no: 1, name: "Slice", kind: "message", T: File },
    { no: 2, name: "Orientation", kind: "enum", T: proto3.getEnumType(CBCT_SectionOrientation) },
    { no: 3, name: "Numeration", kind: "message", T: ToothNumeration },
    { no: 4, name: "Pathology", kind: "enum", T: proto3.getEnumType(AssetContent_Generated_CBCT_ToothPathologySlice_PathologyType) },
    {
      no: 5,
      name: "PathologyComponent",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 6, name: "Mask", kind: "message", T: ClosedPolygon },
    {
      no: 8,
      name: "Scale",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const AssetContent_Generated_CBCT_ToothPathologySlice_PathologyType = proto3.makeEnum(
  "com.diagnocat.model.AssetContent_Generated_CBCT_ToothPathologySlice.PathologyType",
  [
    { no: 0, name: "Caries" },
    { no: 1, name: "PeriapicalLesion" }
  ]
);
const AssetContent_SimpleImage = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_SimpleImage",
  () => [
    { no: 1, name: "Original", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Generated_CBCT_Ortho_PDF = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_CBCT_Ortho_PDF",
  () => [
    { no: 1, name: "PDF", kind: "message", T: File }
  ]
);
const AssetContent_Generated_IOXRay_Image = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Generated_IOXRay_Image",
  () => [
    { no: 1, name: "Image", kind: "message", T: File },
    { no: 2, name: "Derived", kind: "message", T: DerivedData_Image }
  ]
);
const AssetContent_Rendered_Document = proto3.makeMessageType(
  "com.diagnocat.model.AssetContent_Rendered_Document",
  () => [
    { no: 1, name: "Document", kind: "message", T: File }
  ]
);

const SessionErrorCode = proto3.makeEnum(
  "com.diagnocat.model.SessionErrorCode",
  [
    { no: 0, name: "SessionErrorUnknown" },
    { no: 1, name: "SessionErrorCodeCancelled" },
    { no: 2, name: "SessionErrorCodeUnsupportedMIME" },
    { no: 3, name: "SessionErrorCodeProcessing" },
    { no: 4, name: "SessionErrorClientUpload" }
  ]
);
const UploadSessionStatus = proto3.makeEnum(
  "com.diagnocat.model.UploadSessionStatus",
  [
    { no: 0, name: "UploadSessionStatusInProgress" },
    { no: 1, name: "UploadSessionStatusToBeClosed" },
    { no: 2, name: "UploadSessionStatusClosing" },
    { no: 3, name: "UploadSessionStatusSuccessful" },
    { no: 4, name: "UploadSessionStatusCancelled" },
    { no: 5, name: "UploadSessionStatusFailed" }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.UploadSession",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "AssetType", kind: "enum", T: proto3.getEnumType(AssetType) },
    { no: 3, name: "Files", kind: "message", T: UploadSessionFile, repeated: true },
    { no: 10, name: "Target", kind: "message", T: UploadSessionTarget },
    { no: 22, name: "Status", kind: "enum", T: proto3.getEnumType(UploadSessionStatus) },
    { no: 23, name: "Error", kind: "message", T: UploadSessionError, opt: true },
    { no: 24, name: "LastKeepAliveAt", kind: "message", T: Timestamp },
    { no: 10030, name: "Created", kind: "message", T: Created }
  ]
);
const UploadSessionError = proto3.makeMessageType(
  "com.diagnocat.model.UploadSessionError",
  () => [
    { no: 1, name: "Code", kind: "enum", T: proto3.getEnumType(SessionErrorCode) },
    {
      no: 2,
      name: "ErrMessage",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "FileID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "By",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "At",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    { no: 6, name: "Via", kind: "enum", T: proto3.getEnumType(CalledVia) }
  ]
);
const UploadSessionTarget = proto3.makeMessageType(
  "com.diagnocat.model.UploadSessionTarget",
  () => [
    {
      no: 10,
      name: "UserID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 11,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 12,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 13,
      name: "StudyID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 14, name: "GeneratedFromStudyIDs", kind: "scalar", T: 9, repeated: true },
    {
      no: 15,
      name: "ReportID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const UploadSessionFile = proto3.makeMessageType(
  "com.diagnocat.model.UploadSessionFile",
  () => [
    { no: 1, name: "File", kind: "message", T: File },
    {
      no: 2,
      name: "InProcess",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 3,
      name: "Uploaded",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 4, name: "StartedAt", kind: "message", T: Timestamp },
    { no: 6, name: "UploadedAt", kind: "message", T: Timestamp }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.UploadTarget",
  () => [
    {
      no: 1,
      name: "FileID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Path",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "UploadURL",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 4, name: "UploadHeaders", kind: "map", K: 9, V: {
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    } },
    {
      no: 5,
      name: "UploadHeadersExpiresAt",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    }
  ]
);

const StudyPermissions = proto3.makeMessageType(
  "com.diagnocat.model.StudyPermissions",
  () => [
    {
      no: 1,
      name: "CanView",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "CanRequestCBCTGPReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 3,
      name: "CanRequestCBCTEndoReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 4,
      name: "CanRequestCBCTImplantReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 5,
      name: "CanRequestCBCTOrthoSlidesReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 6,
      name: "CanRequestCBCTSegmentationReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 7,
      name: "CanRequestIOXRayReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 8,
      name: "CanRequestPanoReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 9,
      name: "CanRequestDentalPhotoReport",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 11,
      name: "CanDelete",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);

const StudyType = proto3.makeEnum(
  "com.diagnocat.model.StudyType",
  [
    { no: 0, name: "StudyType_InvalidValue" },
    { no: 1, name: "StudyType_CBCT" },
    { no: 2, name: "StudyType_IntraoralXRay" },
    { no: 3, name: "StudyType_PanoramicXRay" },
    { no: 4, name: "StudyType_IOS_Meshes" },
    { no: 5, name: "StudyType_DentalPhoto" }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.Study",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Name",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 9, name: "Time", kind: "message", T: Timestamp },
    {
      no: 4,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 6, name: "Type", kind: "enum", T: proto3.getEnumType(StudyType) },
    { no: 7, name: "AssetIDs", kind: "scalar", T: 9, repeated: true },
    { no: 11, name: "Status", kind: "enum", T: proto3.getEnumType(UploadSessionStatus) },
    { no: 12, name: "UploadSessionStatuses", kind: "message", T: StudyUploadSessionStatus, repeated: true },
    {
      no: 13,
      name: "RawMeshViewer",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 14,
      name: "DisplayAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 1e3, name: "Attributes", kind: "message", T: Attribute, repeated: true },
    { no: 10020, name: "YourPermissions", kind: "message", T: StudyPermissions },
    { no: 10030, name: "Created", kind: "message", T: Created },
    { no: 10040, name: "Revision", kind: "message", T: Revision },
    { no: 10050, name: "Deleted", kind: "message", T: Deleted }
  ]
);
const StudyUploadSessionStatus = proto3.makeMessageType(
  "com.diagnocat.model.StudyUploadSessionStatus",
  () => [
    {
      no: 1,
      name: "UploadSessionID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Status", kind: "enum", T: proto3.getEnumType(UploadSessionStatus) },
    {
      no: 3,
      name: "ErrorMessage",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);

const Report_2D_ImageType = proto3.makeEnum(
  "com.diagnocat.model.Report_2D_ImageType",
  [
    { no: 0, name: "Report_2D_ImageType_InvalidValue" },
    { no: 1, name: "Report_2D_ImageType_IntraoralXRay" },
    { no: 2, name: "Report_2D_ImageType_DentalPhoto" },
    { no: 3, name: "Report_2D_ImageType_PanoramicXRay" },
    { no: 4, name: "Report_2D_ImageType_CBCTSlice" }
  ]
);
const IOXRay_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.IOXRay_Anatomy",
  [
    { no: 0, name: "IOXRay_Anatomy_InvalidValue" },
    { no: 1, name: "IOXRay_Anatomy_AlveolarBone" },
    { no: 2, name: "IOXRay_Anatomy_MaxillarySinus" },
    { no: 3, name: "IOXRay_Anatomy_MandibularCanal" },
    { no: 4, name: "IOXRay_Anatomy_NasalCavity" },
    { no: 5, name: "IOXRay_Anatomy_VestibularBone" },
    { no: 6, name: "IOXRay_Anatomy_OralBone" }
  ]
);
const IOXRay_Tooth_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.IOXRay_Tooth_Anatomy",
  [
    { no: 0, name: "IOXRay_Tooth_Anatomy_InvalidValue" },
    { no: 1, name: "IOXRay_Tooth_Anatomy_Root" },
    { no: 2, name: "IOXRay_Tooth_Anatomy_RootCanal" },
    { no: 3, name: "IOXRay_Tooth_Anatomy_Crown" },
    { no: 4, name: "IOXRay_Tooth_Anatomy_IncisalEdge" },
    { no: 5, name: "IOXRay_Tooth_Anatomy_PulpChamber" },
    { no: 6, name: "IOXRay_Tooth_Anatomy_Enamel" }
  ]
);
const Pano_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.Pano_Anatomy",
  [
    { no: 0, name: "Pano_Anatomy_InvalidValue" },
    { no: 1, name: "Pano_Anatomy_Maxilla" },
    { no: 2, name: "Pano_Anatomy_Mandible" },
    { no: 3, name: "Pano_Anatomy_MaxillarySinus" },
    { no: 4, name: "Pano_Anatomy_MandibularCanal" }
  ]
);
const Pano_Tooth_Anatomy = proto3.makeEnum(
  "com.diagnocat.model.Pano_Tooth_Anatomy",
  [
    { no: 0, name: "Pano_Tooth_Anatomy_InvalidValue" },
    { no: 1, name: "Pano_Tooth_Anatomy_Root" },
    { no: 2, name: "Pano_Tooth_Anatomy_RootCanal" },
    { no: 3, name: "Pano_Tooth_Anatomy_Crown" },
    { no: 4, name: "Pano_Tooth_Anatomy_PulpChamber" },
    { no: 5, name: "Pano_Tooth_Anatomy_Enamel" }
  ]
);
const Report_2D_Image_Meta = proto3.makeMessageType(
  "com.diagnocat.model.Report_2D_Image_Meta",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Type", kind: "enum", T: proto3.getEnumType(Report_2D_ImageType) },
    {
      no: 3,
      name: "StudyAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "GeneratedAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 5, name: "Scale", kind: "message", T: FloatTuple2D },
    {
      no: 6,
      name: "OrientationAngle",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    { no: 11, name: "MedicalImageFeatures", kind: "message", T: MedicalImageFeatures }
  ]
);

const LocalizationType = proto3.makeEnum(
  "com.diagnocat.model.LocalizationType",
  [
    { no: 0, name: "LocalizationTypeInvalidValue" },
    { no: 1, name: "LocalizationType2D" },
    { no: 2, name: "LocalizationType3D" }
  ]
);
const Localization = proto3.makeMessageType(
  "com.diagnocat.model.Localization",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "TargetAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 11, name: "Type", kind: "enum", T: proto3.getEnumType(LocalizationType) },
    { no: 12, name: "Mask", kind: "message", T: ClosedPolygon },
    { no: 13, name: "PolygonalMask", kind: "message", T: ClosedPolygon, repeated: true },
    { no: 14, name: "BBox", kind: "message", T: BBox },
    { no: 15, name: "PeriodontalBoneLossLandmarks", kind: "message", T: LocalizationPBLLandmarks },
    { no: 21, name: "CropMedicalImageFeatures", kind: "message", T: MedicalImageFeatures }
  ]
);
const LocalizationPBLLandmarks = proto3.makeMessageType(
  "com.diagnocat.model.LocalizationPBLLandmarks",
  () => [
    {
      no: 1,
      name: "CementoenamelJunctionLandmarkID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "BoneLevelLandmarkID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);

const MaxFaxAnatomyLocalization = proto3.makeMessageType(
  "com.diagnocat.model.MaxFaxAnatomyLocalization",
  () => [
    { no: 1, name: "Localization", kind: "message", T: Localization },
    { no: 2, name: "IOXRay", kind: "enum", T: proto3.getEnumType(IOXRay_Anatomy), oneof: "Type" },
    { no: 3, name: "Pano", kind: "enum", T: proto3.getEnumType(Pano_Anatomy), oneof: "Type" },
    { no: 4, name: "CBCT", kind: "enum", T: proto3.getEnumType(CBCT_Anatomy), oneof: "Type" }
  ]
);

const Report_IOXRay_GP = proto3.makeMessageType(
  "com.diagnocat.model.Report_IOXRay_GP",
  () => [
    { no: 1, name: "Images_Meta", kind: "message", T: Report_2D_Image_Meta, repeated: true },
    { no: 2, name: "MaxFaxAnatomyLocalizations", kind: "message", T: MaxFaxAnatomyLocalization, repeated: true }
  ]
);

const Report_Pano_GP = proto3.makeMessageType(
  "com.diagnocat.model.Report_Pano_GP",
  () => [
    {
      no: 1,
      name: "PanoStudyAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Images_Meta", kind: "message", T: Report_2D_Image_Meta, repeated: true },
    { no: 3, name: "MaxFaxAnatomyLocalizations", kind: "message", T: MaxFaxAnatomyLocalization, repeated: true }
  ]
);

const Report_CBCT_GP = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_GP",
  () => [
    { no: 1, name: "OrthoScreening", kind: "message", T: Report_CBCT_GP_OrthoScreening },
    { no: 2, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData },
    { no: 3, name: "MaxFaxAnatomyLocalizations", kind: "message", T: MaxFaxAnatomyLocalization, repeated: true }
  ]
);
const Report_CBCT_GP_OrthoScreening = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_GP_OrthoScreening",
  () => [
    { no: 1, name: "AnglesClass", kind: "enum", T: proto3.getEnumType(Report_CBCT_GP_OrthoScreening_OrthoAnglesClass) },
    { no: 2, name: "Crowding", kind: "message", T: Report_CBCT_GP_OrthoScreening_OrthoToothPair, repeated: true },
    { no: 3, name: "Spacing", kind: "message", T: Report_CBCT_GP_OrthoScreening_OrthoToothPair, repeated: true },
    { no: 4, name: "CephalometricConditions", kind: "message", T: Report_CBCT_GP_OrthoScreening_OrthoCephalometricCondition, repeated: true }
  ]
);
const Report_CBCT_GP_OrthoScreening_OrthoAnglesClass = proto3.makeEnum(
  "com.diagnocat.model.Report_CBCT_GP_OrthoScreening.OrthoAnglesClass",
  [
    { no: 0, name: "AnglesClassUnidentified" },
    { no: 1, name: "AnglesClass1" },
    { no: 2, name: "AnglesClass2" },
    { no: 3, name: "AnglesClass3" }
  ]
);
const Report_CBCT_GP_OrthoScreening_OrthoConditionCode = proto3.makeEnum(
  "com.diagnocat.model.Report_CBCT_GP_OrthoScreening.OrthoConditionCode",
  [
    { no: 0, name: "UnspecifiedCondition" },
    { no: 2e3, name: "NormalMaxillaryPosition" },
    { no: 2001, name: "MaxillaryRetroPosition" },
    { no: 2002, name: "MaxillaryAntePosition" },
    { no: 2010, name: "NormalMandibularPosition" },
    { no: 2011, name: "MandibularRetroPosition" },
    { no: 2012, name: "MandibularAntePosition" },
    { no: 2020, name: "NormalMaxillaryAndMandibularSkeletalBasesRelation" },
    { no: 2021, name: "MesialMaxillaryAndMandibularSkeletalBasesRelation" },
    { no: 2022, name: "DistalMaxillaryAndMandibularSkeletalBasesRelation" },
    { no: 2030, name: "NormalInclination" },
    { no: 2031, name: "AnteInclination" },
    { no: 2032, name: "RetroInclination" },
    { no: 2040, name: "NormalDivergence" },
    { no: 2041, name: "HypoDivergence" },
    { no: 2042, name: "HyperDivergence" },
    { no: 2050, name: "UpperIncisorNormalPositionAccordingToU1SnAngle" },
    { no: 2051, name: "UpperIncisorRetrusionAccordingToU1SnAngle" },
    { no: 2052, name: "UpperIncisorProtrusionAccordingToU1SnAngle" },
    { no: 2060, name: "UpperIncisorNormalPositionAccordingToU1PpAngle" },
    { no: 2061, name: "UpperIncisorRetrusionAccordingToU1PpAngle" },
    { no: 2062, name: "UpperIncisorProtrusionAccordingToU1PpAngle" },
    { no: 2070, name: "LowerIncisorNormalPosition" },
    { no: 2071, name: "LowerIncisorRetrusion" },
    { no: 2072, name: "LowerIncisorProtrusion" },
    { no: 2080, name: "NormalOverjet" },
    { no: 2081, name: "IncreasedOverjet" },
    { no: 2082, name: "DecreasedOverjet" },
    { no: 2083, name: "AnteriorCrossbite" },
    { no: 2090, name: "NormalOverbite" },
    { no: 2091, name: "IncreasedOverbite" },
    { no: 2092, name: "DecreasedOverbite" },
    { no: 2093, name: "OpenBite" }
  ]
);
const Report_CBCT_GP_OrthoScreening_OrthoToothPair = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_GP_OrthoScreening.OrthoToothPair",
  () => [
    {
      no: 1,
      name: "FirstToothNumberISO",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 2,
      name: "SecondToothNumberISO",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    }
  ],
  { localName: "Report_CBCT_GP_OrthoScreening_OrthoToothPair" }
);
const Report_CBCT_GP_OrthoScreening_OrthoCephalometricCondition = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_GP_OrthoScreening.OrthoCephalometricCondition",
  () => [
    {
      no: 1,
      name: "Rule",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Value",
      kind: "scalar",
      T: 1
      /* ScalarType.DOUBLE */
    },
    {
      no: 3,
      name: "Unit",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 4, name: "ConditionCode", kind: "enum", T: proto3.getEnumType(Report_CBCT_GP_OrthoScreening_OrthoConditionCode) }
  ],
  { localName: "Report_CBCT_GP_OrthoScreening_OrthoCephalometricCondition" }
);

const Report_CBCT_Endo = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_Endo",
  () => [
    { no: 1, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData },
    { no: 2, name: "PeriapicalLesionLocalization", kind: "message", T: Report_CBCT_Endo_PeriapicalLesionLocalization }
  ]
);
const Report_CBCT_Endo_PeriapicalLesionLocalization = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_Endo_PeriapicalLesionLocalization",
  () => [
    {
      no: 1,
      name: "ToothNumberISO",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    {
      no: 2,
      name: "PeriapicalLesionDetected",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 3,
      name: "HypodensityVolume",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const Report_CBCT_Implant = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_Implant",
  () => [
    { no: 1, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData },
    {
      no: 2,
      name: "SliceStep",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "SliceThickness",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 4,
      name: "MeasurementScale",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const Report_CBCT_Molar = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_Molar",
  () => [
    { no: 1, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData }
  ]
);
const Report_CBCT_Ortho = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_Ortho",
  []
);

const ToothLandmark_Image_Code = proto3.makeEnum(
  "com.diagnocat.model.ToothLandmark_Image_Code",
  [
    { no: 0, name: "ToothLandmark_Image_Code_InvalidValue" },
    { no: 1, name: "ToothLandmark_Image_Code_CementoenamelJunctionMesial" },
    { no: 2, name: "ToothLandmark_Image_Code_CementoenamelJunctionDistal" },
    { no: 3, name: "ToothLandmark_Image_Code_BoneLevelMesial" },
    { no: 4, name: "ToothLandmark_Image_Code_BoneLevelDistal" },
    { no: 5, name: "ToothLandmark_Image_Code_Apex" },
    { no: 6, name: "ToothLandmark_Image_Code_BoneLevelCentral" },
    { no: 7, name: "ToothLandmark_Image_Code_CementoenamelJunctionCentral" }
  ]
);
const ToothLandmark_CBCT_Code = proto3.makeEnum(
  "com.diagnocat.model.ToothLandmark_CBCT_Code",
  [
    { no: 0, name: "ToothLandmark_CBCT_Code_InvalidValue" },
    { no: 1, name: "ToothLandmark_CBCT_Code_UpperToothMedian" },
    { no: 2, name: "ToothLandmark_CBCT_Code_LowerToothMedian" },
    { no: 3, name: "ToothLandmark_CBCT_Code_EnamelJointMesial" },
    { no: 4, name: "ToothLandmark_CBCT_Code_EnamelJointDistal" },
    { no: 5, name: "ToothLandmark_CBCT_Code_EnamelJointVestibular" },
    { no: 6, name: "ToothLandmark_CBCT_Code_EnamelJointOral" },
    { no: 7, name: "ToothLandmark_CBCT_Code_BonePeakMesial" },
    { no: 8, name: "ToothLandmark_CBCT_Code_BonePeakDistal" },
    { no: 9, name: "ToothLandmark_CBCT_Code_BonePeakVestibular" },
    { no: 10, name: "ToothLandmark_CBCT_Code_BonePeakOral" },
    { no: 11, name: "ToothLandmark_CBCT_Code_AttachmentMesial" },
    { no: 12, name: "ToothLandmark_CBCT_Code_AttachmentDistal" },
    { no: 13, name: "ToothLandmark_CBCT_Code_AttachmentVestibular" },
    { no: 14, name: "ToothLandmark_CBCT_Code_AttachmentOral" },
    { no: 15, name: "ToothLandmark_CBCT_Code_Furcation" },
    { no: 16, name: "ToothLandmark_CBCT_Code_Buccal" },
    { no: 17, name: "ToothLandmark_CBCT_Code_Lingual" },
    { no: 18, name: "ToothLandmark_CBCT_Code_Mesial1" },
    { no: 19, name: "ToothLandmark_CBCT_Code_Mesial2" },
    { no: 20, name: "ToothLandmark_CBCT_Code_Distal" },
    { no: 21, name: "ToothLandmark_CBCT_Code_Palatal" },
    { no: 22, name: "ToothLandmark_CBCT_Code_A1" },
    { no: 23, name: "ToothLandmark_CBCT_Code_Mesial" }
  ]
);
const ToothLandmark_IOS_Code = proto3.makeEnum(
  "com.diagnocat.model.ToothLandmark_IOS_Code",
  [
    { no: 0, name: "ToothLandmark_IOS_Code_InvalidValue" },
    { no: 1, name: "ToothLandmark_IOS_Code_CenterOralFrontal" },
    { no: 2, name: "ToothLandmark_IOS_Code_CenterOralPosterior" },
    { no: 3, name: "ToothLandmark_IOS_Code_Mesial" },
    { no: 4, name: "ToothLandmark_IOS_Code_Distal" },
    { no: 5, name: "ToothLandmark_IOS_Code_Cuspidal" },
    { no: 6, name: "ToothLandmark_IOS_Code_CuspidalOral" },
    { no: 7, name: "ToothLandmark_IOS_Code_CuspidalBuccal" },
    { no: 8, name: "ToothLandmark_IOS_Code_CuspidalBuccalMesial" },
    { no: 9, name: "ToothLandmark_IOS_Code_CuspidalBuccalDistal" },
    { no: 10, name: "ToothLandmark_IOS_Code_CuspidalOralMesial" },
    { no: 11, name: "ToothLandmark_IOS_Code_CuspidalOralDistal" },
    { no: 12, name: "ToothLandmark_IOS_Code_CuspidalOralAccesorium" },
    { no: 13, name: "ToothLandmark_IOS_Code_CuspidalDistalAccesorium" },
    { no: 14, name: "ToothLandmark_IOS_Code_CuspidalDistal" }
  ]
);
const CephalometricLandmark_CBCT_Code = proto3.makeEnum(
  "com.diagnocat.model.CephalometricLandmark_CBCT_Code",
  [
    { no: 0, name: "CephalometricLandmark_CBCT_Code_InvalidValue" },
    { no: 1, name: "CephalometricLandmark_CBCT_Code_Nasion" },
    { no: 2, name: "CephalometricLandmark_CBCT_Code_Sella" },
    { no: 3, name: "CephalometricLandmark_CBCT_Code_Basion" },
    { no: 4, name: "CephalometricLandmark_CBCT_Code_AnteriorNasalSpine" },
    { no: 5, name: "CephalometricLandmark_CBCT_Code_PosteriorNasalSpine" },
    { no: 6, name: "CephalometricLandmark_CBCT_Code_APointSubspinale" },
    { no: 7, name: "CephalometricLandmark_CBCT_Code_ProsthionSupradentaleSuperiorProsthion" },
    { no: 8, name: "CephalometricLandmark_CBCT_Code_Interdentale" },
    { no: 9, name: "CephalometricLandmark_CBCT_Code_BPointSupramentale" },
    { no: 10, name: "CephalometricLandmark_CBCT_Code_ProtuberantiaMenti" },
    { no: 11, name: "CephalometricLandmark_CBCT_Code_Pogonion" },
    { no: 12, name: "CephalometricLandmark_CBCT_Code_Gnathion" },
    { no: 13, name: "CephalometricLandmark_CBCT_Code_Menton" },
    { no: 14, name: "CephalometricLandmark_CBCT_Code_OrbitaleRight" },
    { no: 15, name: "CephalometricLandmark_CBCT_Code_OrbitaleLeft" },
    { no: 16, name: "CephalometricLandmark_CBCT_Code_PorionRight" },
    { no: 17, name: "CephalometricLandmark_CBCT_Code_PorionLeft" },
    { no: 18, name: "CephalometricLandmark_CBCT_Code_CondylionRight" },
    { no: 19, name: "CephalometricLandmark_CBCT_Code_CondylionLeft" },
    { no: 20, name: "CephalometricLandmark_CBCT_Code_CondyleLateralPointRight" },
    { no: 21, name: "CephalometricLandmark_CBCT_Code_CondyleLateralPointLeft" },
    { no: 22, name: "CephalometricLandmark_CBCT_Code_CondyleMedialPointRight" },
    { no: 23, name: "CephalometricLandmark_CBCT_Code_CondyleMedialPointLeft" },
    { no: 24, name: "CephalometricLandmark_CBCT_Code_ArticulareRight" },
    { no: 25, name: "CephalometricLandmark_CBCT_Code_ArticulareLeft" },
    { no: 26, name: "CephalometricLandmark_CBCT_Code_UpperRamalPointRight" },
    { no: 27, name: "CephalometricLandmark_CBCT_Code_UpperRamalPointLeft" },
    { no: 28, name: "CephalometricLandmark_CBCT_Code_AnteriorRamalPointRight" },
    { no: 29, name: "CephalometricLandmark_CBCT_Code_GonionRight" },
    { no: 30, name: "CephalometricLandmark_CBCT_Code_GonionLeft" },
    { no: 31, name: "CephalometricLandmark_CBCT_Code_AntegonionRight" },
    { no: 32, name: "CephalometricLandmark_CBCT_Code_AntegonionLeft" },
    { no: 33, name: "CephalometricLandmark_CBCT_Code_UpperMolarMesiobuccalRootPointRight" },
    { no: 34, name: "CephalometricLandmark_CBCT_Code_UpperMolarMesiobuccalRootPointLeft" },
    { no: 35, name: "CephalometricLandmark_CBCT_Code_UpperMolarMesiobuccalCuspPointRight" },
    { no: 36, name: "CephalometricLandmark_CBCT_Code_UpperMolarMesiobuccalCuspPointLeft" },
    { no: 37, name: "CephalometricLandmark_CBCT_Code_LowerMolarMesiobuccalCuspPointRight" },
    { no: 38, name: "CephalometricLandmark_CBCT_Code_LowerMolarMesiobuccalCuspPointLeft" },
    { no: 39, name: "CephalometricLandmark_CBCT_Code_LowerMolarMesialRootPointRight" },
    { no: 40, name: "CephalometricLandmark_CBCT_Code_LowerMolarMesialRootPointLeft" },
    { no: 41, name: "CephalometricLandmark_CBCT_Code_UpperIncisorRootPointRight" },
    { no: 42, name: "CephalometricLandmark_CBCT_Code_UpperIncisorRootPointLeft" },
    { no: 43, name: "CephalometricLandmark_CBCT_Code_UpperIncisorCrownPointRight" },
    { no: 44, name: "CephalometricLandmark_CBCT_Code_UpperIncisorCrownPointLeft" },
    { no: 45, name: "CephalometricLandmark_CBCT_Code_LowerIncisorRootPointRight" },
    { no: 46, name: "CephalometricLandmark_CBCT_Code_LowerIncisorRootPointLeft" },
    { no: 47, name: "CephalometricLandmark_CBCT_Code_LowerIncisorCrownPointRight" },
    { no: 48, name: "CephalometricLandmark_CBCT_Code_LowerIncisorCrownPointLeft" },
    { no: 49, name: "CephalometricLandmark_CBCT_Code_Glabella" },
    { no: 50, name: "CephalometricLandmark_CBCT_Code_SoftTissueNasion" },
    { no: 51, name: "CephalometricLandmark_CBCT_Code_Pronasale" },
    { no: 52, name: "CephalometricLandmark_CBCT_Code_Cartilage" },
    { no: 53, name: "CephalometricLandmark_CBCT_Code_SubnasaleSubnasalSubnasion" },
    { no: 54, name: "CephalometricLandmark_CBCT_Code_LabialisSuperior" },
    { no: 55, name: "CephalometricLandmark_CBCT_Code_StomionSuperius" },
    { no: 56, name: "CephalometricLandmark_CBCT_Code_StomionInferius" },
    { no: 57, name: "CephalometricLandmark_CBCT_Code_LabialInferior" },
    { no: 58, name: "CephalometricLandmark_CBCT_Code_MentolabialSulcus" },
    { no: 59, name: "CephalometricLandmark_CBCT_Code_SoftTissuePogonion" },
    { no: 60, name: "CephalometricLandmark_CBCT_Code_SoftTissueMenton" },
    { no: 61, name: "CephalometricLandmark_CBCT_Code_LateralLeft" },
    { no: 62, name: "CephalometricLandmark_CBCT_Code_LateralRight" },
    { no: 63, name: "CephalometricLandmark_CBCT_Code_CristaGalli" },
    { no: 64, name: "CephalometricLandmark_CBCT_Code_MaxillareLeft" },
    { no: 65, name: "CephalometricLandmark_CBCT_Code_MaxillareRight" },
    { no: 66, name: "CephalometricLandmark_CBCT_Code_WillAndrewsAndLarryAndrewsLeft" },
    { no: 67, name: "CephalometricLandmark_CBCT_Code_WillAndrewsAndLarryAndrewsRight" },
    { no: 68, name: "CephalometricLandmark_CBCT_Code_ZygionLeft" },
    { no: 69, name: "CephalometricLandmark_CBCT_Code_ZygionRight" },
    { no: 70, name: "CephalometricLandmark_CBCT_Code_CenterOfCondyleLeft" },
    { no: 71, name: "CephalometricLandmark_CBCT_Code_CenterOfCondyleRight" },
    { no: 72, name: "CephalometricLandmark_CBCT_Code_EndocanthionLeft" },
    { no: 73, name: "CephalometricLandmark_CBCT_Code_EndocanthionRight" },
    { no: 74, name: "CephalometricLandmark_CBCT_Code_PterygoidLeft" },
    { no: 75, name: "CephalometricLandmark_CBCT_Code_PterygoidRight" }
  ]
);
const ToothLandmark_PBL_Code = proto3.makeEnum(
  "com.diagnocat.model.ToothLandmark_PBL_Code",
  [
    { no: 0, name: "ToothLandmark_PBL_Code_InvalidValue" },
    { no: 1, name: "ToothLandmark_PBL_Code_AttachmentDistalBuccal" },
    { no: 2, name: "ToothLandmark_PBL_Code_AttachmentBuccal" },
    { no: 3, name: "ToothLandmark_PBL_Code_AttachmentMesialBuccal" },
    { no: 4, name: "ToothLandmark_PBL_Code_AttachmentDistalLingual" },
    { no: 5, name: "ToothLandmark_PBL_Code_AttachmentLingual" },
    { no: 6, name: "ToothLandmark_PBL_Code_AttachmentMesialLingual" },
    { no: 7, name: "ToothLandmark_PBL_Code_EnamelJointDistalBuccal" },
    { no: 8, name: "ToothLandmark_PBL_Code_EnamelJointBuccal" },
    { no: 9, name: "ToothLandmark_PBL_Code_EnamelJointMesialBuccal" },
    { no: 10, name: "ToothLandmark_PBL_Code_EnamelJointDistalLingual" },
    { no: 11, name: "ToothLandmark_PBL_Code_EnamelJointLingual" },
    { no: 12, name: "ToothLandmark_PBL_Code_EnamelJointMesialLingual" },
    { no: 13, name: "ToothLandmark_PBL_Code_GingivaDistalBuccal" },
    { no: 14, name: "ToothLandmark_PBL_Code_GingivaBuccal" },
    { no: 15, name: "ToothLandmark_PBL_Code_GingivaMesialBuccal" },
    { no: 16, name: "ToothLandmark_PBL_Code_GingivaDistalLingual" },
    { no: 17, name: "ToothLandmark_PBL_Code_GingivaLingual" },
    { no: 18, name: "ToothLandmark_PBL_Code_GingivaMesialLingual" }
  ]
);

const ToothLandmark = proto3.makeMessageType(
  "com.diagnocat.model.ToothLandmark",
  () => [
    {
      no: 3,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 1, name: "Tooth", kind: "message", T: ToothNumeration },
    {
      no: 4,
      name: "ToothLocalizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 21,
      name: "ToothID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 22,
      name: "ReportID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 23,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 24,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "ModelPosition", kind: "message", T: FloatPoint3D },
    { no: 5, name: "UserPosition", kind: "message", T: FloatPoint3D },
    { no: 11, name: "CBCT", kind: "enum", T: proto3.getEnumType(ToothLandmark_CBCT_Code), oneof: "Code" },
    { no: 12, name: "IOS", kind: "enum", T: proto3.getEnumType(ToothLandmark_IOS_Code), oneof: "Code" },
    { no: 13, name: "Image", kind: "enum", T: proto3.getEnumType(ToothLandmark_Image_Code), oneof: "Code" },
    { no: 14, name: "PBL", kind: "enum", T: proto3.getEnumType(ToothLandmark_PBL_Code), oneof: "Code" },
    { no: 10030, name: "Created", kind: "message", T: Created },
    { no: 10040, name: "Revision", kind: "message", T: Revision },
    { no: 10050, name: "Deleted", kind: "message", T: Deleted }
  ]
);
const CephalometricLandmark = proto3.makeMessageType(
  "com.diagnocat.model.CephalometricLandmark",
  () => [
    {
      no: 3,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 1, name: "Code", kind: "enum", T: proto3.getEnumType(CephalometricLandmark_CBCT_Code) },
    { no: 2, name: "ModelPosition", kind: "message", T: FloatPoint3D },
    { no: 4, name: "UserPosition", kind: "message", T: FloatPoint3D }
  ]
);

const Report_Studio_ToothPosition = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_ToothPosition",
  () => [
    { no: 1, name: "Tooth", kind: "message", T: ToothNumeration },
    {
      no: 2,
      name: "IsMissing",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 3, name: "Position", kind: "message", T: Report_Studio_OriginatedPosition }
  ]
);
const Report_Studio_OriginatedPosition = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_OriginatedPosition",
  () => [
    { no: 1, name: "Origin", kind: "message", T: FloatPoint3D },
    { no: 2, name: "Directions", kind: "message", T: Report_Studio_OriginatedPosition_Report_Studio_OriginatedPosition_Directions },
    { no: 3, name: "AnatomicalBasis", kind: "message", T: AnatomicalBasis3D }
  ]
);
const Report_Studio_OriginatedPosition_Report_Studio_OriginatedPosition_Directions = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_OriginatedPosition.Report_Studio_OriginatedPosition_Directions",
  () => [
    { no: 1, name: "X", kind: "message", T: FloatPoint3D },
    { no: 2, name: "Y", kind: "message", T: FloatPoint3D },
    { no: 3, name: "Z", kind: "message", T: FloatPoint3D }
  ],
  { localName: "Report_Studio_OriginatedPosition_Report_Studio_OriginatedPosition_Directions" }
);

const ToothPeriodontalStatusMeasurement_Site = proto3.makeEnum(
  "com.diagnocat.model.ToothPeriodontalStatusMeasurement_Site",
  [
    { no: 0, name: "PeriodontalMeasurement_Site_InvalidValue" },
    { no: 1, name: "PeriodontalMeasurement_Site_Mesial" },
    { no: 2, name: "PeriodontalMeasurement_Site_MesialBuccal" },
    { no: 3, name: "PeriodontalMeasurement_Site_MesialLingual" },
    { no: 4, name: "PeriodontalMeasurement_Site_Distal" },
    { no: 5, name: "PeriodontalMeasurement_Site_DistalBuccal" },
    { no: 6, name: "PeriodontalMeasurement_Site_DistalLingual" },
    { no: 7, name: "PeriodontalMeasurement_Site_Vestibular" },
    { no: 8, name: "PeriodontalMeasurement_Site_Oral" }
  ]
);
const ToothPeriodontalStatus = proto3.makeMessageType(
  "com.diagnocat.model.ToothPeriodontalStatus",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "ToothNumeration", kind: "message", T: ToothNumeration },
    {
      no: 3,
      name: "ToothLocalizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 4, name: "Measurements", kind: "message", T: ToothPeriodontalStatusMeasurement, repeated: true },
    { no: 5, name: "Roots", kind: "message", T: ToothPeriodontalStatusRoot, repeated: true }
  ]
);
const ToothPeriodontalStatusMeasurement = proto3.makeMessageType(
  "com.diagnocat.model.ToothPeriodontalStatusMeasurement",
  () => [
    { no: 1, name: "Site", kind: "enum", T: proto3.getEnumType(ToothPeriodontalStatusMeasurement_Site) },
    { no: 2, name: "BoneLoss", kind: "scalar", T: 2, opt: true },
    { no: 3, name: "Recession", kind: "scalar", T: 2, opt: true },
    { no: 4, name: "Probing", kind: "scalar", T: 2, opt: true }
  ]
);
const ToothPeriodontalStatusRoot = proto3.makeMessageType(
  "com.diagnocat.model.ToothPeriodontalStatusRoot",
  () => [
    { no: 1, name: "RootType", kind: "enum", T: proto3.getEnumType(CBCT_Tooth_RootType) },
    {
      no: 2,
      name: "Length",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);

const Report_CBCT_Segmentation = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_Segmentation",
  () => [
    { no: 1, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData },
    { no: 2, name: "CephalometricLandmarks", kind: "message", T: CephalometricLandmark, repeated: true },
    { no: 4, name: "TeethLandmarks", kind: "message", T: ToothLandmark, repeated: true },
    { no: 5, name: "TeethPositions", kind: "message", T: Report_Studio_ToothPosition, repeated: true },
    { no: 6, name: "TeethPeriodontalStatuses", kind: "message", T: ToothPeriodontalStatus, repeated: true },
    {
      no: 101,
      name: "Enable3DModelsWithConditions",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);

const Certainty = proto3.makeMessageType(
  "com.diagnocat.model.Certainty",
  () => [
    { no: 1, name: "ModelDecision", kind: "enum", T: proto3.getEnumType(Decision) },
    {
      no: 2,
      name: "ModelScore",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "IsModelScoreSet",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 4, name: "UserDecision", kind: "enum", T: proto3.getEnumType(Decision) },
    { no: 6, name: "UserScore", kind: "scalar", T: 2, opt: true },
    { no: 5, name: "EngineDecision", kind: "enum", T: proto3.getEnumType(Decision) }
  ]
);

const Report_DentalPhoto_GP = proto3.makeMessageType(
  "com.diagnocat.model.Report_DentalPhoto_GP",
  () => [
    { no: 1, name: "TypePredictions", kind: "message", T: Photo_GP_TypePrediction, repeated: true }
  ]
);
const Photo_GP_TypePrediction = proto3.makeMessageType(
  "com.diagnocat.model.Photo_GP_TypePrediction",
  () => [
    {
      no: 1,
      name: "AssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 11, name: "ImageType", kind: "message", T: Photo_GP_TypePrediction_Photo_GP_TypePrediction_ImageType },
    { no: 12, name: "IntraoralSubtype", kind: "message", T: Photo_GP_TypePrediction_Photo_GP_TypePrediction_IntraoralSubtype },
    { no: 13, name: "PortraitSubtype", kind: "message", T: Photo_GP_TypePrediction_Photo_GP_TypePrediction_PortraitSubtype },
    { no: 14, name: "PortraitSide", kind: "message", T: Photo_GP_TypePrediction_Photo_GP_TypePrediction_PortraitSide },
    { no: 15, name: "SmileType", kind: "message", T: Photo_GP_TypePrediction_Photo_GP_TypePrediction_SmileType },
    { no: 16, name: "Adult", kind: "message", T: Certainty },
    { no: 17, name: "Mirror", kind: "message", T: Certainty }
  ]
);
const Photo_GP_TypePrediction_Photo_GP_TypePrediction_ImageType = proto3.makeMessageType(
  "com.diagnocat.model.Photo_GP_TypePrediction.Photo_GP_TypePrediction_ImageType",
  () => [
    { no: 1, name: "Intraoral", kind: "message", T: Certainty },
    { no: 2, name: "Portrait", kind: "message", T: Certainty },
    { no: 3, name: "Other", kind: "message", T: Certainty },
    { no: 4, name: "Lab", kind: "message", T: Certainty }
  ],
  { localName: "Photo_GP_TypePrediction_Photo_GP_TypePrediction_ImageType" }
);
const Photo_GP_TypePrediction_Photo_GP_TypePrediction_IntraoralSubtype = proto3.makeMessageType(
  "com.diagnocat.model.Photo_GP_TypePrediction.Photo_GP_TypePrediction_IntraoralSubtype",
  () => [
    { no: 1, name: "OcclusalLower", kind: "message", T: Certainty },
    { no: 2, name: "OcclusalUpper", kind: "message", T: Certainty },
    { no: 3, name: "OcclusalRight", kind: "message", T: Certainty },
    { no: 4, name: "OcclusalLeft", kind: "message", T: Certainty },
    { no: 5, name: "Frontal", kind: "message", T: Certainty }
  ],
  { localName: "Photo_GP_TypePrediction_Photo_GP_TypePrediction_IntraoralSubtype" }
);
const Photo_GP_TypePrediction_Photo_GP_TypePrediction_PortraitSubtype = proto3.makeMessageType(
  "com.diagnocat.model.Photo_GP_TypePrediction.Photo_GP_TypePrediction_PortraitSubtype",
  () => [
    { no: 1, name: "Frontal", kind: "message", T: Certainty },
    { no: 2, name: "Oblique", kind: "message", T: Certainty },
    { no: 3, name: "Profile", kind: "message", T: Certainty }
  ],
  { localName: "Photo_GP_TypePrediction_Photo_GP_TypePrediction_PortraitSubtype" }
);
const Photo_GP_TypePrediction_Photo_GP_TypePrediction_PortraitSide = proto3.makeMessageType(
  "com.diagnocat.model.Photo_GP_TypePrediction.Photo_GP_TypePrediction_PortraitSide",
  () => [
    { no: 1, name: "Left", kind: "message", T: Certainty },
    { no: 2, name: "Right", kind: "message", T: Certainty }
  ],
  { localName: "Photo_GP_TypePrediction_Photo_GP_TypePrediction_PortraitSide" }
);
const Photo_GP_TypePrediction_Photo_GP_TypePrediction_SmileType = proto3.makeMessageType(
  "com.diagnocat.model.Photo_GP_TypePrediction.Photo_GP_TypePrediction_SmileType",
  () => [
    { no: 1, name: "NoSmile", kind: "message", T: Certainty },
    { no: 2, name: "Smile", kind: "message", T: Certainty },
    { no: 3, name: "Grin", kind: "message", T: Certainty }
  ],
  { localName: "Photo_GP_TypePrediction_Photo_GP_TypePrediction_SmileType" }
);

const Segmentron_PhotoIOSSuperimposition = proto3.makeMessageType(
  "com.diagnocat.model.Segmentron_PhotoIOSSuperimposition",
  () => [
    {
      no: 1,
      name: "PhotoAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 7, name: "Mask", kind: "message", T: ClosedPolygon },
    {
      no: 3,
      name: "FocalLength",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    { no: 4, name: "ViewToCbctTransform", kind: "message", T: ProjectionTransform },
    { no: 5, name: "BBox", kind: "message", T: BBox },
    { no: 8, name: "PixelBBox", kind: "message", T: BBox },
    { no: 6, name: "PlanePosition", kind: "message", T: PlanePosition }
  ]
);
const IOSJawTypeClassification = proto3.makeMessageType(
  "com.diagnocat.model.IOSJawTypeClassification",
  () => [
    {
      no: 1,
      name: "TargetAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Type", kind: "enum", T: proto3.getEnumType(JawType) }
  ]
);

const Report_CBCT_IOS_Superimposition_Orientation = proto3.makeEnum(
  "com.diagnocat.model.Report_CBCT_IOS_Superimposition_Orientation",
  [
    { no: 0, name: "CBCT_IOS_Superimposition_Orientation_CBCT" },
    { no: 1, name: "CBCT_IOS_Superimposition_Orientation_IOS" },
    { no: 2, name: "CBCT_IOS_Superimposition_Orientation_Zero_index" }
  ]
);
const Report_CBCT_IOS_Superimposition = proto3.makeMessageType(
  "com.diagnocat.model.Report_CBCT_IOS_Superimposition",
  () => [
    { no: 1, name: "Orientation", kind: "enum", T: proto3.getEnumType(Report_CBCT_IOS_Superimposition_Orientation) },
    {
      no: 2,
      name: "AlignmentSuccessful",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 4, name: "PhotoIOSSuperimpositions", kind: "message", T: Segmentron_PhotoIOSSuperimposition, repeated: true },
    { no: 5, name: "TeethLandmarks", kind: "message", T: ToothLandmark, repeated: true },
    { no: 6, name: "TeethPositions", kind: "message", T: Report_Studio_ToothPosition, repeated: true },
    { no: 7, name: "TeethPeriodontalStatuses", kind: "message", T: ToothPeriodontalStatus, repeated: true },
    { no: 8, name: "CephalometricLandmarks", kind: "message", T: CephalometricLandmark, repeated: true },
    {
      no: 101,
      name: "Enable3DModelsWithConditions",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);

const Report_Studio_Implant = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Implant",
  () => [
    { no: 1, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData },
    { no: 2, name: "ColorProfile", kind: "message", T: StudioColorProfile },
    { no: 3, name: "TeethPositions", kind: "message", T: Report_Studio_ToothPosition, repeated: true },
    { no: 5, name: "Plans", kind: "message", T: Report_Studio_Implant_CrownImplantPlan, repeated: true },
    { no: 6, name: "TeethLandmarks", kind: "message", T: ToothLandmark, repeated: true },
    { no: 7, name: "CephalometricLandmarks", kind: "message", T: CephalometricLandmark, repeated: true },
    { no: 8, name: "PhotoIOSSuperimpositions", kind: "message", T: Segmentron_PhotoIOSSuperimposition, repeated: true }
  ]
);
const Report_Studio_Implant_CrownImplantPlan = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Implant_CrownImplantPlan",
  () => [
    { no: 1, name: "Tooth", kind: "message", T: ToothNumeration },
    { no: 2, name: "CrownPlan", kind: "message", T: Report_Studio_Implant_CrownImplantPlan_CrownPlan },
    { no: 3, name: "ImplantPlan", kind: "message", T: Report_Studio_Implant_CrownImplantPlan_ImplantPlan, opt: true }
  ]
);
const Report_Studio_Implant_CrownImplantPlan_CrownPlan = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Implant_CrownImplantPlan_CrownPlan",
  () => [
    { no: 1, name: "Position", kind: "message", T: Report_Studio_OriginatedPosition },
    {
      no: 2,
      name: "ArtificialCrownAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "Landmarks", kind: "message", T: ToothLandmark, repeated: true },
    { no: 4, name: "DistanceBetweenNeighbors", kind: "scalar", T: 2, opt: true }
  ]
);
const Report_Studio_Implant_CrownImplantPlan_ImplantPlan = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Implant_CrownImplantPlan_ImplantPlan",
  () => [
    { no: 1, name: "Position", kind: "message", T: Report_Studio_OriginatedPosition },
    {
      no: 2,
      name: "MaximumLength",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "MaximumDiameter",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);

const CephalometricMeasure_Code = proto3.makeEnum(
  "com.diagnocat.model.CephalometricMeasure_Code",
  [
    { no: 0, name: "CephalometricMeasure_Code_InvalidValue" },
    { no: 1, name: "CephalometricMeasure_Code_SN_MP" },
    { no: 2, name: "CephalometricMeasure_Code_FH_OP" },
    { no: 3, name: "CephalometricMeasure_Code_Go_Ang" },
    { no: 4, name: "CephalometricMeasure_Code_Mid_Height" },
    { no: 5, name: "CephalometricMeasure_Code_Low_Height" },
    { no: 6, name: "CephalometricMeasure_Code_Beta" },
    { no: 7, name: "CephalometricMeasure_Code_Wits" },
    { no: 8, name: "CephalometricMeasure_Code_NSeAr" },
    { no: 9, name: "CephalometricMeasure_Code_SNA" },
    { no: 10, name: "CephalometricMeasure_Code_SNB" },
    { no: 11, name: "CephalometricMeasure_Code_ANB" },
    { no: 12, name: "CephalometricMeasure_Code_MP" },
    { no: 13, name: "CephalometricMeasure_Code_S_N" },
    { no: 14, name: "CephalometricMeasure_Code_ULip_Eline" },
    { no: 15, name: "CephalometricMeasure_Code_LLip_Eline" },
    { no: 16, name: "CephalometricMeasure_Code_Overjet" },
    { no: 17, name: "CephalometricMeasure_Code_Overbite" },
    { no: 18, name: "CephalometricMeasure_Code_U1_SN" },
    { no: 19, name: "CephalometricMeasure_Code_U1_PP" },
    { no: 20, name: "CephalometricMeasure_Code_U1_TVL" },
    { no: 21, name: "CephalometricMeasure_Code_Interincisal" },
    { no: 22, name: "CephalometricMeasure_Code_Li_MP" },
    { no: 23, name: "CephalometricMeasure_Code_Shimbashi" }
  ]
);

const Report_Studio_Ortho = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho",
  () => [
    {
      no: 13,
      name: "CBCTStudyID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 14,
      name: "SecondaryCBCTStudyID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 1, name: "CropGeometryData", kind: "message", T: CBCTSeriesGeometryData },
    { no: 2, name: "ColorProfile", kind: "message", T: StudioColorProfile },
    { no: 3, name: "TeethPositions", kind: "message", T: Report_Studio_ToothPosition, repeated: true },
    { no: 5, name: "NaturalHeadPositionTransform", kind: "message", T: ProjectionTransform },
    { no: 6, name: "OrthoPlanes", kind: "message", T: Report_Studio_Ortho_OrthoPlanes },
    { no: 7, name: "TeethMovementPlans", kind: "message", T: Report_Studio_Ortho_TeethMovementPlan, repeated: true },
    { no: 8, name: "CephalometricMeasurements", kind: "message", T: Report_Studio_Ortho_CephalometricMeasurements, repeated: true },
    { no: 9, name: "TeethLandmarks", kind: "message", T: ToothLandmark, repeated: true },
    { no: 10, name: "CephalometricLandmarks", kind: "message", T: CephalometricLandmark, repeated: true },
    { no: 11, name: "PhotoIOSSuperimpositions", kind: "message", T: Segmentron_PhotoIOSSuperimposition, repeated: true },
    { no: 12, name: "TeethAnalysis", kind: "message", T: Report_Studio_Ortho_TeethAnalysis }
  ]
);
const Report_Studio_Ortho_OrthoPlanes = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_OrthoPlanes",
  () => [
    { no: 1, name: "Midline", kind: "message", T: PlanePosition },
    { no: 2, name: "OcclusionalUpper", kind: "message", T: PlanePosition },
    { no: 3, name: "OcclusionalLower", kind: "message", T: PlanePosition }
  ]
);
const Report_Studio_Ortho_TeethMovementPlan = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethMovementPlan",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Order",
      kind: "scalar",
      T: 13
      /* ScalarType.UINT32 */
    },
    { no: 3, name: "Steps", kind: "message", T: Report_Studio_Ortho_TeethMovementPlan_Report_Studio_Ortho_TeethMovementPlan_Step, repeated: true }
  ]
);
const Report_Studio_Ortho_TeethMovementPlan_Report_Studio_Ortho_TeethMovementPlan_Step = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethMovementPlan.Report_Studio_Ortho_TeethMovementPlan_Step",
  () => [
    { no: 1, name: "Tooth", kind: "message", T: ToothNumeration },
    { no: 2, name: "Transform", kind: "message", T: ProjectionTransform }
  ],
  { localName: "Report_Studio_Ortho_TeethMovementPlan_Report_Studio_Ortho_TeethMovementPlan_Step" }
);
const Report_Studio_Ortho_CephalometricMeasurements = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_CephalometricMeasurements",
  () => [
    { no: 1, name: "Measurements", kind: "message", T: Report_Studio_Ortho_CephalometricMeasurements_Measurement, repeated: true },
    { no: 2, name: "Initial", kind: "message", T: Empty, oneof: "State" },
    { no: 3, name: "TeethMovementPlanID", kind: "scalar", T: 9, oneof: "State" }
  ]
);
const Report_Studio_Ortho_CephalometricMeasurements_Measurement = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_CephalometricMeasurements.Measurement",
  () => [
    { no: 1, name: "Type", kind: "enum", T: proto3.getEnumType(CephalometricMeasure_Code) },
    { no: 2, name: "Values", kind: "message", T: Report_Studio_Ortho_CephalometricMeasurements_MeasurementValues }
  ],
  { localName: "Report_Studio_Ortho_CephalometricMeasurements_Measurement" }
);
const Report_Studio_Ortho_CephalometricMeasurements_MeasurementValues = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_CephalometricMeasurements_MeasurementValues",
  () => [
    {
      no: 1,
      name: "Left",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "Right",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "IsPerSide",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 4,
      name: "Norm",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 5,
      name: "NormDeviation",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ]
);
const Report_Studio_Ortho_TeethAnalysis = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis",
  () => [
    { no: 1, name: "Ponts", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_Ponts },
    { no: 2, name: "TonnBolton", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_TonnBolton },
    { no: 3, name: "SpaceCrowding", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure },
    { no: 4, name: "CurveOfSpee", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure }
  ]
);
const Report_Studio_Ortho_TeethAnalysis_Ponts = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis_Ponts",
  () => [
    { no: 1, name: "LowerMolars", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_Ponts_Measure },
    { no: 2, name: "UpperMolars", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_Ponts_Measure },
    { no: 3, name: "LowerPreMolars", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_Ponts_Measure },
    { no: 4, name: "UpperPreMolars", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_Ponts_Measure }
  ]
);
const Report_Studio_Ortho_TeethAnalysis_Ponts_Measure = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis_Ponts.Measure",
  () => [
    {
      no: 1,
      name: "Norm",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "Fact",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "Diff",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ],
  { localName: "Report_Studio_Ortho_TeethAnalysis_Ponts_Measure" }
);
const Report_Studio_Ortho_TeethAnalysis_TonnBolton = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis_TonnBolton",
  () => [
    { no: 1, name: "Tonn", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_TonnBolton_Measure },
    { no: 2, name: "Bolton", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_TonnBolton_Measure }
  ]
);
const Report_Studio_Ortho_TeethAnalysis_TonnBolton_Measure = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis_TonnBolton.Measure",
  () => [
    {
      no: 1,
      name: "IndexNorm",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "IndexFact",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "DiffUpper",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 4,
      name: "DiffLower",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ],
  { localName: "Report_Studio_Ortho_TeethAnalysis_TonnBolton_Measure" }
);
const Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure",
  () => [
    { no: 2, name: "Lower", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure_Measure },
    { no: 1, name: "Upper", kind: "message", T: Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure_Measure }
  ]
);
const Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure_Measure = proto3.makeMessageType(
  "com.diagnocat.model.Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure.Measure",
  () => [
    {
      no: 1,
      name: "Left",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 2,
      name: "Right",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    },
    {
      no: 3,
      name: "Total",
      kind: "scalar",
      T: 2
      /* ScalarType.FLOAT */
    }
  ],
  { localName: "Report_Studio_Ortho_TeethAnalysis_LeftRightMeasure_Measure" }
);

const Report_Pano_Bitewings = proto3.makeMessageType(
  "com.diagnocat.model.Report_Pano_Bitewings",
  () => [
    { no: 1, name: "Images_Meta", kind: "message", T: Report_2D_Image_Meta, repeated: true },
    {
      no: 2,
      name: "PanoStudyAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "MaxFaxAnatomyLocalizations", kind: "message", T: MaxFaxAnatomyLocalization, repeated: true }
  ]
);

const Report_IOS_Segmentation = proto3.makeMessageType(
  "com.diagnocat.model.Report_IOS_Segmentation",
  () => [
    { no: 1, name: "JawTypeClassifications", kind: "message", T: IOSJawTypeClassification, repeated: true }
  ]
);

const ReportPermissions = proto3.makeMessageType(
  "com.diagnocat.model.ReportPermissions",
  () => [
    {
      no: 1,
      name: "CanView",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "CanChangeROI",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 4,
      name: "CanChangeConditions",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 5,
      name: "CanInteractWithImages",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 6,
      name: "CanSign",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 7,
      name: "CanPrint",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 9,
      name: "CanChangeConclusion",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 10,
      name: "CanChangeSettings",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 11,
      name: "CanDelete",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 12,
      name: "CanChangeReportAssets",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 21,
      name: "CanChangeToothComment",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 22,
      name: "CanChangeToothNumeration",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 23,
      name: "CanChangeToothApproved",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 24,
      name: "CanResetTooth",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 25,
      name: "CanChangeToothAssets",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 26,
      name: "CanRestore",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 27,
      name: "CanArchive",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);

const ReportGenerationErrorCode = proto3.makeEnum(
  "com.diagnocat.model.ReportGenerationErrorCode",
  [
    { no: 0, name: "ReportGenerationErrorCode_Unspecified" },
    { no: 1, name: "ReportGenerationErrorCode_StudyUpload_Failed" },
    { no: 2, name: "ReportGenerationErrorCode_StudyUpload_Canceled" },
    { no: 3, name: "ReportGenerationErrorCode_GeneratedAssetUpload_Failed" },
    { no: 11, name: "ReportGenerationErrorCode_FileCannotBeRead" },
    { no: 20, name: "ReportGenerationErrorCode_Dicom_IsNot2DImage" },
    { no: 21, name: "ReportGenerationErrorCode_Dicom_IsNot3DImage" },
    { no: 22, name: "ReportGenerationErrorCode_Dicom_MultipleStudies" },
    { no: 40, name: "ReportGenerationErrorCode_Dicom_InsufficientVolume" },
    { no: 41, name: "ReportGenerationErrorCode_Dicom_DuplicateSlices" },
    { no: 42, name: "ReportGenerationErrorCode_Dicom_Metadata_NonDerivableVoxelSize" },
    { no: 43, name: "ReportGenerationErrorCode_Dicom_Metadata_NonDerivableRescaleInterceptAttribute" },
    { no: 44, name: "ReportGenerationErrorCode_Dicom_Metadata_NonDerivableRescaleSlopeAttribute" },
    { no: 45, name: "ReportGenerationErrorCode_Dicom_Metadata_NonDerivableImagePositionPatientAttribute" },
    { no: 46, name: "ReportGenerationErrorCode_Dicom_Metadata_UnexpectedModality" },
    { no: 47, name: "ReportGenerationErrorCode_Dicom_Metadata_NonDerivableSlicePosition" },
    { no: 48, name: "ReportGenerationErrorCode_Dicom_Metadata_NoPixelData" },
    { no: 49, name: "ReportGenerationErrorCode_Dicom_Metadata_InvalidVoxelSize" },
    { no: 60, name: "ReportGenerationErrorCode_InputData_UnexpectedStudyQuantity" },
    { no: 61, name: "ReportGenerationErrorCode_InputData_UnexpectedStudyContent" },
    { no: 62, name: "ReportGenerationErrorCode_InputData_BadTaskParameters" },
    { no: 80, name: "ReportGenerationErrorCode_Modeling_CbctIosMisalignment" },
    { no: 81, name: "ReportGenerationErrorCode_Modeling_IosWrongOcclusion" },
    { no: 82, name: "ReportGenerationErrorCode_Modeling_IosMissingJaw" },
    { no: 83, name: "ReportGenerationErrorCode_Modeling_RequestedToothNotFound" },
    { no: 84, name: "ReportGenerationErrorCode_Modeling_CbctIosImpossibleSuperimposition" },
    { no: 85, name: "ReportGenerationErrorCode_Modeling_CBCT_Endo_ToothRootsNotFound" },
    { no: 101, name: "ReportGenerationErrorCode_ValidTimeExpired" }
  ]
);

const ReportType = proto3.makeEnum(
  "com.diagnocat.model.ReportType",
  [
    { no: 0, name: "ReportType_InvalidValue" },
    { no: 1, name: "ReportType_CBCT_GP" },
    { no: 2, name: "ReportType_CBCT_Segmentation" },
    { no: 3, name: "ReportType_CBCT_OrthoSlides" },
    { no: 4, name: "ReportType_CBCT_Implant" },
    { no: 5, name: "ReportType_CBCT_Endo" },
    { no: 6, name: "ReportType_CBCT_Molar" },
    { no: 7, name: "ReportType_Pano_GP" },
    { no: 8, name: "ReportType_IOXRay_GP" },
    { no: 9, name: "ReportType_DentalPhoto_GP" },
    { no: 10, name: "ReportType_IOS_Segmentation" },
    { no: 11, name: "ReportType_CBCT_IOS_Superimposition" },
    { no: 12, name: "ReportType_StudioImplant" },
    { no: 14, name: "ReportType_Photo_Ortho" },
    { no: 15, name: "ReportType_StudioOrtho" },
    { no: 16, name: "ReportType_Pano_Bitewings" }
  ]
);
const ReportCancelledReasonCode = proto3.makeEnum(
  "com.diagnocat.model.ReportCancelledReasonCode",
  [
    { no: 0, name: "ReportCancelledReasonCode_Unspecified" },
    { no: 1, name: "ReportCancelledReasonCode_NoImages" }
  ]
);
const ReportPrintSettingsDescriptionLang = proto3.makeEnum(
  "com.diagnocat.model.ReportPrintSettingsDescriptionLang",
  [
    { no: 0, name: "ReportPrintSettings_DescriptionLang_Clinical" },
    { no: 1, name: "ReportPrintSettings_DescriptionLang_Radiological" }
  ]
);
const ReportPrintSettingsPrintType = proto3.makeEnum(
  "com.diagnocat.model.ReportPrintSettingsPrintType",
  [
    { no: 0, name: "ReportPrintSettings_PrintType_Full" },
    { no: 1, name: "ReportPrintSettings_PrintType_Motivation" }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.Report",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "PatientID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 4, name: "SourceStudyIDs", kind: "scalar", T: 9, repeated: true },
    { no: 5, name: "SourceReportIDs", kind: "scalar", T: 9, repeated: true },
    { no: 6, name: "Type", kind: "enum", T: proto3.getEnumType(ReportType) },
    {
      no: 7,
      name: "LegacyConclusion",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 9, name: "ExtendedConclusion", kind: "message", T: ReportExtendedConclusion, opt: true },
    {
      no: 8,
      name: "DisplayAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 11, name: "MedicalImageFeatures", kind: "message", T: MedicalImageFeatures },
    { no: 101, name: "Data_IOXRay_GP", kind: "message", T: Report_IOXRay_GP, oneof: "Specific" },
    { no: 102, name: "Data_Pano_GP", kind: "message", T: Report_Pano_GP, oneof: "Specific" },
    { no: 103, name: "Data_CBCT_GP", kind: "message", T: Report_CBCT_GP, oneof: "Specific" },
    { no: 104, name: "Data_CBCT_Endo", kind: "message", T: Report_CBCT_Endo, oneof: "Specific" },
    { no: 105, name: "Data_CBCT_Implant", kind: "message", T: Report_CBCT_Implant, oneof: "Specific" },
    { no: 106, name: "Data_CBCT_Molar", kind: "message", T: Report_CBCT_Molar, oneof: "Specific" },
    { no: 107, name: "Data_CBCT_Ortho", kind: "message", T: Report_CBCT_Ortho, oneof: "Specific" },
    { no: 108, name: "Data_CBCT_Segmentation", kind: "message", T: Report_CBCT_Segmentation, oneof: "Specific" },
    { no: 109, name: "Data_DentalPhoto", kind: "message", T: Report_DentalPhoto_GP, oneof: "Specific" },
    { no: 110, name: "Data_CBCT_IOS_Superimposition", kind: "message", T: Report_CBCT_IOS_Superimposition, oneof: "Specific" },
    { no: 111, name: "Data_Studio_Implant", kind: "message", T: Report_Studio_Implant, oneof: "Specific" },
    { no: 112, name: "Data_Studio_Ortho", kind: "message", T: Report_Studio_Ortho, oneof: "Specific" },
    { no: 113, name: "Data_Pano_Bitewings", kind: "message", T: Report_Pano_Bitewings, oneof: "Specific" },
    { no: 114, name: "Data_IOS_Segmentation", kind: "message", T: Report_IOS_Segmentation, oneof: "Specific" },
    { no: 201, name: "Settings", kind: "message", T: ReportSettings },
    { no: 202, name: "PrintSettings", kind: "message", T: ReportPrintSettings },
    { no: 203, name: "Signature", kind: "message", T: ReportSignature },
    { no: 301, name: "Status", kind: "message", T: ReportStatus },
    { no: 302, name: "PipelinesTasks", kind: "message", T: Report_PipelinesTask, repeated: true },
    {
      no: 303,
      name: "WasOpened",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 10020, name: "YourPermissions", kind: "message", T: ReportPermissions },
    { no: 10029, name: "OrderedBy", kind: "scalar", T: 9, opt: true },
    { no: 10030, name: "Created", kind: "message", T: Created },
    { no: 10040, name: "Revision", kind: "message", T: Revision },
    { no: 10050, name: "Deleted", kind: "message", T: Deleted },
    { no: 10060, name: "Archived", kind: "message", T: Archived },
    {
      no: 999999,
      name: "BlackURL",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const Report_PipelinesTask = proto3.makeMessageType(
  "com.diagnocat.model.Report.PipelinesTask",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Status", kind: "message", T: ReportStatus },
    { no: 3, name: "SourceStudiesAssetIDs", kind: "scalar", T: 9, repeated: true }
  ],
  { localName: "Report_PipelinesTask" }
);
const ReportStatus = proto3.makeMessageType(
  "com.diagnocat.model.ReportStatus",
  () => [
    {
      no: 1,
      name: "Pending",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "WaitingForUploadsToComplete",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 3,
      name: "TakenByWorker",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 4, name: "InProgress", kind: "message", T: ReportProgress },
    {
      no: 5,
      name: "Completed",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 6, name: "Canceled", kind: "message", T: ReportCanceled },
    { no: 7, name: "Failed", kind: "message", T: ReportFailed }
  ]
);
const ReportProgress = proto3.makeMessageType(
  "com.diagnocat.model.ReportProgress",
  () => [
    {
      no: 1,
      name: "PrimaryPanoramaUploaded",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "SecondaryPanoramaUploaded",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 11,
      name: "AllTeethLocalizationsCompleted",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 12,
      name: "AllConditionsCompleted",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 13,
      name: "AllAssetsUploaded",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);
const ReportFailed = proto3.makeMessageType(
  "com.diagnocat.model.ReportFailed",
  () => [
    {
      no: 1,
      name: "Message",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "Code", kind: "enum", T: proto3.getEnumType(ReportGenerationErrorCode) }
  ]
);
const ReportCanceled = proto3.makeMessageType(
  "com.diagnocat.model.ReportCanceled",
  () => [
    { no: 1, name: "ReportCancelledReason", kind: "enum", T: proto3.getEnumType(ReportCancelledReasonCode) }
  ]
);
const ReportSignature = proto3.makeMessageType(
  "com.diagnocat.model.ReportSignature",
  () => [
    {
      no: 1,
      name: "Signed",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 2, name: "UserSignatures", kind: "message", T: ReportUserSignature, repeated: true }
  ]
);
const ReportUserSignature = proto3.makeMessageType(
  "com.diagnocat.model.ReportUserSignature",
  () => [
    {
      no: 1,
      name: "UserID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 2, name: "SignedAt", kind: "message", T: Timestamp },
    {
      no: 3,
      name: "SignatureAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "StampAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "UserFirstName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 6,
      name: "UserLastName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 7, name: "JobPositions", kind: "enum", T: proto3.getEnumType(OrganizationJobPosition), repeated: true }
  ]
);
const ReportSettings = proto3.makeMessageType(
  "com.diagnocat.model.ReportSettings",
  () => [
    {
      no: 1,
      name: "LowProbabilityMode",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 2,
      name: "ShowProblemAreas",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 4,
      name: "ShowPanoramaSplitInsteadOfSingle",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 5,
      name: "ShowConditionDetails",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 6, name: "ShowPerio", kind: "scalar", T: 8, opt: true },
    { no: 7, name: "ShowRestorative", kind: "scalar", T: 8, opt: true },
    { no: 8, name: "ShowEndo", kind: "scalar", T: 8, opt: true },
    { no: 9, name: "ShowAnatomy", kind: "scalar", T: 8, opt: true }
  ]
);
const ReportPrintSettings = proto3.makeMessageType(
  "com.diagnocat.model.ReportPrintSettings",
  () => [
    { no: 1, name: "DescriptionLang", kind: "enum", T: proto3.getEnumType(ReportPrintSettingsDescriptionLang) },
    { no: 2, name: "PrintType", kind: "enum", T: proto3.getEnumType(ReportPrintSettingsPrintType) },
    {
      no: 3,
      name: "ShowDiagnoses",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 4,
      name: "ShowDiagnosesProbabilities",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 5,
      name: "ShowDentalChart",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 6,
      name: "ShowSlices",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 7,
      name: "ShowTopJaw",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 8,
      name: "ShowBottomJaw",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 9,
      name: "ShowBBoxes",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 10,
      name: "ShowReferralRecommendations",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 11,
      name: "ShowOrthodonticScreening",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 12,
      name: "ShowStudyMetadata",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 15,
      name: "ShowMasks",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 16, name: "ShowPerio", kind: "scalar", T: 8, opt: true },
    { no: 17, name: "ShowRestorative", kind: "scalar", T: 8, opt: true },
    { no: 18, name: "ShowEndo", kind: "scalar", T: 8, opt: true },
    { no: 19, name: "ShowAnatomy", kind: "scalar", T: 8, opt: true },
    {
      no: 13,
      name: "BlackAndWhiteMode",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 14,
      name: "IsInverted",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);
const ReportExtendedConclusion = proto3.makeMessageType(
  "com.diagnocat.model.ReportExtendedConclusion",
  () => [
    {
      no: 1,
      name: "Patient",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "DOB",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "Address",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "PrescribingDoctor",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "StudyDate",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 6,
      name: "TypeOfScan",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 7,
      name: "FOV",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 8,
      name: "ScanSource",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 9,
      name: "CBCTModel",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 10,
      name: "IndicationForScan",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 11,
      name: "LimitationsFactors",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 12,
      name: "Observations",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 13,
      name: "Dentition",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 14,
      name: "AreaOfInterest",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 15,
      name: "Sinuses",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 16,
      name: "Airway",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 17,
      name: "Osseous",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 18,
      name: "Additional",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 19,
      name: "Impressions",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);

const DentalNotationFormat = proto3.makeEnum(
  "com.diagnocat.model.DentalNotationFormat",
  [
    { no: 0, name: "DentalNotationFormatISO" },
    { no: 1, name: "DentalNotationFormatUniversal" }
  ]
);
const OrganizationType = proto3.makeEnum(
  "com.diagnocat.model.OrganizationType",
  [
    { no: 0, name: "OrganizationType_InvalidValue" },
    { no: 1, name: "OrganizationType_Dental" },
    { no: 2, name: "OrganizationType_NonDental" }
  ]
);
const FeatureFlag = proto3.makeEnum(
  "com.diagnocat.model.FeatureFlag",
  [
    { no: 0, name: "FeatureFlag_InvalidValue" },
    { no: 1, name: "FeatureFlag_Show_BillingShop" },
    { no: 7, name: "FeatureFlag_Show_SegmentronConditionsButtons" },
    { no: 8, name: "FeatureFlag_Show_NotForCommercialUseBanner" },
    { no: 9, name: "FeatureFlag_Show_IOSReportButton" },
    { no: 10, name: "FeatureFlag_Show_OrthoStudioAddSecondaryCBCT" },
    { no: 11, name: "FeatureFlag_Show_DentalPhotosWidget" },
    { no: 12, name: "FeatureFlag_Show_CBCTExtendedConclusion" },
    { no: 51, name: "FeatureFlag_Enable_SegmentronSerialisation" },
    { no: 52, name: "FeatureFlag_Enable_ToothSuggestedTreatment" },
    { no: 54, name: "FeatureFlag_Enable_OrthoStudio" },
    { no: 56, name: "FeatureFlag_Enable_StorageFeature" },
    { no: 57, name: "FeatureFlag_Enable_FullHeadMPRs" },
    { no: 58, name: "FeatureFlag_Enable_DICOMToDCCTConversion" },
    { no: 59, name: "FeatureFlag_Enable_DICOMToDCCTConversionFallback" },
    { no: 60, name: "FeatureFlag_Enable_SegmentronAirwaysHeatmap" },
    { no: 65, name: "FeatureFlag_Enable_AgentChat" },
    { no: 67, name: "FeatureFlag_Enable_ConsultationTemplates" },
    { no: 68, name: "FeatureFlag_Enable_Analytics" },
    { no: 69, name: "FeatureFlag_Enable_Encounters" },
    { no: 70, name: "FeatureFlag_Enable_CommunityPublishing" },
    { no: 71, name: "FeatureFlag_Enable_Diagnocoins" },
    { no: 72, name: "FeatureFlag_Enable_ArchiveReports" },
    { no: 73, name: "FeatureFlag_Enable_ReferralProgram" },
    { no: 74, name: "FeatureFlag_Enable_MaskEditing" },
    { no: 75, name: "FeatureFlag_Enable_SegmentronROI" },
    { no: 76, name: "FeatureFlag_Enable_MFASettings" },
    { no: 61, name: "FeatureFlag_FDA_Annotator" },
    { no: 62, name: "FeatureFlag_FDA_Unaided" },
    { no: 63, name: "FeatureFlag_FDA_SegmentronViewerRestrictions" },
    { no: 64, name: "FeatureFlag_FDA_3DGPRestrictions" },
    { no: 66, name: "FeatureFlag_FDA_Version2" },
    { no: 101, name: "FeatureFlag_Hide_Probabilities" },
    { no: 102, name: "FeatureFlag_Hide_ConditionsMasks" },
    { no: 103, name: "FeatureFlag_Hide_PerioMasksAndSlices" },
    { no: 104, name: "FeatureFlag_Hide_CariesSigns" },
    { no: 105, name: "FeatureFlag_Hide_PBLRulesAndMeasurements" },
    { no: 106, name: "FeatureFlag_Hide_AnatomyMasks" },
    { no: 107, name: "FeatureFlag_Hide_AllCBCTMasks" },
    { no: 108, name: "FeatureFlag_Hide_NonDentalFindings" },
    { no: 151, name: "FeatureFlag_Forbid_UploadsFromWeb" },
    { no: 152, name: "FeatureFlag_Forbid_PatientEditing" },
    { no: 153, name: "FeatureFlag_Forbid_Sharing" }
  ]
);
const FeatureFlagType = proto3.makeEnum(
  "com.diagnocat.model.FeatureFlagType",
  [
    { no: 0, name: "FeatureFlagType_InvalidValue" },
    { no: 1, name: "FeatureFlagType_Development" },
    { no: 2, name: "FeatureFlagType_Regulatory" },
    { no: 3, name: "FeatureFlagType_Settings" }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.Organization",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Name",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 3, name: "Type", kind: "enum", T: proto3.getEnumType(OrganizationType) },
    {
      no: 4,
      name: "PrintName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 5,
      name: "LogoAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 6,
      name: "StampAssetID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 7, name: "Contacts", kind: "message", T: OrganizationContacts },
    { no: 8, name: "Settings", kind: "message", T: OrganizationSettings },
    { no: 9, name: "AvailableJobPositions", kind: "enum", T: proto3.getEnumType(OrganizationJobPosition), repeated: true },
    {
      no: 15,
      name: "OwnerEmail",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 11, name: "FeatureFlags", kind: "message", T: OrganizationFeatureFlag, repeated: true },
    {
      no: 12,
      name: "Comment",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 13, name: "OnboardedByDistributor", kind: "message", T: OrganizationOnboardedByDistributor },
    { no: 14, name: "Distributor", kind: "message", T: Distributor },
    { no: 16, name: "DSOParent", kind: "message", T: OrganizationDSOParent },
    {
      no: 17,
      name: "IsDSO",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 18, name: "Billing", kind: "message", T: OrganizationBilling },
    { no: 19, name: "MFARequiredForActor", kind: "scalar", T: 8, opt: true },
    { no: 21, name: "Tags", kind: "scalar", T: 9, repeated: true },
    { no: 10020, name: "YourPermissions", kind: "message", T: OrganizationPermissions },
    { no: 10030, name: "Created", kind: "message", T: Created },
    { no: 10040, name: "Revision", kind: "message", T: Revision },
    { no: 10050, name: "Deleted", kind: "message", T: Deleted }
  ]
);
const OrganizationSettings = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationSettings",
  () => [
    { no: 1, name: "DentalNotationFormat", kind: "enum", T: proto3.getEnumType(DentalNotationFormat) },
    {
      no: 2,
      name: "Language",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "MFARequireForAllMembers",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);
const OrganizationContacts = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationContacts",
  () => [
    {
      no: 1,
      name: "Country",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Region",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "City",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 4,
      name: "Address",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    { no: 5, name: "Phones", kind: "scalar", T: 9, repeated: true },
    { no: 6, name: "Emails", kind: "scalar", T: 9, repeated: true },
    {
      no: 7,
      name: "WebSite",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 8,
      name: "ZipCode",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.OrganizationDefinedPersonalData",
  () => [
    {
      no: 1,
      name: "FirstName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "LastName",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "Email",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const Distributor = proto3.makeMessageType(
  "com.diagnocat.model.Distributor",
  () => [
    {
      no: 1,
      name: "Name",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const OrganizationOnboardedByDistributor = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationOnboardedByDistributor",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Email",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 3,
      name: "Name",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const OrganizationDSOParent = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationDSOParent",
  () => [
    {
      no: 1,
      name: "ID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Email",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    }
  ]
);
const OrganizationBilling = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationBilling",
  () => [
    { no: 1, name: "SubscriptionEndDate", kind: "message", T: Timestamp, opt: true },
    {
      no: 2,
      name: "IsSubscriptionActive",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    }
  ]
);
const OrganizationFeatureFlag = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationFeatureFlag",
  () => [
    { no: 1, name: "Flag", kind: "enum", T: proto3.getEnumType(FeatureFlag) },
    {
      no: 2,
      name: "Value",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    {
      no: 3,
      name: "ReadOnly",
      kind: "scalar",
      T: 8
      /* ScalarType.BOOL */
    },
    { no: 4, name: "Type", kind: "enum", T: proto3.getEnumType(FeatureFlagType) }
  ]
);
proto3.makeMessageType(
  "com.diagnocat.model.OrganizationStorageSize",
  () => [
    {
      no: 1,
      name: "OrganizationID",
      kind: "scalar",
      T: 9
      /* ScalarType.STRING */
    },
    {
      no: 2,
      name: "Total",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 3,
      name: "Own",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    {
      no: 6,
      name: "Patients",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    },
    { no: 4, name: "Studies", kind: "message", T: OrganizationStorageStudySize, repeated: true },
    { no: 5, name: "Reports", kind: "message", T: OrganizationStorageReportSize, repeated: true }
  ]
);
const OrganizationStorageStudySize = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationStorageStudySize",
  () => [
    { no: 1, name: "Type", kind: "enum", T: proto3.getEnumType(StudyType) },
    {
      no: 2,
      name: "Size",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    }
  ]
);
const OrganizationStorageReportSize = proto3.makeMessageType(
  "com.diagnocat.model.OrganizationStorageReportSize",
  () => [
    { no: 1, name: "Type", kind: "enum", T: proto3.getEnumType(ReportType) },
    {
      no: 2,
      name: "Size",
      kind: "scalar",
      T: 3
      /* ScalarType.INT64 */
    }
  ]
);

const ISO_TO_UNIVERSAL = {
  11: 8,
  12: 7,
  13: 6,
  14: 5,
  15: 4,
  16: 3,
  17: 2,
  18: 1,
  21: 9,
  22: 10,
  23: 11,
  24: 12,
  25: 13,
  26: 14,
  27: 15,
  28: 16,
  31: 24,
  32: 23,
  33: 22,
  34: 21,
  35: 20,
  36: 19,
  37: 18,
  38: 17,
  41: 25,
  42: 26,
  43: 27,
  44: 28,
  45: 29,
  46: 30,
  47: 31,
  48: 32,
  55: "A",
  54: "B",
  53: "C",
  52: "D",
  51: "E",
  61: "F",
  62: "G",
  63: "H",
  64: "I",
  65: "J",
  71: "O",
  72: "N",
  73: "M",
  74: "L",
  75: "K",
  85: "T",
  84: "S",
  83: "R",
  82: "Q",
  81: "P"
};
const TOOTH_NUMBER_COLORS = {
  default: {
    text: "#212020",
    handle: { r: 1, g: 1, b: 1, a: 0.6 },
    mask: { r: 1, g: 1, b: 1, a: 0.1 },
    outline: { r: 33 / 255, g: 32 / 255, b: 32 / 255, a: 1 }
  },
  warning: {
    text: "#ff254e",
    handle: { r: 1, g: 0.9, b: 0.9, a: 0.6 },
    mask: { r: 255 / 255, g: 37 / 255, b: 78 / 255, a: 0.05 },
    outline: { r: 255 / 255, g: 37 / 255, b: 78 / 255, a: 1 }
  }
};
class ToothNumber extends Primitive {
  constructor(props, viewport) {
    super(new Group());
    this.toothMasks = [];
    this.duplicateToothNumbers = [];
    this.toothNumber = props.toothNumber;
    this.scale = props.scale ?? 1;
    this.toothID = props.toothID;
    this.detectionID = props.detectionID;
    this.isRemoved = props.isRemoved;
    this.currentNotationFormat = props.format ?? DentalNotationFormat.DentalNotationFormatISO;
    this.text = new TextEntity({
      text: this.getText(),
      fontSize: props.textProps?.fontSize ?? 22,
      anchorY: props.textProps?.anchorY ?? "middle",
      anchorX: props.textProps?.anchorX ?? "center",
      color: props.textProps?.color ?? TOOTH_NUMBER_COLORS.default.text
    });
    const outlineColor = TOOTH_NUMBER_COLORS.default.outline;
    this.handle = new RectangleHandleWithOutline(new Vector3(), {
      size: props.rectangleProps?.size ?? 1,
      width: props.rectangleProps?.width ?? 40,
      height: props.rectangleProps?.height ?? 40,
      radius: props.rectangleProps?.radius ?? 9,
      color: props.rectangleProps?.color ?? "white",
      opacity: outlineColor.a,
      outlineColor: new Color(outlineColor.r, outlineColor.g, outlineColor.b),
      outlineWidth: 2,
      maxZoom: 0.15 / this.scale,
      renderOrder: 10
    }, viewport);
    this.handle.object3d.material.transparent = true;
    this.add(this.handle);
    this.add(this.text);
    this.text.object3d.frustumCulled = false;
    this.setComponents();
    this.setRenderOrders();
    this.setBehavior();
    this.update();
  }
  setRenderOrders() {
    this.handle.object3d.renderOrder = 10;
    this.text.object3d.renderOrder = 12;
  }
  setBehavior() {
    if (!this.text) return;
    const viewport = new Vector4();
    this.text.object3d.matrixAutoUpdate = false;
    const minZoom = 0.15 / this.scale;
    this.handle.onBeforeRenderSet.add((...args) => {
      if (!this.text) return;
      const renderer = args[0];
      const camera = args[2];
      renderer.getViewport(viewport);
      let zoom = 2 / (camera.projectionMatrix.elements[5] * viewport.w);
      this.text.object3d.visible = true;
      zoom = Math.min(zoom, minZoom);
      this.text.object3d.scale.set(zoom, zoom, 1);
      if (this.isRemovedOrPendingRemove) {
        this.text.object3d.position.y = 2 * zoom;
      } else {
        this.text.object3d.position.y = 0;
      }
      this.text.object3d.updateMatrix();
      this.text.object3d.matrixWorld.copy(this.text.object3d.matrix);
      this.text.object3d.matrixWorld.copyPosition(this.text.object3d.parent.matrixWorld);
    });
  }
  get isRemovedOrPendingRemove() {
    return this.pendingToothNumber === -1 || (!this.pendingToothNumber ? this.isRemoved : false);
  }
  convertToothNumber(isoNumber, format) {
    if (format === DentalNotationFormat.DentalNotationFormatUniversal) {
      const universalNotation = ISO_TO_UNIVERSAL[isoNumber];
      if (universalNotation !== void 0) {
        return universalNotation.toString();
      }
    }
    return isoNumber.toString();
  }
  getText() {
    const number = this.pendingToothNumber || this.toothNumber.ISO;
    const showAsterisk = !this.pendingToothNumber && this.toothNumber.SupernumeraryIndex || this.pendingToothNumber && this.duplicateToothNumbers.length;
    const displayNumber = !this.isRemovedOrPendingRemove ? this.convertToothNumber(number, this.currentNotationFormat) : "\xD7";
    return displayNumber + (showAsterisk ? "*" : "");
  }
  update() {
    const text = this.getText();
    if (this.text && this.text.object3d.text !== text) {
      this.text.setProps({ text });
      if (this.isRemovedOrPendingRemove) {
        this.text.setProps({ fontSize: 34 });
      } else {
        this.text.setProps({ fontSize: 22 });
      }
    }
  }
  setToothNumber(toothNumber) {
    this.toothNumber = toothNumber;
    this.update();
  }
  async setPendingNumber(pendingToothNumber) {
    if (this.isRemoved || pendingToothNumber !== this.toothNumber.ISO && pendingToothNumber !== this.pendingToothNumber) {
      this.pendingToothNumber = pendingToothNumber;
      this.update();
      this.add(this.toothMasks);
      this.updateMaskColors(TOOTH_NUMBER_COLORS.default);
    } else {
      this.resetPendingNumber();
    }
  }
  async setPendingRemove() {
    if (this.isRemoved) {
      this.resetPendingNumber();
    } else {
      this.setPendingNumber(-1);
    }
  }
  resetPendingNumber() {
    this.pendingToothNumber = null;
    this.update();
    this.delete(this.toothMasks);
  }
  setNotationFormat(format) {
    if (this.currentNotationFormat === format) return;
    this.currentNotationFormat = format;
    this.update();
  }
  addMasks(masks) {
    this.toothMasks.push(...masks);
    this.toothMasks.forEach((toothMask) => {
      this.object3d.updateMatrix();
      this.object3d.updateMatrixWorld(true);
      toothMask.object3d.applyMatrix4(this.object3d.matrix.clone().invert());
    });
  }
  setWarning(value, duplicates) {
    if (duplicates) {
      this.duplicateToothNumbers = duplicates.filter((tooth) => tooth !== this);
    } else {
      this.duplicateToothNumbers = [];
    }
    let colors;
    if (value) {
      colors = TOOTH_NUMBER_COLORS.warning;
      this.add(this.toothMasks);
      this.handle.object3d.material.uniforms.color.value.set(colors.handle.r, colors.handle.g, colors.handle.b, colors.handle.a);
      this.handle.object3d.material.uniforms.outlineColor.value.set(colors.outline.r, colors.outline.g, colors.outline.b, colors.outline.a);
      this.updateMaskColors(colors);
    } else {
      colors = TOOTH_NUMBER_COLORS.default;
      if (!this.pendingToothNumber) {
        this.delete(this.toothMasks);
      }
      this.handle.object3d.material.uniforms.color.value = new Vector4(colors.handle.r, colors.handle.g, colors.handle.b, colors.handle.a);
      this.handle.object3d.material.uniforms.outlineColor.value = new Vector4(colors.outline.r, colors.outline.g, colors.outline.b, colors.outline.a);
      this.updateMaskColors(colors);
    }
    const text = this.getText();
    this.text.setProps({ color: colors.text, text });
  }
  updateMaskColors(colors) {
    this.toothMasks.forEach((mask) => {
      if (mask.object3d.material) {
        const material = mask.object3d.material;
        material.color.setRGB(colors.mask.r, colors.mask.g, colors.mask.b);
        material.opacity = 1;
      }
      if (mask.fill && mask.fill.material) {
        mask.fill.material.color.setRGB(colors.mask.r, colors.mask.g, colors.mask.b);
        mask.fill.material.opacity = colors.mask.a;
      }
    });
  }
  setComponents() {
    this.setComponent(RaycastChildrenOnly);
    this.handle.setComponent(RectangleHandleWithOutlineRaycast, new RectangleHandleWithOutlineRaycast(this.handle, 0));
    this.hoverComponent = new HoverComponent(this, new ToothNumberHighlighter(this, 1.2));
    this.handle.setComponent(HoverComponent, this.hoverComponent);
  }
  dispose() {
    this.toothMasks.forEach((mask) => mask.dispose());
    this.toothMasks = [];
    super.dispose();
  }
}

class GlobalConfig {
  constructor() {
    this._paths = null;
  }
  static Instance() {
    if (!GlobalConfig.instance) {
      GlobalConfig.instance = new GlobalConfig();
    }
    return GlobalConfig.instance;
  }
  setPaths(paths) {
    this._paths = paths;
  }
  get paths() {
    if (!this._paths) {
      throw new Error("Paths have not been initialized");
    }
    return this._paths;
  }
}
var GlobalConfig$1 = GlobalConfig.Instance();

const collectConditionCodesFromGroups = (groups) => {
  return Object.values(groups).flatMap((conditionCodes) => conditionCodes);
};

const RED_CODES = [
  ConditionCode.VoidsInTheFilling,
  ConditionCode.InternalResorption,
  ConditionCode.Child_EndoTreated_Complications_RootPerforation,
  ConditionCode.Child_EndoTreated_Quality_VoidsPresentInTheRootFilling,
  ConditionCode.HorizontalDisplacement,
  ConditionCode.LateralRadiolucency,
  ConditionCode.Missing,
  ConditionCode.Child_EndoTreated_Obturation_Overfilling,
  ConditionCode.Child_EndoTreated_Complications_SeparatedInstrumentInTheRootCanal,
  ConditionCode.CrownDefectOver50pct,
  ConditionCode.VerticalRootFracture,
  ConditionCode.Overhang,
  ConditionCode.PDLSpaceWidening_AlongTheRoot,
  ConditionCode.PDLSpaceWidening_InPeriapicalRegion,
  ConditionCode.RestorationFracture,
  ConditionCode.CrownFracture,
  ConditionCode.Attrition,
  ConditionCode.ForeignBody,
  ConditionCode.DentalCalculus,
  ConditionCode.LackOfInterproximalContact,
  ConditionCode.Impaction,
  ConditionCode.HorizontalRootFracture,
  ConditionCode.RootFragment,
  ConditionCode.Child_EndoTreated_Obturation_MissedCanal,
  ConditionCode.Overeruption,
  ConditionCode.OpenMargin,
  ConditionCode.FurcationLesion,
  ConditionCode.EctopicEruption,
  ConditionCode.SecondaryCaries,
  ConditionCode.CanalObliteration,
  ConditionCode.CariesSigns,
  ConditionCode.Abfraction,
  ConditionCode.Child_EndoTreated_Obturation_ShortFilling,
  ConditionCode.Hypercementosis,
  ConditionCode.PeriodontalBoneLoss,
  ConditionCode.Child_EndoTreated_Quality_VoidsInThePostArea,
  ConditionCode.Extrusion,
  ConditionCode.Intrusion,
  ConditionCode.LateralLuxation,
  ConditionCode.CervicalResorption,
  ConditionCode.PreEruptiveResorption,
  ConditionCode.ExternalResorption,
  ConditionCode.PeriapicalRadiolucency,
  ConditionCode.PeriapicalRadiopacity,
  ConditionCode.SingsOfCommunicationWithMaxillarySinus,
  ConditionCode.SignsOfInferiorAlveolarCanalInvolvement,
  ConditionCode.PeriImplantitis,
  ConditionCode.FDA_Caries,
  ConditionCode.FDA_RestorativeImperfections
];
const PURPLE_CODES = [
  ConditionCode.Child_EndoTreated_Obturation_AdequateObturation,
  ConditionCode.ArtificialCrown,
  ConditionCode.Child_EndoTreated_Complications_RootPerforation,
  ConditionCode.IndirectRestoration,
  ConditionCode.Pontic,
  ConditionCode.Child_EndoTreated_PostAndCore_CastPostAndCore,
  ConditionCode.Pulpotomy,
  ConditionCode.Implant,
  ConditionCode.Child_EndoTreated_PostAndCore_FiberglassPost,
  ConditionCode.Child_EndoTreated_Obturation_MissedCanal,
  ConditionCode.EndoTreated,
  ConditionCode.OrthodonticAppliance,
  ConditionCode.Filling,
  ConditionCode.Apicoectomy,
  ConditionCode.Child_EndoTreated_PostAndCore_MetalPost,
  ConditionCode.Child_EndoTreated_PostAndCore_Post,
  ConditionCode.Child_EndoTreated_Quality_AdequateDensity,
  ConditionCode.HealingAbutment,
  ConditionCode.EndodonticAccess,
  ConditionCode.FDA_Child_EndoTreated_CanalFilling,
  ConditionCode.FDA_Child_EndoTreated_PostAndCore
];
const YELLOW_CODES = [
  ConditionCode.CariesSigns,
  ConditionCode.PDLSpaceWidening_AlongTheRoot,
  ConditionCode.PDLSpaceWidening_InPeriapicalRegion,
  ConditionCode.PeriapicalRadiolucency,
  ConditionCode.PeriapicalRadiopacity
];
const WHOLE_TOOTH_BBOX_CODES = [
  ConditionCode.EndoTreated,
  ConditionCode.CrownDefectOver50pct
];
var ConditionGroups = /* @__PURE__ */ ((ConditionGroups2) => {
  ConditionGroups2["toothType"] = "type";
  ConditionGroups2["rootsNumbers"] = "rootsNumbers";
  ConditionGroups2["canalsNumbers"] = "canalsNumbers";
  ConditionGroups2["perio"] = "perio";
  ConditionGroups2["position"] = "position";
  ConditionGroups2["crown"] = "crown";
  ConditionGroups2["root"] = "root";
  ConditionGroups2["endo"] = "endo";
  ConditionGroups2["implant"] = "implant";
  ConditionGroups2["periradicularPathologies"] = "periradicularPathologies";
  ConditionGroups2["mucosalThickening"] = "mucosalThickening";
  return ConditionGroups2;
})(ConditionGroups || {});
var ChildConditionGroups = /* @__PURE__ */ ((ChildConditionGroups2) => {
  ChildConditionGroups2["depth"] = "depth";
  ChildConditionGroups2["surface"] = "surface";
  ChildConditionGroups2["perioSeverity"] = "perioSeverity";
  ChildConditionGroups2["perioType"] = "perioType";
  ChildConditionGroups2["impaction"] = "impaction";
  ChildConditionGroups2["obturation"] = "obturation";
  ChildConditionGroups2["quality"] = "quality";
  ChildConditionGroups2["complications"] = "complications";
  ChildConditionGroups2["buildup"] = "buildup";
  ChildConditionGroups2["construction"] = "construction";
  ChildConditionGroups2["involvement"] = "involvement";
  ChildConditionGroups2["mucosalThickeningType"] = "mucosalThickeningType";
  return ChildConditionGroups2;
})(ChildConditionGroups || {});
const ONE_OF_CONDITION_GROUPS = ["type" /* toothType */];
const TOOTH_TYPE_GROUP = {
  ["type" /* toothType */]: [
    ConditionCode.CodeTooth,
    ConditionCode.Implant,
    ConditionCode.Pontic,
    ConditionCode.RootFragment,
    ConditionCode.Missing,
    ConditionCode.ToothGerm
  ]
};
const TOOTH_ANATOMY_GROUPS = {
  ["rootsNumbers" /* rootsNumbers */]: [
    ConditionCode.Roots1,
    ConditionCode.Roots2,
    ConditionCode.Roots3,
    ConditionCode.Roots4
  ],
  ["canalsNumbers" /* canalsNumbers */]: [
    ConditionCode.Canals1,
    ConditionCode.Canals2,
    ConditionCode.Canals3,
    ConditionCode.Canals4,
    ConditionCode.Canals5
  ]
};
const TOOTH_GENERAL_GROUPS = {
  ["perio" /* perio */]: [
    ConditionCode.PeriodontalBoneLoss,
    ConditionCode.FurcationLesion,
    ConditionCode.DentalCalculus
  ],
  ["position" /* position */]: [
    ConditionCode.Impaction,
    ConditionCode.HorizontalDisplacement,
    ConditionCode.Overeruption,
    ConditionCode.EctopicEruption,
    ConditionCode.Extrusion,
    ConditionCode.Intrusion,
    ConditionCode.LateralLuxation
  ],
  ["crown" /* crown */]: [
    ConditionCode.Filling,
    ConditionCode.ArtificialCrown,
    ConditionCode.IndirectRestoration,
    ConditionCode.OrthodonticAppliance,
    ConditionCode.HealingAbutment,
    ConditionCode.PulpStone,
    ConditionCode.Attrition,
    ConditionCode.Abfraction,
    ConditionCode.CrownFracture,
    ConditionCode.RestorationFracture,
    ConditionCode.CrownDefectOver50pct,
    ConditionCode.CervicalResorption,
    ConditionCode.PreEruptiveResorption,
    ConditionCode.VoidsInTheFilling,
    ConditionCode.Overhang,
    ConditionCode.OpenMargin,
    ConditionCode.LackOfInterproximalContact,
    ConditionCode.EndodonticAccess,
    ConditionCode.ToothStump,
    ConditionCode.CariesSigns,
    ConditionCode.SecondaryCaries
  ],
  ["root" /* root */]: [
    ConditionCode.InternalResorption,
    ConditionCode.ExternalResorption,
    ConditionCode.HorizontalRootFracture,
    ConditionCode.VerticalRootFracture,
    ConditionCode.CanalObliteration,
    ConditionCode.Hypercementosis,
    ConditionCode.Apicoectomy
  ],
  ["endo" /* endo */]: [ConditionCode.Pulpotomy, ConditionCode.EndoTreated],
  ["implant" /* implant */]: [ConditionCode.PeriImplantitis],
  ["periradicularPathologies" /* periradicularPathologies */]: [
    ConditionCode.PeriapicalRadiolucency,
    ConditionCode.LateralRadiolucency,
    ConditionCode.PDLSpaceWidening_InPeriapicalRegion,
    ConditionCode.PDLSpaceWidening_AlongTheRoot,
    ConditionCode.PeriapicalRadiopacity,
    ConditionCode.SingsOfCommunicationWithMaxillarySinus,
    ConditionCode.SignsOfInferiorAlveolarCanalInvolvement
  ]
};
const CONDITION_CODES_BY_GROUPS = {
  ...TOOTH_TYPE_GROUP,
  ...TOOTH_ANATOMY_GROUPS,
  ...TOOTH_GENERAL_GROUPS
};
const FLAT_CONDITION_GROUP_CODES = collectConditionCodesFromGroups(
  CONDITION_CODES_BY_GROUPS
);
const PERIODONTAL_BONE_LOSS_CHILD_GROUP = {
  [ConditionCode.PeriodontalBoneLoss]: {
    ["perioSeverity" /* perioSeverity */]: [
      ConditionCode.Child_PeriodontalBoneLoss_Severity_Mild,
      ConditionCode.Child_PeriodontalBoneLoss_Severity_Moderate,
      ConditionCode.Child_PeriodontalBoneLoss_Severity_Severe
    ],
    ["perioType" /* perioType */]: [
      ConditionCode.Child_PeriodontalBoneLoss_Type_Horizontal,
      ConditionCode.Child_PeriodontalBoneLoss_Type_Vertical,
      ConditionCode.Child_PeriodontalBoneLoss_Type_Mixed
    ]
  }
};
const IMPACTION_GROUP = {
  [ConditionCode.Impaction]: {
    ["impaction" /* impaction */]: [
      ConditionCode.Child_Impaction_Subtype_CompleteBony,
      ConditionCode.Child_Impaction_Subtype_PartiallyBony
    ]
  }
};
const FILLING_CHILD_GROUP = {
  [ConditionCode.Filling]: {
    ["depth" /* depth */]: [
      ConditionCode.Child_DefectDepth_Enamel,
      ConditionCode.Child_DefectDepth_Dentin,
      ConditionCode.Child_DefectDepth_WithPulpExposure,
      ConditionCode.Child_DefectDepth_Root,
      ConditionCode.Child_DefectDepth_NotDefined
    ],
    ["surface" /* surface */]: [
      ConditionCode.Child_DefectSurface_Mesial,
      ConditionCode.Child_DefectSurface_Distal,
      ConditionCode.Child_DefectSurface_Occlusial,
      ConditionCode.Child_DefectSurface_IncisalEdge,
      ConditionCode.Child_DefectSurface_Buccal,
      ConditionCode.Child_DefectSurface_Vestibular,
      ConditionCode.Child_DefectSurface_Lingual,
      ConditionCode.Child_DefectSurface_NotDefined
    ],
    ["buildup" /* buildup */]: [ConditionCode.Child_Filling_BuildUp]
  }
};
const CROWN_FRACTURE_CHILD_GROUP = {
  [ConditionCode.CrownFracture]: {
    ["depth" /* depth */]: [
      ConditionCode.Child_DefectDepth_Enamel,
      ConditionCode.Child_DefectDepth_Dentin,
      ConditionCode.Child_DefectDepth_WithPulpExposure,
      ConditionCode.Child_DefectDepth_CrownRoot
    ]
  }
};
const CARIES_CHILD_GROUP = {
  [ConditionCode.CariesSigns]: {
    ["surface" /* surface */]: [
      ConditionCode.Child_DefectSurface_Mesial,
      ConditionCode.Child_DefectSurface_Distal,
      ConditionCode.Child_DefectSurface_Occlusial,
      ConditionCode.Child_DefectSurface_IncisalEdge,
      ConditionCode.Child_DefectSurface_Buccal,
      ConditionCode.Child_DefectSurface_Vestibular,
      ConditionCode.Child_DefectSurface_Lingual,
      ConditionCode.Child_DefectSurface_NotDefined
    ],
    ["depth" /* depth */]: [
      ConditionCode.Child_DefectDepth_Initial,
      ConditionCode.Child_DefectDepth_Enamel,
      ConditionCode.Child_DefectDepth_Dentin,
      ConditionCode.Child_DefectDepth_WithPulpExposure,
      ConditionCode.Child_DefectDepth_Root,
      ConditionCode.Child_DefectDepth_NotDefined
    ]
  }
};
const SECONDARY_CARIES_CHILD_GROUP = {
  [ConditionCode.SecondaryCaries]: {
    ["depth" /* depth */]: [
      ConditionCode.Child_DefectDepth_Initial,
      ConditionCode.Child_DefectDepth_Enamel,
      ConditionCode.Child_DefectDepth_Dentin,
      ConditionCode.Child_DefectDepth_WithPulpExposure,
      ConditionCode.Child_DefectDepth_Root,
      ConditionCode.Child_DefectDepth_NotDefined
    ],
    ["surface" /* surface */]: [
      ConditionCode.Child_DefectSurface_Mesial,
      ConditionCode.Child_DefectSurface_Distal,
      ConditionCode.Child_DefectSurface_Occlusial,
      ConditionCode.Child_DefectSurface_IncisalEdge,
      ConditionCode.Child_DefectSurface_Buccal,
      ConditionCode.Child_DefectSurface_Vestibular,
      ConditionCode.Child_DefectSurface_Lingual,
      ConditionCode.Child_DefectSurface_NotDefined
    ]
  }
};
const ENDO_CHILD_GROUP = {
  [ConditionCode.EndoTreated]: {
    ["obturation" /* obturation */]: [
      ConditionCode.Child_EndoTreated_Obturation_AdequateObturation,
      ConditionCode.Child_EndoTreated_Obturation_ShortFilling,
      ConditionCode.Child_EndoTreated_Obturation_Overfilling,
      ConditionCode.Child_EndoTreated_Obturation_MissedCanal
    ],
    ["quality" /* quality */]: [
      ConditionCode.Child_EndoTreated_Quality_AdequateDensity,
      ConditionCode.Child_EndoTreated_Quality_VoidsPresentInTheRootFilling,
      ConditionCode.Child_EndoTreated_Quality_VoidsInThePostArea
    ],
    ["construction" /* construction */]: [
      ConditionCode.Child_EndoTreated_PostAndCore_CastPostAndCore,
      ConditionCode.Child_EndoTreated_PostAndCore_FiberglassPost,
      ConditionCode.Child_EndoTreated_PostAndCore_MetalPost,
      ConditionCode.Child_EndoTreated_PostAndCore_Post
    ],
    ["complications" /* complications */]: [
      ConditionCode.Child_EndoTreated_Complications_RootPerforation,
      ConditionCode.Child_EndoTreated_Complications_SeparatedInstrumentInTheRootCanal
    ]
  }
};
const MUCOSAL_THICKENING_TYPE_CHILD_GROUP = {
  [ConditionCode.MucosalThickening]: {
    ["mucosalThickeningType" /* mucosalThickeningType */]: [
      ConditionCode.Child_MucosalThickening_Type_Focal,
      ConditionCode.Child_MucosalThickening_Type_Diffused,
      ConditionCode.Child_MucosalThickening_Type_CompleteOrPartialObstruction
    ]
  }
};
const CHILD_CONDITION_GROUPS = {
  ...PERIODONTAL_BONE_LOSS_CHILD_GROUP,
  ...IMPACTION_GROUP,
  ...FILLING_CHILD_GROUP,
  ...CROWN_FRACTURE_CHILD_GROUP,
  ...CARIES_CHILD_GROUP,
  ...SECONDARY_CARIES_CHILD_GROUP,
  ...ENDO_CHILD_GROUP,
  ...MUCOSAL_THICKENING_TYPE_CHILD_GROUP
};
const CONDITION_GROUP_BY_CODE = Object.entries(
  CONDITION_CODES_BY_GROUPS
).reduce((acc, [group, conditionCodes]) => {
  conditionCodes.forEach((code) => {
    acc[code] = group;
  });
  return acc;
}, {});
function mergeCustomizer(objValue, srcValue) {
  if (isArray(objValue)) {
    return objValue.concat(srcValue);
  }
}
const CHILD_CONDITION_GROUP_BY_CODE = Object.entries(
  Object.values(CHILD_CONDITION_GROUPS).reduce(
    (acc, curr) => mergeWith(acc, curr, mergeCustomizer),
    {}
  )
).reduce((acc, [group, conditionCodes]) => {
  conditionCodes.forEach((code) => {
    acc[code] = group;
  });
  return acc;
}, {});
const CHILD_CONDITION_CODES_BY_PARENT_CODE = Object.entries(
  CHILD_CONDITION_GROUPS
).reduce(
  (acc, [parentCode, conditionCodes]) => {
    acc[parentCode] = Object.values(
      conditionCodes
    ).flatMap((code) => code);
    return acc;
  },
  {}
);
const UNCERTAIN_MODEL_SCORE_RANGE = [0.3, 0.5];
const conditionConstants = {
  // Code groups
  RED_CODES,
  PURPLE_CODES,
  YELLOW_CODES,
  // Enums
  ConditionGroups,
  ChildConditionGroups,
  // Group definitions
  ONE_OF_CONDITION_GROUPS,
  TOOTH_TYPE_GROUP,
  TOOTH_ANATOMY_GROUPS,
  TOOTH_GENERAL_GROUPS,
  CONDITION_CODES_BY_GROUPS,
  FLAT_CONDITION_GROUP_CODES,
  // Child groups
  PERIODONTAL_BONE_LOSS_CHILD_GROUP,
  IMPACTION_GROUP,
  FILLING_CHILD_GROUP,
  CROWN_FRACTURE_CHILD_GROUP,
  CARIES_CHILD_GROUP,
  SECONDARY_CARIES_CHILD_GROUP,
  ENDO_CHILD_GROUP,
  MUCOSAL_THICKENING_TYPE_CHILD_GROUP,
  CHILD_CONDITION_GROUPS,
  // Mappings
  CONDITION_GROUP_BY_CODE,
  CHILD_CONDITION_GROUP_BY_CODE,
  CHILD_CONDITION_CODES_BY_PARENT_CODE,
  // Uncertainty
  UNCERTAIN_MODEL_SCORE_RANGE
};
const RED_COLOR = "#ff254e";
const WHITE_COLOR = "#ffffff";
const YELLOW_COLOR = "#ff8a00";
const PURPLE_COLOR = "#5241cc";

const ActionNames = [
  "assets/addMany",
  "assets/setMany",
  "assets/addOne",
  "assets/deleteAsset/fulfilled",
  "tooth/addMany",
  "tooth/setMany",
  "tooth/setNewestMany",
  "tooth/setNewestOne",
  "tooth/setHoveredToothID",
  "reports/setCurrentReport",
  "reports/updateCurrentReport",
  "reports/setNewestOne",
  "reports/setShowPeriapicalRadiolucencyMasks",
  "reports/setNavigationMode",
  "reports/setActiveToothID",
  "reports/setFocusedMetaImageID",
  "reports/resetReportViewSettings",
  "condition/setMany",
  "condition/setNewestMany",
  "toothLandmarks/addMany",
  "maskFilters/toggleFilter",
  "organization/setNewestOne",
  "organization/setNewestMany",
  "organization/setSharedPatientSettings",
  "organization/setCurrentOrganizationID/fulfilled",
  "condition/setHoveredConditionIDs",
  "condition/setHoveredNonDentalConditionIDs",
  "toolbarMIR/initToolbarMIR",
  "toolbarMIR/setToolbarState",
  "toolbarMIR/deleteToolbarMIR",
  "toolbarMIR/hideMarks",
  "toolbarMIR/activateTool",
  "toolbarMIR/statelessAction",
  "modal/openModal",
  "modal/closeModal",
  "patient/setCurrentPatient",
  "toothNumberEdit/addPendingChange",
  "toothNumberEdit/removePendingChange",
  "toothNumberEdit/addPendingRemove",
  "toothNumberEdit/setSelectedTooth",
  "toothNumberEdit/resetState"
];
const UI_TOOL_TO_SLICE_EDITOR_MODE_MAP = {
  "brightness": "contrastBrightnessMode",
  "sharpness": "sharpnessMode",
  "ruler": "rulerMode",
  "arrow": "arrowMode",
  "angle": "angleMode",
  "eraser": "eraserMode"
};
const UI_TOOL_TO_MAIN_MODE_MAP = {
  "brightness": "contrastBrightnessMode",
  "sharpness": "sharpnessMode",
  "editMask": "editMaskMode"
};
const UI_TOOL_TO_MODE_MAP_MATRIX = {
  brightness: {
    matrix: "contrastBrightnessMultipleMode",
    focus: "contrastBrightnessMode",
    crop: "contrastBrightnessMode"
  },
  sharpness: {
    matrix: "sharpnessMultipleMode",
    focus: "sharpnessMode",
    crop: "sharpnessMode"
  },
  editMask: {
    matrix: "editMaskMode",
    fullScreenMatrix: "editMaskMode",
    focus: "editMaskMode",
    crop: "editMaskMode"
  }
};

const conditionIsUncertain = (certainty) => certainty?.EngineDecision === Decision.PositiveDecision && certainty?.UserDecision === Decision.NoDecision && certainty?.ModelScore >= conditionConstants.UNCERTAIN_MODEL_SCORE_RANGE[0] && certainty?.ModelScore < conditionConstants.UNCERTAIN_MODEL_SCORE_RANGE[1];
const maskTypeForExcludeConfig = {
  perio: "all",
  restorative: "all",
  previousTreatment: "all",
  endo: {
    kind: "except",
    codes: [ConditionCode.PeriapicalRadiolucency]
  }
};
const FDA_VERSION_2_COMMON_MASK_CODES = [
  ConditionCode.FDA_Child_EndoTreated_CanalFilling
];
const FDA_VERSION_2_2D_MASK_CODES = [
  ...FDA_VERSION_2_COMMON_MASK_CODES,
  ConditionCode.Child_EndoTreated_Obturation_ShortFilling,
  ConditionCode.Child_EndoTreated_Obturation_Overfilling,
  ConditionCode.FDA_Caries,
  ConditionCode.FDA_RestorativeImperfections,
  ConditionCode.FDA_Child_EndoTreated_PostAndCore,
  ConditionCode.PeriapicalRadiolucency,
  ConditionCode.FurcationLesion,
  ConditionCode.DentalCalculus,
  ConditionCode.OrthodonticAppliance
];
const FDA_VERSION_2_3D_MASK_CODES = [
  ...FDA_VERSION_2_COMMON_MASK_CODES,
  ConditionCode.CariesSigns,
  ConditionCode.Filling,
  ConditionCode.Abfraction
];
const FDA_VERSION_2_BBOX_ONLY_CODES = [
  ConditionCode.PulpStone
];
const FDA_VERSION_2_2D_REPORT_TYPES = [
  ReportType.ReportType_IOXRay_GP,
  ReportType.ReportType_Pano_GP,
  ReportType.ReportType_Pano_Bitewings
];
const isFDAVersion2Enabled = (state) => !!state.featureFlags.get(FeatureFlag.FeatureFlag_FDA_Version2);
const isFDAEnabled = (state) => !!state.featureFlags.get(FeatureFlag.FeatureFlag_FDA_Annotator) || isFDAVersion2Enabled(state);
const isFDAUnaidedEnabled = (state) => isFDAEnabled(state) && !!state.featureFlags.get(FeatureFlag.FeatureFlag_FDA_Unaided);
const isFDA3DGPRestrictionsEnabled = (state) => !!state.featureFlags.get(FeatureFlag.FeatureFlag_FDA_3DGPRestrictions) || isFDAVersion2Enabled(state);
const isFDAVersion2ReportType = (reportType) => reportType === ReportType.ReportType_CBCT_GP || FDA_VERSION_2_2D_REPORT_TYPES.includes(reportType);
const getFDAVersion2MaskConditionCodes = (reportType) => {
  const codes = FDA_VERSION_2_2D_REPORT_TYPES.includes(reportType) ? FDA_VERSION_2_2D_MASK_CODES : FDA_VERSION_2_3D_MASK_CODES;
  return new Set(codes);
};
const shouldDisplayConditionBBox = (state, code) => {
  const isFDAVersion2 = isFDAVersion2Enabled(state);
  const reportType = state.currentReport.get()?.Type;
  if (isFDAVersion2 && isFDAVersion2ReportType(reportType)) {
    if (isFDAUnaidedEnabled(state)) {
      return false;
    }
    return getFDAVersion2MaskConditionCodes(reportType).has(code) || FDA_VERSION_2_BBOX_ONLY_CODES.includes(code) || WHOLE_TOOTH_BBOX_CODES.includes(code);
  }
  return true;
};
const localizationPropsFilter = (state, props, maskType) => {
  const isFDAVersion2 = isFDAVersion2Enabled(state);
  const reportType = state.currentReport.get()?.Type;
  if (isFDAVersion2 && isFDAVersion2ReportType(reportType)) {
    if (isFDAUnaidedEnabled(state)) {
      return false;
    }
    if (!getFDAVersion2MaskConditionCodes(reportType).has(props.Code)) {
      return false;
    }
  }
  if (!isFDAVersion2 && isFDA3DGPRestrictionsEnabled(state) && reportType === ReportType.ReportType_CBCT_GP) {
    if (Object.keys(maskTypeForExcludeConfig).includes(maskType)) {
      const option = maskTypeForExcludeConfig[maskType];
      if (option) {
        if (option === "all") {
          return false;
        } else {
          const isInclude = option.codes.includes(props.Code);
          if (option.kind === "except" && !isInclude || option.kind === "include" && isInclude) {
            return false;
          }
        }
      }
    }
  }
  if (state.dataFromURL.pageType === "preview" && conditionIsUncertain(props.Certainty)) {
    return false;
  }
  if (props.Certainty?.EngineDecision === Decision.PositiveDecision) {
    return true;
  }
};
const getConditionCodeColor = (code, isConditionUncertain, showLowProbability = false) => {
  if (showLowProbability && conditionConstants.YELLOW_CODES.includes(code) && isConditionUncertain) {
    return YELLOW_COLOR;
  }
  if (conditionConstants.RED_CODES.includes(code)) {
    return RED_COLOR;
  }
  if (conditionConstants.PURPLE_CODES.includes(code)) {
    return PURPLE_COLOR;
  }
  return WHITE_COLOR;
};
function isMIRenderActions(actions) {
  return ActionNames.every((actionPath) => {
    const parts = actionPath.split("/");
    if (parts.length === 2) {
      const [namespace, method] = parts;
      return namespace in actions && method in actions[namespace] && typeof actions[namespace][method] === "function" && "payload" in actions[namespace][method]();
    } else if (parts.length === 3) {
      const [namespace, subNamespace, method] = parts;
      return namespace in actions && subNamespace in actions[namespace] && method in actions[namespace][subNamespace] && typeof actions[namespace][subNamespace][method] === "function" && "payload" in actions[namespace][subNamespace][method]();
    }
    return false;
  });
}

let rootAction = null;
let activeAutoRun = null;
let isExecutingListeners = false;
let isShadowedAutoRun = 0;
let syncMode = false;
const autoRunDisposers = /* @__PURE__ */ new Map();
const pendingPromises = /* @__PURE__ */ new Set();
const pendingListeners = /* @__PURE__ */ new Map();
const changes = /* @__PURE__ */ new Map();
const isMapARx = (obj2) => {
  return obj2 && obj2 instanceof Map && obj2.getKind?.() === "MapARx" && typeof obj2.observe === "function" && typeof obj2.set === "function" && typeof obj2.get === "function" && typeof obj2.getAsync === "function";
};
const isObjARx = (obj2) => {
  return obj2 && typeof obj2 === "object" && obj2.getKind?.() === "ObjARx" && typeof obj2.observe === "function" && typeof obj2.getAsync === "function";
};
const isBoxARx = (obj2) => {
  return obj2 && typeof obj2 === "object" && obj2.getKind?.() === "BoxARx" && typeof obj2.observe === "function" && typeof obj2.set === "function" && typeof obj2.get === "function" && typeof obj2.getAsync === "function";
};
const setMeta = (obj2, listener, key, value) => {
  let map2 = changes.get(obj2);
  if (!map2) {
    changes.set(obj2, /* @__PURE__ */ new Map([[listener, /* @__PURE__ */ new Map([[key, value]])]]));
    return;
  }
  let map22 = map2.get(listener);
  if (!map22) {
    map2.set(listener, /* @__PURE__ */ new Map([[key, value]]));
    return;
  }
  map22.set(key, value);
};
const map = (entries) => {
  const self = entries instanceof Map ? entries : new Map(entries);
  const promises = /* @__PURE__ */ new Map();
  const promiseResolvers = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Map();
  const globalListeners = /* @__PURE__ */ new Set();
  const checkActionAndListeners = (key) => {
    if ((key ? listeners.get(key)?.size : listeners.size || globalListeners.size) && !rootAction) {
      throw new Error("Cannot modify observed object outside of an action. Wrap modifications in an @action.");
    }
    if (isExecutingListeners && ([...globalListeners].some((listener) => pendingListeners.has(listener)) || [...listeners.values()].some((set) => set.size && [...set].some((listener) => pendingListeners.has(listener))))) {
      throw new Error("Cannot modify observed object during listener execution");
    }
  };
  const pushPromises = (key) => {
    const promiseResolver = promiseResolvers.get(key);
    if (promiseResolver) {
      pendingPromises.add(promiseResolver);
    }
  };
  const pushListeners = (key, value, currentValue) => {
    const callbacks = listeners.get(key);
    if (callbacks) {
      callbacks.forEach((listener) => pendingListeners.set(listener, () => listener(value)));
    }
    globalListeners.forEach((listener) => {
      setMeta(self, listener, key, { prev: currentValue, current: value, type: "set" });
      pendingListeners.set(listener, () => listener({ obj: self, changes: changes.get(self).get(listener) }));
    });
  };
  const proxy = new Proxy(self, {
    get(target, prop, receiver) {
      if (prop === "getKind") {
        return () => "MapARx";
      }
      if (prop === "size") {
        return Reflect.get(target, "size", target);
      }
      let value = Reflect.get(target, prop, receiver);
      if (prop === "getUntracked") {
        return function(key) {
          return Reflect.get(target, "get", target).call(target, key);
        };
      }
      if (prop === "set" && typeof value === "function") {
        const origSet = value;
        return function(key, value2) {
          const currentValue = Reflect.get(target, "get", receiver).apply(target, [key]);
          if (currentValue === value2) {
            return self;
          } else if (currentValue && typeof currentValue === "object" && "equals" in currentValue && typeof currentValue["equals"] === "function" && currentValue.equals(value2)) {
            return self;
          }
          checkActionAndListeners(key);
          const result = origSet.apply(self, arguments);
          pushPromises(key);
          pushListeners(key, value2, currentValue);
          return result;
        };
      }
      if (prop === "delete") {
        const origDelete = value;
        return function(key) {
          const hasValue = Reflect.get(target, "has", receiver).apply(target, [key]);
          if (!hasValue) return false;
          checkActionAndListeners(key);
          const currentValue = Reflect.get(target, "get", receiver).apply(target, [key]);
          const result = origDelete.apply(self, arguments);
          pushListeners(key, void 0, currentValue);
          return result;
        };
      }
      if (prop === "clear") {
        if (self.size) {
          checkActionAndListeners();
        }
        const origClear = value;
        return function() {
          Reflect.get(target, "forEach", receiver).call(self, (value2, key) => {
            globalListeners.forEach((listener) => {
              setMeta(self, listener, key, { prev: value2, current: value2, type: "set" });
            });
          });
          const result = origClear.apply(self, arguments);
          listeners.forEach((listenerForKey) => {
            listenerForKey.forEach((listener) => {
              pendingListeners.set(listener, () => listener(void 0));
            });
          });
          globalListeners.forEach((listener) => {
            pendingListeners.set(listener, () => listener({ obj: self, changes: changes.get(self).get(listener) }));
          });
          return result;
        };
      }
      if (!isShadowedAutoRun && activeAutoRun) {
        if (prop === "getAsync" || prop === "get") {
          return function(key) {
            const dispose = proxy.observe(key, activeAutoRun);
            autoRunDisposers.get(activeAutoRun)?.add(dispose);
            return Reflect.get(target, "get", receiver).apply(target, [key]);
          };
        } else if (typeof value === "function") {
          return function() {
            const dispose = proxy.observe(activeAutoRun);
            autoRunDisposers.get(activeAutoRun)?.add(dispose);
            return value.apply(self, arguments);
          };
        }
      }
      if (prop === "getAsync" || prop === "getAsyncUntracked") {
        return function(key) {
          if (!syncMode) {
            const has = Reflect.get(target, "has", receiver).apply(target, [key]);
            if (!has) {
              let promise = promises.get(key);
              if (!promise) {
                promise = promiseWithResolvers();
                promises.set(key, promise);
                promiseResolvers.set(key, () => {
                  promise.resolve(Reflect.get(target, "get", receiver).apply(target, [key]));
                  promises.delete(key);
                  promiseResolvers.delete(key);
                });
              }
              return promise.promise;
            }
          }
          return Reflect.get(target, "get", receiver).apply(target, [key]);
        };
      }
      if (prop === "observe") {
        let observe3 = function(key, callback, immediate = false) {
          if (typeof key !== "function" && typeof callback === "function") {
            let set = listeners.get(key);
            if (set) {
              set.add(callback);
            } else {
              set = /* @__PURE__ */ new Set([callback]);
              listeners.set(key, set);
            }
            if (immediate) {
              const has = Reflect.get(target, "has", receiver).apply(target, [key]);
              has && callback(Reflect.get(target, "get", receiver).apply(target, [key]));
            }
            return () => {
              set.delete(callback);
              if (set.size === 0) {
                listeners.delete(key);
              }
            };
          } else if (typeof key === "function") {
            globalListeners.add(key);
            if (callback) {
              key({ obj: self });
            }
            return () => {
              globalListeners.delete(key);
            };
          }
        };
        return observe3;
      }
      if (typeof value === "function") {
        value = value.bind(self);
      }
      return value;
    }
  });
  return proxy;
};
const box = (initialValue) => {
  let currentValue = initialValue;
  let promise;
  const listeners = /* @__PURE__ */ new Set();
  const checkActionAndListeners = () => {
    if (listeners.size && !rootAction) {
      throw new Error("Cannot modify observed object outside of an action. Wrap modifications in an @action.");
    }
    if (isExecutingListeners && [...listeners].some((listener) => pendingListeners.has(listener))) {
      throw new Error("Cannot modify observed object during listener execution");
    }
  };
  return {
    getKind() {
      return "BoxARx";
    },
    set(value) {
      if (currentValue === value) {
        return;
      }
      checkActionAndListeners();
      const prevValue = currentValue;
      currentValue = value;
      if (promise) {
        promise.resolve(value);
        promise = void 0;
      }
      listeners.forEach((listener) => pendingListeners.set(listener, () => listener(value, prevValue)));
    },
    get() {
      if (!isShadowedAutoRun && activeAutoRun) {
        const dispose = this.observe(activeAutoRun);
        autoRunDisposers.get(activeAutoRun)?.add(dispose);
      }
      return currentValue;
    },
    delete() {
      if (!currentValue) return;
      checkActionAndListeners();
      const prevValue = currentValue;
      currentValue = void 0;
      listeners.forEach((listener) => pendingListeners.set(listener, () => listener(currentValue, prevValue)));
    },
    getAsync() {
      if (!isShadowedAutoRun && activeAutoRun) {
        const dispose = this.observe(activeAutoRun);
        autoRunDisposers.get(activeAutoRun)?.add(dispose);
        return currentValue;
      }
      if (currentValue === void 0 && !syncMode) {
        if (!promise) {
          promise = promiseWithResolvers();
        }
        return promise.promise;
      }
      return currentValue;
    },
    observe(listener, immediate = false) {
      listeners.add(listener);
      if (immediate) {
        listener(currentValue);
      }
      return () => listeners.delete(listener);
    }
  };
};
const obj = (initialValue) => {
  const self = initialValue || {};
  const promises = /* @__PURE__ */ new Map();
  const promiseResolvers = /* @__PURE__ */ new Map();
  const listeners = /* @__PURE__ */ new Map();
  const globalListeners = /* @__PURE__ */ new Set();
  const checkActionAndListeners = (prop) => {
    if ((listeners.get(prop)?.size || globalListeners.size) && !rootAction) {
      throw new Error("Cannot modify observed object outside of an action. Wrap modifications in an @action.");
    }
    if (isExecutingListeners && ([...globalListeners].some((listener) => pendingListeners.has(listener)) || [...listeners.values()].some((set) => set.size && [...set].some((listener) => pendingListeners.has(listener))))) {
      throw new Error("Cannot modify observed object during listener execution");
    }
  };
  const proxy = new Proxy(self, {
    get(target, prop) {
      if (prop === "getKind") {
        return () => "ObjARx";
      }
      if (prop === "observe") {
        let observe3 = function(key, listener, immediate = false) {
          if (typeof key === "string" && typeof listener === "function") {
            let set = listeners.get(key);
            if (set) {
              set.add(listener);
            } else {
              set = /* @__PURE__ */ new Set([listener]);
              listeners.set(key, set);
            }
            if (immediate) {
              key in target && listener(target[key]);
            }
            return () => {
              set.delete(listener);
              if (set.size === 0) {
                listeners.delete(key);
              }
            };
          } else {
            globalListeners.add(key);
            if (immediate) {
              key({ obj: self });
            }
            return () => {
              globalListeners.delete(key);
            };
          }
        };
        return observe3;
      }
      if (prop === "copy") {
        return function(source) {
          const props = Object.getOwnPropertyNames(source).filter((prop2) => typeof target[prop2] !== "function");
          const changedProps = props.filter((prop2) => target[prop2] !== source[prop2]);
          if (!changedProps.length) return;
          checkActionAndListeners(prop);
          changedProps.forEach((prop2) => {
            const value = source[prop2];
            const currentValue = target[prop2];
            target[prop2] = value;
            const resolver = promiseResolvers.get(prop2);
            if (resolver) {
              pendingPromises.add(resolver);
            }
            const callbacks = listeners.get(prop2);
            if (callbacks) {
              callbacks.forEach((listener) => pendingListeners.set(listener, () => listener(value)));
            }
            globalListeners.forEach((listener) => {
              setMeta(target, listener, prop2, { prev: currentValue, current: value, type: "set" });
            });
          });
          globalListeners.forEach((listener) => {
            pendingListeners.set(listener, () => listener({ obj: target, changes: changes.get(target).get(listener) }));
          });
        };
      }
      if (!isShadowedAutoRun && activeAutoRun) {
        if (prop === "getAsync" || prop === "get") {
          return function(key) {
            const dispose = proxy.observe(key, activeAutoRun);
            autoRunDisposers.get(activeAutoRun)?.add(dispose);
            return target[key];
          };
        } else if (typeof target[prop] !== "function") {
          const dispose = proxy.observe(prop, activeAutoRun);
          autoRunDisposers.get(activeAutoRun)?.add(dispose);
          return target[prop];
        }
      }
      if (prop === "getAsync") {
        return function(key) {
          if (!(key in target) && !syncMode) {
            let promise = promises.get(key);
            if (!promise) {
              promise = promiseWithResolvers();
              promises.set(key, promise);
              promiseResolvers.set(key, () => {
                promise.resolve(target[key]);
                promises.delete(key);
                promiseResolvers.delete(key);
              });
            }
            return promise.promise;
          }
          return target[key];
        };
      }
      return target[prop];
    },
    set(target, prop, value) {
      const currentValue = target[prop];
      if (currentValue === value) {
        return true;
      }
      checkActionAndListeners(prop);
      target[prop] = value;
      const resolver = promiseResolvers.get(prop);
      if (resolver) {
        pendingPromises.add(resolver);
      }
      const callbacks = listeners.get(prop);
      if (callbacks) {
        callbacks.forEach((listener) => pendingListeners.set(listener, () => listener(value)));
      }
      globalListeners.forEach((listener) => {
        setMeta(target, listener, prop, { prev: currentValue, current: value, type: "set" });
        pendingListeners.set(listener, () => listener({ obj: target, changes: changes.get(target).get(listener) }));
      });
      return true;
    }
  });
  return proxy;
};
const observe = (dependencies, listener, immediate = false) => {
  const fn = () => listener(dependencies);
  return dependencies.map((dependency) => {
    if (isMapARx(dependency) || isObjARx(dependency) || isBoxARx(dependency)) {
      return dependency.observe(fn, immediate);
    }
    if ("obj" in dependency && "key" in dependency) {
      return dependency.obj.observe(dependency.key, fn, immediate);
    }
  });
};
const autoRun = (fn) => {
  const childDisposers = /* @__PURE__ */ new Set();
  const autoRunFn = () => {
    const prevActiveAutoRun = activeAutoRun;
    const prevIsShadowedAutoRun = isShadowedAutoRun;
    isShadowedAutoRun = 0;
    childDisposers.forEach((disposeChild) => disposeChild());
    childDisposers.clear();
    activeAutoRun = autoRunFn;
    autoRunDisposers.get(autoRunFn)?.forEach((dispose2) => dispose2());
    autoRunDisposers.set(autoRunFn, /* @__PURE__ */ new Set());
    fn();
    activeAutoRun = prevActiveAutoRun;
    isShadowedAutoRun = prevIsShadowedAutoRun;
  };
  autoRunFn.__childDisposers = childDisposers;
  const dispose = () => {
    childDisposers.forEach((disposeChild) => disposeChild());
    childDisposers.clear();
    autoRunDisposers.get(autoRunFn)?.forEach((observerDisposers) => {
      observerDisposers();
    });
    autoRunDisposers.delete(autoRunFn);
  };
  if (activeAutoRun && activeAutoRun.__childDisposers) {
    activeAutoRun.__childDisposers.add(dispose);
  }
  autoRunFn();
  return dispose;
};
function shadow(target) {
  return function replacementMethod(...args) {
    isShadowedAutoRun++;
    let result;
    try {
      result = target.call(this, ...args);
    } catch (e) {
      console.error(e);
      throw new Error("error in shadow");
    }
    isShadowedAutoRun--;
    return result;
  };
}
function runInShadow(target) {
  return shadow(target)();
}
function action(target) {
  return function replacementMethod(...args) {
    if (!rootAction) {
      rootAction = target;
    }
    const result = target.call(this, ...args);
    if (rootAction === target) {
      pendingPromises.forEach((promise) => promise());
      pendingPromises.clear();
      isExecutingListeners = true;
      try {
        pendingListeners.forEach((callback) => callback());
        changes.clear();
      } catch (e) {
        console.error("error in listeners", e);
      }
      isExecutingListeners = false;
      pendingListeners.clear();
      rootAction = null;
    }
    return result;
  };
}
function runInAction(target) {
  return action(target)();
}
function computed(options = {}) {
  return function(target, context) {
    let cache = box();
    let disposer;
    return function() {
      if (!disposer) {
        disposer = autoRun(() => {
          const value = context.access.get(this);
          if (options.equals) {
            if (!options.equals(cache.get(), value)) {
              cache.set(value);
            }
          } else {
            cache.set(value);
          }
        });
      }
      return cache.get();
    };
  };
}
function computedFn(options = {}) {
  return function(target) {
    const cache = box();
    let disposer;
    return function() {
      if (!disposer) {
        disposer = autoRun(() => {
          const value = target();
          if (options.equals) {
            if (!options.equals(cache.get(), value)) {
              cache.set(value);
            }
          } else {
            cache.set(value);
          }
        });
      }
      return cache.get();
    };
  };
}
function mode(type) {
  syncMode = type === "sync";
}
const arx = {
  map,
  box,
  obj,
  autoRun,
  observe,
  computed,
  computedFn,
  action,
  runInAction,
  shadow,
  runInShadow,
  mode
};

const selectIOXRayImage = async (state, report) => {
  report = report || await state.currentReport.getAsync();
  if (!report) return;
  if (report.Specific?.case !== "DataIOXRayGP" && report.Specific?.case !== "DataPanoBitewings") return;
  return await Promise.all([...report.Specific.value.ImagesMeta].filter(([, asset]) => asset.Type === Report_2D_ImageType.Report_2D_ImageType_IntraoralXRay).map(async ([assetID]) => state.IOXRayImages.getAsync(assetID)));
};

const getViewOptions = (viewOptions) => {
  if (!viewOptions) return void 0;
  return {
    Sharpness: viewOptions.Sharpness,
    Brightness: viewOptions.Brightness,
    Contrast: viewOptions.Contrast
  };
};
const IOXRayImagesToIOXRayImageInterfaces = (IOXRayImages, report) => {
  const imagesMeta = report.Specific.value.ImagesMeta;
  return IOXRayImages.map((IOXRayImage2) => {
    const assetID = IOXRayImage2.assetID;
    const imageMeta = imagesMeta.get(assetID);
    const meta = imageMeta && {
      imageMetaID: imageMeta.ID,
      ...runInShadow(() => getViewOptions(imageMeta.MedicalImageFeatures.ViewOptions)),
      pixelSpacing: imageMeta.Scale && {
        xMmPerPixel: imageMeta.Scale.X,
        yMmPerPixel: imageMeta.Scale.Y
      },
      OrientationAngle: imageMeta.OrientationAngle.get()
    };
    const image = imageAssetToMIRenderImageInterface(IOXRayImage2, meta);
    return image;
  });
};
const selectPanoImageInterface = async (state, report) => {
  const specificReport = report || await state.currentReport.getAsync();
  if (report?.Specific && report.Specific.case !== "DataPanoBitewings" && report.Specific.case !== "DataPanoGP") return;
  const panoImage = await state.panoImage.getAsync(specificReport.Specific.value.PanoStudyAssetID);
  const imagesMeta = specificReport.Specific.value.ImagesMeta;
  const assetID = panoImage.assetID;
  const imageMeta = imagesMeta.get(assetID);
  const meta = imageMeta && {
    imageMetaID: imageMeta.ID,
    ...getViewOptions(imageMeta.MedicalImageFeatures.ViewOptions)
  };
  return { imageInterface: imageAssetToMIRenderImageInterface(panoImage, meta), report: specificReport };
};
const selectReformatGeneralImageInterface = async (state, report) => {
  report = await state.currentReport.getAsync();
  const reportID = report.ID;
  const reformatGeneral = await state.PanoReformatGeneral.getAsync(reportID);
  return imageAssetToMIRenderImageInterface(reformatGeneral, report?.MedicalImageFeatures?.ViewOptions);
};
const selectReformatSplitImageInterface = async (state, report) => {
  report = await state.currentReport.getAsync();
  const reportID = report.ID;
  const reformatSplit = await state.PanoReformatSplit.getAsync(reportID);
  return imageAssetToMIRenderImageInterface(reformatSplit, report?.MedicalImageFeatures?.ViewOptions);
};
const imageAssetToMIRenderImageInterface = (imageAsset, imageMeta) => {
  return {
    ImageID: imageAsset.assetID,
    OriginalFileID: imageAsset.imageID,
    PreviewFileID: imageAsset.previewID,
    OrientationAngle: imageMeta?.OrientationAngle ?? 0,
    MedicalImageFeatures: {
      ViewOptions: {
        Sharpness: imageMeta?.Sharpness || imageAsset.medicalImageFeatures?.ViewOptions?.Sharpness || 0,
        Brightness: imageMeta?.Brightness || imageAsset.medicalImageFeatures?.ViewOptions?.Brightness || 0,
        Contrast: imageMeta?.Contrast || imageAsset.medicalImageFeatures?.ViewOptions?.Contrast || 0
      }
    },
    originalSize: {
      width: Number(imageAsset.size?.width) || 0,
      height: Number(imageAsset.size?.height) || 0
    },
    meta: {
      imageMetaID: imageMeta?.imageMetaID,
      scale: "Scale" in imageAsset ? imageAsset.Scale : void 0
    },
    pixelSpacing: imageMeta?.pixelSpacing
  };
};

const MIN_BBOX_SIZE = 20;
const transformBBox = (localizationID, bbox, color, width = 2) => {
  const boxSizeX = (bbox?.X?.Max ?? NaN) - (bbox?.X?.Min ?? NaN);
  const boxSizeY = (bbox?.Y?.Max ?? NaN) - (bbox?.Y?.Min ?? NaN);
  return {
    id: localizationID,
    max: {
      x: (bbox?.X?.Max ?? 0) + Math.max((MIN_BBOX_SIZE - boxSizeX) / 2, 0),
      y: (bbox?.Y?.Max ?? 0) + Math.max((MIN_BBOX_SIZE - boxSizeY) / 2, 0)
    },
    min: {
      x: (bbox?.X?.Min ?? 0) - Math.max((MIN_BBOX_SIZE - boxSizeX) / 2, 0),
      y: (bbox?.Y?.Min ?? 0) - Math.max((MIN_BBOX_SIZE - boxSizeY) / 2, 0)
    },
    color,
    width
  };
};

const selectHoveredConditionBBoxes = (state) => {
  const reportID = state.currentReport.get()?.ID;
  if (!reportID) {
    return;
  }
  const showLowProbability = state.currentReport.get()?.Settings?.LowProbabilityMode;
  const hoveredConditionCodes = state.hoveredConditions.get();
  if (!hoveredConditionCodes) {
    return;
  }
  const hoveredConditions = hoveredConditionCodes.map((code) => state.conditionsState.flatConditions.get(reportID)?.Tooth.get(code)).filter((x) => x != void 0).filter((condition) => shouldDisplayConditionBBox(state, condition.Code)).sort((a, b) => (b.Certainty?.ModelScore ?? 0) - (a.Certainty?.ModelScore ?? 0));
  if (!hoveredConditions || !hoveredConditions.length) {
    return;
  }
  const isUncertain = conditionIsUncertain(hoveredConditions[0].Certainty);
  const color = getConditionCodeColor(
    hoveredConditions[0].Code,
    isUncertain,
    showLowProbability
  );
  const result = /* @__PURE__ */ new Map();
  const pushBox = (assetID, box) => {
    const obj = result.get(assetID);
    if (obj) {
      obj.boxes.push(box);
    } else {
      result.set(assetID, { imageID: assetID, boxes: [box] });
    }
  };
  hoveredConditions.forEach((condition) => {
    if (WHOLE_TOOTH_BBOX_CODES.includes(condition.Code)) {
      const toothID = condition.Attribution?.ToothID;
      const tooth = toothID ? state.flatTeeth.get(reportID)?.get(toothID) : void 0;
      if (tooth) {
        tooth.Detections.forEach((detection) => {
          const localization = detection.Localizations;
          if (!localization?.BBox) return;
          pushBox(localization.TargetAssetID, transformBBox(localization.ID, localization.BBox, color));
        });
        return;
      }
    }
    condition.Localization.forEach((localization) => {
      pushBox(localization.TargetAssetID, transformBBox(localization.ID, localization.BBox, color));
    });
  });
  return [...result.values()];
};

const selectHoveredToothBBoxes = (state) => {
  const reportID = state.currentReport.get()?.ID;
  if (!reportID) {
    return;
  }
  const reportType = state.currentReport.get()?.Type;
  if (isFDAVersion2Enabled(state) && isFDAVersion2ReportType(reportType) && isFDAUnaidedEnabled(state)) {
    return;
  }
  const hoveredToothID = state.hoveredToothID.get();
  if (!hoveredToothID) {
    return;
  }
  const hoveredTooth = state.flatTeeth.get(reportID)?.get(hoveredToothID);
  if (!hoveredTooth) {
    return;
  }
  const isMissingCondition = !!state.conditionsState.conditions.get(reportID)?.Tooth.get(hoveredTooth.ID)?.get(ConditionCode.Missing);
  const result = /* @__PURE__ */ new Map();
  hoveredTooth.Detections.forEach((detection) => {
    const localization = detection.Localizations;
    if (!localization) return;
    const assetID = localization.TargetAssetID;
    const BBox = localization.BBox;
    if (!BBox) return;
    const color = isMissingCondition ? RED_COLOR : WHITE_COLOR;
    const transformedBbox = transformBBox(localization.ID, BBox, color);
    const obj = result.get(assetID);
    if (obj) {
      obj.boxes.push(transformedBbox);
    } else {
      result.set(assetID, { imageID: assetID, boxes: [transformedBbox] });
    }
  });
  return [...result.values()];
};

const DEFAULT_MEDICAL_IMAGE_HEIGHT = 140;
const MASK_FILTERS_RESTORATIVE_CODES = [
  ConditionCode.Attrition,
  ConditionCode.Abfraction,
  ConditionCode.CrownFracture,
  ConditionCode.RestorationFracture,
  ConditionCode.PreEruptiveResorption,
  ConditionCode.VoidsInTheFilling,
  ConditionCode.Overhang,
  ConditionCode.OpenMargin,
  ConditionCode.LackOfInterproximalContact,
  ConditionCode.ToothStump,
  ConditionCode.CariesSigns,
  ConditionCode.SecondaryCaries,
  ConditionCode.Child_DefectDepth_Enamel,
  ConditionCode.Child_DefectDepth_Dentin,
  ConditionCode.Child_DefectDepth_WithPulpExposure,
  ConditionCode.Child_DefectDepth_Initial,
  ConditionCode.Child_DefectDepth_NotDefined,
  ConditionCode.RootFragment,
  ConditionCode.FDA_Caries,
  ConditionCode.FDA_RestorativeImperfections
];
const MASK_FILTERS_ENDO_CODES = [
  ConditionCode.CervicalResorption,
  ConditionCode.Extrusion,
  ConditionCode.Intrusion,
  ConditionCode.LateralLuxation,
  ConditionCode.CervicalResorption,
  ConditionCode.EndodonticAccess,
  ConditionCode.InternalResorption,
  ConditionCode.CanalObliteration,
  ConditionCode.Pulpotomy,
  ConditionCode.Child_EndoTreated_Obturation_ShortFilling,
  ConditionCode.Child_EndoTreated_Obturation_Overfilling,
  ConditionCode.Child_EndoTreated_Obturation_MissedCanal,
  ConditionCode.Child_EndoTreated_Complications_RootPerforation,
  ConditionCode.Child_EndoTreated_Complications_SeparatedInstrumentInTheRootCanal,
  ConditionCode.Child_EndoTreated_Quality_VoidsPresentInTheRootFilling,
  ConditionCode.PeriapicalRadiolucency,
  ConditionCode.LateralRadiolucency,
  ConditionCode.PDLSpaceWidening_AlongTheRoot,
  ConditionCode.PDLSpaceWidening_InPeriapicalRegion,
  ConditionCode.SingsOfCommunicationWithMaxillarySinus,
  ConditionCode.SignsOfInferiorAlveolarCanalInvolvement
];
const MASK_FILTERS_PREVIOUS_TREATMENT = [
  ConditionCode.Filling,
  ConditionCode.ArtificialCrown,
  ConditionCode.IndirectRestoration,
  ConditionCode.OrthodonticAppliance,
  ConditionCode.HealingAbutment,
  ConditionCode.EndodonticAccess,
  ConditionCode.EndoTreated,
  ConditionCode.Child_EndoTreated_Obturation_AdequateObturation,
  ConditionCode.Child_EndoTreated_Quality_AdequateDensity,
  ConditionCode.Child_EndoTreated_PostAndCore_CastPostAndCore,
  ConditionCode.Child_EndoTreated_PostAndCore_FiberglassPost,
  ConditionCode.Child_EndoTreated_PostAndCore_MetalPost,
  ConditionCode.Child_EndoTreated_PostAndCore_Post,
  ConditionCode.FDA_Child_EndoTreated_PostAndCore,
  ConditionCode.FDA_Child_EndoTreated_CanalFilling
];
const MASK_FILTERS_PERIO_CODES = [
  ConditionCode.PeriodontalBoneLoss,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Mild,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Moderate,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Severe,
  ConditionCode.Child_PeriodontalBoneLoss_Type_Horizontal,
  ConditionCode.Child_PeriodontalBoneLoss_Type_Vertical,
  ConditionCode.Child_PeriodontalBoneLoss_Type_Mixed,
  ConditionCode.FurcationLesion,
  ConditionCode.DentalCalculus,
  ConditionCode.Impaction,
  ConditionCode.PeriImplantitis
];
const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Root,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_RootCanal,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Crown,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_IncisalEdge,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_PulpChamber,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Enamel
];
const MASK_FILTERS_PANO_TOOTH_ANATOMY_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Root,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_RootCanal,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Crown,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_PulpChamber,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Enamel
];
const MASK_FILTERS_CBCT_TOOTH_ANATOMY_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Pulp,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Dentin,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Enamel,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Root
];
const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_DENTIN_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Crown,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Root
];
const MASK_FILTERS_PANO_TOOTH_ANATOMY_DENTIN_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Crown,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Root
];
const MASK_FILTERS_CBCT_TOOTH_ANATOMY_DENTIN_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Dentin,
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Root
];
const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_PULP_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_PulpChamber,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_RootCanal
];
const MASK_FILTERS_PANO_TOOTH_ANATOMY_PULP_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_RootCanal,
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_PulpChamber
];
const MASK_FILTERS_CBCT_TOOTH_ANATOMY_PULP_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Pulp
];
const MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_ENAMEL_CODES = [
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_IncisalEdge,
  IOXRay_Tooth_Anatomy.IOXRay_Tooth_Anatomy_Enamel
];
const MASK_FILTERS_PANO_TOOTH_ANATOMY_ENAMEL_CODES = [
  Pano_Tooth_Anatomy.Pano_Tooth_Anatomy_Enamel
];
const MASK_FILTERS_CBCT_TOOTH_ANATOMY_ENAMEL_CODES = [
  CBCT_Tooth_Anatomy.CBCT_Tooth_Anatomy_Enamel
];
const MASK_FILTERS_IOXRAY_MAX_FAX_ANATOMY_CODES = [
  IOXRay_Anatomy.IOXRay_Anatomy_AlveolarBone,
  IOXRay_Anatomy.IOXRay_Anatomy_MandibularCanal,
  IOXRay_Anatomy.IOXRay_Anatomy_VestibularBone,
  IOXRay_Anatomy.IOXRay_Anatomy_OralBone
];
const MASK_FILTERS_PANO_MAX_FAX_ANATOMY_CODES = [
  Pano_Anatomy.Pano_Anatomy_MandibularCanal,
  Pano_Anatomy.Pano_Anatomy_Maxilla,
  Pano_Anatomy.Pano_Anatomy_Mandible
];
const MASK_FILTERS_CBCT_MAX_FAX_ANATOMY_CODES = [
  CBCT_Anatomy.CBCT_Anatomy_MandibularCanal,
  CBCT_Anatomy.CBCT_Anatomy_Maxilla,
  CBCT_Anatomy.CBCT_Anatomy_Mandible
];
const MASK_CONDITIONS_TO_SHOW = MASK_FILTERS_PERIO_CODES.concat(
  MASK_FILTERS_RESTORATIVE_CODES,
  MASK_FILTERS_ENDO_CODES,
  MASK_FILTERS_PREVIOUS_TREATMENT
);
const CONDITIONS_BY_MASK_GROUP = {
  perio: MASK_FILTERS_PERIO_CODES,
  restorative: MASK_FILTERS_RESTORATIVE_CODES,
  endo: MASK_FILTERS_ENDO_CODES,
  chart: MASK_FILTERS_PREVIOUS_TREATMENT
};
const PBL_RED_COLOR = "#FF3333";
const PBL_GRAY_COLOR = "#FFFFFF";
const PBL_WHITE_MAX_MM = 3;
const PBL_RED_CODES = [
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Mild,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Moderate,
  ConditionCode.Child_PeriodontalBoneLoss_Severity_Severe
];
const WA_CLIENT_CBCT_MASK_RENDER_CONFIG = {
  perio: {
    color: new Color(1, 0.145, 0.306).getHex(),
    opacity: 0.3,
    renderOrder: 90,
    blending: "NoBlending",
    outlineOpacity: 1
  },
  restorative: {
    color: new Color(0.757, 0.047, 0.427).getHex(),
    opacity: 0.6,
    renderOrder: 70,
    blending: "NoBlending",
    outlineOpacity: 1
  },
  endo: {
    color: new Color(1, 0.298, 0).getHex(),
    opacity: 0.3,
    renderOrder: 80,
    blending: "NoBlending",
    outlineOpacity: 1
  },
  unsorted: {
    color: new Color(1, 0, 0).getHex(),
    opacity: 0.3,
    renderOrder: 50,
    blending: "NoBlending",
    outlineOpacity: 0
  },
  dentin: {
    color: new Color(255 / 255, 220 / 255, 177 / 255).getHex(),
    opacity: 0.3,
    renderOrder: 30,
    blending: "NoBlending",
    outlineOpacity: 0.5
  },
  previousTreatment: {
    color: new Color(1, 1, 1).getHex(),
    opacity: 1,
    renderOrder: 50,
    blending: "NoBlending",
    outlineOpacity: 0
  },
  canal: {
    color: new Color(230 / 255, 185 / 255, 171 / 255).getHex(),
    opacity: 0.3,
    renderOrder: 50,
    blending: "NoBlending",
    outlineOpacity: 1
  },
  pulp: {
    color: new Color(230 / 255, 185 / 255, 171 / 255).getHex(),
    opacity: 0.8,
    renderOrder: 40,
    blending: "NoBlending",
    outlineOpacity: 1
  },
  enamel: {
    color: new Color(1, 1, 1).getHex(),
    opacity: 0.3,
    renderOrder: 10,
    blending: "NoBlending",
    outlineOpacity: 0.5
  },
  periapicalRadiolucency: {
    color: new Color("#FF4C00").getHex(),
    opacity: 0.2,
    renderOrder: 0,
    blending: "NoBlending",
    outlineOpacity: 1
  },
  maxilla: {
    color: new Color(0, 148 / 255, 1).getHex(),
    opacity: 0.15,
    renderOrder: 0,
    blending: "NoBlending",
    outlineOpacity: 0.3
  },
  mandible: {
    color: new Color(0, 148 / 255, 1).getHex(),
    opacity: 0.15,
    renderOrder: 0,
    blending: "NoBlending",
    outlineOpacity: 0.3
  },
  oralBone: {
    color: new Color(0, 148 / 255, 1).getHex(),
    opacity: 0.15,
    renderOrder: 99,
    fillRenderOrder: 2,
    blending: "NoBlending",
    outlineOpacity: 0.3
  },
  vestibularBone: {
    color: new Color(6 / 255, 76 / 255, 125 / 255).getHex(),
    opacity: 0.5,
    renderOrder: 99,
    fillRenderOrder: 1,
    blending: "NoBlending",
    outlineOpacity: 0.7,
    dashed: true,
    dashScale: 1,
    dashSize: 0.5,
    gapSize: 0.5
  },
  tooth: {
    color: new Color(117 / 255, 100 / 255, 237 / 255).getHex(),
    opacity: 0.1,
    renderOrder: 99,
    fillRenderOrder: 1,
    blending: "NoBlending",
    outlineOpacity: 0.7
  }
};
const WA_CLIENT_OTHER_REPORTS_MASK_RENDER_CONFIG = {
  ...WA_CLIENT_CBCT_MASK_RENDER_CONFIG,
  dentin: {
    ...WA_CLIENT_CBCT_MASK_RENDER_CONFIG.dentin,
    renderOrder: 10
  },
  enamel: {
    ...WA_CLIENT_CBCT_MASK_RENDER_CONFIG.enamel,
    renderOrder: 30
  }
};
const WA_CLIENT_MASK_RENDER_CONFIG = WA_CLIENT_CBCT_MASK_RENDER_CONFIG;
const WA_CLIENT_CBCT_REPORT_TYPES = /* @__PURE__ */ new Set([
  ReportType.ReportType_CBCT_GP,
  ReportType.ReportType_CBCT_Segmentation,
  ReportType.ReportType_CBCT_OrthoSlides,
  ReportType.ReportType_CBCT_Implant,
  ReportType.ReportType_CBCT_Endo,
  ReportType.ReportType_CBCT_Molar,
  ReportType.ReportType_CBCT_IOS_Superimposition
]);
const getWAClientMaskRenderConfig = (reportType) => {
  if (reportType === void 0 || WA_CLIENT_CBCT_REPORT_TYPES.has(reportType)) {
    return WA_CLIENT_CBCT_MASK_RENDER_CONFIG;
  }
  return WA_CLIENT_OTHER_REPORTS_MASK_RENDER_CONFIG;
};
const LOW_PROBABILITY_CONDITION_CODES = [
  ConditionCode.CariesSigns,
  ConditionCode.PDLSpaceWidening_AlongTheRoot,
  ConditionCode.PDLSpaceWidening_InPeriapicalRegion
];
const DICOM_MIME_TYPE = "application/dicom";
const constants = {
  DEFAULT_MEDICAL_IMAGE_HEIGHT,
  MASK_FILTERS_RESTORATIVE_CODES,
  MASK_FILTERS_ENDO_CODES,
  MASK_FILTERS_PREVIOUS_TREATMENT,
  MASK_FILTERS_PERIO_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_DENTIN_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_DENTIN_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_DENTIN_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_PULP_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_PULP_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_PULP_CODES,
  MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_ENAMEL_CODES,
  MASK_FILTERS_PANO_TOOTH_ANATOMY_ENAMEL_CODES,
  MASK_FILTERS_CBCT_TOOTH_ANATOMY_ENAMEL_CODES,
  MASK_FILTERS_IOXRAY_MAX_FAX_ANATOMY_CODES,
  MASK_FILTERS_PANO_MAX_FAX_ANATOMY_CODES,
  MASK_FILTERS_CBCT_MAX_FAX_ANATOMY_CODES,
  MASK_CONDITIONS_TO_SHOW,
  CONDITIONS_BY_MASK_GROUP,
  WA_CLIENT_CBCT_MASK_RENDER_CONFIG,
  WA_CLIENT_OTHER_REPORTS_MASK_RENDER_CONFIG,
  WA_CLIENT_MASK_RENDER_CONFIG,
  getWAClientMaskRenderConfig,
  LOW_PROBABILITY_CONDITION_CODES,
  DICOM_MIME_TYPE,
  PBL_RED_COLOR,
  PBL_GRAY_COLOR,
  PBL_RED_CODES,
  PBL_WHITE_MAX_MM
};

const getToothDetectionsCBCT = async (assetID, state) => {
  const reportID = (await state.currentReport.getAsync())?.ID;
  if (!reportID) return;
  const reportTeethDetections = await state.teethDetections.getAsync(reportID);
  if (!reportTeethDetections) return;
  const teethDetectionsCBCT = await reportTeethDetections.CBCT.getAsync(assetID);
  if (!teethDetectionsCBCT) return;
  const teethDentin = constants.MASK_FILTERS_CBCT_TOOTH_ANATOMY_DENTIN_CODES.map((code) => teethDetectionsCBCT.getAsync(code));
  const teethPulp = constants.MASK_FILTERS_CBCT_TOOTH_ANATOMY_PULP_CODES.map((code) => teethDetectionsCBCT.getAsync(code));
  const teethEnamel = constants.MASK_FILTERS_CBCT_TOOTH_ANATOMY_ENAMEL_CODES.map((code) => teethDetectionsCBCT.getAsync(code));
  return {
    teethDentin,
    teethPulp,
    teethEnamel
  };
};
const getToothDetectionsIOXRay = async (assetID, state) => {
  const reportID = (await state.currentReport.getAsync())?.ID;
  if (!reportID) return;
  const reportTeethDetections = await state.teethDetections.getAsync(reportID);
  if (!reportTeethDetections) return;
  const teethDetectionsIOXRay = await reportTeethDetections.IOXRay.getAsync(assetID);
  if (!teethDetectionsIOXRay) return;
  const teethDentin = constants.MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_DENTIN_CODES.map((code) => teethDetectionsIOXRay.getAsync(code));
  const teethPulp = constants.MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_PULP_CODES.map((code) => teethDetectionsIOXRay.getAsync(code));
  const teethEnamel = constants.MASK_FILTERS_IOXRAY_TOOTH_ANATOMY_ENAMEL_CODES.map((code) => teethDetectionsIOXRay.getAsync(code));
  return {
    teethDentin,
    teethPulp,
    teethEnamel
  };
};
const getToothDetectionsPano = async (assetID, state) => {
  const reportID = (await state.currentReport.getAsync())?.ID;
  if (!reportID) return;
  const reportTeethDetections = await state.teethDetections.getAsync(reportID);
  if (!reportTeethDetections) return;
  const teethDetectionsPano = await reportTeethDetections.Pano.getAsync(assetID);
  if (!teethDetectionsPano) return;
  const teethDentin = constants.MASK_FILTERS_PANO_TOOTH_ANATOMY_DENTIN_CODES.map((code) => teethDetectionsPano.getAsync(code));
  const teethPulp = constants.MASK_FILTERS_PANO_TOOTH_ANATOMY_PULP_CODES.map((code) => teethDetectionsPano.getAsync(code));
  const teethEnamel = constants.MASK_FILTERS_PANO_TOOTH_ANATOMY_ENAMEL_CODES.map((code) => teethDetectionsPano.getAsync(code));
  return {
    teethDentin,
    teethPulp,
    teethEnamel
  };
};
const toMaskData = (conditionProps, config, groupID, hover) => {
  return conditionProps.Localization.PolygonalMask.map((polygon) => ({
    imageID: conditionProps.TargetAssetID,
    localizationID: conditionProps.Localization.ID,
    path: polygon.Points.map((point) => ({ x: point.X, y: point.Y })),
    color: config.color,
    outlineColor: config.color,
    outlineOpacity: config.outlineOpacity,
    opacity: config.opacity,
    outlineWidth: 1,
    renderOrder: config.renderOrder,
    fillRenderOrder: config.fillRenderOrder,
    groupID,
    hover,
    blending: config.blending,
    dashed: config.dashed,
    dashScale: config.dashScale,
    dashSize: config.dashSize,
    dashOffset: config.dashOffset,
    gapSize: config.gapSize
  }));
};
const conditionToMaskData = (localizationProps, config, groupID, hover) => {
  const maskData = toMaskData(localizationProps, config, groupID, hover);
  maskData.forEach((mask) => {
    mask.meta = { conditionCode: localizationProps.Code };
  });
  return maskData;
};
const maxFaxAndToothAnatomyToMaskData = (maxFaxAnatomyProps, config, groupID, hover) => {
  const maskData = toMaskData(maxFaxAnatomyProps, config, groupID, hover);
  maskData.forEach((mask) => {
    mask.meta = { conditionCode: maxFaxAnatomyProps.localizationType.value };
  });
  return maskData;
};
const getToothDetectionsByType = async (assetID, type, state) => {
  switch (type) {
    case "CBCT":
      return getToothDetectionsCBCT(assetID, state);
    case "IOXRay":
      return getToothDetectionsIOXRay(assetID, state);
    case "Pano":
      return getToothDetectionsPano(assetID, state);
  }
};
const ForMaxFaxAndTeethAnatomy = (data, config, groupID, hover = "none") => {
  if (!data) return;
  if (data instanceof Promise) {
    return new Promise(async (resolve) => {
      const resolvedData = await data;
      resolve([...resolvedData].map(
        ([_, item]) => maxFaxAndToothAnatomyToMaskData(item, config, groupID, hover)
      ).flat());
    });
  }
  return [...data].map(
    ([_, item]) => maxFaxAndToothAnatomyToMaskData(item, config, groupID, hover)
  ).flat();
};
const createConditionMaskData = (data, config, groupID) => {
  if (!data) return;
  const performOperation = (data2) => {
    return [...data2].map(
      ([_, condition]) => conditionToMaskData(condition, config, groupID, "fill")
    ).flat();
  };
  if (data instanceof Promise) {
    return new Promise(async (resolve) => {
      const resolvedData = await data;
      resolve(performOperation(resolvedData));
    });
  }
  return performOperation(data);
};
const getToothDetectionsMaskData = async (assetID, type, state, renderConfig) => {
  const reportType = (await state.currentReport.getAsync())?.Type;
  const activeRenderConfig = constants.getWAClientMaskRenderConfig(reportType);
  let toothDetections = await getToothDetectionsByType(assetID, type, state);
  if (!toothDetections) return;
  const dentinMaskData = toothDetections.teethDentin.map(
    (dentin) => ForMaxFaxAndTeethAnatomy(dentin, activeRenderConfig.dentin, "anatomy")
  ).flat();
  const pulpMaskData = toothDetections.teethPulp.map(
    (pulp) => ForMaxFaxAndTeethAnatomy(pulp, activeRenderConfig.pulp, "anatomy")
  ).flat();
  const enamelMaskData = toothDetections.teethEnamel.map(
    (enamel) => ForMaxFaxAndTeethAnatomy(enamel, activeRenderConfig.enamel, "anatomy")
  ).flat();
  return { dentinMaskData, pulpMaskData, enamelMaskData };
};
const getMaxFaxMaskData = async (assetID, type, state, renderConfig) => {
  const report = await state.currentReport.getAsync();
  const reportID = report?.ID;
  if (!reportID) return;
  const activeRenderConfig = constants.getWAClientMaskRenderConfig(report?.Type);
  const maxFax = await state.maxFaxAnatomyState.getMaxFaxByType(assetID, type, reportID);
  if (!maxFax) return;
  const hover = type == "IOXRay" ? "outline" : "none";
  const boneMaskData = [...Object.entries(maxFax.bone)].map(
    ([kind, boneData]) => ForMaxFaxAndTeethAnatomy(boneData, activeRenderConfig[kind], "anatomy", hover)
  ).flat();
  const canalMaskData = ForMaxFaxAndTeethAnatomy(maxFax.canal, activeRenderConfig.canal, "anatomy") || [];
  return { boneMaskData, canalMaskData };
};
const getPeriapicalRadiolucencyMaskData = async (assetID, state, renderConfig) => {
  const report = await state.currentReport.getAsync();
  const reportID = report?.ID;
  if (!reportID) return;
  const activeRenderConfig = constants.getWAClientMaskRenderConfig(report?.Type);
  const conditions = (await (await state.conditionsState.localizationGrouped.getAsync(reportID))?.getAsync(assetID))?.periapicalRadiolucency;
  if (!conditions) return;
  return createConditionMaskData(
    conditions,
    activeRenderConfig.periapicalRadiolucency,
    "periapicalRadiolucency"
  );
};

var __create$6 = Object.create;
var __defProp$6 = Object.defineProperty;
var __getOwnPropDesc$6 = Object.getOwnPropertyDescriptor;
var __knownSymbol$6 = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError$6 = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp$6 = (obj, key, value) => key in obj ? __defProp$6(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart$6 = (base) => [, , , __create$6(null)];
var __decoratorStrings$6 = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn$6 = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError$6("Function expected") : fn;
var __decoratorContext$6 = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings$6[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError$6("Already initialized") : fns.push(__expectFn$6(fn || null)) });
var __decoratorMetadata$6 = (array, target) => __defNormalProp$6(target, __knownSymbol$6("metadata"), array[3]);
var __runInitializers$6 = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement$6 = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings$6[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc$6(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext$6(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn$6(it) && (desc[key] = it );
  }
  return desc && __defProp$6(target, name, desc), target;
};
var _update_dec, _init$6, _update_dec2, _init2;
function updateOrCreateObserved(current, data, Constructor) {
  if (!data) return current;
  if (current) {
    current.update(data);
    return current;
  }
  return new Constructor(data);
}
_update_dec = [action];
class ReportObserved {
  constructor(data) {
    __runInitializers$6(_init$6, 5, this);
    this.Specific = arx.obj({ value: void 0, case: void 0 });
    this.update(data);
  }
  update(data) {
    this.ID = data.ID;
    this.OrganizationID = data.OrganizationID;
    this.PatientID = data.PatientID;
    this.Type = data.Type;
    this.LegacyConclusion = data.LegacyConclusion;
    this.DisplayAssetID = data.DisplayAssetID;
    if (data.PrintSettings) {
      if (this.PrintSettings) {
        this.PrintSettings.copy(data.PrintSettings);
      } else {
        this.PrintSettings = arx.obj(data.PrintSettings);
      }
    }
    if (data.Settings) {
      if (this.Settings) {
        this.Settings.copy(data.Settings);
      } else {
        this.Settings = arx.obj(data.Settings);
      }
    }
    this.MedicalImageFeatures = updateOrCreateObserved(
      this.MedicalImageFeatures,
      data.MedicalImageFeatures,
      MedicalImageFeaturesObserved
    );
    switch (data.Specific.case) {
      case "DataIOXRayGP":
        this.Specific.case = data.Specific.case;
        this.Specific.value = updateOrCreateObserved(
          this.Specific.value instanceof ReportIOXRayGPObserved ? this.Specific.value : void 0,
          data.Specific.value,
          ReportIOXRayGPObserved
        );
        break;
      case "DataPanoGP":
        this.Specific.case = data.Specific.case;
        this.Specific.value = updateOrCreateObserved(
          this.Specific.value instanceof ReportPanoGPObserved ? this.Specific.value : void 0,
          data.Specific.value,
          ReportPanoGPObserved
        );
        break;
      case "DataCBCTGP":
        this.Specific.case = data.Specific.case;
        this.Specific.value = updateOrCreateObserved(
          this.Specific.value instanceof ReportCBCTGPObserved ? this.Specific.value : void 0,
          data.Specific.value,
          ReportCBCTGPObserved
        );
        break;
      case "DataPanoBitewings":
        this.Specific.case = data.Specific.case;
        this.Specific.value = updateOrCreateObserved(
          this.Specific.value instanceof ReportPanoBitewingsObserved ? this.Specific.value : void 0,
          data.Specific.value,
          ReportPanoBitewingsObserved
        );
        break;
    }
  }
}
_init$6 = __decoratorStart$6();
__decorateElement$6(_init$6, 1, "update", _update_dec, ReportObserved);
__decoratorMetadata$6(_init$6, ReportObserved);
_update_dec2 = [action];
class ReportFMXObserved {
  constructor(data) {
    __runInitializers$6(_init2, 5, this);
    this.ImagesMeta = arx.map(data.ImagesMeta.map((imageMeta) => [imageMeta.GeneratedAssetID || imageMeta.StudyAssetID, new Report2DImageMetaObserved(imageMeta)]));
  }
  update(data) {
    data.ImagesMeta.forEach((imageMeta) => {
      const imageMetaObserved = this.ImagesMeta.get(imageMeta.GeneratedAssetID || imageMeta.StudyAssetID);
      if (imageMetaObserved) {
        imageMetaObserved.update(imageMeta);
      } else {
        this.ImagesMeta.set(imageMeta.GeneratedAssetID || imageMeta.StudyAssetID, new Report2DImageMetaObserved(imageMeta));
      }
    });
  }
}
_init2 = __decoratorStart$6();
__decorateElement$6(_init2, 1, "update", _update_dec2, ReportFMXObserved);
__decoratorMetadata$6(_init2, ReportFMXObserved);
class ReportPanoBitewingsObserved extends ReportFMXObserved {
  constructor(data) {
    super(data);
    this.PanoStudyAssetID = data.PanoStudyAssetID;
    this.MaxFaxAnatomyLocalizations = data.MaxFaxAnatomyLocalizations;
    data && this.update(data);
  }
}
class ReportIOXRayGPObserved extends ReportFMXObserved {
  constructor(data) {
    super(data);
    this.MaxFaxAnatomyLocalizations = data.MaxFaxAnatomyLocalizations;
    data && this.update(data);
  }
}
class ReportPanoGPObserved extends ReportFMXObserved {
  constructor(data) {
    super(data);
    this.PanoStudyAssetID = data.PanoStudyAssetID;
    this.MaxFaxAnatomyLocalizations = data.MaxFaxAnatomyLocalizations;
    data && this.update(data);
  }
}
class ReportCBCTGPObserved {
  constructor(data) {
    data && this.update(data);
  }
  update(data) {
    this.OrthoScreening = data?.OrthoScreening;
    this.CropGeometryData = data?.CropGeometryData;
    this.MaxFaxAnatomyLocalizations = data?.MaxFaxAnatomyLocalizations ?? [];
  }
}
class Report2DImageMetaObserved {
  constructor(data) {
    this.OrientationAngle = arx.box();
    this.MedicalImageFeatures = new MedicalImageFeaturesObserved();
    this.Scale = new FloatTuple2DObserved();
    data && this.update(data);
  }
  update(data) {
    this.ID = data.ID ?? this.ID;
    this.Type = data.Type ?? this.Type;
    this.StudyAssetID = data.StudyAssetID ?? this.StudyAssetID;
    this.GeneratedAssetID = data.GeneratedAssetID ?? this.GeneratedAssetID;
    data.Scale && this.Scale?.update(data.Scale);
    this.OrientationAngle.set(data.OrientationAngle ?? 0);
    data.MedicalImageFeatures && this.MedicalImageFeatures?.update(data.MedicalImageFeatures);
  }
}
class FloatTuple2DObserved {
  constructor(data) {
    data && this.update(data);
  }
  update(data) {
    this.X = data.X ?? 0;
    this.Y = data.Y ?? 0;
  }
}
class MedicalImageFeaturesObserved {
  constructor(data) {
    this.ViewOptions = arx.obj({ ...data?.ViewOptions });
  }
  update(data) {
    data.ViewOptions && this.ViewOptions.copy(data.ViewOptions);
  }
}

const selectBPLData = async (reportID, assetID, state, scale) => {
  const conditions = (await state.conditionsState.flatConditions.getAsync(reportID))?.Tooth;
  const pblConditions = await (await state.conditionsState.pblConditions.getAsync(reportID))?.getAsync(assetID);
  const teeth = await (await state.teeth.getAsync(reportID))?.getAsync(assetID);
  const teethLandmarks = await state.teethLandmarks.getAsync(reportID);
  if (!conditions || !pblConditions || !teeth || !teethLandmarks) return;
  const PBLs = [];
  teeth.forEach((tooth) => {
    if (!tooth.IsInROI) return;
    const pblConditionsForTooth = pblConditions.get(tooth.ID);
    if (!pblConditionsForTooth) return;
    pblConditionsForTooth.forEach((pblCondition) => {
      const lowerLandmarkID = pblCondition.Localization.PeriodontalBoneLossLandmarks?.BoneLevelLandmarkID;
      const upperLandmarkID = pblCondition.Localization.PeriodontalBoneLossLandmarks?.CementoenamelJunctionLandmarkID;
      if (!lowerLandmarkID || !upperLandmarkID) return;
      const lowerLandmark = teethLandmarks.get(tooth.ID)?.Image.get(lowerLandmarkID);
      const upperLandmark = teethLandmarks.get(tooth.ID)?.Image.get(upperLandmarkID);
      if (!lowerLandmark || !upperLandmark) return;
      let positiveCondition;
      const ChildrenIDs = conditions.get(pblCondition.conditionID)?.ChildrenIDs;
      if (ChildrenIDs) {
        [...ChildrenIDs].some((childConditionId) => {
          const condition = conditions.get(childConditionId);
          if (!condition) return false;
          if (condition.Certainty?.ModelDecision === Decision.PositiveDecision) {
            positiveCondition = condition;
            return true;
          }
        });
      }
      const boneLossMM = Math.hypot(
        (upperLandmark.ModelPosition?.X || 0) - (lowerLandmark.ModelPosition?.X || 0),
        (upperLandmark.ModelPosition?.Y || 0) - (lowerLandmark.ModelPosition?.Y || 0)
      ) * scale;
      const pblColor = boneLossMM >= constants.PBL_WHITE_MAX_MM && positiveCondition && constants.PBL_RED_CODES.includes(positiveCondition.Code) ? constants.PBL_RED_COLOR : constants.PBL_GRAY_COLOR;
      const pbl = {
        start: {
          x: lowerLandmark?.ModelPosition?.X || 0,
          y: lowerLandmark?.ModelPosition?.Y || 0
        },
        end: {
          x: upperLandmark?.ModelPosition?.X || 0,
          y: upperLandmark?.ModelPosition?.Y || 0
        },
        color: pblColor,
        textProps: {
          color: pblColor,
          outlineColor: pblColor === constants.PBL_GRAY_COLOR ? "black" : void 0,
          outlineWidth: pblColor === constants.PBL_GRAY_COLOR ? 0.4 : void 0
        }
      };
      PBLs.push(pbl);
    });
  });
  return PBLs;
};

var vertexShader = "#version 300 es\n#define GLSLIFY 1\nuniform vec2 size;uniform vec4 viewport;uniform mat4 modelViewMatrix;uniform mat4 projectionMatrix;uniform float glowSize;uniform float lineWidth;in vec3 position;in vec2 uv;out vec2 coords;out float scale;void main(){float objectScale=max(length((modelViewMatrix*vec4(0.0,1.0,0.0,0.0)).xyz),0.000001);scale=objectScale*(projectionMatrix*vec4(0,1,0,1.0)).y*viewport.w;float glowOffset=(glowSize/scale)*2.0;vec2 expandedSize=size+vec2(glowOffset);coords=uv*expandedSize;float expansionFactor=1.0+(glowOffset/max(size.x,size.y));vec3 expandedPosition=position*expansionFactor;gl_Position=projectionMatrix*modelViewMatrix*vec4(expandedPosition,1.0);}"; // eslint-disable-line

var fragmentShader = "#version 300 es\nprecision highp float;\n#define GLSLIFY 1\nuniform vec4 color;uniform float radius;uniform vec2 size;uniform float lineWidth;uniform float glowIntensity;uniform float glowSize;in vec2 coords;in float scale;out vec4 out_color;float roundedBoxSDF(vec2 CenterPosition,vec2 Size,float Radius){return length(max(abs(CenterPosition)-Size+Radius,0.0f))-Radius;}void main(){float thickness=lineWidth/scale;float glowOffset=(glowSize/scale)*2.0;vec2 size2=size.xy-thickness*2.0;vec2 location=(size.xy+vec2(glowOffset))/2.0;float edgeSoftness=1.0f/scale;float radius=min(radius/scale,min(size2.x,size2.y)/3.0f);float distance=roundedBoxSDF(location-coords.xy,size2/2.0,radius);float borderMask=1.0f-smoothstep(-edgeSoftness,edgeSoftness,abs(distance)-thickness);float glowDistance=glowSize/scale;float glowFalloff=distance*distance/(glowDistance*glowDistance);float glowAlpha=exp(-glowFalloff)*glowIntensity;vec3 lerpedColor=mix(color.rgb,vec3(1.0),0.5);vec4 borderColor=vec4(lerpedColor,borderMask);vec4 glowColor=vec4(color.rgb,glowAlpha);out_color=mix(glowColor,borderColor,borderMask);}"; // eslint-disable-line

class NeonRoundCornerBBoxMaterial extends RawShaderMaterial {
  constructor(size, radius, color, lineWidth, glowIntensity = 0.5, glowSize = 20) {
    color = new Color(color);
    super({
      vertexShader,
      fragmentShader,
      side: DoubleSide,
      uniforms: {
        lineWidth: { value: lineWidth },
        size: { value: size },
        radius: { value: radius },
        viewport: { value: new Vector4() },
        color: { value: new Vector4(color.r, color.g, color.b, 1) },
        glowIntensity: { value: glowIntensity },
        glowSize: { value: glowSize }
      }
    });
    this.toneMapped = false;
    this.transparent = true;
    this.depthTest = false;
    this.depthWrite = false;
  }
  setSize(width, height) {
    this.uniforms.size.value.set(width, height);
  }
}

class RoundCornerBBoxHighlighter extends Highlighter {
  constructor(entity) {
    if (!(entity instanceof RoundCornerBBox)) {
      throw new Error("entity must be a RoundCornerBBox");
    }
    super(entity);
    this.originalMaterial = entity.object3d.material;
    this.neonMaterial = new NeonRoundCornerBBoxMaterial(
      new Vector2(),
      20,
      // radius
      new Color().fromArray(this.originalMaterial.uniforms.color.value.toArray()),
      2,
      // lineWidth
      1.5,
      // glowIntensity
      4
      // glowSize
    );
    this.neonMaterial.uniforms.viewport.value = this.originalMaterial.uniforms.viewport.value;
  }
  highlight() {
    this.entity.object3d.material = this.neonMaterial;
    const { x, y } = this.originalMaterial.uniforms.size.value;
    this.neonMaterial.setSize(x, y);
  }
  unHighlight() {
    this.entity.object3d.material = this.originalMaterial;
  }
}

class ConditionBBox extends RoundCornerBBox {
  constructor(props, viewport) {
    super(props, viewport);
    this.id = props.id;
    this.setComponent(HoverComponent, new HoverComponent(this, new RoundCornerBBoxHighlighter(this)));
  }
}

const selectNDFs = async (state, reportID, lineWidth = 2, borderRadius = 20) => {
  reportID = reportID || (await state.currentReport.getAsync())?.ID;
  if (!reportID) return;
  const maxFaxConditions = (await state.conditionsState.flatConditions.getAsync(reportID))?.MaxFax;
  if (!maxFaxConditions) return;
  return [...maxFaxConditions.values()].filter((condition) => condition.Certainty?.EngineDecision === Decision.PositiveDecision).map((condition) => {
    return condition.Localization.map((localization) => {
      const box = localization.BBox;
      if (!box) return;
      const data = {
        id: localization.ID,
        min: flipY({ x: box.X.Min, y: box.Y.Min }),
        max: flipY({ x: box.X.Max, y: box.Y.Max }),
        width: lineWidth,
        borderRadius,
        color: "#fffe54"
      };
      return new ConditionBBox(data);
    });
  }).flat().filter((x) => typeof x !== "undefined");
};

const getToothMaskData = async (toothID, assetID, state, config = constants.WA_CLIENT_MASK_RENDER_CONFIG.tooth, groupID = "tooth", hover = "none") => {
  const reportID = (await state.currentReport.getAsync())?.ID;
  if (!reportID) return null;
  const tooth = state.flatTeeth.get(reportID)?.get(toothID);
  if (!tooth) return null;
  const maskData = [];
  for (const detection of tooth.Detections) {
    const localization = detection.Localizations;
    if (localization?.TargetAssetID === assetID && localization.PolygonalMask?.length) {
      localization.PolygonalMask.forEach((polygon) => {
        maskData.push({
          imageID: assetID,
          localizationID: localization.ID,
          path: polygon.Points.map((point) => ({ x: point.X, y: point.Y })),
          color: config.color,
          outlineColor: config.color,
          outlineOpacity: config.outlineOpacity,
          opacity: config.opacity,
          outlineWidth: 3,
          renderOrder: config.renderOrder,
          fillRenderOrder: config.fillRenderOrder,
          groupID,
          hover,
          blending: config.blending,
          dashed: config.dashed,
          dashScale: config.dashScale,
          dashSize: config.dashSize,
          dashOffset: config.dashOffset,
          gapSize: config.gapSize,
          meta: {
            toothID,
            detectionID: detection.ID
          }
        });
      });
    }
  }
  return maskData.length > 0 ? maskData : null;
};

const MASK_SMOOTHING_ITERATIONS = 4;
const makeMask = (maskData, pixelSpacing = DEFAULT_PIXEL_SPACING_MM, lineType) => {
  let maskPropsExceptPoints = {
    id: maskData.localizationID,
    contourEditID: JSON.stringify([
      maskData.imageID,
      maskData.groupID,
      maskData.localizationID
    ]),
    color: maskData.outlineColor,
    opacity: maskData.outlineOpacity,
    fillColor: maskData.color,
    fillOpacity: maskData.opacity,
    width: maskData.outlineWidth || 3,
    renderOrder: typeof maskData.renderOrder === "number" ? MathUtils.clamp(maskData.renderOrder, 0, 99) - 100 : void 0,
    fillRenderOrder: typeof maskData.fillRenderOrder === "number" ? MathUtils.clamp(maskData.fillRenderOrder, 0, 99) - 100 : void 0,
    dashed: maskData.dashed,
    dashScale: (maskData.dashScale || 1) * pixelSpacing.xMmPerPixel,
    dashSize: maskData.dashSize,
    dashOffset: maskData.dashOffset,
    gapSize: maskData.gapSize,
    hover: maskData.hover,
    blending: maskData.blending,
    meta: maskData.meta,
    pixelSpacingMm: pixelSpacing
  };
  let mask;
  if (Array.isArray(maskData.path)) {
    if (!maskData.path.length) {
      console.warn(`[addMasks] path is empty. Condition localizationID = ${maskData.localizationID}`);
      return;
    }
    const controlPoints = maskData.path.map((point) => flipY({ ...point, z: 0 }));
    const firstPoint = controlPoints[0];
    const lastPoint = controlPoints.at(-1);
    if (controlPoints.length > 1 && firstPoint.x === lastPoint?.x && firstPoint.y === lastPoint.y) {
      controlPoints.pop();
    }
    const maskProps = {
      points: maskData.path,
      controlPoints,
      smoothingIterations: MASK_SMOOTHING_ITERATIONS,
      ...maskPropsExceptPoints
    };
    maskProps.points = smoothPath(controlPoints, MASK_SMOOTHING_ITERATIONS);
    mask = new Mask(maskProps, void 0, lineType);
  } else {
    const maskProps = {
      ...maskPropsExceptPoints,
      min: flipY({ x: maskData.path.X.Min, y: maskData.path.Y.Min }),
      max: flipY({ x: maskData.path.X.Max, y: maskData.path.Y.Max })
    };
    mask = new BoxMask(maskProps);
  }
  return mask;
};

const tripleMap = (key1, key2, key3, value, map) => {
  if (typeof value === "undefined" || value === null) return false;
  const map1 = map.get(key1);
  if (map1 instanceof Map) {
    return doubleMap(key2, key3, value, map1);
  } else {
    map.set(key1, arx.map([[key2, arx.map([[key3, value]])]]));
  }
  return true;
};
const doubleMap = (key1, key2, value, map) => {
  if (typeof value === "undefined" || value === null) return false;
  const map2 = map.get(key1);
  if (map2 instanceof Map) {
    return singleMap(key2, value, map2);
  } else {
    map.set(key1, arx.map([[key2, value]]));
  }
  return true;
};
const singleMap = (key, value, map) => {
  if (typeof value === "undefined" || value === null) return false;
  const arr = map.get(key);
  if (Array.isArray(arr)) {
    arr.push(value);
  } else {
    map.set(key, value);
  }
  return true;
};

const _Setup = class _Setup {
  constructor(state) {
    this.state = state;
  }
  addAutoRun(fn) {
    const disposer = autoRun(fn);
    _Setup.disposers.push(disposer);
    return disposer;
  }
  async watchTeethChange() {
    this.addAutoRun(() => {
      MIRenderAPI$1.setTeeth();
    });
  }
  async watchReportChange() {
    const reportID = await this.state.dataFromURL.getAsync("reportID");
    this.addAutoRun(() => {
      if (!this.state.dataFromURL.reportID || this.state.dataFromURL.reportID === reportID) return;
      _Setup.disposers.forEach((disposer) => disposer());
      _Setup.disposers.length = 0;
      MIRenderAPI$1.shutdown();
    });
  }
  watchFeatureFlags() {
    this.addAutoRun(() => {
      const organizationID = this.state.currentReport.get()?.OrganizationID;
      if (!organizationID) return;
      const featureFlags = this.state.organizations.get(organizationID)?.FeatureFlags;
      if (!featureFlags) return;
      runInShadow(() => {
        featureFlags.forEach((featureFlag) => {
          singleMap(featureFlag.Flag, featureFlag.Value, this.state.featureFlags);
        });
      });
    });
  }
  watchHoveredConditionBoxes() {
    this.addAutoRun(() => {
      if (this.state.featureFlags.get(FeatureFlag.FeatureFlag_Hide_ConditionsMasks)) {
        return;
      }
      const conditionBBoxes = selectHoveredConditionBBoxes(this.state);
      const toothBBoxes = selectHoveredToothBBoxes(this.state);
      const allBBoxes = [...conditionBBoxes || [], ...toothBBoxes || []];
      runInShadow(() => {
        MIRenderAPI$1.deleteConditionBoxes();
        allBBoxes.length > 0 && MIRenderAPI$1.addConditionBoxes(allBBoxes);
      });
    });
  }
  watchColorInversion() {
    this.addAutoRun(() => {
      let isInverted;
      if (this.state.dataFromURL.pageType !== "preview") {
        isInverted = this.state.toolbarsState["main"]?.invert;
      } else {
        isInverted = this.state.currentReport.get()?.PrintSettings?.IsInverted;
      }
      if (isInverted) {
        MIRenderAPI$1.invertColors();
      } else {
        MIRenderAPI$1.straightColors();
      }
    });
    this.addAutoRun(() => {
      const isInverted = this.state.currentReport.get()?.PrintSettings?.IsInverted;
      if (this.state.dataFromURL.pageType !== "preview") {
        if (isInverted) {
          MIRenderAPI$1.straightSliceEditorColors();
        }
      } else {
        if (isInverted) {
          MIRenderAPI$1.invertSliceEditorColors();
        } else {
          MIRenderAPI$1.straightSliceEditorColors();
        }
      }
    });
  }
  async addConditionMask(assetID) {
    const report = await this.state.currentReport.getAsync();
    const reportID = report?.ID || await this.state.dataFromURL.getAsync("reportID");
    const conditions = await (await this.state.conditionsState.localizationGrouped.getAsync(reportID))?.getAsync(assetID);
    if (!conditions) return;
    const renderConfig = constants.getWAClientMaskRenderConfig(report?.Type);
    const { perio, endo, restorative, chart, enamel, periapicalRadiolucency } = conditions;
    const addMask = (data, maskType, groupID, hover = "fill") => () => {
      const handleOperation = (props) => {
        if (localizationPropsFilter(this.state, props, maskType)) {
          MIRenderAPI$1.deleteMasksByID(props.Localization.TargetAssetID, [props.Localization.ID], groupID);
          MIRenderAPI$1.addMasks(conditionToMaskData(props, renderConfig[maskType], groupID, hover));
        } else {
          MIRenderAPI$1.deleteMasksByID(props.Localization.TargetAssetID, [props.Localization.ID], groupID);
        }
      };
      data.forEach((props) => handleOperation(props));
    };
    _Setup.disposers.push(
      autoRun(addMask(perio, "perio", "perio")),
      autoRun(addMask(endo, "endo", "endo")),
      autoRun(addMask(restorative, "restorative", "restorative")),
      autoRun(addMask(chart, "previousTreatment", "chart", "none")),
      autoRun(addMask(enamel, "enamel", "anatomy", "none"))
    );
  }
  async addMaxFaxMask(assetID, type) {
    if (isFDAVersion2Enabled(this.state)) return;
    const masks = await getMaxFaxMaskData(assetID, type, this.state);
    if (!masks) return;
    masks && Object.values(masks).flat().forEach((mask) => {
      if (!mask) return;
      if (mask instanceof Promise) {
        mask.then((mask2) => mask2 && MIRenderAPI$1.addMasks(mask2));
      } else {
        MIRenderAPI$1.addMasks([mask]);
      }
    });
  }
  async addToothDetectionsMask(assetID, type) {
    if (isFDAVersion2Enabled(this.state)) return;
    const masks = await getToothDetectionsMaskData(assetID, type, this.state);
    if (!masks) return;
    masks && Object.values(masks).flat().forEach((mask) => {
      if (!mask) return;
      if (mask instanceof Promise) {
        mask.then((mask2) => mask2 && MIRenderAPI$1.addMasks(mask2));
      } else {
        MIRenderAPI$1.addMasks([mask]);
      }
    });
  }
  async getScale(assetID, waitForGeneralImageDownloaded) {
    let scale;
    const SpecificValue = (await this.state.currentReport.getAsync())?.Specific?.value;
    const image = waitForGeneralImageDownloaded ? await waitForGeneralImageDownloaded : void 0;
    if (image && SpecificValue instanceof ReportCBCTGPObserved) {
      const texture = image.getImage().texture;
      if (texture instanceof DicomTexture) {
        scale = texture.pixelSpacing?.xMmPerPixel;
      }
    } else if (SpecificValue instanceof ReportFMXObserved) {
      scale = SpecificValue.ImagesMeta.get(assetID)?.Scale.X;
      if (!scale) {
        const width = this.state.panoImage.get(assetID)?.size?.width;
        if (width) {
          return 300 / width;
        }
      }
    }
    return scale || 1;
  }
  async addPBLs(reportID, assetID, waitForGeneralImageDownloaded) {
    const scale = await this.getScale(assetID, waitForGeneralImageDownloaded);
    const PBLs = await selectBPLData(reportID, assetID, this.state, scale);
    if (!PBLs) return;
    MIRenderAPI$1.setPBLs([{
      PBLs,
      imageID: assetID,
      scale
    }]);
  }
  async addTeethNumbers(reportID, waitForGeneralImageDownloaded) {
    const teeth = await this.state.flatTeeth.getAsync(reportID);
    await this.state.currentReport.getAsync();
    const teethNumbers = /* @__PURE__ */ new Map();
    const teethByDetections = /* @__PURE__ */ new Map();
    const currentOrganizationID = await this.state.currentOrganizationID.getAsync();
    const currentOrganization = await this.state.organizations.getAsync(currentOrganizationID);
    const format = currentOrganization?.Settings?.DentalNotationFormat || DentalNotationFormat.DentalNotationFormatISO;
    if (!teeth) return;
    const makeToothNumber = async (tooth, detection) => {
      if (!detection.Localizations) return;
      const box = detection.Localizations.BBox;
      const assetID = detection.Localizations.TargetAssetID;
      if (!box || !tooth.Numeration) return;
      const newBox = { min: { x: box.X?.Min || 0, y: -(box.Y?.Max || 0) }, max: { x: box.X?.Max || 0, y: -(box.Y?.Min || 0) } };
      const width = newBox.max.x - newBox.min.x;
      const height = newBox.max.y - newBox.min.y;
      const position = { x: newBox.min.x + width / 2, y: newBox.min.y + height / 2 };
      const toothNumber = MIRenderAPI$1.addTeethNumbers([{
        position,
        assetID,
        scale: await this.getScale(assetID, waitForGeneralImageDownloaded),
        numeration: tooth.Numeration,
        detectionID: detection.ID,
        toothID: tooth.ID,
        isRemoved: tooth.IsRemoved || detection.IsRemoved,
        format
      }])?.[0];
      if (!toothNumber) {
        return;
      }
      const arr = teethNumbers.get(assetID);
      if (arr) {
        arr.push(toothNumber);
      } else {
        teethNumbers.set(assetID, [toothNumber]);
      }
      teethByDetections.set(detection.ID, toothNumber);
      this.state.teethNumberPendingChanges.observe(toothNumber.detectionID, async (change) => {
        if (change) {
          const isRemove = change.newNumber === -1;
          if (!toothNumber.toothMasks.length) {
            const maskData = await getToothMaskData(toothNumber.toothID, assetID, this.state);
            const masks = maskData ? maskData.map((x) => makeMask(x)).filter((x) => typeof x !== "undefined") : void 0;
            masks && toothNumber.addMasks(masks);
          }
          if (isRemove) {
            toothNumber.setPendingRemove();
          } else {
            await toothNumber.setPendingNumber(change.newNumber);
          }
        } else {
          await toothNumber.resetPendingNumber();
        }
        updateDuplicates(assetID);
      }, false);
    };
    teeth.observe(async ({ obj, changes }) => {
      if (!changes) {
        await Promise.all([...obj.values()]?.map(async (tooth) => {
          await Promise.all(tooth.Detections.map(async (detection) => {
            await makeToothNumber(tooth, detection);
          }));
        }));
        teethNumbers.forEach((_, assetID) => {
          updateDuplicates(assetID);
        });
        return;
      }
      const changedAssets = /* @__PURE__ */ new Set();
      await Promise.all([...changes.values()].map(async (change) => {
        await Promise.all(change.current.Detections.map((detection) => {
          changedAssets.add(detection.Localizations?.TargetAssetID);
          const toothNumber = teethByDetections.get(detection.ID);
          if (toothNumber) {
            if (change.current.Numeration) {
              toothNumber.toothNumber = change.current.Numeration;
              toothNumber.toothID = change.current.ID;
              toothNumber.isRemoved = change.current.IsRemoved || detection.IsRemoved;
              if (toothNumber.pendingToothNumber === toothNumber.toothNumber.ISO) {
                toothNumber.resetPendingNumber();
              } else {
                toothNumber.update();
              }
            }
          } else {
            makeToothNumber(change.current, detection);
          }
        }));
      }));
      changedAssets.forEach((assetID) => {
        assetID && updateDuplicates(assetID);
      });
    }, true);
    this.addAutoRun(() => {
      const currentOrganizationID2 = this.state.currentOrganizationID.get();
      if (currentOrganizationID2) {
        const currentOrganization2 = this.state.organizations.get(currentOrganizationID2);
        const format2 = currentOrganization2?.Settings?.DentalNotationFormat || DentalNotationFormat.DentalNotationFormatISO;
        teethNumbers.forEach((toothNumber) => toothNumber.forEach((t) => t.setNotationFormat(format2)), true);
      }
    });
    const updateDuplicates = (assetID) => {
      const teethNumbersForAsset = teethNumbers.get(assetID);
      if (!teethNumbersForAsset) return;
      const numberGroups = /* @__PURE__ */ new Map();
      teethNumbersForAsset.forEach((tooth) => {
        if (tooth.isRemovedOrPendingRemove) return;
        const currentNumber = tooth.pendingToothNumber ?? tooth.toothNumber.ISO;
        const group = numberGroups.get(currentNumber) || [];
        group.push(tooth);
        numberGroups.set(currentNumber, group);
      });
      teethNumbersForAsset.forEach(async (tooth) => {
        if (tooth.isRemovedOrPendingRemove) {
          tooth.setWarning(false);
          return;
        }
        const currentNumber = tooth.pendingToothNumber ?? tooth.toothNumber.ISO;
        const group = numberGroups.get(currentNumber) || [];
        if (!tooth.toothMasks.length && group.length > 1) {
          const maskData = await getToothMaskData(tooth.toothID, assetID, this.state);
          const masks = maskData ? maskData.map((x) => makeMask(x)).filter((x) => typeof x !== "undefined") : void 0;
          masks && tooth.addMasks(masks);
        }
        tooth.setWarning(group.length > 1, group.length > 1 ? group : void 0);
      });
    };
  }
  isTypewriter() {
    const url = new URL(window.location.toString());
    return url.searchParams.get("isTypewriter") === "true";
  }
  setFlagForTypewriter() {
    if (this.isTypewriter()) {
      ImageLoader$1.waitForLoadingComplete().then(async () => {
        await Promise.all(Object.values(MIRender.view.viewports).filter((x) => typeof x !== "undefined" && x.isEnable()).map((x) => x.waitForRenderComplete()));
        await sleep(3e3);
        window.MI_RENDER_READY = true;
      });
    }
  }
  async setupNDF(report, waitForLoad) {
    const reportID = report.ID;
    const container = await waitForLoad;
    if (!container) return;
    const NDFImageContainer = new ImageContainer(container.width, container.height, container.id, container.getImage().clone());
    MIRenderAPI$1.NDFAPI.setImage(NDFImageContainer);
    const bBoxes = await selectNDFs(this.state, reportID, 2, 20);
    bBoxes && NDFImageContainer.addConditionBoxes(bBoxes);
    const getLocalizationIDs = (conditionId) => {
      const localization = this.state.conditionsState.flatConditions.get(reportID)?.MaxFax.get(conditionId)?.Localization;
      if (!localization) return [];
      return localization.map((localization2) => localization2.ID).filter((x) => typeof x !== "undefined");
    };
    this.state.hoveredNonDentalConditions.observe((conditions, prevConditions) => {
      if (prevConditions) {
        const prevIds = prevConditions.map((condition) => getLocalizationIDs(condition)).flat();
        MIRenderAPI$1.NDFAPI.unhoverConditionBox(prevIds);
      }
      const ids = conditions.map((condition) => getLocalizationIDs(condition)).flat();
      MIRenderAPI$1.NDFAPI.hoverConditionBox(ids);
    });
  }
};
_Setup.disposers = [];
let Setup = _Setup;

class SetupCBCT extends Setup {
  async setup() {
    MIRenderAPI$1.launch();
    let preserveDrawingBuffer, isTypewriter;
    isTypewriter = preserveDrawingBuffer = this.isTypewriter();
    MIRenderAPI$1.addMainView(preserveDrawingBuffer);
    MIRenderAPI$1.NDFAPI.addNDFView(preserveDrawingBuffer);
    MIRenderAPI$1.addSliceEditor();
    this.watchReportChange();
    this.watchFeatureFlags();
    this.initMainView(false).then(({ general, report, waitForGeneralImageDownloaded }) => {
      this.watchTeethChange();
      this.watchHoveredConditionBoxes();
      this.watchToolbarsState();
      this.watchMaskVisibility();
      !isTypewriter && this.addMasks(general.ImageID);
      this.addPeriapicalRadiolucencyMasks();
      this.watchColorInversion();
      !isTypewriter && this.addPBLs(report.ID, general.ImageID, waitForGeneralImageDownloaded);
      this.onCloseModal();
      this.watchAnnotationVisibility();
      this.setupNDF(report, waitForGeneralImageDownloaded);
      this.setFlagForTypewriter();
      this.addTeethNumbers(report.ID, waitForGeneralImageDownloaded);
    });
  }
  async initMainView(usePreview = true) {
    const report = await this.state.currentReport.getAsync();
    if (!report) return Promise.reject(new Error("Report not found"));
    const general = await selectReformatGeneralImageInterface(this.state, report);
    const split = await selectReformatSplitImageInterface(this.state, report);
    this.splitID = split.ImageID;
    this.generalID = general.ImageID;
    const result = MIRenderAPI$1.specifyMainView(
      [[[general]], [[split]]],
      void 0,
      {
        [general.ImageID]: {
          viewportType: "pano"
        },
        [split.ImageID]: {
          viewportType: "pano"
        }
      },
      void 0,
      usePreview
    );
    const waitForGeneralImageDownloaded = result?.[0]?.waitForLoad || Promise.reject();
    if (report.Settings?.ShowPanoramaSplitInsteadOfSingle) {
      MIRenderAPI$1.getLayoutController()?.focus(this.splitID);
    } else {
      MIRenderAPI$1.getLayoutController()?.focus(this.generalID);
    }
    return { general, split, report, waitForGeneralImageDownloaded };
  }
  async watchMaskVisibility() {
    MIRenderAPI$1.specifyMaskGroups(["perio", "endo", "restorative", "chart", "anatomy"]);
    const isVisible = (maskGroup, flags) => {
      const previewPage = this.state.dataFromURL.pageType === "preview";
      const isVisible2 = this.state.maskFilters[maskGroup] && flags.every((flag) => !this.state.featureFlags.get(flag));
      const isPrintVisible = this.state.currentReport.get()?.PrintSettings?.ShowMasks && isVisible2;
      return previewPage ? isPrintVisible : isVisible2 && !this.state.toolbarsState.main?.editNumbers;
    };
    const getConditionMaskFlags = () => {
      const flags = [FeatureFlag.FeatureFlag_Hide_ConditionsMasks, FeatureFlag.FeatureFlag_Hide_AllCBCTMasks];
      if (!isFDAVersion2Enabled(this.state)) {
        flags.push(FeatureFlag.FeatureFlag_FDA_Annotator);
      }
      return flags;
    };
    const getAnatomyMaskFlags = () => {
      const flags = [FeatureFlag.FeatureFlag_Hide_AnatomyMasks, FeatureFlag.FeatureFlag_Hide_AllCBCTMasks];
      if (!isFDAVersion2Enabled(this.state)) {
        flags.push(FeatureFlag.FeatureFlag_FDA_Annotator);
      }
      return flags;
    };
    this.addAutoRun(() => {
      isVisible("perio", getConditionMaskFlags()) ? MIRenderAPI$1.showMasks(["perio"]) : MIRenderAPI$1.hideMasks(["perio"]);
    });
    this.addAutoRun(() => {
      const visible = !this.state.featureFlags.get(FeatureFlag.FeatureFlag_Hide_PBLRulesAndMeasurements) && !this.state.featureFlags.get(FeatureFlag.FeatureFlag_Hide_AllCBCTMasks) && isVisible("perio", [FeatureFlag.FeatureFlag_Hide_ConditionsMasks, FeatureFlag.FeatureFlag_FDA_Annotator]) && !isFDA3DGPRestrictionsEnabled(this.state);
      if (visible) {
        MIRenderAPI$1.showPBLs();
      } else {
        MIRenderAPI$1.hidePBLs();
      }
    });
    this.addAutoRun(() => {
      isVisible("endo", getConditionMaskFlags()) ? MIRenderAPI$1.showMasks(["endo"]) : MIRenderAPI$1.hideMasks(["endo"]);
    });
    this.addAutoRun(() => {
      isVisible("restorative", getConditionMaskFlags()) ? MIRenderAPI$1.showMasks(["restorative"]) : MIRenderAPI$1.hideMasks(["restorative"]);
    });
    this.addAutoRun(() => {
      isVisible("chart", getConditionMaskFlags()) ? MIRenderAPI$1.showMasks(["chart"]) : MIRenderAPI$1.hideMasks(["chart"]);
      if (isFDAVersion2Enabled(this.state)) {
        MIRenderAPI$1.hideMasks(["anatomy"]);
      } else if (isVisible("chart", getAnatomyMaskFlags())) {
        MIRenderAPI$1.showMasks(["anatomy"]);
      } else {
        MIRenderAPI$1.hideMasks(["anatomy"]);
      }
    });
    this.addAutoRun(() => {
      if (this.state.dataFromURL.pageType === "preview") {
        MIRenderAPI$1.hidePBLsText();
      } else {
        MIRenderAPI$1.showPBLsText();
      }
    });
  }
  watchToolbarsState() {
    const split = () => {
      runInShadow(() => {
        if (this.splitID) {
          MIRenderAPI$1.getLayoutController()?.focus(this.splitID);
        }
      });
    };
    const general = () => {
      runInShadow(() => {
        if (this.generalID) {
          MIRenderAPI$1.getLayoutController()?.focus(this.generalID);
        }
      });
    };
    this.addAutoRun(() => {
      if (this.state.dataFromURL.pageType === "preview") {
        if (this.state.currentReport.get()?.Settings?.ShowPanoramaSplitInsteadOfSingle) {
          split();
        } else {
          general();
        }
      } else {
        const toolbarState = this.state.toolbarsState["main"];
        if (toolbarState && toolbarState.split) {
          split();
        } else {
          general();
        }
      }
    });
    this.addAutoRun(() => {
      if (this.state.dataFromURL.pageType === "preview") {
        MIRenderAPI$1.activateMode("main", "printMode");
      } else {
        const toolbarState = this.state.toolbarsState.main;
        if (toolbarState && toolbarState.activeControl) {
          const modeName = UI_TOOL_TO_MAIN_MODE_MAP[toolbarState.activeControl];
          modeName && MIRenderAPI$1.activateMode("main", modeName);
        } else {
          MIRenderAPI$1.activateMode("main", "mainMode");
        }
      }
    });
    this.addAutoRun(() => {
      const toolbarState = this.state.toolbarsState.sliceEditor;
      if (toolbarState && toolbarState.activeControl) {
        const modeName = UI_TOOL_TO_SLICE_EDITOR_MODE_MAP[toolbarState.activeControl];
        modeName && MIRenderAPI$1.activateMode("sliceEditor", modeName);
      } else {
        MIRenderAPI$1.activateMode("sliceEditor", "mainMode");
      }
    });
    this.addAutoRun(() => {
      if (this.state.toolbarsState.main?.editNumbers && this.state.dataFromURL.pageType !== "preview") {
        MIRenderAPI$1.showTeethNumbers();
      } else {
        MIRenderAPI$1.hideTeethNumbers();
      }
    });
  }
  async addMasks(reformatAssetID) {
    const report = await this.state.currentReport.getAsync();
    if (!report) return;
    this.addConditionMask(reformatAssetID);
    this.addMaxFaxMask(reformatAssetID, "CBCT");
    this.addToothDetectionsMask(reformatAssetID, "CBCT");
  }
  onCloseModal() {
    this.state.isModalOpen.observe((isOpen) => {
      if (!isOpen) {
        MIRenderAPI$1.hideSlice();
      }
    });
  }
  watchAnnotationVisibility() {
    this.addAutoRun(() => {
      const toolbarState = this.state.toolbarsState.sliceEditor;
      if (toolbarState && toolbarState.hideMarks) {
        MIRenderAPI$1.hideMarks();
      } else {
        MIRenderAPI$1.showMarks();
      }
    });
  }
  async addPeriapicalRadiolucencyMasks() {
    const viewName = "sliceEditor";
    const imageController = MIRender.getView(viewName)?.imageController;
    imageController?.imageContainers.observe((data) => {
      let imageContainers;
      if (data.changes) {
        imageContainers = [...data.changes.values()].filter(({ type }) => type === "set").map(({ current: imageContainer }) => imageContainer);
      } else {
        imageContainers = [...data.obj.values()];
      }
      imageContainers.forEach(async (imageContainer) => {
        const assetID = imageContainer.id;
        imageContainer.specifyMaskGroups(["periapicalRadiolucency"]);
        const mask = await getPeriapicalRadiolucencyMaskData(assetID, this.state);
        if (mask && mask.length > 0) {
          await MIRenderAPI$1.addPeriapicalRadiolucencyMasks(mask);
          this.addAutoRun(() => {
            if (this.state.showPeriapicalRadiolucencyMasks.get() && !this.state.featureFlags.get(FeatureFlag.FeatureFlag_Hide_PerioMasksAndSlices) && !isFDAVersion2Enabled(this.state) && !isFDAUnaidedEnabled(this.state)) {
              imageContainer.showMasks(["periapicalRadiolucency"]);
            } else {
              imageContainer.hideMasks(["periapicalRadiolucency"]);
            }
            MIRender.getView("sliceEditor")?.imageController.updatePictures(assetID);
          });
        }
      });
    }, true);
  }
  async addPBLsWithoutConditions(reportID, assetID, waitForGeneralImageDownloaded) {
    const flatConditions = (await this.state.conditionsState.flatConditions.getAsync(reportID))?.Tooth;
    const teethLandmarksIndexedByCode = await this.state.teethLandmarksIndexedByCode.getAsync(reportID);
    const teeth = await (await this.state.teeth.getAsync(reportID))?.getAsync(assetID);
    if (!teeth || !teethLandmarksIndexedByCode || !flatConditions) return;
    const makePBL = (lowerLandmark, upperLandmark, pblColor) => {
      return {
        start: {
          x: lowerLandmark?.ModelPosition?.X || 0,
          y: lowerLandmark?.ModelPosition?.Y || 0
        },
        end: {
          x: upperLandmark?.ModelPosition?.X || 0,
          y: upperLandmark?.ModelPosition?.Y || 0
        },
        color: pblColor,
        textProps: {
          color: pblColor,
          outlineColor: pblColor === "#D4D4D4" ? "black" : void 0,
          outlineWidth: pblColor === "#D4D4D4" ? 0.4 : void 0
        }
      };
    };
    teeth.forEach(async (_, toothID) => {
      const toothConditions = await (await this.state.conditionsState.conditions.getAsync(reportID))?.Tooth.getAsync(toothID);
      if (!toothConditions) return;
      const perioConditions = await toothConditions?.getAsync(ConditionCode.PeriodontalBoneLoss);
      if (!perioConditions) return;
      const toothLandmarks = teethLandmarksIndexedByCode.get(toothID);
      if (!toothLandmarks) return;
      const lowerLandmarkMesial = toothLandmarks.Image.get(ToothLandmark_Image_Code.ToothLandmark_Image_Code_BoneLevelMesial);
      const upperLandmarkMesial = toothLandmarks.Image.get(ToothLandmark_Image_Code.ToothLandmark_Image_Code_CementoenamelJunctionMesial);
      const lowerLandmarkDistal = toothLandmarks.Image.get(ToothLandmark_Image_Code.ToothLandmark_Image_Code_BoneLevelDistal);
      const upperLandmarkDistal = toothLandmarks.Image.get(ToothLandmark_Image_Code.ToothLandmark_Image_Code_CementoenamelJunctionDistal);
      const lowerLandmarkCentral = toothLandmarks.Image.get(ToothLandmark_Image_Code.ToothLandmark_Image_Code_BoneLevelCentral);
      const upperLandmarkCentral = toothLandmarks.Image.get(ToothLandmark_Image_Code.ToothLandmark_Image_Code_CementoenamelJunctionCentral);
      const isRed = constants.PBL_RED_CODES.some((code) => {
        const conditions = toothConditions.get(code);
        if (!conditions) return false;
        return [...conditions.values()].some((condition) => {
          return condition.Certainty?.ModelDecision === Decision.PositiveDecision;
        });
      });
      const PBLs = [];
      const pblColor = isRed ? constants.PBL_RED_COLOR : constants.PBL_GRAY_COLOR;
      if (lowerLandmarkMesial && upperLandmarkMesial) {
        PBLs.push(makePBL(lowerLandmarkMesial, upperLandmarkMesial, pblColor));
      }
      if (lowerLandmarkDistal && upperLandmarkDistal) {
        PBLs.push(makePBL(lowerLandmarkDistal, upperLandmarkDistal, pblColor));
      }
      if (lowerLandmarkCentral && upperLandmarkCentral) {
        PBLs.push(makePBL(lowerLandmarkCentral, upperLandmarkCentral, pblColor));
      }
      let scale = 1;
      const image = await waitForGeneralImageDownloaded;
      if (image) {
        const texture = image.getImage().texture;
        if (texture instanceof DicomTexture) {
          scale = texture.pixelSpacing?.xMmPerPixel || 1;
        }
      }
      MIRenderAPI$1.setPBLs([{
        PBLs,
        imageID: assetID,
        scale
      }]);
    });
  }
}

const getModeName = (vewName, state) => {
  const activeControl = state.toolbarsState?.[vewName]?.activeControl;
  if (!activeControl) return;
  const layoutMode = state.layoutState.mode;
  if (!layoutMode) return;
  return UI_TOOL_TO_MODE_MAP_MATRIX[activeControl]?.[layoutMode];
};

class SetupFMX extends Setup {
  setup() {
    MIRenderAPI$1.launch();
    MIRenderAPI$1.addMainView(this.isTypewriter());
    this.watchReportChange();
    this.watchTeethChange();
    this.watchFeatureFlags();
  }
  watchStateChange() {
    const onToolbarStateChange = () => MIRenderAPI$1.activateMode("main", getModeName("main", this.state) || "mainMode");
    const onLayoutStateChange = () => MIRenderAPI$1.getLayoutController()?.[this.state.layoutState.mode](this.state.layoutState.id);
    const onPageTypeChange = () => {
      if (this.state.dataFromURL.pageType === "preview") {
        localDisposer?.();
        MIRenderAPI$1.getLayoutController()?.fullScreenMatrix();
        MIRenderAPI$1.activateMode("main", "printMode");
      } else if (!localDisposer) {
        const layoutStateDisposer = autoRun(onLayoutStateChange);
        const toolbarStateDisposer = autoRun(onToolbarStateChange);
        localDisposer = () => {
          toolbarStateDisposer();
          layoutStateDisposer();
          localDisposer = void 0;
        };
      }
    };
    let localDisposer;
    const disposer = this.state.dataFromURL.observe("pageType", onPageTypeChange, true);
    this.addAutoRun(() => {
      if (this.state.toolbarsState.main?.editNumbers && this.state.dataFromURL.pageType !== "preview") {
        MIRenderAPI$1.showTeethNumbers();
      } else {
        MIRenderAPI$1.hideTeethNumbers();
      }
    });
    Setup.disposers.push(() => {
      disposer();
      localDisposer?.();
    });
  }
  async getImageMeta() {
    const report = await this.state.currentReport.getAsync();
    if (!report) return;
    if (report.Specific?.case !== this.reportSpecificCase) return;
    return [...report.Specific.value.ImagesMeta];
  }
  async watchMaskVisibility() {
    const imageMeta = await this.getImageMeta();
    if (!imageMeta) return;
    MIRenderAPI$1.specifyMaskGroups(["perio", "endo", "restorative", "chart", "anatomy"]);
    const isVisible = (maskGroup, flags) => {
      const previewPage = this.state.dataFromURL.pageType === "preview";
      const isVisible2 = this.state.maskFilters[maskGroup] && flags.every((flag) => !this.state.featureFlags.get(flag));
      const isPrintVisible = this.state.currentReport.get()?.PrintSettings?.ShowMasks && isVisible2;
      return previewPage ? isPrintVisible : isVisible2 && !this.state.toolbarsState.main?.editNumbers;
    };
    this.addAutoRun(() => {
      isVisible("perio", [FeatureFlag.FeatureFlag_Hide_ConditionsMasks]) ? MIRenderAPI$1.showMasks(["perio"]) : MIRenderAPI$1.hideMasks(["perio"]);
    });
    this.addAutoRun(() => {
      if (isVisible("perio", [
        FeatureFlag.FeatureFlag_Hide_ConditionsMasks,
        FeatureFlag.FeatureFlag_Hide_PBLRulesAndMeasurements
      ]) && !isFDAVersion2Enabled(this.state)) {
        MIRenderAPI$1.showPBLs();
      } else {
        MIRenderAPI$1.hidePBLs();
      }
    });
    this.addAutoRun(() => {
      isVisible("endo", [FeatureFlag.FeatureFlag_Hide_ConditionsMasks]) ? MIRenderAPI$1.showMasks(["endo"]) : MIRenderAPI$1.hideMasks(["endo"]);
    });
    this.addAutoRun(() => {
      isVisible("restorative", [FeatureFlag.FeatureFlag_Hide_ConditionsMasks]) ? MIRenderAPI$1.showMasks(["restorative"]) : MIRenderAPI$1.hideMasks(["restorative"]);
    });
    this.addAutoRun(() => {
      isVisible("chart", [FeatureFlag.FeatureFlag_Hide_AnatomyMasks]) ? MIRenderAPI$1.showMasks(["chart", "anatomy"]) : MIRenderAPI$1.hideMasks(["chart", "anatomy"]);
    });
    const updatePictures = () => {
      const previewPage = this.state.dataFromURL.pageType === "preview";
      if (previewPage) {
        requestAnimationFrame(() => {
          MIRenderAPI$1.updatePictures();
        });
      }
    };
    let localDisposer;
    const autoRunDisposer = autoRun(() => {
      const previewPage = this.state.dataFromURL.pageType === "preview";
      if (previewPage) {
        localDisposer = this.state.currentReport.get()?.PrintSettings?.observe("ShowMasks", updatePictures);
      } else {
        localDisposer?.();
      }
    });
    Setup.disposers.push(() => {
      autoRunDisposer();
      localDisposer?.();
    });
  }
  async watchViewOptionsChange() {
    const imageMeta = await this.getImageMeta();
    if (!imageMeta) return;
    imageMeta.forEach(async ([assetID, meta]) => {
      const view = MIRender.getView("main");
      const image = (await view.imageController.imageContainers.getAsync(assetID))?.getImage();
      if (!image) return;
      this.addAutoRun(() => {
        const brightness = meta.MedicalImageFeatures.ViewOptions.Brightness;
        const contrast = meta.MedicalImageFeatures.ViewOptions.Contrast;
        view.executeCommand(new ContrastBrightnessCommand(image.brightness, image.contrast, brightness || image.brightness, contrast || image.contrast, image));
      });
      this.addAutoRun(() => {
        const sharpness = meta.MedicalImageFeatures.ViewOptions.Sharpness;
        view?.executeCommand(new SharpnessCommand(image.sharpness, sharpness || image.sharpness, image));
      });
    });
  }
  async addMasks() {
    const report = await this.state.currentReport.getAsync();
    if (!report) return;
    if (report.Specific?.case !== this.reportSpecificCase) return;
    const assetIDs = [...report.Specific.value.ImagesMeta.keys()];
    assetIDs.forEach(async (assetID) => {
      let types = [];
      switch (report.Type) {
        case ReportType.ReportType_Pano_GP:
          types = ["Pano"];
          break;
        case ReportType.ReportType_IOXRay_GP:
          types = ["IOXRay"];
          break;
        case ReportType.ReportType_Pano_Bitewings:
          types = ["IOXRay", "Pano"];
          break;
      }
      await Promise.all([
        this.addConditionMask(assetID),
        ...types.map((type) => {
          this.addMaxFaxMask(assetID, type);
          this.addToothDetectionsMask(assetID, type);
        })
      ]);
      MIRenderAPI$1.updatePictures(assetID);
    });
  }
}

class SetupPano extends SetupFMX {
  constructor() {
    super(...arguments);
    this.reportSpecificCase = "DataPanoGP";
  }
  async setup() {
    super.setup();
    const isTypewriter = this.isTypewriter();
    MIRenderAPI$1.NDFAPI.addNDFView(isTypewriter);
    selectPanoImageInterface(this.state).then((data) => {
      if (!data) return;
      const waitForLoadPano = this.setupMainViewAndWatchChanges(data.imageInterface);
      !isTypewriter && this.addMasks();
      this.watchMaskVisibility();
      this.watchHoveredConditionBoxes();
      this.watchStateChange();
      this.watchColorInversion();
      !isTypewriter && this.addPBLs(data.report.ID, data.imageInterface.ImageID);
      this.setupNDF(data.report, waitForLoadPano);
      this.setFlagForTypewriter();
      this.addTeethNumbers(data.report.ID);
    });
  }
  async getPanoImage() {
    const report = await this.state.currentReport.getAsync();
    if (!report) return;
    if (report.Specific?.case !== this.reportSpecificCase) return;
    const pano = await this.state.panoImage.getAsync(report.Specific.value.PanoStudyAssetID);
    return { pano, report };
  }
  setupMainViewAndWatchChanges(image, usePreview = true) {
    let result;
    this.addAutoRun(() => {
      result = runInShadow(() => MIRenderAPI$1.specifyMainView(
        [[[image]]],
        void 0,
        { [image.ImageID]: { viewportType: "pano" } },
        void 0,
        usePreview
      ));
    });
    return result?.[0]?.waitForLoad || Promise.reject();
  }
}

const JAW = {
  quartile1: [18, 17, 16, 15, 14, 13, 12, 11],
  quartile2: [21, 22, 23, 24, 25, 26, 27, 28],
  quartile3: [31, 32, 33, 34, 35, 36, 37, 38],
  quartile4: [48, 47, 46, 45, 44, 43, 42, 41],
  primaryQuartile1: [55, 54, 53, 52, 51],
  primaryQuartile2: [61, 62, 63, 64, 65],
  primaryQuartile3: [71, 72, 73, 74, 75],
  primaryQuartile4: [85, 84, 83, 82, 81]
};
const UPPER_VIEWER_ORDER = [...JAW.quartile1, ...JAW.quartile2];
const LOWER_VIEWER_ORDER = [...JAW.quartile4, ...JAW.quartile3];
const toPermanentToothNumber = (tooth) => tooth >= 51 ? tooth - 40 : tooth;
const getViewerPosition = (tooth) => {
  const permanent = toPermanentToothNumber(tooth);
  const upperIndex = UPPER_VIEWER_ORDER.indexOf(permanent);
  return upperIndex >= 0 ? upperIndex : LOWER_VIEWER_ORDER.indexOf(permanent);
};
const sortIOXRayImagesByViewerPosition = (images) => {
  const imagesWithPositions = images.map((image) => {
    const positions = image.teethISONumbers.map(getViewerPosition).filter((position) => position >= 0);
    const hasPositions = positions.length > 0;
    return {
      image,
      meanPosition: hasPositions ? positions.reduce((sum, position) => sum + position, 0) / positions.length : Number.POSITIVE_INFINITY,
      leftmostPosition: hasPositions ? Math.min(...positions) : Number.POSITIVE_INFINITY
    };
  });
  return sortBy(imagesWithPositions, ["meanPosition", "leftmostPosition"]).map(
    ({ image }) => image
  );
};

const LOWER_JAW_TEETH_ISO_NUMBERS = [
  ...JAW.quartile3,
  ...JAW.quartile4,
  ...JAW.primaryQuartile3,
  ...JAW.primaryQuartile4
];
const UPPER_JAW_TEETH_ISO_NUMBERS = [
  ...JAW.quartile1,
  ...JAW.quartile2,
  ...JAW.primaryQuartile1,
  ...JAW.primaryQuartile2
];
const TEETH_GROUPS = {
  MOLARS: [18, 17, 16, 28, 27, 26, 48, 47, 46, 38, 37, 36],
  UPPER_LEFT: [18, 17, 16],
  UPPER_RIGHT: [26, 27, 28],
  LOWER_LEFT: [48, 47, 46],
  LOWER_RIGHT: [36, 37, 38],
  UPPER_CENTRAL: [11, 21],
  LOWER_CENTRAL: [31, 41],
  MIDDLE_LEFT: [16, 17, 46, 47],
  MIDDLE_RIGHT: [26, 27, 36, 37]
};
const LEFT_SIDE_QUADRANTS = [
  ...JAW.quartile1,
  // 11-18
  ...JAW.quartile4,
  // 41-48
  ...JAW.primaryQuartile1,
  // 51-55
  ...JAW.primaryQuartile4
  // 81-85
];
const RIGHT_SIDE_QUADRANTS = [
  ...JAW.quartile2,
  // 21-28
  ...JAW.quartile3,
  // 31-38
  ...JAW.primaryQuartile2,
  // 61-65
  ...JAW.primaryQuartile3
  // 71-75
];
const MIDLINE_TEETH = [
  11,
  12,
  21,
  22,
  // permanent upper central/lateral incisors
  41,
  42,
  31,
  32,
  // permanent lower central/lateral incisors
  51,
  52,
  61,
  62,
  // primary upper central/lateral incisors
  81,
  82,
  71,
  72
  // primary lower central/lateral incisors
];
const THRESHOLDS = {
  UPPER_LEFT: 14,
  UPPER_RIGHT: 24,
  MIDDLE_RIGHT: 30,
  LOWER_RIGHT: 34,
  LOWER_LEFT: 44,
  UPPER_JAW_SIDE: 20,
  LOWER_JAW_SIDE: 40
};
const isUpperJaw = (teethNumbers) => teethNumbers.every((tooth) => UPPER_JAW_TEETH_ISO_NUMBERS.includes(tooth));
const isLowerJaw = (teethNumbers) => teethNumbers.every((tooth) => LOWER_JAW_TEETH_ISO_NUMBERS.includes(tooth));
const hasTeethInUpperJaw = (teethNumbers) => teethNumbers.some((tooth) => UPPER_JAW_TEETH_ISO_NUMBERS.includes(tooth));
const hasTeethInLowerJaw = (teethNumbers) => teethNumbers.some((tooth) => LOWER_JAW_TEETH_ISO_NUMBERS.includes(tooth));
const isBitewing = (teethNumbers) => hasTeethInUpperJaw(teethNumbers) && hasTeethInLowerJaw(teethNumbers);
const isCrossesMidline = (teethNumbers) => {
  const leftCount = teethNumbers.filter((t) => LEFT_SIDE_QUADRANTS.includes(t)).length;
  const rightCount = teethNumbers.filter((t) => RIGHT_SIDE_QUADRANTS.includes(t)).length;
  if (leftCount < 2 || rightCount < 2) return false;
  const hasMidlineTeeth = teethNumbers.some((t) => MIDLINE_TEETH.includes(t));
  return hasMidlineTeeth;
};
const hasAnyTeethFrom = (teethNumbers, teethSet) => teethNumbers.some((tooth) => teethSet.includes(tooth));
const calculateAverage = (numbers) => numbers.length ? numbers.reduce((sum, num) => sum + num, 0) / numbers.length : 0;
const isRotatedSideways = (orientationAngle) => Math.abs((orientationAngle ?? 0) % 180) === 90;
const isWideImage = (image) => {
  const width = image?.originalSize?.width;
  const height = image?.originalSize?.height;
  if (width && height) {
    return isRotatedSideways(image.OrientationAngle) ? height > width : width > height;
  }
  const { teethISONumbers } = image;
  return isBitewing(teethISONumbers) || hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.MOLARS);
};
const getTeethForAnalysis = (sortedTeeth, hasUpperTeeth, hasLowerTeeth) => {
  if (hasUpperTeeth && hasLowerTeeth || sortedTeeth.length <= 4) {
    return sortedTeeth;
  }
  const avgAllTeeth = calculateAverage(sortedTeeth);
  if (hasUpperTeeth && !hasLowerTeeth) {
    return avgAllTeeth < THRESHOLDS.UPPER_JAW_SIDE ? sortedTeeth.slice(1) : sortedTeeth.slice(0, -1);
  }
  if (hasLowerTeeth && !hasUpperTeeth) {
    return avgAllTeeth < THRESHOLDS.LOWER_JAW_SIDE ? sortedTeeth.slice(1) : sortedTeeth.slice(0, -1);
  }
  return sortedTeeth;
};
const determineSector = (image) => {
  const { teethISONumbers } = image;
  if (!teethISONumbers.length) {
    return "UpperMiddle";
  }
  const sortedTeeth = teethISONumbers.map(toPermanentToothNumber).sort((a, b) => a - b);
  const hasUpperTeeth = hasTeethInUpperJaw(sortedTeeth);
  const hasLowerTeeth = hasTeethInLowerJaw(sortedTeeth);
  const isBitewingImage = isBitewing(sortedTeeth);
  const teethForAnalysis = getTeethForAnalysis(
    sortedTeeth,
    hasUpperTeeth,
    hasLowerTeeth
  );
  const avgTooth = calculateAverage(teethForAnalysis);
  const isWide = isWideImage(image);
  if (isBitewingImage) {
    return avgTooth < THRESHOLDS.MIDDLE_RIGHT ? "MiddleRight" : "MiddleLeft";
  }
  if (hasUpperTeeth && !hasLowerTeeth) {
    if (avgTooth < THRESHOLDS.UPPER_LEFT && isWide) return "UpperLeft";
    if (avgTooth > THRESHOLDS.UPPER_RIGHT && isWide) return "UpperRight";
    return "UpperMiddle";
  }
  if (hasLowerTeeth && !hasUpperTeeth) {
    if (avgTooth < THRESHOLDS.LOWER_RIGHT && isWide) return "LowerRight";
    if (avgTooth > THRESHOLDS.LOWER_LEFT && isWide) return "LowerLeft";
    return "LowerMiddle";
  }
  return "UpperMiddle";
};
const handleSpecialCases = (image) => {
  const { teethISONumbers } = image;
  const isWide = isWideImage(image);
  if (isBitewing(teethISONumbers)) {
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.MIDDLE_LEFT)) {
      return "MiddleLeft";
    }
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.MIDDLE_RIGHT)) {
      return "MiddleRight";
    }
    return "MiddleLeft";
  }
  if (isCrossesMidline(teethISONumbers)) {
    if (isUpperJaw(teethISONumbers)) {
      return "UpperMiddle";
    }
    if (isLowerJaw(teethISONumbers)) {
      return "LowerMiddle";
    }
  }
  if (isUpperJaw(teethISONumbers)) {
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.UPPER_LEFT) && isWide) {
      return "UpperLeft";
    }
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.UPPER_RIGHT) && isWide) {
      return "UpperRight";
    }
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.UPPER_CENTRAL) && !isWide) {
      return "UpperMiddle";
    }
  }
  if (isLowerJaw(teethISONumbers)) {
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.LOWER_LEFT) && isWide) {
      return "LowerLeft";
    }
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.LOWER_RIGHT) && isWide) {
      return "LowerRight";
    }
    if (hasAnyTeethFrom(teethISONumbers, TEETH_GROUPS.LOWER_CENTRAL) && !isWide) {
      return "LowerMiddle";
    }
  }
  return null;
};
const groupIOXRayImagesByPartition = (IOXRayImages) => {
  const initialGroupedImages = {
    UpperLeft: [],
    UpperMiddle: [],
    UpperRight: [],
    MiddleLeft: [],
    MiddleMiddle: [],
    MiddleRight: [],
    LowerLeft: [],
    LowerMiddle: [],
    LowerRight: []
  };
  if (!IOXRayImages) {
    return initialGroupedImages;
  }
  const groupedImages = IOXRayImages.reduce((grouped, image) => {
    const specialCase = handleSpecialCases(image);
    const sector = specialCase || determineSector(image);
    return {
      ...grouped,
      [sector]: [...grouped[sector], image]
    };
  }, initialGroupedImages);
  return Object.entries(groupedImages).reduce(
    (result, [sector, images]) => ({
      ...result,
      [sector]: sortIOXRayImagesByViewerPosition(images)
    }),
    {}
  );
};

const maxBitewingsGap = 60;
const bitewingsGapRatio = 0.02;
const occupiedWidth = (image) => {
  const { width, height } = image.originalSize;
  return image.OrientationAngle ? Cell.sizeUtil(width, height, image.OrientationAngle * Math.PI / 180).width : width;
};
const buildPanoWingsMatrix = (pano, bitewings, panoProps = {}) => {
  if (!bitewings.length) {
    return {
      matrix: [[[pano]]],
      specialImageProps: { [pano.ImageID]: { ...panoProps } },
      layoutOptions: { sizeMode: "pixels" }
    };
  }
  const bitewingsWidth = bitewings.map(occupiedWidth).reduce((total, width) => total + width, 0);
  const contentGap = Math.min(bitewingsWidth * bitewingsGapRatio, maxBitewingsGap);
  const scale = (contentGap * (bitewings.length - 1) + bitewingsWidth) / pano.originalSize.width;
  return {
    matrix: [[[pano]], [bitewings]],
    specialImageProps: { [pano.ImageID]: { ...panoProps, scale } },
    layoutOptions: { contentGap, sizeMode: "pixels" }
  };
};

const selectTeeth = (reportID, assetID, state) => {
  const teethMap = state.teeth.get(reportID)?.get(assetID);
  const teeth = teethMap && [...teethMap.values()].map((tooth) => tooth.Numeration?.ISO).filter((iso) => iso !== void 0);
  if (!teeth) {
    console.error(`Teeth are not defined for image ${assetID}`);
  }
  return teeth;
};

class SetupPanoWings extends SetupFMX {
  constructor() {
    super(...arguments);
    this.reportSpecificCase = "DataPanoBitewings";
  }
  async setup() {
    super.setup();
    const report = await this.state.currentReport.getAsync();
    if (report?.Specific?.case !== this.reportSpecificCase) return;
    const isTypewriter = this.isTypewriter();
    this.getIOXRayImage(report).then(({ IOXRayImages, pano }) => {
      if (!IOXRayImages || !pano) return;
      this.setupMainViewAndWatchChanges(IOXRayImages, pano.imageInterface, report);
      !isTypewriter && this.addMasks();
      this.watchMaskVisibility();
      this.watchHoveredConditionBoxes();
      this.watchStateChange();
      this.watchColorInversion();
      !isTypewriter && IOXRayImages.forEach((image) => {
        this.addPBLs(report.ID, image.assetID);
      });
      !isTypewriter && this.addPBLs(report.ID, pano.imageInterface.ImageID);
      this.setFlagForTypewriter();
      this.addTeethNumbers(report.ID);
    });
  }
  async getIOXRayImage(report) {
    const IOXRayImages = await selectIOXRayImage(this.state, report);
    const pano = await selectPanoImageInterface(this.state);
    return { IOXRayImages, pano };
  }
  setupMainViewAndWatchChanges(image, panoImageInterface, report, usePreview = true) {
    this.addAutoRun(() => {
      const imageInterfaces = IOXRayImagesToIOXRayImageInterfaces(image, report);
      const imageInterfacesWithTeeth = imageInterfaces.map((img) => ({ ...img, teethISONumbers: selectTeeth(report.ID, img.ImageID, this.state) })).filter((item) => item.teethISONumbers !== void 0);
      const groupedImages = groupIOXRayImagesByPartition(imageInterfacesWithTeeth);
      const bitewings = [
        ...groupedImages["MiddleLeft"],
        ...groupedImages["MiddleRight"]
      ];
      const { matrix, specialImageProps, layoutOptions } = buildPanoWingsMatrix(
        panoImageInterface,
        bitewings,
        { viewportType: "pano" }
      );
      runInShadow(() => MIRenderAPI$1.specifyMainView(
        matrix,
        getViewOptions(report?.MedicalImageFeatures?.ViewOptions),
        specialImageProps,
        layoutOptions,
        usePreview
      ));
    });
  }
}

class SetupIOXRay extends SetupFMX {
  constructor() {
    super(...arguments);
    this.reportSpecificCase = "DataIOXRayGP";
  }
  async setup() {
    super.setup();
    const isTypewriter = this.isTypewriter();
    const report = await this.state.currentReport.getAsync();
    if (report?.Specific?.case !== this.reportSpecificCase) return;
    selectIOXRayImage(this.state, report).then((IOXrayImages) => {
      if (!IOXrayImages) return;
      this.setupMainViewAndWatchChanges(IOXrayImages, report);
      !isTypewriter && this.addMasks();
      this.watchMaskVisibility();
      this.watchHoveredConditionBoxes();
      this.watchStateChange();
      this.watchColorInversion();
      !isTypewriter && IOXrayImages.forEach((image) => {
        this.addPBLs(report.ID, image.assetID);
      });
      this.setFlagForTypewriter();
      this.addTeethNumbers(report.ID);
    });
  }
  setupMainViewAndWatchChanges(image, report, usePreview = true) {
    this.addAutoRun(() => {
      const imageInterfaces = IOXRayImagesToIOXRayImageInterfaces(image, report);
      const imageInterfacesWithTeeth = imageInterfaces.map((img) => ({ ...img, teethISONumbers: selectTeeth(report.ID, img.ImageID, this.state) })).filter((item) => item.teethISONumbers !== void 0);
      const groupedImages = groupIOXRayImagesByPartition(imageInterfacesWithTeeth);
      MIRenderAPI$1.specifyMainView(
        [
          [
            groupedImages.UpperLeft,
            groupedImages.UpperMiddle,
            groupedImages.UpperRight
          ],
          [
            groupedImages.MiddleLeft,
            groupedImages.MiddleMiddle,
            groupedImages.MiddleRight
          ],
          [
            groupedImages.LowerLeft,
            groupedImages.LowerMiddle,
            groupedImages.LowerRight
          ]
        ],
        getViewOptions(report?.MedicalImageFeatures?.ViewOptions),
        void 0,
        void 0,
        usePreview
      );
    });
  }
}

var __create$5 = Object.create;
var __defProp$5 = Object.defineProperty;
var __getOwnPropDesc$5 = Object.getOwnPropertyDescriptor;
var __knownSymbol$5 = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError$5 = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp$5 = (obj, key, value) => key in obj ? __defProp$5(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart$5 = (base) => [, , , __create$5(base?.[__knownSymbol$5("metadata")] ?? null)];
var __decoratorStrings$5 = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn$5 = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError$5("Function expected") : fn;
var __decoratorContext$5 = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings$5[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError$5("Already initialized") : fns.push(__expectFn$5(fn || null)) });
var __decoratorMetadata$5 = (array, target) => __defNormalProp$5(target, __knownSymbol$5("metadata"), array[3]);
var __runInitializers$5 = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement$5 = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings$5[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc$5(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext$5(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn$5(it) && (desc[key] = it );
  }
  return desc && __defProp$5(target, name, desc), target;
};
var _onReportAdded_dec, _onSetTeethOne_dec, _onSetTeeth_dec, _onAddTeeth_dec, _closeModal_dec, _openModal_dec$1, _a, _init$5;
const buildDownloadFileName = (patientName, descriptor) => [patientName, descriptor].filter(Boolean).join(" ").replace(/\s+/g, "_") || void 0;
class ActionListeners extends (_a = EventDispatcher, _openModal_dec$1 = [action], _closeModal_dec = [action], _onAddTeeth_dec = [action], _onSetTeeth_dec = [action], _onSetTeethOne_dec = [action], _onReportAdded_dec = [action], _a) {
  constructor(state, eventsList) {
    super(eventsList);
    __runInitializers$5(_init$5, 5, this);
    this.disposers = [];
    this.state = state;
  }
  listen() {
    this.disposers = [
      this.addEventListener("assets/addMany", this.onAssetsAdded.bind(this)),
      this.addEventListener("assets/setMany", this.onAssetsAdded.bind(this)),
      this.addEventListener("assets/addOne", this.onOneAssetsAdded.bind(this)),
      this.addEventListener("assets/deleteAsset/fulfilled", this.onOneAssetsDeleted.bind(this)),
      this.addEventListener("tooth/addMany", this.onAddTeeth.bind(this)),
      this.addEventListener("tooth/setMany", this.onSetTeeth.bind(this)),
      this.addEventListener("tooth/setNewestMany", this.onSetTeeth.bind(this)),
      this.addEventListener("tooth/setNewestOne", this.onSetTeethOne.bind(this)),
      this.addEventListener("tooth/setHoveredToothID", this.setHoveredToothID.bind(this)),
      this.addEventListener("reports/setCurrentReport", this.onReportAdded.bind(this)),
      this.addEventListener("reports/updateCurrentReport", this.onReportUpdated.bind(this)),
      this.addEventListener("reports/setNewestOne", this.onSetNewestOneReport.bind(this)),
      this.addEventListener("reports/resetReportViewSettings", this.onResetReportViewSettings.bind(this)),
      this.addEventListener("condition/setMany", this.onAddConditions.bind(this)),
      this.addEventListener("condition/setNewestMany", this.onAddConditions.bind(this)),
      this.addEventListener("condition/setHoveredConditionIDs", this.setHoveredConditionIDs.bind(this)),
      this.addEventListener("condition/setHoveredNonDentalConditionIDs", this.setHoveredNonDentalConditionIDs.bind(this)),
      this.addEventListener("toothLandmarks/addMany", this.onAddTeethLandmarks.bind(this)),
      this.addEventListener("maskFilters/toggleFilter", this.handleMaskFilterToggle.bind(this)),
      this.addEventListener("organization/setNewestOne", this.onSetOrganizationOne.bind(this)),
      this.addEventListener("organization/setNewestMany", this.onSetOrganizationMany.bind(this)),
      this.addEventListener("organization/setSharedPatientSettings", this.onSetSharedPatientSettings.bind(this)),
      this.addEventListener("organization/setCurrentOrganizationID/fulfilled", this.onSetCurrentOrganizationID.bind(this)),
      this.addEventListener("toolbarMIR/initToolbarMIR", this.initToolbar.bind(this)),
      this.addEventListener("toolbarMIR/setToolbarState", this.setToolbarState.bind(this)),
      this.addEventListener("toolbarMIR/deleteToolbarMIR", this.deleteToolbar.bind(this)),
      this.addEventListener("toolbarMIR/activateTool", this.activateTool.bind(this)),
      this.addEventListener("toolbarMIR/hideMarks", this.toggleMarks.bind(this)),
      this.addEventListener("toolbarMIR/statelessAction", this.handleStatelessAction.bind(this)),
      this.addEventListener("reports/setFocusedMetaImageID", this.setFocusedAssetID.bind(this)),
      this.addEventListener("reports/setActiveToothID", this.setCropId.bind(this)),
      this.addEventListener("reports/setShowPeriapicalRadiolucencyMasks", this.onShowPeriapicalRadiolucencyMasks.bind(this)),
      this.addEventListener("modal/openModal", this.openModal.bind(this)),
      this.addEventListener("modal/closeModal", this.closeModal.bind(this)),
      this.addEventListener("patient/setCurrentPatient", this.setCurrentPatient.bind(this)),
      this.addEventListener("toothNumberEdit/addPendingChange", this.addTeethNumberPendingChange.bind(this)),
      this.addEventListener("toothNumberEdit/removePendingChange", this.removeTeethNumberPendingChange.bind(this)),
      this.addEventListener("toothNumberEdit/addPendingRemove", this.addTeethNumberPendingRemove.bind(this)),
      this.addEventListener("toothNumberEdit/resetState", this.resetTeethNumberPendingChange.bind(this))
    ].filter((x) => x !== void 0);
  }
  dispose() {
    this.disposers.forEach((disposer) => disposer());
  }
  openModal(event) {
    if (event.payload.modalID === "MIRenderModal") {
      this.state.openModal(true);
    }
  }
  closeModal(event) {
    if (event.payload === "MIRenderModal") {
      this.state.openModal(false);
    }
  }
  setFocusedAssetID(event) {
    if (event.payload) {
      this.state.setMode("focus", event.payload);
    } else {
      this.state.setMode("matrix");
    }
  }
  setCropId(event) {
    if (event.payload) {
      this.state.setMode("crop", event.payload);
    } else {
      this.state.setMode("matrix");
    }
  }
  onResetReportViewSettings() {
    this.state.setMode("matrix");
  }
  handleMaskFilterToggle(event) {
    this.state.toggleMaskFilters(event.payload);
  }
  onSetOrganizationMany(event) {
    this.state.setOrganization(event.payload);
  }
  onSetOrganizationOne(event) {
    this.state.setOrganization([event.payload]);
  }
  onSetSharedPatientSettings(event) {
    this.state.setFeatureFlags(event.payload);
  }
  onSetCurrentOrganizationID(event) {
    this.state.setCurrentOrganizationID(event.payload);
  }
  onAssetsAdded(event) {
    if (!event.payload.length) return;
    this.state.addAssets(event.payload);
  }
  onOneAssetsAdded(event) {
    this.state.addAssets([event.payload]);
  }
  onOneAssetsDeleted(event) {
    this.state.deleteAsset(event.payload);
  }
  onAddTeethLandmarks(event) {
    this.state.addTeethLandmarks(event.payload);
  }
  onAddTeeth(event) {
    this.state.addDisplaySlices(event.payload);
    this.state.setTeethAnatomy(event.payload);
    this.state.setTeeth(event.payload);
  }
  onSetTeeth(event) {
    this.state.setDisplaySlices(event.payload);
    this.state.setTeethAnatomy(event.payload);
    this.state.setTeeth(event.payload);
  }
  onSetTeethOne(event) {
    this.state.setDisplaySlices([event.payload]);
    this.state.setTeethAnatomy([event.payload]);
    this.state.setTeeth([event.payload]);
  }
  onReportAdded(event) {
    this.state.setCurrentReport(event.payload);
    this.state.maxFaxAnatomyState.setMaxFaxAnatomy(event.payload);
  }
  onReportUpdated(event) {
    this.state.setCurrentReport(event.payload);
  }
  onSetNewestOneReport(event) {
    if (this.state.currentReport.get()?.ID === event.payload.ID) {
      this.state.setCurrentReport(event.payload);
    }
  }
  onShowPeriapicalRadiolucencyMasks(event) {
    this.state.setShowPeriapicalRadiolucencyMasks(event.payload);
  }
  onAddConditions(event) {
    this.state.setConditions(event.payload);
  }
  setHoveredConditionIDs(event) {
    this.state.setHoveredConditions(event.payload);
  }
  setHoveredNonDentalConditionIDs(event) {
    this.state.setHoveredNonDentalConditions(event.payload);
  }
  setHoveredToothID(event) {
    this.state.setHoveredToothID(event.payload);
  }
  initToolbar(event) {
    this.state.setToolbar(event.payload.toolbarID, event.payload.initialState);
  }
  setToolbarState(event) {
    this.state.setToolbar(event.payload.toolbarID, event.payload.state);
  }
  deleteToolbar(event) {
    this.state.setToolbar(event.payload);
  }
  activateTool(event) {
    this.state.activateTool(event.payload.toolbarID, event.payload.control, event.payload.value);
  }
  toggleMarks(event) {
    this.state.toggleMarks(event.payload.toolbarID, event.payload.value);
  }
  setCurrentPatient(event) {
    this.state.setCurrentPatient(event.payload);
  }
  addTeethNumberPendingChange(event) {
    this.state.addTeethNumberPendingChange(event.payload);
  }
  removeTeethNumberPendingChange(event) {
    this.state.removeTeethNumberPendingChange(event.payload);
  }
  addTeethNumberPendingRemove(event) {
    this.state.addTeethNumberPendingRemove(event.payload);
  }
  resetTeethNumberPendingChange() {
    this.state.resetTeethNumberPendingChange();
  }
  async handleStatelessAction(event) {
    switch (event.payload.toolName) {
      case "reset":
        if (this.onReset && this.onReset()) {
          event.payload.action?.();
        }
        break;
      case "download":
        if (event.payload.toolbarID === "sliceEditor") {
          const slicePatient = this.state.currentPatient.get();
          const slicePatientName = slicePatient ? `${slicePatient.PersonalData?.LastName} ${slicePatient.PersonalData?.FirstName}` : void 0;
          this.onDownloadSlice?.(buildDownloadFileName(slicePatientName, event.payload.fileName));
          event.payload.action?.();
          break;
        }
        if (!this.onDownload) {
          return;
        }
        const report = await this.state.currentReport.getAsync();
        if (!report) return;
        const reportID = report.ID;
        const getPatientName = () => {
          const patient = this.state.currentPatient.get();
          if (!patient) return;
          return `${patient.PersonalData?.LastName} ${patient.PersonalData?.FirstName}`;
        };
        let generalAssetID;
        let splitAssetID;
        let generalDescriptor;
        if (report.Type === ReportType.ReportType_CBCT_GP) {
          generalAssetID = (await this.state.PanoReformatGeneral.getAsync(reportID))?.assetID;
          splitAssetID = (await this.state.PanoReformatSplit.getAsync(reportID))?.assetID;
          generalDescriptor = "Pano 3D";
        } else if (report.Type === ReportType.ReportType_Pano_GP || report.Type === ReportType.ReportType_Pano_Bitewings) {
          generalAssetID = report.Specific.value.PanoStudyAssetID;
          generalDescriptor = "Pano 2D";
        }
        const panoPatientName = getPatientName();
        generalAssetID && this.onDownload(generalAssetID, buildDownloadFileName(panoPatientName, generalDescriptor));
        splitAssetID && this.onDownload(splitAssetID, buildDownloadFileName(panoPatientName, `${generalDescriptor} split`));
        event.payload.action?.();
        break;
    }
  }
}
_init$5 = __decoratorStart$5(_a);
__decorateElement$5(_init$5, 1, "openModal", _openModal_dec$1, ActionListeners);
__decorateElement$5(_init$5, 1, "closeModal", _closeModal_dec, ActionListeners);
__decorateElement$5(_init$5, 1, "onAddTeeth", _onAddTeeth_dec, ActionListeners);
__decorateElement$5(_init$5, 1, "onSetTeeth", _onSetTeeth_dec, ActionListeners);
__decorateElement$5(_init$5, 1, "onSetTeethOne", _onSetTeethOne_dec, ActionListeners);
__decorateElement$5(_init$5, 1, "onReportAdded", _onReportAdded_dec, ActionListeners);
__decoratorMetadata$5(_init$5, ActionListeners);

const isCBCTGPToothSlice = (slice) => {
  return slice.type === AssetType.AssetType_Report_Generated_CBCT_GP_ToothSlice;
};
const isCBCTToothPathologySlice = (slice) => {
  return slice.type === AssetType.AssetType_Report_Generated_CBCT_ToothPathologySlice;
};
const isCBCTToothPathologyCariesSlice = (slice) => {
  return isCBCTToothPathologySlice(slice) && slice.pathology === AssetContent_Generated_CBCT_ToothPathologySlice_PathologyType.Caries;
};
const isCBCTToothPathologyPeriapicalLesionSlice = (slice) => {
  return isCBCTToothPathologySlice(slice) && slice.pathology === AssetContent_Generated_CBCT_ToothPathologySlice_PathologyType.PeriapicalLesion;
};
const isUserUploadedToothSlice = (slice) => {
  return slice.type === AssetType.AssetType_Report_Uploaded_ToothSlice;
};
const isUserUploadedMPRPlane = (slice) => {
  return slice.type === AssetType.AssetType_Report_Uploaded_MPRPlane;
};
const toggledToolNames = [
  "ruler",
  "arrow",
  "angle",
  "eraser",
  "view",
  "sharpening",
  "brightness",
  "move",
  "zoom",
  "sharpness",
  "editMask"
];

const getImageData = (file, key) => {
  const result = {
    imageID: file.ID,
    [key]: file,
    size: file.Metadata.case === "Image" ? {
      width: Number(file.Metadata.value.Width),
      height: Number(file.Metadata.value.Height)
    } : void 0
  };
  return result;
};
const getBaseAssetData = (asset) => ({
  assetID: asset.ID,
  assetKind: asset.Kind.case,
  contentKind: asset.Kind.value.Content.case
});
const extendMedicalImageFeatures = (cbctGPToothSlice) => {
  const viewOptions = cbctGPToothSlice.medicalImageFeatures?.ViewOptions;
  const { WindowLevel, WindowWidth } = cbctGPToothSlice;
  if (viewOptions && viewOptions.WindowLevel && viewOptions.WindowWidth && typeof viewOptions.Brightness !== "number" && typeof viewOptions.Contrast !== "number") {
    viewOptions.Brightness = windowCenterToBrightness(viewOptions.WindowLevel, WindowWidth, WindowLevel);
    viewOptions.Contrast = windowWidthToContrast(viewOptions.WindowWidth, WindowWidth);
  }
  return cbctGPToothSlice;
};
const windowCenterToBrightness = (windowCenter, windowWidthBase, windowCenterBase) => {
  return (windowCenterBase - windowCenter) / windowWidthBase;
};
const windowWidthToContrast = (windowWidth, windowWidthBase) => {
  if (windowWidth < windowWidthBase) {
    return (windowWidthBase - windowWidth) / windowWidthBase;
  } else {
    return (windowWidthBase - windowWidth) / windowWidth;
  }
};
const getGeneratedPanoImage = (asset) => asset.Kind.value.Content.value.PanoramaImage ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.PanoramaImage, "image"),
  previewID: asset.Kind.value.Content.value.Derived?.Preview?.ID,
  type: asset.Type,
  StudyID: asset.Kind.value.StudyID
} : void 0;
const getGeneratedIOXRayImage = (asset) => asset.Kind.value.Belonging && asset.Kind.value.Content.value.Image ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.Image, "image"),
  previewID: asset.Kind.value.Content.value.Derived?.Preview?.ID,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  type: asset.Type,
  Belonging: asset.Kind.value.Belonging
} : void 0;
const getStudyIOXRayImage = (asset) => asset.Kind.value.Content.value.Image ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.Image, "image"),
  previewID: asset.Kind.value.Content.value.Derived?.Preview?.ID,
  type: asset.Type
} : void 0;
const getCBCTGPToothSlice = (asset) => asset.Kind.value.Belonging && asset.Kind.value.Content.value.Slice ? extendMedicalImageFeatures({
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.Slice, "toothSlice"),
  toothID: asset.Kind.value.Belonging.ToothID,
  orientation: asset.Kind.value.Content.value.Orientation,
  order: asset.Kind.value.Content.value.Metadata.case === "Regular" && asset.Kind.value.Content.value.Metadata.value.Order,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  CreatedAt: asset.Created?.At?.toDate().getTime() ?? 0,
  type: asset.Type,
  Scale: asset.Kind.value.Content.value.Scale,
  WindowWidth: asset.Kind.value.Content.value.WindowWidth,
  WindowLevel: asset.Kind.value.Content.value.WindowLevel
}) : void 0;
const getCBCTToothPathologySlice = (asset) => asset.Kind.value.Belonging && asset.Kind.value.Content.value.Slice ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.Slice, "ToothPathologySlice"),
  toothID: asset.Kind.value.Belonging.ToothID,
  orientation: asset.Kind.value.Content.value.Orientation,
  masks: asset.Kind.value.Content.value.Mask?.Points,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  pathology: asset.Kind.value.Content.value.Pathology,
  Scale: asset.Kind.value.Content.value.Scale,
  type: asset.Type
} : void 0;
const getUserUploadedToothSlice = (asset) => asset.Kind.value.Belonging && asset.Kind.value.Content.value.Slice ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.Slice, "userUploadedToothSlice"),
  toothID: asset.Kind.value.Belonging.ToothID,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  CreatedAt: asset.Created?.At?.toDate().getTime() ?? 0,
  type: asset.Type
} : void 0;
const getUserUploadedMPRPlane = (asset) => asset.Kind.value.Belonging && asset.Kind.value.Content.value.MPRPlane ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.MPRPlane, "userUploadedMPRPlane"),
  toothID: asset.Kind.value.Belonging.ToothID,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  CreatedAt: asset.Created?.At?.toDate().getTime() ?? 0,
  type: asset.Type
} : void 0;
const getCBCTGPPanoramaReformatGeneral = (asset) => asset.Kind.value.Content.value.PanoramaSingle && asset.Kind.value.Belonging ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.PanoramaSingle, "panoramaSingle"),
  previewID: asset.Kind.value.Content.value.Derived?.Preview?.ID,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  type: asset.Type,
  Belonging: asset.Kind.value.Belonging
} : void 0;
const getCBCTGPPanoramaReformatSplit = (asset) => asset.Kind.value.Content.value.PanoramaSplit && asset.Kind.value.Belonging ? {
  ...getBaseAssetData(asset),
  ...getImageData(asset.Kind.value.Content.value.PanoramaSplit, "panoramaSingle"),
  previewID: asset.Kind.value.Content.value.Derived?.Preview?.ID,
  medicalImageFeatures: asset.Kind.value.MedicalImageFeatures,
  type: asset.Type,
  Belonging: asset.Kind.value.Belonging
} : void 0;
const handleAsset = (asset, state) => {
  if (!asset.Kind.value) return void 0;
  const kind = asset.Kind.case;
  const content = asset.Kind.value.Content.case;
  if (kind === "GeneratedReport") {
    switch (content) {
      case "IOXRayImage": {
        const image = getGeneratedIOXRayImage(asset);
        if (image) {
          asset.Deleted?.Deleted ? state.IOXRayImages.delete(image.assetID) : singleMap(image.assetID, image, state.IOXRayImages);
        }
        return image;
      }
      case "CBCTGPToothSlice": {
        const slice = getCBCTGPToothSlice(asset);
        if (slice) {
          if (asset.Deleted?.Deleted) {
            state.GeneratedReportSlices.get(slice.toothID)?.get(slice.orientation)?.delete(slice.assetID);
            state.FlatSlices.delete(slice.assetID);
          } else {
            tripleMap(slice.toothID, slice.orientation, slice.assetID, slice, state.GeneratedReportSlices);
            singleMap(slice.assetID, slice, state.FlatSlices);
          }
        }
        return slice;
      }
      case "CBCTToothPathologySlice": {
        const slice = getCBCTToothPathologySlice(asset);
        if (slice) {
          if (asset.Deleted?.Deleted) {
            state.CBCTToothPathologySlices.get(slice.toothID)?.get(slice.pathology)?.get(slice.orientation)?.delete(slice.assetID);
            state.FlatSlices.delete(slice.assetID);
          } else {
            quadrupleMap(slice.toothID, slice.pathology, slice.orientation, slice.assetID, slice, state.CBCTToothPathologySlices);
            singleMap(slice.assetID, slice, state.FlatSlices);
          }
        }
        return slice;
      }
      case "CBCTGPPanoramaReformatGeneral": {
        const panorama = getCBCTGPPanoramaReformatGeneral(asset);
        if (panorama) {
          asset.Deleted?.Deleted ? state.PanoReformatGeneral.delete(panorama.Belonging.ReportID) : singleMap(panorama.Belonging.ReportID, panorama, state.PanoReformatGeneral);
        }
        return panorama;
      }
      case "CBCTGPPanoramaReformatSplit": {
        const panorama = getCBCTGPPanoramaReformatSplit(asset);
        if (panorama) {
          asset.Deleted?.Deleted ? state.PanoReformatSplit.delete(panorama.Belonging.ReportID) : singleMap(panorama.Belonging.ReportID, panorama, state.PanoReformatSplit);
        }
        return panorama;
      }
    }
  } else if (kind === "Study") {
    switch (content) {
      case "IntraoralXRay": {
        const image = getStudyIOXRayImage(asset);
        if (image) {
          asset.Deleted?.Deleted ? state.IOXRayImages.delete(image.assetID) : singleMap(image.assetID, image, state.IOXRayImages);
        }
        return image;
      }
      case "PanoramicXRay": {
        const image = getGeneratedPanoImage(asset);
        if (image) {
          asset.Deleted?.Deleted ? state.panoImage.delete(image.assetID) : singleMap(image.assetID, image, state.panoImage);
        }
      }
    }
  } else if (kind === "Report") {
    switch (content) {
      case "UserUploadedToothSlice": {
        const slice = getUserUploadedToothSlice(asset);
        if (slice) {
          if (asset.Deleted?.Deleted) {
            state.UserUploadedSlices.get(slice.toothID)?.delete(slice.assetID);
            state.FlatSlices.delete(slice.assetID);
          } else {
            doubleMap(slice.toothID, slice.assetID, slice, state.UserUploadedSlices);
            singleMap(slice.assetID, slice, state.FlatSlices);
          }
        }
        return slice;
      }
      case "UserUploadedMPRPlane": {
        const slice = getUserUploadedMPRPlane(asset);
        if (slice) {
          if (asset.Deleted?.Deleted) {
            state.UserUploadedMPRPlanes.get(slice.toothID)?.delete(slice.assetID);
            state.FlatSlices.delete(slice.assetID);
          } else {
            doubleMap(slice.toothID, slice.assetID, slice, state.UserUploadedMPRPlanes);
            singleMap(slice.assetID, slice, state.FlatSlices);
          }
        }
        return slice;
      }
    }
  }
  return void 0;
};
const makeLocalizationPropsGroup = () => {
  return {
    perio: arx.map(),
    restorative: arx.map(),
    endo: arx.map(),
    chart: arx.map(),
    enamel: arx.map(),
    periapicalRadiolucency: arx.map()
  };
};
const isToggledTool = (control) => [...toggledToolNames].includes(control);
const quadrupleMap = (key1, key2, key3, key4, value, map) => {
  if (typeof value === "undefined" || value === null) return false;
  const map1 = map.get(key1);
  if (map1 instanceof Map) {
    return tripleMap(key2, key3, key4, value, map1);
  } else {
    map.set(key1, arx.map([[key2, arx.map([[key3, arx.map([[key4, value]])]])]]));
  }
  return true;
};

var __create$4 = Object.create;
var __defProp$4 = Object.defineProperty;
var __getOwnPropDesc$4 = Object.getOwnPropertyDescriptor;
var __knownSymbol$4 = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError$4 = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp$4 = (obj, key, value) => key in obj ? __defProp$4(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart$4 = (base) => [, , , __create$4(null)];
var __decoratorStrings$4 = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn$4 = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError$4("Function expected") : fn;
var __decoratorContext$4 = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings$4[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError$4("Already initialized") : fns.push(__expectFn$4(fn || null)) });
var __decoratorMetadata$4 = (array, target) => __defNormalProp$4(target, __knownSymbol$4("metadata"), array[3]);
var __runInitializers$4 = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement$4 = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings$4[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc$4(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext$4(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn$4(it) && (desc[key] = it );
  }
  return desc && __defProp$4(target, name, desc), target;
};
var _setMaxFaxAnatomy_dec, _init$4;
_setMaxFaxAnatomy_dec = [action];
class MaxFaxAnatomyState {
  constructor() {
    __runInitializers$4(_init$4, 5, this);
    this.maxFaxAnatomy = arx.map();
  }
  reset() {
    this.maxFaxAnatomy = arx.map();
  }
  setMaxFaxAnatomy(report) {
    if (!report || !report.Specific.value || !report.ID) return;
    const reportID = report.ID;
    const MaxFaxAnatomyLocalizations = report.Specific.value.MaxFaxAnatomyLocalizations;
    if (!MaxFaxAnatomyLocalizations) return;
    if (!this.maxFaxAnatomy.has(reportID)) {
      this.maxFaxAnatomy.set(reportID, {
        IOXRay: arx.map(),
        Pano: arx.map(),
        CBCT: arx.map()
      });
    }
    const reportMaxFaxAnatomy = this.maxFaxAnatomy.get(reportID);
    MaxFaxAnatomyLocalizations.forEach((anatomy) => {
      const type = anatomy.Type.case;
      if (!anatomy.Localization || !type) {
        return;
      }
      const filteredMask = anatomy.Localization.PolygonalMask?.filter((mask) => mask.Points.length);
      if (!filteredMask.length) {
        return;
      }
      anatomy.Localization = anatomy.Localization.clone();
      anatomy.Localization.PolygonalMask = filteredMask;
      tripleMap(anatomy.Localization.TargetAssetID, anatomy.Type.value, anatomy.Localization.ID, {
        maxFaxAnatomyID: anatomy.Localization.ID,
        TargetAssetID: anatomy.Localization.TargetAssetID,
        localizationType: anatomy.Type,
        Localization: anatomy.Localization
      }, reportMaxFaxAnatomy[type]);
    });
  }
  async getMaxFaxByType(assetID, type, reportID) {
    switch (type) {
      case "CBCT":
        return this.getMaxFaxCBCT(assetID, reportID);
      case "IOXRay":
        return this.getMaxFaxIOXRay(assetID, reportID);
      case "Pano":
        return this.getMaxFaxPano(assetID, reportID);
    }
  }
  async getMaxFaxCBCT(assetID, reportID) {
    const reportMaxFaxAnatomy = await this.maxFaxAnatomy.getAsync(reportID);
    if (!reportMaxFaxAnatomy) return;
    const maxFaxCBCT = await reportMaxFaxAnatomy.CBCT.getAsync(assetID);
    if (!maxFaxCBCT) return;
    return {
      bone: {
        maxilla: maxFaxCBCT.getAsync(CBCT_Anatomy.CBCT_Anatomy_Maxilla),
        mandible: maxFaxCBCT.getAsync(CBCT_Anatomy.CBCT_Anatomy_Mandible)
      },
      canal: maxFaxCBCT.getAsync(CBCT_Anatomy.CBCT_Anatomy_MandibularCanal)
    };
  }
  async getMaxFaxIOXRay(assetID, reportID) {
    const reportMaxFaxAnatomy = await this.maxFaxAnatomy.getAsync(reportID);
    if (!reportMaxFaxAnatomy) return;
    const maxFaxIOXRay = await reportMaxFaxAnatomy.IOXRay.getAsync(assetID);
    if (!maxFaxIOXRay) return;
    return {
      bone: {
        vestibularBone: maxFaxIOXRay.getAsync(IOXRay_Anatomy.IOXRay_Anatomy_VestibularBone),
        oralBone: maxFaxIOXRay.getAsync(IOXRay_Anatomy.IOXRay_Anatomy_OralBone) || maxFaxIOXRay.getAsync(IOXRay_Anatomy.IOXRay_Anatomy_AlveolarBone)
      },
      canal: maxFaxIOXRay.getAsync(IOXRay_Anatomy.IOXRay_Anatomy_MandibularCanal)
    };
  }
  async getMaxFaxPano(assetID, reportID) {
    const reportMaxFaxAnatomy = await this.maxFaxAnatomy.getAsync(reportID);
    if (!reportMaxFaxAnatomy) return;
    const maxFaxPano = await reportMaxFaxAnatomy.Pano.getAsync(assetID);
    if (!maxFaxPano) return;
    return {
      bone: {
        maxilla: maxFaxPano.getAsync(Pano_Anatomy.Pano_Anatomy_Maxilla),
        mandible: maxFaxPano.getAsync(Pano_Anatomy.Pano_Anatomy_Mandible)
      },
      canal: maxFaxPano.getAsync(Pano_Anatomy.Pano_Anatomy_MandibularCanal)
    };
  }
}
_init$4 = __decoratorStart$4();
__decorateElement$4(_init$4, 1, "setMaxFaxAnatomy", _setMaxFaxAnatomy_dec, MaxFaxAnatomyState);
__decoratorMetadata$4(_init$4, MaxFaxAnatomyState);

class ConditionsState {
  constructor() {
    this.localizations = arx.map();
    this.localizationGrouped = arx.map();
    this.pblConditions = arx.map();
    this.conditions = arx.map();
    this.flatConditions = arx.map();
  }
  reset() {
    this.localizations = arx.map();
    this.localizationGrouped = arx.map();
    this.pblConditions = arx.map();
    this.conditions = arx.map();
    this.flatConditions = arx.map();
  }
  makeLocalizationProps(condition, localization) {
    return {
      conditionID: condition.ID,
      localizationID: localization.ID,
      ParentID: condition.ParentID,
      ReportID: condition.ReportID,
      TargetAssetID: localization.TargetAssetID,
      Localization: localization,
      Code: condition.Code,
      ParentCode: condition.ParentCode,
      Certainty: condition.Certainty,
      Attribution: condition.Attribution.value,
      Revision: condition.Revision,
      equals: (other) => {
        return condition.ID === other.conditionID && condition.Revision?.Number === other.Revision?.Number && localization.equals(other.Localization);
      }
    };
  }
  makeConditionProps(condition) {
    return {
      conditionID: condition.ID,
      ParentID: condition.ParentID,
      ReportID: condition.ReportID,
      ChildrenIDs: /* @__PURE__ */ new Set(),
      Localization: condition.Localizations,
      Code: condition.Code,
      ParentCode: condition.ParentCode,
      Certainty: condition.Certainty,
      Attribution: condition.Attribution.value,
      Revision: condition.Revision,
      equals: (other) => {
        return condition.ID === other.conditionID && condition.Revision?.Number === other.Revision?.Number;
      }
    };
  }
  async setConditions(conditions, currentReportID) {
    runInAction(() => {
      let conditionsForReport = this.conditions.get(currentReportID);
      if (!conditionsForReport) {
        conditionsForReport = {
          Tooth: arx.map(),
          MaxFax: arx.map(),
          undefined: arx.map()
        };
        this.conditions.set(currentReportID, conditionsForReport);
      }
      let flatConditionsForReport = this.flatConditions.get(currentReportID);
      if (!flatConditionsForReport) {
        flatConditionsForReport = {
          Tooth: arx.map(),
          MaxFax: arx.map(),
          undefined: arx.map()
        };
        this.flatConditions.set(currentReportID, flatConditionsForReport);
      }
      conditions.forEach((condition) => {
        if (condition.ReportID !== currentReportID || condition.Deleted?.Deleted) {
          return;
        }
        condition.Localizations.forEach((localization) => {
          const localizationProps = this.makeLocalizationProps(condition, localization);
          quadrupleMap(condition.ReportID, localization.TargetAssetID, condition.Code, localization.ID, localizationProps, this.localizations);
          if (localization.PeriodontalBoneLossLandmarks) {
            condition.Attribution.case === "Tooth" && quadrupleMap(condition.ReportID, localization.TargetAssetID, condition.Attribution.value.ToothID, localization.ID, localizationProps, this.pblConditions);
          }
        });
        const conditionProps = this.makeConditionProps(condition);
        if (condition.Attribution.case === "Tooth") {
          tripleMap(condition.Attribution.value.ToothID, condition.Code, condition.ID, conditionProps, conditionsForReport.Tooth);
          singleMap(condition.ID, conditionProps, flatConditionsForReport.Tooth);
        } else {
          const AttributionCase = String(condition.Attribution.case);
          doubleMap(condition.Code, condition.ID, conditionProps, conditionsForReport[AttributionCase]);
          singleMap(condition.ID, conditionProps, flatConditionsForReport[AttributionCase]);
        }
      });
      conditions.forEach((condition) => {
        if (condition.ParentID) {
          this.flatConditions.get(condition.ReportID)?.Tooth.get(condition.ParentID)?.ChildrenIDs.add(condition.ID);
        }
      });
      const getGropedLocalization = (group, localizationsWithCodes) => new Map(group.map((code) => localizationsWithCodes.get(code)).filter((x) => typeof x !== "undefined").map((x) => [...x]).flat());
      conditions.forEach((condition) => {
        condition.Localizations.forEach((localization) => {
          const targetAssetID = localization.TargetAssetID;
          const reportID = condition.ReportID;
          const localizationsWithCodes = this.localizations.get(reportID)?.get(targetAssetID);
          if (!localizationsWithCodes) return;
          let groupsForReport = this.localizationGrouped.get(reportID);
          if (!groupsForReport) {
            const groupedConditions = makeLocalizationPropsGroup();
            groupsForReport = arx.map([[targetAssetID, groupedConditions]]);
            this.localizationGrouped.set(reportID, groupsForReport);
          } else {
            if (!groupsForReport.get(targetAssetID)) {
              groupsForReport.set(targetAssetID, makeLocalizationPropsGroup());
            }
          }
          const groups = groupsForReport.get(targetAssetID);
          const periapicalRadiolucencyLocalizations = localizationsWithCodes.get(ConditionCode.PeriapicalRadiolucency);
          const periapicalRadiolucencyLocalizationsProps = periapicalRadiolucencyLocalizations?.get(localization.ID);
          if (periapicalRadiolucencyLocalizationsProps) {
            groups.periapicalRadiolucency.set(localization.ID, periapicalRadiolucencyLocalizationsProps);
          }
          const perioLocalizations = getGropedLocalization(constants.MASK_FILTERS_PERIO_CODES, localizationsWithCodes);
          const localizationProp = perioLocalizations?.get(localization.ID);
          if (localizationProp) {
            groups.perio.set(localization.ID, localizationProp);
            return;
          }
          const endoLocalizations = getGropedLocalization(constants.MASK_FILTERS_ENDO_CODES, localizationsWithCodes);
          const endoLocalizationsProps = endoLocalizations?.get(localization.ID);
          if (endoLocalizationsProps) {
            groups.endo.set(localization.ID, endoLocalizationsProps);
            return;
          }
          const restorativeLocalizations = getGropedLocalization(constants.MASK_FILTERS_RESTORATIVE_CODES, localizationsWithCodes);
          const restorativeLocalizationsProps = restorativeLocalizations?.get(localization.ID);
          if (restorativeLocalizationsProps) {
            groups.restorative.set(localization.ID, restorativeLocalizationsProps);
            return;
          }
          const chartLocalizations = getGropedLocalization(constants.MASK_FILTERS_PREVIOUS_TREATMENT, localizationsWithCodes);
          const chartLocalizationsProps = chartLocalizations?.get(localization.ID);
          if (chartLocalizationsProps) {
            groups.chart.set(localization.ID, chartLocalizationsProps);
            return;
          }
          const enamelLocalizations = localizationsWithCodes.get(ConditionCode.ToothGerm);
          const enamelLocalizationsProps = enamelLocalizations?.get(localization.ID);
          if (enamelLocalizationsProps) {
            groups.enamel.set(localization.ID, enamelLocalizationsProps);
            return;
          }
        });
      });
    });
  }
  log() {
    console.log("ReportID, ToothID, ConditionCode, ConditionID => ConditionProps");
    console.log(Object.fromEntries([...this.conditions]));
    console.log("ReportID, ConditionID => ConditionProps");
    console.log(Object.fromEntries([...this.flatConditions]));
    console.log("ReportID, AssetID, ConditionCode, LocalizationID => LocalizationProps");
    console.log(Object.fromEntries([...this.localizations]));
    console.log("ReportID, AssetID, LocalizationID => LocalizationProps");
    console.log(Object.fromEntries([...this.localizationGrouped]));
  }
}

var __create$3 = Object.create;
var __defProp$3 = Object.defineProperty;
var __getOwnPropDesc$3 = Object.getOwnPropertyDescriptor;
var __knownSymbol$3 = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError$3 = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp$3 = (obj, key, value) => key in obj ? __defProp$3(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart$3 = (base) => [, , , __create$3(null)];
var __decoratorStrings$3 = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn$3 = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError$3("Function expected") : fn;
var __decoratorContext$3 = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings$3[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError$3("Already initialized") : fns.push(__expectFn$3(fn || null)) });
var __decoratorMetadata$3 = (array, target) => __defNormalProp$3(target, __knownSymbol$3("metadata"), array[3]);
var __runInitializers$3 = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement$3 = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings$3[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc$3(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext$3(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn$3(it) && (desc[key] = it );
  }
  return desc && __defProp$3(target, name, desc), target;
};
var _resetTeethNumberPendingChange_dec, _addTeethNumberPendingRemove_dec, _removeTeethNumberPendingChange_dec, _addTeethNumberPendingChange_dec, _setCurrentPatient_dec, _setShowPeriapicalRadiolucencyMasks_dec, _toggleMarks_dec, _activateTool_dec, _deleteToolbar_dec, _setToolbar_dec, _setCurrentReport_dec, _setTeeth_dec$1, _setMode_dec, _addDisplaySlices_dec, _setDisplaySlices_dec, _toggleMaskFilters_dec, _resetMaskContourEdit_dec, _commitMaskContourEdit_dec, _restoreMaskContourUserDefinedVertices_dec, _setMaskContourUserDefinedVertex_dec, _initializeMaskContourEdit_dec, _setMaskFilters_dec, _setCurrentOrganizationID_dec, _setFocusedNonDentalConditions_dec, _setHoveredToothID_dec, _setHoveredNonDentalConditions_dec, _setHoveredConditions_dec, _deleteAsset_dec, _openModal_dec, _init$3;
const cloneMaskContourVertex = (point) => ({
  x: point.x,
  y: point.y,
  z: point.z
});
const MASK_CONTOUR_POSITION_EPSILON_SQ = 1e-12;
const cloneMaskContourEdit = (edit) => ({
  committedVertices: edit.committedVertices.map(cloneMaskContourVertex),
  initialVertices: edit.initialVertices.map(cloneMaskContourVertex),
  userDefinedVertices: new Map(
    [...edit.userDefinedVertices].map(([index, point]) => [
      index,
      cloneMaskContourVertex(point)
    ])
  )
});
_openModal_dec = [action], _deleteAsset_dec = [action], _setHoveredConditions_dec = [action], _setHoveredNonDentalConditions_dec = [action], _setHoveredToothID_dec = [action], _setFocusedNonDentalConditions_dec = [action], _setCurrentOrganizationID_dec = [action], _setMaskFilters_dec = [action], _initializeMaskContourEdit_dec = [action], _setMaskContourUserDefinedVertex_dec = [action], _restoreMaskContourUserDefinedVertices_dec = [action], _commitMaskContourEdit_dec = [action], _resetMaskContourEdit_dec = [action], _toggleMaskFilters_dec = [action], _setDisplaySlices_dec = [action], _addDisplaySlices_dec = [action], _setMode_dec = [action], _setTeeth_dec$1 = [action], _setCurrentReport_dec = [action], _setToolbar_dec = [action], _deleteToolbar_dec = [action], _activateTool_dec = [action], _toggleMarks_dec = [action], _setShowPeriapicalRadiolucencyMasks_dec = [action], _setCurrentPatient_dec = [action], _addTeethNumberPendingChange_dec = [action], _removeTeethNumberPendingChange_dec = [action], _addTeethNumberPendingRemove_dec = [action], _resetTeethNumberPendingChange_dec = [action];
class State {
  constructor() {
    __runInitializers$3(_init$3, 5, this);
    this.GeneratedReportSlices = arx.map();
    this.CBCTToothPathologySlices = arx.map();
    this.UserUploadedSlices = arx.map();
    this.UserUploadedMPRPlanes = arx.map();
    this.DisplaySlices = arx.map();
    this.FlatSlices = arx.map();
    this.PanoReformatGeneral = arx.map();
    this.PanoReformatSplit = arx.map();
    this.IOXRayImages = arx.map();
    this.panoImage = arx.map();
    this.currentReport = arx.box();
    this.currentPatient = arx.box();
    this.conditionsState = new ConditionsState();
    this.teethDetections = arx.map();
    this.teeth = arx.map();
    this.flatTeeth = arx.map();
    this.maxFaxAnatomyState = new MaxFaxAnatomyState();
    this.organizations = arx.map();
    this.currentOrganizationID = arx.box();
    this.featureFlags = arx.map();
    this.maskFilters = arx.obj();
    /** Session-only contour edits; these are not serialized to report data. */
    this.maskContourEdits = arx.map();
    this.hoveredConditions = arx.box();
    this.hoveredNonDentalConditions = arx.box();
    this.focusedNonDentalConditions = arx.box();
    this.hoveredToothID = arx.box();
    this.toolbarsState = arx.obj();
    this.layoutState = arx.obj({ mode: "matrix", id: void 0 });
    this.URL = arx.box("");
    this.dataFromURL = arx.obj();
    this.showPeriapicalRadiolucencyMasks = arx.box();
    this.teethLandmarks = arx.map();
    this.teethLandmarksIndexedByCode = arx.map();
    this.teethNumberPendingChanges = arx.map();
    this.isModalOpen = arx.box(false);
  }
  reset() {
    this.GeneratedReportSlices = arx.map();
    this.CBCTToothPathologySlices = arx.map();
    this.UserUploadedSlices = arx.map();
    this.UserUploadedMPRPlanes = arx.map();
    this.DisplaySlices = arx.map();
    this.FlatSlices = arx.map();
    this.PanoReformatGeneral = arx.map();
    this.PanoReformatSplit = arx.map();
    this.IOXRayImages = arx.map();
    this.panoImage = arx.map();
    this.currentReport = arx.box();
    this.currentPatient = arx.box();
    this.conditionsState.reset();
    this.teethDetections = arx.map();
    this.teeth = arx.map();
    this.flatTeeth = arx.map();
    this.maxFaxAnatomyState.reset();
    this.organizations = arx.map();
    this.currentOrganizationID = arx.box();
    this.featureFlags = arx.map();
    this.maskFilters = arx.obj();
    this.maskContourEdits = arx.map();
    this.hoveredConditions = arx.box();
    this.hoveredNonDentalConditions = arx.box();
    this.focusedNonDentalConditions = arx.box();
    this.hoveredToothID = arx.box();
    this.toolbarsState = arx.obj();
    this.layoutState = arx.obj({ mode: "matrix", id: void 0 });
    this.URL = arx.box("");
    this.dataFromURL = arx.obj();
    this.showPeriapicalRadiolucencyMasks = arx.box();
    this.teethLandmarks = arx.map();
    this.teethLandmarksIndexedByCode = arx.map();
    this.teethNumberPendingChanges = arx.map();
    this.isModalOpen = arx.box(false);
  }
  openModal(value) {
    this.isModalOpen.set(value);
  }
  addAssets(assets) {
    assets.forEach((asset) => handleAsset(asset, this));
  }
  deleteAsset(asset) {
    asset.Asset && handleAsset(asset.Asset, this);
  }
  setHoveredConditions(conditions) {
    this.hoveredConditions.set([...new Set(conditions)]);
  }
  setHoveredNonDentalConditions(conditions) {
    this.hoveredNonDentalConditions.set(Array.isArray(conditions) ? conditions : [conditions]);
  }
  setHoveredToothID(toothID) {
    this.hoveredToothID.set(toothID);
  }
  setFocusedNonDentalConditions(conditions) {
    this.focusedNonDentalConditions.set(Array.isArray(conditions) ? conditions : [conditions]);
  }
  async setOrganization(organization) {
    runInAction(() => {
      organization.forEach((org) => {
        singleMap(org.ID, org, this.organizations);
      });
    });
    this.setFeatureFlags();
  }
  setCurrentOrganizationID(organizationID) {
    this.currentOrganizationID.set(organizationID);
  }
  async setFeatureFlags(flags) {
    const report = await this.currentReport.getAsync();
    runInAction(() => {
      if (!report) return;
      const featureFlags = this.organizations.get(report.OrganizationID)?.FeatureFlags || flags?.featureFlags;
      if (!featureFlags) return;
      featureFlags.forEach((featureFlag) => {
        singleMap(featureFlag.Flag, featureFlag.Value, this.featureFlags);
      });
    });
  }
  setMaskFilters(filters) {
    this.maskFilters.perio = filters.perio ?? false;
    this.maskFilters.restorative = filters.restorative ?? false;
    this.maskFilters.endo = filters.endo ?? false;
    this.maskFilters.chart = filters.chart ?? false;
  }
  initializeMaskContourEdit(maskID, initialVertices) {
    const existing = this.maskContourEdits.getUntracked(maskID);
    if (existing) {
      return cloneMaskContourEdit(existing);
    }
    const edit = {
      committedVertices: initialVertices.map(cloneMaskContourVertex),
      initialVertices: initialVertices.map(cloneMaskContourVertex),
      userDefinedVertices: /* @__PURE__ */ new Map()
    };
    this.maskContourEdits.set(maskID, edit);
    return cloneMaskContourEdit(edit);
  }
  getMaskContourEdit(maskID) {
    const edit = this.maskContourEdits.get(maskID);
    return edit ? cloneMaskContourEdit(edit) : void 0;
  }
  getCommittedMaskContour(maskID) {
    return this.maskContourEdits.getUntracked(maskID)?.committedVertices.map(cloneMaskContourVertex);
  }
  setMaskContourUserDefinedVertex(maskID, vertexIndex, point) {
    const edit = this.maskContourEdits.get(maskID);
    const initialPoint = edit?.initialVertices[vertexIndex];
    if (!edit || !initialPoint) {
      return;
    }
    const userDefinedVertices = new Map(edit.userDefinedVertices);
    const displacementSq = (point.x - initialPoint.x) ** 2 + (point.y - initialPoint.y) ** 2 + (point.z - initialPoint.z) ** 2;
    if (displacementSq <= MASK_CONTOUR_POSITION_EPSILON_SQ) {
      userDefinedVertices.delete(vertexIndex);
    } else {
      userDefinedVertices.set(vertexIndex, cloneMaskContourVertex(point));
    }
    this.maskContourEdits.set(maskID, {
      committedVertices: edit.committedVertices,
      initialVertices: edit.initialVertices,
      userDefinedVertices
    });
  }
  restoreMaskContourUserDefinedVertices(maskID, userDefinedVertices) {
    const edit = this.maskContourEdits.get(maskID);
    if (!edit) {
      return;
    }
    this.maskContourEdits.set(maskID, {
      committedVertices: edit.committedVertices,
      initialVertices: edit.initialVertices,
      userDefinedVertices: new Map(
        [...userDefinedVertices].map(([index, point]) => [
          index,
          cloneMaskContourVertex(point)
        ])
      )
    });
  }
  commitMaskContourEdit(maskID, committedVertices) {
    const edit = this.maskContourEdits.get(maskID);
    if (!edit) {
      return;
    }
    this.maskContourEdits.set(maskID, {
      committedVertices: committedVertices.map(cloneMaskContourVertex),
      initialVertices: edit.initialVertices,
      userDefinedVertices: edit.userDefinedVertices
    });
  }
  resetMaskContourEdit(maskID) {
    const edit = this.maskContourEdits.get(maskID);
    if (!edit) {
      return;
    }
    this.maskContourEdits.set(maskID, {
      committedVertices: edit.committedVertices,
      initialVertices: edit.initialVertices,
      userDefinedVertices: /* @__PURE__ */ new Map()
    });
  }
  toggleMaskFilters(filter) {
    this.maskFilters[filter] = !this.maskFilters[filter];
  }
  setDisplaySlices(teeth) {
    teeth.forEach((tooth) => {
      this.DisplaySlices.set(tooth.ID, new Set(tooth.DisplaySlices));
    });
  }
  addDisplaySlices(teeth) {
    teeth.forEach((tooth) => {
      this.DisplaySlices.set(tooth.ID, new Set(tooth.DisplaySlices));
    });
  }
  setMode(mode, id) {
    if (mode === "matrix" || mode === "fullScreenMatrix") {
      this.layoutState.mode = mode;
      this.layoutState.id = void 0;
    } else if (id) {
      this.layoutState.mode = mode;
      this.layoutState.id = id;
    }
  }
  setTeeth(teeth) {
    teeth.forEach((tooth) => {
      const previousTooth = this.flatTeeth.get(tooth.ReportID)?.get(tooth.ID);
      if (previousTooth) {
        const teethOfReport = this.teeth.get(tooth.ReportID);
        if (!teethOfReport) return;
        previousTooth.Detections.forEach((detection) => {
          const localization = detection.Localizations;
          if (!localization) return;
          const newDetection = tooth.Detections.find((detection2) => detection2.Localizations?.TargetAssetID === localization.TargetAssetID);
          if (!newDetection) {
            teethOfReport.get(localization.TargetAssetID)?.delete(tooth.ID);
          }
        });
      }
      doubleMap(tooth.ReportID, tooth.ID, tooth, this.flatTeeth);
      tooth.Detections.forEach((detection) => {
        const localization = detection.Localizations;
        if (!localization) return;
        tripleMap(tooth.ReportID, localization.TargetAssetID, tooth.ID, tooth, this.teeth);
      });
    });
  }
  async setTeethAnatomy(teeth) {
    const currentReport = await this.currentReport.getAsync();
    if (!currentReport || !currentReport.ID) return;
    const reportID = currentReport.ID;
    action(() => {
      if (!this.teethDetections.has(reportID)) {
        this.teethDetections.set(reportID, {
          IOXRay: arx.map(),
          Pano: arx.map(),
          CBCT: arx.map()
        });
      }
      const reportTeethDetections = this.teethDetections.get(reportID);
      teeth.forEach((tooth) => {
        if (tooth.ReportID !== reportID) {
          return;
        }
        tooth.Detections.forEach((detection) => {
          detection.Anatomies.forEach((anatomy) => {
            const type = anatomy.Type.case;
            if (!anatomy.Localization || !type) {
              return;
            }
            const filteredMask = anatomy.Localization.PolygonalMask?.filter((mask) => mask.Points.length);
            if (!filteredMask.length) {
              return;
            }
            anatomy.Localization = anatomy.Localization.clone();
            anatomy.Localization.PolygonalMask = filteredMask;
            tripleMap(anatomy.Localization.TargetAssetID, anatomy.Type.value, anatomy.Localization.ID, {
              toothDetectionID: anatomy.Localization.ID,
              TargetAssetID: anatomy.Localization.TargetAssetID,
              localizationType: anatomy.Type,
              Localization: anatomy.Localization
            }, reportTeethDetections[type]);
          });
        });
      });
    })();
  }
  async addTeethLandmarks(landmarks) {
    const currentReport = await this.currentReport.getAsync();
    if (!currentReport) return;
    const reportID = currentReport.ID;
    const makeGroup = () => ({
      CBCT: arx.map(),
      IOS: arx.map(),
      Image: arx.map(),
      PBL: arx.map()
    });
    runInAction(() => {
      let teethLandmarks = this.teethLandmarks.get(reportID);
      let teethLandmarksIndexedByCode = this.teethLandmarksIndexedByCode.get(reportID);
      if (!teethLandmarks) {
        teethLandmarks = arx.map();
        this.teethLandmarks.set(reportID, teethLandmarks);
      }
      if (!teethLandmarksIndexedByCode) {
        teethLandmarksIndexedByCode = arx.map();
        this.teethLandmarksIndexedByCode.set(reportID, teethLandmarksIndexedByCode);
      }
      landmarks.forEach((landmark) => {
        const type = landmark.Code.case;
        let landmarksForTooth = teethLandmarks.get(landmark.ToothID);
        let landmarksForToothIndexedByCode = teethLandmarksIndexedByCode.get(landmark.ToothID);
        if (!landmarksForTooth) {
          landmarksForTooth = makeGroup();
          teethLandmarks.set(landmark.ToothID, landmarksForTooth);
        }
        if (!landmarksForToothIndexedByCode) {
          landmarksForToothIndexedByCode = makeGroup();
          teethLandmarksIndexedByCode.set(landmark.ToothID, landmarksForToothIndexedByCode);
        }
        switch (type) {
          case "CBCT":
            landmarksForTooth.CBCT.set(landmark.ID, landmark);
            landmarksForToothIndexedByCode.CBCT.set(landmark.Code.value, landmark);
            break;
          case "IOS":
            landmarksForTooth.IOS.set(landmark.ID, landmark);
            landmarksForToothIndexedByCode.IOS.set(landmark.Code.value, landmark);
            break;
          case "Image":
            landmarksForTooth.Image.set(landmark.ID, landmark);
            landmarksForToothIndexedByCode.Image.set(landmark.Code.value, landmark);
            break;
          case "PBL":
            landmarksForTooth.PBL.set(landmark.ID, landmark);
            landmarksForToothIndexedByCode.PBL.set(landmark.Code.value, landmark);
            break;
        }
      });
    });
  }
  async setConditions(conditions) {
    const currentReport = await this.currentReport.getAsync();
    if (!currentReport || !currentReport.ID) return;
    this.conditionsState.setConditions(conditions, currentReport.ID);
  }
  setCurrentReport(report) {
    if (!report) return;
    const currentReport = this.currentReport.get();
    if (currentReport) {
      currentReport.update(report);
    } else {
      this.currentReport.set(arx.obj(new ReportObserved(report)));
    }
  }
  setToolbar(toolbarId, state) {
    const {
      split = false,
      invert = false,
      expand = false,
      hideMarks = false,
      activeControl = void 0,
      editNumbers = false
    } = state || {};
    this.toolbarsState[toolbarId] = arx.obj({ split, invert, expand, hideMarks, activeControl, editNumbers });
  }
  deleteToolbar(toolbarId, state) {
    delete this.toolbarsState[toolbarId];
  }
  activateTool(toolbarId, control, value) {
    const state = this.toolbarsState[toolbarId];
    if (!state) return;
    if (!control || isToggledTool(control)) {
      if (control === "editMask" && value) {
        state.editNumbers = false;
      }
      state.activeControl = value ? control : void 0;
    } else {
      state[control] = value;
      if (control === "editNumbers" && value && state.activeControl === "editMask") {
        state.activeControl = void 0;
      }
    }
  }
  toggleMarks(toolbarId, value) {
    const state = this.toolbarsState[toolbarId];
    if (!state) return;
    state.hideMarks = value;
  }
  setShowPeriapicalRadiolucencyMasks(value) {
    this.showPeriapicalRadiolucencyMasks.set(value);
  }
  setCurrentPatient(patient) {
    this.currentPatient.set(patient);
  }
  addTeethNumberPendingChange(pendingChange) {
    this.teethNumberPendingChanges.set(pendingChange.tooth.detectionID, pendingChange);
  }
  removeTeethNumberPendingChange(detectionID) {
    this.teethNumberPendingChanges.delete(detectionID);
  }
  addTeethNumberPendingRemove(tooth) {
    const detectionID = tooth.detectionID;
    const pendingChange = {
      tooth,
      newNumber: -1,
      SupernumeraryIndex: 0
    };
    this.teethNumberPendingChanges.set(detectionID, pendingChange);
  }
  resetTeethNumberPendingChange() {
    this.teethNumberPendingChanges.clear();
  }
}
_init$3 = __decoratorStart$3();
__decorateElement$3(_init$3, 1, "openModal", _openModal_dec, State);
__decorateElement$3(_init$3, 1, "deleteAsset", _deleteAsset_dec, State);
__decorateElement$3(_init$3, 1, "setHoveredConditions", _setHoveredConditions_dec, State);
__decorateElement$3(_init$3, 1, "setHoveredNonDentalConditions", _setHoveredNonDentalConditions_dec, State);
__decorateElement$3(_init$3, 1, "setHoveredToothID", _setHoveredToothID_dec, State);
__decorateElement$3(_init$3, 1, "setFocusedNonDentalConditions", _setFocusedNonDentalConditions_dec, State);
__decorateElement$3(_init$3, 1, "setCurrentOrganizationID", _setCurrentOrganizationID_dec, State);
__decorateElement$3(_init$3, 1, "setMaskFilters", _setMaskFilters_dec, State);
__decorateElement$3(_init$3, 1, "initializeMaskContourEdit", _initializeMaskContourEdit_dec, State);
__decorateElement$3(_init$3, 1, "setMaskContourUserDefinedVertex", _setMaskContourUserDefinedVertex_dec, State);
__decorateElement$3(_init$3, 1, "restoreMaskContourUserDefinedVertices", _restoreMaskContourUserDefinedVertices_dec, State);
__decorateElement$3(_init$3, 1, "commitMaskContourEdit", _commitMaskContourEdit_dec, State);
__decorateElement$3(_init$3, 1, "resetMaskContourEdit", _resetMaskContourEdit_dec, State);
__decorateElement$3(_init$3, 1, "toggleMaskFilters", _toggleMaskFilters_dec, State);
__decorateElement$3(_init$3, 1, "setDisplaySlices", _setDisplaySlices_dec, State);
__decorateElement$3(_init$3, 1, "addDisplaySlices", _addDisplaySlices_dec, State);
__decorateElement$3(_init$3, 1, "setMode", _setMode_dec, State);
__decorateElement$3(_init$3, 1, "setTeeth", _setTeeth_dec$1, State);
__decorateElement$3(_init$3, 1, "setCurrentReport", _setCurrentReport_dec, State);
__decorateElement$3(_init$3, 1, "setToolbar", _setToolbar_dec, State);
__decorateElement$3(_init$3, 1, "deleteToolbar", _deleteToolbar_dec, State);
__decorateElement$3(_init$3, 1, "activateTool", _activateTool_dec, State);
__decorateElement$3(_init$3, 1, "toggleMarks", _toggleMarks_dec, State);
__decorateElement$3(_init$3, 1, "setShowPeriapicalRadiolucencyMasks", _setShowPeriapicalRadiolucencyMasks_dec, State);
__decorateElement$3(_init$3, 1, "setCurrentPatient", _setCurrentPatient_dec, State);
__decorateElement$3(_init$3, 1, "addTeethNumberPendingChange", _addTeethNumberPendingChange_dec, State);
__decorateElement$3(_init$3, 1, "removeTeethNumberPendingChange", _removeTeethNumberPendingChange_dec, State);
__decorateElement$3(_init$3, 1, "addTeethNumberPendingRemove", _addTeethNumberPendingRemove_dec, State);
__decorateElement$3(_init$3, 1, "resetTeethNumberPendingChange", _resetTeethNumberPendingChange_dec, State);
__decoratorMetadata$3(_init$3, State);

class Orchestrator extends ActionListeners {
  constructor(state = new State()) {
    super(state, ActionNames);
    this.state = state;
  }
  static Instance() {
    if (!Orchestrator.instance) {
      Orchestrator.instance = new Orchestrator();
    }
    return Orchestrator.instance;
  }
  orchestrate({ PATHS: PATHS2, store, actions }) {
    const CORS_POLICY = location.hostname === "localhost" || location.hostname === "127.0.0.1" ? "include" : "same-origin";
    if (!isMIRenderActions(actions)) {
      throw new Error("actions is not a valid MIRenderActions");
    }
    this.listen();
    this.onDownload = MedicalImageRender.downLoadPanoReformat;
    this.onDownloadSlice = MedicalImageRender.downLoadSlice;
    this.onReset = MedicalImageRender.resetSliceState;
    this.actions = actions;
    this.store = store;
    GlobalConfig$1.setPaths(PATHS2);
    MIRenderAPI$1.setState(this.state);
    MIRenderAPI$1.setCredentials(CORS_POLICY);
    this.onURLChange(window.location.pathname);
    const oldPushState = window.history.pushState.bind(window.history);
    window.history.pushState = (data, unused, url) => {
      url && this.onURLChange(url.toString());
      oldPushState(data, unused, url);
    };
    window.addEventListener("popstate", () => {
      this.onURLChange(window.location.pathname);
    }, false);
    if (location.hostname === "localhost") {
      window.app = MIRender;
      window.log = () => this.state.conditionsState.log();
    }
  }
  syncStore(store) {
    const filters = store.getState().maskFilters.filters;
    this.state.setMaskFilters({
      perio: filters.perio.isActive,
      restorative: filters.restorative.isActive,
      endo: filters.endo.isActive,
      chart: filters.chart.isActive
    });
    this.state.setShowPeriapicalRadiolucencyMasks(store.getState().reports.showPeriapicalRadiolucencyMasks);
  }
  async onURLChange(url) {
    const paths = GlobalConfig$1.paths;
    const setupMap = {
      [paths.gp]: SetupCBCT,
      [paths.ioxRay]: SetupIOXRay,
      [paths.pano]: SetupPano,
      [paths.bitewings]: SetupPanoWings
    };
    const reportTypeToSetup = {
      DataPanoGP: SetupPano,
      DataPanoBitewings: SetupPanoWings,
      DataIOXRayGP: SetupIOXRay,
      DataCBCTGP: SetupCBCT
    };
    const reportID = url.match(/\/(reports|preview2d)\/([^\/]+)/)?.[2];
    if (!reportID) return;
    runInAction(() => {
      this.state.dataFromURL.reportID = reportID;
    });
    const patientID = url.match(/\/patients\/([^\/]+)/)?.[1];
    const toothID = url.match(new RegExp(`${paths.gp}/tooth/([^/]+)`))?.[1];
    const pathMatch = url.match(/\/(gp|ioxray|pano|bitewings|preview|preview2d)(?:\/|\?|$)/);
    const pageType = pathMatch ? pathMatch[1].replace("2d", "") : void 0;
    runInAction(() => {
      this.state.URL.set(url);
      this.state.dataFromURL.toothID = toothID;
      this.state.dataFromURL.patientID = patientID;
      this.state.dataFromURL.pageType = pageType;
      if (reportID && this.state.currentReport.get()?.ID !== reportID) {
        this.state.currentReport.delete();
      }
    });
    if (MIRender.status() === "stop") {
      for (const [path, SetupClass] of Object.entries(setupMap)) {
        if (pageType === path) {
          this.setupApp(new SetupClass(this.state));
          return;
        }
      }
      if (pageType === "preview") {
        const report = await this.state.currentReport.getAsync();
        const SetupClass = reportTypeToSetup[report?.Specific?.case];
        if (SetupClass) {
          this.setupApp(new SetupClass(this.state));
        }
        return;
      }
    }
  }
  setupApp(setup) {
    this.syncStore(this.store);
    setup.setup();
  }
}
var Orchestrator$1 = Orchestrator.Instance();

class ToothNumberClickTool extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.handleClick = this.handleClick.bind(this);
    this.onMouseMoveInitial = this.onMouseMoveInitial.bind(this);
  }
  activate() {
    MIRender.mouseController.addListener("pointermove", this.onMouseMoveInitial, this.hostObject.getDomElement());
    MIRender.mouseController.addListener("pointerdown", this.handleClick, this.hostObject.getDomElement(), "left", 10);
  }
  deactivate() {
    MIRender.mouseController.removeListener("pointermove", this.onMouseMoveInitial, this.hostObject.getDomElement());
    MIRender.mouseController.removeListener("pointerdown", this.handleClick, this.hostObject.getDomElement(), "left");
  }
  setObjects() {
    const raycasted = this.view.raycastController.getObject();
    if (!raycasted) {
      this.entity = void 0;
      return;
    }
    if (raycasted.parent instanceof ToothNumber) {
      this.entity = raycasted.parent;
    }
  }
  onMouseMoveInitial() {
    this.setObjects();
    if (this.entity) {
      this.hostObject.getDomElement().style.cursor = "pointer";
    } else {
      this.hostObject.getDomElement().style.cursor = "default";
    }
  }
  handleClick() {
    if (!this.entity) return;
    MIRender.mouseController.listeners.get(this.hostObject.getDomElement())?.disableButton("click");
    if (this.delete) {
      Orchestrator$1.store.dispatch(Orchestrator$1.actions.toothNumberEdit.removePendingChange(this.entity.detectionID));
    } else {
      Orchestrator$1.store.dispatch(Orchestrator$1.actions.toothNumberEdit.setSelectedTooth({
        toothID: this.entity.toothID,
        ISONumber: this.entity.toothNumber.ISO,
        SupernumeraryIndex: this.entity.toothNumber.SupernumeraryIndex,
        isRemoved: this.entity.isRemoved,
        detectionID: this.entity.detectionID
      }));
    }
    MIRender.mouseController.addListener("pointerup", async () => {
      await sleep(0);
      MIRender.mouseController.listeners.get(this.hostObject.getDomElement())?.enableButton("click");
    }, this.hostObject.getDomElement(), "left");
  }
}

class FitImageByDoubleClick extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onDoubleClick = this.onDoubleClick.bind(this);
  }
  async activate() {
    MIRender.mouseController.addListener("dblclick", this.onDoubleClick, this.hostObject.getDomElement());
    this.imageContainer = await this.view.getImageContainer(this.hostObject.name);
  }
  deactivate() {
    MIRender.mouseController.removeListener("dblclick", this.onDoubleClick, this.hostObject.getDomElement());
  }
  onDoubleClick() {
    if (!this.imageContainer) return;
    this.imageContainer.lookAtImage(this.hostObject.cameraController.getOrthographicCamera());
    this.view.dispatchEvent({ type: "render" });
  }
}

const EDIT_MASK_VIEWPORT_TOOLS = [
  Drag,
  Hover,
  Zoom2DOnCursor,
  Pan2D
];
const EDIT_MASK_VIEWPORT_TOOLS_WITH_CAMERA_FIT = [
  FitCameraToImage,
  ...EDIT_MASK_VIEWPORT_TOOLS
];

const MatrixViewportEventNames = [
  ...viewportEventNames,
  "imageAdded",
  "lookAtBox",
  "downLoadImage"
];
const _MatrixViewViewportMode = class _MatrixViewViewportMode extends Mode {
};
_MatrixViewViewportMode.except = (tools) => new _MatrixViewViewportMode({ tools: { toolList: tools, include: false } });
let MatrixViewViewportMode = _MatrixViewViewportMode;
const MIRenderViewportServiceTools$1 = (key) => {
  if (key === "matrix") {
    return /* @__PURE__ */ new Set([Render]);
  } else if (key === "pano") {
    return /* @__PURE__ */ new Set([Render, DownLoadImage]);
  }
  return /* @__PURE__ */ new Set([Render, ImagePropsChangeInterceptor]);
};
const MatrixViewportTools = tuple(
  Pan2D,
  Zoom2DOnCursor,
  ImageContrastBrightness,
  ImageSharpness,
  FitCameraToImage,
  FocusByClick,
  ImageContrastBrightnessMultiple,
  ImageSharpnessMultiple,
  Drag,
  Hover,
  ToothNumberClickTool,
  FitImageByDoubleClick
);
const FULL_SCREEN_VIEWPORT = "fullScreenViewport";
const MatrixViewportModes = (key) => {
  if (key === FULL_SCREEN_VIEWPORT) {
    return {
      mainMode: new MatrixViewViewportMode([Drag, Hover, Zoom2DOnCursor, Pan2D, ToothNumberClickTool]),
      matrixMode: new MatrixViewViewportMode([Drag, Hover, ToothNumberClickTool]),
      fulScreenMode: new MatrixViewViewportMode({ tools: { toolList: [Pan2D, Zoom2DOnCursor, ToothNumberClickTool], include: true } }),
      contrastBrightnessMode: new MatrixViewViewportMode({ tools: { toolList: [], include: true } }),
      sharpnessMode: new MatrixViewViewportMode({ tools: { toolList: [], include: true } }),
      contrastBrightnessMultipleMode: new MatrixViewViewportMode({ tools: { toolList: [ImageContrastBrightnessMultiple], include: true } }),
      sharpnessMultipleMode: new MatrixViewViewportMode({ tools: { toolList: [ImageSharpnessMultiple], include: true } }),
      editMaskMode: new MatrixViewViewportMode([...EDIT_MASK_VIEWPORT_TOOLS]),
      printMode: new MatrixViewViewportMode([])
    };
  } else if (key === "pano") {
    return {
      mainMode: new MatrixViewViewportMode([Drag, FitCameraToImage, FitImageByDoubleClick, Pan2D, Zoom2DOnCursor, Hover, ToothNumberClickTool]),
      matrixMode: new MatrixViewViewportMode([Drag, Hover, FocusByClick, ToothNumberClickTool]),
      fulScreenMode: new MatrixViewViewportMode({ tools: { toolList: [], include: true } }),
      contrastBrightnessMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageContrastBrightness], include: true } }),
      sharpnessMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageSharpness], include: true } }),
      contrastBrightnessMultipleMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageContrastBrightness], include: true } }),
      sharpnessMultipleMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageSharpness], include: true } }),
      editMaskMode: new MatrixViewViewportMode([...EDIT_MASK_VIEWPORT_TOOLS_WITH_CAMERA_FIT]),
      printMode: new MatrixViewViewportMode([FitCameraToImage])
    };
  } else {
    return {
      mainMode: new MatrixViewViewportMode([FitCameraToImage, Drag, Hover, Zoom2DOnCursor, Pan2D, FocusByClick, ToothNumberClickTool, FitImageByDoubleClick]),
      matrixMode: new MatrixViewViewportMode([Drag, Hover, FocusByClick, ToothNumberClickTool]),
      fulScreenMode: new MatrixViewViewportMode({ tools: { toolList: [Pan2D, Zoom2DOnCursor, ToothNumberClickTool], include: true } }),
      contrastBrightnessMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageContrastBrightness], include: true } }),
      sharpnessMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageSharpness], include: true } }),
      contrastBrightnessMultipleMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageContrastBrightnessMultiple], include: true } }),
      sharpnessMultipleMode: new MatrixViewViewportMode({ tools: { toolList: [FitCameraToImage, FitImageByDoubleClick, ImageSharpnessMultiple], include: true } }),
      editMaskMode: new MatrixViewViewportMode([...EDIT_MASK_VIEWPORT_TOOLS_WITH_CAMERA_FIT]),
      printMode: new MatrixViewViewportMode([FitCameraToImage])
    };
  }
};
const setupMatrixViewports = (view, viewportRefs) => {
  const viewports = [];
  for (const viewportName in viewportRefs) {
    if (!view.getViewport(viewportName)) {
      const viewport = view.addViewport(viewportName, viewportRefs[viewportName].divElement, { eventNames: MatrixViewportEventNames });
      viewport.alpha = 0;
      viewport.toolController.setTools(MatrixViewportTools);
      viewport.toolController.setModes(MatrixViewportModes(viewportRefs[viewportName].viewportType || viewportName));
      viewport.toolController.setServiceTools(MIRenderViewportServiceTools$1(viewportRefs[viewportName].viewportType || viewportName));
      view.getLayer("roundCorners").add(new RoundCornerViewportEntity(viewport, 8, "black"));
      viewports.push(viewport);
    }
  }
  return viewports;
};

class ViewportDomRefs {
  constructor(view) {
    this.viewportsRef = /* @__PURE__ */ new Map();
    this.view = view;
    this.setViewportRef({ current: HTML.div({ style: { width: "100%", height: "100%", zIndex: "3" } }) }, FULL_SCREEN_VIEWPORT);
  }
  fromProps(props) {
    props.viewports?.forEach((ref, name) => {
      this.setViewportRef(ref.object, name, ref.viewportType);
    });
  }
  dispose() {
    this.viewportsRef.forEach((viewport) => {
      viewport.revoke();
    });
    this.viewportsRef.clear();
  }
  getViewportRef(viewportName) {
    return this.viewportsRef.get(viewportName)?.proxy;
  }
  deleteViewportRef(viewportName) {
    this.viewportsRef.get(viewportName)?.revoke();
    this.viewportsRef.delete(viewportName);
  }
  setViewportRef(ref, viewportName, viewportType) {
    const set = (target, prop, value) => {
      if (prop === "current") {
        let viewport = this.view.getViewport(viewportName);
        if (viewport) {
          if (!value) {
            viewport.dispose();
          } else {
            if (!viewport.isEnable()) {
              viewport.init();
            }
            if (target.current !== value) {
              viewport.setDomElement(value);
            }
          }
        }
      }
      return Reflect.set(target, prop, value);
    };
    const revocable = Proxy.revocable(ref, { set });
    if (!this.viewportsRef.get(viewportName)) {
      this.viewportsRef.set(viewportName, { object: ref, viewportType, proxy: revocable.proxy, revoke: revocable.revoke });
    }
  }
  getDomElements() {
    const result = {};
    this.viewportsRef.forEach((ref, id) => {
      result[id] = { divElement: ref.object.current, viewportType: ref.viewportType };
    });
    return result;
  }
  getViewportElement(name) {
    const ref = this.viewportsRef.get(name);
    if (!ref) return;
    return { [name]: { divElement: ref.object.current, viewportType: ref.viewportType } };
  }
}

class FMXLayoutController {
  constructor(view) {
    this.disposeCurrentMode = () => {
    };
    this.view = view;
    this.zoomFit = this.zoomFit.bind(this);
  }
  getMode() {
    return this.mode;
  }
  setTeeth(teeth) {
    this.teeth = teeth;
  }
  setImageMatrix(imageMatrix, specialImages, matrixLayoutOptions) {
    const newImages = imageMatrix.flat(2);
    const oldImages = this.imageMatrix?.flat(2);
    if (oldImages && oldImages.length && oldImages.length === newImages.length && oldImages.every((oldImage, index) => oldImage.ImageID === newImages[index].ImageID && oldImage.OrientationAngle === newImages[index].OrientationAngle)) {
      return false;
    }
    [...this.domRefs.viewportsRef.entries()].forEach(([viewportName, _]) => {
      if (viewportName === FULL_SCREEN_VIEWPORT) return;
      const contains = newImages.find((imageProps) => viewportName === imageProps.ImageID);
      if (!contains) {
        this.domRefs.deleteViewportRef(viewportName);
        this.domRefs.viewportsRef.delete(viewportName);
        this.view.deleteViewport(viewportName);
        this.view.imageController.deleteImage(viewportName);
      }
    });
    newImages.forEach((imageProps) => {
      const viewportName = imageProps.ImageID;
      const viewportRef = this.domRefs.getViewportRef(viewportName);
      if (!viewportRef) {
        this.domRefs.setViewportRef({ current: null }, viewportName, specialImages?.[viewportName]?.viewportType);
      }
    });
    if (specialImages && Object.keys(specialImages).some((key) => !newImages.find((image) => image.ImageID === key))) {
      console.error("special image property defined for non-existent image");
    }
    const cssRule = newImages.length === 1 ? "" : void 0;
    const sizeMode = matrixLayoutOptions?.sizeMode ?? "millimeters";
    const newMatrixLayout = FMXMatrixLayout(imageMatrix, this.domRefs, specialImages, matrixLayoutOptions, cssRule, sizeMode);
    const newFullScreenMatrixLayout = FMXMatrixLayout(imageMatrix, void 0, specialImages, matrixLayoutOptions, cssRule, sizeMode);
    newFullScreenMatrixLayout.updateBlocks(ImageContainer, this.view.imageController.imageContainers);
    this.matrixLayout = newMatrixLayout;
    this.fullScreenMatrixLayout = newFullScreenMatrixLayout;
    if (this.mode === "fullScreenMatrix") {
      this.zoomFit();
    } else if (this.mode === "matrix") {
      this.disposeCurrentMode();
      this.matrix();
    } else if (this.mode === "focus" && this.focusID) {
      const viewport = this.view.getViewport(this.focusID);
      if (viewport) {
        this.lookAtImage(viewport);
      }
    } else if (this.mode === void 0) {
      this.matrix();
    }
    this.imageMatrix = imageMatrix;
    const domElements = this.domRefs.getDomElements();
    for (const key in domElements) {
      if (!newImages.find((image) => image.ImageID === key)) {
        delete domElements[key];
      }
    }
    setupMatrixViewports(this.view, domElements).forEach((viewport) => viewport.init());
    this.view.dispatchEvent({ type: "render" });
    return true;
  }
  get domRefs() {
    return this.view.viewportDomRefs;
  }
  get fullScreenDomElement() {
    const ref = this.domRefs.viewportsRef.get(FULL_SCREEN_VIEWPORT)?.object.current;
    if (!ref) {
      throw new Error("fullscreen viewport is not defined");
    }
    return ref;
  }
  fullScreenMatrix() {
    if (this.mode === "fullScreenMatrix") {
      return;
    }
    this.domRefs.viewportsRef.get(FULL_SCREEN_VIEWPORT)?.object;
    let viewport = MIRender.view.getViewport(FULL_SCREEN_VIEWPORT);
    if (!viewport) {
      const element = this.domRefs.getViewportElement(FULL_SCREEN_VIEWPORT);
      if (!element) return;
      setupMatrixViewports(this.view, element)[0]?.init();
      viewport = MIRender.view.getViewport(FULL_SCREEN_VIEWPORT);
      if (!viewport) return;
    }
    this.disposeCurrentMode();
    MIRender.view.getDomElement().appendChild(this.fullScreenDomElement);
    const ref = this.domRefs.getViewportRef(FULL_SCREEN_VIEWPORT);
    if (ref) {
      ref.current = this.fullScreenDomElement;
    }
    this.zoomFit();
    viewport.addEventListener("resize", this.zoomFit);
    this.disposeCurrentMode = () => {
      viewport.removeEventListener("resize", this.zoomFit);
      this.fullScreenDomElement.remove();
      viewport.dispose();
      this.mode = void 0;
      this.disposeCurrentMode = () => {
      };
      for (let imageViewport of this.view.getViewports()) {
        const container = this.view.imageController.imageContainers.get(imageViewport.name);
        const component = container?.getComponent(ViewportComponent);
        component?.deleteViewport(viewport);
        component?.addViewport(imageViewport);
      }
    };
    for (let imageViewport of this.view.getViewports()) {
      const container = this.view.imageController.imageContainers.get(imageViewport.name);
      const component = container?.getComponent(ViewportComponent);
      component?.deleteViewport(imageViewport);
      component?.addViewport(viewport);
    }
    this.mode = "fullScreenMatrix";
    this.view.eventIsListened("layout") && this.view.dispatchEvent({ type: "layout", mode: "fullScreenMatrix" });
  }
  zoomFit() {
    const viewport = MIRender.view.getViewport(FULL_SCREEN_VIEWPORT);
    if (!viewport) return;
    if (!this.fullScreenMatrixLayout) return;
    const camera = viewport.cameraController.getOrthographicCamera();
    const width = this.fullScreenMatrixLayout.computedWidth;
    const height = this.fullScreenMatrixLayout.computedHeight;
    viewport.camera.position.set(width / 2, -height / 2, 0);
    const cameraWidth = camera.right - camera.left;
    const cameraHeight = camera.top - camera.bottom;
    camera.zoom = Math.min(cameraHeight / height, cameraWidth / width);
    camera.updateMatrix();
    camera.updateProjectionMatrix();
    this.view.dispatchEvent({ type: "transformCamera", viewport });
  }
  lookAtImage(viewport, box) {
    const camera = viewport.cameraController.getOrthographicCamera();
    const container = this.view.imageController.imageContainers.get(viewport.name);
    if (!container) {
      return;
    }
    container.lookAtImage(camera, box);
    this.view.dispatchEvent({ type: "transformCamera", viewport });
  }
  matrix() {
    if (this.mode === "matrix") {
      return;
    }
    this.disposeCurrentMode();
    const parent = this.view.domElement;
    if (parent) {
      this.matrixLayout.mountElement(parent);
    }
    const matrixLayout = this.matrixLayout;
    this.disposeCurrentMode = () => {
      matrixLayout.unmountElement();
      this.mode = void 0;
      this.disposeCurrentMode = () => {
      };
    };
    const viewContainer = this.view.getDomElement();
    if (viewContainer) {
      viewContainer.style.aspectRatio = `${this.matrixLayout.computedWidth / this.matrixLayout.computedHeight}`;
    }
    this.mode = "matrix";
    this.view.setActiveViewport(void 0);
    this.view.eventIsListened("layout") && this.view.dispatchEvent({ type: "layout", mode: "matrix" });
  }
  focus(id) {
    if (this.focusID === id) {
      return;
    }
    if (this.mode !== "focus") {
      this.disposeCurrentMode();
    } else if (this.focusID) {
      const prevRef = this.domRefs.getViewportRef(this.focusID);
      if (prevRef) {
        prevRef.current = null;
      }
    }
    const ref = this.domRefs.getViewportRef(id);
    if (!ref) {
      return;
    }
    if (!this.fullScreenDomElement.parentElement) {
      MIRender.view.getDomElement().appendChild(this.fullScreenDomElement);
    }
    ref.current = this.fullScreenDomElement;
    this.disposeCurrentMode = () => {
      this.fullScreenDomElement.remove();
      ref.current = null;
      this.focusID = void 0;
      this.mode = void 0;
      this.disposeCurrentMode = () => {
      };
    };
    const viewContainer = this.view.getDomElement();
    const imageContainer = this.view.imageController.imageContainers.get(id);
    if (viewContainer && imageContainer) {
      viewContainer.style.aspectRatio = `${imageContainer.width / imageContainer.height}`;
    }
    this.mode = "focus";
    this.focusID = id;
    this.view.setActiveViewport(id);
    this.view.eventIsListened("layout") && this.view.dispatchEvent({ type: "layout", mode: "focus", id });
  }
  crop(toothID) {
    if (this.cropID === toothID) {
      return;
    }
    const tooth = this.teeth.get(toothID);
    if (!tooth) {
      console.error(`[CROP LAYOUT] tooth with id ${toothID} is not defined`);
      return;
    }
    const props = tooth.Localizations.map((localization) => {
      const box = localization.BBox;
      if (!box) {
        console.error("[CROP LAYOUT] localization bounding box is not defined");
        return;
      }
      const imageContainer = this.view.imageController.imageContainers.get(localization.TargetAssetID);
      if (!imageContainer) {
        console.error(`[CROP LAYOUT] image with Asset ID = ${localization.TargetAssetID} not found`);
        return;
      }
      const newBox = { min: { x: box.X?.Min || 0, y: -(box.Y?.Max || 0) }, max: { x: box.X?.Max || 0, y: -(box.Y?.Min || 0) } };
      const width = newBox.max.x - newBox.min.x;
      const height = newBox.max.y - newBox.min.y;
      const rotation = imageContainer.object3d.rotation.z;
      const scale = imageContainer.object3d.scale.x;
      return { width, height, rotation, id: imageContainer.id, box: newBox, scale };
    }).filter((x) => x);
    if (!props.length) {
      console.error(`[CROP LAYOUT] no bounding box for tooth with id ${toothID}`);
      return;
    }
    const parent = this.view.domElement;
    if (!parent) {
      console.error(`[CROP LAYOUT] no main view container`);
      return;
    }
    this.disposeCurrentMode();
    this.cropLayout = MIRenderCropLayout(props, this.domRefs);
    this.cropLayout.mountElement(parent);
    props.forEach(
      (item) => MIRender.view.getViewport(item.id)?.dispatchEvent({ type: "lookAtBox", box: item.box })
    );
    this.disposeCurrentMode = () => {
      this.cropLayout.unmountElement();
      this.cropID = void 0;
      this.mode = void 0;
      this.disposeCurrentMode = () => {
      };
    };
    const viewContainer = this.view.getDomElement();
    if (viewContainer) {
      viewContainer.style.aspectRatio = `${this.cropLayout.computedWidth / this.cropLayout.computedHeight}`;
    }
    this.mode = "crop";
    this.cropID = toothID;
    this.view.eventIsListened("layout") && this.view.dispatchEvent({ type: "layout", mode: "crop", id: toothID });
  }
}

class IterableWeakMap {
  constructor(entries) {
    this.map = /* @__PURE__ */ new Map();
    this.weakMap = /* @__PURE__ */ new WeakMap();
    this.registry = new FinalizationRegistry((key) => this.map.delete(key));
    if (entries) {
      entries.forEach(([key, value]) => this.set(key, value));
    }
  }
  set(key, value) {
    let ref = this.weakMap.get(key);
    if (ref) {
      this.map.set(ref, value);
    } else {
      ref = new WeakRef(key);
      this.map.set(ref, value);
      this.weakMap.set(key, ref);
      this.registry.register(key, ref);
    }
  }
  delete(key) {
    const ref = this.weakMap.get(key);
    if (ref) {
      this.map.delete(ref);
      this.weakMap.delete(key);
    }
  }
  has(key) {
    return this.weakMap.has(key);
  }
  forEach(callback) {
    for (const [ref, value] of this.map) {
      const key = ref.deref();
      if (key) {
        callback(value, key);
      }
    }
  }
  *[Symbol.iterator]() {
    for (const [ref, value] of this.map) {
      const key = ref.deref();
      if (key) {
        yield [key, value];
      }
    }
  }
  *values() {
    for (const [ref, value] of this.map) {
      if (ref.deref()) {
        yield value;
      }
    }
  }
}

var __create$2 = Object.create;
var __defProp$2 = Object.defineProperty;
var __getOwnPropDesc$2 = Object.getOwnPropertyDescriptor;
var __knownSymbol$2 = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError$2 = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp$2 = (obj, key, value) => key in obj ? __defProp$2(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart$2 = (base) => [, , , __create$2(null)];
var __decoratorStrings$2 = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn$2 = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError$2("Function expected") : fn;
var __decoratorContext$2 = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings$2[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError$2("Already initialized") : fns.push(__expectFn$2(fn || null)) });
var __decoratorMetadata$2 = (array, target) => __defNormalProp$2(target, __knownSymbol$2("metadata"), array[3]);
var __runInitializers$2 = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement$2 = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings$2[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc$2(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext$2(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn$2(it) && (desc[key] = it );
  }
  return desc && __defProp$2(target, name, desc), target;
};
var _deleteImage_dec, _addImage_dec, _dispose_dec, _init$2;
_dispose_dec = [action], _addImage_dec = [action], _deleteImage_dec = [action];
class ImageControllerBase {
  constructor(view) {
    __runInitializers$2(_init$2, 5, this);
    this.promises = /* @__PURE__ */ new Map();
    this.view = view;
    this.imageContainers = arx.map();
    this.imageProps = /* @__PURE__ */ new Map();
    this.pictures = /* @__PURE__ */ new Map();
    this.promises = /* @__PURE__ */ new Map();
    this.viewportToImageMap = /* @__PURE__ */ new Map();
  }
  setMaskContourHydrator(getCommittedMaskContour) {
    this.getCommittedMaskContour = getCommittedMaskContour;
  }
  dispose() {
    this.promises.forEach((pending) => pending.resolve(null));
    this.promises.clear();
    this.imageProps.clear();
    this.imageContainers.clear();
    this.pictures.forEach((pictures) => {
      pictures.forEach((picture) => picture.dispose());
    });
  }
  addImage(props, URL2, previewURL, additionalURLs) {
    if (this.imageProps.has(props.ImageID)) {
      console.error(`image with id ${props.ImageID} already added`);
      const container2 = this.imageContainers.get(props.ImageID);
      return { container: container2, waitForLoad: this.promises.get(props.ImageID)?.promise || Promise.resolve(container2) };
    }
    this.imageProps.set(props.ImageID, props);
    const container = this.imageContainers.get(props.ImageID) || new ImageContainer(props.originalSize.width, props.originalSize.height, props.ImageID);
    this.imageContainers.set(props.ImageID, container);
    const image = new XRayImage(new Texture(), props);
    image.normalBlendingInverseRender();
    container.setImage(image);
    const isLiveImage = () => this.imageContainers.get(image.id)?.getImage() === image;
    const waitForLoad = promiseWithResolvers();
    this.promises.set(props.ImageID, waitForLoad);
    const forgetPromise = () => {
      if (this.promises.get(props.ImageID) === waitForLoad) {
        this.promises.delete(props.ImageID);
      }
    };
    const setTexture = (texture) => {
      if (!isLiveImage()) {
        return;
      }
      const prevUniforms = image.object3d.material.uniforms;
      const width = props.originalSize.width || void 0;
      const height = props.originalSize.height || void 0;
      image.setMaterial(texture, width, height);
      image.object3d.material.uniforms.brightness = prevUniforms.brightness;
      image.object3d.material.uniforms.contrast = prevUniforms.contrast;
      image.object3d.material.uniforms.sharpness = prevUniforms.sharpness;
      image.object3d.material.uniforms.invert = prevUniforms.invert;
      image.normalBlendingInverseRender();
      texture.needsUpdate = true;
      !this.isTypewriter() && container.getLayer() && this.view.dispatchEvent({ type: "render" });
    };
    const setTextures = async (urls) => {
      try {
        const texture = await ImageLoader$1.get(urls.shift());
        setTexture(texture);
      } catch (e) {
        if (urls.length) {
          setTextures(urls);
        } else {
          console.error(e);
          waitForLoad.reject(e);
          forgetPromise();
        }
      }
    };
    const abandonLoad = () => {
      waitForLoad.resolve(null);
      forgetPromise();
    };
    ImageLoader$1.get(previewURL || URL2).then(async (texture) => {
      if (!isLiveImage()) {
        abandonLoad();
        return;
      }
      setTexture(texture);
      if (previewURL) {
        await setTextures([URL2, ...additionalURLs || []]);
      }
      const loadedContainer = this.imageContainers.get(image.id);
      if (!isLiveImage() || !loadedContainer) {
        abandonLoad();
        return;
      }
      waitForLoad.resolve(loadedContainer);
      forgetPromise();
    }).catch(async (e) => {
      if (previewURL) {
        await setTextures([URL2, ...additionalURLs || []]);
      } else if (additionalURLs) {
        await setTextures(additionalURLs);
      } else {
        console.error(e);
        waitForLoad.reject(e);
        forgetPromise();
        return;
      }
      const fallbackContainer = this.imageContainers.get(image.id);
      waitForLoad.resolve(isLiveImage() && fallbackContainer ? fallbackContainer : null);
      forgetPromise();
    });
    return { container, waitForLoad: waitForLoad.promise };
  }
  updatePictures(imageId) {
    if (imageId) {
      this.pictures.get(imageId)?.forEach((picture) => picture.update());
    } else {
      this.pictures.forEach((pictures) => {
        pictures.forEach((picture) => picture.update());
      });
    }
  }
  addPicture(assetID, picture) {
    const img = picture.imageElement.deref();
    if (!img) return;
    const iterableWeakMap = this.pictures.get(assetID);
    if (iterableWeakMap) {
      if (!iterableWeakMap.has(img)) {
        iterableWeakMap.set(img, picture);
      }
    } else {
      this.pictures.set(assetID, new IterableWeakMap([[img, picture]]));
    }
  }
  deleteImage(imageID) {
    const container = this.imageContainers.get(imageID);
    if (!container) {
      console.error(`image with id ${imageID} not found`);
      return;
    }
    const pending = this.promises.get(imageID);
    if (pending) {
      this.promises.delete(imageID);
      pending.resolve(null);
    }
    container.layer?.delete(container);
    container.dispose();
    this.imageContainers.delete(imageID);
    this.imageProps.delete(imageID);
  }
  async addMasks(data) {
    await Promise.all(
      data.map(async (item) => {
        const container = await this.imageContainers.getAsync(item.imageID);
        if (!container) {
          console.error(`[addMasks] no image with id ${item.imageID}`);
          return;
        }
        const pendingLoad = this.promises.get(item.imageID)?.promise;
        const loadedContainer = pendingLoad ? await pendingLoad.catch(() => null) : container;
        const image = (loadedContainer ?? container).getImage();
        if (!image) {
          console.error(`[addMasks] no image with id ${item.imageID}`);
          return;
        }
        const mask = makeMask(item, image.getPixelSpacing());
        if (mask) {
          const committedContour = mask instanceof Mask ? this.getCommittedMaskContour?.(mask.contourEditID) : void 0;
          if (mask instanceof Mask && committedContour) {
            mask.restoreEditablePoints(committedContour.map(
              (point) => new Vector3(point.x, point.y, point.z)
            ));
          }
          container.addMask([mask], item.groupID);
        }
      })
    );
    this.view.dispatchEvent({ type: "render" });
  }
  isTypewriter() {
    const url = new URL(window.location.toString());
    return url.searchParams.get("isTypewriter") === "true";
  }
}
_init$2 = __decoratorStart$2();
__decorateElement$2(_init$2, 1, "dispose", _dispose_dec, ImageControllerBase);
__decorateElement$2(_init$2, 1, "addImage", _addImage_dec, ImageControllerBase);
__decorateElement$2(_init$2, 1, "deleteImage", _deleteImage_dec, ImageControllerBase);
__decoratorMetadata$2(_init$2, ImageControllerBase);

class MatrixImagesController extends ImageControllerBase {
  constructor(view) {
    super(view);
    this.screenProps = {
      brightness: 0,
      contrast: 0,
      sharpness: 0
    };
    this.toothCrops = /* @__PURE__ */ new Map();
  }
  dispose() {
    super.dispose();
    this.toothCrops.clear();
  }
  addImagesToMatrix(imageMatrix, usePreview = true) {
    const map = /* @__PURE__ */ new Map();
    imageMatrix.flat(2).forEach((item) => map.set(item.ImageID, item));
    const results = [...this.imageContainers.values()].map((container) => {
      if (container.getImage()) {
        return;
      }
      const imageProps = map.get(container.id);
      if (!imageProps) {
        return;
      }
      if (this.view.layoutController.getMode() === "fullScreenMatrix") {
        container.getComponent(ViewportComponent)?.addViewport(FULL_SCREEN_VIEWPORT);
      } else {
        container.getComponent(ViewportComponent)?.addViewport(container.id);
        const viewport = this.view.getViewport(container.id);
        viewport && this.viewportToImageMap.set(viewport, container.id);
      }
      this.view.getLayer("image")?.add(container);
      const originalURL = getFileSrc(imageProps.OriginalFileID);
      const optimizedURL = getImageSrc(imageProps.ImageID, "optimized");
      const previewURL = usePreview ? getImageSrc(imageProps.ImageID, "preview") : void 0;
      const result = this.addImage(imageProps, optimizedURL, previewURL, [originalURL]);
      this.view.getViewport(container.id)?.dispatchEvent({ type: "imageAdded", imageID: container.id, image: container.getImage() });
      return result;
    });
    if (this.isTypewriter()) {
      Promise.all(results.map((x) => x?.waitForLoad)).then(() => {
        this.view.dispatchEvent({ type: "render" });
      });
    }
    return results;
  }
  addImage(props, URL, previewURL, additionalURLs) {
    const result = super.addImage(props, URL, previewURL, additionalURLs);
    result?.waitForLoad.then((loaded) => loaded && this.updatePictures(props.ImageID));
    return result;
  }
  updatePictures(imageId) {
    if (imageId) {
      this.pictures.get(imageId)?.forEach((picture) => picture.update());
    } else {
      this.pictures.forEach((pictures) => {
        pictures.forEach((picture) => picture.update());
      });
    }
  }
  addPBL(props) {
    props.forEach((item) => {
      const id = item.imageID;
      const container = this.imageContainers.get(id);
      if (!container) {
        console.error(`[addPBL] no image with id ${id}`);
        return;
      }
      item.PBLs.map((pblProps) => {
        pblProps.start = flipY(pblProps.start);
        pblProps.end = flipY(pblProps.end);
        const handleProps = { color: pblProps.color, size: 5.5, maxZoom: 3 / (container.height * item.scale ** 2) };
        const pbl = new PBL({ ...pblProps, scale: item.scale, startHandleProps: handleProps, endHandleProps: handleProps }, id);
        container.addPBL(pbl);
      });
    });
  }
  addTeethNumbers(props) {
    return props.map(({ assetID, toothID, numeration, position, scale, detectionID, isRemoved, format }) => {
      const container = this.imageContainers.get(assetID);
      if (!container) {
        return;
      }
      scale = scale / container.object3d.scale.x;
      const toothNumber = new ToothNumber({ toothNumber: numeration, toothID, scale, detectionID, isRemoved, format }, container.id);
      toothNumber.object3d.position.set(position.x, position.y, 0);
      container.addTeethNumber(toothNumber);
      return toothNumber;
    }).filter((x) => typeof x !== "undefined");
  }
  addConditionBoxes(data) {
    data.forEach((item) => {
      const container = this.imageContainers.get(item.imageID);
      if (!container) {
        console.error(`[addConditionBoxes] no image with id ${item.imageID}`);
        return;
      }
      const boxes = item.boxes.map((data2) => {
        data2.min = flipY(data2.min);
        data2.max = flipY(data2.max);
        return new ConditionBBox(data2, item.imageID);
      });
      container.addConditionBoxes(boxes);
    });
  }
  addCrop(imageID, toothID, picture) {
    const container = picture.container.deref();
    if (!container) return;
    const map = this.toothCrops.get(toothID);
    if (map) {
      const set = map.get(container);
      if (set instanceof Set) {
        if (!set.has(picture)) {
          set.add(picture);
        }
      } else {
        map.set(container, /* @__PURE__ */ new Set([picture]));
      }
    } else {
      this.toothCrops.set(toothID, new WeakMap([[container, /* @__PURE__ */ new Set([picture])]]));
    }
    this.addPicture(imageID, picture);
  }
}

class MatrixView extends View {
  constructor() {
    super();
    this.imageController = new MatrixImagesController(this);
    this.layoutController = new FMXLayoutController(this);
    this.viewportDomRefs = new ViewportDomRefs(this);
    this.raycastController.getNearest = (generator) => [...generator].sort((a, b) => b.raycast.entity.object3d.renderOrder - a.raycast.entity.object3d.renderOrder)[0];
  }
  init(props) {
    super.init(props);
    this.viewportDomRefs.fromProps(props.domRefs);
    setupMatrixViewports(this, this.viewportDomRefs.getDomElements());
    this.canvas.style.position = "absolute";
    this.domElement.style.containerType = "size";
    this.domElement.style.display = "flex";
    this.domElement.style.justifyContent = "center";
    this.domElement.style.alignItems = "center";
    this.domElement.style.flexWrap = "wrap";
  }
  dispose() {
    super.dispose();
    this.layoutController.disposeCurrentMode();
    this.imageController.dispose();
    this.viewportDomRefs.dispose();
  }
  async getImageContainer(name) {
    return await this.imageController.imageContainers.getAsync(name);
  }
}

const MatrixViewUIEventNames = [
  "brightnessContrastChanged",
  "sharpnessChanged",
  "hoverMask",
  "maskEditStateChanged",
  "layout",
  "screenBrightnessContrastChanged",
  "screenSharpnessChanged",
  "invertColors",
  "straightColors",
  "toggleInversion"
];
const MatrixViewEventNames = [
  ...coreEventNames,
  ...MatrixViewUIEventNames
];

class MultiCommand extends Command {
  constructor(commands) {
    super();
    this.commands = commands;
  }
  addToTail(command) {
    this.commands.push(command);
  }
  addToHead(command) {
    this.commands.unshift(command);
  }
  execute(view) {
    return this.commands.every((command) => command.execute(view));
  }
  undo(view) {
    const length = this.commands.length;
    let reducer = true;
    for (let i = length - 1; i >= 0; i--) {
      reducer = reducer && this.commands[i].undo(view);
    }
    return reducer;
  }
}

const clonePoints = (points) => points.map((point) => point.clone());
const cloneUserDefinedVertices = (vertices) => new Map(
  [...vertices].map(([index, point]) => [index, { ...point }])
);
class MaskContourChangeCommand extends Command {
  constructor({
    nextPoints,
    nextUserDefinedVertices,
    previousPoints,
    previousUserDefinedVertices,
    resolveMask,
    restoreCommittedVertices,
    restoreUserDefinedVertices
  }) {
    super();
    this.committed = false;
    this.nextPoints = clonePoints(nextPoints);
    this.nextUserDefinedVertices = cloneUserDefinedVertices(nextUserDefinedVertices);
    this.previousPoints = clonePoints(previousPoints);
    this.previousUserDefinedVertices = cloneUserDefinedVertices(previousUserDefinedVertices);
    this.resolveMask = resolveMask;
    this.restoreCommittedVertices = restoreCommittedVertices;
    this.restoreUserDefinedVertices = restoreUserDefinedVertices;
  }
  commit() {
    this.committed = true;
    return this;
  }
  execute(view) {
    return this.restore(
      view,
      this.nextPoints,
      this.previousPoints,
      this.nextUserDefinedVertices
    );
  }
  undo(view) {
    return this.restore(
      view,
      this.previousPoints,
      this.nextPoints,
      this.previousUserDefinedVertices
    );
  }
  restore(view, points, previousPoints, userDefinedVertices) {
    const mask = this.resolveMask();
    this.restoreUserDefinedVertices?.(
      cloneUserDefinedVertices(userDefinedVertices)
    );
    if (this.committed) {
      this.restoreCommittedVertices?.(points.map((point) => ({
        x: point.x,
        y: point.y,
        z: point.z
      })));
    }
    if (!mask) {
      view.dispatchEvent({ type: "render" });
      return true;
    }
    mask.restoreEditablePoints(points);
    if (this.committed) {
      view.dispatchEvent({
        type: "geometryChanged",
        entity: mask,
        previous: clonePoints(previousPoints)
      });
    }
    view.dispatchEvent({ type: "render" });
    return true;
  }
}

const HOVER_FILL_OPACITY_INCREASE = 0.1;
class EditMaskHighlighter extends Highlighter {
  constructor(entity) {
    super(entity);
    if (!(entity instanceof Mask)) {
      throw new Error("Mask-only component");
    }
  }
  highlight() {
    if (this.initialAppearance) {
      return;
    }
    const fillMaterial = this.entity.fill.material;
    this.initialAppearance = {
      fillOpacity: fillMaterial.opacity
    };
    fillMaterial.opacity = Math.min(
      1,
      fillMaterial.opacity + HOVER_FILL_OPACITY_INCREASE
    );
  }
  unHighlight() {
    if (!this.initialAppearance) {
      return;
    }
    this.entity.fill.material.opacity = this.initialAppearance.fillOpacity;
    this.initialAppearance = void 0;
  }
}

const RELATIVE_DRAWING_TOLERANCE = 0.01;
const DIRECTION_EPSILON = 1e-7;
const getDrawnContourTolerance = (points, maximumTolerance) => {
  const bounds = points.reduce((result, point) => ({
    maxX: Math.max(result.maxX, point.x),
    maxY: Math.max(result.maxY, point.y),
    minX: Math.min(result.minX, point.x),
    minY: Math.min(result.minY, point.y)
  }), {
    maxX: -Infinity,
    maxY: -Infinity,
    minX: Infinity,
    minY: Infinity
  });
  const extent = Math.hypot(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
  return Math.min(maximumTolerance, extent * RELATIVE_DRAWING_TOLERANCE);
};
const refineDrawnContourForSmoothing = ({
  drawnPointCount,
  points,
  smoothingIterations,
  tolerance
}) => {
  if (smoothingIterations <= 0 || tolerance <= 0) {
    return points.map((point) => point.clone());
  }
  const supportPointCount = Math.ceil(smoothingIterations / 2);
  const cornerSpacings = points.map((point, index) => {
    if (index >= drawnPointCount) {
      return Infinity;
    }
    const previous = points[(index + points.length - 1) % points.length];
    const next = points[(index + 1) % points.length];
    const incoming = point.clone().sub(previous).normalize();
    const outgoing = next.clone().sub(point).normalize();
    const halfTurnSine = Math.sqrt(Math.max(0, (1 - incoming.dot(outgoing)) / 2));
    return halfTurnSine > DIRECTION_EPSILON ? tolerance / (smoothingIterations * halfTurnSine) : Infinity;
  });
  return points.flatMap((point, index) => {
    const nextIndex = (index + 1) % points.length;
    const next = points[nextIndex];
    const length = point.distanceTo(next);
    const supportFractions = [index, nextIndex].flatMap((cornerIndex, side) => {
      const spacing = cornerSpacings[cornerIndex];
      if (!Number.isFinite(spacing) || length === 0) {
        return [];
      }
      const step = Math.min(spacing / length, 1 / (2 * supportPointCount + 1));
      return Array.from({ length: supportPointCount }, (_, offset) => {
        const fraction = (offset + 1) * step;
        return side === 0 ? fraction : 1 - fraction;
      });
    }).sort((first, second) => first - second);
    return [
      point.clone(),
      ...supportFractions.map((fraction) => point.clone().lerp(next, fraction))
    ];
  });
};

const GEOMETRY_EPSILON = 1e-7;
const DEFAULT_SIMPLIFICATION_TOLERANCE = 0.75;
const cross2D = (firstX, firstY, secondX, secondY) => firstX * secondY - firstY * secondX;
const getSegmentIntersection = (firstStart, firstEnd, secondStart, secondEnd) => {
  const firstX = firstEnd.x - firstStart.x;
  const firstY = firstEnd.y - firstStart.y;
  const secondX = secondEnd.x - secondStart.x;
  const secondY = secondEnd.y - secondStart.y;
  const denominator = cross2D(firstX, firstY, secondX, secondY);
  if (Math.abs(denominator) <= GEOMETRY_EPSILON) {
    return;
  }
  const offsetX = secondStart.x - firstStart.x;
  const offsetY = secondStart.y - firstStart.y;
  const firstInterpolation = cross2D(
    offsetX,
    offsetY,
    secondX,
    secondY
  ) / denominator;
  const secondInterpolation = cross2D(
    offsetX,
    offsetY,
    firstX,
    firstY
  ) / denominator;
  if (firstInterpolation < -GEOMETRY_EPSILON || firstInterpolation > 1 + GEOMETRY_EPSILON || secondInterpolation < -GEOMETRY_EPSILON || secondInterpolation > 1 + GEOMETRY_EPSILON) {
    return;
  }
  const clampedFirstInterpolation = Math.min(
    1,
    Math.max(0, firstInterpolation)
  );
  return {
    firstInterpolation: clampedFirstInterpolation,
    point: firstStart.clone().lerp(firstEnd, clampedFirstInterpolation),
    secondInterpolation: Math.min(1, Math.max(0, secondInterpolation))
  };
};
const getPointAtPathPosition = (points, position) => {
  const startIndex = Math.max(
    0,
    Math.min(points.length - 1, Math.floor(position))
  );
  const start = points[startIndex];
  const end = points[Math.min(startIndex + 1, points.length - 1)];
  return start.clone().lerp(
    end,
    Math.max(0, Math.min(1, position - startIndex))
  );
};
const getClosedPathSection = (points, { endPosition, startPosition }) => {
  const start = getPointAtPathPosition(points, startPosition);
  const end = getPointAtPathPosition(points, endPosition);
  const closure = start.add(end).multiplyScalar(0.5);
  return normalizeClosedPath([
    closure,
    ...points.filter((_, index) => index > startPosition + GEOMETRY_EPSILON && index < endPosition - GEOMETRY_EPSILON)
  ]);
};
const getClosedStrokeRanges = (points, closureDistance) => {
  const ranges = [];
  const addRange = (startPosition, endPosition, requireExcursion = false) => {
    if (endPosition - startPosition <= 1 + GEOMETRY_EPSILON) {
      return;
    }
    if (requireExcursion) {
      const closure = getPointAtPathPosition(points, startPosition).add(getPointAtPathPosition(points, endPosition)).multiplyScalar(0.5);
      const span = endPosition - startPosition;
      const hasExcursion = [0.25, 0.5, 0.75].some((interpolation) => getPointAtPathPosition(
        points,
        startPosition + span * interpolation
      ).distanceTo(closure) > closureDistance);
      if (!hasExcursion) {
        return;
      }
    }
    if (ranges.some((range) => Math.abs(range.startPosition - startPosition) <= GEOMETRY_EPSILON && Math.abs(range.endPosition - endPosition) <= GEOMETRY_EPSILON)) {
      return;
    }
    ranges.push({ endPosition, startPosition });
  };
  points.slice(0, -1).forEach((start, firstIndex) => {
    const end = points[firstIndex + 1];
    points.slice(firstIndex + 2, -1).forEach((secondStart, offset) => {
      const secondIndex = firstIndex + offset + 2;
      const intersection = getSegmentIntersection(
        start,
        end,
        secondStart,
        points[secondIndex + 1]
      );
      if (intersection) {
        addRange(
          firstIndex + intersection.firstInterpolation,
          secondIndex + intersection.secondInterpolation
        );
      }
    });
  });
  if (closureDistance > 0) {
    const closureDistanceSquared = closureDistance ** 2;
    points.forEach((start, startIndex) => {
      points.slice(startIndex + 3).forEach((end, offset) => {
        if (start.distanceToSquared(end) <= closureDistanceSquared) {
          addRange(startIndex, startIndex + offset + 3, true);
        }
      });
    });
  }
  return ranges.sort((first, second) => second.endPosition - second.startPosition - (first.endPosition - first.startPosition));
};
const getContourIntersections = (contour, stroke) => {
  const intersections = [];
  stroke.slice(0, -1).forEach((strokeStart, strokeIndex) => {
    const strokeEnd = stroke[strokeIndex + 1];
    contour.forEach((contourStart, contourIndex) => {
      const contourEnd = contour[(contourIndex + 1) % contour.length];
      const intersection = getSegmentIntersection(
        strokeStart,
        strokeEnd,
        contourStart,
        contourEnd
      );
      if (!intersection) {
        return;
      }
      intersections.push({
        contourPosition: contourIndex + intersection.secondInterpolation,
        point: intersection.point,
        strokePosition: strokeIndex + intersection.firstInterpolation
      });
    });
  });
  return intersections.sort((first, second) => first.strokePosition - second.strokePosition).filter((intersection, index, sortedIntersections) => !sortedIntersections.slice(0, index).some((previous) => previous.point.distanceToSquared(intersection.point) <= GEOMETRY_EPSILON ** 2));
};
const getPointToSegmentDistanceSquared = (point, start, end) => {
  const deltaX = end.x - start.x;
  const deltaY = end.y - start.y;
  const lengthSquared = deltaX ** 2 + deltaY ** 2;
  if (lengthSquared <= GEOMETRY_EPSILON ** 2) {
    return (point.x - start.x) ** 2 + (point.y - start.y) ** 2;
  }
  const interpolation = Math.min(1, Math.max(0, ((point.x - start.x) * deltaX + (point.y - start.y) * deltaY) / lengthSquared));
  const nearestX = start.x + deltaX * interpolation;
  const nearestY = start.y + deltaY * interpolation;
  return (point.x - nearestX) ** 2 + (point.y - nearestY) ** 2;
};
const simplifyOpenPath = (points, tolerance) => {
  if (points.length <= 2 || tolerance <= 0) {
    return points.map((point) => point.clone());
  }
  const start = points[0];
  const end = points.at(-1);
  const farthest = points.slice(1, -1).reduce(
    (result, point, offset) => {
      const distanceSquared = getPointToSegmentDistanceSquared(point, start, end);
      return distanceSquared > result.distanceSquared ? { distanceSquared, index: offset + 1 } : result;
    },
    { distanceSquared: -1, index: -1 }
  );
  if (farthest.distanceSquared <= tolerance ** 2) {
    return [start.clone(), end.clone()];
  }
  const before = simplifyOpenPath(
    points.slice(0, farthest.index + 1),
    tolerance
  );
  const after = simplifyOpenPath(points.slice(farthest.index), tolerance);
  return [...before.slice(0, -1), ...after];
};
const removeAdjacentDuplicates = (points) => points.reduce(
  (result, point) => result.length && result.at(-1).distanceToSquared(point) <= GEOMETRY_EPSILON ** 2 ? result : [...result, point.clone()],
  []
);
const normalizeClosedPath = (points) => {
  const normalized = removeAdjacentDuplicates(points);
  if (normalized.length > 1 && normalized[0].distanceToSquared(normalized.at(-1)) <= GEOMETRY_EPSILON ** 2) {
    normalized.pop();
  }
  return normalized;
};
const simplifyClosedPath = (points, tolerance) => {
  const uniquePoints = normalizeClosedPath(points);
  if (uniquePoints.length <= 3) {
    return uniquePoints;
  }
  const splitIndex = uniquePoints.reduce(
    (result, point, index) => {
      const distanceSquared = point.distanceToSquared(uniquePoints[0]);
      return distanceSquared > result.distanceSquared ? { distanceSquared, index } : result;
    },
    { distanceSquared: -1, index: 0 }
  ).index;
  const firstHalf = simplifyOpenPath(
    uniquePoints.slice(0, splitIndex + 1),
    tolerance
  );
  const secondHalf = simplifyOpenPath(
    [...uniquePoints.slice(splitIndex), uniquePoints[0]],
    tolerance
  );
  return removeAdjacentDuplicates([
    ...firstHalf.slice(0, -1),
    ...secondHalf.slice(0, -1)
  ]);
};
const getSignedArea = (points) => points.reduce(
  (area, point, index) => {
    const next = points[(index + 1) % points.length];
    return area + point.x * next.y - next.x * point.y;
  },
  0
) / 2;
const isPointInsideClosedContour = (point, contour) => contour.some((start, index) => getPointToSegmentDistanceSquared(
  point,
  start,
  contour[(index + 1) % contour.length]
) <= GEOMETRY_EPSILON ** 2) || contour.reduce((inside, start, index) => {
  const end = contour[(index + 1) % contour.length];
  const crossesRay = start.y > point.y !== end.y > point.y && point.x < (end.x - start.x) * (point.y - start.y) / (end.y - start.y) + start.x;
  return crossesRay ? !inside : inside;
}, false);
const getForwardContourArc = (contour, start, end) => {
  const pointCount = contour.length;
  const forwardLength = ((end.contourPosition - start.contourPosition) % pointCount + pointCount) % pointCount;
  const vertices = Array.from({ length: pointCount }, (_, offset) => {
    const unwrappedPosition = Math.floor(start.contourPosition) + offset + 1;
    return {
      distance: unwrappedPosition - start.contourPosition,
      point: contour[unwrappedPosition % pointCount]
    };
  }).filter(({ distance }) => distance > GEOMETRY_EPSILON && distance < forwardLength - GEOMETRY_EPSILON).map(({ point }) => point.clone());
  return [start.point.clone(), ...vertices, end.point.clone()];
};
const getStrokeSection = (stroke, start, end) => removeAdjacentDuplicates([
  start.point,
  ...stroke.filter((_, index) => index > start.strokePosition + GEOMETRY_EPSILON && index < end.strokePosition - GEOMETRY_EPSILON),
  end.point
]);
const hasSelfIntersection = (points) => {
  if (points.length < 4) {
    return false;
  }
  return points.some((start, firstIndex) => {
    const firstEndIndex = (firstIndex + 1) % points.length;
    return points.some((secondStart, secondIndex) => {
      const secondEndIndex = (secondIndex + 1) % points.length;
      const isAdjacent = firstIndex === secondIndex || firstEndIndex === secondIndex || secondEndIndex === firstIndex;
      if (isAdjacent || secondIndex < firstIndex) {
        return false;
      }
      return !!getSegmentIntersection(
        start,
        points[firstEndIndex],
        secondStart,
        points[secondEndIndex]
      );
    });
  });
};
const orientLikeContour = (points, contour) => getSignedArea(points) * getSignedArea(contour) < 0 ? [...points].reverse() : [...points];
const createIntersectingContour = (contour, stroke, intersections, simplificationTolerance, smoothingIterations) => {
  const firstIntersection = intersections[0];
  const lastIntersection = intersections.at(-1);
  const strokeSection = getStrokeSection(stroke, firstIntersection, lastIntersection);
  const tolerance = getDrawnContourTolerance(
    strokeSection,
    simplificationTolerance
  );
  const drawnSection = simplifyOpenPath(
    strokeSection,
    smoothingIterations > 0 ? tolerance / 2 : tolerance
  );
  if (drawnSection.length < 2) {
    return;
  }
  const candidates = [
    removeAdjacentDuplicates([
      ...drawnSection,
      ...getForwardContourArc(
        contour,
        lastIntersection,
        firstIntersection
      ).slice(1, -1)
    ]),
    removeAdjacentDuplicates([
      ...[...drawnSection].reverse(),
      ...getForwardContourArc(
        contour,
        firstIntersection,
        lastIntersection
      ).slice(1, -1)
    ])
  ].filter((candidate) => candidate.length >= 3 && Math.abs(getSignedArea(candidate)) > GEOMETRY_EPSILON && !hasSelfIntersection(candidate));
  const selected = candidates.reduce(
    (largest, candidate) => !largest || Math.abs(getSignedArea(candidate)) > Math.abs(getSignedArea(largest)) ? candidate : largest,
    void 0
  );
  if (!selected) {
    return;
  }
  return {
    kind: "intersecting",
    points: orientLikeContour(refineDrawnContourForSmoothing({
      drawnPointCount: drawnSection.length,
      points: selected,
      smoothingIterations,
      tolerance: tolerance / 2
    }), contour)
  };
};
const createContourFromDrawStroke = ({
  closureDistance = 0,
  closureStroke,
  contour,
  isClosed,
  mapContourPosition,
  retainedContour = contour,
  simplificationTolerance = DEFAULT_SIMPLIFICATION_TOLERANCE,
  smoothingIterations = 0,
  stroke
}) => {
  const normalizedContour = normalizeClosedPath(contour);
  const uniqueStrokeIndices = stroke.reduce((indices, point, index) => {
    const previousPoint = indices.length ? stroke[indices.at(-1)] : void 0;
    if (!previousPoint || previousPoint.distanceToSquared(point) > GEOMETRY_EPSILON ** 2) {
      indices.push(index);
    }
    return indices;
  }, []);
  const uniqueStroke = uniqueStrokeIndices.map((index) => stroke[index].clone());
  const normalizedClosureStroke = closureStroke?.length === stroke.length ? uniqueStrokeIndices.map((index) => new Vector3(
    closureStroke[index].x,
    closureStroke[index].y,
    0
  )) : uniqueStroke;
  if (normalizedContour.length < 3 || uniqueStroke.length < 2 || !Number.isFinite(closureDistance) || closureDistance < 0 || !Number.isFinite(simplificationTolerance) || simplificationTolerance < 0 || !Number.isInteger(smoothingIterations) || smoothingIterations < 0) {
    return;
  }
  const normalizedStroke = isClosed && uniqueStroke[0].distanceToSquared(uniqueStroke.at(-1)) > GEOMETRY_EPSILON ** 2 ? [...uniqueStroke, uniqueStroke[0]] : uniqueStroke;
  const intersections = getContourIntersections(
    normalizedContour,
    normalizedStroke
  );
  if (intersections.length >= 2) {
    const normalizedRetainedContour = normalizeClosedPath(retainedContour);
    const retainedIntersections = mapContourPosition ? intersections.map((intersection) => ({
      ...intersection,
      contourPosition: mapContourPosition(intersection.contourPosition)
    })) : intersections;
    if (normalizedRetainedContour.length < 3 || retainedIntersections.some((intersection) => !Number.isFinite(intersection.contourPosition))) {
      return;
    }
    return createIntersectingContour(
      normalizedRetainedContour,
      normalizedStroke,
      retainedIntersections,
      simplificationTolerance,
      smoothingIterations
    );
  }
  const closedStrokeCandidates = isClosed ? [normalizeClosedPath(uniqueStroke)] : getClosedStrokeRanges(
    normalizedClosureStroke,
    closureDistance
  ).map((range) => getClosedPathSection(uniqueStroke, range));
  const closedCandidates = closedStrokeCandidates.map((candidate) => {
    const tolerance = getDrawnContourTolerance(candidate, simplificationTolerance);
    return {
      points: simplifyClosedPath(
        candidate,
        smoothingIterations > 0 ? tolerance / 2 : tolerance
      ),
      tolerance
    };
  }).filter(({ points: candidate }) => candidate.length >= 3 && Math.abs(getSignedArea(candidate)) > GEOMETRY_EPSILON && !hasSelfIntersection(candidate) && getContourIntersections(
    normalizedContour,
    [...candidate, candidate[0]]
  ).length === 0 && !candidate.some((point) => isPointInsideClosedContour(point, normalizedContour)));
  const closedContour = closedCandidates.reduce(
    (largest, candidate) => !largest || Math.abs(getSignedArea(candidate.points)) > Math.abs(getSignedArea(largest.points)) ? candidate : largest,
    void 0
  );
  if (!closedContour) {
    return;
  }
  return {
    kind: "closed",
    points: orientLikeContour(refineDrawnContourForSmoothing({
      drawnPointCount: closedContour.points.length,
      points: closedContour.points,
      smoothingIterations,
      tolerance: closedContour.tolerance / 2
    }), normalizedContour)
  };
};

const DRAW_CURSOR = "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDQ4IDQ4Ij48cGF0aCBkPSJNMzguOTc2NiAxMi45NDRMMzUuMDU1OSA5LjAyMzMzQzM0LjQ1MDEgOC40MTkwNiAzMy42MjkzIDguMDc5NzEgMzIuNzczNyA4LjA3OTcxQzMxLjkxOCA4LjA3OTcxIDMxLjA5NzIgOC40MTkwNiAzMC40OTE0IDkuMDIzMzNMMTAuMTg2NSAyOS4zMjgyQzkuODA5MDcgMjkuNzAzNSA5LjUwOTg0IDMwLjE0OTkgOS4zMDYxNyAzMC42NDE2QzkuMTAyNSAzMS4xMzMzIDguOTk4NDQgMzEuNjYwNiA5LjAwMDAyIDMyLjE5MjhWMzcuNDk5OUM5LjAwMDAyIDM3Ljg5NzggOS4xNTgwNSAzOC4yNzkzIDkuNDM5MzYgMzguNTYwNkM5LjcyMDY2IDM4Ljg0MTkgMTAuMTAyMiAzOC45OTk5IDEwLjUgMzguOTk5OUgxNS44MDcyQzE2LjMzOTUgMzkuMDAxNiAxNi44NjY4IDM4Ljg5NzUgMTcuMzU4NiAzOC42OTM5QzE3Ljg1MDQgMzguNDkwMiAxOC4yOTY5IDM4LjE5MDkgMTguNjcyMiAzNy44MTM0TDM4Ljk3NjYgMTcuNTA4NUMzOS41ODA5IDE2LjkwMjcgMzkuOTIwMiAxNi4wODIgMzkuOTIwMiAxNS4yMjYzQzM5LjkyMDIgMTQuMzcwNiAzOS41ODA5IDEzLjU0OTkgMzguOTc2NiAxMi45NDRaTTE2LjU1MDYgMzUuNjkyM0MxNi40NTMxIDM1Ljc5MDEgMTYuMzM3MyAzNS44Njc3IDE2LjIwOTcgMzUuOTIwNUMxNi4wODIxIDM1Ljk3MzMgMTUuOTQ1MyAzNi4wMDAzIDE1LjgwNzIgMzUuOTk5OUgxMlYzMi4xOTI4QzExLjk5OTYgMzIuMDU0NyAxMi4wMjY2IDMxLjkxNzkgMTIuMDc5NCAzMS43OTAzQzEyLjEzMjIgMzEuNjYyNyAxMi4yMDk4IDMxLjU0NjggMTIuMzA3NyAzMS40NDk0TDI2LjgxODEgMTYuOTM5TDMxLjA2MSAyMS4xODE5TDE2LjU1MDYgMzUuNjkyM1pNMzYuODU1IDE1LjM4NzRMMzMuMTgxNSAxOS4wNjA5TDI4LjkzOTEgMTQuODE4TDMyLjYxMjYgMTEuMTQ0NUMzMi42NTU1IDExLjEwMjIgMzIuNzEzNCAxMS4wNzg2IDMyLjc3MzcgMTEuMDc4NkMzMi44MzM5IDExLjA3ODYgMzIuODkxOCAxMS4xMDIyIDMyLjkzNDggMTEuMTQ0NUwzNi44NTUgMTUuMDY1MkMzNi44OTc2IDE1LjEwOCAzNi45MjE1IDE1LjE2NTkgMzYuOTIxNSAxNS4yMjYzQzM2LjkyMTUgMTUuMjg2NyAzNi44OTc2IDE1LjM0NDYgMzYuODU1IDE1LjM4NzRaIiBmaWxsPSIjZmZmIiBzdHJva2U9IiMwMDAiIHN0cm9rZS13aWR0aD0iMi41IiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBwYWludC1vcmRlcj0ic3Ryb2tlIi8+PC9zdmc+') 6 25, crosshair";
const DRAW_CLOSE_DISTANCE_PX = 10;
const DRAW_PREVIEW_RENDER_ORDER = 104;
const DRAW_SAMPLE_DISTANCE_PX = 2;
const MAX_CLICK_MOVEMENT_PX = 5;
const POINT_POSITION_EPSILON_SQ = 1e-12;
const EDITED_MASK_FILL_OPACITY = 0.2;
const SELECTED_MASK_CONTOUR_WIDTH_INCREASE = 2;
const isRenderedAfter = (mask, other) => {
  const fillRenderOrderDifference = mask.fill.renderOrder - other.fill.renderOrder;
  if (fillRenderOrderDifference !== 0) {
    return fillRenderOrderDifference > 0;
  }
  return mask.fill.id > other.fill.id;
};
const getTopRenderedMask = (masks) => {
  let topMask;
  for (const mask of masks) {
    if (!topMask || isRenderedAfter(mask, topMask)) {
      topMask = mask;
    }
  }
  return topMask;
};
const isolateSelectedMask = (masks, selectedMask) => {
  const previousVisibility = /* @__PURE__ */ new Map();
  for (const mask of masks) {
    if (mask === selectedMask) {
      continue;
    }
    previousVisibility.set(mask, mask.object3d.visible);
    mask.object3d.visible = false;
  }
  return previousVisibility;
};
const restoreMaskVisibility = (previousVisibility) => {
  previousVisibility.forEach((visible, mask) => {
    mask.object3d.visible = visible;
  });
  previousVisibility.clear();
};
class EditMask extends ViewTool {
  constructor(view) {
    super(view);
    this.originalHoverComponents = /* @__PURE__ */ new Map();
    this.originalRaycasts = /* @__PURE__ */ new Map();
    this.maskVisibilityBeforeSelection = /* @__PURE__ */ new Map();
    this.editMode = "draw";
    this.drawRaycaster = new Raycaster();
    this.hasPendingChanges = false;
    this.deformationRadii = { ...DEFAULT_MASK_DEFORMATION_RADII };
    this.sessionUndoStack = new UndoStack(view);
    this.onAdd = this.onAdd.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.onViewportAdded = this.onViewportAdded.bind(this);
    this.onViewportRemoved = this.onViewportRemoved.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onDrawPointerMove = this.onDrawPointerMove.bind(this);
    this.onDrawGestureCancelled = this.onDrawGestureCancelled.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onModeChanged = this.onModeChanged.bind(this);
    this.onTransformStart = this.onTransformStart.bind(this);
    this.onTransform = this.onTransform.bind(this);
    this.onTransformEnd = this.onTransformEnd.bind(this);
    this.onUndoRedo = this.onUndoRedo.bind(this);
  }
  activate() {
    App.Instance().mouseController.addListener(
      "pointerup",
      this.onPointerUp,
      document,
      "left",
      EventPriority.Tool + 5
    );
    App.Instance().keyboardController.addListener(
      "keydown",
      this.onUndoRedo,
      "KeyZ"
    );
    for (const viewport of this.hostObject.getViewports()) {
      this.addViewportListener(viewport);
    }
    this.hostObject.addEventListener("add", this.onAdd);
    this.hostObject.addEventListener("delete", this.onDelete);
    this.hostObject.addEventListener("viewportAdded", this.onViewportAdded);
    this.hostObject.addEventListener("viewportRemoved", this.onViewportRemoved);
    this.hostObject.addEventListener("transformStart", this.onTransformStart);
    this.hostObject.addEventListener("transform", this.onTransform);
    this.hostObject.addEventListener("transformEnd", this.onTransformEnd);
    this.hostObject.addEventListener("modeChanged", this.onModeChanged);
    for (const layer of this.hostObject.getLayers()) {
      this.makeMasksEditable(layer.getObjects(true));
    }
    this.resumeSession();
    this.emitMaskEditState();
  }
  deactivate() {
    this.cancelDrawStroke();
    App.Instance().mouseController.removeListener(
      "pointerup",
      this.onPointerUp,
      document,
      "left"
    );
    App.Instance().keyboardController.removeListener(
      "keydown",
      this.onUndoRedo,
      "KeyZ"
    );
    this.pointerDownGesture = void 0;
    for (const viewport of this.hostObject.getViewports()) {
      this.removeViewportListener(viewport);
    }
    this.hostObject.removeEventListener("add", this.onAdd);
    this.hostObject.removeEventListener("delete", this.onDelete);
    this.hostObject.removeEventListener("viewportAdded", this.onViewportAdded);
    this.hostObject.removeEventListener("viewportRemoved", this.onViewportRemoved);
    this.hostObject.removeEventListener("transformStart", this.onTransformStart);
    this.hostObject.removeEventListener("transform", this.onTransform);
    this.hostObject.removeEventListener("transformEnd", this.onTransformEnd);
    this.hostObject.removeEventListener("modeChanged", this.onModeChanged);
    this.setHoveredMask();
    this.suspendSession();
    for (const mask of [...this.originalRaycasts.keys()]) {
      this.restoreMask(mask, true);
    }
    this.hostObject.dispatchEvent({ type: "render" });
  }
  onModeChanged() {
    this.syncDrawInputViewport();
  }
  onAdd(event) {
    this.makeMasksEditable(event.entities);
  }
  onDelete(event) {
    this.forEachMask(event.entities, (mask) => this.restoreMask(mask));
  }
  onViewportAdded(event) {
    this.addViewportListener(event.viewport);
  }
  onViewportRemoved(event) {
    this.pointerDownGesture = void 0;
    if (this.drawStroke?.viewport === event.viewport) {
      this.cancelDrawStroke();
    }
    this.setHoveredMask();
    if (event.viewport === this.selectedViewport) {
      this.discardSessionChanges();
      this.clearSelection();
    }
    this.removeViewportListener(event.viewport);
  }
  setMaskContourEditStore(store) {
    this.maskContourEditStore = store;
  }
  setDeformationRadii(normalMovementRadiusMm, affectedRadiusMm) {
    this.deformationRadii = validateMaskDeformationRadii(
      normalMovementRadiusMm,
      affectedRadiusMm
    );
    this.selectedMask?.setVertexDeformationRadii(this.deformationRadii);
    if (this.selectedMask) {
      this.hostObject.dispatchEvent({ type: "render" });
    }
  }
  getDeformationRadii() {
    return { ...this.deformationRadii };
  }
  onTransformStart(event) {
    this.deformationStartPoints = void 0;
    this.deformationStartUserDefinedVertices = void 0;
    const selectedMask = this.selectedMask;
    const handle = event.entities.find(
      (entity) => entity instanceof RoundHandle && entity.parent === selectedMask
    );
    if (selectedMask && handle instanceof RoundHandle) {
      const points = selectedMask.getEditablePoints();
      const userDefinedVertices = this.getCurrentUserDefinedVertices(selectedMask);
      if (selectedMask.beginVertexDeformation(handle, this.deformationRadii)) {
        this.deformationStartPoints = points;
        this.deformationStartUserDefinedVertices = userDefinedVertices;
      }
    }
  }
  onTransform(event) {
    const selectedMask = this.selectedMask;
    if (!selectedMask || !event.entities.some(
      (entity) => entity instanceof RoundHandle && entity.parent === selectedMask
    )) {
      return;
    }
    this.hasPendingChanges = true;
    this.emitMaskEditState();
  }
  onTransformEnd(event) {
    const selectedMask = this.selectedMask;
    const previousPoints = this.deformationStartPoints;
    const previousUserDefinedVertices = this.deformationStartUserDefinedVertices;
    this.deformationStartPoints = void 0;
    this.deformationStartUserDefinedVertices = void 0;
    if (selectedMask && event.entities.some(
      (entity) => entity instanceof RoundHandle && entity.parent === selectedMask
    )) {
      const change = selectedMask.endVertexDeformation();
      if (change?.changed) {
        const points = selectedMask.getEditablePoints();
        const edit = this.maskContourEditStore?.getMaskContourEdit(
          selectedMask.contourEditID
        );
        if (change.isContourDrag || points.length !== edit?.initialVertices.length) {
          this.maskContourEditStore?.restoreMaskContourUserDefinedVertices(
            selectedMask.contourEditID,
            new Map(points.map((point, index) => [index, {
              x: point.x,
              y: point.y,
              z: point.z
            }]))
          );
        } else {
          this.maskContourEditStore?.setMaskContourUserDefinedVertex(
            selectedMask.contourEditID,
            change.selectedIndex,
            change.point
          );
        }
        if (previousPoints && previousUserDefinedVertices) {
          this.sessionUndoStack.add(this.createContourChangeCommand(
            selectedMask,
            previousPoints,
            selectedMask.getEditablePoints(),
            previousUserDefinedVertices,
            this.getCurrentUserDefinedVertices(selectedMask)
          ));
        }
      }
      this.updatePendingChanges();
    }
  }
  onUndoRedo(event) {
    if (!event.ctrlKey && !event.metaKey) {
      return;
    }
    event.preventDefault();
    if (this.drawStroke) {
      this.cancelDrawStroke();
      this.hostObject.dispatchEvent({ type: "render" });
      return;
    }
    const undoStack = this.selectedMask ? this.sessionUndoStack : this.hostObject.undoStack;
    if (event.shiftKey) {
      undoStack.redo();
    } else {
      undoStack.undo();
    }
    if (this.selectedMask) {
      this.updatePendingChanges();
    }
  }
  addViewportListener(viewport) {
    App.Instance().mouseController.addListener(
      "pointermove",
      this.onPointerMove,
      viewport.getDomElement(),
      EventPriority.Tool + 5
    );
    App.Instance().mouseController.addListener(
      "pointerleave",
      this.onPointerLeave,
      viewport.getDomElement(),
      EventPriority.Tool + 5
    );
    App.Instance().mouseController.addListener(
      "pointerdown",
      this.onPointerDown,
      viewport.getDomElement(),
      "left",
      EventPriority.Tool + 5
    );
  }
  removeViewportListener(viewport) {
    App.Instance().mouseController.removeListener(
      "pointermove",
      this.onPointerMove,
      viewport.getDomElement()
    );
    App.Instance().mouseController.removeListener(
      "pointerleave",
      this.onPointerLeave,
      viewport.getDomElement()
    );
    App.Instance().mouseController.removeListener(
      "pointerdown",
      this.onPointerDown,
      viewport.getDomElement(),
      "left"
    );
  }
  onPointerMove() {
    if (this.editMode === "draw" && this.selectedMask) {
      if (this.drawInputViewport) {
        this.drawInputViewport.getDomElement().style.cursor = DRAW_CURSOR;
      }
      this.setHoveredMask();
      return;
    }
    if (this.selectedMask) {
      if (this.selectedMask.previewDeformationForEntity(
        this.hostObject.raycastController.getObject()
      )) {
        this.hostObject.dispatchEvent({ type: "render" });
      }
      this.setHoveredMask();
      return;
    }
    this.setHoveredMask(
      this.getTopRenderedMaskAtPointer()
    );
  }
  onPointerLeave() {
    if (this.drawStroke) {
      return;
    }
    if (this.selectedMask?.previewDeformationForEntity()) {
      this.hostObject.dispatchEvent({ type: "render" });
    }
    this.setHoveredMask();
  }
  onPointerDown(event) {
    this.pointerDownGesture = void 0;
    if (this.editMode === "draw" && this.selectedMask) {
      this.startDrawStroke(event);
      return;
    }
    const entity = this.hostObject.raycastController.getObject();
    if (entity instanceof RoundHandle && entity.parent === this.selectedMask) {
      return;
    }
    this.pointerDownGesture = {
      clientX: event.clientX,
      clientY: event.clientY,
      mask: this.getTopRenderedMaskAtPointer()
    };
  }
  onPointerUp(event) {
    if (this.drawStroke) {
      this.finishDrawStroke(event);
      return;
    }
    const gesture = this.pointerDownGesture;
    this.pointerDownGesture = void 0;
    if (!gesture) {
      return;
    }
    const distance = Math.hypot(
      event.clientX - gesture.clientX,
      event.clientY - gesture.clientY
    );
    if (distance > MAX_CLICK_MOVEMENT_PX) {
      return;
    }
    const mask = gesture.mask;
    if (!mask) {
      if (this.hasPendingChanges) {
        return;
      }
      this.clearSelection();
      this.hostObject.dispatchEvent({ type: "render" });
      return;
    }
    this.selectMask(mask);
  }
  startDrawStroke(event) {
    const mask = this.selectedMask;
    const viewport = this.selectedViewport;
    if (!mask || !viewport || event.currentTarget && event.currentTarget !== viewport.getDomElement()) {
      return;
    }
    const point = this.getDrawPoint(event, mask, viewport);
    if (!point) {
      return;
    }
    this.cancelDrawStroke();
    const preview = new Polyline({
      color: mask.object3d.material.color.getHex(),
      opacity: 1,
      points: [point, point],
      renderOrder: DRAW_PREVIEW_RENDER_ORDER,
      width: mask.object3d.material.linewidth + SELECTED_MASK_CONTOUR_WIDTH_INCREASE
    }, Line2);
    preview.object3d.material.depthTest = false;
    preview.object3d.material.depthWrite = false;
    preview.object3d.material.transparent = true;
    preview.object3d.layers.mask = mask.object3d.layers.mask;
    preview.update();
    mask.add(preview);
    this.drawStroke = {
      captureElement: viewport.getDomElement(),
      clientPoints: [new Vector2(event.clientX, event.clientY)],
      mask,
      points: [point],
      previousPoints: mask.getEditablePoints(),
      previousUserDefinedVertices: this.getCurrentUserDefinedVertices(mask),
      preview,
      pointerID: Number.isFinite(event.pointerId) ? event.pointerId : void 0,
      startedInsideMask: isPointInsideClosedContour(
        point,
        mask.getPoints().slice(0, -1)
      ),
      viewport
    };
    const stroke = this.drawStroke;
    stroke.captureElement.addEventListener(
      "pointercancel",
      this.onDrawGestureCancelled
    );
    stroke.captureElement.addEventListener(
      "lostpointercapture",
      this.onDrawGestureCancelled
    );
    if (typeof window !== "undefined") {
      window.addEventListener("blur", this.onDrawGestureCancelled);
    }
    if (stroke.pointerID !== void 0 && stroke.captureElement.setPointerCapture) {
      try {
        stroke.captureElement.setPointerCapture(stroke.pointerID);
      } catch {
      }
    }
    App.Instance().mouseController.addListener(
      "pointermove",
      this.onDrawPointerMove,
      document,
      EventPriority.Tool + 5
    );
    event.preventDefault();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  onDrawPointerMove(event) {
    this.appendDrawPoint(event);
  }
  onDrawGestureCancelled(event) {
    const stroke = this.drawStroke;
    if (!stroke) {
      return;
    }
    const eventPointerID = event.pointerId;
    if (Number.isFinite(eventPointerID) && stroke.pointerID !== void 0 && eventPointerID !== stroke.pointerID) {
      return;
    }
    this.cancelDrawStroke();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  appendDrawPoint(event, force = false) {
    const stroke = this.drawStroke;
    if (!stroke) {
      return;
    }
    const clientPoint = new Vector2(event.clientX, event.clientY);
    if (!force && clientPoint.distanceTo(stroke.clientPoints.at(-1)) < DRAW_SAMPLE_DISTANCE_PX) {
      return;
    }
    const point = this.getDrawPoint(event, stroke.mask, stroke.viewport);
    if (!point) {
      return;
    }
    if (point.distanceToSquared(stroke.points.at(-1)) <= POINT_POSITION_EPSILON_SQ) {
      return;
    }
    stroke.clientPoints.push(clientPoint);
    stroke.points.push(point);
    stroke.preview.setPoints(stroke.points);
    this.hostObject.dispatchEvent({ type: "render" });
  }
  finishDrawStroke(event) {
    this.appendDrawPoint(event, true);
    const stroke = this.drawStroke;
    if (!stroke) {
      return;
    }
    const firstClientPoint = stroke.clientPoints[0];
    const pointerUpClientPoint = new Vector2(event.clientX, event.clientY);
    const maximumDistanceFromStart = stroke.clientPoints.reduce(
      (distance, point) => Math.max(
        distance,
        point.distanceTo(firstClientPoint)
      ),
      firstClientPoint.distanceTo(pointerUpClientPoint)
    );
    if (maximumDistanceFromStart <= MAX_CLICK_MOVEMENT_PX) {
      this.disposeDrawStroke(stroke);
      if (!stroke.startedInsideMask && !this.hasPendingChanges) {
        this.clearSelection();
      }
      this.hostObject.dispatchEvent({ type: "render" });
      return;
    }
    const result = createContourFromDrawStroke({
      closureDistance: DRAW_CLOSE_DISTANCE_PX,
      closureStroke: stroke.clientPoints,
      contour: stroke.mask.getPoints().slice(0, -1),
      isClosed: stroke.clientPoints.length > 2 && firstClientPoint.distanceTo(pointerUpClientPoint) <= DRAW_CLOSE_DISTANCE_PX,
      mapContourPosition: (position) => stroke.mask.getEditableContourPosition(position),
      retainedContour: stroke.previousPoints,
      smoothingIterations: stroke.mask.getContourSmoothingIterations(),
      stroke: stroke.points
    });
    this.disposeDrawStroke(stroke);
    if (!result) {
      this.hostObject.dispatchEvent({ type: "render" });
      return;
    }
    this.applyDrawnContour(stroke, result.points);
  }
  applyDrawnContour(stroke, points) {
    const nextUserDefinedVertices = new Map(points.map((point, index) => [
      index,
      { x: point.x, y: point.y, z: point.z }
    ]));
    stroke.mask.restoreEditablePoints(points);
    this.maskContourEditStore?.restoreMaskContourUserDefinedVertices(
      stroke.mask.contourEditID,
      nextUserDefinedVertices
    );
    const nextPoints = stroke.mask.getEditablePoints();
    if (this.arePointSetsEqual(stroke.previousPoints, nextPoints) && this.areUserDefinedVertexSetsEqual(
      stroke.previousUserDefinedVertices,
      nextUserDefinedVertices
    )) {
      this.maskContourEditStore?.restoreMaskContourUserDefinedVertices(
        stroke.mask.contourEditID,
        stroke.previousUserDefinedVertices
      );
      this.updatePendingChanges();
      this.hostObject.dispatchEvent({ type: "render" });
      return;
    }
    this.sessionUndoStack.add(this.createContourChangeCommand(
      stroke.mask,
      stroke.previousPoints,
      nextPoints,
      stroke.previousUserDefinedVertices,
      nextUserDefinedVertices
    ));
    this.updatePendingChanges();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  getDrawPoint(event, mask, viewport) {
    const bounds = viewport.getDomElement().getBoundingClientRect();
    if (!bounds.width || !bounds.height) {
      return;
    }
    const pointer = new Vector2(
      (event.clientX - bounds.left) / bounds.width * 2 - 1,
      -(event.clientY - bounds.top) / bounds.height * 2 + 1
    );
    this.drawRaycaster.setFromCamera(pointer, viewport.camera);
    mask.object3d.updateWorldMatrix(true, false);
    const ray = this.drawRaycaster.ray.clone().applyMatrix4(
      mask.object3d.matrixWorld.clone().invert()
    );
    if (Math.abs(ray.direction.z) <= Number.EPSILON) {
      return;
    }
    const contourZ = mask.getEditablePoints()[0]?.z ?? 0;
    return ray.at((contourZ - ray.origin.z) / ray.direction.z, new Vector3());
  }
  cancelDrawStroke() {
    if (this.drawStroke) {
      this.disposeDrawStroke(this.drawStroke);
    }
  }
  disposeDrawStroke(stroke) {
    stroke.captureElement.removeEventListener(
      "pointercancel",
      this.onDrawGestureCancelled
    );
    stroke.captureElement.removeEventListener(
      "lostpointercapture",
      this.onDrawGestureCancelled
    );
    if (typeof window !== "undefined") {
      window.removeEventListener("blur", this.onDrawGestureCancelled);
    }
    App.Instance().mouseController.removeListener(
      "pointermove",
      this.onDrawPointerMove,
      document
    );
    if (this.drawStroke === stroke) {
      this.drawStroke = void 0;
    }
    if (stroke.pointerID !== void 0 && stroke.captureElement.hasPointerCapture?.(stroke.pointerID)) {
      stroke.captureElement.releasePointerCapture(stroke.pointerID);
    }
    stroke.mask.delete(stroke.preview);
    stroke.preview.dispose();
  }
  getTopRenderedMaskAtPointer() {
    if (!this.hostObject.getActiveViewport()) {
      return;
    }
    const intersectedMasks = [];
    for (const mask of this.originalRaycasts.keys()) {
      if (!this.isVisible(mask)) {
        continue;
      }
      for (const intersection of this.hostObject.raycastController.raycastObject(mask)) {
        if (intersection.raycast.entity === mask) {
          intersectedMasks.push(mask);
          break;
        }
      }
    }
    return getTopRenderedMask(intersectedMasks);
  }
  setHoveredMask(mask) {
    if (this.hoveredMask === mask) {
      return;
    }
    this.hoveredMaskHighlighter?.unHighlight();
    this.hoveredMask = mask;
    this.hoveredMaskHighlighter = mask ? new EditMaskHighlighter(mask) : void 0;
    this.hoveredMaskHighlighter?.highlight();
    this.hostObject.dispatchEvent({
      type: "hoverMask",
      localizationID: mask?.id,
      meta: mask?.meta
    });
    this.hostObject.dispatchEvent({ type: "render" });
  }
  isVisible(mask) {
    const layer = mask.getLayer();
    if (!layer || layer.getView() !== this.hostObject || layer.getGroup().parent !== this.hostObject.scene) {
      return false;
    }
    const layerGroup = layer.getGroup();
    let object = mask.object3d;
    while (object) {
      if (!object.visible) {
        return false;
      }
      if (object === layerGroup) {
        return true;
      }
      object = object.parent;
    }
    return false;
  }
  selectMask(mask) {
    if (this.selectedMask === mask) {
      return;
    }
    if (this.hasPendingChanges) {
      return;
    }
    this.setHoveredMask();
    this.clearSelection();
    this.selectedMask = mask;
    this.selectedMaskFillOpacity = mask?.fill.material.opacity;
    this.selectedMaskPoints = mask?.getEditablePoints();
    this.selectedViewport = mask ? this.hostObject.getActiveViewport() : void 0;
    if (mask) {
      this.showSelectedMaskFill();
      const edit = this.maskContourEditStore?.initializeMaskContourEdit(
        mask.contourEditID,
        mask.getInitialEditablePoints()
      );
      this.selectedMaskUserDefinedVertices = edit?.userDefinedVertices ?? /* @__PURE__ */ new Map();
      this.maskVisibilityBeforeSelection = isolateSelectedMask(
        this.originalRaycasts.keys(),
        mask
      );
      mask.setVertexDeformationRadii(this.deformationRadii);
    }
    this.syncSelectedMaskEditMode();
    this.emitMaskEditState();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  suspendSession() {
    const selectedMask = this.selectedMask;
    if (!selectedMask) {
      return;
    }
    this.cancelIncompleteDeformation();
    if (!this.hasPendingChanges) {
      this.clearSelection();
      return;
    }
    this.suspendedSession = {
      points: selectedMask.getEditablePoints()
    };
    if (this.selectedMaskPoints) {
      selectedMask.restoreEditablePoints(this.selectedMaskPoints);
    }
    selectedMask.hideVertices();
    selectedMask.hideEditContourOverlay();
    this.restoreSelectedMaskFill();
    this.syncDrawInputViewport();
    this.emitMaskEditState();
    restoreMaskVisibility(this.maskVisibilityBeforeSelection);
  }
  resumeSession() {
    const selectedMask = this.selectedMask;
    const suspendedSession = this.suspendedSession;
    if (!suspendedSession) {
      return;
    }
    if (!selectedMask || !this.originalRaycasts.has(selectedMask)) {
      this.clearSelection();
      return;
    }
    selectedMask.restoreEditablePoints(suspendedSession.points);
    this.suspendedSession = void 0;
    this.showSelectedMaskFill();
    const viewports = [...this.hostObject.getViewports()];
    if (!this.selectedViewport || !viewports.includes(this.selectedViewport)) {
      this.selectedViewport = this.hostObject.getActiveViewport() ?? viewports[0];
    }
    selectedMask.setVertexDeformationRadii(this.deformationRadii);
    this.syncSelectedMaskEditMode();
    this.updatePendingChanges();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  cancelIncompleteDeformation() {
    const selectedMask = this.selectedMask;
    const previousPoints = this.deformationStartPoints;
    this.deformationStartPoints = void 0;
    this.deformationStartUserDefinedVertices = void 0;
    selectedMask?.endVertexDeformation();
    if (selectedMask && previousPoints) {
      selectedMask.restoreEditablePoints(previousPoints);
    }
    this.updatePendingChanges();
  }
  clearSelection() {
    const selectedMask = this.selectedMask;
    this.cancelDrawStroke();
    this.setHoveredMask();
    selectedMask?.endVertexDeformation();
    selectedMask?.hideVertices();
    selectedMask?.hideEditContourOverlay();
    this.restoreSelectedMaskFill();
    this.sessionUndoStack.clear();
    this.deformationStartPoints = void 0;
    this.deformationStartUserDefinedVertices = void 0;
    this.selectedMask = void 0;
    this.selectedMaskFillOpacity = void 0;
    this.selectedMaskPoints = void 0;
    this.selectedMaskUserDefinedVertices = void 0;
    this.selectedViewport = void 0;
    this.suspendedSession = void 0;
    this.syncDrawInputViewport();
    this.hasPendingChanges = false;
    this.emitMaskEditState();
    restoreMaskVisibility(this.maskVisibilityBeforeSelection);
  }
  showSelectedMaskFill() {
    if (this.selectedMask) {
      this.selectedMask.fill.material.opacity = EDITED_MASK_FILL_OPACITY;
    }
  }
  restoreSelectedMaskFill() {
    if (this.selectedMask && this.selectedMaskFillOpacity !== void 0) {
      this.selectedMask.fill.material.opacity = this.selectedMaskFillOpacity;
    }
  }
  applyChanges() {
    this.cancelDrawStroke();
    const selectedMask = this.selectedMask;
    const previous = this.selectedMaskPoints;
    const previousUserDefinedVertices = this.selectedMaskUserDefinedVertices ?? /* @__PURE__ */ new Map();
    const changed = selectedMask && previous ? !this.arePointSetsEqual(selectedMask.getEditablePoints(), previous) || !this.areUserDefinedVertexSetsEqual(
      this.getCurrentUserDefinedVertices(selectedMask),
      previousUserDefinedVertices
    ) : false;
    const sessionCommands = this.getAppliedSessionCommands();
    if (changed && selectedMask && previous && sessionCommands.length === 0) {
      sessionCommands.push(this.createContourChangeCommand(
        selectedMask,
        previous,
        selectedMask.getEditablePoints(),
        previousUserDefinedVertices,
        this.getCurrentUserDefinedVertices(selectedMask)
      ));
    }
    if (changed && sessionCommands.length > 0) {
      this.maskContourEditStore?.commitMaskContourEdit(
        selectedMask.contourEditID,
        selectedMask.getEditablePoints().map((point) => ({
          x: point.x,
          y: point.y,
          z: point.z
        }))
      );
      sessionCommands.forEach((command) => command.commit());
      this.hostObject.executeCommand(new MultiCommand(sessionCommands), false);
    }
    this.clearSelection();
    if (changed && selectedMask) {
      this.hostObject.dispatchEvent({
        type: "geometryChanged",
        entity: selectedMask,
        previous
      });
    }
    this.hostObject.dispatchEvent({ type: "render" });
  }
  cancelChanges() {
    this.cancelDrawStroke();
    this.discardSessionChanges();
    this.clearSelection();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  resetChanges() {
    this.cancelDrawStroke();
    const selectedMask = this.selectedMask;
    if (!selectedMask) {
      return;
    }
    const previousPoints = selectedMask.getEditablePoints();
    const previousUserDefinedVertices = this.getCurrentUserDefinedVertices(selectedMask);
    const edit = this.maskContourEditStore?.getMaskContourEdit(
      selectedMask.contourEditID
    );
    const initialPoints = edit?.initialVertices.map(
      (point) => new Vector3(point.x, point.y, point.z)
    ) ?? selectedMask.getInitialEditablePoints();
    selectedMask.restoreEditablePoints(initialPoints);
    selectedMask.clearVertexDeformationPreview();
    this.maskContourEditStore?.resetMaskContourEdit(selectedMask.contourEditID);
    const nextPoints = selectedMask.getEditablePoints();
    const nextUserDefinedVertices = this.getCurrentUserDefinedVertices(selectedMask);
    if (!this.arePointSetsEqual(previousPoints, nextPoints) || !this.areUserDefinedVertexSetsEqual(
      previousUserDefinedVertices,
      nextUserDefinedVertices
    )) {
      this.sessionUndoStack.add(this.createContourChangeCommand(
        selectedMask,
        previousPoints,
        nextPoints,
        previousUserDefinedVertices,
        nextUserDefinedVertices
      ));
    }
    this.updatePendingChanges();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  createContourChangeCommand(mask, previousPoints, nextPoints, previousUserDefinedVertices, nextUserDefinedVertices) {
    const store = this.maskContourEditStore;
    return new MaskContourChangeCommand({
      nextPoints,
      nextUserDefinedVertices,
      previousPoints,
      previousUserDefinedVertices,
      resolveMask: () => this.resolveLiveMask(mask.contourEditID, mask),
      restoreCommittedVertices: store ? (vertices) => store.commitMaskContourEdit(mask.contourEditID, vertices) : void 0,
      restoreUserDefinedVertices: store ? (vertices) => store.restoreMaskContourUserDefinedVertices(
        mask.contourEditID,
        vertices
      ) : void 0
    });
  }
  resolveLiveMask(contourEditID, fallback) {
    if (typeof this.hostObject.getLayers !== "function") {
      return fallback;
    }
    let resolved;
    for (const layer of this.hostObject.getLayers()) {
      for (const entity of layer.getObjects(true)) {
        entity.traverse((child) => {
          if (child instanceof Mask && child.contourEditID === contourEditID) {
            resolved = child;
          }
        });
        if (resolved) {
          return resolved;
        }
      }
    }
    return fallback.getLayer()?.getView() === this.hostObject ? fallback : void 0;
  }
  getAppliedSessionCommands() {
    return this.sessionUndoStack.getCommandList().slice(0, this.sessionUndoStack.getIndex() + 1).filter((command) => command instanceof MaskContourChangeCommand);
  }
  getCurrentUserDefinedVertices(mask) {
    return this.maskContourEditStore?.getMaskContourEdit(
      mask.contourEditID
    )?.userDefinedVertices ?? /* @__PURE__ */ new Map();
  }
  discardSessionChanges() {
    const selectedMask = this.selectedMask;
    const selectedMaskPoints = this.selectedMaskPoints;
    const selectedUserDefinedVertices = this.selectedMaskUserDefinedVertices;
    this.sessionUndoStack.undoAll();
    if (selectedMask && selectedMaskPoints && !this.arePointSetsEqual(selectedMask.getEditablePoints(), selectedMaskPoints)) {
      selectedMask.restoreEditablePoints(selectedMaskPoints);
    }
    if (selectedMask && selectedUserDefinedVertices) {
      this.maskContourEditStore?.restoreMaskContourUserDefinedVertices(
        selectedMask.contourEditID,
        selectedUserDefinedVertices
      );
    }
    this.sessionUndoStack.clear();
  }
  updatePendingChanges() {
    const selectedMask = this.selectedMask;
    const selectedMaskPoints = this.selectedMaskPoints;
    const selectedUserDefinedVertices = this.selectedMaskUserDefinedVertices;
    if (!selectedMask || !selectedMaskPoints || !selectedUserDefinedVertices) {
      this.hasPendingChanges = false;
      this.emitMaskEditState();
      return;
    }
    this.hasPendingChanges = !this.arePointSetsEqual(selectedMask.getEditablePoints(), selectedMaskPoints) || !this.areUserDefinedVertexSetsEqual(
      this.getCurrentUserDefinedVertices(selectedMask),
      selectedUserDefinedVertices
    );
    this.emitMaskEditState();
  }
  arePointSetsEqual(first, second) {
    return first.length === second.length && first.every(
      (point, index) => point.distanceToSquared(second[index]) <= POINT_POSITION_EPSILON_SQ
    );
  }
  areUserDefinedVertexSetsEqual(first, second) {
    return first.size === second.size && [...first].every(([index, point]) => {
      const otherPoint = second.get(index);
      return !!otherPoint && (point.x - otherPoint.x) ** 2 + (point.y - otherPoint.y) ** 2 + (point.z - otherPoint.z) ** 2 <= POINT_POSITION_EPSILON_SQ;
    });
  }
  getEditState() {
    const selectedMask = this.suspendedSession ? void 0 : this.selectedMask;
    return {
      canReset: selectedMask?.hasEditablePointChanges() ?? false,
      hasPendingChanges: !!selectedMask && this.hasPendingChanges,
      isMaskSelected: !!selectedMask
    };
  }
  emitMaskEditState() {
    this.hostObject.dispatchEvent({
      type: "maskEditStateChanged",
      ...this.getEditState()
    });
  }
  setEditMode(mode) {
    if (this.editMode === mode) {
      return;
    }
    this.cancelDrawStroke();
    this.editMode = mode;
    this.syncSelectedMaskEditMode();
    this.hostObject.dispatchEvent({ type: "render" });
  }
  syncSelectedMaskEditMode() {
    const selectedMask = this.selectedMask;
    if (selectedMask) {
      selectedMask.clearVertexDeformationPreview();
      if (this.editMode === "draw") {
        selectedMask.hideVertices();
      } else {
        selectedMask.showVertices(this.editMode === "dots");
      }
      this.syncSelectedMaskContourOverlay();
    }
    this.syncDrawInputViewport();
  }
  syncDrawInputViewport() {
    const nextViewport = !this.suspendedSession && this.editMode === "draw" && this.selectedMask ? this.selectedViewport : void 0;
    if (this.drawInputViewport && this.drawInputViewport !== nextViewport) {
      const previousElement = this.drawInputViewport.getDomElement();
      const previousListeners = App.Instance().mouseController.listeners.get(
        previousElement
      );
      previousListeners?.enableButton(
        "pointerdown",
        "left"
      );
      previousListeners?.enableMove();
      previousElement.style.cursor = "default";
    }
    if (nextViewport) {
      const nextElement = nextViewport.getDomElement();
      const nextListeners = App.Instance().mouseController.listeners.get(nextElement);
      nextListeners?.disableButtonExcept(
        "pointerdown",
        this.onPointerDown,
        "left"
      );
      nextListeners?.disableMoveExcept(this.onPointerMove);
      nextElement.style.cursor = DRAW_CURSOR;
    }
    this.drawInputViewport = nextViewport;
  }
  makeMasksEditable(entities) {
    this.forEachMask(entities, (mask) => {
      if (this.originalRaycasts.has(mask)) {
        return;
      }
      const edit = this.maskContourEditStore?.initializeMaskContourEdit(
        mask.contourEditID,
        mask.getInitialEditablePoints()
      );
      if (edit && !this.arePointSetsEqual(
        mask.getEditablePoints(),
        edit.committedVertices.map((point) => new Vector3(point.x, point.y, point.z))
      )) {
        mask.restoreEditablePoints(edit.committedVertices.map(
          (point) => new Vector3(point.x, point.y, point.z)
        ));
      }
      const hover = mask.getComponent(HoverComponent);
      if (hover?.isHovered()) {
        hover.unhover();
      }
      this.originalHoverComponents.set(mask, hover);
      mask.removeComponent(HoverComponent);
      this.originalRaycasts.set(mask, mask.getComponent(Raycast));
      mask.setComponent(
        EditableMaskRaycastComponent,
        new EditableMaskRaycastComponent(mask)
      );
      if (this.selectedMask && mask !== this.selectedMask) {
        this.maskVisibilityBeforeSelection.set(mask, mask.object3d.visible);
        mask.object3d.visible = false;
      }
    });
  }
  syncSelectedMaskContourOverlay() {
    const selectedMask = this.selectedMask;
    if (!selectedMask) {
      return;
    }
    if (this.editMode === "dots") {
      selectedMask.hideEditContourOverlay();
    } else {
      selectedMask.showEditContourOverlay(
        selectedMask.object3d.material.linewidth + SELECTED_MASK_CONTOUR_WIDTH_INCREASE
      );
    }
  }
  restoreMask(mask, preserveSession = false) {
    if (!this.originalRaycasts.has(mask)) {
      return;
    }
    if (this.selectedMask === mask && !preserveSession) {
      this.discardSessionChanges();
      this.clearSelection();
    }
    if (this.hoveredMask === mask) {
      this.setHoveredMask();
    }
    const visible = this.maskVisibilityBeforeSelection.get(mask);
    if (visible !== void 0) {
      mask.object3d.visible = visible;
    }
    this.maskVisibilityBeforeSelection.delete(mask);
    mask.hideEditContourOverlay();
    const raycast = this.originalRaycasts.get(mask);
    if (raycast) {
      mask.setComponent(Raycast, raycast);
    } else {
      mask.removeComponent(Raycast);
    }
    const hover = this.originalHoverComponents.get(mask);
    if (hover) {
      mask.setComponent(HoverComponent, hover);
    } else {
      mask.removeComponent(HoverComponent);
    }
    this.originalHoverComponents.delete(mask);
    this.originalRaycasts.delete(mask);
  }
  forEachMask(entities, callback) {
    entities.forEach((entity) => {
      entity.traverse((child) => {
        if (child instanceof Mask) {
          callback(child);
        }
      });
    });
  }
}

const MatrixViewTools = tuple(ActiveViewSwitcher, UndoRedo, HoverMask, PBLVisibilityTool, InvertColor, EditMask);
const _MatrixViewMode = class _MatrixViewMode extends Mode {
};
_MatrixViewMode.except = (tools) => new _MatrixViewMode({ tools: { toolList: tools, include: false } });
let MatrixViewMode = _MatrixViewMode;
const MatrixViewLayerNames = ["image", "roundCorners"];
const MatrixViewViewModes = {
  mainMode: MatrixViewMode.except([EditMask]),
  matrixMode: MatrixViewMode.except([EditMask]),
  fulScreenMode: MatrixViewMode.except([EditMask]),
  contrastBrightnessMode: MatrixViewMode.except([HoverMask, EditMask]),
  sharpnessMode: MatrixViewMode.except([HoverMask, EditMask]),
  contrastBrightnessMultipleMode: MatrixViewMode.except([HoverMask, EditMask]),
  sharpnessMultipleMode: MatrixViewMode.except([HoverMask, EditMask]),
  editMaskMode: MatrixViewMode.except([HoverMask, UndoRedo]),
  printMode: new MatrixViewMode([InvertColor, PBLVisibilityTool])
};
const defaultDomRefs$2 = {
  view: { current: HTML.div({ style: { width: "100px", height: "100px" } }) }
};
const setupMatrixView = (domRefs = defaultDomRefs$2, preserveDrawingBuffer = false) => {
  const view = new MatrixView();
  view.toolController.setTools(MatrixViewTools);
  view.toolController.setModes(MatrixViewViewModes);
  view.setListeners(MatrixViewEventNames);
  MatrixViewLayerNames.forEach((name) => view.addLayer(name));
  view.init({
    container: domRefs.view?.current,
    rendererProps: { antialias: true, preserveDrawingBuffer },
    domRefs
  });
  return view;
};

const SEEventNames = [
  ...coreEventNames,
  ...MatrixViewUIEventNames
];

class SliceController extends ImageControllerBase {
  constructor(view) {
    super(view);
    this.slicePictures = /* @__PURE__ */ new Map();
  }
  dispose() {
    super.dispose();
    this.slicePictures.clear();
  }
  addImage(props, URL, previewURL) {
    const result = super.addImage(props, URL, previewURL);
    const mainViewport = this.view.getViewport("main");
    if (mainViewport) {
      result.container.getComponent(ViewportComponent)?.addViewport(mainViewport);
    }
    return result;
  }
  updatePictures(imageId) {
    if (imageId) {
      this.pictures.get(imageId)?.forEach((picture) => picture.update());
    } else {
      this.pictures.forEach((pictures) => {
        pictures.forEach((picture) => picture.update());
      });
    }
  }
  addSlicePicture(imageID, toothID, picture) {
    const img = picture.imageElement.deref();
    if (!img) return;
    const map = this.slicePictures.get(toothID);
    if (map) {
      if (!map.has(img)) {
        map.set(img, picture);
      }
    } else {
      this.slicePictures.set(toothID, new WeakMap([[img, picture]]));
    }
    this.addPicture(imageID, picture);
  }
  async addMasks(data) {
    await super.addMasks(data);
    const uniqueImages = new Set(data.map((item) => item.imageID));
    uniqueImages.forEach((imageID) => this.updatePictures(imageID));
    this.view.dispatchEvent({ type: "render" });
  }
  hideAnnotations(assetID) {
    assetID = this.view.currentAssetID;
    if (assetID) {
      this.imageContainers.get(assetID)?.hideAnnotations();
    }
  }
  showAnnotations(assetID) {
    assetID = this.view.currentAssetID;
    if (assetID) {
      this.imageContainers.get(assetID)?.showAnnotations();
    }
  }
  deleteAnnotations(assetID) {
    assetID = this.view.currentAssetID;
    if (assetID) {
      const result = this.imageContainers.get(assetID)?.deleteAllAnnotations();
      result && this.view.dispatchEvent({ type: "render" });
      return result;
    }
    return false;
  }
  resetViewOptions(assetID) {
    assetID = this.view.currentAssetID;
    if (assetID) {
      const image = this.imageContainers.get(assetID)?.getImage();
      if (image) {
        const { brightness, contrast, sharpness } = image;
        if (brightness == 0 && contrast == 0 && sharpness == 0) return false;
        const command = new MultiCommand([
          new ContrastBrightnessCommand(image.brightness, image.contrast, 0, 0, image),
          new SharpnessCommand(image.sharpness, 0, image)
        ]);
        this.view.executeCommand(command);
        return true;
      }
    }
    return false;
  }
}

class FitCameraToImageForSliceEditor extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.fit = this.fit.bind(this);
  }
  activate() {
    this.fit();
    this.hostObject.addEventListener("resize", this.fit);
  }
  deactivate() {
    this.hostObject.removeEventListener("resize", this.fit);
  }
  fit() {
    const camera = this.hostObject.cameraController.getOrthographicCamera();
    const size = new Vector3();
    const imageContainer = this.hostObject.view.getLayer("image")?.getObjects()[0];
    if (!(imageContainer instanceof ImageContainer)) {
      return;
    }
    const image = imageContainer.getImage();
    image.object3d.updateWorldMatrix(true, false);
    const x = new Vector3().setFromMatrixColumn(image.object3d.matrixWorld, 0);
    const y = new Vector3().setFromMatrixColumn(image.object3d.matrixWorld, 1);
    const diagonalA = x.clone().add(y);
    const diagonalB = x.clone().sub(y);
    size.set(Math.max(Math.abs(diagonalA.x), Math.abs(diagonalB.x)), Math.max(Math.abs(diagonalA.y), Math.abs(diagonalB.y)), 0);
    const box = image.object3d.geometry.boundingBox.clone();
    box.applyMatrix4(image.object3d.matrixWorld);
    box.getCenter(camera.position);
    const cameraWidth = camera.right - camera.left;
    const cameraHeight = camera.top - camera.bottom;
    camera.zoom = Math.min(cameraHeight / size.y, cameraWidth / size.x);
    camera.updateMatrix();
    camera.updateProjectionMatrix();
    this.view.dispatchEvent({ type: "transformCamera", viewport: this.hostObject });
  }
}

class EraseAnnotations extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
  }
  activate() {
    MIRender.mouseController.addListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    MIRender.mouseController.addListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left");
  }
  deactivate() {
    MIRender.mouseController.removeListener("pointermove", this.onMouseMove, this.hostObject.getDomElement());
    MIRender.mouseController.removeListener("pointerdown", this.onMouseDown, this.hostObject.getDomElement(), "left");
  }
  onMouseMove() {
    const raycasted = this.view.raycastController.getObject();
    if (!raycasted) {
      this.annotation = void 0;
      return;
    }
    if (isAnnotation(raycasted)) {
      this.annotation = raycasted;
    }
  }
  onMouseDown() {
    if (!this.annotation) return;
    const container = this.getContainer();
    if (!container) return;
    container.deleteAnnotation(this.annotation);
    this.view.dispatchEvent({ type: "annotationsUserDelete", entities: [this.annotation], container });
    this.annotation = void 0;
  }
  getContainer() {
    const imageController = this.view.imageController;
    const assetID = imageController.viewportToImageMap.get(this.hostObject);
    if (!assetID) return;
    const container = imageController.imageContainers.get(assetID);
    if (!container) return;
    return container;
  }
}

class LineRaycast extends Raycast {
  constructor(entity) {
    if (!("getSegments" in entity)) {
      throw new Error("LineRaycast can only be used on Line annotations");
    }
    super(entity);
    this.dir = new Vector3();
    this.dir2 = new Vector3();
  }
  isRaycast(raycaster) {
    const segments = this.entity.getSegments();
    for (let i = 0; i < segments.length - 1; i++) {
      const intersection = this.intersectLine(raycaster, segments[i], segments[i + 1]);
      if (intersection) return intersection;
    }
    return false;
  }
  intersectLine(raycaster, start, end) {
    const camera = raycaster.camera;
    const zoom = camera instanceof OrthographicCamera ? camera.zoom : 1;
    const threshold = 5 / zoom;
    if (raycaster.ray.origin.distanceTo(start) < threshold && raycaster.ray.origin.distanceTo(end) < threshold) {
      return [{ distance: raycaster.ray.origin.z, point: raycaster.ray.origin.clone(), object: this.entity.object3d }];
    }
    this.dir.subVectors(end, start);
    const length = this.dir.length();
    this.dir.normalize();
    this.dir2.subVectors(raycaster.ray.origin, start);
    const dot = this.dir.dot(this.dir2);
    const isOnLine = dot > 0 && dot < length;
    if (!isOnLine) return false;
    const perpendicular = new Vector3(-this.dir.y, this.dir.x, 0);
    const distance = perpendicular.dot(this.dir2);
    if (Math.abs(distance) > threshold) return false;
    return [{ distance: raycaster.ray.origin.z, point: raycaster.ray.origin.clone(), object: this.entity.object3d }];
  }
}

class RaycastBehavior extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onModeChanged = this.onModeChanged.bind(this);
  }
  activate() {
    this.hostObject.view.addEventListener("modeChanged", this.onModeChanged);
  }
  deactivate() {
    this.hostObject.view.removeEventListener("modeChanged", this.onModeChanged);
  }
  getContainer() {
    const imageController = this.view.imageController;
    const assetID = imageController.viewportToImageMap.get(this.hostObject);
    if (!assetID) return;
    const container = imageController.imageContainers.get(assetID);
    if (!container) return;
    return container;
  }
  onModeChanged(e) {
    const container = this.getContainer();
    if (!container) return;
    if (e.mode === "eraserMode") {
      [...container.getAnnotations()].forEach((annotation) => {
        annotation.removeComponent(RaycastChildrenOnly);
        annotation.setComponent(LineRaycast);
      });
    } else if (e.previous === "eraserMode") {
      [...container.getAnnotations()].forEach((annotation) => {
        annotation.removeComponent(LineRaycast);
        annotation.setComponent(RaycastChildrenOnly);
      });
    }
  }
}

class DownLoadSliceImage extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.downLoadImage = this.downLoadImage.bind(this);
  }
  activate() {
    this.hostObject.addEventListener("downLoadImage", this.downLoadImage);
  }
  deactivate() {
    this.hostObject.removeEventListener("downLoadImage", this.downLoadImage);
  }
  async downLoadImage(event) {
    const imageController = this.view.imageController;
    const assetID = imageController.viewportToImageMap.get(this.hostObject);
    if (!assetID) return;
    const imageContainer = imageController.imageContainers.get(assetID);
    if (!imageContainer) return;
    const imageData = await toImageData(imageContainer, "sliceEditor", { minWidth: 1800, minHeight: 1e3 });
    downloadImageDataAsPng(imageData, event.fileName || assetID || "image");
  }
}

const SEViewportEventNames = [
  ...viewportEventNames,
  "imageAdded",
  "applyFilter",
  "removeFilter",
  "lookAtBox",
  "downLoadImage"
];
const _MIRenderViewportMode = class _MIRenderViewportMode extends Mode {
};
_MIRenderViewportMode.except = (tools) => new _MIRenderViewportMode({ tools: { toolList: tools, include: false } });
let MIRenderViewportMode = _MIRenderViewportMode;
const MIRenderViewportServiceTools = /* @__PURE__ */ new Set([Render, RaycastBehavior, DownLoadSliceImage]);
const MIRenderViewportTools = tuple(
  Pan2D,
  Zoom2DOnCursor,
  ImageContrastBrightness,
  ImageSharpness,
  FitCameraToImageForSliceEditor,
  Drag,
  Hover,
  DrawRuler,
  DrawArrow,
  DrawAngle,
  EraseAnnotations
);
const SEViewportModes = {
  mainMode: new MIRenderViewportMode([FitCameraToImageForSliceEditor, Drag, Hover, Zoom2DOnCursor, Pan2D]),
  contrastBrightnessMode: new MIRenderViewportMode({ tools: { toolList: [ImageContrastBrightness], include: true } }),
  sharpnessMode: new MIRenderViewportMode({ tools: { toolList: [ImageSharpness], include: true } }),
  rulerMode: new MIRenderViewportMode([DrawRuler]),
  arrowMode: new MIRenderViewportMode([DrawArrow]),
  angleMode: new MIRenderViewportMode([DrawAngle]),
  eraserMode: new MIRenderViewportMode([EraseAnnotations, Hover])
};
const setupSEViewport = (view) => {
  const viewport = view.addViewport("main", HTML.div({ style: { width: "100%", height: "100%" } }), { eventNames: SEViewportEventNames });
  viewport.alpha = 0;
  viewport.toolController.setTools(MIRenderViewportTools);
  viewport.toolController.setModes(SEViewportModes);
  viewport.toolController.setServiceTools(MIRenderViewportServiceTools);
  view.setActiveViewport("main");
  return viewport;
};

class SliceEditor extends View {
  constructor() {
    super();
    this.imageController = new SliceController(this);
    this.raycastController.getNearest = (generator) => [...generator].sort((a, b) => b.raycast.entity.object3d.renderOrder - a.raycast.entity.object3d.renderOrder)[0];
  }
  init(props) {
    super.init(props);
    const viewport = setupSEViewport(this);
    this.domElement.appendChild(viewport.getDomElement());
    viewport.init();
    this.canvas.style.position = "absolute";
    this.domElement.style.containerType = "size";
    this.domElement.style.display = "flex";
    this.domElement.style.justifyContent = "center";
    this.domElement.style.alignItems = "center";
    this.domElement.style.flexWrap = "wrap";
  }
  dispose() {
    super.dispose();
    this.imageController.dispose();
  }
  get currentAssetID() {
    const viewport = this.viewports.main;
    if (!viewport) {
      throw new Error(`viewport not found`);
    }
    return this.imageController.viewportToImageMap.get(viewport);
  }
  getImageContainer(assetID) {
    const container = this.imageController.imageContainers.get(assetID);
    if (!container) {
      throw new Error(`image whit id ${assetID} not found`);
    }
    this.domElement.style.aspectRatio = `${container.width / container.height}`;
    return container;
  }
  showSlice(imageID) {
    const container = this.getImageContainer(imageID);
    const imageLayer = this.getLayer("image");
    if (!imageLayer) {
      console.error(`image layer not found`);
      return;
    }
    imageLayer.clear();
    const viewport = this.viewports.main;
    viewport && this.imageController.viewportToImageMap.set(viewport, imageID);
    imageLayer.add(container);
  }
  hideSlice() {
    const imageLayer = this.getLayer("image");
    if (!imageLayer) {
      console.error(`image layer not found`);
      return;
    }
    imageLayer.clear();
    this.updateCurrentSlice();
  }
  updateCurrentSlice() {
    const assetID = this.currentAssetID;
    if (assetID) {
      this.imageController.updatePictures(assetID);
    }
  }
}

const SEViewTools = tuple(ActiveViewSwitcher, UndoRedo);
const _SliceEditorViewMode = class _SliceEditorViewMode extends Mode {
};
_SliceEditorViewMode.except = (tools) => new _SliceEditorViewMode({ tools: { toolList: tools, include: false } });
let SliceEditorViewMode = _SliceEditorViewMode;
const SELayerNames = ["image"];
const SEViewModes = {
  mainMode: new SliceEditorViewMode(),
  contrastBrightnessMode: SliceEditorViewMode.except([]),
  sharpnessMode: SliceEditorViewMode.except([]),
  rulerMode: SliceEditorViewMode.except([]),
  arrowMode: SliceEditorViewMode.except([]),
  angleMode: SliceEditorViewMode.except([]),
  eraserMode: SliceEditorViewMode.except([])
};
const defaultDomRefs$1 = {
  view: { current: HTML.div({ style: { width: "100px", height: "100px" } }) }
};
const setupSliceEditor = (domRefs = defaultDomRefs$1, preserveDrawingBuffer = false) => {
  const view = new SliceEditor();
  view.toolController.setTools(SEViewTools);
  view.toolController.setModes(SEViewModes);
  view.setListeners(SEEventNames);
  SELayerNames.forEach((name) => view.addLayer(name));
  view.init({
    name: "sliceEditor",
    container: domRefs.view?.current,
    rendererProps: { antialias: true, preserveDrawingBuffer },
    domRefs
  });
  return view;
};

function errorGuard(target, context) {
  const methodName = String(context.name);
  return function replacementMethod(...args) {
    try {
      return target.call(this, ...args);
    } catch (e) {
      console.error(`[${methodName}] ${e}`);
    }
  };
}
function checkForRun(target, context) {
  const methodName = String(context.name);
  return function replacementMethod(...args) {
    if (MIRender.status() === "run") {
      return target.call(this, ...args);
    } else {
      console.error(`[${methodName}] MedicalImageRender is not running`);
    }
  };
}
function errorGuardAsync(target, context) {
  const methodName = String(context.name);
  return async function replacementMethod(...args) {
    try {
      return await target.call(this, ...args);
    } catch (e) {
      console.error(`[${methodName}] ${e}`);
      return Promise.resolve(void 0);
    }
  };
}
function checkForRunAsync(target, context) {
  const methodName = String(context.name);
  return async function replacementMethod(...args) {
    if (MIRender.status() === "run") {
      return await target.call(this, ...args);
    } else {
      console.error(`[${methodName}] MedicalImageRender is not running`);
      return Promise.resolve(void 0);
    }
  };
}

const selectedSlices = async (toothID, state) => {
  const result = {
    Generated: { 0: [], 1: [], 2: [], 3: [] },
    UserUploaded: [],
    UserUploadedMPRPlanes: [],
    PeriapicalSlices: [],
    CariesSlices: []
  };
  const assetIDs = [...await state.DisplaySlices.getAsync(toothID)];
  const slices = await Promise.all(assetIDs.map(async (assetID) => await state.FlatSlices.getAsync(assetID)));
  for (const slice of slices) {
    if (isCBCTGPToothSlice(slice)) {
      result.Generated[slice.orientation].push(slice);
    } else if (isUserUploadedToothSlice(slice)) {
      result.UserUploaded.push(slice);
    } else if (isUserUploadedMPRPlane(slice)) {
      result.UserUploadedMPRPlanes.push(slice);
    } else if (isCBCTToothPathologyCariesSlice(slice)) {
      result.CariesSlices.push(slice);
    } else if (isCBCTToothPathologyPeriapicalLesionSlice(slice)) {
      result.PeriapicalSlices.push(slice);
    }
  }
  return result;
};

const userUploadedSlices = async (toothID, state) => {
  const userUploads = await state.UserUploadedSlices.getAsync(toothID);
  const mprPlanes = await state.UserUploadedMPRPlanes.getAsync(toothID);
  return { userUploads, mprPlanes };
};

const selectCariesSlices = async (toothID, state) => {
  return [...(await (await state.CBCTToothPathologySlices.getAsync(toothID)).getAsync(AssetContent_Generated_CBCT_ToothPathologySlice_PathologyType.Caries)).values()].map((slice) => [...slice.values()]).flat();
};

const selectPeriapicalSlices = async (toothID, state) => {
  return [...(await (await state.CBCTToothPathologySlices.getAsync(toothID)).getAsync(AssetContent_Generated_CBCT_ToothPathologySlice_PathologyType.PeriapicalLesion)).values()].map((slice) => [...slice.values()]).flat();
};

class SlicePicture extends Picture {
  constructor(container, style) {
    if (style instanceof HTMLCanvasElement) {
      super(container, style);
    } else {
      const img = HTML.canvas(style);
      super(container, img);
      container.appendChild(img);
    }
  }
  derefImage() {
    return this.imageElement.deref();
  }
  update() {
    const canvas = this.imageElement.deref();
    if (!canvas) return;
    if (this.imageContainer.getImage().object3d.material.uniforms.dataTexture.value.image) {
      const context = canvas.getContext("2d");
      let width = this.getWidth();
      let height = this.getHeight();
      const annotations = [...this.imageContainer.getAnnotations()];
      if (annotations.length) {
        if (width) width *= 3;
        if (height) height *= 3;
        annotations.forEach((x) => {
          x.object3d.material.linewidth *= 2;
          x.startHandle.size *= 2;
          x.endHandle.size *= 2;
          if ("text" in x && x.text instanceof TextEntity) {
            x.text.object3d.scale.multiplyScalar(2);
          }
        });
      }
      const imageData = toImageData(this.imageContainer, "sliceEditor", { width, height });
      canvas.width = imageData.width;
      canvas.height = imageData.height;
      context.putImageData(imageData, 0, 0);
      canvas.setAttribute("ready", "true");
      canvas.dispatchEvent(new Event("ready"));
      if (annotations.length) {
        annotations.forEach((x) => {
          x.object3d.material.linewidth /= 2;
          x.startHandle.size /= 2;
          x.endHandle.size /= 2;
          if ("text" in x) {
            x.text.object3d.scale.divideScalar(2);
          }
        });
      }
    }
  }
}

class HoverNDF extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onHover = this.onHover.bind(this);
    this.onUnhover = this.onUnhover.bind(this);
  }
  activate() {
    this.hostObject.addEventListener("hoverConditionBox", this.onHover);
    this.hostObject.addEventListener("unhoverConditionBox", this.onUnhover);
  }
  deactivate() {
    this.hostObject.removeEventListener("hoverConditionBox", this.onHover);
    this.hostObject.removeEventListener("unhoverConditionBox", this.onUnhover);
  }
  onHover(event) {
    event.ids.forEach((id) => {
      const box = this.view.imageContainer.get()?.getConditionBox(id);
      if (box) {
        box.getComponent(HoverComponent)?.hover();
      }
    });
    this.view.dispatchEvent({ type: "render" });
  }
  onUnhover(event) {
    event.ids.forEach((id) => {
      const box = this.view.imageContainer.get()?.getConditionBox(id);
      if (box) {
        box.getComponent(HoverComponent)?.unhover();
      }
    });
    this.view.dispatchEvent({ type: "render" });
  }
}

const lookAtBoxAnimated = (camera, box, boxExpansion = 0, duration, render, transform) => {
  const size = new Vector3();
  const boxTemp = new Box3().setFromPoints([box.min.clone(), box.max.clone()]);
  boxTemp.expandByScalar(boxExpansion);
  const targetPosition = new Vector3();
  boxTemp.getCenter(targetPosition);
  boxTemp.getSize(size);
  const cameraWidth = camera.right - camera.left;
  const cameraHeight = camera.top - camera.bottom;
  const targetZoom = Math.min(cameraHeight / size.y, cameraWidth / size.x);
  const startPosition = camera.position.clone();
  const startZoom = camera.zoom;
  const clock = new Clock();
  clock.start();
  const animate = () => {
    const t = clock.getElapsedTime();
    const progress = MathUtils.clamp(t / duration, 0, 1);
    camera.position.lerpVectors(startPosition, targetPosition, progress);
    camera.zoom = MathUtils.lerp(startZoom, targetZoom, progress);
    camera.updateMatrix();
    camera.updateProjectionMatrix();
    render();
    if (t < duration) {
      requestAnimationFrame(animate);
    }
  };
  animate();
};

class FocusToNDF extends ViewportTool {
  constructor(viewport) {
    super(viewport);
    this.onFocus = this.onFocus.bind(this);
    this.onUnfocus = this.onUnfocus.bind(this);
  }
  activate() {
    this.hostObject.addEventListener("focusConditionBox", this.onFocus);
    this.hostObject.addEventListener("unfocusConditionBox", this.onUnfocus);
  }
  deactivate() {
    this.hostObject.removeEventListener("focusConditionBox", this.onFocus);
    this.hostObject.removeEventListener("unfocusConditionBox", this.onUnfocus);
  }
  onFocus(event) {
    const container = this.view.imageContainer.get();
    if (!container) return;
    const id = event.ids[0];
    const conditionBBox = container.getConditionBox(id);
    if (conditionBBox) {
      this.originalBox = container.getImage().object3d.geometry.boundingBox.clone().applyMatrix4(container.getImage().object3d.matrixWorld);
      const size = new Vector3().subVectors(conditionBBox.max, conditionBBox.min);
      const maxSize = Math.max(size.x, size.y, size.z);
      lookAtBoxAnimated(this.hostObject.cameraController.getOrthographicCamera(), conditionBBox, maxSize / 2, 0.2, () => this.view.dispatchEvent({ type: "render" }));
    }
  }
  onUnfocus(event) {
    lookAtBoxAnimated(this.hostObject.cameraController.getOrthographicCamera(), this.originalBox, 0, 0.2, () => this.view.dispatchEvent({ type: "render" }));
    this.view.dispatchEvent({ type: "render" });
  }
}

class NDFRender extends Render {
  constructor(viewport) {
    super(viewport);
    this.render = this.render.bind(this);
  }
  activate() {
    super.activate();
    const mainView = MIRender.getView("main");
    if (mainView) {
      mainView.addEventListener("brightnessContrastChanged", this.render);
      mainView.addEventListener("sharpnessChanged", this.render);
      mainView.addEventListener("toggleInversion", this.render);
      mainView.addEventListener("straightColors", this.render);
      mainView.addEventListener("invertColors", this.render);
    }
  }
  deactivate() {
    super.deactivate();
    const mainView = MIRender.getView("main");
    if (mainView) {
      mainView.removeEventListener("brightnessContrastChanged", this.render);
      mainView.removeEventListener("sharpnessChanged", this.render);
      mainView.removeEventListener("toggleInversion", this.render);
      mainView.removeEventListener("straightColors", this.render);
      mainView.removeEventListener("invertColors", this.render);
    }
  }
  render() {
    this.hostObject.render();
  }
}

const NDFViewportEventNames = [
  ...viewportEventNames,
  "lookAtBox",
  "hoverConditionBox",
  "unhoverConditionBox",
  "focusConditionBox",
  "unfocusConditionBox"
];
const _NDFViewViewportMode = class _NDFViewViewportMode extends Mode {
};
_NDFViewViewportMode.except = (tools) => new _NDFViewViewportMode({ tools: { toolList: tools, include: false } });
let NDFViewViewportMode = _NDFViewViewportMode;
const NDFViewportServiceTools = /* @__PURE__ */ new Set([NDFRender]);
const NDFViewportTools = tuple(FitCameraToImage, HoverNDF, FocusToNDF);
const NDFViewportModes = {
  mainMode: new NDFViewViewportMode()
};
const setupNDFViewport = (view) => {
  const div = HTML.div({ style: { width: "100%", height: "100%" } });
  const viewport = view.addViewport("main", div, { eventNames: NDFViewportEventNames });
  viewport.alpha = 0;
  viewport.toolController.setTools(NDFViewportTools);
  viewport.toolController.setModes(NDFViewportModes);
  viewport.toolController.setServiceTools(NDFViewportServiceTools);
  return viewport;
};

class NDFView extends View {
  constructor() {
    super();
    this.imageContainer = arx.box();
  }
  init(props) {
    super.init(props);
    const viewport = setupNDFViewport(this);
    this.domElement.appendChild(viewport.getDomElement());
    viewport.init();
    viewport.activateMode("mainMode");
    this.canvas.style.position = "absolute";
    this.domElement.style.containerType = "size";
    this.domElement.style.display = "flex";
    this.domElement.style.justifyContent = "center";
    this.domElement.style.alignItems = "center";
    this.domElement.style.flexWrap = "wrap";
  }
  getImageContainer() {
    return this.imageContainer.getAsync();
  }
  setImageContainer(imageContainer) {
    this.imageContainer.set(imageContainer);
    this.domElement.style.aspectRatio = `${imageContainer.width / imageContainer.height}`;
    const imageLayer = this.getLayer("image");
    if (!imageLayer) {
      console.error(`image layer not found`);
      return;
    }
    imageLayer.clear();
    imageLayer.add(imageContainer);
    this.dispatchEvent({ type: "render" });
  }
}

const NDFEventNames = [
  ...coreEventNames
];

const NDFViewTools = tuple(UndoRedo);
const _NDFViewMode = class _NDFViewMode extends Mode {
};
_NDFViewMode.except = (tools) => new _NDFViewMode({ tools: { toolList: tools, include: false } });
let NDFViewMode = _NDFViewMode;
const NDFLayerNames = ["image"];
const NDFViewViewModes = {
  mainMode: new NDFViewMode()
};
const defaultDomRefs = {
  view: { current: HTML.div({ style: { width: "100px", height: "100px" } }) }
};
const setupNDFView = (domRefs = defaultDomRefs, preserveDrawingBuffer = false) => {
  const view = new NDFView();
  view.toolController.setTools(NDFViewTools);
  view.toolController.setModes(NDFViewViewModes);
  view.setListeners(NDFEventNames);
  NDFLayerNames.forEach((name) => view.addLayer(name));
  view.init({
    container: domRefs.view?.current,
    rendererProps: { antialias: true, preserveDrawingBuffer },
    domRefs
  });
  return view;
};

var __create$1 = Object.create;
var __defProp$1 = Object.defineProperty;
var __getOwnPropDesc$1 = Object.getOwnPropertyDescriptor;
var __knownSymbol$1 = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError$1 = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart$1 = (base) => [, , , __create$1(null)];
var __decoratorStrings$1 = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn$1 = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError$1("Function expected") : fn;
var __decoratorContext$1 = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings$1[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError$1("Already initialized") : fns.push(__expectFn$1(fn || null)) });
var __decoratorMetadata$1 = (array, target) => __defNormalProp$1(target, __knownSymbol$1("metadata"), array[3]);
var __runInitializers$1 = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement$1 = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings$1[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc$1(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext$1(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn$1(it) && (desc[key] = it );
  }
  return desc && __defProp$1(target, name, desc), target;
};
var _addNDFView_dec, _init$1;
_addNDFView_dec = [checkForRun, errorGuard];
class NDFAPI {
  constructor() {
    __runInitializers$1(_init$1, 5, this);
  }
  addNDFView(preserveDrawingBuffer = false, domRefs) {
    if (MIRender.getView("noneDentalFindings")) {
      console.error("The NoneDentalFindings view already exists");
    }
    const view = setupNDFView(domRefs, preserveDrawingBuffer);
    MIRender.setView("noneDentalFindings", view);
    this.view = view;
    return () => MIRender.removeView("noneDentalFindings");
  }
  setImage(imageContainer) {
    if (!this.view) {
      throw new Error("NDFView not found");
    }
    this.view.setImageContainer(imageContainer);
  }
  hoverConditionBox(ids) {
    if (!this.view) {
      throw new Error("NDFView not found");
    }
    this.view.viewports.main?.dispatchEvent({ type: "hoverConditionBox", ids });
  }
  unhoverConditionBox(ids) {
    if (!this.view) {
      throw new Error("NDFView not found");
    }
    this.view.viewports.main?.dispatchEvent({ type: "unhoverConditionBox", ids });
  }
  focusConditionBox(ids) {
    if (!this.view) {
      throw new Error("NDFView not found");
    }
    this.view.viewports.main?.dispatchEvent({ type: "focusConditionBox", ids });
  }
  unfocusConditionBox() {
    if (!this.view) {
      throw new Error("NDFView not found");
    }
    this.view.viewports.main?.dispatchEvent({ type: "unfocusConditionBox" });
  }
}
_init$1 = __decoratorStart$1();
__decorateElement$1(_init$1, 1, "addNDFView", _addNDFView_dec, NDFAPI);
__decoratorMetadata$1(_init$1, NDFAPI);

const checkImageMatrix = (imageMatrix) => {
  for (let i = imageMatrix.length - 1; i >= 0; i--) {
    if (imageMatrix[i].every((item) => item.length === 0)) {
      imageMatrix.splice(i, 1);
    }
  }
  if (imageMatrix.length > 0) {
    const maxCols = Math.max(...imageMatrix.map((row) => row.length));
    for (let col = maxCols - 1; col >= 0; col--) {
      if (imageMatrix.every((row) => col >= row.length || row[col].length === 0)) {
        imageMatrix.forEach((row) => {
          if (col < row.length) {
            row.splice(col, 1);
          }
        });
      }
    }
  }
  return imageMatrix.filter((x) => x);
};

var __create = Object.create;
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __decoratorStart = (base) => [, , , __create(null)];
var __decoratorStrings = ["class", "method", "getter", "setter", "accessor", "field", "value", "get", "set"];
var __expectFn = (fn) => fn !== void 0 && typeof fn !== "function" ? __typeError("Function expected") : fn;
var __decoratorContext = (kind, name, done, metadata, fns) => ({ kind: __decoratorStrings[kind], name, metadata, addInitializer: (fn) => done._ ? __typeError("Already initialized") : fns.push(__expectFn(fn || null)) });
var __decoratorMetadata = (array, target) => __defNormalProp(target, __knownSymbol("metadata"), array[3]);
var __runInitializers = (array, flags, self, value) => {
  for (var i = 0, fns = array[flags >> 1], n = fns && fns.length; i < n; i++) fns[i].call(self) ;
  return value;
};
var __decorateElement = (array, flags, name, decorators, target, extra) => {
  var it, done, ctx, access, k = flags & 7, s = !!(flags & 8), p = !!(flags & 16);
  var j = 2 , key = __decoratorStrings[k + 5];
  var extraInitializers = array[j] || (array[j] = []);
  var desc = ((target = target.prototype), __getOwnPropDesc(target , name));
  for (var i = decorators.length - 1; i >= 0; i--) {
    ctx = __decoratorContext(k, name, done = {}, array[3], extraInitializers);
    {
      ctx.static = s, ctx.private = p, access = ctx.access = { has: (x) => name in x };
      access.get = (x) => x[name];
    }
    it = (0, decorators[i])(desc[key]  , ctx), done._ = 1;
    __expectFn(it) && (desc[key] = it );
  }
  return desc && __defProp(target, name, desc), target;
};
var _removeEventListener_dec, _addEventListenerOnce_dec, _addEventListener_dec, _removeAppEventListener_dec, _addAppEventListenerOnce_dec, _addAppEventListener_dec, _getAnnotations_dec, _insertPeriapicalSlices_dec, _insertCariesSlices_dec, _insertSelectedToothSlices_dec, _insertUserUploadedSlices_dec, _insertGeneratedSlices_dec, _insertSlicesToExistingImages_dec, _updatePictures_dec, _insertToothCrops_dec, _straightSliceEditorColors_dec, _invertSliceEditorColors_dec, _straightColors_dec, _invertColors_dec, _toggleInversion_dec, _setTeeth_dec, _deleteConditionBoxes_dec, _addConditionBoxes_dec, _showPBLsText_dec, _hidePBLsText_dec, _showPBLs_dec, _hidePBLs_dec, _hideTeethNumbers_dec, _showTeethNumbers_dec, _deletePBL_dec, _setPBLs_dec, _hideMasks_dec, _showMasks_dec, _deleteMasksByID_dec, _deleteMasks_dec, _addMasks_dec, _hidePeriapicalRadiolucencyMasks_dec, _showPeriapicalRadiolucencyMasks_dec, _addPeriapicalRadiolucencyMasks_dec, _specifyMaskGroups_dec, _lookAtBox_dec, _setMaskDeformationRadii_dec, _resetMaskEdit_dec, _cancelMaskEdit_dec, _applyMaskEdit_dec, _getMaskEditState_dec, _activateMode_dec, _showMarks_dec, _hideMarks_dec, _hideSlice_dec, _removeSliceEditorImage_dec, _setSliceEditorAnnotations_dec, _addSliceEditorImage_dec, _showSlice_dec, _setFullHeadMPRBackend_dec, _setFullHeadMPRDebugOptions_dec, _getFullHeadMPRBenchmark_dec, _getFullHeadMPRMetrics_dec, _setFullHeadMPRColorProfile_dec, _getFullHeadMPRFocus_dec, _setFullHeadMPRToothSliceMode_dec, _captureFullHeadMPRScreenshot_dec, _getFullHeadMPRViewSnapshot_dec, _stepFullHeadMPRSlice_dec, _getFullHeadMPRActivePane_dec, _setFullHeadMPRChrome_dec, _setFullHeadMPRZoomSyncEnabled_dec, _setFullHeadMPRSliceThicknessMm_dec, _setFullHeadMPRCrosshairsVisible_dec, _isFullHeadMPRViewReady_dec, _getFullHeadMPRVisibleProjections_dec, _setFullHeadMPRPrimaryProjection_dec, _getFullHeadMPRPrimaryProjection_dec, _setFullHeadMPRPaneLayout_dec, _getFullHeadMPRPaneLayout_dec, _getFullHeadMPRPaneElement_dec, _getFullHeadMPRPaneSurface_dec, _deleteFullHeadMPRSelectedAnnotation_dec, _cancelFullHeadMPRAnnotationInteraction_dec, _setFullHeadMPRAnnotationsVisible_dec, _setFullHeadMPRAnnotationTarget_dec, _setFullHeadMPRAnnotationTool_dec, _setFullHeadMPRImageOptions_dec, _setFullHeadMPRImageOptionsTarget_dec, _setFullHeadMPRImageTool_dec, _setFullHeadMPRInputMode_dec, _resetFullHeadMPRView_dec, _clearFullHeadMPRToothFocus_dec, _setFullHeadMPRToothFocus_dec, _setFullHeadMPRFocusWorld_dec, _setFullHeadMPRFocus_dec, _disposeFullHeadMPR_dec, _startFullHeadMPR_dec, _getCurrentMode_dec, _resetUndoStack_dec, _setCredentials_dec, _specifyMainView_dec, _setViewContainer_dec, _removeView_dec, _addFullHeadMPRView_dec, _addSliceEditor_dec, _addMainView_dec, _shutdown_dec, _launch_dec, _init;
_launch_dec = [errorGuard], _shutdown_dec = [errorGuard, shadow], _addMainView_dec = [checkForRun, errorGuard], _addSliceEditor_dec = [checkForRun, errorGuard], _addFullHeadMPRView_dec = [checkForRun, errorGuard], _removeView_dec = [checkForRun, errorGuard], _setViewContainer_dec = [checkForRun, errorGuard], _specifyMainView_dec = [errorGuard, shadow], _setCredentials_dec = [errorGuard], _resetUndoStack_dec = [checkForRun, errorGuard], _getCurrentMode_dec = [checkForRun, errorGuard], _startFullHeadMPR_dec = [checkForRunAsync, errorGuardAsync], _disposeFullHeadMPR_dec = [checkForRun, errorGuard], _setFullHeadMPRFocus_dec = [checkForRun, errorGuard], _setFullHeadMPRFocusWorld_dec = [checkForRun, errorGuard], _setFullHeadMPRToothFocus_dec = [checkForRun, errorGuard], _clearFullHeadMPRToothFocus_dec = [checkForRun, errorGuard], _resetFullHeadMPRView_dec = [checkForRun, errorGuard], _setFullHeadMPRInputMode_dec = [checkForRun, errorGuard], _setFullHeadMPRImageTool_dec = [checkForRun, errorGuard], _setFullHeadMPRImageOptionsTarget_dec = [checkForRun, errorGuard], _setFullHeadMPRImageOptions_dec = [checkForRun, errorGuard], _setFullHeadMPRAnnotationTool_dec = [checkForRun, errorGuard], _setFullHeadMPRAnnotationTarget_dec = [checkForRunAsync, errorGuardAsync], _setFullHeadMPRAnnotationsVisible_dec = [checkForRun, errorGuard], _cancelFullHeadMPRAnnotationInteraction_dec = [checkForRun, errorGuard], _deleteFullHeadMPRSelectedAnnotation_dec = [checkForRun, errorGuard], _getFullHeadMPRPaneSurface_dec = [checkForRun, errorGuard], _getFullHeadMPRPaneElement_dec = [checkForRun, errorGuard], _getFullHeadMPRPaneLayout_dec = [checkForRun, errorGuard], _setFullHeadMPRPaneLayout_dec = [checkForRun, errorGuard], _getFullHeadMPRPrimaryProjection_dec = [checkForRun, errorGuard], _setFullHeadMPRPrimaryProjection_dec = [checkForRun, errorGuard], _getFullHeadMPRVisibleProjections_dec = [checkForRun, errorGuard], _isFullHeadMPRViewReady_dec = [checkForRun, errorGuard], _setFullHeadMPRCrosshairsVisible_dec = [checkForRun, errorGuard], _setFullHeadMPRSliceThicknessMm_dec = [checkForRun, errorGuard], _setFullHeadMPRZoomSyncEnabled_dec = [checkForRun, errorGuard], _setFullHeadMPRChrome_dec = [checkForRun, errorGuard], _getFullHeadMPRActivePane_dec = [checkForRun, errorGuard], _stepFullHeadMPRSlice_dec = [checkForRun, errorGuard], _getFullHeadMPRViewSnapshot_dec = [checkForRun, errorGuard], _captureFullHeadMPRScreenshot_dec = [checkForRunAsync, errorGuardAsync], _setFullHeadMPRToothSliceMode_dec = [checkForRun, errorGuard], _getFullHeadMPRFocus_dec = [checkForRun, errorGuard], _setFullHeadMPRColorProfile_dec = [checkForRun, errorGuard], _getFullHeadMPRMetrics_dec = [checkForRun, errorGuard], _getFullHeadMPRBenchmark_dec = [checkForRun, errorGuard], _setFullHeadMPRDebugOptions_dec = [checkForRun, errorGuard], _setFullHeadMPRBackend_dec = [checkForRun, errorGuard], _showSlice_dec = [checkForRun, errorGuard], _addSliceEditorImage_dec = [checkForRun, errorGuard], _setSliceEditorAnnotations_dec = [checkForRunAsync, errorGuardAsync], _removeSliceEditorImage_dec = [checkForRun, errorGuard], _hideSlice_dec = [checkForRun, errorGuard], _hideMarks_dec = [checkForRun, errorGuard], _showMarks_dec = [checkForRun, errorGuard], _activateMode_dec = [checkForRun, errorGuard], _getMaskEditState_dec = [checkForRun, errorGuard], _applyMaskEdit_dec = [checkForRun, errorGuard], _cancelMaskEdit_dec = [checkForRun, errorGuard], _resetMaskEdit_dec = [checkForRun, errorGuard], _setMaskDeformationRadii_dec = [checkForRun, errorGuard], _lookAtBox_dec = [checkForRun, errorGuard], _specifyMaskGroups_dec = [checkForRunAsync, errorGuardAsync, shadow], _addPeriapicalRadiolucencyMasks_dec = [checkForRun, errorGuard, shadow], _showPeriapicalRadiolucencyMasks_dec = [checkForRun, errorGuard, shadow], _hidePeriapicalRadiolucencyMasks_dec = [checkForRun, errorGuard, shadow], _addMasks_dec = [checkForRun, errorGuard, shadow], _deleteMasks_dec = [checkForRun, errorGuard], _deleteMasksByID_dec = [checkForRun, errorGuard], _showMasks_dec = [checkForRun, errorGuard, shadow], _hideMasks_dec = [checkForRun, errorGuard, shadow], _setPBLs_dec = [checkForRun, errorGuard, shadow], _deletePBL_dec = [checkForRun, errorGuard, shadow], _showTeethNumbers_dec = [checkForRun, errorGuard, shadow], _hideTeethNumbers_dec = [checkForRun, errorGuard, shadow], _hidePBLs_dec = [checkForRun, errorGuard, shadow], _showPBLs_dec = [checkForRun, errorGuard, shadow], _hidePBLsText_dec = [checkForRun, errorGuard, shadow], _showPBLsText_dec = [checkForRun, errorGuard, shadow], _addConditionBoxes_dec = [checkForRun, errorGuard, shadow], _deleteConditionBoxes_dec = [checkForRun, errorGuard, shadow], _setTeeth_dec = [checkForRun, errorGuard], _toggleInversion_dec = [checkForRun, errorGuard], _invertColors_dec = [checkForRun, errorGuard, shadow], _straightColors_dec = [checkForRun, errorGuard], _invertSliceEditorColors_dec = [checkForRun, errorGuard], _straightSliceEditorColors_dec = [checkForRun, errorGuard], _insertToothCrops_dec = [checkForRun, errorGuard], _updatePictures_dec = [checkForRun, errorGuard, shadow], _insertSlicesToExistingImages_dec = [checkForRunAsync, errorGuardAsync], _insertGeneratedSlices_dec = [checkForRunAsync, errorGuardAsync], _insertUserUploadedSlices_dec = [checkForRunAsync, errorGuardAsync], _insertSelectedToothSlices_dec = [checkForRunAsync, errorGuardAsync], _insertCariesSlices_dec = [checkForRunAsync, errorGuardAsync], _insertPeriapicalSlices_dec = [checkForRunAsync, errorGuardAsync], _getAnnotations_dec = [checkForRun, errorGuard], _addAppEventListener_dec = [checkForRun, errorGuard], _addAppEventListenerOnce_dec = [checkForRun, errorGuard], _removeAppEventListener_dec = [checkForRun, errorGuard], _addEventListener_dec = [checkForRun, errorGuard], _addEventListenerOnce_dec = [checkForRun, errorGuard], _removeEventListener_dec = [checkForRun, errorGuard];
class MIRenderAPI {
  constructor() {
    __runInitializers(_init, 5, this);
    this.NDFAPI = new NDFAPI();
    this.maskDeformationRadii = { ...DEFAULT_MASK_DEFORMATION_RADII };
  }
  setState(state) {
    this.state = state;
    const view = MIRender.getView("main");
    view?.imageController.setMaskContourHydrator((maskID) => state.getCommittedMaskContour(maskID));
    const editMask = view?.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      editMask.setMaskContourEditStore(state);
    }
  }
  launch() {
    setupMIRender();
  }
  shutdown() {
    if (MIRender.status() === "run") {
      MIRender.clear();
    } else {
      console.error("[launch] already shutdown");
    }
  }
  addMainView(preserveDrawingBuffer = false, domRefs) {
    if (MIRender.getView("main")) {
      console.error("The main view already exists");
      return;
    }
    const view = setupMatrixView(domRefs, preserveDrawingBuffer);
    const state = this.state;
    view.imageController.setMaskContourHydrator(state ? (maskID) => state.getCommittedMaskContour(maskID) : void 0);
    const editMask = view.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      if (this.state) {
        editMask.setMaskContourEditStore(this.state);
      }
      editMask.setDeformationRadii(
        this.maskDeformationRadii.normalMovementRadiusMm,
        this.maskDeformationRadii.affectedRadiusMm
      );
    }
    MIRender.setView("main", view);
    return () => MIRender.removeView("main");
  }
  addSliceEditor(preserveDrawingBuffer = false, domRefs) {
    if (MIRender.getView("sliceEditor")) {
      console.error("The 'sliceEditor' view already exists");
    }
    const view = setupSliceEditor(domRefs, preserveDrawingBuffer);
    MIRender.setView("sliceEditor", view);
    return () => MIRender.removeView("sliceEditor");
  }
  addFullHeadMPRView(preserveDrawingBuffer = true, { retainVolume = false } = {}) {
    const existingView = MIRender.getView("fullHeadMPR");
    if (existingView && (!retainVolume || existingView.getContainer()?.isConnected)) {
      console.error("The 'fullHeadMPR' view already exists");
      return;
    }
    const view = existingView ?? setupFullHeadMPRView(preserveDrawingBuffer);
    if (!existingView) {
      MIRender.setView("fullHeadMPR", view);
    }
    return () => {
      if (retainVolume) {
        view.detach();
      } else {
        MIRender.removeView("fullHeadMPR");
      }
    };
  }
  removeView(viewName) {
    MIRender.removeView(viewName);
  }
  isViewExist(viewName) {
    return Boolean(MIRender.getView(viewName));
  }
  setViewContainer(viewName, ref) {
    if (!ref.current) {
      console.error(`ref for ${viewName} with nullish current`);
      return;
    }
    const view = MIRender.getView(viewName);
    if (!view) {
      console.error(`view ${viewName} is not defined`);
      return;
    }
    view.setContainer(ref.current);
  }
  setScreenProps(reportViewOptions) {
    const view = MIRender.getView("main");
    if (!view) {
      console.error(`main view is not defined`);
      return;
    }
    view.imageController.screenProps.brightness = reportViewOptions.Brightness || 0;
    view.imageController.screenProps.contrast = reportViewOptions.Contrast || 0;
    view.imageController.screenProps.sharpness = reportViewOptions.Sharpness || 0;
  }
  specifyMainView(images, reportViewOptions, specialImages, matrixLayoutOptions, usePreview = true) {
    const view = MIRender.getView("main");
    reportViewOptions && this.setScreenProps(reportViewOptions);
    const imageMatrix = checkImageMatrix(Array.isArray(images) ? images : getImageMatrix(images));
    const needsToAdd = view?.layoutController.setImageMatrix(imageMatrix, specialImages, matrixLayoutOptions);
    if (needsToAdd) {
      return view?.imageController.addImagesToMatrix(imageMatrix, usePreview);
    }
  }
  setCredentials(credentials) {
    const value = credentials === "include";
    ImageLoader$1.setWithCredentials(value);
    configureSharedDCCTFetchCredentials(credentials);
  }
  resetUndoStack(viewName) {
    MIRender.getView(viewName)?.undoStack.undoAll();
  }
  getCurrentMode(viewName) {
    return MIRender.getView(viewName)?.toolController.getModeName();
  }
  getLayoutController() {
    const view = MIRender.getView("main");
    if (!view) {
      console.error(`main view is not found`);
      return;
    }
    return view.layoutController;
  }
  getFullHeadMPRView() {
    const view = MIRender.getView("fullHeadMPR");
    if (!view) {
      console.error("The 'fullHeadMPR' view does not exist");
      return;
    }
    return view;
  }
  async startFullHeadMPR(options) {
    const view = this.getFullHeadMPRView();
    if (!view) {
      return;
    }
    await view.controller.start(options);
  }
  disposeFullHeadMPR() {
    this.getFullHeadMPRView()?.controller.stop();
  }
  setFullHeadMPRFocus(focus) {
    this.getFullHeadMPRView()?.controller.setFocus(focus);
  }
  setFullHeadMPRFocusWorld(point, reason = "api") {
    this.getFullHeadMPRView()?.controller.setFocusWorld(point, reason);
  }
  setFullHeadMPRToothFocus(payload) {
    this.getFullHeadMPRView()?.controller.setToothFocus(payload);
  }
  clearFullHeadMPRToothFocus() {
    this.getFullHeadMPRView()?.controller.clearToothFocus();
  }
  resetFullHeadMPRView(options) {
    this.getFullHeadMPRView()?.controller.resetToDefaultView(options);
  }
  setFullHeadMPRInputMode(mode) {
    this.getFullHeadMPRView()?.controller.setInputMode(mode);
  }
  setFullHeadMPRImageTool(tool) {
    this.getFullHeadMPRView()?.controller.setImageTool(tool);
  }
  setFullHeadMPRImageOptionsTarget(projection, target) {
    this.getFullHeadMPRView()?.controller.setImageOptionsTarget(projection, target);
  }
  setFullHeadMPRImageOptions(projection, options) {
    this.getFullHeadMPRView()?.controller.setImageOptions(projection, options);
  }
  setFullHeadMPRAnnotationTool(tool) {
    this.getFullHeadMPRView()?.controller.setAnnotationTool(tool);
  }
  async setFullHeadMPRAnnotationTarget(projection, target) {
    await this.getFullHeadMPRView()?.controller.setAnnotationTarget(projection, target);
  }
  setFullHeadMPRAnnotationsVisible(visible) {
    this.getFullHeadMPRView()?.controller.setAnnotationsVisible(visible);
  }
  cancelFullHeadMPRAnnotationInteraction() {
    return this.getFullHeadMPRView()?.controller.cancelAnnotationInteraction() ?? false;
  }
  deleteFullHeadMPRSelectedAnnotation() {
    return this.getFullHeadMPRView()?.controller.deleteSelectedAnnotation() ?? false;
  }
  getFullHeadMPRPaneSurface(projection) {
    return this.getFullHeadMPRView()?.controller.getPaneSurface(projection) ?? null;
  }
  getFullHeadMPRPaneElement(projection) {
    return this.getFullHeadMPRView()?.controller.getPaneElement(projection) ?? null;
  }
  getFullHeadMPRPaneLayout() {
    return this.getFullHeadMPRView()?.controller.getPaneLayout() ?? null;
  }
  setFullHeadMPRPaneLayout(layout) {
    this.getFullHeadMPRView()?.controller.setPaneLayout(layout);
  }
  getFullHeadMPRPrimaryProjection() {
    return this.getFullHeadMPRView()?.controller.getPrimaryProjection() ?? null;
  }
  setFullHeadMPRPrimaryProjection(projection) {
    this.getFullHeadMPRView()?.controller.setPrimaryProjection(projection);
  }
  getFullHeadMPRVisibleProjections() {
    return this.getFullHeadMPRView()?.controller.getVisibleProjections() ?? [];
  }
  isFullHeadMPRViewReady(projection) {
    return this.getFullHeadMPRView()?.controller.isViewReady(projection) ?? false;
  }
  setFullHeadMPRCrosshairsVisible(visible) {
    this.getFullHeadMPRView()?.controller.setCrosshairsVisible(visible);
  }
  setFullHeadMPRSliceThicknessMm(thickness) {
    this.getFullHeadMPRView()?.controller.setSliceThicknessMm(thickness);
  }
  setFullHeadMPRZoomSyncEnabled(enabled) {
    this.getFullHeadMPRView()?.controller.setZoomSyncEnabled(enabled);
  }
  setFullHeadMPRChrome(options) {
    this.getFullHeadMPRView()?.controller.setChrome(options);
  }
  getFullHeadMPRActivePane() {
    return this.getFullHeadMPRView()?.controller.getActivePane();
  }
  stepFullHeadMPRSlice(direction) {
    this.getFullHeadMPRView()?.controller.stepSlice(direction);
  }
  getFullHeadMPRViewSnapshot(projection) {
    return this.getFullHeadMPRView()?.controller.getViewSnapshot(projection);
  }
  async captureFullHeadMPRScreenshot(projection) {
    const view = this.getFullHeadMPRView();
    if (!view) {
      return null;
    }
    return view.controller.captureScreenshot(projection);
  }
  setFullHeadMPRToothSliceMode(mode) {
    this.getFullHeadMPRView()?.controller.setToothSliceMode(mode);
  }
  getFullHeadMPRFocus() {
    return this.getFullHeadMPRView()?.controller.getFocus();
  }
  setFullHeadMPRColorProfile(colorProfile) {
    this.getFullHeadMPRView()?.controller.setColorProfile(colorProfile);
  }
  getFullHeadMPRMetrics() {
    return this.getFullHeadMPRView()?.controller.getMetrics();
  }
  getFullHeadMPRBenchmark() {
    return this.getFullHeadMPRView()?.controller.getBenchmark();
  }
  setFullHeadMPRDebugOptions(debugOptions) {
    this.getFullHeadMPRView()?.controller.setDebugOptions(debugOptions);
  }
  setFullHeadMPRBackend(backend) {
    this.getFullHeadMPRView()?.controller.setBackend(backend);
  }
  showSlice(imageID) {
    MIRender.getView("sliceEditor")?.showSlice(imageID);
  }
  addSliceEditorImage(props, URL) {
    return MIRender.getView("sliceEditor")?.imageController.addImage(props, URL)?.waitForLoad;
  }
  async setSliceEditorAnnotations(imageID, layers, scaleMmPerPixel) {
    const imageContainer = MIRender.getView("sliceEditor")?.imageController.imageContainers.get(imageID);
    if (!imageContainer) {
      return;
    }
    imageContainer.deleteAllAnnotations();
    await Promise.all(layers.map((layer) => this.addAnnotation(layer, imageContainer, scaleMmPerPixel)));
  }
  removeSliceEditorImage(imageID) {
    MIRender.getView("sliceEditor")?.imageController.deleteImage(imageID);
  }
  hideSlice() {
    MIRender.getView("sliceEditor")?.hideSlice();
  }
  hideMarks() {
    MIRender.getView("sliceEditor")?.imageController.hideAnnotations();
  }
  showMarks() {
    MIRender.getView("sliceEditor")?.imageController.showAnnotations();
  }
  activateMode(viewName, mode) {
    const view = MIRender.getView(viewName);
    view?.activateMode(mode);
  }
  getMaskEditState() {
    const editMask = MIRender.getView("main")?.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      return editMask.getEditState();
    }
  }
  applyMaskEdit() {
    const editMask = MIRender.getView("main")?.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      editMask.applyChanges();
    }
  }
  cancelMaskEdit() {
    const editMask = MIRender.getView("main")?.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      editMask.cancelChanges();
    }
  }
  resetMaskEdit() {
    const editMask = MIRender.getView("main")?.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      editMask.resetChanges();
    }
  }
  setMaskDeformationRadii(normalMovementRadiusMm, affectedRadiusMm) {
    this.maskDeformationRadii = validateMaskDeformationRadii(
      normalMovementRadiusMm,
      affectedRadiusMm
    );
    const view = MIRender.getView("main");
    const editMask = view?.toolController.getTool(EditMask);
    if (editMask instanceof EditMask) {
      editMask.setDeformationRadii(normalMovementRadiusMm, affectedRadiusMm);
    }
  }
  lookAtBox(box, viewportName) {
    MIRender.view.getViewport(viewportName)?.dispatchEvent({ type: "lookAtBox", box });
  }
  async specifyMaskGroups(groupIDs, assetID) {
    const view = MIRender.getView("main");
    if (!view) {
      console.error(`[specifyMaskGroups] main view is not found`);
      return;
    }
    if (assetID) {
      const container = await view.imageController.imageContainers.getAsync(assetID);
      if (!container) {
        console.error(`[specifyMaskGroups] no image with id ${assetID}`);
        return;
      }
      container.specifyMaskGroups(groupIDs);
    } else {
      view.imageController.imageContainers.forEach((container) => container.specifyMaskGroups(groupIDs));
    }
  }
  addPeriapicalRadiolucencyMasks(data) {
    MIRender.getView("sliceEditor")?.imageController.addMasks(data);
  }
  showPeriapicalRadiolucencyMasks() {
    MIRender.getView("sliceEditor")?.imageController.imageContainers.forEach((container) => container.showMasks(["perio"]));
  }
  hidePeriapicalRadiolucencyMasks() {
    MIRender.getView("sliceEditor")?.imageController.imageContainers.forEach((container) => container.hideMasks(["perio"]));
  }
  addMasks(data) {
    MIRender.getView("main")?.imageController.addMasks(data);
  }
  deleteMasks(groupIDs) {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.deleteMasks(groupIDs));
  }
  deleteMasksByID(assetID, maskIDs, groupID) {
    MIRender.getView("main")?.imageController.imageContainers.get(assetID)?.deleteMak(maskIDs, groupID);
  }
  showMasks(groupIDs, duration = 0) {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.showMasks(groupIDs, duration));
  }
  hideMasks(groupIDs, duration = 0) {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.hideMasks(groupIDs, duration));
  }
  setPBLs(data) {
    MIRender.getView("main")?.imageController.addPBL(data);
  }
  deletePBL() {
    console.error("deletePBL is not implemented");
  }
  addTeethNumbers(data) {
    return MIRender.getView("main")?.imageController.addTeethNumbers(data);
  }
  showTeethNumbers() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.showTeethNumbers());
  }
  hideTeethNumbers() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.hideTeethNumbers());
  }
  hidePBLs() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.hidePBLs());
  }
  showPBLs() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.showPBLs());
  }
  hidePBLsText() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => {
      [...container.getPBLs()].forEach((pbl) => {
        pbl.hideEndpoints();
        pbl.hideText();
      });
    });
  }
  showPBLsText() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => {
      [...container.getPBLs()].forEach((pbl) => {
        pbl.showEndpoints();
        pbl.showText();
      });
    });
  }
  addConditionBoxes(data) {
    MIRender.getView("main")?.imageController.addConditionBoxes(data);
  }
  deleteConditionBoxes() {
    MIRender.getView("main")?.imageController.imageContainers.forEach((container) => container.deleteConditionBoxes());
  }
  setTeeth() {
    const view = MIRender.getView("main");
    const reportID = this.state.currentReport.get()?.ID;
    if (!reportID) {
      console.error("[addTeeth] current report id is not defined");
      return;
    }
    const teeth = this.state.flatTeeth.get(reportID);
    if (!teeth) {
      console.error("[addTeeth] no teeth for current report");
      return;
    }
    view.layoutController.setTeeth(teeth);
  }
  toggleInversion() {
    MIRender.getView("main")?.dispatchEvent({ type: "toggleInversion" });
  }
  invertColors() {
    MIRender.getView("main")?.dispatchEvent({ type: "invertColors" });
  }
  straightColors() {
    MIRender.getView("main")?.dispatchEvent({ type: "straightColors" });
  }
  invertSliceEditorColors() {
    MIRender.getView("sliceEditor")?.imageController.pictures.forEach((pictures) => {
      pictures.forEach((picture) => {
        picture.imageContainer.getImage().object3d.material.uniforms.invert.value = 1;
        picture.update();
      });
    });
  }
  straightSliceEditorColors() {
    MIRender.getView("sliceEditor")?.imageController.pictures.forEach((pictures) => {
      pictures.forEach((picture) => {
        picture.imageContainer.getImage().object3d.material.uniforms.invert.value = 0;
        picture.update();
      });
    });
  }
  insertToothCrops(toothID, container, styles) {
    const view = MIRender.getView("main");
    if (!view) {
      console.error(`[insertToothCrops] main view is not found`);
      return;
    }
    const reportID = this.state.dataFromURL.reportID;
    if (!reportID) {
      console.error(`[insertToothCrops] report id is not defined`);
      return;
    }
    const tooth = this.state.flatTeeth.get(reportID)?.get(toothID);
    const imageController = view.imageController;
    if (imageController.toothCrops.get(toothID)?.get(container)) {
      return;
    }
    tooth?.Detections.forEach(async (detection) => {
      const localization = detection.Localizations;
      if (!localization) return;
      const bBox = localization.BBox;
      if (bBox) {
        const assetID = localization.TargetAssetID;
        const crop = new CroppedPicture(bBox, container, styles);
        MIRender.getView("main")?.imageController.addCrop(assetID, toothID, crop);
        const imageContainer = await imageController.imageContainers.getAsync(assetID);
        crop.setImageContainer(imageContainer);
        crop.update();
      }
    });
  }
  getCanvasElement(containerElement) {
    const nodeList = containerElement.querySelectorAll("canvas");
    const imageElements = /* @__PURE__ */ new Map();
    [...nodeList].filter((canvas) => canvas !== null).forEach((canvas, index) => imageElements.set(canvas.id || index, canvas));
    return imageElements;
  }
  updatePictures(imageID) {
    MIRender.getView("main")?.imageController.updatePictures(imageID);
  }
  async insertSlicesToExistingImages({ toothID, container, styles, onClick }) {
    const viewName = "sliceEditor";
    const imageController = MIRender.getView(viewName)?.imageController;
    if (!imageController) return;
    const imageElements = this.getCanvasElement(container);
    imageElements.forEach(async (img) => {
      const slicePictures = imageController.slicePictures.get(toothID)?.get(img);
      if (slicePictures) return;
      const assetID = img.id;
      if (onClick) {
        img.onclick = () => onClick(assetID);
      }
      const picture = new SlicePicture(container, img || styles);
      MIRender.getView(viewName)?.imageController.addSlicePicture(assetID, toothID, picture);
      let imageContainer = imageController?.imageContainers.get(assetID);
      let result;
      if (!imageContainer) {
        const slice = await this.state.FlatSlices.getAsync(img.id);
        const imageInterface = imageAssetToMIRenderImageInterface(slice);
        const URL = getFileSrc(imageInterface.OriginalFileID);
        result = imageController.addImage(imageInterface, URL);
        imageContainer = result.container;
        if (imageContainer) {
          slice.medicalImageFeatures?.Annotations?.Layers && await Promise.all(
            slice.medicalImageFeatures.Annotations.Layers.map(async (layer) => {
              await this.addAnnotation(layer, imageContainer);
            })
          );
        }
      }
      picture.setImageContainer(imageContainer);
      if (result) {
        result.waitForLoad.then((loaded) => loaded && imageController.updatePictures(img.id));
      } else {
        picture.update();
      }
      return picture;
    });
  }
  async addAnnotation(annotationsLayer, imageContainer, scale) {
    await addAnnotationLayer({
      container: imageContainer,
      layer: annotationsLayer,
      scale: scale || this.state.FlatSlices.get(imageContainer.id)?.Scale
    });
  }
  async insertSlices(slices, { toothID, container, styles, onClick, selectIMG = "both", useIMG = "both" }) {
    const viewName = "sliceEditor";
    const imageController = MIRender.getView(viewName)?.imageController;
    if (!imageController) return;
    const imageElements = this.getCanvasElement(container);
    const getImage = (slice, index2) => {
      if (selectIMG === "byAssetID") {
        return imageElements.get(slice.assetID);
      } else if (selectIMG === "sequentially") {
        return imageElements.get(index2);
      } else {
        return imageElements.get(slice.assetID) || imageElements.get(index2);
      }
    };
    let index = 0;
    const map = imageController.slicePictures.get(toothID);
    let existedSliceIDs = [];
    if (map) {
      imageElements.forEach((img) => {
        const picture = map.get(img);
        if (picture) {
          existedSliceIDs.push(picture.imageContainer.id);
        }
      });
    }
    const notExistedSlices = slices.filter((slice) => !existedSliceIDs.includes(slice.assetID));
    notExistedSlices.forEach(async (slice) => {
      const assetID = slice.assetID;
      let img;
      if (useIMG === "both" || useIMG === "existing") {
        img = getImage(slice, index++);
        if (!img && useIMG === "existing") {
          return;
        }
      }
      if (onClick) {
        if (!img) {
          if (styles) {
            styles.onclick = () => onClick(assetID);
          } else {
            styles = { onclick: () => onClick(assetID), style: {} };
          }
        } else {
          img.onclick = () => onClick(assetID);
        }
      }
      const picture = new SlicePicture(container, img || styles);
      MIRender.getView(viewName)?.imageController.addSlicePicture(assetID, toothID, picture);
      let result;
      let imageContainer = imageController?.imageContainers.get(assetID);
      if (!imageContainer) {
        const imageInterface = imageAssetToMIRenderImageInterface(slice);
        const URL = getFileSrc(imageInterface.OriginalFileID);
        result = imageController.addImage(imageInterface, URL);
        imageContainer = result.container;
        if (imageContainer) {
          picture.setImageContainer(imageContainer);
          slice.medicalImageFeatures?.Annotations?.Layers && await Promise.all(
            slice.medicalImageFeatures.Annotations.Layers.map(async (layer) => {
              await this.addAnnotation(layer, imageContainer);
            })
          );
        }
      } else {
        picture.setImageContainer(imageContainer);
      }
      const isInverted = () => this.state.dataFromURL.pageType === "preview" && this.state.currentReport.get()?.PrintSettings?.IsInverted;
      if (result) {
        result.waitForLoad.then((loaded) => {
          if (!loaded) {
            return;
          }
          if (isInverted()) {
            loaded.getImage().object3d.material.uniforms.invert.value = 1;
          }
          imageController.updatePictures(loaded.id);
        });
      } else {
        if (isInverted()) {
          imageContainer.getImage().object3d.material.uniforms.invert.value = 1;
        }
        picture.update();
      }
      return picture;
    });
  }
  async insertGeneratedSlices({ orientation, toothID, container, styles, onClick, selectIMG: takeImage = "both", useIMG: insertion = "both" }) {
    const allGeneratedSlices = await this.state.GeneratedReportSlices.getAsync(toothID);
    const slicesForOrientation = await allGeneratedSlices?.getAsync(orientation);
    if (!slicesForOrientation) return;
    return this.insertSlices([...slicesForOrientation.values()], { toothID, container, styles, onClick, selectIMG: takeImage, useIMG: insertion });
  }
  async insertUserUploadedSlices({ toothID, container, styles, onClick, selectIMG: takeImage = "both", useIMG: insertion = "both" }) {
    const slices = await userUploadedSlices(toothID, this.state);
    const orderedDisplaySlices = [
      ...slices.userUploads.values(),
      ...slices.mprPlanes.values()
    ].sort((a, b) => a.CreatedAt - b.CreatedAt);
    return this.insertSlices(orderedDisplaySlices, { toothID, container, styles, onClick, selectIMG: takeImage, useIMG: insertion });
  }
  async insertSelectedToothSlices({ toothID, container, styles, onClick, selectIMG: takeImage = "both", useIMG: insertion = "both" }) {
    const displaySlices = await selectedSlices(toothID, this.state);
    const orderedDisplaySlices = [
      ...Object.values(displaySlices.Generated).flat(),
      ...[
        ...displaySlices.UserUploaded,
        ...displaySlices.UserUploadedMPRPlanes
      ].sort((a, b) => a.CreatedAt - b.CreatedAt),
      ...displaySlices.CariesSlices,
      ...displaySlices.PeriapicalSlices
    ];
    return this.insertSlices(orderedDisplaySlices, { toothID, container, styles, onClick, selectIMG: takeImage, useIMG: insertion });
  }
  async insertCariesSlices({ toothID, container, styles, onClick, selectIMG: takeImage = "both", useIMG: insertion = "both" }) {
    const caries = await selectCariesSlices(toothID, this.state);
    return this.insertSlices(caries, { toothID, container, styles, onClick, selectIMG: takeImage, useIMG: insertion });
  }
  async insertPeriapicalSlices({ toothID, container, styles, onClick, selectIMG: takeImage = "both", useIMG: insertion = "both" }) {
    const caries = await selectPeriapicalSlices(toothID, this.state);
    return this.insertSlices(caries, { toothID, container, styles, onClick, selectIMG: takeImage, useIMG: insertion });
  }
  getAnnotations(assetID) {
    const annotationsGenerator = MIRender.getView("sliceEditor")?.imageController.imageContainers.get(assetID)?.getAnnotations();
    if (!annotationsGenerator) return;
    return [...annotationsGenerator];
  }
  addAppEventListener(type, callback, priority) {
    return MIRender.addEventListener(type, callback, priority);
  }
  addAppEventListenerOnce(type, callback, priority) {
    return MIRender.addEventListenerOnce(type, callback, priority);
  }
  removeAppEventListener(type, callback) {
    MIRender.removeEventListener(type, callback);
  }
  addEventListener(type, callback, viewName = "main", priority) {
    const view = MIRender.getView(viewName);
    if (!view) return;
    return view.addEventListener(type, callback, priority);
  }
  addEventListenerOnce(type, callback, viewName = "main", priority) {
    const view = MIRender.getView(viewName);
    if (!view) return;
    return view.addEventListenerOnce(type, callback, priority);
  }
  removeEventListener(type, callback, viewName = "main") {
    const view = MIRender.getView(viewName);
    if (!view) return;
    view.removeEventListener(type, callback);
  }
  resetSliceState() {
    const imageController = MIRender.getView("sliceEditor")?.imageController;
    if (!imageController) return false;
    const resultViewOptions = imageController.resetViewOptions();
    const resultAnnotations = imageController.deleteAnnotations();
    return resultViewOptions || resultAnnotations;
  }
  downLoadPanoReformat(assetID, fileName) {
    MIRender.getView("main")?.getViewport(assetID)?.dispatchEvent({ type: "downLoadImage", fileName });
  }
  downLoadSlice(fileName) {
    MIRender.getView("sliceEditor")?.getViewport("main")?.dispatchEvent({ type: "downLoadImage", fileName });
  }
}
_init = __decoratorStart();
__decorateElement(_init, 1, "launch", _launch_dec, MIRenderAPI);
__decorateElement(_init, 1, "shutdown", _shutdown_dec, MIRenderAPI);
__decorateElement(_init, 1, "addMainView", _addMainView_dec, MIRenderAPI);
__decorateElement(_init, 1, "addSliceEditor", _addSliceEditor_dec, MIRenderAPI);
__decorateElement(_init, 1, "addFullHeadMPRView", _addFullHeadMPRView_dec, MIRenderAPI);
__decorateElement(_init, 1, "removeView", _removeView_dec, MIRenderAPI);
__decorateElement(_init, 1, "setViewContainer", _setViewContainer_dec, MIRenderAPI);
__decorateElement(_init, 1, "specifyMainView", _specifyMainView_dec, MIRenderAPI);
__decorateElement(_init, 1, "setCredentials", _setCredentials_dec, MIRenderAPI);
__decorateElement(_init, 1, "resetUndoStack", _resetUndoStack_dec, MIRenderAPI);
__decorateElement(_init, 1, "getCurrentMode", _getCurrentMode_dec, MIRenderAPI);
__decorateElement(_init, 1, "startFullHeadMPR", _startFullHeadMPR_dec, MIRenderAPI);
__decorateElement(_init, 1, "disposeFullHeadMPR", _disposeFullHeadMPR_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRFocus", _setFullHeadMPRFocus_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRFocusWorld", _setFullHeadMPRFocusWorld_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRToothFocus", _setFullHeadMPRToothFocus_dec, MIRenderAPI);
__decorateElement(_init, 1, "clearFullHeadMPRToothFocus", _clearFullHeadMPRToothFocus_dec, MIRenderAPI);
__decorateElement(_init, 1, "resetFullHeadMPRView", _resetFullHeadMPRView_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRInputMode", _setFullHeadMPRInputMode_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRImageTool", _setFullHeadMPRImageTool_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRImageOptionsTarget", _setFullHeadMPRImageOptionsTarget_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRImageOptions", _setFullHeadMPRImageOptions_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRAnnotationTool", _setFullHeadMPRAnnotationTool_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRAnnotationTarget", _setFullHeadMPRAnnotationTarget_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRAnnotationsVisible", _setFullHeadMPRAnnotationsVisible_dec, MIRenderAPI);
__decorateElement(_init, 1, "cancelFullHeadMPRAnnotationInteraction", _cancelFullHeadMPRAnnotationInteraction_dec, MIRenderAPI);
__decorateElement(_init, 1, "deleteFullHeadMPRSelectedAnnotation", _deleteFullHeadMPRSelectedAnnotation_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRPaneSurface", _getFullHeadMPRPaneSurface_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRPaneElement", _getFullHeadMPRPaneElement_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRPaneLayout", _getFullHeadMPRPaneLayout_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRPaneLayout", _setFullHeadMPRPaneLayout_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRPrimaryProjection", _getFullHeadMPRPrimaryProjection_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRPrimaryProjection", _setFullHeadMPRPrimaryProjection_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRVisibleProjections", _getFullHeadMPRVisibleProjections_dec, MIRenderAPI);
__decorateElement(_init, 1, "isFullHeadMPRViewReady", _isFullHeadMPRViewReady_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRCrosshairsVisible", _setFullHeadMPRCrosshairsVisible_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRSliceThicknessMm", _setFullHeadMPRSliceThicknessMm_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRZoomSyncEnabled", _setFullHeadMPRZoomSyncEnabled_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRChrome", _setFullHeadMPRChrome_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRActivePane", _getFullHeadMPRActivePane_dec, MIRenderAPI);
__decorateElement(_init, 1, "stepFullHeadMPRSlice", _stepFullHeadMPRSlice_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRViewSnapshot", _getFullHeadMPRViewSnapshot_dec, MIRenderAPI);
__decorateElement(_init, 1, "captureFullHeadMPRScreenshot", _captureFullHeadMPRScreenshot_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRToothSliceMode", _setFullHeadMPRToothSliceMode_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRFocus", _getFullHeadMPRFocus_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRColorProfile", _setFullHeadMPRColorProfile_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRMetrics", _getFullHeadMPRMetrics_dec, MIRenderAPI);
__decorateElement(_init, 1, "getFullHeadMPRBenchmark", _getFullHeadMPRBenchmark_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRDebugOptions", _setFullHeadMPRDebugOptions_dec, MIRenderAPI);
__decorateElement(_init, 1, "setFullHeadMPRBackend", _setFullHeadMPRBackend_dec, MIRenderAPI);
__decorateElement(_init, 1, "showSlice", _showSlice_dec, MIRenderAPI);
__decorateElement(_init, 1, "addSliceEditorImage", _addSliceEditorImage_dec, MIRenderAPI);
__decorateElement(_init, 1, "setSliceEditorAnnotations", _setSliceEditorAnnotations_dec, MIRenderAPI);
__decorateElement(_init, 1, "removeSliceEditorImage", _removeSliceEditorImage_dec, MIRenderAPI);
__decorateElement(_init, 1, "hideSlice", _hideSlice_dec, MIRenderAPI);
__decorateElement(_init, 1, "hideMarks", _hideMarks_dec, MIRenderAPI);
__decorateElement(_init, 1, "showMarks", _showMarks_dec, MIRenderAPI);
__decorateElement(_init, 1, "activateMode", _activateMode_dec, MIRenderAPI);
__decorateElement(_init, 1, "getMaskEditState", _getMaskEditState_dec, MIRenderAPI);
__decorateElement(_init, 1, "applyMaskEdit", _applyMaskEdit_dec, MIRenderAPI);
__decorateElement(_init, 1, "cancelMaskEdit", _cancelMaskEdit_dec, MIRenderAPI);
__decorateElement(_init, 1, "resetMaskEdit", _resetMaskEdit_dec, MIRenderAPI);
__decorateElement(_init, 1, "setMaskDeformationRadii", _setMaskDeformationRadii_dec, MIRenderAPI);
__decorateElement(_init, 1, "lookAtBox", _lookAtBox_dec, MIRenderAPI);
__decorateElement(_init, 1, "specifyMaskGroups", _specifyMaskGroups_dec, MIRenderAPI);
__decorateElement(_init, 1, "addPeriapicalRadiolucencyMasks", _addPeriapicalRadiolucencyMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "showPeriapicalRadiolucencyMasks", _showPeriapicalRadiolucencyMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "hidePeriapicalRadiolucencyMasks", _hidePeriapicalRadiolucencyMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "addMasks", _addMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "deleteMasks", _deleteMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "deleteMasksByID", _deleteMasksByID_dec, MIRenderAPI);
__decorateElement(_init, 1, "showMasks", _showMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "hideMasks", _hideMasks_dec, MIRenderAPI);
__decorateElement(_init, 1, "setPBLs", _setPBLs_dec, MIRenderAPI);
__decorateElement(_init, 1, "deletePBL", _deletePBL_dec, MIRenderAPI);
__decorateElement(_init, 1, "showTeethNumbers", _showTeethNumbers_dec, MIRenderAPI);
__decorateElement(_init, 1, "hideTeethNumbers", _hideTeethNumbers_dec, MIRenderAPI);
__decorateElement(_init, 1, "hidePBLs", _hidePBLs_dec, MIRenderAPI);
__decorateElement(_init, 1, "showPBLs", _showPBLs_dec, MIRenderAPI);
__decorateElement(_init, 1, "hidePBLsText", _hidePBLsText_dec, MIRenderAPI);
__decorateElement(_init, 1, "showPBLsText", _showPBLsText_dec, MIRenderAPI);
__decorateElement(_init, 1, "addConditionBoxes", _addConditionBoxes_dec, MIRenderAPI);
__decorateElement(_init, 1, "deleteConditionBoxes", _deleteConditionBoxes_dec, MIRenderAPI);
__decorateElement(_init, 1, "setTeeth", _setTeeth_dec, MIRenderAPI);
__decorateElement(_init, 1, "toggleInversion", _toggleInversion_dec, MIRenderAPI);
__decorateElement(_init, 1, "invertColors", _invertColors_dec, MIRenderAPI);
__decorateElement(_init, 1, "straightColors", _straightColors_dec, MIRenderAPI);
__decorateElement(_init, 1, "invertSliceEditorColors", _invertSliceEditorColors_dec, MIRenderAPI);
__decorateElement(_init, 1, "straightSliceEditorColors", _straightSliceEditorColors_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertToothCrops", _insertToothCrops_dec, MIRenderAPI);
__decorateElement(_init, 1, "updatePictures", _updatePictures_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertSlicesToExistingImages", _insertSlicesToExistingImages_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertGeneratedSlices", _insertGeneratedSlices_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertUserUploadedSlices", _insertUserUploadedSlices_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertSelectedToothSlices", _insertSelectedToothSlices_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertCariesSlices", _insertCariesSlices_dec, MIRenderAPI);
__decorateElement(_init, 1, "insertPeriapicalSlices", _insertPeriapicalSlices_dec, MIRenderAPI);
__decorateElement(_init, 1, "getAnnotations", _getAnnotations_dec, MIRenderAPI);
__decorateElement(_init, 1, "addAppEventListener", _addAppEventListener_dec, MIRenderAPI);
__decorateElement(_init, 1, "addAppEventListenerOnce", _addAppEventListenerOnce_dec, MIRenderAPI);
__decorateElement(_init, 1, "removeAppEventListener", _removeAppEventListener_dec, MIRenderAPI);
__decorateElement(_init, 1, "addEventListener", _addEventListener_dec, MIRenderAPI);
__decorateElement(_init, 1, "addEventListenerOnce", _addEventListenerOnce_dec, MIRenderAPI);
__decorateElement(_init, 1, "removeEventListener", _removeEventListener_dec, MIRenderAPI);
__decoratorMetadata(_init, MIRenderAPI);
var MIRenderAPI$1 = new MIRenderAPI();

const MIRenderMiddleware$1 = () => (next) => (action) => {
  if (ActionNames.includes(action.type)) {
    Orchestrator$1.dispatchEvent(action);
  }
  next(action);
};

const insertToExistingImages = (props, ref) => {
  MedicalImageRender.insertSlicesToExistingImages({
    toothID: props.toothID,
    container: ref.current,
    onClick: props.onClick,
    selectIMG: "byAssetID",
    useIMG: "existing"
  });
};
const SlicesMIR$1 = (props, insert) => {
  const ref = useRef(null);
  useEffect(
    () => {
      if (!ref.current) return;
      const add = (e) => {
        if (e.some(
          (e2) => e2.type === "childList" && e2.target instanceof Element && e2.target.tagName !== "svg" && e2.addedNodes.length
        )) {
          insert ? insert(props, ref) : insertToExistingImages(props, ref);
        }
      };
      const mutationObserver = new MutationObserver(add);
      mutationObserver.observe(ref.current, {
        attributeFilter: ["id", "class", "style"],
        subtree: true,
        childList: true
      });
      insert ? insert(props, ref) : insertToExistingImages(props, ref);
      return () => {
        mutationObserver.disconnect();
      };
    },
    [props.toothID]
  );
  return /* @__PURE__ */ jsx("div", { className: props.className, ref, children: props.children });
};

const TOOTH_BASIS_SLICE_STEP_MM = 1;
const TOOTH_BASIS_SLAB_THICKNESS_MM = 0.5;
const TOOTH_BASIS_DEFAULT_SPACING_MM = 0.3;
const resolveToothBasisSpacingOutMm = ({
  spacingOutMm,
  voxelSpacing
}) => {
  if (spacingOutMm !== void 0) {
    return spacingOutMm;
  }
  const finest = Math.min(voxelSpacing.x, voxelSpacing.y, voxelSpacing.z);
  return Number.isFinite(finest) && finest > 0 ? finest : TOOTH_BASIS_DEFAULT_SPACING_MM;
};
const AXIAL_PAD_X_MM = 3;
const AXIAL_PAD_Y_MM = 3;
const AXIAL_OFFSET_ROOT_PAD_MM = 3;
const SAGITTAL_PAD_Y_MM = 2;
const FRONTAL_PAD_X_MM = 4;
const VERTICAL_CROWN_PAD_MM = 1;
const VERTICAL_ROOT_PAD_MM = 10;
const TOOTH_BASIS_SERIES_NAMES = [
  "axial",
  "sagittal",
  "frontal"
];
const MIN_SLICE_STEP_MM = 0.04;
const MAX_SLICES_PER_SERIES = 4096;
const isUsableStepMm = (stepMm) => Number.isFinite(stepMm) && stepMm >= MIN_SLICE_STEP_MM;
const DEFAULT_TOOTH_BASIS_SLICE_OPTIONS = {
  sliceStepMm: TOOTH_BASIS_SLICE_STEP_MM,
  thickness: { kind: "slab", thicknessMm: TOOTH_BASIS_SLAB_THICKNESS_MM }
};
const resolveToothBasisSliceOptions = (seriesName, sliceOptions) => {
  const requested = sliceOptions?.[seriesName];
  if (!requested || !isUsableStepMm(requested.sliceStepMm)) {
    return DEFAULT_TOOTH_BASIS_SLICE_OPTIONS;
  }
  return requested;
};
const projectCornersOntoAxis = (box, origin, axis) => {
  const locals = [box.min.x, box.max.x].flatMap(
    (x) => [box.min.y, box.max.y].flatMap(
      (y) => [box.min.z, box.max.z].map(
        (z) => (x - origin.x) * axis.x + (y - origin.y) * axis.y + (z - origin.z) * axis.z
      )
    )
  );
  return { min: Math.min(...locals), max: Math.max(...locals) };
};
const projectBoxToLocalRanges = (box, origin, basis) => ({
  x: projectCornersOntoAxis(box, origin, basis.md),
  y: projectCornersOntoAxis(box, origin, basis.vo),
  z: projectCornersOntoAxis(box, origin, basis.cr)
});
const pad = (range, padding) => ({
  min: range.min - padding,
  max: range.max + padding
});
const buildOffsets = (from, to, requestedStepMm) => {
  const spanMm = Math.max(0, to + 1e-9 - from);
  const stepMm = Math.max(
    requestedStepMm,
    spanMm / (MAX_SLICES_PER_SERIES - 1)
  );
  const count = Math.max(1, Math.floor(spanMm / stepMm) + 1);
  return {
    offsetsMm: Array.from(
      { length: count },
      (unused, index) => Number((from + index * stepMm).toFixed(6))
    ),
    stepMm
  };
};
const toRasterSize = (range, spacingMm) => Math.max(1, Math.ceil((range.max - range.min) / spacingMm));
const mid = (range) => (range.min + range.max) / 2;
const dotV = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const crossV = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x
});
const negateV = (v) => ({ x: -v.x, y: -v.y, z: -v.z });
const normalizeV = (v) => {
  const length = Math.sqrt(dotV(v, v));
  return Number.isFinite(length) && length > 1e-6 ? { x: v.x / length, y: v.y / length, z: v.z / length } : null;
};
const projectOntoPlane = (vector, planeNormal) => {
  const along = dotV(vector, planeNormal);
  return {
    x: vector.x - planeNormal.x * along,
    y: vector.y - planeNormal.y * along,
    z: vector.z - planeNormal.z * along
  };
};
const rasterRange = (range, sizePx, spacingMm) => {
  const center = mid(range);
  const half = sizePx * spacingMm / 2;
  return { min: center - half, max: center + half };
};
const resolveAxialScreenAxes = (basis) => {
  const crownToRootAxis = {
    x: basis.cr.x * basis.crownToRootSign,
    y: basis.cr.y * basis.crownToRootSign,
    z: basis.cr.z * basis.crownToRootSign
  };
  const u = normalizeV(projectOntoPlane({ x: 1, y: 0, z: 0 }, crownToRootAxis)) ?? basis.md;
  const vCandidate = normalizeV(crossV(crownToRootAxis, u)) ?? basis.vo;
  return {
    u,
    v: dotV(vCandidate, { x: 0, y: 1, z: 0 }) <= 0 ? vCandidate : negateV(vCandidate)
  };
};
const buildToothBasisSeriesSpec = ({
  axialScreenRanges,
  basis,
  localRanges,
  origin,
  sliceOptions,
  spacingOutMm = TOOTH_BASIS_DEFAULT_SPACING_MM
}) => {
  if (!Number.isFinite(spacingOutMm) || spacingOutMm <= 0) {
    return null;
  }
  const { x, y, z } = localRanges;
  const centerAt = (local) => ({
    x: origin.x + basis.md.x * local.md + basis.vo.x * local.vo + basis.cr.x * local.cr,
    y: origin.y + basis.md.y * local.md + basis.vo.y * local.vo + basis.cr.y * local.cr,
    z: origin.z + basis.md.z * local.md + basis.vo.z * local.vo + basis.cr.z * local.cr
  });
  const buildSeries = ({
    name,
    offsetsFrom,
    offsetsTo,
    toLocalCenter,
    uAxis,
    uAxisLocal,
    uRange,
    vAxis,
    vAxisLocal,
    vRange,
    normal,
    normalLocal
  }) => {
    const widthPx = toRasterSize(uRange, spacingOutMm);
    const heightPx = toRasterSize(vRange, spacingOutMm);
    const requested = resolveToothBasisSliceOptions(name, sliceOptions);
    const { offsetsMm, stepMm } = buildOffsets(
      offsetsFrom,
      offsetsTo,
      requested.sliceStepMm
    );
    return {
      name,
      sliceStepMm: stepMm,
      frame: {
        heightPx,
        normalLocal,
        plane: { normal, uAxis, vAxis },
        spacingMm: spacingOutMm,
        uAxisLocal,
        uRangeMm: rasterRange(uRange, widthPx, spacingOutMm),
        vAxisLocal,
        vRangeMm: rasterRange(vRange, heightPx, spacingOutMm),
        widthPx
      },
      slices: offsetsMm.map((offsetMm) => ({
        offsetMm,
        layout: {
          centerPatientMm: centerAt(toLocalCenter(offsetMm)),
          uAxis,
          vAxis,
          normal,
          width: widthPx,
          height: heightPx,
          mmPerPixelU: spacingOutMm,
          mmPerPixelV: spacingOutMm
        }
      }))
    };
  };
  const sign = basis.crownToRootSign;
  const anatomicalZ = sign > 0 ? z : { min: -z.max, max: -z.min };
  const crownToRootAxis = {
    x: basis.cr.x * sign,
    y: basis.cr.y * sign,
    z: basis.cr.z * sign
  };
  const axialU = pad(x, AXIAL_PAD_X_MM);
  const axialV = pad(y, AXIAL_PAD_Y_MM);
  const verticalV = sign > 0 ? {
    min: z.min - VERTICAL_CROWN_PAD_MM,
    max: z.max + VERTICAL_ROOT_PAD_MM
  } : {
    min: z.min - VERTICAL_ROOT_PAD_MM,
    max: z.max + VERTICAL_CROWN_PAD_MM
  };
  const sagittalU = pad(y, SAGITTAL_PAD_Y_MM);
  const frontalU = pad(x, FRONTAL_PAD_X_MM);
  const { u: axialUAxis, v: axialVAxis } = resolveAxialScreenAxes(basis);
  const localComponents = (axis) => ({
    md: dotV(axis, basis.md),
    vo: dotV(axis, basis.vo),
    cr: dotV(axis, crownToRootAxis)
  });
  const boundingFrameRange = (axis) => {
    const mdComponent = dotV(axis, basis.md);
    const voComponent = dotV(axis, basis.vo);
    const center = mid(axialU) * mdComponent + mid(axialV) * voComponent;
    const half = (Math.abs((axialU.max - axialU.min) * mdComponent) + Math.abs((axialV.max - axialV.min) * voComponent)) / 2;
    return { min: center - half, max: center + half };
  };
  const axialFrameU = axialScreenRanges ? pad(axialScreenRanges.u, AXIAL_PAD_X_MM) : boundingFrameRange(axialUAxis);
  const axialFrameV = axialScreenRanges ? pad(axialScreenRanges.v, AXIAL_PAD_Y_MM) : boundingFrameRange(axialVAxis);
  const axialCenter = {
    md: mid(axialFrameU) * dotV(axialUAxis, basis.md) + mid(axialFrameV) * dotV(axialVAxis, basis.md),
    vo: mid(axialFrameU) * dotV(axialUAxis, basis.vo) + mid(axialFrameV) * dotV(axialVAxis, basis.vo)
  };
  return {
    basis,
    series: [
      buildSeries({
        name: "axial",
        offsetsFrom: anatomicalZ.min,
        offsetsTo: anatomicalZ.max + AXIAL_OFFSET_ROOT_PAD_MM,
        toLocalCenter: (offsetMm) => ({
          md: axialCenter.md,
          vo: axialCenter.vo,
          cr: offsetMm * sign
        }),
        uAxis: axialUAxis,
        uAxisLocal: localComponents(axialUAxis),
        uRange: axialFrameU,
        vAxis: axialVAxis,
        vAxisLocal: localComponents(axialVAxis),
        vRange: axialFrameV,
        normal: crownToRootAxis,
        normalLocal: { md: 0, vo: 0, cr: 1 }
      }),
      buildSeries({
        name: "sagittal",
        offsetsFrom: x.min,
        offsetsTo: x.max,
        toLocalCenter: (offsetMm) => ({
          md: offsetMm,
          vo: mid(sagittalU),
          cr: mid(verticalV)
        }),
        // Server VO rasters keep the screen right per patient side (measured:
        // the left side mirrors the anatomical vo axis)
        uAxis: basis.sideSign > 0 ? basis.vo : { x: -basis.vo.x, y: -basis.vo.y, z: -basis.vo.z },
        uAxisLocal: { md: 0, vo: basis.sideSign, cr: 0 },
        uRange: basis.sideSign > 0 ? sagittalU : { min: -sagittalU.max, max: -sagittalU.min },
        vAxis: basis.cr,
        vAxisLocal: { md: 0, vo: 0, cr: sign },
        vRange: verticalV,
        normal: basis.md,
        normalLocal: { md: 1, vo: 0, cr: 0 }
      }),
      buildSeries({
        name: "frontal",
        offsetsFrom: y.min,
        offsetsTo: y.max,
        toLocalCenter: (offsetMm) => ({
          md: mid(frontalU),
          vo: offsetMm,
          cr: mid(verticalV)
        }),
        uAxis: basis.md,
        uAxisLocal: { md: 1, vo: 0, cr: 0 },
        uRange: frontalU,
        vAxis: basis.cr,
        vAxisLocal: { md: 0, vo: 0, cr: sign },
        vRange: verticalV,
        normal: basis.vo,
        normalLocal: { md: 0, vo: 1, cr: 0 }
      })
    ]
  };
};
const planeCorners = (layout) => {
  const halfWidthMm = layout.width / 2 * layout.mmPerPixelU;
  const halfHeightMm = layout.height / 2 * layout.mmPerPixelV;
  return [-1, 1].flatMap(
    (uSign) => [-1, 1].map((vSign) => ({
      x: layout.centerPatientMm.x + layout.uAxis.x * halfWidthMm * uSign + layout.vAxis.x * halfHeightMm * vSign,
      y: layout.centerPatientMm.y + layout.uAxis.y * halfWidthMm * uSign + layout.vAxis.y * halfHeightMm * vSign,
      z: layout.centerPatientMm.z + layout.uAxis.z * halfWidthMm * uSign + layout.vAxis.z * halfHeightMm * vSign
    }))
  );
};
const findSpecSlice = ({
  plane,
  spec
}) => {
  const series = spec.series.find((entry) => entry.name === plane.seriesName);
  if (!series) {
    return null;
  }
  const nearest = series.slices.reduce(
    (best, slice) => !best || Math.abs(slice.offsetMm - plane.offsetMm) < Math.abs(best.offsetMm - plane.offsetMm) ? slice : best,
    null
  );
  return nearest && Math.abs(nearest.offsetMm - plane.offsetMm) <= series.sliceStepMm / 2 ? nearest : null;
};
const specPlanePoints = (spec) => spec.series.flatMap(
  (series) => series.slices.flatMap((slice) => planeCorners(slice.layout))
);

const TOOTH_TISSUE_THRESHOLD_HU = 1100;
const TOOTH_EXTENT_THRESHOLDS_HU = [700, 900, 1100, 1300];
const HISTOGRAM_BIN_MM = 0.25;
const MIN_TOOTH_VOXEL_COUNT = 1e3;
const HORIZONTAL_PERCENTILE = 0.01;
const AXIAL_PERCENTILE = 5e-3;
const MIN_VALLEY_KEEP_RATIO = 0.5;
const VALLEY_MAX_FRACTION_OF_PEAK = 0.55;
const VALLEY_RISE_RATIO = 1.5;
const SHOULDER_FRACTION_OF_PEAK = 0.35;
const SHOULDER_FLOOR_FRACTION_OF_PEAK = 0.05;
const SHOULDER_RUN_BINS = 12;
const dotVec3 = (left, right) => left.x * right.x + left.y * right.y + left.z * right.z;
const makeHistogram = (binCount) => ({
  counts: new Uint32Array(binCount),
  total: 0
});
const percentileFromHistogram = (histogram, originMm, percentile) => {
  if (histogram.total < MIN_TOOTH_VOXEL_COUNT) {
    return null;
  }
  const lowTarget = histogram.total * percentile;
  const highTarget = histogram.total * (1 - percentile);
  let cumulative = 0;
  let lowBin = -1;
  let highBin = -1;
  for (let bin = 0; bin < histogram.counts.length; bin += 1) {
    cumulative += histogram.counts[bin];
    if (lowBin === -1 && cumulative >= lowTarget) {
      lowBin = bin;
    }
    if (cumulative >= highTarget) {
      highBin = bin;
      break;
    }
  }
  if (lowBin === -1 || highBin === -1 || highBin < lowBin) {
    return null;
  }
  return {
    min: originMm + lowBin * HISTOGRAM_BIN_MM,
    max: originMm + (highBin + 1) * HISTOGRAM_BIN_MM
  };
};
const valleyCutFromHistogram = (histogram, originMm) => {
  const { counts } = histogram;
  const centerBin = Math.min(
    counts.length - 1,
    Math.max(0, Math.round(-originMm / HISTOGRAM_BIN_MM))
  );
  const peak = Math.max(...counts);
  if (peak === 0) {
    return null;
  }
  const valleyCeiling = peak * VALLEY_MAX_FRACTION_OF_PEAK;
  const shoulderLevel = peak * SHOULDER_FRACTION_OF_PEAK;
  const shoulderFloor = peak * SHOULDER_FLOOR_FRACTION_OF_PEAK;
  const walk = (step) => {
    let minValue = Number.POSITIVE_INFINITY;
    let minBin = centerBin;
    let shoulderRun = 0;
    let shoulderBin = centerBin;
    for (let bin = centerBin; bin >= 0 && bin < counts.length; bin += step) {
      const value = counts[bin];
      if (value < minValue) {
        minValue = value;
        minBin = bin;
      }
      if (minValue <= valleyCeiling && value > Math.max(minValue * VALLEY_RISE_RATIO, peak * 0.05)) {
        return minBin;
      }
      if (value >= shoulderFloor && value < shoulderLevel) {
        if (shoulderRun === 0) {
          shoulderBin = bin;
        }
        shoulderRun += 1;
        if (shoulderRun >= SHOULDER_RUN_BINS) {
          return shoulderBin;
        }
      } else {
        shoulderRun = 0;
      }
    }
    return step === 1 ? counts.length : -1;
  };
  const highBin = walk(1);
  const lowBin = walk(-1);
  const min = originMm + (lowBin + 1) * HISTOGRAM_BIN_MM;
  const max = originMm + highBin * HISTOGRAM_BIN_MM;
  return max > min ? { min, max } : null;
};
const AXIS_REFINE_PASSES = 3;
const AXIS_REFINE_RADIUS_RATIO = 1.5;
const POWER_ITERATIONS = 48;
const principalAxisOf = (moments, seed) => {
  if (moments.count < MIN_TOOTH_VOXEL_COUNT) {
    return null;
  }
  const meanX = moments.x / moments.count;
  const meanY = moments.y / moments.count;
  const meanZ = moments.z / moments.count;
  const xx = moments.xx / moments.count - meanX * meanX;
  const xy = moments.xy / moments.count - meanX * meanY;
  const xz = moments.xz / moments.count - meanX * meanZ;
  const yy = moments.yy / moments.count - meanY * meanY;
  const yz = moments.yz / moments.count - meanY * meanZ;
  const zz = moments.zz / moments.count - meanZ * meanZ;
  let vector = seed;
  for (let step = 0; step < POWER_ITERATIONS; step += 1) {
    const next = {
      x: xx * vector.x + xy * vector.y + xz * vector.z,
      y: xy * vector.x + yy * vector.y + yz * vector.z,
      z: xz * vector.x + yz * vector.y + zz * vector.z
    };
    const length = Math.sqrt(next.x * next.x + next.y * next.y + next.z * next.z);
    if (!(length > 0)) {
      return null;
    }
    vector = { x: next.x / length, y: next.y / length, z: next.z / length };
  }
  return vector;
};
const intersectRanges = (left, right) => {
  const min = Math.max(left.min, right.min);
  const max = Math.min(left.max, right.max);
  return max > min ? { min, max } : null;
};
const measureToothLocalExtents = ({
  axisSeedRadiusMm,
  basis,
  geometry,
  origin,
  screenAxes,
  searchBox,
  skipAxis = false,
  skipRootProfiles = false,
  volume
}) => {
  const screenComponents = screenAxes ? {
    uMd: dotVec3(screenAxes.u, basis.md),
    uVo: dotVec3(screenAxes.u, basis.vo),
    vMd: dotVec3(screenAxes.v, basis.md),
    vVo: dotVec3(screenAxes.v, basis.vo)
  } : null;
  const axes = screenAxes ? [basis.md, basis.vo, basis.cr, screenAxes.u, screenAxes.v] : [basis.md, basis.vo, basis.cr];
  const searchCorners = [searchBox.min.x, searchBox.max.x].flatMap(
    (x) => [searchBox.min.y, searchBox.max.y].flatMap(
      (y) => [searchBox.min.z, searchBox.max.z].map((z) => ({ x, y, z }))
    )
  );
  const axisStarts = axes.map(
    (axis) => Math.min(
      ...searchCorners.map(
        (corner) => (corner.x - origin.x) * axis.x + (corner.y - origin.y) * axis.y + (corner.z - origin.z) * axis.z
      )
    )
  );
  const axisSpans = axes.map(
    (axis, axisIndex) => Math.max(
      ...searchCorners.map(
        (corner) => (corner.x - origin.x) * axis.x + (corner.y - origin.y) * axis.y + (corner.z - origin.z) * axis.z
      )
    ) - axisStarts[axisIndex]
  );
  const binCounts = axisSpans.map(
    (span) => Math.max(1, Math.ceil(span / HISTOGRAM_BIN_MM) + 1)
  );
  const histograms = TOOTH_EXTENT_THRESHOLDS_HU.map(
    () => binCounts.map((binCount) => makeHistogram(binCount))
  );
  const [mdBinCount, voBinCount, crBinCount] = binCounts;
  const mdByCr = new Uint32Array(skipRootProfiles ? 0 : crBinCount * mdBinCount);
  const voByCr = new Uint32Array(skipRootProfiles ? 0 : crBinCount * voBinCount);
  const moments = {
    count: 0,
    x: 0,
    xx: 0,
    xy: 0,
    xz: 0,
    y: 0,
    yy: 0,
    yz: 0,
    z: 0,
    zz: 0
  };
  const { data, originIJK, shape } = volume;
  const sliceStride = shape.x * shape.y;
  const lowestThreshold = Math.min(...TOOTH_EXTENT_THRESHOLDS_HU);
  const locals = new Float64Array(axes.length);
  for (let k = 0; k < shape.z; k += 1) {
    const patientZ = (originIJK.z + k) * geometry.spacing.z + geometry.patientCsOrigin.z;
    if (patientZ < searchBox.min.z || patientZ > searchBox.max.z) {
      continue;
    }
    for (let j = 0; j < shape.y; j += 1) {
      const patientY = (originIJK.y + j) * geometry.spacing.y + geometry.patientCsOrigin.y;
      if (patientY < searchBox.min.y || patientY > searchBox.max.y) {
        continue;
      }
      const rowOffset = k * sliceStride + j * shape.x;
      for (let i = 0; i < shape.x; i += 1) {
        const value = data[rowOffset + i];
        if (value <= lowestThreshold) {
          continue;
        }
        const patientX = (originIJK.x + i) * geometry.spacing.x + geometry.patientCsOrigin.x;
        if (patientX < searchBox.min.x || patientX > searchBox.max.x) {
          continue;
        }
        const dx = patientX - origin.x;
        const dy = patientY - origin.y;
        const dz = patientZ - origin.z;
        const mdLocal = dx * basis.md.x + dy * basis.md.y + dz * basis.md.z;
        const voLocal = dx * basis.vo.x + dy * basis.vo.y + dz * basis.vo.z;
        const crLocal = dx * basis.cr.x + dy * basis.cr.y + dz * basis.cr.z;
        locals[0] = mdLocal;
        locals[1] = voLocal;
        locals[2] = crLocal;
        if (screenComponents) {
          locals[3] = mdLocal * screenComponents.uMd + voLocal * screenComponents.uVo;
          locals[4] = mdLocal * screenComponents.vMd + voLocal * screenComponents.vVo;
        }
        for (let thresholdIndex = 0; thresholdIndex < TOOTH_EXTENT_THRESHOLDS_HU.length; thresholdIndex += 1) {
          if (value <= TOOTH_EXTENT_THRESHOLDS_HU[thresholdIndex]) {
            continue;
          }
          for (let axisIndex = 0; axisIndex < axes.length; axisIndex += 1) {
            const bin = Math.floor(
              (locals[axisIndex] - axisStarts[axisIndex]) / HISTOGRAM_BIN_MM
            );
            const histogram = histograms[thresholdIndex][axisIndex];
            if (bin >= 0 && bin < histogram.counts.length) {
              histogram.counts[bin] += 1;
              histogram.total += 1;
            }
          }
        }
        if (value > TOOTH_TISSUE_THRESHOLD_HU) {
          moments.count += 1;
          moments.x += dx;
          moments.y += dy;
          moments.z += dz;
          moments.xx += dx * dx;
          moments.xy += dx * dy;
          moments.xz += dx * dz;
          moments.yy += dy * dy;
          moments.yz += dy * dz;
          moments.zz += dz * dz;
          if (!skipRootProfiles) {
            const mdBin = Math.floor((mdLocal - axisStarts[0]) / HISTOGRAM_BIN_MM);
            const voBin = Math.floor((voLocal - axisStarts[1]) / HISTOGRAM_BIN_MM);
            const crBin = Math.floor((crLocal - axisStarts[2]) / HISTOGRAM_BIN_MM);
            if (crBin >= 0 && crBin < crBinCount) {
              if (mdBin >= 0 && mdBin < mdBinCount) {
                mdByCr[crBin * mdBinCount + mdBin] += 1;
              }
              if (voBin >= 0 && voBin < voBinCount) {
                voByCr[crBin * voBinCount + voBin] += 1;
              }
            }
          }
        }
      }
    }
  }
  const extentsByThreshold = {};
  let screenExtents = null;
  TOOTH_EXTENT_THRESHOLDS_HU.forEach((threshold, thresholdIndex) => {
    const [xHistogram, yHistogram, zHistogram] = histograms[thresholdIndex];
    const horizontal = (histogram, axisIndex) => {
      const percentile = percentileFromHistogram(
        histogram,
        axisStarts[axisIndex],
        HORIZONTAL_PERCENTILE
      );
      const valley = valleyCutFromHistogram(histogram, axisStarts[axisIndex]);
      if (!percentile) {
        return null;
      }
      if (!valley) {
        return percentile;
      }
      const cut = intersectRanges(percentile, valley);
      const keptRatio = cut ? (cut.max - cut.min) / (percentile.max - percentile.min) : 0;
      return cut && keptRatio >= MIN_VALLEY_KEEP_RATIO ? cut : percentile;
    };
    const x = horizontal(xHistogram, 0);
    const y = horizontal(yHistogram, 1);
    const z = percentileFromHistogram(zHistogram, axisStarts[2], AXIAL_PERCENTILE);
    extentsByThreshold[threshold] = x && y && z ? { x, y, z } : null;
    if (screenAxes && threshold === TOOTH_TISSUE_THRESHOLD_HU) {
      const u = horizontal(histograms[thresholdIndex][3], 3);
      const v = horizontal(histograms[thresholdIndex][4], 4);
      screenExtents = u && v ? { u, v } : null;
    }
  });
  const scanCylinderMoments = ({
    axis,
    radiusMm
  }) => {
    const scanned = {
      count: 0,
      radial2: 0,
      x: 0,
      xx: 0,
      xy: 0,
      xz: 0,
      y: 0,
      yy: 0,
      yz: 0,
      z: 0,
      zz: 0
    };
    const radius2 = radiusMm * radiusMm;
    for (let k = 0; k < shape.z; k += 1) {
      const patientZ = (originIJK.z + k) * geometry.spacing.z + geometry.patientCsOrigin.z;
      if (patientZ < searchBox.min.z || patientZ > searchBox.max.z) {
        continue;
      }
      for (let j = 0; j < shape.y; j += 1) {
        const patientY = (originIJK.y + j) * geometry.spacing.y + geometry.patientCsOrigin.y;
        if (patientY < searchBox.min.y || patientY > searchBox.max.y) {
          continue;
        }
        const rowOffset = k * sliceStride + j * shape.x;
        for (let i = 0; i < shape.x; i += 1) {
          const value = data[rowOffset + i];
          if (value <= TOOTH_TISSUE_THRESHOLD_HU) {
            continue;
          }
          const patientX = (originIJK.x + i) * geometry.spacing.x + geometry.patientCsOrigin.x;
          if (patientX < searchBox.min.x || patientX > searchBox.max.x) {
            continue;
          }
          const dx = patientX - origin.x;
          const dy = patientY - origin.y;
          const dz = patientZ - origin.z;
          const along = dx * axis.x + dy * axis.y + dz * axis.z;
          const radial2 = dx * dx + dy * dy + dz * dz - along * along;
          if (radial2 > radius2) {
            continue;
          }
          scanned.count += 1;
          scanned.radial2 += radial2;
          scanned.x += dx;
          scanned.y += dy;
          scanned.z += dz;
          scanned.xx += dx * dx;
          scanned.xy += dx * dy;
          scanned.xz += dx * dz;
          scanned.yy += dy * dy;
          scanned.yz += dy * dz;
          scanned.zz += dz * dz;
        }
      }
    }
    return scanned;
  };
  const alignedWithCr = (axis) => axis.x * basis.cr.x + axis.y * basis.cr.y + axis.z * basis.cr.z >= 0 ? axis : { x: -axis.x, y: -axis.y, z: -axis.z };
  const refineMassAxis = (extents) => {
    const fallback = principalAxisOf(moments, basis.cr);
    const fallbackAxis = fallback ? alignedWithCr(fallback) : null;
    const seedRadiusMm = axisSeedRadiusMm ?? (extents ? Math.min(
      extents.x.max - extents.x.min,
      extents.y.max - extents.y.min
    ) / 2 : 0);
    if (!(seedRadiusMm > 0)) {
      return fallbackAxis;
    }
    let axis = basis.cr;
    let radiusMm = seedRadiusMm;
    for (let pass = 0; pass < AXIS_REFINE_PASSES; pass += 1) {
      const scanned = scanCylinderMoments({ axis, radiusMm });
      const next = principalAxisOf(scanned, axis);
      if (!next) {
        return axis;
      }
      axis = alignedWithCr(next);
      radiusMm = AXIS_REFINE_RADIUS_RATIO * Math.sqrt(scanned.radial2 / scanned.count);
      if (!(radiusMm > 0)) {
        return axis;
      }
    }
    return axis;
  };
  const workingExtents = extentsByThreshold[TOOTH_TISSUE_THRESHOLD_HU] ?? null;
  const massAxis = skipAxis ? null : refineMassAxis(workingExtents);
  const workingIndex = TOOTH_EXTENT_THRESHOLDS_HU.indexOf(
    TOOTH_TISSUE_THRESHOLD_HU
  );
  const workingHistograms = histograms[workingIndex];
  const centroidAlongAxis = (histogram, axisStartMm) => {
    if (histogram.total === 0) {
      return null;
    }
    let weightedSum = 0;
    histogram.counts.forEach((count, binIndex) => {
      weightedSum += count * (axisStartMm + HISTOGRAM_BIN_MM * (binIndex + 0.5));
    });
    return weightedSum / histogram.total;
  };
  const centroidX = centroidAlongAxis(workingHistograms[0], axisStarts[0]);
  const centroidY = centroidAlongAxis(workingHistograms[1], axisStarts[1]);
  const centroidZ = centroidAlongAxis(workingHistograms[2], axisStarts[2]);
  return {
    centroidMm: centroidX !== null && centroidY !== null && centroidZ !== null ? { x: centroidX, y: centroidY, z: centroidZ } : null,
    massAxis,
    screenExtents,
    extents: workingExtents,
    extentsByThreshold,
    profiles: {
      axisStartsMm: axisStarts.map((start) => Number(start.toFixed(2))),
      binMm: HISTOGRAM_BIN_MM,
      mdByCr: {
        columnCount: mdBinCount,
        counts: Array.from(mdByCr),
        rowCount: crBinCount
      },
      voByCr: {
        columnCount: voBinCount,
        counts: Array.from(voByCr),
        rowCount: crBinCount
      },
      x: Array.from(workingHistograms[0].counts),
      y: Array.from(workingHistograms[1].counts),
      z: Array.from(workingHistograms[2].counts)
    }
  };
};

const SHORT_SEARCH_BOX_CR_MM = 15;
const EXPANDED_SEARCH_BOX_CR_MM = 24;
const enclosingPatientBox = (points) => ({
  min: {
    x: Math.min(...points.map((point) => point.x)),
    y: Math.min(...points.map((point) => point.y)),
    z: Math.min(...points.map((point) => point.z))
  },
  max: {
    x: Math.max(...points.map((point) => point.x)),
    y: Math.max(...points.map((point) => point.y)),
    z: Math.max(...points.map((point) => point.z))
  }
});
const boxClearanceOf = ({
  basis,
  extents,
  origin,
  searchBox
}) => {
  const box = projectBoxToLocalRanges(searchBox, origin, basis);
  if (!extents) {
    return {
      crMax: 0,
      crMin: 0,
      mdMax: 0,
      mdMin: 0,
      voMax: 0,
      voMin: 0,
      worst: 0
    };
  }
  const clearance = {
    crMax: box.z.max - extents.z.max,
    crMin: extents.z.min - box.z.min,
    mdMax: box.x.max - extents.x.max,
    mdMin: extents.x.min - box.x.min,
    voMax: box.y.max - extents.y.max,
    voMin: extents.y.min - box.y.min
  };
  return { ...clearance, worst: Math.min(...Object.values(clearance)) };
};
const toothBasisMeasurementBox = ({
  basis,
  origin,
  searchBox
}) => {
  const ranges = projectBoxToLocalRanges(searchBox, origin, basis);
  const spanMm = ranges.z.max - ranges.z.min;
  if (spanMm >= SHORT_SEARCH_BOX_CR_MM) {
    return searchBox;
  }
  const center = {
    x: (searchBox.min.x + searchBox.max.x) / 2,
    y: (searchBox.min.y + searchBox.max.y) / 2,
    z: (searchBox.min.z + searchBox.max.z) / 2
  };
  const halfMm = EXPANDED_SEARCH_BOX_CR_MM / 2;
  const points = [
    ...patientBoxCorners(searchBox),
    ...[1, -1].map((sign) => ({
      x: center.x + basis.cr.x * sign * halfMm,
      y: center.y + basis.cr.y * sign * halfMm,
      z: center.z + basis.cr.z * sign * halfMm
    }))
  ];
  return enclosingPatientBox(points);
};
const prepareToothBasisMeasurement = async ({
  consumerID = TOOTH_SERIES_CONSUMER_ID,
  onProgress,
  reader,
  request,
  signal,
  spacingOutMm = request.spacingOutMm
}) => {
  const effectiveSpacingOutMm = resolveToothBasisSpacingOutMm({
    spacingOutMm,
    voxelSpacing: reader.geometry.spacing
  });
  const basis = resolveToothBasis({
    anatomicalBasis: request.anatomicalBasis,
    iso: request.iso
  });
  if (!basis) {
    throw new Error("Tooth basis is degenerate or the ISO is invalid");
  }
  const searchSpec = buildToothBasisSeriesSpec({
    basis,
    localRanges: projectBoxToLocalRanges(
      request.searchBox,
      request.origin,
      basis
    ),
    origin: request.origin,
    sliceOptions: request.sliceOptions,
    spacingOutMm: effectiveSpacingOutMm
  });
  if (!searchSpec) {
    throw new Error("Tooth-basis series spec is degenerate");
  }
  const specOf = (measured) => measured.extents ? buildToothBasisSeriesSpec({
    axialScreenRanges: measured.screenExtents,
    basis,
    localRanges: measured.extents,
    origin: request.origin,
    sliceOptions: request.sliceOptions,
    spacingOutMm: effectiveSpacingOutMm
  }) ?? searchSpec : searchSpec;
  const cachedMeasurement = getCachedToothBasisMeasurement(request);
  if (cachedMeasurement) {
    return {
      basis,
      boxClearanceMm: boxClearanceOf({
        basis,
        extents: cachedMeasurement.extents,
        origin: request.origin,
        searchBox: toothBasisMeasurementBox({
          basis,
          origin: request.origin,
          searchBox: request.searchBox
        })
      }),
      measurement: cachedMeasurement,
      spacingOutMm: effectiveSpacingOutMm,
      spec: specOf(cachedMeasurement)
    };
  }
  const measurementBox = toothBasisMeasurementBox({
    basis,
    origin: request.origin,
    searchBox: request.searchBox
  });
  const measurementRoi = buildToothSampleRoi({
    geometry: reader.geometry,
    paddingMm: CONFIGURED_MAX_SLAB_THICKNESS_MM / 2,
    patientPoints: patientBoxCorners(measurementBox)
  });
  if (!measurementRoi) {
    throw new Error("Tooth search box does not intersect the volume");
  }
  const measurementVolume = await loadStableToothRoi({
    consumerID,
    onProgress: stampToothRoiPhase("measurement", onProgress),
    reader,
    signal,
    voxelBox: measurementRoi
  });
  try {
    const measurement = measureToothLocalExtents({
      basis,
      geometry: reader.geometry,
      origin: request.origin,
      screenAxes: resolveAxialScreenAxes(basis),
      searchBox: measurementBox,
      skipAxis: true,
      volume: measurementVolume
    });
    cacheToothBasisMeasurement({ measurement, request });
    return {
      basis,
      boxClearanceMm: boxClearanceOf({
        basis,
        extents: measurement.extents,
        origin: request.origin,
        searchBox: measurementBox
      }),
      measurement,
      spacingOutMm: effectiveSpacingOutMm,
      spec: specOf(measurement)
    };
  } finally {
    measurementVolume.release();
  }
};
const prepareToothBasisRender = async ({
  consumerID = TOOTH_SERIES_CONSUMER_ID,
  onProgress,
  rasteriser,
  reader,
  request,
  signal,
  spacingOutMm = request.spacingOutMm
}) => {
  const effectiveSpacingOutMm = resolveToothBasisSpacingOutMm({
    spacingOutMm,
    voxelSpacing: reader.geometry.spacing
  });
  const { basis, boxClearanceMm, measurement, spec } = await prepareToothBasisMeasurement({
    consumerID,
    onProgress,
    reader,
    request,
    signal,
    spacingOutMm: effectiveSpacingOutMm
  });
  const roi = buildToothSampleRoi({
    geometry: reader.geometry,
    paddingMm: CONFIGURED_MAX_SLAB_THICKNESS_MM / 2,
    patientPoints: specPlanePoints(spec)
  });
  if (!roi) {
    throw new Error("Tooth-basis series ROI does not intersect the volume");
  }
  const volume = rasteriser ? null : await loadStableToothRoi({
    consumerID,
    onProgress: stampToothRoiPhase("series", onProgress),
    reader,
    signal,
    voxelBox: roi
  });
  return { basis, boxClearanceMm, measurement, roi, spec, volume };
};
const rasteriseToothBasisTile = async ({
  rasteriser,
  reader,
  seriesName,
  signal,
  slice,
  thickness,
  volume,
  window
}) => {
  const sampling = resolveSlabSampling({
    configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
    normal: slice.layout.normal,
    spacing: reader.geometry.spacing,
    thickness
  });
  if (!sampling) {
    throw new Error(`Tooth-basis ${seriesName} slab parameters are invalid`);
  }
  const rendered = rasteriser ? await rasteriser({
    layout: slice.layout,
    signal,
    slabOffsetsMm: sampling.offsetsMm,
    window
  }) : renderToothSlice({
    geometry: reader.geometry,
    layout: slice.layout,
    slabMode: "mean",
    slabOffsetsMm: sampling.offsetsMm,
    volume,
    window,
    signal
  });
  if (!rendered) {
    throw new Error("Tooth-basis series rendering was aborted");
  }
  return {
    slabOffsetsMm: sampling.offsetsMm,
    tile: {
      height: rendered.height,
      offsetMm: slice.offsetMm,
      pixels: rendered.data,
      sampling: {
        effectiveThicknessMm: sampling.effectiveThicknessMm,
        sampleCount: sampling.sampleCount,
        sampleSpacingMm: sampling.sampleSpacingMm
      },
      width: rendered.width
    }
  };
};

const describeToothBasisSeriesWithReader = async ({
  onProgress,
  reader,
  request,
  signal
}) => {
  try {
    const context = await prepareToothBasisMeasurement({
      onProgress,
      reader,
      request,
      signal
    });
    const outline = {
      massCentroidMm: context.measurement.centroidMm,
      series: context.spec.series.map((series) => ({
        name: series.name,
        offsetsMm: series.slices.map((slice) => slice.offsetMm)
      }))
    };
    return outline;
  } finally {
    releaseToothRoiDemand(reader, TOOTH_SERIES_CONSUMER_ID);
  }
};
const describeToothBasisSeries = async (request, signal, onProgress) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  try {
    return await describeToothBasisSeriesWithReader({
      onProgress,
      reader,
      request,
      signal
    });
  } finally {
    reader.release();
  }
};

const buildHorizontalNormalBasis = ({
  anatomicalBasis,
  iso,
  normalDeg,
  verticalAxisMode
}) => {
  const n = {
    x: Math.cos(normalDeg * Math.PI / 180),
    y: Math.sin(normalDeg * Math.PI / 180),
    z: 0
  };
  const toothAxis = verticalAxisMode === "tooth-axial" ? resolveToothBasis({ anatomicalBasis, iso })?.cr : null;
  if (verticalAxisMode === "tooth-axial" && !toothAxis) {
    return null;
  }
  const vSource = toothAxis ?? { x: 0, y: 0, z: 1 };
  const vDotN = vSource.x * n.x + vSource.y * n.y + vSource.z * n.z;
  const vRaw = {
    x: vSource.x - n.x * vDotN,
    y: vSource.y - n.y * vDotN,
    z: vSource.z - n.z * vDotN
  };
  const vLength = Math.hypot(vRaw.x, vRaw.y, vRaw.z);
  if (vLength < 1e-6) {
    return null;
  }
  const v = { x: vRaw.x / vLength, y: vRaw.y / vLength, z: vRaw.z / vLength };
  const u = {
    x: v.y * n.z - v.z * n.y,
    y: v.z * n.x - v.x * n.z,
    z: v.x * n.y - v.y * n.x
  };
  return { md: v, vo: n, cr: u, crownToRootSign: 1, sideSign: 1 };
};
const buildParityCursor = ({
  displacementFromOriginMm,
  inPlaneCenters,
  orientation
}) => {
  if (orientation === "axial") {
    return {
      md: inPlaneCenters.md,
      vo: inPlaneCenters.vo,
      cr: displacementFromOriginMm
    };
  }
  if (orientation === "vestibuloOral") {
    return {
      md: inPlaneCenters.md,
      vo: displacementFromOriginMm,
      cr: inPlaneCenters.cr
    };
  }
  return {
    md: displacementFromOriginMm,
    vo: inPlaneCenters.vo,
    cr: inPlaneCenters.cr
  };
};
const projectBoxOntoAxis = (box, origin, axis) => {
  const locals = [box.min.x, box.max.x].flatMap(
    (x) => [box.min.y, box.max.y].flatMap(
      (y) => [box.min.z, box.max.z].map(
        (z) => (x - origin.x) * axis.x + (y - origin.y) * axis.y + (z - origin.z) * axis.z
      )
    )
  );
  return { min: Math.min(...locals), max: Math.max(...locals) };
};
const resolveDisplacementFromOrigin = ({
  displacementMm,
  localBounds,
  reference
}) => {
  if (reference === "outer-box-min") {
    return localBounds.min + displacementMm;
  }
  if (reference === "outer-box-max") {
    return localBounds.max - displacementMm;
  }
  return displacementMm;
};
const renderToothParitySliceWithReader = async ({
  reader,
  request,
  signal
}) => {
  const basis = request.horizontalNormalDeg !== void 0 ? buildHorizontalNormalBasis({
    anatomicalBasis: request.anatomicalBasis,
    iso: request.iso,
    normalDeg: request.horizontalNormalDeg,
    verticalAxisMode: request.verticalAxisMode
  }) : request.planeSpace === "volume" ? {
    md: { x: 1, y: 0, z: 0 },
    vo: { x: 0, y: 1, z: 0 },
    cr: { x: 0, y: 0, z: 1 },
    crownToRootSign: 1,
    sideSign: 1
  } : resolveToothBasis({
    anatomicalBasis: request.anatomicalBasis,
    iso: request.iso
  });
  if (!basis) {
    throw new Error("Tooth basis is degenerate or the ISO is unsupported");
  }
  const referenceBox = request.planeSpace === "volume" && request.innerBox ? request.innerBox : request.outerBox;
  const frame = getToothPlaneFrame(basis, request.orientation);
  const sampling = resolveSlabSampling({
    configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
    normal: frame.n,
    spacing: reader.geometry.spacing,
    thickness: request.thickness
  });
  if (!sampling) {
    throw new Error("Slab sampling parameters are invalid");
  }
  const localBounds = projectBoxOntoAxis(referenceBox, request.origin, frame.n);
  const displacementFromOriginMm = resolveDisplacementFromOrigin({
    displacementMm: request.displacementMm,
    localBounds,
    reference: request.displacementReference ?? "origin"
  });
  const centerOf = (axis) => {
    const bounds = projectBoxOntoAxis(referenceBox, request.origin, axis);
    return (bounds.min + bounds.max) / 2;
  };
  const referenceCenters = request.inPlaneReference === "outer-box-center" ? {
    md: centerOf(basis.md),
    vo: centerOf(basis.vo),
    cr: centerOf(basis.cr)
  } : { md: 0, vo: 0, cr: 0 };
  const inPlaneCenters = {
    md: request.inPlaneCentersMm?.md ?? referenceCenters.md,
    vo: request.inPlaneCentersMm?.vo ?? referenceCenters.vo,
    cr: request.inPlaneCentersMm?.cr ?? referenceCenters.cr
  };
  const baseLayout = buildSliceLayout({
    basis,
    cursor: buildParityCursor({
      displacementFromOriginMm,
      inPlaneCenters,
      orientation: request.orientation
    }),
    heightPx: request.heightPx,
    mmPerPixel: request.mmPerPixel,
    orientation: request.orientation,
    origin: request.origin,
    widthPx: request.widthPx
  });
  if (!baseLayout) {
    throw new Error("Slice layout parameters are invalid");
  }
  const layout = request.swapInPlaneAxes ? { ...baseLayout, uAxis: baseLayout.vAxis, vAxis: baseLayout.uAxis } : baseLayout;
  const halfWidthMm = request.widthPx / 2 * request.mmPerPixel;
  const halfHeightMm = request.heightPx / 2 * request.mmPerPixel;
  const planeCorners = [-1, 1].flatMap(
    (uSign) => [-1, 1].map((vSign) => ({
      x: layout.centerPatientMm.x + layout.uAxis.x * halfWidthMm * uSign + layout.vAxis.x * halfHeightMm * vSign,
      y: layout.centerPatientMm.y + layout.uAxis.y * halfWidthMm * uSign + layout.vAxis.y * halfHeightMm * vSign,
      z: layout.centerPatientMm.z + layout.uAxis.z * halfWidthMm * uSign + layout.vAxis.z * halfHeightMm * vSign
    }))
  );
  const parityBox = {
    min: {
      x: Math.min(request.outerBox.min.x, ...planeCorners.map((c) => c.x)),
      y: Math.min(request.outerBox.min.y, ...planeCorners.map((c) => c.y)),
      z: Math.min(request.outerBox.min.z, ...planeCorners.map((c) => c.z))
    },
    max: {
      x: Math.max(request.outerBox.max.x, ...planeCorners.map((c) => c.x)),
      y: Math.max(request.outerBox.max.y, ...planeCorners.map((c) => c.y)),
      z: Math.max(request.outerBox.max.z, ...planeCorners.map((c) => c.z))
    }
  };
  const roi = buildStableToothRoi({
    basis,
    configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
    geometry: reader.geometry,
    origin: request.origin,
    outerBox: parityBox
  });
  if (!roi) {
    throw new Error("Tooth ROI does not intersect the volume");
  }
  const volume = await loadStableToothRoi({
    onProgress: (progress) => {
      if (progress.loaded === 0 || progress.loaded === progress.total) ;
    },
    reader,
    signal,
    voxelBox: roi
  }).catch((error) => {
    throw error;
  });
  try {
    const rendered = renderToothSlice({
      geometry: reader.geometry,
      layout,
      slabMode: request.slabMode,
      slabOffsetsMm: sampling.offsetsMm,
      volume,
      window: request.window,
      signal
    });
    if (!rendered) {
      throw new Error("CPU tooth slice rendering was aborted");
    }
    return {
      height: rendered.height,
      pixels: rendered.data,
      roi,
      sampling: {
        effectiveThicknessMm: sampling.effectiveThicknessMm,
        sampleCount: sampling.sampleCount,
        sampleSpacingMm: sampling.sampleSpacingMm
      },
      width: rendered.width
    };
  } finally {
    volume.release();
    releaseToothRoiDemand(reader);
  }
};

const renderToothParitySlice = async (request, signal) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  try {
    return await renderToothParitySliceWithReader({ reader, request, signal });
  } finally {
    reader.release();
  }
};

const VOLUME_BASIS = {
  md: { x: 1, y: 0, z: 0 },
  vo: { x: 0, y: 1, z: 0 },
  cr: { x: 0, y: 0, z: 1 },
  crownToRootSign: 1,
  sideSign: 1
};
const projectBoxCentreOntoAxis = (box, origin, axis) => {
  const locals = [box.min.x, box.max.x].flatMap(
    (x) => [box.min.y, box.max.y].flatMap(
      (y) => [box.min.z, box.max.z].map(
        (z) => (x - origin.x) * axis.x + (y - origin.y) * axis.y + (z - origin.z) * axis.z
      )
    )
  );
  return (Math.min(...locals) + Math.max(...locals)) / 2;
};
const flipRowsVertically = (pixels, width, height) => {
  const flipped = new Uint8ClampedArray(pixels.length);
  const rowBytes = width * 4;
  for (let row = 0; row < height; row += 1) {
    flipped.set(
      pixels.subarray(row * rowBytes, (row + 1) * rowBytes),
      (height - 1 - row) * rowBytes
    );
  }
  return flipped;
};
const renderToothSeriesWithReader = async ({
  onProgress,
  reader,
  request,
  signal
}) => {
  const basis = request.series.kind === "axial" ? VOLUME_BASIS : buildHorizontalNormalBasis({
    anatomicalBasis: request.anatomicalBasis,
    iso: request.iso,
    normalDeg: request.series.normalDeg,
    verticalAxisMode: "tooth-axial"
  });
  if (!basis) {
    throw new Error("Tooth series basis is degenerate or the ISO is invalid");
  }
  const referenceBox = request.series.kind === "axial" ? request.innerBox ?? request.outerBox : request.outerBox;
  const inPlaneCenters = {
    md: projectBoxCentreOntoAxis(referenceBox, request.origin, basis.md),
    vo: projectBoxCentreOntoAxis(referenceBox, request.origin, basis.vo),
    cr: projectBoxCentreOntoAxis(referenceBox, request.origin, basis.cr)
  };
  const buildCursor = (displacementMm) => request.series.kind === "axial" ? { md: inPlaneCenters.md, vo: inPlaneCenters.vo, cr: displacementMm } : { md: inPlaneCenters.md, vo: displacementMm, cr: inPlaneCenters.cr };
  const layouts = request.slices.map((slice) => {
    const layout = buildSliceLayout({
      basis,
      cursor: buildCursor(slice.displacementMm),
      heightPx: slice.heightPx,
      mmPerPixel: slice.mmPerPixel,
      orientation: request.series.kind === "axial" ? "axial" : "vestibuloOral",
      origin: request.origin,
      widthPx: slice.widthPx
    });
    if (!layout) {
      throw new Error(
        `Tooth series slice ${slice.assetID} layout parameters are invalid`
      );
    }
    return { layout, slice };
  });
  const corners = layouts.flatMap(({ layout, slice }) => {
    const halfWidthMm = slice.widthPx / 2 * slice.mmPerPixel;
    const halfHeightMm = slice.heightPx / 2 * slice.mmPerPixel;
    return [-1, 1].flatMap(
      (uSign) => [-1, 1].map((vSign) => ({
        x: layout.centerPatientMm.x + layout.uAxis.x * halfWidthMm * uSign + layout.vAxis.x * halfHeightMm * vSign,
        y: layout.centerPatientMm.y + layout.uAxis.y * halfWidthMm * uSign + layout.vAxis.y * halfHeightMm * vSign,
        z: layout.centerPatientMm.z + layout.uAxis.z * halfWidthMm * uSign + layout.vAxis.z * halfHeightMm * vSign
      }))
    );
  });
  const seriesBox = {
    min: {
      x: Math.min(request.outerBox.min.x, ...corners.map((corner) => corner.x)),
      y: Math.min(request.outerBox.min.y, ...corners.map((corner) => corner.y)),
      z: Math.min(request.outerBox.min.z, ...corners.map((corner) => corner.z))
    },
    max: {
      x: Math.max(request.outerBox.max.x, ...corners.map((corner) => corner.x)),
      y: Math.max(request.outerBox.max.y, ...corners.map((corner) => corner.y)),
      z: Math.max(request.outerBox.max.z, ...corners.map((corner) => corner.z))
    }
  };
  const roi = buildStableToothRoi({
    basis,
    configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
    geometry: reader.geometry,
    origin: request.origin,
    outerBox: seriesBox
  });
  if (!roi) {
    throw new Error("Tooth series ROI does not intersect the volume");
  }
  const volume = await loadStableToothRoi({
    consumerID: TOOTH_SERIES_CONSUMER_ID,
    onProgress,
    reader,
    signal,
    voxelBox: roi
  });
  try {
    const slices = layouts.map(({ layout, slice }) => {
      const sampling = resolveSlabSampling({
        configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
        normal: layout.normal,
        spacing: reader.geometry.spacing,
        thickness: slice.thickness
      });
      if (!sampling) {
        throw new Error(
          `Tooth series slice ${slice.assetID} slab parameters are invalid`
        );
      }
      const rendered = renderToothSlice({
        geometry: reader.geometry,
        layout,
        slabMode: request.slabMode,
        slabOffsetsMm: sampling.offsetsMm,
        volume,
        window: slice.window,
        signal
      });
      if (!rendered) {
        throw new Error("Tooth series rendering was aborted");
      }
      return {
        assetID: slice.assetID,
        height: rendered.height,
        pixels: request.series.kind === "axial" ? flipRowsVertically(rendered.data, rendered.width, rendered.height) : rendered.data,
        sampling: {
          effectiveThicknessMm: sampling.effectiveThicknessMm,
          sampleCount: sampling.sampleCount,
          sampleSpacingMm: sampling.sampleSpacingMm
        },
        width: rendered.width
      };
    });
    return { roi, slices };
  } finally {
    volume.release();
    releaseToothRoiDemand(reader, TOOTH_SERIES_CONSUMER_ID);
  }
};

const renderToothSeries = async (request, signal, onProgress) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  try {
    return await renderToothSeriesWithReader({ onProgress, reader, request, signal });
  } finally {
    reader.release();
  }
};

const resolveToothBasisSliceStepBySeries = (spec) => TOOTH_BASIS_SERIES_NAMES.reduce(
  (steps, name) => ({
    ...steps,
    [name]: spec.series.find((group) => group.name === name)?.sliceStepMm ?? DEFAULT_TOOTH_BASIS_SLICE_OPTIONS.sliceStepMm
  }),
  {}
);
const resolveToothBasisSlabThicknessBySeries = ({
  series,
  sliceOptions,
  spacing,
  spec
}) => TOOTH_BASIS_SERIES_NAMES.reduce((thicknesses, name) => {
  const rendered = series[name]?.[0]?.sampling.effectiveThicknessMm;
  const normal = spec.series.find((group) => group.name === name)?.slices[0]?.layout.normal;
  return {
    ...thicknesses,
    [name]: rendered ?? (normal ? resolveSlabSampling({
      configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
      normal,
      spacing,
      thickness: resolveToothBasisSliceOptions(name, sliceOptions).thickness
    })?.effectiveThicknessMm ?? 0 : 0)
  };
}, {});
const renderToothBasisPlanesWithReader = async ({
  onProgress,
  planes,
  rasteriser,
  reader,
  request,
  signal
}) => {
  try {
    const { boxClearanceMm, measurement, spec } = await prepareToothBasisMeasurement({
      onProgress,
      reader,
      request,
      signal
    });
    const selected = planes.map((plane) => ({
      plane,
      slice: findSpecSlice({ plane, spec })
    }));
    const missing = selected.find(
      (entry) => entry.slice === null
    );
    if (missing) {
      throw new Error(
        `Tooth-basis ${missing.plane.seriesName} has no slice at ${missing.plane.offsetMm} mm`
      );
    }
    const resolved = selected.filter(
      (entry) => entry.slice !== null
    );
    const voxelBoxes = resolved.map(({ slice }) => {
      const voxelBox = buildToothSampleRoi({
        geometry: reader.geometry,
        paddingMm: CONFIGURED_MAX_SLAB_THICKNESS_MM / 2,
        patientPoints: planeCorners(slice.layout)
      });
      if (!voxelBox) {
        throw new Error("Tooth-basis plane ROI does not intersect the volume");
      }
      return voxelBox;
    });
    const volumes = rasteriser ? [] : await loadToothRoiPlanes({
      consumerID: TOOTH_SERIES_CONSUMER_ID,
      onProgress: stampToothRoiPhase("cards", onProgress),
      reader,
      signal,
      voxelBoxes
    });
    try {
      const series = spec.series.reduce(
        (acc, seriesSpec) => ({ ...acc, [seriesSpec.name]: [] }),
        {}
      );
      const frames = spec.series.reduce(
        (acc, seriesSpec) => ({ ...acc, [seriesSpec.name]: seriesSpec.frame }),
        {}
      );
      for (const [index, { plane, slice }] of resolved.entries()) {
        const { tile } = await rasteriseToothBasisTile({
          rasteriser,
          reader,
          seriesName: plane.seriesName,
          signal,
          slice,
          thickness: resolveToothBasisSliceOptions(
            plane.seriesName,
            request.sliceOptions
          ).thickness,
          volume: volumes[index] ?? null,
          window: request.window
        });
        series[plane.seriesName] = [...series[plane.seriesName], tile];
      }
      return {
        extentsByThreshold: measurement.extentsByThreshold,
        frames,
        boxClearanceMm,
        massCentroidMm: measurement.centroidMm,
        massAxis: measurement.massAxis,
        measuredExtents: measurement.extents,
        profiles: measurement.profiles,
        roi: enclosingVoxelBox(voxelBoxes),
        series,
        slabThicknessMm: resolveToothBasisSlabThicknessBySeries({
          series,
          sliceOptions: request.sliceOptions,
          spacing: reader.geometry.spacing,
          spec
        }),
        sliceStepMm: resolveToothBasisSliceStepBySeries(spec)
      };
    } finally {
      volumes.forEach((volume) => volume.release());
    }
  } finally {
    releaseToothRoiDemand(reader, TOOTH_SERIES_CONSUMER_ID);
  }
};
const renderToothBasisSeriesWithReader = async ({
  onProgress,
  planes,
  rasteriser,
  reader,
  request,
  signal
}) => {
  if (planes !== void 0 && planes.length === 0) {
    throw new Error("At least one tooth-basis plane must be requested");
  }
  if (planes !== void 0) {
    return renderToothBasisPlanesWithReader({
      onProgress,
      planes,
      rasteriser,
      reader,
      request,
      signal
    });
  }
  try {
    const { boxClearanceMm, measurement, roi, spec, volume } = await prepareToothBasisRender({
      onProgress,
      rasteriser,
      reader,
      request,
      signal
    });
    try {
      const series = {};
      const frames = {};
      for (const seriesSpec of spec.series) {
        frames[seriesSpec.name] = seriesSpec.frame;
        series[seriesSpec.name] = [];
        for (const slice of seriesSpec.slices) {
          const { tile } = await rasteriseToothBasisTile({
            rasteriser,
            reader,
            seriesName: seriesSpec.name,
            signal,
            slice,
            thickness: resolveToothBasisSliceOptions(
              seriesSpec.name,
              request.sliceOptions
            ).thickness,
            volume,
            window: request.window
          });
          series[seriesSpec.name].push(tile);
        }
      }
      return {
        extentsByThreshold: measurement.extentsByThreshold,
        frames,
        boxClearanceMm,
        massCentroidMm: measurement.centroidMm,
        massAxis: measurement.massAxis,
        measuredExtents: measurement.extents,
        profiles: measurement.profiles,
        roi,
        series,
        slabThicknessMm: resolveToothBasisSlabThicknessBySeries({
          series,
          sliceOptions: request.sliceOptions,
          spacing: reader.geometry.spacing,
          spec
        }),
        sliceStepMm: resolveToothBasisSliceStepBySeries(spec)
      };
    } finally {
      volume?.release();
    }
  } finally {
    releaseToothRoiDemand(reader, TOOTH_SERIES_CONSUMER_ID);
  }
};

const renderToothBasisSeries = async (request, signal, onProgress, options) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  try {
    const rasteriser = typeof document !== "undefined" ? createFullHeadMPRSliceRasteriser(reader) : null;
    try {
      return await renderToothBasisSeriesWithReader({
        onProgress,
        planes: options?.planes,
        rasteriser: rasteriser?.render,
        reader,
        request,
        signal
      });
    } finally {
      rasteriser?.release();
    }
  } finally {
    reader.release();
  }
};

const renderToothBasisSliceWithReader = async ({
  onProgress,
  rasteriser,
  reader,
  request,
  signal,
  slice
}) => {
  try {
    const { spec, volume } = await prepareToothBasisRender({
      consumerID: TOOTH_ZOOM_CONSUMER_ID,
      onProgress,
      rasteriser,
      reader,
      request,
      signal,
      spacingOutMm: slice.spacingOutMm
    });
    try {
      const group = spec.series.find(
        (candidate) => candidate.name === slice.seriesName
      );
      if (!group) {
        throw new Error(`Tooth-basis series ${slice.seriesName} is unknown`);
      }
      const matched = group.slices.find(
        (candidate) => Math.abs(candidate.offsetMm - slice.offsetMm) < group.sliceStepMm / 2
      );
      if (!matched) {
        throw new Error(
          `Tooth-basis ${slice.seriesName} has no slice at ${slice.offsetMm} mm`
        );
      }
      const { tile } = await rasteriseToothBasisTile({
        rasteriser,
        reader,
        seriesName: slice.seriesName,
        signal,
        slice: matched,
        thickness: resolveToothBasisSliceOptions(
          slice.seriesName,
          request.sliceOptions
        ).thickness,
        volume,
        window: request.window
      });
      return {
        frame: group.frame,
        offsetMm: matched.offsetMm,
        seriesName: slice.seriesName,
        tile
      };
    } finally {
      volume?.release();
    }
  } finally {
    releaseToothRoiDemand(reader, TOOTH_ZOOM_CONSUMER_ID);
  }
};

const renderToothBasisSlice = async (request, slice, signal, onProgress) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  try {
    const rasteriser = typeof document !== "undefined" ? createFullHeadMPRSliceRasteriser(reader) : null;
    try {
      return await renderToothBasisSliceWithReader({
        onProgress,
        rasteriser: rasteriser?.render,
        reader,
        request,
        signal,
        slice
      });
    } finally {
      rasteriser?.release();
    }
  } finally {
    reader.release();
  }
};

const prefetchToothBasisSeriesWithReader = async ({
  onProgress,
  priority,
  reader,
  request,
  signal
}) => {
  const basis = resolveToothBasis({
    anatomicalBasis: request.anatomicalBasis,
    iso: request.iso
  });
  if (!basis) {
    throw new Error("Tooth basis is degenerate or the ISO is invalid");
  }
  const buildRoi = (patientPoints) => buildToothSampleRoi({
    geometry: reader.geometry,
    paddingMm: CONFIGURED_MAX_SLAB_THICKNESS_MM / 2,
    patientPoints
  });
  const measurementRoi = buildRoi(
    patientBoxCorners(
      toothBasisMeasurementBox({
        basis,
        origin: request.origin,
        searchBox: request.searchBox
      })
    )
  );
  if (!measurementRoi) {
    throw new Error("Tooth search box does not intersect the volume");
  }
  const phaseOneOutcome = await prefetchStableToothRoi({
    onProgress: stampToothRoiPhase("measurement", onProgress),
    priority,
    reader,
    signal,
    voxelBox: measurementRoi
  });
  if (phaseOneOutcome.reason === "aborted") {
    return {
      loadedChunkCount: phaseOneOutcome.loaded,
      reason: "aborted",
      requiredChunkCount: phaseOneOutcome.total,
      roi: measurementRoi
    };
  }
  const measured = await prepareToothBasisMeasurement({
    consumerID: TOOTH_PREFETCH_CONSUMER_ID,
    reader,
    request,
    signal
  }).catch((error) => {
    if (signal?.aborted) {
      return null;
    }
    throw error;
  }).finally(() => releaseToothRoiDemand(reader, TOOTH_PREFETCH_CONSUMER_ID));
  if (!measured) {
    return {
      loadedChunkCount: phaseOneOutcome.loaded,
      reason: "aborted",
      requiredChunkCount: phaseOneOutcome.total,
      roi: measurementRoi
    };
  }
  const seriesRoi = buildRoi(specPlanePoints(measured.spec));
  if (!seriesRoi) {
    throw new Error("Tooth-basis series ROI does not intersect the volume");
  }
  const renderRoi = enclosingVoxelBox([measurementRoi, seriesRoi]);
  const seriesOutcome = await prefetchStableToothRoi({
    onProgress: stampToothRoiPhase("series", onProgress),
    priority,
    reader,
    signal,
    voxelBox: seriesRoi
  });
  return {
    loadedChunkCount: seriesOutcome.loaded,
    reason: seriesOutcome.reason,
    requiredChunkCount: seriesOutcome.total,
    roi: renderRoi
  };
};

const prefetchToothBasisSeries = async (request, signal, onProgress, options) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  try {
    return await prefetchToothBasisSeriesWithReader({
      onProgress,
      priority: options?.priority,
      reader,
      request,
      signal
    });
  } finally {
    reader.release();
  }
};

const WHOLE_VOLUME_CONSUMER_ID = "full-head-mpr-whole-volume";
const WHOLE_VOLUME_WAVE_SIZE = 48;
const startWholeVolumePrefetch = ({
  onGaveUp,
  onProgress,
  reader
}) => {
  const total = reader.chunks.length;
  let released = false;
  const publishProgress = () => {
    onProgress?.({ loaded: reader.getLoadedChunkCount(), total });
  };
  const unsubscribe = reader.subscribeToChunkLoads(() => {
    if (released) {
      return;
    }
    publishProgress();
  });
  const unsubscribeGaveUp = reader.subscribeToPrefetchGaveUp(() => {
    if (released) {
      return;
    }
    onGaveUp?.();
  });
  publishProgress();
  if (reader.getLoadedChunkCount() < total) {
    reader.updateConsumerDemand(
      WHOLE_VOLUME_CONSUMER_ID,
      reader.chunks.map((chunk) => chunk.index),
      { priority: "background", waveSize: WHOLE_VOLUME_WAVE_SIZE }
    );
  }
  return {
    release: () => {
      if (released) {
        return;
      }
      released = true;
      unsubscribe();
      unsubscribeGaveUp();
      reader.releaseConsumerDemand(WHOLE_VOLUME_CONSUMER_ID);
    }
  };
};

const prefetchWholeVolume = async (volumeFileID, signal, onProgress, onGaveUp) => {
  const reader = await getSharedDCCTVolumeReader(volumeFileID, signal);
  if (signal?.aborted) {
    reader.release();
    return null;
  }
  const lease = tryAcquireFullHeadMPRGpuVolume(reader);
  const prefetch = startWholeVolumePrefetch({
    onGaveUp,
    onProgress,
    reader: lease ? {
      ...reader,
      getLoadedChunkCount: () => reader.chunks.filter((chunk) => lease.renderer.hasUploadedChunk(chunk.index)).length,
      updateConsumerDemand: (consumerID, indices, options) => reader.updateConsumerDemand(
        consumerID,
        indices.filter((index) => !lease.renderer.hasUploadedChunk(index)),
        options
      )
    } : reader
  });
  return {
    ...prefetch,
    release: () => {
      prefetch.release();
      lease?.release();
      reader.release();
    }
  };
};

const createFullHeadMPRSession = (container) => {
  const view = setupFullHeadMPRView(true, container);
  const controller = view.controller;
  const lifecycle = new AbortController();
  controller.setChrome({ paneFrame: "outlined", showDevChrome: false });
  controller.setInputMode("pan");
  return {
    addEventListener: view.addEventListener.bind(view),
    captureScreenshot: (projection) => controller.captureScreenshot(projection),
    cancelAnnotationInteraction: () => controller.cancelAnnotationInteraction(),
    deleteSelectedAnnotation: () => controller.deleteSelectedAnnotation(),
    dispose: () => {
      if (lifecycle.signal.aborted) {
        return;
      }
      lifecycle.abort();
      view.dispose();
    },
    getActivePane: () => controller.getActivePane(),
    getPaneElement: (projection) => controller.getPaneElement(projection),
    getPaneLayout: () => controller.getPaneLayout(),
    getPaneSurface: (projection) => controller.getPaneSurface(projection),
    getPrimaryProjection: () => controller.getPrimaryProjection(),
    getVisibleProjections: () => controller.getVisibleProjections(),
    getViewSnapshot: (projection) => controller.getViewSnapshot(projection),
    isViewReady: (projection) => controller.isViewReady(projection),
    removeEventListener: view.removeEventListener.bind(view),
    restoreSavedView: (savedView) => controller.restoreSavedView(savedView),
    setAnnotationTarget: (projection, target) => controller.setAnnotationTarget(projection, target),
    setAnnotationTool: (tool) => controller.setAnnotationTool(tool),
    setAnnotationsVisible: (visible) => controller.setAnnotationsVisible(visible),
    setInputMode: (mode) => controller.setInputMode(mode),
    setImageTool: (tool) => controller.setImageTool(tool),
    setImageOptionsTarget: (projection, target) => controller.setImageOptionsTarget(projection, target),
    setImageOptions: (projection, options) => controller.setImageOptions(projection, options),
    setPaneLayout: (layout) => controller.setPaneLayout(layout),
    setPrimaryProjection: (projection) => controller.setPrimaryProjection(projection),
    setSliceThicknessMm: (thickness) => controller.setSliceThicknessMm(thickness),
    setSliceStepMm: (steps) => controller.setSliceStepMm(steps),
    setVisibleProjections: (projections) => controller.setVisibleProjections(projections),
    setCrosshairsVisible: (visible) => controller.setCrosshairsVisible(visible),
    setZoomSyncEnabled: (enabled) => controller.setZoomSyncEnabled(enabled),
    stepSlice: (direction, projection) => controller.stepSlice(direction, projection),
    start: async (savedView, options) => {
      await controller.start({
        preferredBackend: options?.preferredBackend,
        colorProfile: savedView.window,
        volumeFileID: savedView.volumeFileID
      });
      if (!lifecycle.signal.aborted) {
        controller.restoreSavedView(savedView, { presentationID: options?.presentationID });
      }
    }
  };
};

const describeToothBasisSlice = async (request, slice, signal) => {
  const reader = await getSharedDCCTVolumeReader(request.volumeFileID, signal);
  const consumerID = `full-head-mpr-slice-description:${Math.random().toString(36).slice(2)}`;
  try {
    const { spec } = await prepareToothBasisMeasurement({
      consumerID,
      reader,
      request,
      signal,
      spacingOutMm: slice.spacingOutMm
    });
    const group = spec.series.find(({ name }) => name === slice.seriesName);
    const matched = group?.slices.find(
      ({ offsetMm }) => Math.abs(offsetMm - slice.offsetMm) < group.sliceStepMm / 2
    );
    if (!matched) {
      throw new Error("The requested tooth-basis slice does not exist");
    }
    const thickness = resolveToothBasisSliceOptions(
      slice.seriesName,
      request.sliceOptions
    ).thickness;
    const layout = matched.layout;
    const sampling = resolveSlabSampling({
      configuredMaxSlabThicknessMm: CONFIGURED_MAX_SLAB_THICKNESS_MM,
      normal: layout.normal,
      spacing: reader.geometry.spacing,
      thickness
    });
    if (!sampling) {
      throw new Error("Invalid tooth-basis slice sampling");
    }
    return {
      centerMm: layout.centerPatientMm,
      heightMm: layout.height * layout.mmPerPixelV,
      normal: layout.normal,
      projection: slice.seriesName === "frontal" ? "coronal" : slice.seriesName,
      sampleOffsetsMm: sampling.offsetsMm,
      sampling: thickness.kind === "native" ? "native" : "mean-slab",
      thicknessMm: sampling.effectiveThicknessMm,
      uAxis: layout.uAxis,
      vAxis: layout.vAxis,
      volumeFileID: request.volumeFileID,
      widthMm: layout.width * layout.mmPerPixelU,
      window: request.window
    };
  } finally {
    releaseToothRoiDemand(reader, consumerID);
    reader.release();
  }
};

const ToothCrops = (props) => {
  const ref = useRef(null);
  useEffect(
    () => {
      if (ref.current) {
        MedicalImageRender.insertToothCrops(props.toothID, ref.current, props.imageAttributes);
      }
    },
    []
  );
  return /* @__PURE__ */ jsx("div", { className: props.className, ref });
};

const insert = (props, ref) => {
  MedicalImageRender.insertSelectedToothSlices({
    toothID: props.toothID,
    container: ref.current,
    styles: props.imageAttributes,
    onClick: props.onClick,
    selectIMG: "both",
    useIMG: "both"
  });
};
const SelectedSlicesMIR = (props) => SlicesMIR$1(props, insert);

const SliceEditorComponent = (props) => {
  const sliceEditorRef = useRef(null);
  const { child, className, imageID, handleSetAnnotations: handleChangeAnnotations, handleSetMedicalImageViewOptions } = props;
  useEffect(() => {
    let disposers = [];
    const saveAnnotations = () => {
      const annotations = entityToAnnotation(MedicalImageRender.getAnnotations(imageID) || []);
      handleChangeAnnotations(annotations);
    };
    const saveViewOptions = (event) => {
      const medicalImageViewOptions = new MedicalImageViewOptions({
        Brightness: event?.brightness,
        Contrast: event?.contrast,
        Sharpness: event?.sharpness
      });
      if (medicalImageViewOptions) {
        handleSetMedicalImageViewOptions(medicalImageViewOptions);
      }
    };
    MedicalImageRender.setViewContainer("sliceEditor", sliceEditorRef);
    if (handleChangeAnnotations) {
      disposers = [
        MedicalImageRender.addEventListener("transformEnd", saveAnnotations, "sliceEditor"),
        MedicalImageRender.addEventListener("annotationsUserDelete", saveAnnotations, "sliceEditor")
      ];
    }
    if (handleSetMedicalImageViewOptions) {
      disposers.push(
        MedicalImageRender.addEventListener("brightnessContrastChanged", saveViewOptions, "sliceEditor"),
        MedicalImageRender.addEventListener("sharpnessChanged", saveViewOptions, "sliceEditor")
      );
    }
    return () => disposers.forEach((disposer) => disposer?.());
  }, [sliceEditorRef.current]);
  useEffect(() => {
    imageID && MedicalImageRender.showSlice(imageID);
  }, [imageID]);
  return /* @__PURE__ */ jsx("div", { id: MathUtils.generateUUID(), ref: sliceEditorRef, className, children: child });
};

const ViewMIR = (props) => {
  const { children, className, viewName, id, style } = props;
  const viewRef = useRef(null);
  useEffect(() => {
    viewRef.current && MedicalImageRender.setViewContainer(viewName, viewRef);
  }, [MedicalImageRender.isViewExist(viewName)]);
  return /* @__PURE__ */ jsx(
    "div",
    {
      ref: viewRef,
      id: id || MathUtils.generateUUID(),
      style,
      className,
      children
    }
  );
};

const MedicalImageRender = MIRenderAPI$1;
const MIRenderOrchestrator = Orchestrator$1;
const MIRenderMiddleware = MIRenderMiddleware$1;
const SlicesMIR = (props) => SlicesMIR$1(props);

export { ANNOTATION_PRESENTATION, FULL_HEAD_MPR_PLANE_TOLERANCE, MIRenderMiddleware, MIRenderOrchestrator, MedicalImageRender, SelectedSlicesMIR, SliceEditorComponent as SliceEditor, SlicesMIR, ToothCrops, ViewMIR, clearFullHeadMPRSession, configureSharedDCCTVolumeSize, createFullHeadMPRSession, describeToothBasisSeries, describeToothBasisSlice, formatAnnotationAngle, formatAnnotationLength, getSharedDCCTVolumeInfo, getSharedDCCTVolumeMetrics, isSameFullHeadMprAnnotationPlane, isSameFullHeadMprPhysicalPlane, isSameFullHeadMprPlane, makeAnnotationAngleGeometry, makeAnnotationAngleLabel, makeAnnotationArrowGeometry, makeAnnotationRulerLabel, makeFullHeadMprPointTransform, measureAnnotationLength, parseDicom, prefetchToothBasisSeries, prefetchWholeVolume, promiseWithResolvers, renderFullHeadMPRView, renderToothBasisSeries, renderToothBasisSlice, renderToothParitySlice, renderToothSeries, resolveFullHeadMPRViewSampling, resolveToothBasis, sortIOXRayImagesByViewerPosition, toPermanentToothNumber };
